import { Kit } from '../seasons/kit.ts';
import type { PassPlace } from './pass.ts';
import * as THREE from 'three';
import { glowSprite } from '../prims.ts';
import { type DamageType, COLOSSUS, glyphTurns, UNIQUE_FORMS, SIMA, SIMA_ANY_ERA, STRATA, STRATUM, UNSLEEPING_IDS, UNSLEEPING_OF, DEEP_CRYPT, DRAGON, GEAR_EFFECT_FX, NIGHTMARES, PALADIN, UNSLEEPING, WARRIOR, PLUNDER, SAGA, SIMA_TROPHY, SKILLS, SKILL_CASTS, STRUCTURES, TICK_HZ, lookOf, type SeasonId, type SkillLook, type StructureType, type UnsleepingId } from '@somnhold/content';
import {
  captainOf,
  cellId,
  DIRS,
  districtAt,
  districtCenter,
  doorOpen,
  hexRing,
  hexToWorld,
  isRevealed,
  spotted,
  T_GATE,
  T_RAMPART,
  type BattleEvent,
  type BattleState,
  type Bot,
  type DungeonLayout,
  type DungeonPuzzle,
  type SimaRun,
  type Enemy,
  type Expedition,
  type Maze,
  type Hero,
  type MapDef,
  type Structure,
  type Zone,
  frostable,
  geo,
  guardianOf,
  heroWalkable,
  kitOf,
  landStep,
  mapEra,
  rankAt,
} from '@somnhold/sim';
import { ANIM, Animator } from '../anim/animator.ts';
import { HAWK_STRIKE } from '../anim/creatures/hawk.ts';
import { posePoint, type AnimParams, type ClipInfo } from '../anim/atlas.ts';
import { BarLayer } from './bars.ts';
import type { ModelBatch } from './batch.ts';
import { StructBatcher, type StructSink } from './structbatch.ts';
import { SCALE, facingToRotY, simToWorld, WORLD_PER_HEX } from './coords.ts';
import { footYaw } from './footyaw.ts';
import { GlowLayer } from './glow.ts';
import { GearFxLayer, warmGearFx } from './gearfx.ts';
import { HolyLayer } from './holy.ts';
import { AuraLayer, type AuraKind } from './aura.ts';
import { SkillFxLayer, warmSkillFx } from './skillfx.ts';
import { GLIDE, Track } from './interp.ts';
import { Library, SIMA_GUARDIANS, baked, heroKey, isBaked, structKey } from './library.ts';
import { bakePool } from './bake-pool.ts';
import { FAR_BELOW_PX, LOD } from './lod.ts';
import { bakeStill, stillGlows } from './still.ts';
import { CAT_POSES, LECTERN_MAX, lecternTrophy, lullabyBowls } from '../models/saberes.ts';
import { alarmFrame, campfire, namestone } from '../models/saberes2.ts';
import { YardProps, type YardPiece } from './yard.ts';
import { EnsignLayer, type EnsignLook, type EnsignSpot } from './ensign.ts';
import { mulberry32 } from '../rand.ts';
import { simaWinchTrophy, WINCH_SLAB_SPOT, WINCH_SPOT, type WinchLook } from '../models/sima-winch.ts';
import { buildTerrainMesh, terrainHeightAt, type TerrainBuild } from './terrain.ts';
import { buildLandMesh, buildRift, landHeightAt, LEVEL_H, type LandBuild } from './landmesh.ts';
import { buildCrypt, type CryptBuild } from './crypt.ts';
import { MIST } from './outskirts.ts';
import { ShadowLayer } from './shadows.ts';
import { themeOf, type RingPiece, type SeasonTheme, type ThemeScene } from '../seasons/index.ts';
import { Vfx } from './vfx.ts';
import { wallGraph, yawOf, cellWorld, type WallCell, type WallGraph } from './wallgraph.ts';
import { DistrictDressing } from './districts.ts';
import { GreatWorkLayer, type DistrictPreview, type GreatWorkView } from './greatwork.ts';
import { Ascuas } from './ascuas.ts';
import { InkLayer } from './ink.ts';
import { buildOmen, type OmenCtx, type OmenScene } from '../omens/index.ts';
import { buildVillage } from '../omens/village.ts';
import { ClueMarks } from './clues.ts';
import { CrowdLayer } from './crowd.ts';
import { FacetakerLayer } from './facetaker.ts';
import { RescueLayer } from './rescue.ts';
import { HerdLayer } from './herd.ts';
import { LashLayer } from './lash.ts';
import { HuntLayer } from './hunt.ts';
import { buildTail, TAIL_SEA_Y } from '../omens/tail.ts';
import { buildCamp } from '../omens/camp.ts';
import { buildWoods } from '../omens/woods.ts';
import { BeerLayer } from './beer.ts';
import { SOMN_SKY, SomnLayer } from './somnsky.ts';
import { SealPlates, type SealLook } from './seal.ts';
import { Obsidian, OBSIDIAN } from './obsidian.ts';
import { CollapseLayer } from './collapse.ts';
import { TrapLayer } from './trap.ts';
import { DragonFieldLayer } from './dragonfield.ts';
import { BlastLayer } from './blast.ts';
import { buildStakes } from './stakes.ts';
import { MineLayer } from './mine.ts';
import { HallsLayer } from './halls.ts';
import { AnvilLayer } from './anvil.ts';
import { BesideLayer } from './beside.ts';
import { RaceLayer } from './race.ts';
import { OpenGateLayer } from './opengate.ts';
import { ForgeLayer } from './forge.ts';
import { PipesLayer } from './pipes.ts';
import { RibsLayer } from './ribs.ts';
import { NeckLayer } from './neck.ts';
import { OrderPostLayer } from './orderpost.ts';
import { BellLayer } from './bell.ts';
import { BellNightLayer } from './bellnight.ts';
import { castKey, sendaCast } from './senda.ts';
import { GuardhouseCrypt, SPINE_PLACES, SpineLayer } from './spine.ts';
import { ALTAR_PLACES, AltarsLayer } from './altars.ts';
import { TALLER_PLACES, TallerLayer } from './taller.ts';
import { ASTER_PLACES, AsterLayer } from './aster.ts';
import { BIBLIO_PLACES, BiblioLayer } from './biblio.ts';
import { CABLE_PLACES, CableLayer } from './cable.ts';
import { CORAZON_PLACES, CorazonLayer } from './corazon.ts';
import { OJOS_PLACES, OjosLayer } from './ojos.ts';
import { TormoLayer, tormoBoard } from './tormo.ts';
import { CAZA_PLACES, CazaLayer } from './caza.ts';
import { VERT_PLACES, VertLayer } from './vertebras.ts';
import { BOTE_PLACES, BoteLayer } from './bote.ts';
import { ISLA_PLACES, IslaLayer } from './isla.ts';
import { ROCA_PLACES, RocaLayer } from './roca.ts';
import { huntTrophy } from './roca-props.ts';
import { CAMINO_PLACES, CaminoLayer } from './camino.ts';
import { PASARELA_PLACES, PasarelaLayer } from './pasarela.ts';
import { NIEVE_PLACES, NieveLayer } from './nieve.ts';
import { AtriumCrypt, ERMITA_PLACES, ErmitaLayer, ermitaSkin } from './ermita.ts';
import { BOSQUE_PLACES, BosqueLayer } from './bosque.ts';
import { HORNOS_PLACES, HornosLayer } from './hornos.ts';
import { OTONO_PLACES, OtonoLayer } from './otono.ts';
import { COPA_PLACES, CopaLayer } from './copa.ts';
import { BarkCrypt, RAICES_PLACES, RaicesLayer } from './raices.ts';
import { buildGroveTrophy, type GroveLook, type GroveTrophy } from './grove-trophy.ts';
import { SALT_PLACES, SaltLayer } from './salt.ts';
import { ARCH_PLACES, ArchLayer } from './arch.ts';
import { MONASTERY_PLACES, MonasteryLayer } from './monastery.ts';
import { RootsCrypt, SEVENTH_PLACES, SeventhLayer, damWheel, maritShield, mergeFigure, wayBanner } from './seventh.ts';
import { buildTormo } from '../models/tormo.ts';
import { CANDLE_TIME, druidSeason, parseLook, PREY_PAINT, SEASON_BLOOM } from '../models/dress.ts';
import type { ChasmPlace } from './chasm.ts';
import type { FortPlace } from './fort.ts';
import { WellLayer } from './well.ts';
import { FairLayer } from './fair.ts';
import { ShoreLayer } from './shore.ts';
import { FinLayer } from './fin.ts';
import { LighthouseLayer } from './lighthouse.ts';
import { SunkenLayer } from './sunken.ts';
import { UN_RENDER, UnsleepingFx, felled, moment, presence, vanished, type UnCtx } from './unsleeping/index.ts';
import { DeepCryptLook } from './deep.ts';
import { SimaLook } from './sima.ts';
import { PuzzleLayer } from './puzzles.ts';
import { CellarsLayer } from './cellars.ts';
import { PlunderLayer } from './plunder.ts';
import { MazeLayer } from './maze.ts';
import { keepCrownBracket } from '../models/structures.ts';
import { FALLEN_PATIO_R, PATIO_PIECES, PLINTH_SCALE, PLINTH_SMALL, PLINTH_TOP, sagaPlinth } from '../models/trophies.ts';
import { BARREL_SPOT, barrelPileTrophy, clueBoardTrophy, CLUES_SPOT, CORRAL_SPOT, corralTrophy, mooringPostTrophy, POST_SPOT, SPEAR_SPOT, spearTrophy, MAP_TABLE_SPOT, mapTableTrophy, VIGIL_BANNER_SPOT, vigilBannerTrophy, HARPOON_SPOT, harpoonTrophy, BEACON_SPOT, beaconTrophy, BUCKET_SPOT, JAR_SPOT, BANNER_SPOT, bucketTrophy, NET_SPOT, OAR_SPOT, BOAT_SPOT, netTrophy, HEART_SPOT, heartTrophy, FANG_SPOT, fangTrophy, PLAQUE_SPOT, plaqueTrophy, type PlaqueTrophy, POST_SIGN_SPOT, CLAPPER_SPOT, SHARD_SPOT, postSignTrophy, lidBellTrophy, MINE_CART_SPOT, GUARD_SPOT, KEY_SPOT, HAMMER_SPOT, ORE_SPOT, mineCartTrophy, FACES_LANTERN_SPOT, facesLanternTrophy, LANTERNS_SPOT, lanternStringTrophy, fallenTrophy, fallenSpot, furrowsTableTrophy, gateBellTrophy, SHELF_SPOT, TABLE_SPOT, tomeShelfTrophy, GLYPH_STONE_SPOT, glyphStoneTrophy, type GlyphStoneLook, type BarrelLook, type BellLook, type BellTrophy, type LidBellTrophy, type ShelfLook, type TableLook } from '../models/trophies.ts';
import type { Model } from '../models/types.ts';
import { FormFx } from './formfx.ts';

/** Golems gathering on terraced land: `n` of them between a store and a source, each with the load it brings back. */
/** Where a saga's plinth stands in the patio of deeds: its cell, pushed in to the keep's yard (`small`), not won yet (`waiting`). */
/** The paths' statues on the patio: each hero's miniature key (no rig: a still shape) and name, and the plaque's title. */
export interface StatueLook {
  title: string;
  figures: { key: string; name: string }[];
}

export interface PatioSlot {
  q: number;
  r: number;
  small: boolean;
  waiting: boolean;
  /** At the keep's foot with no plinth (the Furrows' table, the tomes' shelf). */
  bare?: boolean;
}

export interface GatherView {
  kind: 'wood' | 'stone' | 'crystal';
  store: { q: number; r: number };
  source: { q: number; r: number };
  n: number;
}

/** What a gathering golem carries home, as a pile model. */
const LOAD: Record<GatherView['kind'], string> = { wood: 'pile:forest', stone: 'pile:quarry', crystal: 'pile:crystal' };

/** Construction shown on the board (campaign jobs). */
export interface WorkView {
  q: number;
  r: number;
  structure: string;
  level: number;
  material: number;
  stage: 'blueprint' | 'building';
  /** 0..1 */
  progress: number;
  /** A golem is on it right now. */
  active: boolean;
  /** Upgrade of an existing structure (don't hide it). */
  upgrade: boolean;
  /** Terraced land: the other cells a three-cell building takes. */
  foot?: { q: number; r: number }[];
  /** Being drawn, not planned yet: a paler ghost. */
  draft?: boolean;
}

export interface ViewOptions {
  quality: 'high' | 'low';
  /** Hero colors by player id (rune circles, ghost tint). */
  colors?: Record<string, string>;
  /** Player id of the local hero (selection ring). */
  me?: string;
  /** What stands at the center: the keep, or an incursion node ("node:quarry"…). */
  centerpiece?: string;
  /** The battle's holiday season (`state.season`): its theme dresses the board. Absent: the ordinary look. */
  season?: SeasonId;
  /** A saga descent without a Warden (`dungeon.hallWins`): the last hall is drawn as the way out. */
  exitHall?: boolean;
  /**
   * Torches the crypt keeps from the start, lit or not: a live party's size (a waiting room rebuilds as friends
   * join). Absent: one per hero as they come.
   */
  torches?: number;
  /**
   * The bastion between waves (bastion-screen.ts): nothing to defend, so no paladin's aura is drawn, live or spectre (its
   * ring at 3.5 hexes covered the keep and the works, and a friend logging in brought it back for everyone).
   */
  calm?: boolean;
}

/** Every projectile model (library.ts `projectile`), the branches' shots among them. */
const PROJECTILES = ['arrow', 'bolt', 'dart', 'rock', 'boulder', 'cinder', 'glassShard', 'harpoon', 'pitchPot', 'splitStone', 'emberPot', 'whistleBolt', 'limeSack'];
/** What a tower's branch throws instead of its usual shot (docs/OBRAS.md), by the projectile it fires. */
const BRANCH_SHOT: Record<string, Partial<Record<string, string>>> = {
  harpoon: { bolt: 'harpoon' },
  pitch: { rock: 'pitchPot' },
  split: { boulder: 'splitStone' },
  embers: { boulder: 'emberPot' },
  // The whistling ballista (an ingenio, docs/SABERES.md §4.2): a reed bolt that howls, drawn with a pale streak.
  whistler: { bolt: 'whistleBolt' },
  // The lime catapult: a sack of quicklime that bursts in a pale cloud (the sim's `lime` zone).
  lime: { rock: 'limeSack' },
};
/** The lime catapult's cloud: quicklime's pale cold white. */
const LIME_PALE = new THREE.Color('#9a968c');
/** The garrison's campfire (the song). */
const SONG_FIRE = new THREE.Color('#ff8a3a');
/** A drowsed nightmare's sleep: the drowsing tower's cold white. */
const DROWSE_PALE = new THREE.Color('#e4ecff');
/** The whistling bolt's streak: the pale cold of the reed's howl (the arrows' trail, a shade greener). */
const WHISTLE_TRAIL = new THREE.Color('#e2ecdf');
/** A pot of pitch or embers in flight: its glow. */
const POT_GLOW = new THREE.Color('#ff7a2a');
/** A tower on a wall run (the adarve) stands a little smaller than on its own cell, on the wall-walk. */
const MOUNT_K = 0.82;
/** How far up a wall's model its wall-walk is (stone and iron runs: the body under the merlons, structures.ts wall). */
const WALL_WALK = 0.66;

/** One leg of the barracks cat's round (BattleView.catRoute): world x/z, a lift over the ground (or a height, `abs`). */
interface CatLeg {
  kind: 'walk' | 'leap' | 'sit' | 'sleep' | 'hidden';
  t0: number;
  dur: number;
  ax: number;
  ay: number;
  az: number;
  bx: number;
  by: number;
  bz: number;
  abs: boolean;
  yaw: number;
  /** How far she had walked when it began, and how long it is (her stride's frame). */
  d0: number;
  len: number;
}
interface CatRoute {
  legs: CatLeg[];
  total: number;
  phase: number;
  /** The layout it was laid out for (grid revision, structures, keep era). */
  key: string;
}
/** The yaw of a model facing +Z turned the way a boulder rolls (`roll`, 0–5 in DIRS). */
const rollYaw = (roll: number | undefined): number => {
  const d = DIRS[roll ?? 0] ?? DIRS[0];
  if (!d) return 0;
  const w = hexToWorld(d.q, d.r);
  return Math.atan2(w.x, w.y);
};
/** The cat's baked frames (models/saberes.ts CAT_POSES). */
const CAT_SIT = CAT_POSES.indexOf('sit');
const CAT_SLEEP = CAT_POSES.indexOf('sleep');
const CAT_LEAP = CAT_POSES.indexOf('leap');

/** The ranger's arrows in flight: a cold white streak (brighter for a volley). */
const ARROW_TRAIL = new THREE.Color('#dfe8f4');
const ARROW_VOLLEY = new THREE.Color('#eef4ff');
/** The cold edge of a warrior's cleave. */
const CLEAVE_EDGE = new THREE.Color('#cfe0ff');
/** Under a nightmare caught in the ranger's net. */
const NET_GLOW = new THREE.Color('#e9edf2');
const FLY_HEIGHT: Partial<Record<string, number>> = { wraith: 0.45, gargoyle: 0.75, crow: 0.9, wyvern: 0.9, moth: 0.8, wyrmling: 0.9 };
/** The dragon's head among its bones (anim/creatures/dragon.ts): its jaws follow it. */
const DRAGON_HEAD_BONE = 7;
/** How high the dragon circles over the clearing (world units). */
const DRAGON_SKY = 2.4;
/** Point lights a crypt's scene keeps for its room lights all fight long (see cullRoomLights). */
/** Brann and Hilde's bet this battle is played under, if any (rules.bet: HUD and looks only). */
const betOf = (s: BattleState): string | undefined => s.rules?.bet;
/** The bots a hero's kit can call: its casts' `bot` steps (nested ones too), and the automaton by its variant (sim's cast.ts). */
const companionsOf = (h: Hero): string[] => {
  const out = new Set<string>();
  const walk = (x: unknown): void => {
    if (Array.isArray(x)) for (const y of x) walk(y);
    else if (x && typeof x === 'object') {
      const o = x as Record<string, unknown>;
      const kind = o.k === 'bot' ? (o.b as Record<string, unknown> | undefined)?.kind : undefined;
      if (typeof kind === 'string') out.add(kind);
      for (const v of Object.values(o)) walk(v);
    }
  };
  kitOf(h).forEach((id, i) => {
    if (id === 'automaton') out.add(h.forks?.[i] === 'colossus' ? 'colossus' : h.forks?.[i] === 'swarm' ? 'spider' : 'automaton');
    else walk(SKILL_CASTS[id]?.steps);
  });
  return [...out];
};
/** Where the cask leaks, in the barrel cart's model space (models/saga.ts `barrelCart`): the tap, then two staves. */
const BEER_LEAKS = [new THREE.Vector3(0, 0.53, 0.88), new THREE.Vector3(0.31, 0.72, 0.28), new THREE.Vector3(-0.3, 0.82, 0.48)];
const ROOM_SLOTS = 4;
/** Point lights the heroes' torches take at most, however big the party (docs/RENDIMIENTO.md: lights cost every lit fragment). */
const TORCH_LIGHTS = 4;
/** Seconds a room light takes to fade in or out when it takes or gives up one of those. */
const ROOM_FADE_S = 0.35;
const RED = new THREE.Color('#ff4f3a');
/** How far the obsidian colossus's slam reaches, in world units (the sim's COLOSSUS.slamRadius). */
const COLOSSUS_REACH = (COLOSSUS.slamRadius / 1000) * WORLD_PER_HEX;
const GREEN = new THREE.Color('#7fd36a');
/** Dorn's own automatons' light (Las vías 4): a friend's cold blue-green, not a foe's red. */
const DORN_TEAL = new THREE.Color('#5fe8c8');
/** The learned skills' light: the hawk's pale ring, frost, cold holy light. */
const HAWK_PALE = new THREE.Color('#e8e2d6');
const HAWK_BAR = new THREE.Color('#d8cfc0');
const DECOY_BAR = new THREE.Color('#ff8a6e');
const ORB_FROST = new THREE.Color('#9fdcff');
const HOLY_COLD = new THREE.Color('#dfe8ff');
const ROAR_RED = new THREE.Color('#ff6a3a');
const RAGE = new THREE.Color('#ff8a6e');
/** Out of sight under the board: things kept in the scene only so their shaders compile ahead. */
const PARKED = new THREE.Vector3(0, -60, 0);
const EMBER_GLOW = new THREE.Color('#ff5a30');
/** Kaela's moss and thorns (docs/ANTIHEROES.md). */
const KAELA_MOSS = new THREE.Color('#4f9a32');
const WASP_GREEN = new THREE.Color('#9ab84a');
/** The ranger's venom. */
const VENOM = new THREE.Color('#8fdc4a');
const DRONE_CYAN = new THREE.Color('#4fdcff');
const TURNED_BLUE = new THREE.Color('#4fe0ff');
const MINE_DUST = new THREE.Color('#6b5a44');
const TAR_DARK = new THREE.Color('#2a2420');
const ULT_PHYS = new THREE.Color('#ff8a6e');
/** The weapon's glow and the motes shed walking, by the type of the ultimate carried (physical: none). */
const ULT_GLOW: Partial<Record<SkillLook, { color: THREE.Color; mote: string; up: number }>> = {
  fire: { color: new THREE.Color('#ff7a2a'), mote: '#ffb347', up: 0.04 },
  frost: { color: new THREE.Color('#9fdcff'), mote: '#f2faff', up: -0.01 },
  bolt: { color: new THREE.Color('#c8bcff'), mote: '#ffffff', up: 0.02 },
  poison: { color: new THREE.Color('#5fcf9a'), mote: '#9ab84a', up: -0.015 },
  holy: { color: new THREE.Color('#eef2ff'), mote: '#ffffff', up: 0.035 },
};
const BANNER_RED = new THREE.Color('#c23a2a');
const SNOW_CLOUD = new THREE.Color('#c8d8ea');
const COIL_BOLT = new THREE.Color('#c8bcff');
const METEOR_SKY = new THREE.Color('#ff6a2a');
const STEEL = new THREE.Color('#c4c8d2');
const RIFT_DARK = new THREE.Color('#2a2040');
const RIFT_CORE = new THREE.Color('#9a86d0');
const FLARE_HOT = new THREE.Color('#ff8a5a');
const QUAKE_EARTH = new THREE.Color('#4a3a2c');
const ACID_GREEN = new THREE.Color('#b5e06a');
const SHADOW_LINK = new THREE.Color('#9a86d0');
const SNARE_STEEL = new THREE.Color('#e3e6ec');
const THORN_GREEN = new THREE.Color('#5f8a46');
const PLAGUE_GREEN = new THREE.Color('#7f9a3a');
const BLUE = new THREE.Color('#7fb8ff');
const STONE = new THREE.Color('#eceef2');
/** Holy light: pale blue halo, white core, warm white for consecrated fire. */
const HALO = new THREE.Color('#a9d2ff');
const WHITE = new THREE.Color('#ffffff');
const WARM = new THREE.Color('#ffd9b8');
const TAU = Math.PI * 2;
/** The artificer's clockwork light. */
const CLOCK_CYAN = new THREE.Color('#4fdcff');
/** The caravan's lanterns. */
const LANTERN = new THREE.Color('#ffb877');

/** Flash colors for recently hit entities (instance tint multiplies material color). */
const FLASH = new THREE.Color(2.4, 2.1, 2.0);
const GHOSTED = new THREE.Color(0.6, 0.6, 0.7);
// Sigrun's paint, dull and lit (BattleView.lightPrey).
const PREY_DULL = new THREE.Color('#8a2418');
const PREY_DULL_E = new THREE.Color('#4a0c06');
const PREY_LIT = new THREE.Color('#ff4f3a');
const PREY_LIT_E = new THREE.Color('#ff2c12');
/** A tower the frost slows (docs/PRESAGIOS.md §3.3): its stone and wood go pale and blue under the rime. */
const ICED = new THREE.Color(0.7, 0.88, 1.45);
const RIME = new THREE.Color('#bfe6ff');
const EMBER = new THREE.Color('#ff8a3a');

/**
 * Draws a BattleState. `sync()` is called once per sim tick (12 Hz) and rebuilds all instance
 * buffers — entities never interpolate (stop-motion, SPEC §15). `frame()` runs every animation
 * frame for camera-facing effects.
 */
/** Per node: which golem works it and the color of the chips that fly. */
const EXTRACT: Record<string, { golem: number; color: string }> = {
  forest: { golem: 1, color: '#d2a46c' },
  quarry: { golem: 0, color: '#c9c3b8' },
  crystal: { golem: 2, color: '#8fd3ff' },
  altar: { golem: 3, color: '#c9b8ff' },
  rescue: { golem: 0, color: '#bfa98a' },
  lair: { golem: 3, color: '#ff8a6e' },
  iron: { golem: 2, color: '#9a5a3e' },
  fort: { golem: 0, color: '#a58cff' },
};

/**
 * A tower's enchantment, as it reads on the board: the glow on its crown and at its foot, and what it sheds
 * (embers rise, frost motes drift down, sparks crackle, acid drips and bubbles). Acid is a sickly
 * green-yellow, apart from the cursed-tower green and the holiday wisps.
 */
const ENCHANT_LOOK: Record<string, { glow: THREE.Color; fx: string; foot: number }> = {
  fire: { glow: new THREE.Color('#ff7a2a'), fx: '#ffb070', foot: 0.16 },
  frost: { glow: new THREE.Color('#9fdcff'), fx: '#eaf8ff', foot: 0.18 },
  lightning: { glow: new THREE.Color('#bcd4ff'), fx: '#ffffff', foot: 0.12 },
  acid: { glow: new THREE.Color('#c6e21a'), fx: '#d8f040', foot: 0.24 },
};
/** Acid-eaten nightmares take on a bilious tint while it lasts. */
const ACID_TINT = new THREE.Color(0.86, 1.0, 0.42);
const ACID = new THREE.Color('#c6e21a');
/** Heat glowing off lava, eruptions and the magma lord. */
const LAVA_GLOW = new THREE.Color('#ff5a1f');
/** The seal's plates: lit (stepped right) and flashing (the order being shown). */
const RUNE_LIT = new THREE.Color('#ff6a3a');
const FLASH_RUNE = new THREE.Color('#ff8a6e');
/** A clockwork turret with more than this left to wait is resting after an overload (it fires every few ticks otherwise). */
const CLOCK_REST = STRUCTURES.clockwork.reload * 2 + 4;
/** How far the trebuchet's arm turns from cocked when it throws (models/era4.ts). */
const TREB_THROW = 2.95;
/** A three-cell building on terraced land is drawn this much bigger than its one-cell self. */
const BIG = 1.85;
/** How much bigger a building on several cells is drawn: three cells BIG; the Titan's Eye (seven) is modelled at world size. */
const footScale = (foot?: { q: number; r: number }[]): number => (!foot ? 1 : foot.length >= 6 ? 1 : BIG);
/** The watchtower's mark over a nightmare it spots under the ground. */
const SPOTTED = new THREE.Color('#dfe8ff');

/**
 * Wear of a structure (the ring's cells included): 'x' broken, 2 crumbling (< 50 % hp), 1 worn (< 90 %),
 * 0 intact. Worn shows early on purpose: a few knocks already crack it, so the repair is asked for.
 */
const wearStage = (st: { hp: number; maxHp: number; destroyed: boolean }): 'x' | 0 | 1 | 2 =>
  st.destroyed || st.hp <= 0 ? 'x' : st.hp * 100 < st.maxHp * 50 ? 2 : st.hp * 100 < st.maxHp * 90 ? 1 : 0;

/** Timber structures, which smoulder (embers) instead of only smoking when crumbling. */
const WOODEN = (st: { type: string; material: number }): boolean =>
  st.type === 'catapult' || st.type === 'trebuchet' || st.type === 'barracks' || ((st.type === 'ballista' || st.type === 'wall' || st.type === 'gate') && st.material === 1);


/**
 * Flat see-through marks on the floor (rings, discs, warnings): pulled toward the camera in the depth
 * test so they never flicker against the ground or each other's exact height.
 */
const decalMat = (p: THREE.MeshBasicMaterialParameters): THREE.MeshBasicMaterial =>
  new THREE.MeshBasicMaterial({ ...p, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });

const ARMERO_GLOW = new THREE.Color('#ff8a6e');
/** The Chasm's winch: its size on the board (a little over a cell across) and its shaft's breathing light. */
const WINCH_SCALE = 0.78;
const WINCH_PULSE = new THREE.Color('#ff4f3a');

/** The Unsleeping an event's name belongs to (`<id><Name>`), if any. */
const unOf = (name: string): UnsleepingId | undefined => UNSLEEPING_IDS.find((id) => name.startsWith(id));

/** From this many nightmares on, those out of the camera's sight aren't drawn (see BattleView.cullView); `?cull=0` draws them all. */
export const CROWD = { cull: true };
const CULL_FROM = 120;
/** How far past the view (world units) a nightmare still counts as seen: its size, its bar and a margin. */
const CULL_MARGIN = 3;
/** A camera that moved more than this in a frame (a cut, a fast drag) draws everything the next one. */
const CULL_MOVE = 4;

/** Ground whose whole look is its damage type: turned by a glyph, it is drawn as ground of the new type instead. */
const TYPED_GROUND: ReadonlySet<string> = new Set(['storm', 'blizzard', 'plague', 'burn', 'meteors', 'rift', 'spores', 'hallow']);
/** How long the unique forms' summons stand (their life bars). */
const FORM_BOT_LIFE: Partial<Record<string, number>> = {
  gasparGuard: UNIQUE_FORMS.gasparHammer.final.fx.ticks,
  cloak: UNIQUE_FORMS.wandererCloak.a!.fx.ticks,
  barkBear: UNIQUE_FORMS.dwarfBark.final.fx.ticks,
};
const SPECTRE_BLUE = new THREE.Color('#cfdcff');
const SAP_GREEN = new THREE.Color('#9df0c4');
/** The unique forms' own ground (sim battle/forms.ts), drawn by formfx.ts. */
const FORM_ZONES: ReadonlySet<string> = new Set(['cleft', 'fireLine', 'toll', 'oldRoot', 'parapet', 'puddle', 'vigil', 'sprout']);

/** The colours of the damage types a conversion glyph turns things into (docs/GLIFOS.md §1; PODERES §5). */
const VEIL: Partial<Record<DamageType, { glow: THREE.Color; mote: string }>> = {
  fire: { glow: new THREE.Color('#ff6a2a'), mote: '#ffb347' },
  frost: { glow: new THREE.Color('#9fdcff'), mote: '#e6f6ff' },
  lightning: { glow: new THREE.Color('#9fb8ff'), mote: '#f0f4ff' },
  poison: { glow: new THREE.Color('#5fbf4a'), mote: '#a6f08a' },
  // A trail glyph leaves ground of the skill's own type, whatever it is.
  physical: { glow: new THREE.Color('#8a8070'), mote: '#b8ae9c' },
  holy: { glow: new THREE.Color('#dfe8ff'), mote: '#ffffff' },
  shadow: { glow: new THREE.Color('#6a4a9a'), mote: '#a98ad8' },
  acid: { glow: new THREE.Color('#9fd04a'), mote: '#d6f28a' },
};

let COMPANY_HALO: THREE.Texture | null = null;
/** A soft oval of light, bright in the middle and gone at the rim (made once). */
const haloTexture = (): THREE.Texture => {
  if (COMPANY_HALO) return COMPANY_HALO;
  const w = 64;
  const h = 32;
  const data = new Uint8Array(w * h * 4);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const dx = (x / (w - 1)) * 2 - 1;
      const dy = (y / (h - 1)) * 2 - 1;
      const v = Math.max(0, 1 - Math.sqrt(dx * dx * 0.7 + dy * dy));
      const k = (y * w + x) * 4;
      data[k] = data[k + 1] = data[k + 2] = Math.round(255 * v * v);
      data[k + 3] = 255;
    }
  COMPANY_HALO = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
  COMPANY_HALO.needsUpdate = true;
  return COMPANY_HALO;
};

/**
 * The paladin's path's trophy (docs/sendas/paladin.md §10): the Way's banner («Juremos a la par») or Marit's old shield
 * («Juro yo»), with the monastery dam's wheel beside it, and Marit standing by it if she stayed. Where: the Temple's
 * hex, else the yard of deeds (the caller says).
 */
export interface WayLook {
  kind: 'banner' | 'shield';
  marit: boolean;
  at: { q: number; r: number };
}

/**
 * The artificer's path's trophy (docs/sendas/artifice.md §10): Tormo standing by the bastion's gate, or leaning on the
 * artificer's Workshop if it stands (the caller picks the cell and which way he faces); his shoulders' cracks redder
 * with the old dust; a plaque at his feet with the names of the artificers who brought him (one Tormo for all).
 */
export interface TormoLook {
  at: { q: number; r: number };
  /** Where he looks (a point on the board: the bastion's middle, or out from the Workshop's wall). */
  face: { q: number; r: number };
  old: boolean;
  names: string[];
  title: string;
}

/**
 * The barbarian's path's trophy (docs/sendas/barbaro.md §10): Sigrun's axe driven into a log (taken from the ice) or her
 * diary on the stone of thirty-one strokes (left there), the small wooden boar beside it always. Where: beside the
 * statues in the yard of deeds (the caller says).
 */
export interface HuntLook {
  kind: 'axe' | 'diary';
  at: { q: number; r: number };
}

/**
 * The cleric's path's trophy (docs/sendas/clerigo.md §10): Tessa's little lantern and a mirror of the Hermitage on a
 * lectern, her stole over its edge; let believe (`cloth`), the mirror is Aud's with Piet's grey cloth over its top. Where:
 * by the Sanctum, else the yard of deeds (the caller says).
 */
export interface RoadLook {
  cloth: boolean;
  at: { q: number; r: number };
}
/** The Carcoma's size: a sixth smaller for each root cut, and with her life (down to fitting in a hand). */
const carcomaShrink = (s: BattleState, e: { hp: number; maxHp: number }): number => {
  const cut = s.senda?.deep?.cut.filter(Boolean).length ?? 0;
  const life = e.maxHp > 0 ? Math.max(0, e.hp) / e.maxHp : 1;
  return (1 - cut * 0.08) * (0.45 + 0.55 * life);
};

/** The Pass of the Rift's own looks (chapter 5 of the warrior's path): the Ventisquero, its cubs; null for any other. */
const passSkin = (s: BattleState, id: number): string | null => {
  const st = s.senda;
  if (!st) return null;
  if (st.storm?.boss === id) return 'ventisquero';
  for (const list of [st.storm?.cubs, st.crag?.cubs, st.line?.cubs, st.beam?.cubs]) if (list) for (const c of list) if (c.id === id) return 'snowCub';
  // The paladin's chapter 1: the Arado (an ogre in the sim).
  if (st.canal?.plow.id === id) return 'arado';
  // The paladin's chapter 5: La Que Huyó (a drowned in the sim) and her squires (goblins).
  const sev = st.seventh;
  if (sev && sev.boss === id) return 'fledBoss';
  if (sev && sev.shieldId.includes(id)) return 'fledSquire';
  // The artificer's chapter 1: Nadd's old golems and the Overseer (ogres in the sim).
  if (st.golem) for (const g of st.golem.list) if (g.id === id) return g.boss ? 'capataz' : 'workGolem';
  // The artificer's chapter 2: La Picotera on the winch's drum (a gargoyle in the sim).
  if (st.gearbox && st.gearbox.boss === id) return 'picotera';
  // The artificer's chapter 3: La Tenaza at the trip-hammer (an ogre in the sim).
  if (st.cams && st.cams.tz.id === id) return 'tenaza';
  // The artificer's chapter 4: La Barrena in the drill hall (an ogre in the sim).
  if (st.drills && st.drills.bz.id === id) return 'barrena';
  // The artificer's chapter 5: the ash cubs (goblins in the sim) of the shell and the plates.
  if (st.shell?.cubs.includes(id) || st.plates?.cubs.includes(id)) return 'ashCub';
  // The barbarian's chapter 1: the howler at the head of the pack on the ledge (a wolf in the sim).
  if (st.howler?.id === id) return 'howler';
  // The barbarian's chapter 2: the den's two howlers by the cave's mouth.
  if (st.vert?.den?.howlers.some((x) => x.id === id)) return 'howler';
  // The barbarian's chapter 3: the old howler among the floes.
  if (st.bote?.elder?.id === id) return 'elderHowler';
  // The barbarian's chapter 4: the Ash Seal on Sigrun's last night, and its two howlers.
  if (st.isla?.seal?.id === id) return 'ashSeal';
  if (st.isla?.seal?.howlers.some((x) => x.id === id)) return 'howler';
  // The barbarian's chapter 5: the Grey Wolf, the climb's howler, the cubs (wolves in the sim, small and frail).
  const rk = st.roca;
  if (rk?.wolf?.id === id) return 'grayWolf';
  if (rk?.climb?.howler.id === id) return 'howler';
  const rr = s.rules?.senda?.roca;
  const cubHp = rr?.mouths?.cubHp ?? rr?.bed?.cubHp ?? rr?.wolf?.cubHp;
  if (cubHp && s.enemies.some((e) => e.id === id && e.maxHp === cubHp && e.kind === 'wolf')) return 'wolfCub';
  // The druid's chapter 1: the Lodazal (a drowned in the sim); the clearing's slimes swollen with the shoots they ate.
  const dr = st.druid;
  if (dr && dr.boss === id) return 'lodazal';
  const fed = dr?.fed[id] ?? 0;
  if (fed) return fed >= 2 ? 'slimeFat2' : 'slimeFat';
  // The druid's chapter 2: the old wyvern of the counterfire.
  if (st.summer && st.summer.boss === id) return 'wyvernOld';
  // The druid's chapter 3: the broodmother of the test.
  if (st.autumn && st.autumn.boss === id) return 'broodmother';
  // The druid's chapter 4: the frost that climbs.
  if (st.winter && st.winter.boss === id) return 'escarcha';
  // The druid's chapter 5: the Carcoma (a drowned in the sim).
  if (st.deep && st.deep.boss === id) return 'carcoma';
  // The mage's chapter 1: the burnt library's loose pages (soot crows in the sim).
  if ((st.soot || st.seals) && s.enemies.find((e) => e.id === id)?.kind === 'crow') return 'page';
  return null;
};

export class BattleView {
  readonly scene = new THREE.Scene();
  readonly root = new THREE.Group();
  readonly vfx = new Vfx();
  readonly bars = new BarLayer();
  readonly entityGlow = new GlowLayer(4096);
  private readonly addGlow = (x: number, y: number, z: number, color: THREE.Color, size: number, opacity: number): void => this.entityGlow.add(x, y, z, color, size, opacity);
  readonly shadows = new ShadowLayer();
  readonly overlay = new THREE.Group();
  /** The cleric's sanctuaries and wards. */
  readonly holy = new HolyLayer();
  /** The armory's effects: rare gear in a fight, the offerings' braziers (gearfx.ts). */
  readonly gearFx = new GearFxLayer();
  readonly skillFx = new SkillFxLayer();
  /** The unique pieces' forms, seen (formfx.ts): shared primitives only. */
  private readonly formFx = new FormFx({
    vfx: this.vfx,
    glow: this.entityGlow,
    flare: (p, color, size, life) => this.flare(p, color, size, life),
    shake: (n) => {
      this.shake = Math.max(this.shake, n);
    },
    later: (n, run) => this.later.push({ n, run }),
    low: () => this.opts.quality === 'low',
    at: (x, y, h = 0) => simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y) + h),
    atTo: (x, y, h, out) => simToWorld(x, y, out, this.heightAt(x, y) + h),
    k: WORLD_PER_HEX / 1000,
  });
  /** The paladins' auras on the ground (aura.ts). */
  readonly aura = new AuraLayer();
  /** Each standing paladin's aura this frame (reused): where, how far, which, its slot in the layer. */
  private auras: { id: number; kind: AuraKind; slot: number; p: THREE.Vector3; r: number; r2: number }[] = [];
  private aurasN = 0;
  /** A nightmare just struck by holy light, waiting to see if a hero's hit follows it (retribution). */
  private holyHit: { tick: number; id: number; x: number; y: number } | null = null;
  /** Blows softened by devotion shown this tick (a few at most). */
  private devotionShown = 0;
  /** Soft flashes that fade over a few ticks (holy light, impacts). */
  private flares: { p: THREE.Vector3; color: THREE.Color; size: number; life: number; max: number }[] = [];
  /** Effects that play out over the next few ticks (a mend's arc travelling to its target). */
  private later: { n: number; run: () => void }[] = [];
  terrain: TerrainBuild;
  private flashes = new Map<number, number>();
  private lastTick = -1;
  private heroRing: THREE.Mesh;
  private ghostRings = new Map<string, THREE.Mesh>();
  private hover: THREE.LineLoop;
  private rangeRing: THREE.Mesh;
  /** A second ground ring for an area that isn't a range (where helping a site counts). */
  private areaRing: THREE.Mesh;
  private preview: THREE.Group | null = null;
  private previewKey = '';
  private blueprintMat = new THREE.MeshBasicMaterial({ color: '#7fb8ff', transparent: true, opacity: 0.45, depthWrite: false });
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  /** A second scratch point (the unique stage glow passes one to the effects while `v` is in use). */
  private vq = new THREE.Vector3();
  private s = new THREE.Vector3();
  private eu = new THREE.Euler();
  private local = new THREE.Matrix4();
  private yAxis = new THREE.Vector3(0, 1, 0);
  private zAxis = new THREE.Vector3(0, 0, 1);
  private lights: THREE.PointLight[] = [];
  readonly keepPos = new THREE.Vector3();
  /** World positions by entity id, refreshed each sync (HUD projections). */
  readonly positions = new Map<number, THREE.Vector3>();
  /**
   * Live play: where to draw my own hero instead of where the simulation has it (the client's prediction
   * of my walking, ahead of the room's ticks), with its walk and heading; null keeps the simulation's.
   */
  heroLead: ((h: Hero, x: number, y: number) => { x: number; y: number; walk: boolean; facing: number } | null) | null = null;
  /** A prediction is on screen: redraw every frame, even with nothing else moving or a tick late. */
  leading = false;
  /**
   * A path's cinematic (client game/cine.ts) is drawing this board: no health bars, no rings, the heroes posed as the
   * scene says. Off in every fight.
   */
  cinema = false;
  /** A figure held in a pose by a cinematic: its clip, and the moment of it to hold (absent: the clip plays on). */
  readonly posed = new Map<number, { clip: string; t?: number }>();
  /** A path's people on the field this frame, by id (the HUD's bubbles and their names ride over them). */
  readonly sendaAt = new Map<string, THREE.Vector3>();
  /** A campfire burning on the board (a path's campfire scene), in sim units. */
  private fireAt: { x: number; y: number } | null = null;
  private batchesTouched = new Set<ModelBatch>();
  private works: WorkView[] = [];
  /** Cells to highlight (selection) — drawn as a red outline. */
  selected: { q: number; r: number } | null = null;
  private selLine: THREE.LineLoop;

  private dirty = false;
  /** How far the screen is between the previous tick and this one (0..1): what walks and flies is drawn in between. */
  private alpha = 1;
  private track = { hero: new Track(), enemy: new Track(), bot: new Track(), proj: new Track() };

  /** The armory's rack (bastion): its model's key and where it stands, in sim units (null: no forge, or nothing found yet). */
  private armeroAt: { key: string; x: number; y: number } | null = null;
  setArmero(a: { key: string; x: number; y: number } | null): void {
    const was = this.armeroAt;
    if (was?.key === a?.key && was?.x === a?.x && was?.y === a?.y) return;
    this.armeroAt = this.map.kind === 'bastion' && !this.crypt ? a : null;
    // Baked here, once, rather than on the first frame that draws it.
    if (this.armeroAt) this.lib.batch(this.armeroAt.key);
    this.invalidate();
  }

  /** The old barracks' panoply over the rack (bastion, docs/PANOPLIA.md §9.6): its key and spot (null: no piece rearmed). */
  private panopliaAt: { key: string; x: number; y: number } | null = null;
  setPanoplia(a: { key: string; x: number; y: number } | null): void {
    const was = this.panopliaAt;
    if (was?.key === a?.key && was?.x === a?.x && was?.y === a?.y) return;
    this.panopliaAt = this.map.kind === 'bastion' && !this.crypt ? a : null;
    // Baked here, once, rather than on the first frame that draws it.
    if (this.panopliaAt) this.lib.batch(this.panopliaAt.key);
    this.invalidate();
  }

  /** The panoply, standing behind the rack and turned as it is. */
  private drawPanoplia(use: (k: string) => ModelBatch): void {
    const a = this.panopliaAt;
    if (!a) return;
    const p = simToWorld(a.x, a.y, new THREE.Vector3(), this.heightAt(a.x, a.y));
    this.compose(p, 0.25, this.lib.scaleOf(a.key));
    use(a.key).add(this.m, 0);
  }

  /**
   * The rack, turned to face the table camera: the pieces are what has to read from the table, not the rack's
   * back (the cellars' trophy first read as a dark blob). Where it stands is the bastion's call (a free spot by the forge).
   */
  private drawArmero(use: (k: string) => ModelBatch): void {
    const a = this.armeroAt;
    if (!a) return;
    const p = simToWorld(a.x, a.y, new THREE.Vector3(), this.heightAt(a.x, a.y));
    this.compose(p, 0.25, this.lib.scaleOf(a.key));
    use(a.key).add(this.m, 0);
    // A warm pool of light on the ground: the shared glow layer, no light of its own.
    this.entityGlow.add(p.x, p.y + 0.05, p.z, ARMERO_GLOW, 2.2, 0.28);
  }

  /* ---------- the saberes in the bastion (docs/SABERES.md) ---------- */

  /**
   * The still pieces round the keep, all in one merged mesh (engine/yard.ts): the lectern of lore, the stone of names,
   * the alarm's bell frame, the song's campfire and the lullaby's bowls. Laid out again when the grid changes or a
   * setter asks (yardRev = −1); the mesh is rebuilt only when a piece, its count or its cell changes.
   */
  private yard: YardProps | null = null;
  private yardRev = -1;
  /** How many saberes the lectern shows (0: none), how many lines the stone of names carries (−1: no stone). */
  private lecternN = 0;
  private namestoneN = -1;
  private songOn = false;
  /** The lectern's cell ('q,r', 'keep' against the keep, '': none): the cat's round keeps off it. */
  private lecternCell = '';
  /** Where the song's campfire burns (null: no song). */
  private songFireAt: THREE.Vector3 | null = null;

  /**
   * The lectern of lore («Atril de los saberes», docs/SABERES.md §6): one piece of lore on it for each saber the
   * bastion found (1 to 18; more stand no higher), 0 takes it down. It stands in a free cell of the keep's yard (no
   * patio plinth, no structure on it), the front ones first, turned to the table camera.
   */
  setLectern(n: number): void {
    const next = Math.max(0, Math.min(LECTERN_MAX, Math.floor(n || 0)));
    // The bastion screen sets it on every state: the same count changes nothing.
    if (next === this.lecternN) return;
    this.lecternN = next;
    this.yardRev = -1;
    this.dirty = true;
  }

  /** The keep's yard, front cells first (the front of the board is +r): where the lectern and the rest may stand. */
  private static readonly LECTERN_CELLS: readonly (readonly [number, number])[] = [[0, 1], [-1, 1], [1, 0], [-1, 0], [1, -1], [0, -1]];

  /** The rite at the keep this Marea (docs/SABERES.md §4.3). */
  private rite: { id: string } | null = null;

  /**
   * The rite the bastion holds this Marea, shown at the keep (docs/SABERES.md §4.3). The lullaby: a ring of clay bowls
   * round the keep's foot, embers in each. The alarm: the garrison's small cracked bell in its timber frame in a free
   * cell of the keep's yard, swinging now and then. The props: oak beams crossed and braced inside every gate (drawn
   * with the gates, as instances). Null takes it down. Bastion only; no light.
   */
  setRite(id: 'lullaby' | 'alarm' | 'props' | null): void {
    if (this.map.kind !== 'bastion' || this.crypt || (this.rite?.id ?? null) === id) return;
    this.rite = id ? { id } : null;
    if (id === 'props') this.lib.batch('gateProps');
    this.yardRev = -1;
    this.dirty = true;
  }

  /** The props inside every standing gate: crossed toward the keep, on the gate's inner side. */
  private drawProps(s: BattleState, use: (k: string) => ModelBatch): void {
    const b = use('gateProps');
    for (const st of s.structures) {
      if (st.type !== 'gate' || st.destroyed) continue;
      const p = this.standAt(st.q, st.r);
      const len = Math.hypot(p.x, p.z) || 1;
      const yaw = Math.atan2(-p.x, -p.z);
      p.x -= (p.x / len) * 0.12;
      p.z -= (p.z / len) * 0.12;
      this.compose(p, yaw, 1);
      b.add(this.m, 0);
    }
  }

  /**
   * The stone of names (docs/SABERES.md §4.5): a tall stone by the keep with a line carved for each Marea won since it
   * was found (`lines`, up to 80). 0: known and still uncarved (only the Vigil's mark on it); −1 takes it down. Like the
   * lectern it stands in a free cell of the keep's yard (the next free after the lectern's), its face to the camera.
   */
  setNamestone(lines: number): void {
    const n = lines < 0 ? -1 : Math.min(80, Math.floor(lines || 0));
    if (n === this.namestoneN) return;
    this.namestoneN = n;
    this.yardRev = -1;
    this.dirty = true;
  }

  /**
   * The garrison's song (docs/SABERES.md §4.5): while it plays (the client says so), a campfire burns in a free cell of
   * the keep's yard and three golems stand round it. The fire's glow is the shared layer's; no light.
   */
  setSong(on: boolean): void {
    if (on === this.songOn) return;
    this.songOn = on;
    this.yardRev = -1;
    this.dirty = true;
  }

  /** Whether anything stands (or stood) round the keep that the yard has to lay out. */
  private get yardWanted(): boolean {
    return this.lecternN > 0 || this.namestoneN >= 0 || this.songOn || this.rite?.id === 'alarm' || this.rite?.id === 'lullaby' || !!(this.yard && !this.yard.empty);
  }

  /** The free cells of the keep's yard, front first, after the patio's plinths, what is built and what is planned. */
  private yardFree(s: BattleState): (readonly [number, number])[] {
    const taken = new Set<string>();
    for (const slot of Object.values(this.patio)) taken.add(`${slot.q},${slot.r}`);
    for (const st of s.structures) {
      taken.add(`${st.q},${st.r}`);
      for (const f of st.foot ?? []) taken.add(`${f.q},${f.r}`);
    }
    for (const w of this.works) taken.add(`${w.q},${w.r}`);
    return BattleView.LECTERN_CELLS.filter(([q, r]) => !taken.has(`${q},${r}`));
  }

  /** Lays out the yard's pieces, each in its own cell (the lectern first, then the stone, the bell, the fire). */
  private syncYard(s: BattleState): void {
    if (this.map.kind !== 'bastion' || this.crypt) return;
    if (!this.yard) {
      this.yard = new YardProps();
      this.scene.add(this.yard.group);
    }
    const free = this.yardFree(s);
    const k = this.lib.scaleOf(this.center());
    const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
    const pieces: YardPiece[] = [];
    /** A cell's spot, `in` of the way out from the keep (the cell's middle at 1); no cell: by the keep. */
    const spot = (cell: readonly [number, number] | undefined, at: number, dx: number, dz: number): THREE.Vector3 => {
      if (!cell) return kp.clone().add(new THREE.Vector3(dx, 0, dz));
      const c = hexToWorld(cell[0], cell[1]);
      return simToWorld(c.x * at, c.y * at, new THREE.Vector3(), this.heightAt(c.x * at, c.y * at));
    };
    const put = (key: string, make: () => Model, p: THREE.Vector3, yaw: number, scale: number): void => {
      this.compose(p, yaw, scale);
      pieces.push({ key, make, m: this.m.clone() });
    };
    this.lecternCell = '';
    if (this.lecternN > 0) {
      const cell = free.shift();
      this.lecternCell = cell ? cell.join(',') : 'keep';
      const n = this.lecternN;
      // Toward the table camera (it looks from yaw π/4), a little off the cell's middle toward the keep.
      put(`lectern${n}`, () => lecternTrophy({ n }), spot(cell, 0.86, -0.55, 0.75), Math.PI / 4, k * (cell ? 0.9 : 0.75));
    }
    if (this.namestoneN >= 0) {
      const n = this.namestoneN;
      put(`namestone${n}`, () => namestone({ lines: n }), spot(free.shift(), 0.8, 0.7, -0.5), Math.PI / 4, k * 0.9);
    }
    if (this.rite?.id === 'alarm') put('alarm', () => alarmFrame(), spot(free.shift(), 0.8, -0.7, -0.5), Math.PI / 4, k);
    this.songFireAt = null;
    if (this.songOn) {
      const p = spot(free.shift(), 1, 0.9, 0.9);
      this.songFireAt = p.clone();
      put('campfire', () => campfire(), p, 0, k);
      for (let i = 0; i < 3; i++) this.lib.batch(this.rk(`golem:${i}`));
    }
    // The lullaby's bowls ring the keep's foot, in the keep's own frame.
    if (this.rite?.id === 'lullaby') put('lullaby', () => lullabyBowls(), kp, Math.PI / 6, k);
    this.yard.set(pieces);
  }

  /** The yard's glows (the shared glow layer), the song's flicker and its golems round the fire. */
  private drawYard(s: BattleState, use: (k: string) => ModelBatch): void {
    for (const g of this.yard?.glows ?? []) this.entityGlow.add(g.p.x, g.p.y, g.p.z, g.color, g.size, g.op);
    const f = this.songFireAt;
    if (!f) return;
    const k = 0.85 + 0.15 * Math.sin(this.now * 7.3) * Math.sin(this.now * 3.1);
    this.entityGlow.add(f.x, f.y + 0.3, f.z, SONG_FIRE, 1.6 * k, 0.55);
    this.entityGlow.add(f.x, f.y + 0.05, f.z, SONG_FIRE, 3.2, 0.2 * k);
    if (this.fresh && s.tick % 3 === 0) this.vfx.burst(this.v.set(f.x, f.y + 0.35, f.z), { n: 1, color: '#ffb070', speed: 0.01, up: 0.03, size: 0.06, life: 14, gravity: -0.002 });
    const gs = this.lib.scaleOf('golem:0');
    for (let i = 0; i < 3; i++) {
      const a = Math.PI / 4 + Math.PI * 0.55 + i * 2.1;
      const p = this.catP.set(f.x + Math.sin(a) * 0.55, 0, f.z + Math.cos(a) * 0.55);
      p.y = this.heightAt(p.x / (WORLD_PER_HEX / 1000), p.z / (WORLD_PER_HEX / 1000));
      const gb = use(this.rk(`golem:${i}`));
      this.compose(p, a + Math.PI, gs);
      gb.add(this.m, i, undefined, 1, this.act(gb, `song${i}`, ['idle'], this.now + i * 0.7));
      this.shadows.add(p.x, p.y, p.z, 0.5);
    }
  }

  /** The sieve's catch is waiting to be sifted (CampaignState.sift): its pale heaps by the pit. */
  private sifting = false;

  /** Whether the sieve has a catch waiting (CampaignState.sift set): pale heaps of dust by its pit while it does. */
  setSifting(on: boolean): void {
    if (on === this.sifting) return;
    this.sifting = on && this.map.kind === 'bastion' && !this.crypt;
    if (this.sifting) this.lib.batch('siftDust');
    this.dirty = true;
  }

  private drawSift(s: BattleState, use: (k: string) => ModelBatch): void {
    const st = s.structures.find((x) => x.type === 'sieve' && !x.destroyed);
    if (!st) return;
    const p = this.standAt(st.q, st.r, st.foot);
    this.compose(p, st.foot ? footYaw(st.q, st.r, st.foot) : 0, this.lib.scaleOf('siftDust') * footScale(st.foot));
    use('siftDust').add(this.m, 0);
  }

  /** The group's ensign (docs/SABERES.md §4.5): its look, its layer, the grid it was hung for. */
  private ensign: EnsignLook | null = null;
  private ensignLayer: EnsignLayer | null = null;
  private ensignRev = -1;

  /**
   * The group's ensign: a gonfalon on a standard on the keep's walk, one by each standing gate and one on every tower
   * of level 3 and up, all turned to the table camera. One merged mesh, its cloth stirred in the districts' shader.
   * Null takes it down. Bastion only.
   */
  setEnsign(e: { field: number; color: number; sign: number } | null): void {
    const next = e && this.map.kind === 'bastion' && !this.crypt ? { field: e.field | 0, color: e.color | 0, sign: e.sign | 0 } : null;
    if (next?.field === this.ensign?.field && next?.color === this.ensign?.color && next?.sign === this.ensign?.sign) return;
    this.ensign = next;
    if (next && !this.ensignLayer) {
      this.ensignLayer = new EnsignLayer(this.opts.quality === 'low');
      this.scene.add(this.ensignLayer.group);
    }
    if (!next) this.ensignLayer?.set(null, []);
    this.ensignRev = -1;
    this.dirty = true;
  }

  /** Where the ensign hangs on this layout. */
  private ensignSpots(s: BattleState): EnsignSpot[] {
    const out: EnsignSpot[] = [];
    const face = Math.PI / 4;
    const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
    const ks = this.lib.scaleOf(this.center());
    const c = Math.cos(Math.PI / 6), sn = Math.sin(Math.PI / 6);
    // On the keep: a standard on the front of its walk.
    const [x, z] = [0.12, 0.34];
    const walk = kp.y + 1.94 * ks;
    out.push({ x: kp.x + (x * c + z * sn) * ks, y: walk + 0.95 * ks, z: kp.z + (-x * sn + z * c) * ks, yaw: face, w: 0.34, pole: walk });
    for (const st of s.structures) {
      if (st.destroyed) continue;
      const def = STRUCTURES[st.type];
      if (st.type === 'gate') {
        // By each gate, on its inner side, on a standard of its own.
        const p = this.standAt(st.q, st.r);
        const len = Math.hypot(p.x, p.z) || 1;
        const ix = -p.x / len, iz = -p.z / len;
        const gx = p.x + ix * 0.55 - iz * 0.45, gz = p.z + iz * 0.55 + ix * 0.45;
        const g = this.heightAt(gx / (WORLD_PER_HEX / 1000), gz / (WORLD_PER_HEX / 1000));
        out.push({ x: gx, y: g + 1.65, z: gz, yaw: face, w: 0.3, pole: g });
      } else if (def.category === 'tower' && st.level >= 3) {
        // On top of the tower, on a short staff.
        const key = structKey(st.type, st.level, st.material, wearStage(st), st.branch);
        const p = this.standAt(st.q, st.r, st.foot);
        const top = p.y + this.lib.heightOf(key) * this.lib.scaleOf(key) * footScale(st.foot);
        out.push({ x: p.x + 0.18, y: top + 0.55, z: p.z - 0.18, yaw: face, w: 0.24, pole: top - 0.05 });
      }
    }
    return out;
  }

  /** Boulders rolling down their line this moment (the sim's `shot` of kind 'boulder'): world ends, start, length. */
  private rolling: { ax: number; az: number; bx: number; bz: number; sx: number; sy: number; tx: number; ty: number; at: number; dur: number }[] = [];
  private rollQ = new THREE.Quaternion();
  private rollAxis = new THREE.Vector3();

  /** Each rolling boulder: down its line by the view's clock, turning over as it goes, dust off it each tick. */
  private drawRolling(use: (k: string) => ModelBatch): void {
    const b = use('proj:boulder');
    for (let i = this.rolling.length - 1; i >= 0; i--) {
      const r = this.rolling[i] as (typeof this.rolling)[number];
      const f = (this.now - r.at) / r.dur;
      if (f >= 1 || f < 0) {
        const end = simToWorld(r.tx, r.ty, this.catP, this.heightAt(r.tx, r.ty) + 0.1);
        if (f >= 1) this.vfx.burst(end, { n: 12, color: '#8b857a', speed: 0.07, up: 0.05, size: 0.3, life: 16, smoke: true });
        this.rolling.splice(i, 1);
        continue;
      }
      const x = r.sx + (r.tx - r.sx) * f;
      const y = r.sy + (r.ty - r.sy) * f;
      const len = Math.hypot(r.bx - r.ax, r.bz - r.az) || 1;
      const p = simToWorld(x, y, this.catP, this.heightAt(x, y) + 0.36);
      // Turning over about the axis across its way, as far as it has rolled.
      this.rollAxis.set((r.bz - r.az) / len, 0, -(r.bx - r.ax) / len);
      this.rollQ.setFromAxisAngle(this.rollAxis, (f * len) / 0.36);
      this.m.compose(p, this.rollQ, this.s.set(1.8, 1.8, 1.8));
      b.add(this.m, 0);
      this.shadows.add(p.x, p.y - 0.36, p.z, 0.7);
      if (this.fresh) this.vfx.burst(this.v.set(p.x, p.y - 0.3, p.z), { n: 2, color: '#8b857a', speed: 0.03, up: 0.03, size: 0.22, life: 10, smoke: true });
    }
  }

  /** The barracks cat (docs/SABERES.md §4.5): how many kittens and her seed (null: no cat). */
  private catOpts: { kittens: number; seed: number } | null = null;
  /** Her round of the bastion, laid out once per layout (see catRoute), walked every frame by the view's clock. */
  private catWalk: CatRoute | null = null;
  private catP = new THREE.Vector3();
  private catRev = -1;
  /** The lectern's key when her round was laid out (she walks round its cell). */
  private catLectern = '';

  /**
   * The barracks cat and up to three kittens, in the bastion between waves (never in a fight): she sleeps on the
   * keep's wall-walk, comes down and out of its door, walks a stretch of the walls, sits at the foot of the
   * highest building, and goes back in; the kittens follow her. Not in the sim: she walks by the view's clock in
   * the frame's drawing (like the gathering golems), as instances of one baked model with a frame per pose.
   */
  setCat(o: { kittens: number; seed: number } | null): void {
    const next = o && this.map.kind === 'bastion' && !this.crypt ? { kittens: Math.max(0, Math.min(3, Math.floor(o.kittens))), seed: o.seed | 0 } : null;
    if (next?.kittens === this.catOpts?.kittens && next?.seed === this.catOpts?.seed) return;
    this.catOpts = next;
    this.catWalk = null;
    this.catRev = -1;
    // Baked here, once, rather than on the first frame that draws her.
    if (next) this.lib.batch('cat');
    this.dirty = true;
  }

  /** The cat's round: legs over the bastion, laid out from the keep, the walls and the highest building. */
  private catRoute(s: BattleState): CatRoute {
    const seed = this.catOpts?.seed ?? 0;
    const rng = mulberry32(7717 + seed);
    const R = this.map.radius;
    const g = geo(R);
    const K = WORLD_PER_HEX / 1000;
    const legs: CatLeg[] = [];
    let t = 0;
    let dist = 0;
    const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
    const ks = this.lib.scaleOf(this.center());
    const rot = (x: number, z: number): [number, number] => {
      const c = Math.cos(Math.PI / 6), sn = Math.sin(Math.PI / 6);
      return [kp.x + (x * c + z * sn) * ks, kp.z + (-x * sn + z * c) * ks];
    };
    const push = (kind: CatLeg['kind'], dur: number, a: [number, number, number], b: [number, number, number], abs: boolean, yaw: number): void => {
      const len = Math.hypot(b[0] - a[0], b[2] - a[2]);
      legs.push({ kind, t0: t, dur, ax: a[0], ay: a[1], az: a[2], bx: b[0], by: b[1], bz: b[2], abs, yaw, d0: dist, len });
      t += dur;
      if (kind === 'walk') dist += len;
    };
    const SPEED = 0.3;
    let at: [number, number, number] = [0, 0, 0];
    const walkTo = (x: number, z: number, lift = 0): void => {
      const len = Math.hypot(x - at[0], z - at[2]);
      if (len < 0.02) return;
      push('walk', len / SPEED, at, [x, lift, z], false, Math.atan2(x - at[0], z - at[2]));
      at = [x, lift, z];
    };
    const cellXZ = (id: number): [number, number] => {
      const c = hexToWorld(g.q[id] as number, g.r[id] as number);
      return [c.x * K, c.y * K];
    };
    const land = this.map.land;
    // Cells she can walk: what a hero can stand on, one terrace step at a time.
    const path = (from: number, to: Set<number>): number[] => {
      if (from < 0 || to.size === 0) return [];
      const prev = new Map<number, number>([[from, -1]]);
      const queue = [from];
      for (let i = 0; i < queue.length; i++) {
        const id = queue[i] as number;
        if (to.has(id)) {
          const out: number[] = [];
          for (let c = id; c >= 0; c = prev.get(c) as number) out.push(c);
          return out.reverse();
        }
        for (let k = id * 6; k < id * 6 + 6; k++) {
          const nb = g.nb[k] as number;
          if (nb < 0 || prev.has(nb) || !heroWalkable(s, nb) || (land && landStep(land, id, nb) < 0)) continue;
          prev.set(nb, id);
          queue.push(nb);
        }
      }
      return [];
    };
    const walkPath = (cells: number[]): void => {
      for (const id of cells) {
        const [x, z] = cellXZ(id);
        walkTo(x + (rng() - 0.5) * 0.16, z + (rng() - 0.5) * 0.16);
      }
    };
    const near = (id: number): number[] => {
      const out: number[] = [];
      for (let k = id * 6; k < id * 6 + 6; k++) {
        const nb = g.nb[k] as number;
        if (nb >= 0 && heroWalkable(s, nb)) out.push(nb);
      }
      // Nearest the keep first.
      return out.sort((a, b) => Math.hypot(...cellXZ(a)) - Math.hypot(...cellXZ(b)) || a - b);
    };
    // Asleep at the keep's foot on its front-right corner, on the lullaby's ring between two of its bowls: the side
    // the table camera faces (on the keep's flank it hid her, QA 2026-11-04), clear of the lectern before the door and
    // of the plinth's corner. The kittens curl beside her along the ring. Up, a sit, then out into the yard.
    const [sx, sz] = rot(0.72, 0.68);
    push('sleep', 18 + rng() * 8, [sx, 0, sz], [sx, 0, sz], false, Math.PI / 6 + Math.PI / 4);
    at = [sx, 0, sz];
    push('sit', 3.5, at, at, false, Math.PI / 6 + Math.PI / 4);
    // Out by the yard cell nearest her spot that the lectern doesn't stand on (it stands in the yard, not in the sim).
    const lecternCell = this.lecternCell;
    const homes = near(cellId(R, 0, 0)).filter((id) => `${g.q[id]},${g.r[id]}` !== lecternCell);
    homes.sort((a, b) => Math.hypot(cellXZ(a)[0] - sx, cellXZ(a)[1] - sz) - Math.hypot(cellXZ(b)[0] - sx, cellXZ(b)[1] - sz) || a - b);
    const home = homes[0] ?? -1;
    // A stretch of the walls: a stone or iron run of two or three cells, the nearest the keep.
    const wallAt = new Map<number, { lift: number }>();
    for (const st of s.structures) {
      if (st.destroyed || st.hp <= 0) continue;
      if (st.type === 'wall' && st.material >= 2) wallAt.set(cellId(R, st.q, st.r), { lift: WALL_WALK * this.lib.scaleOf(structKey('wall', st.level, st.material)) });
    }
    let run: number[] = [];
    let bestD = Infinity;
    for (const id of [...wallAt.keys()].sort((a, b) => a - b)) {
      const d = Math.hypot(...cellXZ(id));
      if (d >= bestD) continue;
      const next: number[] = [];
      for (let k = id * 6; k < id * 6 + 6; k++) if (wallAt.has(g.nb[k] as number)) next.push(g.nb[k] as number);
      if (!next.length || !near(id).length) continue;
      const w1 = next[Math.floor(rng() * next.length)] as number;
      const r = [id, w1];
      for (let k = w1 * 6; k < w1 * 6 + 6; k++) {
        const w2 = g.nb[k] as number;
        if (w2 !== id && wallAt.has(w2) && !next.includes(w2)) {
          r.push(w2);
          break;
        }
      }
      if (!near(r[r.length - 1] as number).length) continue;
      run = r;
      bestD = d;
    }
    if (home >= 0) {
      walkTo(...cellXZ(home));
      if (run.length) {
        const w0 = run[0] as number;
        const foot = near(w0)[0] as number;
        walkPath(path(home, new Set([foot])));
        const lift = wallAt.get(w0)?.lift ?? 0.43;
        const [wx, wz] = cellXZ(w0);
        // Up onto the wall in one leap, along it, a long sit looking out over the field, and down at the far end.
        push('leap', 0.55, at, [wx, lift, wz], false, Math.atan2(wx - at[0], wz - at[2]));
        at = [wx, lift, wz];
        for (const id of run.slice(1)) walkTo(...cellXZ(id), lift);
        push('sit', 8 + rng() * 6, at, at, false, Math.atan2(at[0], at[2]));
        const end = near(run[run.length - 1] as number)[0] as number;
        const [ex, ez] = cellXZ(end);
        push('leap', 0.5, at, [ex, 0, ez], false, Math.atan2(ex - at[0], ez - at[2]));
        at = [ex, 0, ez];
        walkPath(path(end, new Set([home])));
      }
      // The highest building: she sits at its foot on the side toward the keep, looking out at the table.
      let top: BattleState['structures'][number] | null = null;
      for (const st of s.structures) {
        if (st.destroyed || st.hp <= 0 || st.type === 'wall' || st.type === 'rampart' || st.type === 'gate' || STRUCTURES[st.type].category === 'trap') continue;
        if (!top || st.level > top.level || (st.level === top.level && (st.foot?.length ?? 1) > (top.foot?.length ?? 1))) top = st;
      }
      if (top) {
        const cells = [cellId(R, top.q, top.r), ...(top.foot ?? []).map((f) => cellId(R, f.q, f.r))];
        const goals = new Set(cells.flatMap((id) => near(id)));
        const route = path(home, goals);
        const last = route[route.length - 1];
        if (last !== undefined && goals.has(last)) {
          walkPath(route.slice(0, -1));
          const [lx, lz] = cellXZ(last);
          walkTo(lx, lz);
          const towards = cells.map(cellXZ).sort((a, b) => Math.hypot(a[0] - lx, a[1] - lz) - Math.hypot(b[0] - lx, b[1] - lz))[0] as [number, number];
          const k = top.foot ? 0.38 : 0.3;
          walkTo(lx + (towards[0] - lx) * k, lz + (towards[1] - lz) * k);
          push('sit', 10 + rng() * 8, at, at, false, Math.PI / 4);
          walkPath(path(last, new Set([home])));
        }
      }
      walkTo(...cellXZ(home));
    }
    // Back to her spot to sleep (the round starts there again).
    walkTo(sx, sz);
    // Where in her round she is goes by the seed alone, so a new layout doesn't make her jump far.
    return { legs, total: t, phase: (seed % 997) * 7.3, key: '' };
  }

  /** The cat and her kittens this frame. */
  private drawCat(s: BattleState, use: (k: string) => ModelBatch): void {
    const o = this.catOpts;
    if (!o || !this.opts.calm) return;
    // A new grid revision comes with every state the bastion takes in: the round is laid out again only if what
    // stands moved (a cheap signature, looked at once per revision).
    if (this.catRev !== s.gridRev || !this.catWalk || this.catLectern !== this.lecternCell) {
      this.catRev = s.gridRev;
      this.catLectern = this.lecternCell;
      let key = `${this.keepEra()}|${this.catLectern}`;
      for (const st of s.structures) key += `|${st.type}${st.q},${st.r}:${st.level}${st.material}${st.destroyed || st.hp <= 0 ? 'x' : ''}`;
      if (this.catWalk?.key !== key) {
        this.catWalk = this.catRoute(s);
        this.catWalk.key = key;
      }
    }
    const w = this.catWalk;
    if (w.total <= 0) return;
    const b = use('cat');
    const base = this.lib.scaleOf('cat');
    const p = this.catP;
    const legs = w.legs;
    for (let i = 0; i <= o.kittens; i++) {
      const t = ((((this.now + w.phase - i * 1.15) % w.total) + w.total) % w.total);
      let li = 0;
      while (li < legs.length - 1 && (legs[li + 1] as CatLeg).t0 <= t) li++;
      const l = legs[li] as CatLeg;
      if (l.kind === 'hidden') continue;
      const f = l.dur > 0 ? Math.min(1, (t - l.t0) / l.dur) : 0;
      let x = l.ax + (l.bx - l.ax) * f;
      let z = l.az + (l.bz - l.az) * f;
      let lift = l.ay + (l.by - l.ay) * f;
      let yaw = l.yaw;
      let frame = CAT_SIT;
      if (l.kind === 'walk') frame = Math.floor((l.d0 + l.len * f) / 0.045) & 3;
      else if (l.kind === 'leap') {
        frame = CAT_LEAP;
        lift += Math.sin(f * Math.PI) * 0.22;
      } else if (l.kind === 'sleep') frame = CAT_SLEEP;
      // The kittens: beside her on the way, curled up round her asleep, sat by her.
      if (i > 0) {
        const side = i === 2 ? -1 : 1;
        const off = l.kind === 'walk' || l.kind === 'leap' ? 0.07 * side : i === 3 ? 0 : 0.13 * side;
        const back = l.kind === 'walk' || l.kind === 'leap' ? 0 : i === 3 ? -0.12 : 0.02;
        const c = Math.cos(yaw), sn = Math.sin(yaw);
        x += c * off + sn * back;
        z += -sn * off + c * back;
        if (l.kind === 'sleep') yaw += i * 1.7;
      }
      const ground = this.heightAt(x / (WORLD_PER_HEX / 1000), z / (WORLD_PER_HEX / 1000));
      const y = l.abs ? lift : ground + lift;
      p.set(x, y, z);
      const k = i === 0 ? base : base * 0.55;
      this.compose(p, yaw, k);
      b.add(this.m, frame);
      if (!l.abs) this.shadows.add(x, y, z, i === 0 ? 0.32 : 0.2);
    }
  }

  setWorks(w: WorkView[]): void {
    this.works = w;
    this.dirty = true;
  }

  /** Terraced land: the golems out gathering, walking between a store and what they gather (docs/CONSTRUCCION.md §5). */
  private gathers: GatherView[] = [];
  setGathers(g: GatherView[]): void {
    this.gathers = g;
    this.dirty = true;
  }

  /** The offerings lit at the keep this Marea (bastion): a brazier burns by the keep for each. */
  private offerings: string[] = [];
  setOfferings(kinds: readonly string[]): void {
    if (kinds.join() === this.offerings.join()) return;
    this.offerings = kinds.slice();
    if (this.map.kind === 'bastion' && !this.crypt) this.gearFx.setBraziers(this.offeringSpots());
    this.dirty = true;
  }

  /** The group's trophies on the keep (bastion): the gate bell from the cellars (null: not raised yet). */
  private bell: { key: string; model: BellTrophy } | null = null;
  /** The Furrows' trophy: the table of the Furrows by the keep's foot (null: the centre never fell). */
  private table: { key: string; model: Model } | null = null;
  setTrophies(t: { bell: BellLook | null; table?: TableLook | null; shelf?: ShelfLook | null; stone?: GlyphStoneLook | null; fallen?: string[] | null; barrels?: BarrelLook | null; clues?: number | null; lanterns?: boolean | null; facesLantern?: boolean | null; corral?: boolean | null; post?: boolean | null; spear?: boolean | null; ragna?: 'home' | 'call' | null; mapTable?: boolean | null; vigilBanner?: boolean | null; harpoon?: boolean | null; beacon?: boolean | null; mineCart?: boolean | null; railsGuard?: boolean | null; railsKey?: boolean | null; railsHammer?: boolean | null; railsOre?: boolean | null; bucket?: boolean | null; jar?: boolean | null; banner?: 'grande' | 'mediana' | 'chica' | null; shoreNet?: boolean | null; shoreOar?: boolean | null; shoreBoat?: boolean | null; shoreHeart?: { q: number; r: number } | null; shoreFang?: { q: number; r: number } | null; gatePlaque?: { at: { q: number; r: number }; title: string; names: string[] } | null; lidSign?: boolean | null; lidClapper?: boolean | null; lidShard?: boolean | null; lidBell?: boolean | null; patio?: Record<string, PatioSlot> | null; statues?: StatueLook | null; company?: { q: number; r: number } | null; way?: WayLook | null; tormo?: TormoLook | null; hunt?: HuntLook | null; road?: RoadLook | null; grove?: GroveLook | null }): void {
    this.setPatio(t.patio ?? null);
    this.setStatues(t.statues ?? null);
    this.setCompany(t.company ?? null);
    this.setWay(t.way ?? null);
    this.setTormoTrophy(t.tormo ?? null);
    this.setHunt(t.hunt ?? null);
    this.setRoad(t.road ?? null);
    this.setGrove(t.grove ?? null);
    this.setTable(t.table ?? null);
    this.setShelf(t.shelf ?? null);
    this.setGlyphStone(t.stone ?? null);
    this.setFallen(t.fallen ?? null, t.ragna ?? null);
    this.setBarrels(t.barrels ?? null);
    this.setClueBoard(t.clues ?? null);
    this.setLanterns(t.lanterns ? true : null);
    this.setFacesLantern(t.facesLantern ? true : null);
    this.setCorral(t.corral ? true : null);
    this.setPost(t.post ? true : null);
    this.setSpear(t.spear ? true : null);
    this.setMapTable(t.mapTable ? true : null);
    this.setVigilBanner(t.vigilBanner ? true : null);
    this.setHarpoon(t.harpoon ? true : null);
    this.setBeacon(t.beacon ? true : null);
    this.setBucket(t.bucket ? { jar: !!t.jar, banner: t.banner ?? null } : null);
    this.setShoreNet(!!t.shoreNet, !!t.shoreOar, !!t.shoreBoat);
    this.setShoreHeart(t.shoreHeart ?? null);
    this.setShoreFang(t.shoreFang ?? null);
    this.setGatePlaque(t.gatePlaque ?? null);
    this.setLidSign(t.lidSign ? { clapper: !!t.lidClapper, shard: !!t.lidShard } : null);
    this.setLidBell(!!t.lidBell);
    this.setMineCart(t.mineCart ? { guard: !!t.railsGuard, key: !!t.railsKey, hammer: !!t.railsHammer, ore: !!t.railsOre } : null);
    const key = t.bell ? `${!!t.bell.banners}${!!t.bell.sash}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.bell?.key ?? '') === key) return;
    if (this.bell) {
      this.scene.remove(this.bell.model.root);
      this.bell.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.bell = null;
    }
    if (t.bell) {
      const model = gateBellTrophy(t.bell);
      bakeStill(model, true);
      // In the keep's own frame: it hangs from the keep's front corner.
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.bell = { key, model };
    }
    this.dirty = true;
  }

  /**
   * The patio of deeds round the keep: where each saga's plinth stands (the client picks the cells, docs/SAGAS.md
   * «El patio de las gestas»). A change of place takes every saga trophy down so the setters raise it again there.
   */
  private patio: Record<string, PatioSlot> = {};
  private patioKey = '';
  private plinths: THREE.Group | null = null;
  private setPatio(patio: Record<string, PatioSlot> | null): void {
    if (this.map.kind !== 'bastion' || this.crypt) return;
    const next = patio ?? {};
    const key = Object.keys(next)
      .sort()
      .map((k) => { const x = next[k] as PatioSlot; return `${k}${x.q},${x.r}${x.small ? 's' : ''}${x.waiting ? 'w' : ''}${x.bare ? 'b' : ''}`; })
      .join('|');
    if (key === this.patioKey) return;
    this.patioKey = key;
    this.patio = next;
    // The yard's pieces keep clear of the plinths: they look again at the next drawing.
    this.yardRev = -1;
    if (this.plinths) {
      this.scene.remove(this.plinths);
      this.plinths.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.plinths = null;
    }
    // All the plinths baked as one model: nine baked one by one were 27 more calls on the bastion (167 → 194 with
    // every trophy, phone profile, 2026-11-02); together they are 3 (docs/RENDIMIENTO.md).
    const each: Model[] = [];
    const root = new THREE.Group();
    for (const slot of Object.values(next)) {
      if (slot.bare) continue;
      const model = sagaPlinth({ waiting: slot.waiting });
      this.compose(this.patioAt(slot), 0, PLINTH_SCALE * (slot.small ? PLINTH_SMALL : 1));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      root.add(model.root);
      each.push(model);
    }
    if (each.length) {
      bakeStill({ root, height: 1, tick: (n) => each.forEach((m) => m.tick(n)) }, true);
      root.updateMatrixWorld(true);
      this.scene.add(root);
      this.plinths = root;
    }
    // Down with what stood elsewhere: the calls that follow put it up on its plinth.
    this.setClueBoard(null);
    this.setLanterns(null);
    this.setFacesLantern(null);
    this.setCorral(null);
    this.setPost(null);
    this.setSpear(null);
    this.setMapTable(null);
    this.setVigilBanner(null);
    this.setHarpoon(null);
    this.setBeacon(null);
    this.setBucket(null);
    this.setShoreNet(false);
    this.setLidSign(null);
    this.setMineCart(null);
    this.setFallen(null);
    this.setBarrels(null);
    this.setTable(null);
    this.setShelf(null);
    this.setGlyphStone(null);
    this.dirty = true;
  }

  /**
   * The paths' statues (docs/SENDAS.md, «El legado»): each hero who walked its path to the end, its miniature in light
   * stone on the `sendas` plinth (up to four abreast), and the plaque with their names and the title under them. The
   * statues are the hero models' own shapes with one stone material, baked still with the plinth's yard: a handful of
   * calls, nothing that moves.
   */
  private statues: THREE.Group | null = null;
  private statueKey = '';
  private setStatues(st: StatueLook | null): void {
    const slot = this.patio.sendas;
    const key = st && slot ? `${slot.q},${slot.r}${slot.small ? 's' : ''}|${st.title}|${st.figures.map((f) => `${f.key}/${f.name}`).join(',')}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || key === this.statueKey) return;
    this.statueKey = key;
    if (this.statues) {
      this.scene.remove(this.statues);
      this.statues.traverse((o) => {
        const m = o as THREE.Mesh;
        const mat = m.material as THREE.MeshStandardMaterial | undefined;
        if (mat?.map) {
          m.geometry?.dispose();
          mat.map.dispose();
          mat.dispose();
        }
      });
      this.statues = null;
    }
    if (!st || !slot || !st.figures.length) return;
    const root = new THREE.Group();
    root.name = 'senda-statues';
    const ps = PLINTH_SCALE * (slot.small ? PLINTH_SMALL : 1);
    const at = this.patioAt(slot);
    const top = at.clone().setY(at.y + PLINTH_TOP * ps);
    const figs = st.figures.slice(0, 4);
    const yaw = Math.PI / 4;
    const side = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw));
    figs.forEach((f, i) => {
      const g = this.lib.ghostOf(f.key, STATUE_STONE);
      const off = (i - (figs.length - 1) / 2) * 0.42 * ps;
      const p = top.clone().addScaledVector(side, off);
      this.compose(p, yaw, this.lib.scaleOf(f.key) * 0.92 * ps);
      g.matrixAutoUpdate = false;
      g.matrix.copy(this.m);
      root.add(g);
    });
    // The plaque at the plinth's foot, its face to the table's camera.
    const plaque = plaqueTrophy(st.title, figs.map((f) => f.name));
    plaque.root.name = 'senda-plaque';
    bakeStill(plaque, true);
    const front = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(0.78 * ps);
    this.compose(at.clone().add(front), yaw, this.lib.scaleOf(this.center()) * 0.34 * ps);
    plaque.root.matrixAutoUpdate = false;
    plaque.root.matrix.copy(this.m);
    root.add(plaque.root);
    root.updateMatrixWorld(true);
    this.scene.add(root);
    this.statues = root;
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts
      ?.load('48px "IM Fell English SC"')
      .then(() => {
        if (this.statues !== root) return;
        plaque.redraw();
        this.dirty = true;
      })
      .catch(() => undefined);
    this.dirty = true;
  }

  /**
   * The warrior's path's trophy (docs/sendas/guerrero.md §10): the Company's eleven shields in a line over the main
   * road's gate, their red crosses to the table's camera, a breath of red on each. One merged mesh.
   */
  private company: THREE.Mesh | null = null;
  private companyKey = '';
  private companyAt: THREE.Vector3[] = [];

  private companyHalo: THREE.Mesh | null = null;

  private setCompany(at: { q: number; r: number } | null): void {
    const key = at ? `${at.q},${at.r}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || key === this.companyKey) return;
    this.companyKey = key;
    for (const m of [this.company, this.companyHalo]) {
      if (!m) continue;
      this.scene.remove(m);
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
    this.company = null;
    this.companyHalo = null;
    this.companyAt = [];
    if (!at) return;
    const f = hexToWorld(at.q, at.r);
    const len = Math.hypot(f.x, f.y) || 1;
    // Along the wall (across the way in), over the arch: a gentle arc rising to the middle, Harl's in the middle.
    const ax = -f.y / len;
    const ay = f.x / len;
    const kit = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    // Facing in, toward the keep, leaning back so the table's camera reads their faces from any side.
    const yaw = Math.atan2(f.x, f.y) + Math.PI;
    const ground = this.heightAt(f.x, f.y);
    const local = (x: number, y: number, z: number, sx: number, sy: number, sz: number, rz = 0): THREE.Matrix4 =>
      new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, rz)), new THREE.Vector3(sx, sy, sz));
    for (let i = 0; i < 11; i++) {
      const k = (i - 5) * 520;
      const sx = f.x * (1 - 380 / len) + ax * k;
      const sy = f.y * (1 - 380 / len) + ay * k;
      const p = simToWorld(sx, sy, new THREE.Vector3(), ground + 1.85 + (25 - (i - 5) * (i - 5)) * 0.016);
      const big = i === 5 ? 1.2 : 1;
      const m = new THREE.Matrix4().compose(p, new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.85, yaw, 0, 'YXZ')), new THREE.Vector3(1.5 * big, 1.5 * big, 1.5 * big));
      // A heater shield: a broad top, its point below (a square turned on its corner), the iron rim behind.
      kit.add(box, '#2b2f36', m, local(0, 0.06, -0.03, 0.46, 0.32, 0.03));
      kit.add(box, '#2b2f36', m, local(0, -0.12, -0.03, 0.33, 0.33, 0.03, Math.PI / 4));
      kit.add(box, '#c9c6c0', m, local(0, 0.06, 0, 0.42, 0.28, 0.04));
      kit.add(box, '#c9c6c0', m, local(0, -0.12, 0, 0.29, 0.29, 0.04, Math.PI / 4));
      // The Company's red cross of cloth, tied on.
      kit.add(box, '#c23224', m, local(0, -0.02, 0.026, 0.07, 0.42, 0.015));
      kit.add(box, '#c23224', m, local(0, 0.08, 0.027, 0.36, 0.07, 0.015));
      this.companyAt.push(p);
    }
    // Under the middle one, the Company's banner: red, its mark in pale thread (a line, eleven notches, a peak over them).
    const mid = this.companyAt[5] as THREE.Vector3;
    const bm = new THREE.Matrix4().compose(mid.clone().setY(mid.y - 1.25), new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.3, yaw, 0, 'YXZ')), new THREE.Vector3(1, 1, 1));
    kit.add(box, '#3b3430', bm, local(0, 0.62, -0.02, 1.15, 0.06, 0.06));
    kit.add(box, '#b12c22', bm, local(0, 0, 0, 0.95, 1.2, 0.03));
    kit.add(box, '#b12c22', bm, local(-0.24, -0.66, 0, 0.47, 0.2, 0.03, 0.35));
    kit.add(box, '#b12c22', bm, local(0.24, -0.66, 0, 0.47, 0.2, 0.03, -0.35));
    kit.add(box, '#e8e4dc', bm, local(0, -0.12, 0.02, 0.68, 0.045, 0.01));
    for (let i = 0; i < 11; i++) kit.add(box, '#e8e4dc', bm, local(-0.3 + i * 0.06, -0.17, 0.02, 0.02, 0.09, 0.01));
    for (const sgn of [-1, 1]) kit.add(box, '#e8e4dc', bm, local(sgn * 0.12, 0.14, 0.02, 0.3, 0.045, 0.01, -sgn * 0.7));
    box.dispose();
    const mesh = kit.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.05, flatShading: true, emissive: '#2a0a06' }), 'company-shields');
    if (!mesh) return;
    mesh.castShadow = true;
    this.scene.add(mesh);
    this.company = mesh;
    // A breath of red light behind the row (one additive quad, no light: docs/RENDIMIENTO.md).
    const a = this.companyAt[0] as THREE.Vector3;
    const b = this.companyAt[10] as THREE.Vector3;
    const halo = new THREE.Mesh(
      new THREE.PlaneGeometry(a.distanceTo(b) + 2.4, 2.2),
      new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0.16, depthWrite: false, blending: THREE.AdditiveBlending, map: haloTexture() }),
    );
    halo.position.copy(mid).add(new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(-0.35));
    halo.rotation.set(0, yaw, 0);
    halo.name = 'company-halo';
    this.scene.add(halo);
    this.companyHalo = halo;
    this.dirty = true;
  }

  /** The table camera's yaw that looks at the eleven shields' faces (from the keep's side toward the gate). */
  companyYaw(): number | null {
    const c = this.companyAt[5];
    if (!c) return null;
    return Math.atan2(-c.x, -c.z);
  }

  /** Where the eleven shields hang (the camera turns to them when they're new), null if they don't. */
  companyCenter(): THREE.Vector3 | null {
    return this.companyAt[5]?.clone() ?? null;
  }

  /** The eleven shields just hung over the gate: a red flash along them. */
  flareCompany(): void {
    for (const p of this.companyAt) this.vfx.burst(p, { n: 4, color: '#ff8a6e', speed: 0.03, up: 0.04, size: 0.05, life: 22, gravity: 0.004, spread: 0.3 });
    const mid = this.companyAt[5];
    if (mid) this.flare(mid, '#ff8a6e', 5, 24);
    this.dirty = true;
  }

  /**
   * The paladin's path's trophy (WayLook): one merged mesh at the game camera's scale (half again a figure's size, as
   * the Company's shields), facing the bastion's middle; a soft white and red halo behind it (one additive quad, no light).
   */
  private way: THREE.Mesh | null = null;
  private wayHalo: THREE.Mesh | null = null;
  private wayKey = '';
  private wayAt: THREE.Vector3 | null = null;
  private wayFace = Math.PI / 4;

  private setWay(t: WayLook | null): void {
    const key = t ? `${t.kind}:${t.marit ? 1 : 0}:${t.at.q},${t.at.r}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || key === this.wayKey) return;
    this.wayKey = key;
    for (const m of [this.way, this.wayHalo]) {
      if (!m) continue;
      this.scene.remove(m);
      // (The trophy carries its banner's cloth as a child mesh.)
      m.traverse((o) => {
        if (!(o instanceof THREE.Mesh)) return;
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      });
    }
    this.way = null;
    this.wayHalo = null;
    this.wayAt = null;
    if (!t) return;
    const f = hexToWorld(t.at.q, t.at.r);
    const len = Math.hypot(f.x, f.y);
    // Facing the bastion's middle (where the camera stands to look at it); at the middle, the camera's own side.
    const yaw = len > 600 ? Math.atan2(-f.x, -f.y) : Math.PI / 4;
    this.wayFace = yaw;
    const p = simToWorld(f.x, f.y, new THREE.Vector3(), this.heightAt(f.x, f.y));
    const big = 1.55;
    const kit = new Kit();
    // The banner's white cloth on its own: lit cold from within, so the keep's warm fire never turns it pink.
    const cloth = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const at = (x: number, y: number, z: number, ry = 0, s = 1): THREE.Matrix4 =>
      new THREE.Matrix4().compose(
        p.clone().add(new THREE.Vector3(x, 0, z).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw).multiplyScalar(big)).setY(p.y + y * big),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yaw + ry, 0)),
        new THREE.Vector3(s * big, s * big, s * big),
      );
    // A low plinth of pale stone under all three.
    kit.add(box, '#8d9299', at(0, 0.08, 0), new THREE.Matrix4().makeScale(2.3, 0.16, 0.9));
    kit.add(box, '#a2a7ad', at(0, 0.18, 0), new THREE.Matrix4().makeScale(2.1, 0.05, 0.75));
    if (t.kind === 'banner') {
      // The Way's banner on its pole, leaning back a little so the table's camera reads its eye.
      kit.add(box, '#2a2e35', at(0, 1.25, -0.12), new THREE.Matrix4().makeScale(0.06, 2.2, 0.06));
      kit.add(box, '#2a2e35', at(0, 2.38, -0.12), new THREE.Matrix4().makeScale(0.12, 0.08, 0.12));
      const m = at(0, 1.55, -0.08).multiply(new THREE.Matrix4().makeRotationX(-0.12));
      wayBanner(kit, m, {}, cloth);
    } else {
      // Marit's old shield on a stand of dark wood, leaning back.
      kit.add(box, '#3a312b', at(0, 0.55, -0.22), new THREE.Matrix4().makeScale(0.08, 0.8, 0.08));
      kit.add(box, '#3a312b', at(0, 0.3, -0.05), new THREE.Matrix4().makeScale(0.5, 0.06, 0.3));
      maritShield(kit, at(0, 0.82, -0.1).multiply(new THREE.Matrix4().makeRotationX(-0.28)), {});
    }
    // The monastery's wheel, upright on its rim on the right, chocked.
    damWheel(kit, at(0.95, 0.7, -0.05, Math.PI / 2, 0.5), {});
    kit.add(box, '#3a312b', at(0.95, 0.24, 0.22), new THREE.Matrix4().makeScale(0.5, 0.1, 0.12));
    // Marit at its left, standing, if she stayed.
    if (t.marit) mergeFigure(kit, 'marit', at(-0.85, 0.2, 0.05, 0.25, SCALE.hero));
    box.dispose();
    const mesh = kit.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.05, flatShading: true, emissive: '#262e38' }), 'way-trophy');
    if (!mesh) return;
    mesh.castShadow = true;
    const cm = cloth.size ? cloth.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0, color: '#c4cfdd', emissive: '#71819a', side: THREE.DoubleSide }), 'way-trophy-cloth') : null;
    if (cm) {
      cm.castShadow = true;
      mesh.add(cm);
    }
    this.scene.add(mesh);
    this.way = mesh;
    this.wayAt = p.clone().setY(p.y + 1.3 * big);
    const halo = new THREE.Mesh(
      new THREE.PlaneGeometry(4.2, 3.6),
      new THREE.MeshBasicMaterial({ color: '#dfe8f2', transparent: true, opacity: 0.14, depthWrite: false, blending: THREE.AdditiveBlending, map: haloTexture() }),
    );
    halo.position.copy(this.wayAt).add(new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(-0.6));
    halo.rotation.set(0, yaw, 0);
    halo.name = 'way-halo';
    this.scene.add(halo);
    this.wayHalo = halo;
    this.dirty = true;
  }

  /**
   * The barbarian's path's trophy (HuntLook): one merged mesh at the game camera's scale (the Way's size), facing the
   * bastion's middle: a plinth of dark driftwood on stone; the axe in its log or the diary on its stone; the wooden boar.
   * Its pale parts (the axe's blade, the diary's pages, the strokes, the boar's tusks) lit cold from within, so the keep's
   * warm fire never turns them cream or pink; a cold halo behind it (one additive quad, no light).
   */
  private hunt: THREE.Mesh | null = null;
  private huntHalo: THREE.Mesh | null = null;
  private huntKey = '';
  private huntAt: THREE.Vector3 | null = null;
  private huntFace = Math.PI / 4;

  private setHunt(t: HuntLook | null): void {
    const key = t ? `${t.kind}:${t.at.q},${t.at.r}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || key === this.huntKey) return;
    this.huntKey = key;
    for (const m of [this.hunt, this.huntHalo]) {
      if (!m) continue;
      this.scene.remove(m);
      m.traverse((o) => {
        if (!(o instanceof THREE.Mesh)) return;
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      });
    }
    this.hunt = null;
    this.huntHalo = null;
    this.huntAt = null;
    if (!t) return;
    const f = hexToWorld(t.at.q, t.at.r);
    const len = Math.hypot(f.x, f.y);
    const yaw = len > 600 ? Math.atan2(-f.x, -f.y) : Math.PI / 4;
    this.huntFace = yaw;
    const p = simToWorld(f.x, f.y, new THREE.Vector3(), this.heightAt(f.x, f.y));
    const big = 1.55;
    const kit = new Kit();
    const cold = new Kit();
    const at = (x: number, y: number, z: number, ry = 0, s = 1): THREE.Matrix4 =>
      new THREE.Matrix4().compose(
        p.clone().add(new THREE.Vector3(x, 0, z).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw).multiplyScalar(big)).setY(p.y + y * big),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yaw + ry, 0)),
        new THREE.Vector3(s * big, s * big, s * big),
      );
    const fur = new Kit();
    huntTrophy(kit, at(0, 0, 0), t.kind === 'axe', cold, fur);
    const mesh = kit.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.06, flatShading: true, emissive: '#232a33' }), 'hunt-trophy');
    if (!mesh) return;
    mesh.castShadow = true;
    const cm = cold.size ? cold.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.5, metalness: 0.2, color: '#c4cfdd', emissive: '#6f7f98', flatShading: true }), 'hunt-trophy-cold') : null;
    // The wolf's skin: its greys tinted cold and a little lit from within (a plain grey went pink under the keep's fire),
    // but less than the pale parts, so its dark saddle and its pale paws still read apart.
    const fm = fur.size ? fur.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.95, metalness: 0, color: '#a2b3cc', emissive: '#3a4657', flatShading: true }), 'hunt-trophy-fur') : null;
    if (fm) {
      fm.castShadow = true;
      mesh.add(fm);
    }
    if (cm) {
      cm.castShadow = true;
      mesh.add(cm);
    }
    this.scene.add(mesh);
    this.hunt = mesh;
    this.huntAt = p.clone().setY(p.y + 1.2 * big);
    const halo = new THREE.Mesh(
      new THREE.PlaneGeometry(4.0, 3.0),
      new THREE.MeshBasicMaterial({ color: '#dfe8f2', transparent: true, opacity: 0.13, depthWrite: false, blending: THREE.AdditiveBlending, map: haloTexture() }),
    );
    halo.position.copy(this.huntAt).add(new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(-0.6));
    halo.rotation.set(0, yaw, 0);
    halo.name = 'hunt-halo';
    this.scene.add(halo);
    this.huntHalo = halo;
    this.dirty = true;
  }

  /** The table camera's yaw that looks at the Long Hunt's trophy's face. */
  huntYaw(): number | null {
    return this.huntAt ? this.huntFace : null;
  }

  /** Where the Long Hunt's trophy stands (the camera turns to it when it is new), null if it doesn't. */
  huntCenter(): THREE.Vector3 | null {
    return this.huntAt?.clone() ?? null;
  }

  /** The Long Hunt's trophy just set: a cold flash over it, a ring of ash at its foot, red sparks. */
  flareHunt(): void {
    const c = this.huntAt;
    if (!c) return;
    this.vfx.burst(c, { n: 12, color: '#ff8a6e', speed: 0.03, up: 0.05, size: 0.06, life: 24, gravity: 0.004, spread: 0.6 });
    this.vfx.ring(c.clone().setY(c.y - 1.3), 0.3, 2.4, '#c9ced4', 12, 0.7);
    this.flare(c, '#dfe8f2', 5, 24);
    this.dirty = true;
  }

  /**
   * The druid's path's trophy (GroveLook, engine/grove-trophy.ts): the body, its leaves and Olmo's face (three meshes,
   * every piece lit by its own colour so the omen's red never turns it pink), facing out from the keep, at the game
   * camera's scale; a soft green halo behind (one additive quad).
   */
  private grove: GroveTrophy | null = null;
  private groveHalo: THREE.Mesh | null = null;
  private groveKey = '';
  private groveAt: THREE.Vector3 | null = null;
  private groveFace = Math.PI / 4;

  private setGrove(t: GroveLook | null): void {
    const key = t ? `${t.kind}:${t.kaela ? 1 : 0}:${t.at.q},${t.at.r}:${t.line ?? ''}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || key === this.groveKey) return;
    this.groveKey = key;
    if (this.grove) {
      this.scene.remove(this.grove.root);
      this.grove.dispose();
    }
    if (this.groveHalo) {
      this.scene.remove(this.groveHalo);
      this.groveHalo.geometry.dispose();
      (this.groveHalo.material as THREE.Material).dispose();
    }
    this.grove = null;
    this.groveHalo = null;
    this.groveAt = null;
    if (!t) return;
    const f = hexToWorld(t.at.q, t.at.r);
    const len = Math.hypot(f.x, f.y);
    // (It faces along the yard's ring, sideways to the keep: facing the keep or away from it, the camera that turns to
    // it had the keep, the statues and the heroes idling there lined up behind its crowns.)
    const yaw = len > 600 ? Math.atan2(f.x, f.y) + Math.PI / 2 : Math.PI / 4;
    this.groveFace = yaw;
    const p = simToWorld(f.x, f.y, new THREE.Vector3(), this.heightAt(f.x, f.y));
    const big = 1.5;
    const built = buildGroveTrophy(p, yaw, big, t);
    this.scene.add(built.root);
    this.grove = built;
    this.groveAt = p.clone().setY(p.y + 1.25 * big);
    const halo = new THREE.Mesh(
      new THREE.PlaneGeometry(4.6, 4.0),
      new THREE.MeshBasicMaterial({ color: '#cfeec0', transparent: true, opacity: 0.12, depthWrite: false, blending: THREE.AdditiveBlending, map: haloTexture() }),
    );
    halo.position.copy(this.groveAt).add(new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(-0.7));
    halo.rotation.set(0, yaw, 0);
    halo.name = 'grove-halo';
    this.scene.add(halo);
    this.groveHalo = halo;
    // The carved line in the slab's own font, once it loads.
    (document as Document & { fonts?: FontFaceSet }).fonts
      ?.load('48px "IM Fell English SC"')
      .then(() => {
        if (this.grove !== built) return;
        built.redraw();
        this.dirty = true;
      })
      .catch(() => undefined);
    this.dirty = true;
  }

  /** The table camera's yaw that looks at the druid's trophy's face. */
  groveYaw(): number | null {
    return this.groveAt ? this.groveFace : null;
  }

  /** Where the druid's trophy stands (the camera turns to it when it is new), null if it doesn't. */
  groveCenter(): THREE.Vector3 | null {
    return this.groveAt?.clone() ?? null;
  }

  /** The druid's trophy just set: a green flash over it, leaves rising. */
  flareGrove(): void {
    const c = this.groveAt;
    if (!c) return;
    this.vfx.burst(c, { n: 16, color: '#9ee070', speed: 0.03, up: 0.05, size: 0.07, life: 26, gravity: 0.002, spread: 0.7 });
    this.vfx.ring(c.clone().setY(c.y - 1.8), 0.3, 2.6, '#d8f8c0', 10, 0.7);
    this.flare(c, '#e8ffe0', 5, 24);
    this.dirty = true;
  }

  /** The table camera's yaw that looks at the Way's trophy's face. */
  wayYaw(): number | null {
    return this.wayAt ? this.wayFace : null;
  }

  /** Where the Way's trophy stands (the camera turns to it when it is new), null if it doesn't. */
  wayCenter(): THREE.Vector3 | null {
    return this.wayAt?.clone() ?? null;
  }

  /**
   * Tormo by the gate (the artificer's path's trophy): his own meshes a bone (the fight's builder, standing still), his
   * iron and stone lit a little cold from within so the keep's warm fire never turns the stone cream; his eyes blue, the
   * heart red through the slit, both marks on his shoulder; the plaque with the names at his feet.
   */
  private tormoT: { root: THREE.Group; dispose(): void } | null = null;
  private tormoPlaque: THREE.Object3D | null = null;
  private tormoKey = '';
  private tormoAt: THREE.Vector3 | null = null;
  private tormoFace = Math.PI / 4;

  private setTormoTrophy(t: TormoLook | null): void {
    const key = t ? `${t.at.q},${t.at.r}|${t.face.q},${t.face.r}|${t.old ? 1 : 0}|${t.names.join(',')}|${t.title}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || key === this.tormoKey) return;
    this.tormoKey = key;
    if (this.tormoT) {
      this.scene.remove(this.tormoT.root);
      this.tormoT.dispose();
      this.tormoT = null;
    }
    if (this.tormoPlaque) {
      this.scene.remove(this.tormoPlaque);
      this.tormoPlaque.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) {
          m.geometry.dispose();
          (m.material as THREE.Material).dispose();
        }
      });
      this.tormoPlaque = null;
    }
    this.tormoAt = null;
    if (!t) return;
    const f = hexToWorld(t.at.q, t.at.r);
    const to = hexToWorld(t.face.q, t.face.r);
    const yaw = Math.atan2(to.x - f.x, to.y - f.y);
    this.tormoFace = yaw;
    const p = simToWorld(f.x, f.y, new THREE.Vector3(), this.heightAt(f.x, f.y));
    const T = buildTormo({ eyes: 'blue', clan: true, ...(t.old ? { dust: 'old' as const } : {}) });
    T.root.scale.setScalar(SCALE.hero * 0.92);
    T.root.position.copy(p);
    T.root.rotation.y = yaw;
    // A heavy rest: the weight on one leg, a hand open at his side.
    T.pivots.legR.rotation.x = -0.16;
    T.pivots.shinR.rotation.x = 0.22;
    T.pivots.legL.rotation.z = 0.05;
    T.pivots.legR.rotation.z = -0.05;
    T.pivots.armL.rotation.z = -0.16;
    T.pivots.armR.rotation.z = 0.12;
    T.pivots.armR.rotation.x = -0.22;
    T.pivots.foreR.rotation.x = -0.3;
    // Leaning back a little, his face up toward the walls (the table's camera is high: so it meets his eyes).
    T.pivots.head.rotation.x = -0.32;
    T.pivots.body.rotation.x = -0.1;
    T.root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = true;
      const mat = m.material as THREE.MeshStandardMaterial;
      // (A touch cold from within, so the keep's fire never turns the slate cream; more than this and the whole of him
      // went one flat blue-grey, his plates and rivets lost.)
      if (mat.isMeshStandardMaterial) mat.emissive.set('#0c1116');
    });
    // His plinth: two courses of the quarry's slate, a band of iron with its rivets between them, a step at the front.
    const PL = 0.34;
    const inv = 1 / T.root.scale.x;
    const pk = new Kit();
    const hexG = new THREE.CylinderGeometry(1, 1, 1, 6);
    const boxG = new THREE.BoxGeometry(1, 1, 1);
    const sph = new THREE.SphereGeometry(0.5, 6, 4);
    const at = (x: number, y: number, z: number, sx: number, sy: number, sz: number, yaw = 0): THREE.Matrix4 =>
      new THREE.Matrix4().compose(new THREE.Vector3(x * inv, y * inv, z * inv), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yaw, 0)), new THREE.Vector3(sx * inv, sy * inv, sz * inv));
    pk.add(hexG, '#5d636b', at(0, -PL + 0.09, 0, 1.18, 0.18, 1.18, Math.PI / 6));
    pk.add(hexG, '#2c3137', at(0, -PL + 0.2, 0, 1.04, 0.05, 1.04, Math.PI / 6));
    pk.add(hexG, '#7a8088', at(0, -PL + 0.28, 0, 0.98, 0.12, 0.98, Math.PI / 6));
    pk.add(hexG, '#959ba2', at(0, -0.012, 0, 0.96, 0.025, 0.96, Math.PI / 6));
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      pk.add(sph, '#a9b2bc', at(Math.sin(a) * 1.0, -PL + 0.2, Math.cos(a) * 1.0, 0.07, 0.07, 0.07));
    }
    pk.add(boxG, '#6c727a', at(0, -PL + 0.06, 1.2, 1.0, 0.12, 0.34));
    const plinthMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.1, flatShading: true });
    const plinth = pk.build(plinthMat, 'tormo-plinth');
    hexG.dispose();
    boxG.dispose();
    sph.dispose();
    if (plinth) {
      plinth.receiveShadow = true;
      plinth.castShadow = true;
      T.root.add(plinth);
    }
    T.root.position.y += PL;
    this.scene.add(T.root);
    this.tormoT = {
      root: T.root,
      dispose: () => {
        T.dispose();
        plinth?.geometry.dispose();
        plinthMat.dispose();
      },
    };
    this.tormoAt = p.clone().setY(p.y + 2.0);
    // The plaque at his feet, its face to the table's camera.
    const plaque = plaqueTrophy(t.title, t.names);
    plaque.root.name = 'tormo-plaque';
    bakeStill(plaque, true);
    const front = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(1.75);
    this.compose(p.clone().add(front), Math.PI / 4, this.lib.scaleOf(this.center()) * 0.3);
    plaque.root.matrixAutoUpdate = false;
    plaque.root.matrix.copy(this.m);
    this.scene.add(plaque.root);
    this.tormoPlaque = plaque.root;
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts
      ?.load('48px "IM Fell English SC"')
      .then(() => {
        if (this.tormoPlaque !== plaque.root) return;
        plaque.redraw();
        this.dirty = true;
      })
      .catch(() => undefined);
    this.dirty = true;
  }

  /** The table camera's yaw that looks at Tormo's face. */
  tormoYaw(): number | null {
    return this.tormoAt ? this.tormoFace : null;
  }

  /** Where Tormo stands by the gate (the camera turns to him when he is new), null if he doesn't. */
  tormoCenter(): THREE.Vector3 | null {
    return this.tormoAt?.clone() ?? null;
  }

  /** Tormo just come: a cold flash over him, red sparks from his heart, a ring at his feet. */
  flareTormo(): void {
    const c = this.tormoAt;
    if (!c) return;
    this.vfx.burst(c, { n: 14, color: '#ff8a6e', speed: 0.03, up: 0.05, size: 0.06, life: 24, gravity: 0.004, spread: 0.8 });
    this.vfx.ring(c.clone().setY(c.y - 1.9), 0.3, 3.0, '#dfe8f2', 10, 0.7);
    this.flare(c.clone().setY(c.y + 0.6), '#cfe4ff', 5, 26);
    this.dirty = true;
  }

  /** The Way's trophy just set: a white flash over it, red sparks. */
  flareWay(): void {
    const c = this.wayAt;
    if (!c) return;
    this.vfx.burst(c, { n: 12, color: '#ff8a6e', speed: 0.03, up: 0.05, size: 0.06, life: 24, gravity: 0.004, spread: 0.6 });
    this.vfx.ring(c.clone().setY(c.y - 1.9), 0.3, 2.6, '#ffffff', 10, 0.7);
    this.flare(c, '#ffffff', 5, 24);
    this.dirty = true;
  }

  /**
   * The cleric's path's trophy (RoadLook): one merged mesh at the game camera's scale (as the Way's), facing the bastion's
   * middle: a plinth of pale stone, a lectern of dark carved wood, the mirror of polished steel in its iron ring tilted on
   * it, Tessa's lantern hanging from its crook (its panes lit white), her stole over the lectern's edge, three clay lamps
   * of the vigil at its foot; a cold white halo behind (one additive quad, no light). The white cloth and the steel lit
   * cold from within, so the keep's warm fire never turns them cream or pink.
   */
  private road: THREE.Mesh | null = null;
  private roadHalo: THREE.Mesh | null = null;
  private roadKey = '';
  private roadAt: THREE.Vector3 | null = null;
  private roadFace = Math.PI / 4;

  private setRoad(t: RoadLook | null): void {
    const key = t ? `${t.cloth ? 1 : 0}:${t.at.q},${t.at.r}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || key === this.roadKey) return;
    this.roadKey = key;
    for (const m of [this.road, this.roadHalo]) {
      if (!m) continue;
      this.scene.remove(m);
      m.traverse((o) => {
        if (!(o instanceof THREE.Mesh)) return;
        o.geometry.dispose();
        (o.material as THREE.Material).dispose();
      });
    }
    this.road = null;
    this.roadHalo = null;
    this.roadAt = null;
    if (!t) return;
    const f = hexToWorld(t.at.q, t.at.r);
    const len = Math.hypot(f.x, f.y);
    // (It faces out, away from the keep: the camera that turns to it then has the keep behind it, not in front.)
    const yaw = len > 600 ? Math.atan2(f.x, f.y) : Math.PI / 4;
    this.roadFace = yaw;
    const p = simToWorld(f.x, f.y, new THREE.Vector3(), this.heightAt(f.x, f.y));
    const big = 1.85;
    const kit = new Kit();
    // The cold-lit pieces on their own: the steel, the stole, the lantern's panes.
    const cold = new Kit();
    // The flames: lit from within, not by the board's light.
    const fire = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 14);
    const at = (x: number, y: number, z: number, ry = 0, s = 1): THREE.Matrix4 =>
      new THREE.Matrix4().compose(
        p.clone().add(new THREE.Vector3(x, 0, z).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw).multiplyScalar(big)).setY(p.y + y * big),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yaw + ry, 0)),
        new THREE.Vector3(s * big, s * big, s * big),
      );
    const sc = (x: number, y: number, z: number): THREE.Matrix4 => new THREE.Matrix4().makeScale(x, y, z);
    // The plinth: two steps of dark slate (pale stone went white on the yard's pale floor), a band carved with the
    // Hermitage's sun, its edges dressed lighter.
    kit.add(box, '#3f444c', at(0, 0.08, 0), sc(1.8, 0.16, 1.1));
    kit.add(box, '#6f757e', at(0, 0.165, 0), sc(1.84, 0.025, 1.14));
    kit.add(box, '#4b515a', at(0, 0.23, -0.04), sc(1.58, 0.1, 0.9));
    kit.add(box, '#7a8089', at(0, 0.285, -0.04), sc(1.62, 0.02, 0.94));
    kit.add(box, '#2c3037', at(0, 0.09, 0.552), sc(1.2, 0.07, 0.01));
    const sun = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    kit.add(sun, '#a8aeb6', at(0, 0.09, 0.556).multiply(new THREE.Matrix4().makeRotationX(Math.PI / 2)), sc(0.1, 0.01, 0.1));
    for (let i = 0; i < 8; i++) kit.add(box, '#a8aeb6', at(0, 0.09, 0.556).multiply(new THREE.Matrix4().makeRotationZ((i * Math.PI) / 4)).multiply(new THREE.Matrix4().makeTranslation(0.085, 0, 0)), sc(0.05, 0.012, 0.01));
    // Behind the lectern, a pointed arch of the Hermitage's stone, the halo within it: the window it came from.
    const arch = '#5a6069';
    for (const k of [-1, 1]) {
      kit.add(box, arch, at(k * 0.62, 1.0, -0.36), sc(0.16, 1.44, 0.16));
      kit.add(box, '#7a8089', at(k * 0.62, 0.32, -0.36), sc(0.22, 0.08, 0.22));
      kit.add(box, '#7a8089', at(k * 0.62, 1.72, -0.36), sc(0.2, 0.06, 0.2));
      // The arch's two halves leaning in to the point.
      kit.add(box, arch, at(k * 0.36, 2.0, -0.36).multiply(new THREE.Matrix4().makeRotationZ(k * 0.82)), sc(0.16, 0.78, 0.16));
    }
    kit.add(box, '#8a9099', at(0, 2.3, -0.36), sc(0.16, 0.16, 0.2));
    kit.add(box, '#1c2027', at(0, 1.2, -0.44), sc(1.08, 1.8, 0.02));
    // The lectern: a foot of dark wood, a turned post, the sloped desk with its lip; the crook for the lantern.
    const wood = '#3a2f29';
    kit.add(box, wood, at(0, 0.33, 0), sc(0.62, 0.07, 0.44));
    kit.add(cyl, wood, at(0, 0.68, 0), sc(0.13, 0.72, 0.13));
    for (const y of [0.46, 0.68, 0.9]) kit.add(cyl, '#2b231e', at(0, y, 0), sc(0.18, 0.05, 0.18));
    const desk = at(0, 1.08, 0).multiply(new THREE.Matrix4().makeRotationX(-0.55));
    kit.add(box, wood, desk, sc(0.8, 0.05, 0.58));
    kit.add(box, '#2b231e', desk.clone().multiply(new THREE.Matrix4().makeTranslation(0, 0.04, 0.28)), sc(0.82, 0.06, 0.04));
    // The desk's front carved: a dark panel with a raised edge.
    kit.add(box, '#2b231e', desk.clone().multiply(new THREE.Matrix4().makeTranslation(0, -0.04, 0)), sc(0.7, 0.03, 0.48));
    kit.add(box, wood, at(0.44, 1.24, -0.05), sc(0.045, 0.56, 0.045));
    kit.add(box, wood, at(0.53, 1.5, -0.05), sc(0.22, 0.045, 0.045));
    // The mirror: a disc of steel in an iron ring, tilted back on the desk, rivets round it.
    const mir = at(0, 1.44, -0.02).multiply(new THREE.Matrix4().makeRotationX(-0.38));
    const disc = new THREE.CylinderGeometry(0.5, 0.5, 1, 24);
    cold.add(disc, '#e4ebf3', mir.clone().multiply(new THREE.Matrix4().makeRotationX(Math.PI / 2)), sc(0.6, 0.035, 0.6));
    const rim = new THREE.TorusGeometry(0.31, 0.035, 6, 24);
    kit.add(rim, '#30343a', mir.clone().multiply(new THREE.Matrix4().makeTranslation(0, 0, 0.02)));
    for (let i = 0; i < 8; i++) {
      const a = (i * Math.PI) / 4;
      kit.add(box, '#596069', mir.clone().multiply(new THREE.Matrix4().makeTranslation(Math.cos(a) * 0.31, Math.sin(a) * 0.31, 0.04)), sc(0.04, 0.04, 0.03));
    }
    if (t.cloth) {
      // Piet's cloth over the mirror's top: grey wool, falling over its rim on both sides.
      kit.add(box, '#5d6470', mir.clone().multiply(new THREE.Matrix4().makeTranslation(0, 0.22, 0.05)), sc(0.7, 0.2, 0.025));
      for (const k of [-1, 1]) kit.add(box, '#545b66', mir.clone().multiply(new THREE.Matrix4().makeTranslation(k * 0.35, 0.06, 0.03).multiply(new THREE.Matrix4().makeRotationZ(k * 0.12))), sc(0.045, 0.36, 0.025));
      kit.add(box, '#474d57', mir.clone().multiply(new THREE.Matrix4().makeTranslation(0, 0.13, 0.065)), sc(0.66, 0.025, 0.01));
    }
    // Tessa's stole over the desk's left edge: two white ends hanging, each with a band of dragon-red embroidery and
    // a little sun at its tip.
    for (const k of [0, 1]) {
      cold.add(box, '#dce3ed', at(-0.36 - k * 0.07, 0.9, 0.13 + k * 0.05, 0.1), sc(0.13, 0.46, 0.02));
      kit.add(box, '#a8382b', at(-0.36 - k * 0.07, 0.74, 0.142 + k * 0.05, 0.1), sc(0.135, 0.035, 0.022));
      kit.add(box, '#a8382b', at(-0.36 - k * 0.07, 0.69, 0.142 + k * 0.05, 0.1), sc(0.135, 0.015, 0.022));
      kit.add(box, '#2d323a', at(-0.36 - k * 0.07, 0.94, 0.142 + k * 0.05, 0.1 + Math.PI / 4), sc(0.05, 0.05, 0.022));
    }
    cold.add(box, '#dce3ed', at(-0.32, 1.14, -0.02, 0.1), sc(0.2, 0.03, 0.42));
    // Her lantern from the crook: a ring, a little cage of iron, its panes lit white.
    kit.add(box, '#26292f', at(0.62, 1.42, -0.05), sc(0.015, 0.12, 0.015));
    const cage = new THREE.CylinderGeometry(0.5, 0.56, 1, 6);
    kit.add(cage, '#26292f', at(0.62, 1.25, -0.05), sc(0.19, 0.24, 0.19));
    cold.add(cage, '#ffffff', at(0.62, 1.25, -0.05), sc(0.14, 0.18, 0.14));
    const cap = new THREE.ConeGeometry(0.5, 1, 6);
    kit.add(cap, '#26292f', at(0.62, 1.4, -0.05), sc(0.22, 0.1, 0.22));
    kit.add(box, '#26292f', at(0.62, 1.12, -0.05), sc(0.2, 0.025, 0.2));
    // The vigil's clay lamps along the plinth's step, lit: little flames over their spouts.
    const flame = new THREE.ConeGeometry(0.5, 1, 6);
    for (const [x, z] of [
      [-0.7, 0.36],
      [-0.36, 0.44],
      [0.36, 0.44],
      [0.7, 0.36],
      [0.0, 0.5],
    ] as const) {
      kit.add(cyl, '#5e4a3e', at(x, 0.21, z), sc(0.15, 0.08, 0.15));
      kit.add(cyl, '#3e322a', at(x, 0.255, z), sc(0.1, 0.02, 0.1));
      fire.add(flame, '#ff8a4a', at(x, 0.31, z), sc(0.05, 0.1, 0.05));
      fire.add(flame, '#fff1e0', at(x, 0.295, z), sc(0.025, 0.05, 0.025));
    }
    flame.dispose();
    sun.dispose();
    box.dispose();
    cyl.dispose();
    disc.dispose();
    rim.dispose();
    cage.dispose();
    cap.dispose();
    const mesh = kit.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.05, flatShading: true, emissive: '#262e38' }), 'road-trophy');
    if (!mesh) return;
    mesh.castShadow = true;
    const cm = cold.size ? cold.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.4, metalness: 0.2, color: '#c4cfdd', emissive: '#71819a', side: THREE.DoubleSide }), 'road-trophy-cold') : null;
    if (cm) {
      cm.castShadow = true;
      mesh.add(cm);
    }
    const fm = fire.size ? fire.build(new THREE.MeshBasicMaterial({ vertexColors: true }), 'road-trophy-fire') : null;
    if (fm) mesh.add(fm);
    this.scene.add(mesh);
    this.road = mesh;
    this.roadAt = p.clone().setY(p.y + 1.25 * big);
    const halo = new THREE.Mesh(
      new THREE.PlaneGeometry(4.4, 4.6),
      new THREE.MeshBasicMaterial({ color: '#dfe8f2', transparent: true, opacity: 0.2, depthWrite: false, blending: THREE.AdditiveBlending, map: haloTexture() }),
    );
    halo.position.copy(this.roadAt).add(new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(-0.85));
    halo.rotation.set(0, yaw, 0);
    halo.name = 'road-halo';
    this.scene.add(halo);
    this.roadHalo = halo;
    this.dirty = true;
  }

  /** The table camera's yaw that looks at the Last Road's trophy's face. */
  roadYaw(): number | null {
    return this.roadAt ? this.roadFace : null;
  }

  /** Where the Last Road's trophy stands (the camera turns to it when it is new), null if it doesn't. */
  roadCenter(): THREE.Vector3 | null {
    return this.roadAt?.clone() ?? null;
  }

  /** The Last Road's trophy just set: a white flash over the mirror, a ring at its foot, pale sparks. */
  flareRoad(): void {
    const c = this.roadAt;
    if (!c) return;
    this.vfx.burst(c, { n: 14, color: '#f4f8ff', speed: 0.03, up: 0.05, size: 0.06, life: 24, gravity: 0.004, spread: 0.6 });
    this.vfx.ring(c.clone().setY(c.y - 1.8), 0.3, 2.6, '#ff8a6e', 10, 0.7);
    this.flare(c, '#ffffff', 5, 24);
    this.dirty = true;
  }

  /** A new statue on the paths' plinth: a pale flash over it, a shaft of light, a ring at its foot. */
  flareStatues(): void {
    const slot = this.patio.sendas;
    if (!this.statues || !slot) return;
    const at = this.patioAt(slot);
    const top = at.clone().setY(at.y + 1.6);
    this.flare(top, '#e6ecf4', 4.5, 24);
    this.vfx.shaft(at.clone().setY(at.y + 0.2), top.clone().setY(top.y + 3), '#e6ecf4', 26, 0.55);
    this.vfx.ring(at.clone().setY(at.y + 0.1), 0.4, 2.8, '#ff8a6e', 18, 0.7);
    this.dirty = true;
  }

  /** Where the paths' statues stand (the camera turns to them for a new one), null if none. */
  statuesAt(): THREE.Vector3 | null {
    const slot = this.patio.sendas;
    return this.statues && slot ? this.patioAt(slot) : null;
  }

  private patioAt(slot: PatioSlot): THREE.Vector3 {
    const w = hexToWorld(slot.q, slot.r);
    return simToWorld(w.x, w.y, new THREE.Vector3(), this.heightAt(w.x, w.y));
  }

  /** Moves a saga trophy built in the keep's frame (its matrix already set there) onto its saga's plinth, if it has one. */
  private toPatio(root: THREE.Object3D, key: string): void {
    const d = PATIO_PIECES[key];
    const slot = d && this.patio[d.saga];
    if (!d || !slot) return;
    const ps = PLINTH_SCALE * (slot.small ? PLINTH_SMALL : 1);
    const at = this.patioAt(slot).add(new THREE.Vector3(d.on[0] * ps, slot.bare ? 0 : PLINTH_TOP * ps, d.on[1] * ps));
    const keep = new THREE.Matrix4().compose(simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0)), new THREE.Quaternion().setFromAxisAngle(this.yAxis, Math.PI / 6), new THREE.Vector3().setScalar(this.lib.scaleOf(this.center())));
    const k = (ps * d.size) / d.scale;
    const place = new THREE.Matrix4()
      .compose(at, new THREE.Quaternion().setFromAxisAngle(this.yAxis, d.face - d.rot), new THREE.Vector3(k, k, k))
      .multiply(new THREE.Matrix4().makeTranslation(-d.at.x, 0, -d.at.z));
    root.matrix.premultiply(place.multiply(keep.invert()));
    root.updateMatrixWorld(true);
  }

  private setTable(look: TableLook | null): void {
    const key = look ? `${!!look.posts}${!!look.sash}${look.carve ? `${look.carve.seed}/${look.carve.radius}` : ''}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.table?.key ?? '') === key) return;
    if (this.table) {
      this.scene.remove(this.table.model.root);
      this.table.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.table = null;
    }
    if (look) {
      const model = furrowsTableTrophy(look);
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'table');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.table = { key, model };
    }
    this.dirty = true;
  }

  /** The tomes' shelf by the keep (null: no tome filled or being gathered). */
  private shelf: { key: string; model: Model } | null = null;
  private setShelf(look: ShelfLook | null): void {
    const key = look ? `${look.books.join(',')}|${look.reading}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.shelf?.key ?? '') === key) return;
    if (this.shelf) {
      this.scene.remove(this.shelf.model.root);
      this.shelf.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.shelf = null;
    }
    if (look) {
      const model = tomeShelfTrophy(look);
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'shelf');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.shelf = { key, model };
    }
    this.dirty = true;
  }

  /** The glyph stone in front of the keep (null: no glyph completed or being gathered). */
  private stone: { key: string; model: Model } | null = null;
  private setGlyphStone(look: GlyphStoneLook | null): void {
    const key = look ? `${look.marks.join(',')}|${look.carving}|${look.clan ? 1 : 0}${look.remedies ? 1 : 0}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.stone?.key ?? '') === key) return;
    if (this.stone) {
      this.scene.remove(this.stone.model.root);
      this.stone.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.stone = null;
    }
    if (look) {
      const model = glyphStoneTrophy(look);
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'stone');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.stone = { key, model };
    }
    this.dirty = true;
  }

  /** A new glyph on the stone: a red flare over it. */
  flareGlyphStone(): void {
    if (!this.stone) return;
    const p = GLYPH_STONE_SPOT.clone().applyMatrix4(this.stone.model.root.matrix);
    this.flare(p, '#ff8a6e', 3, 14);
    this.vfx.burst(p, { n: 16, color: '#e9ecf1', speed: 0.04, up: 0.06, size: 0.08, life: 20, gravity: -0.002, spread: 0.5 });
    this.dirty = true;
  }

  /** The Unsleeping felled for good, by the keep's flank (null or empty: none yet). */
  private fallen: { key: string; model: Model } | null = null;
  private setFallen(ids: string[] | null, ragna: 'home' | 'call' | null = null): void {
    // Ragna after El Cuerno 4 has a look of her own there (gone home at the call: her axe, though she never fell).
    const key = ids?.length || ragna ? `${(ids ?? []).join(',')}|${ragna ?? ''}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.fallen?.key ?? '') === key) return;
    if (this.fallen) {
      this.scene.remove(this.fallen.model.root);
      this.fallen.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.fallen = null;
    }
    if (key) {
      const model = fallenTrophy({ fallen: ids ?? [], ...(ragna ? { ragna } : {}), ...(this.patio.fallen ? { ring: FALLEN_PATIO_R } : {}) });
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'fallen');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.fallen = { key, model };
    }
    this.dirty = true;
  }

  /** Brann and Hilde's barrels by the keep's flank (null: no bet won yet). */
  private barrels: { key: string; model: Model } | null = null;
  private setBarrels(look: BarrelLook | null): void {
    const key = look && look.barrels > 0 ? `${look.barrels}|${(look.from ?? []).join(',')}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.barrels?.key ?? '') === key) return;
    if (this.barrels) {
      this.scene.remove(this.barrels.model.root);
      this.barrels.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.barrels = null;
    }
    if (look && key) {
      const model = barrelPileTrophy(look);
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'barrels');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.barrels = { key, model };
    }
    this.dirty = true;
  }

  /** The clue board of «Caras prestadas» by the keep's front-right (null: the saga not begun; 0: the bare board). */
  private clueBoard: { key: string; model: Model } | null = null;
  private setClueBoard(n: number | null): void {
    const key = n === null ? '' : String(Math.min(12, n));
    if (this.map.kind !== 'bastion' || this.crypt || (this.clueBoard?.key ?? '') === key) return;
    if (this.clueBoard) {
      this.scene.remove(this.clueBoard.model.root);
      this.clueBoard.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.clueBoard = null;
    }
    if (n !== null) {
      const model = clueBoardTrophy({ clues: n });
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'clues');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.clueBoard = { key, model };
    }
    this.dirty = true;
  }

  /** «La cuerda de faroles» (Caras prestadas 2) in front of the keep (null: not yet). */
  private lanterns: { key: string; model: Model } | null = null;
  private setLanterns(on: true | null): void {
    const key = on ? '1' : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.lanterns?.key ?? '') === key) return;
    if (this.lanterns) {
      this.scene.remove(this.lanterns.model.root);
      this.lanterns.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.lanterns = null;
    }
    if (on) {
      const model = lanternStringTrophy({});
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'lanterns');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.lanterns = { key, model };
    }
    this.dirty = true;
  }

  /** «El farol que lo desenmascaró» (Caras prestadas 4) by the keep (null: not yet). */
  private facesLantern: { key: string; model: Model } | null = null;
  private setFacesLantern(on: true | null): void {
    const key = on ? '1' : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.facesLantern?.key ?? '') === key) return;
    if (this.facesLantern) {
      this.scene.remove(this.facesLantern.model.root);
      this.facesLantern.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.facesLantern = null;
    }
    if (on) {
      const model = facesLanternTrophy({});
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'facesLantern');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.facesLantern = { key, model };
    }
    this.dirty = true;
  }

  /** «Un corral con dos jabalíes de piedra» (El Cuerno 1) on the keep's right flank (null: not yet). */
  private corral: { key: string; model: Model } | null = null;
  private setCorral(on: true | null): void {
    const key = on ? '1' : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.corral?.key ?? '') === key) return;
    if (this.corral) {
      this.scene.remove(this.corral.model.root);
      this.corral.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.corral = null;
    }
    if (on) {
      const model = corralTrophy({});
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'corral');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.corral = { key, model };
    }
    this.dirty = true;
  }

  /** The corral just built: a pale flare over it and dust kicked up by the boars. */
  flareCorral(): void {
    if (!this.corral) return;
    const p = CORRAL_SPOT.clone().applyMatrix4(this.corral.model.root.matrix);
    this.flare(p, '#e8f0ff', 3.2, 16);
    this.vfx.burst(p, { n: 16, color: '#8a8478', speed: 0.03, up: 0.03, size: 0.12, life: 24, smoke: true, spread: 0.7 });
    this.vfx.burst(p, { n: 10, color: '#ff8a6e', speed: 0.03, up: 0.05, size: 0.06, life: 18, gravity: -0.002, spread: 0.6 });
    this.dirty = true;
  }

  /** «Un poste de amarre del clan» (El Cuerno 2) beside the corral (null: not yet). */
  private post: { key: string; model: Model } | null = null;
  private setPost(on: true | null): void {
    const key = on ? '1' : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.post?.key ?? '') === key) return;
    if (this.post) {
      this.scene.remove(this.post.model.root);
      this.post.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.post = null;
    }
    if (on) {
      const model = mooringPostTrophy({});
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'post');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.post = { key, model };
    }
    this.dirty = true;
  }

  /** The post just set up: a cold flare at its top and the clan's red wool on the wind. */
  flarePost(): void {
    if (!this.post) return;
    const p = POST_SPOT.clone().applyMatrix4(this.post.model.root.matrix);
    this.flare(p, '#e8f0ff', 3.2, 16);
    this.vfx.burst(p, { n: 14, color: '#ff8a6e', speed: 0.03, up: 0.02, size: 0.07, life: 22, gravity: 0.001, spread: 0.5 });
    this.vfx.ring(p.clone().setY(this.heightAt(0, 0) + 0.05), 0.2, 1.6, '#dfe9ff', 14, 0.6);
    this.dirty = true;
  }

  /** «La lanza que lo remató» (El Cuerno 3) planted left of the keep, toward the camera (null: not yet). */
  private spear: { key: string; model: Model } | null = null;
  private setSpear(on: true | null): void {
    const key = on ? '1' : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.spear?.key ?? '') === key) return;
    if (this.spear) {
      this.scene.remove(this.spear.model.root);
      this.spear.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.spear = null;
    }
    if (on) {
      const model = spearTrophy({});
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'spear');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.spear = { key, model };
    }
    this.dirty = true;
  }

  /**
   * The clan's call (El Cuerno 4): Ulla's hunting horn from the camp. Slow pale rings roll out over the graves from
   * the camp, one after another, and reach her; a cold light falls on her and the nightmares stand still.
   */
  private hornCall(id: number, x: number, y: number): void {
    const low = this.opts.quality === 'low';
    const camp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0) + 0.15);
    const her = simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y));
    const far = Math.max(4, camp.distanceTo(her) + 1.5);
    for (let i = 0; i < 4; i++) {
      this.later.push({
        n: i * 6,
        run: () => {
          this.vfx.ring(camp.clone().setY(camp.y + 0.05 + i * 0.04), 0.4, far, '#e4ecff', 26, 0.4 - i * 0.06);
          this.vfx.ring(camp.clone().setY(camp.y + 0.6), 0.2, far * 0.6, '#ffb0a0', 20, 0.18);
        },
      });
    }
    this.flare(camp.clone().setY(camp.y + 1.2), '#ffe2c8', 2, 18);
    // It reaches her: the crimson goes out of her, a shaft of moonlight, ash lifting.
    this.later.push({
      n: 14,
      run: () => {
        this.vfx.shaft(her, her.clone().setY(her.y + 5), '#dfe9ff', 30, 0.22);
        this.vfx.ring(her.clone().setY(her.y + 0.05), 0.2, 1.6, '#dfe9ff', 18, 0.6);
        this.vfx.burst(her.clone().setY(her.y + 0.4), { n: low ? 6 : 14, color: '#8a8478', speed: 0.02, up: 0.03, size: 0.4, life: 26, smoke: true, spread: 0.5 });
        this.flare(her.clone().setY(her.y + 1.4), '#dfe9ff', 2, 20);
      },
    });
    this.force(id, 'kneel');
    this.shake = Math.max(this.shake, 0.12);
  }

  /** Every tick she kneels: pale ash rising off her, a faint ring at her knee. */
  private kneeling(s: BattleState, id: number): void {
    const e = s.enemies.find((x) => x.id === id);
    if (!e || (s.tick + id) % 3 !== 0) return;
    const p = simToWorld(e.x, e.y, new THREE.Vector3(), this.heightAt(e.x, e.y));
    this.vfx.burst(p.clone().setY(p.y + 0.6), { n: this.opts.quality === 'low' ? 1 : 3, color: '#cfd6e4', speed: 0.01, up: 0.025, size: 0.07, life: 22, gravity: -0.001, spread: 0.4 });
  }

  /** Orla's map table (La Quemadura 1) by the keep, the Burn half drawn on it (null: not yet). */
  private mapTable: Model | null = null;
  private setMapTable(on: true | null): void {
    if (this.map.kind !== 'bastion' || this.crypt || !!this.mapTable === !!on) return;
    if (this.mapTable) {
      this.scene.remove(this.mapTable.root);
      this.mapTable.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.mapTable = null;
    }
    if (on) {
      const model = mapTableTrophy({});
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'mapTable');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.mapTable = model;
    }
    this.dirty = true;
  }

  /** The first Vigil's banner (La Quemadura 2) by the keep (null: not yet). */
  private vigilBanner: Model | null = null;
  private setVigilBanner(on: true | null): void {
    if (this.map.kind !== 'bastion' || this.crypt || !!this.vigilBanner === !!on) return;
    if (this.vigilBanner) {
      this.scene.remove(this.vigilBanner.root);
      this.vigilBanner.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.vigilBanner = null;
    }
    if (on) {
      const model = vigilBannerTrophy({});
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'vigilBanner');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.vigilBanner = model;
    }
    this.dirty = true;
  }

  /** The trap's harpoon (La Quemadura 3) laid up behind the keep (null: not yet). */
  private harpoonTrophy: Model | null = null;
  private setHarpoon(on: true | null): void {
    if (this.map.kind !== 'bastion' || this.crypt || !!this.harpoonTrophy === !!on) return;
    if (this.harpoonTrophy) {
      this.scene.remove(this.harpoonTrophy.root);
      this.harpoonTrophy.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.harpoonTrophy = null;
    }
    if (on) {
      const model = harpoonTrophy({});
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'harpoon');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.harpoonTrophy = model;
    }
    this.dirty = true;
  }

  /** The watchtower's beacon (La Quemadura 4) burning in the bastion's far corner (null: not yet). */
  private beaconTrophy: Model | null = null;
  private setBeacon(on: true | null): void {
    if (this.map.kind !== 'bastion' || this.crypt || !!this.beaconTrophy === !!on) return;
    if (this.beaconTrophy) {
      this.scene.remove(this.beaconTrophy.root);
      this.beaconTrophy.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.beaconTrophy = null;
    }
    if (on) {
      const model = beaconTrophy({});
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'beacon');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.beaconTrophy = model;
    }
    this.dirty = true;
  }

  /** The Order's post sign (El Párpado 1) set up by the keep (null: not yet). */
  private lidSignTrophy: Model | null = null;
  private lidSignKey = '';
  private setLidSign(on: { clapper: boolean; shard: boolean } | null): void {
    const key = on ? `1${on.clapper ? 'c' : ''}${on.shard ? 's' : ''}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || this.lidSignKey === key) return;
    this.lidSignKey = key;
    if (this.lidSignTrophy) {
      this.scene.remove(this.lidSignTrophy.root);
      this.lidSignTrophy.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.lidSignTrophy = null;
    }
    if (on) {
      const model = postSignTrophy({ clapper: on.clapper, shard: on.shard });
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'lidSign');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.lidSignTrophy = model;
    }
    this.dirty = true;
  }

  /** Pell's bucket (El pozo 1) on its bit of curb by the keep (null: not yet). */
  private bucketTrophy: Model | null = null;
  private bucketKey = '';
  private setBucket(on: { jar: boolean; banner: 'grande' | 'mediana' | 'chica' | null } | null): void {
    const key = on ? `${on.jar ? 'jar' : 'bucket'}${on.banner ?? ''}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || this.bucketKey === key) return;
    this.bucketKey = key;
    if (this.bucketTrophy) {
      this.scene.remove(this.bucketTrophy.root);
      this.bucketTrophy.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.bucketTrophy = null;
    }
    if (on) {
      const model = bucketTrophy({ jar: on.jar, ...(on.banner ? { banner: on.banner } : {}) });
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'bucket');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.bucketTrophy = model;
    }
    this.dirty = true;
  }

  /** Pell's bucket just left in the bastion: a cold glint off its water, a splash of droplets, a pale ring at its foot. */
  flareBucket(): void {
    if (!this.bucketTrophy) return;
    const p = BUCKET_SPOT.clone().applyMatrix4(this.bucketTrophy.root.matrix);
    this.flare(p, '#cfe8ff', 3.2, 18);
    this.vfx.burst(p, { n: 16, color: '#bfe0ff', speed: 0.04, up: 0.05, size: 0.045, life: 22, gravity: 0.008, spread: 0.4 });
    const foot = new THREE.Vector3(BUCKET_SPOT.x, 0.1, BUCKET_SPOT.z).applyMatrix4(this.bucketTrophy.root.matrix);
    this.vfx.ring(foot, 0.2, 2.2, '#7fb8ff', 14, 0.6);
    this.dirty = true;
  }

  /** The stilt-houses' great net (La Orilla 1) on its drying frame by the keep. */
  private netTrophy: Model | null = null;
  private netOar = false;
  private netBoat = false;
  private setShoreNet(on: boolean, oar = false, boat = false): void {
    if (this.map.kind !== 'bastion' || this.crypt || (!!this.netTrophy === on && this.netOar === (on && oar) && this.netBoat === (on && boat))) return;
    this.netOar = on && oar;
    this.netBoat = on && boat;
    if (this.netTrophy) {
      this.scene.remove(this.netTrophy.root);
      this.netTrophy.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.netTrophy = null;
    }
    if (on) {
      const model = netTrophy({ oar: this.netOar, boat: this.netBoat });
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'net');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.netTrophy = model;
    }
    this.dirty = true;
  }

  /** The dragon's heart (La Orilla 4, taken) by the bastion's forge (`at`, its cell), or by the keep with no forge. */
  private heartTrophy: Model | null = null;
  private heartAt = '';
  private setShoreHeart(at: { q: number; r: number } | null): void {
    const key = at ? `${at.q},${at.r}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || this.heartAt === key) return;
    this.heartAt = key;
    if (this.heartTrophy) {
      this.scene.remove(this.heartTrophy.root);
      this.heartTrophy.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.heartTrophy = null;
    }
    if (at) {
      const model = heartTrophy();
      bakeStill(model, true);
      // Beside the forge, a little towards the keep so the forge's own walls don't hide it.
      const f = hexToWorld(at.q, at.r);
      const len = Math.max(1, Math.hypot(f.x, f.y));
      const x = f.x - (f.x / len) * 700;
      const y = f.y - (f.y / len) * 700;
      const p = simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y));
      this.compose(p, Math.atan2(-f.x, -f.y), this.lib.scaleOf(this.center()) * 0.9);
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.heartTrophy = model;
    }
    this.dirty = true;
  }

  /** The Drowned Dragon's fang (La Orilla 5) across the way in at a gate of the ring wall (`at`, the gate's cell). */
  private fangTrophy: Model | null = null;
  private fangAt = '';
  private setShoreFang(at: { q: number; r: number } | null): void {
    const key = at ? `${at.q},${at.r}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || this.fangAt === key) return;
    this.fangAt = key;
    if (this.fangTrophy) {
      this.scene.remove(this.fangTrophy.root);
      this.fangTrophy.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.fangTrophy = null;
    }
    if (at) {
      const model = fangTrophy();
      bakeStill(model, true);
      // On the gate, across the way in (the way runs out from the keep: the fang lies across it).
      const f = hexToWorld(at.q, at.r);
      const p = simToWorld(f.x, f.y, new THREE.Vector3(), this.heightAt(f.x, f.y));
      this.compose(p, Math.atan2(f.x, f.y), this.lib.scaleOf(this.center()) * 1.05);
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.fangTrophy = model;
    }
    this.dirty = true;
  }

  /**
   * Hilde's plaque (bet 9, the open gate): just inside a gate of the ring wall (`at`, the gate's cell), beside the way
   * in, its face turned to the table's camera; the names of the heroes who held the open gate cut into it.
   */
  private plaqueTrophy: PlaqueTrophy | null = null;
  private plaqueAt = '';
  private setGatePlaque(p: { at: { q: number; r: number }; title: string; names: string[] } | null): void {
    const key = p ? `${p.at.q},${p.at.r}|${p.title}|${p.names.join(',')}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || this.plaqueAt === key) return;
    this.plaqueAt = key;
    if (this.plaqueTrophy) {
      this.scene.remove(this.plaqueTrophy.root);
      this.plaqueTrophy.root.traverse((o) => {
        const m = o as THREE.Mesh;
        m.geometry?.dispose();
        const mat = m.material as THREE.MeshStandardMaterial | undefined;
        if (mat?.map) {
          mat.map.dispose();
          mat.dispose();
        }
      });
      this.plaqueTrophy = null;
    }
    if (p) {
      const model = plaqueTrophy(p.title, p.names);
      model.root.name = 'gate-plaque';
      bakeStill(model, true);
      // Inside the gate (towards the keep) and off to the side of the way in.
      const f = hexToWorld(p.at.q, p.at.r);
      const len = Math.hypot(f.x, f.y) || 1;
      const ux = f.x / len;
      const uy = f.y / len;
      const sx = f.x - ux * 1350 + uy * 1250;
      const sy = f.y - uy * 1350 - ux * 1250;
      const at = simToWorld(sx, sy, new THREE.Vector3(), this.heightAt(sx, sy));
      // Its face to the table's camera (it looks down from +X +Z).
      this.compose(at, Math.PI / 4, this.lib.scaleOf(this.center()) * 1.05);
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.plaqueTrophy = model;
      // The names in the right letters once the fonts are in (drawn once more, then left alone).
      const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
      fonts
        ?.load('48px "IM Fell English SC"')
        .then(() => {
          if (this.plaqueTrophy !== model) return;
          model.redraw();
          this.dirty = true;
        })
        .catch(() => undefined);
    }
    this.dirty = true;
  }

  /** The plaque just put up: a cold flash off the iron, sparks from the forge's last strike, a ring on the ground. */
  flarePlaque(): void {
    if (!this.plaqueTrophy) return;
    const p = PLAQUE_SPOT.clone().applyMatrix4(this.plaqueTrophy.root.matrix);
    this.flare(p, '#e6ecf4', 4.0, 22);
    this.vfx.burst(p, { n: 18, color: '#ff8a6e', speed: 0.05, up: 0.06, size: 0.04, life: 22, gravity: 0.01, spread: 0.6 });
    const foot = new THREE.Vector3(0, 0.1, 0).applyMatrix4(this.plaqueTrophy.root.matrix);
    this.vfx.ring(foot, 0.3, 2.6, '#cfd8e6', 16, 0.7);
    this.dirty = true;
  }

  /** The fang just hung over the gate: a cold flash off its blade, spray shaken off it, a ring on the ground. */
  flareFang(): void {
    if (!this.fangTrophy) return;
    const p = FANG_SPOT.clone().applyMatrix4(this.fangTrophy.root.matrix);
    this.flare(p, '#dff0ff', 4.6, 24);
    this.vfx.burst(p, { n: 22, color: '#e6f2ff', speed: 0.05, up: 0.05, size: 0.06, life: 24, gravity: 0.012, spread: 0.8 });
    const foot = new THREE.Vector3(0, 0.1, 0).applyMatrix4(this.fangTrophy.root.matrix);
    this.vfx.ring(foot, 0.3, 3, '#cfe8ff', 16, 0.7);
    this.dirty = true;
  }

  /** The heart just set down by the forge: a red flare, sparks off the ember, a ring of heat on the ground. */
  flareHeart(): void {
    if (!this.heartTrophy) return;
    const p = HEART_SPOT.clone().applyMatrix4(this.heartTrophy.root.matrix);
    this.flare(p, '#ff6a3a', 4.2, 22);
    this.vfx.burst(p, { n: 22, color: '#ffb070', speed: 0.05, up: 0.07, size: 0.05, life: 26, gravity: -0.002, spread: 0.5 });
    const foot = new THREE.Vector3(0, 0.1, 0).applyMatrix4(this.heartTrophy.root.matrix);
    this.vfx.ring(foot, 0.2, 2.6, '#ff4f3a', 16, 0.7);
    this.dirty = true;
  }

  /** The oar just leant by the net: a glint off its blade, sea-spray shaken off it. */
  flareOar(): void {
    if (!this.netTrophy) return;
    const p = OAR_SPOT.clone().applyMatrix4(this.netTrophy.root.matrix);
    this.flare(p, '#ffd6a0', 2.8, 16);
    this.vfx.burst(p, { n: 14, color: '#bfe0ff', speed: 0.04, up: 0.05, size: 0.04, life: 20, gravity: 0.008, spread: 0.4 });
    this.dirty = true;
  }

  /** The boat saved from the Maw, just hauled up by the net: sea-water off its hull, a glint, a ring in the sand. */
  flareBoat(): void {
    if (!this.netTrophy) return;
    const p = BOAT_SPOT.clone().applyMatrix4(this.netTrophy.root.matrix);
    this.flare(p, '#cfe8ff', 3.2, 18);
    this.vfx.burst(p, { n: 16, color: '#bfe0ff', speed: 0.05, up: 0.06, size: 0.05, life: 22, gravity: 0.01, spread: 0.6 });
    const foot = new THREE.Vector3(BOAT_SPOT.x, 0.1, BOAT_SPOT.z).applyMatrix4(this.netTrophy.root.matrix);
    this.vfx.ring(foot, 0.2, 2.4, '#7fb8ff', 14, 0.6);
    this.dirty = true;
  }

  /** The great net just hung up: a cold glint off the black scale, droplets shaken off, a ring at the frame's foot. */
  flareNet(): void {
    if (!this.netTrophy) return;
    const p = NET_SPOT.clone().applyMatrix4(this.netTrophy.root.matrix);
    this.flare(p, '#cfe8ff', 3.4, 18);
    this.vfx.burst(p, { n: 18, color: '#bfe0ff', speed: 0.04, up: 0.05, size: 0.045, life: 22, gravity: 0.008, spread: 0.6 });
    const foot = new THREE.Vector3(NET_SPOT.x, 0.1, NET_SPOT.z).applyMatrix4(this.netTrophy.root.matrix);
    this.vfx.ring(foot, 0.2, 2.6, '#7fb8ff', 14, 0.6);
    this.dirty = true;
  }

  /** The fair's banner just hung (El pozo 3): a warm flash on it, a flutter of sparks, a ring at its foot. */
  flareBanner(): void {
    if (!this.bucketTrophy) return;
    const p = BANNER_SPOT.clone().applyMatrix4(this.bucketTrophy.root.matrix);
    this.flare(p, '#ffd6a0', 3.4, 18);
    this.vfx.burst(p, { n: 18, color: '#ff8a6e', speed: 0.04, up: 0.04, size: 0.045, life: 24, gravity: 0.002, spread: 0.5 });
    const foot = new THREE.Vector3(BANNER_SPOT.x, 0.1, BANNER_SPOT.z).applyMatrix4(this.bucketTrophy.root.matrix);
    this.vfx.ring(foot, 0.2, 2.4, '#ffd6a0', 14, 0.6);
    this.dirty = true;
  }

  /** The jar of well water just come up (El pozo 2): a cold glint, droplets, a ring at its foot. */
  flareJar(): void {
    if (!this.bucketTrophy) return;
    const p = JAR_SPOT.clone().applyMatrix4(this.bucketTrophy.root.matrix);
    this.flare(p, '#cfe8ff', 3.2, 18);
    this.vfx.burst(p, { n: 16, color: '#bfe0ff', speed: 0.04, up: 0.06, size: 0.045, life: 22, gravity: 0.008, spread: 0.4 });
    const foot = new THREE.Vector3(JAR_SPOT.x, 0.1, JAR_SPOT.z).applyMatrix4(this.bucketTrophy.root.matrix);
    this.vfx.ring(foot, 0.2, 2.2, '#7fb8ff', 14, 0.6);
    this.dirty = true;
  }

  /** The fast way's cart (Las vías 1) at the mouth dug out by the keep's foot, Dorn's automaton by it (Las vías 2) and his key on its rack (Las vías 3; null: not yet). */
  private mineCart: Model | null = null;
  private mineCartGuard = false;
  private mineCartKey = false;
  /** Las vías 4: Bruni's hammer on its stretch of forge wall, and (the rails left open) the week's ore heaped up. */
  private mineCartMore = '';
  private setMineCart(on: { guard: boolean; key: boolean; hammer?: boolean; ore?: boolean } | null): void {
    const more = `${on?.hammer ? 1 : 0}${on?.ore ? 1 : 0}`;
    if (this.map.kind !== 'bastion' || this.crypt || (!!this.mineCart === !!on && this.mineCartGuard === !!on?.guard && this.mineCartKey === !!on?.key && this.mineCartMore === more)) return;
    this.mineCartGuard = !!on?.guard;
    this.mineCartKey = !!on?.key;
    this.mineCartMore = more;
    if (this.mineCart) {
      this.scene.remove(this.mineCart.root);
      this.mineCart.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.mineCart = null;
    }
    if (on) {
      const model = mineCartTrophy({ guard: on.guard, key: on.key, hammer: !!on.hammer, ore: !!on.ore });
      bakeStill(model, true);
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      this.toPatio(model.root, 'mineCart');
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.mineCart = model;
    }
    this.dirty = true;
  }

  /** The cart just brought up: a warm flare at the fast way's mouth, sparks off the rails, dust from the arch. */
  flareMineCart(): void {
    if (!this.mineCart) return;
    const p = MINE_CART_SPOT.clone().applyMatrix4(this.mineCart.root.matrix);
    this.flare(p, '#ffd6b0', 3.4, 18);
    this.vfx.burst(p, { n: 14, color: '#ffc27a', speed: 0.04, up: 0.03, size: 0.04, life: 14, gravity: 0.006, spread: 0.5 });
    const foot = new THREE.Vector3(MINE_CART_SPOT.x, 0.1, MINE_CART_SPOT.z).applyMatrix4(this.mineCart.root.matrix);
    this.vfx.ring(foot, 0.2, 2.2, '#ffa66b', 14, 0.6);
    this.dirty = true;
  }

  /** Dorn's automaton just carried up: a cold flare on its dark helm, dust off the rubble at its feet. */
  flareRailsGuard(): void {
    if (!this.mineCart) return;
    const p = GUARD_SPOT.clone().applyMatrix4(this.mineCart.root.matrix);
    this.flare(p, '#ff8a6e', 2.6, 16);
    this.vfx.burst(p, { n: 10, color: '#55545c', speed: 0.03, up: 0.03, size: 0.3, life: 20, smoke: true, spread: 0.5 });
    const foot = new THREE.Vector3(GUARD_SPOT.x, 0.1, GUARD_SPOT.z).applyMatrix4(this.mineCart.root.matrix);
    this.vfx.ring(foot, 0.2, 1.8, '#ff8a6e', 14, 0.6);
    this.dirty = true;
  }

  /** Dorn's key just hung up: a warm flare off its bit, a few embers, dust off the rack. */
  flareRailsKey(): void {
    if (!this.mineCart) return;
    const p = KEY_SPOT.clone().applyMatrix4(this.mineCart.root.matrix);
    this.flare(p, '#ffb070', 2.8, 18);
    this.vfx.burst(p, { n: 12, color: '#ff8a4a', speed: 0.02, up: 0.05, size: 0.05, life: 26, gravity: -0.002, spread: 0.4 });
    const foot = new THREE.Vector3(KEY_SPOT.x, 0.1, KEY_SPOT.z).applyMatrix4(this.mineCart.root.matrix);
    this.vfx.ring(foot, 0.2, 1.8, '#ffa66b', 14, 0.6);
    this.dirty = true;
  }

  /** Bruni's hammer just hung up: a cold blue flare off its runes, embers and soot off the wall. */
  flareRailsHammer(): void {
    if (!this.mineCart) return;
    const p = HAMMER_SPOT.clone().applyMatrix4(this.mineCart.root.matrix);
    this.flare(p, '#4fe0ff', 2.8, 18);
    this.vfx.burst(p, { n: 12, color: '#ff8a4a', speed: 0.02, up: 0.05, size: 0.05, life: 26, gravity: -0.002, spread: 0.4 });
    this.vfx.burst(p, { n: 6, color: '#2a2628', speed: 0.02, up: 0.03, size: 0.3, life: 22, smoke: true, spread: 0.4 });
    const foot = new THREE.Vector3(HAMMER_SPOT.x, 0.1, HAMMER_SPOT.z).applyMatrix4(this.mineCart.root.matrix);
    this.vfx.ring(foot, 0.2, 1.8, '#4fe0ff', 14, 0.6);
    this.dirty = true;
  }

  /** The week's ore just come up: dust off the heap, a glint off its crystals. */
  flareRailsOre(): void {
    if (!this.mineCart) return;
    const p = ORE_SPOT.clone().applyMatrix4(this.mineCart.root.matrix);
    this.flare(p, '#bcd8ff', 2.4, 16);
    this.vfx.burst(p, { n: 10, color: '#55545c', speed: 0.03, up: 0.03, size: 0.3, life: 20, smoke: true, spread: 0.5 });
    this.vfx.burst(p, { n: 8, color: '#bcd8ff', speed: 0.04, up: 0.05, size: 0.04, life: 16, gravity: 0.006, spread: 0.4 });
    const foot = new THREE.Vector3(ORE_SPOT.x, 0.1, ORE_SPOT.z).applyMatrix4(this.mineCart.root.matrix);
    this.vfx.ring(foot, 0.2, 1.8, '#9fc4ff', 14, 0.6);
    this.dirty = true;
  }

  /**
   * The Chasm's winch beside the forge (docs/SIMA.md): where it stands (sim units, a free spot by the forge: the
   * bastion's call) and how far the group got (null: the Chasm not open). Baked like the keep's trophies (two draws).
   */
  private winch: { key: string; model: Model; smoke: boolean } | null = null;
  setSimaWinch(w: { look: WinchLook; x: number; y: number } | null): void {
    const key = w ? `${w.x},${w.y}|${w.look.mark}|${w.look.stones.map((st) => `${st.stratum}${st.floor}`).join(',')}` : '';
    if (this.map.kind !== 'bastion' || this.crypt || (this.winch?.key ?? '') === key) return;
    if (this.winch) {
      this.scene.remove(this.winch.model.root);
      this.winch.model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.winch = null;
    }
    if (w) {
      const model = simaWinchTrophy(w.look);
      bakeStill(model, true);
      const p = simToWorld(w.x, w.y, new THREE.Vector3(), this.heightAt(w.x, w.y));
      // Turned a little toward the table camera, like the armory's rack: the rope and the slab are what must read.
      this.compose(p, 0.3, WINCH_SCALE);
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.winch = { key, model, smoke: w.look.mark >= SIMA_TROPHY.smoke };
    }
    this.dirty = true;
  }

  /** Something new on the winch (a stone, the mark, a stage): a red flare out of the shaft, embers, a ring on the ground. */
  flareSimaWinch(): void {
    if (!this.winch) return;
    const p = WINCH_SPOT.clone().applyMatrix4(this.winch.model.root.matrix);
    this.flare(p.clone().setY(p.y + 0.6), '#ff8a6e', 3.2, 18);
    this.vfx.burst(p, { n: 16, color: '#ff6a3a', speed: 0.03, up: 0.06, size: 0.05, life: 22, gravity: -0.002, spread: 0.4 });
    this.vfx.burst(p, { n: 8, color: '#3a3438', speed: 0.02, up: 0.03, size: 0.3, life: 24, smoke: true, spread: 0.4 });
    const foot = new THREE.Vector3(0, 0.06, 0).applyMatrix4(this.winch.model.root.matrix);
    this.vfx.ring(foot, 0.3, 2.0, '#ff4f3a', 16, 0.6);
    const slab = WINCH_SLAB_SPOT.clone().applyMatrix4(this.winch.model.root.matrix);
    this.flare(slab, '#ff4f3a', 1.6, 14);
    this.dirty = true;
  }

  /** Where the winch's shaft is, in the world (null: not standing). */
  simaWinchPos(): THREE.Vector3 | null {
    return this.winch ? WINCH_SPOT.clone().applyMatrix4(this.winch.model.root.matrix) : null;
  }

  /**
   * From the fortieth floor the shaft breathes: its red light swells and fades (a glow in the shared layer, nothing
   * new drawn) and a thread of dark smoke rises out of it (a puff now and then, on fresh ticks only).
   */
  private drawWinch(s: BattleState): void {
    const w = this.winch;
    if (!w?.smoke) return;
    const e = w.model.root.matrix.elements;
    const x = e[12] as number;
    const y = e[13] as number;
    const z = e[14] as number;
    const k = WINCH_SCALE;
    const beat = 0.5 + 0.5 * Math.sin((s.tick % 36) * ((Math.PI * 2) / 36));
    this.entityGlow.add(x, y + 0.4 * k, z, WINCH_PULSE, 2.6 * k + beat * 0.5, 0.18 + beat * 0.22);
    if (!this.fresh) return;
    if (s.tick % 5 === 0) this.vfx.burst(this.v.set(x, y + 0.4 * k, z).clone(), { n: 1, color: s.tick % 15 === 0 ? '#4a2a26' : '#2e2a2e', speed: 0.004, up: 0.022, size: 0.3, life: 30, smoke: true, spread: 0.18 });
    if (s.tick % 11 === 0) this.vfx.burst(this.v.set(x, y + 0.3 * k, z).clone(), { n: 2, color: '#ff6a3a', speed: 0.01, up: 0.03, size: 0.04, life: 16, gravity: -0.001, spread: 0.25 });
  }

  /** The beacon just lit in the bastion: a burst of fire and sparks off its cup, a warm ring at its foot. */
  flareBeacon(): void {
    if (!this.beaconTrophy) return;
    const p = BEACON_SPOT.clone().applyMatrix4(this.beaconTrophy.root.matrix);
    this.flare(p, '#fff0d8', 4, 22);
    this.flare(p, '#ff8a3a', 7, 34);
    this.vfx.burst(p, { n: 26, color: '#ffb070', speed: 0.05, up: 0.06, size: 0.06, life: 30, gravity: -0.002, spread: 0.5 });
    const foot = new THREE.Vector3(BEACON_SPOT.x, 0.1, BEACON_SPOT.z).applyMatrix4(this.beaconTrophy.root.matrix);
    this.vfx.ring(foot, 0.2, 2.8, '#ffb070', 16, 0.6);
    this.dirty = true;
  }

  /** The Order's sign just set up: a cold flash on its painted eye, dust shaken off the board, a ring at its feet. */
  flareLidSign(): void {
    if (!this.lidSignTrophy) return;
    const p = POST_SIGN_SPOT.clone().applyMatrix4(this.lidSignTrophy.root.matrix);
    this.flare(p, '#ffe8c0', 3.4, 22);
    this.flare(p, '#ff4f3a', 5, 30);
    this.vfx.burst(p, { n: 22, color: '#b8b4a2', speed: 0.04, up: 0.03, size: 0.35, life: 30, smoke: true, spread: 0.6 });
    const foot = new THREE.Vector3(POST_SIGN_SPOT.x, 0.1, POST_SIGN_SPOT.z).applyMatrix4(this.lidSignTrophy.root.matrix);
    this.vfx.ring(foot, 0.2, 2.6, '#ff8a6e', 16, 0.6);
    this.dirty = true;
  }

  /** The old clapper just hung by the sign: a cold flash on its ball, a ring at the post's foot. */
  flareLidClapper(): void {
    if (!this.lidSignTrophy) return;
    const p = CLAPPER_SPOT.clone().applyMatrix4(this.lidSignTrophy.root.matrix);
    this.flare(p, '#cfe3ff', 3, 22);
    this.flare(p, '#ff4f3a', 4.4, 28);
    const foot = new THREE.Vector3(CLAPPER_SPOT.x, 0.1, CLAPPER_SPOT.z).applyMatrix4(this.lidSignTrophy.root.matrix);
    this.vfx.ring(foot, 0.2, 2.4, '#ff8a6e', 16, 0.6);
    this.dirty = true;
  }

  /** The night of the bell's small bell (El Párpado 4) at the very top of the keep (null: not yet). */
  private lidBell: LidBellTrophy | null = null;
  private setLidBell(on: boolean): void {
    if (this.map.kind !== 'bastion' || this.crypt || !!this.lidBell === on) return;
    if (this.lidBell) {
      this.scene.remove(this.lidBell.root);
      this.lidBell.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
      this.lidBell = null;
    }
    if (on) {
      // Off the keep's own crown, in the keep's frame (its height changes with the era).
      // Eras IV and V end in a point: there the bell hangs off a bracket on the crown instead.
      const top = this.lib.heightOf(this.center());
      const model = lidBellTrophy({ top, beside: this.opts.centerpiece ? null : keepCrownBracket(this.keepEra(), top) });
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(this.m);
      model.root.updateMatrixWorld(true);
      this.scene.add(model.root);
      this.lidBell = model;
    }
    this.dirty = true;
  }

  /** The small bell just hung at the top of the keep: it rings, a cold flash, rings of light rolling off the keep. */
  flareLidBell(): void {
    const b = this.lidBell;
    if (!b) return;
    b.ring();
    // Off the bell itself: over the fire on eras I to III, out on the crown's bracket on IV and V.
    const p = b.at.clone().applyMatrix4(b.root.matrix);
    this.flare(p, '#cfe3ff', 4, 30);
    this.flare(p, '#ff4f3a', 5, 34);
    for (let i = 0; i < 3; i++) this.vfx.ring(p.clone().setY(p.y - 0.25 * i), 0.3, 2.2 + i * 1.1, i === 0 ? '#cfe3ff' : '#ff8a6e', 14 + i * 5, 0.7 - i * 0.15);
    this.dirty = true;
  }

  /** The black glass shard just stood by the sign: a cold flash off its edge, dark smoke going off it, a ring. */
  flareLidShard(): void {
    if (!this.lidSignTrophy) return;
    const p = SHARD_SPOT.clone().applyMatrix4(this.lidSignTrophy.root.matrix);
    this.flare(p, '#c7d0ff', 3.4, 22);
    this.flare(p, '#ff4f3a', 4.2, 28);
    this.vfx.burst(p, { n: 12, color: '#15161d', speed: 0.02, up: 0.03, size: 0.45, life: 34, smoke: true, spread: 0.4 });
    const foot = new THREE.Vector3(SHARD_SPOT.x, 0.1, SHARD_SPOT.z).applyMatrix4(this.lidSignTrophy.root.matrix);
    this.vfx.ring(foot, 0.2, 2.4, '#ff8a6e', 16, 0.6);
    this.dirty = true;
  }

  /** The harpoon just laid up: a pale flare on the dragon's scale, sparks off the iron, dust from the trestles. */
  flareHarpoon(): void {
    if (!this.harpoonTrophy) return;
    const p = HARPOON_SPOT.clone().applyMatrix4(this.harpoonTrophy.root.matrix);
    this.flare(p, '#e8f0ff', 3.4, 18);
    this.vfx.burst(p, { n: 14, color: '#ffd6c0', speed: 0.03, up: 0.03, size: 0.05, life: 20, gravity: 0.004, spread: 0.5 });
    const foot = new THREE.Vector3(HARPOON_SPOT.x, 0.1, HARPOON_SPOT.z).applyMatrix4(this.harpoonTrophy.root.matrix);
    this.vfx.burst(foot, { n: 10, color: '#6a6466', speed: 0.02, up: 0.02, size: 0.3, life: 30, smoke: true, spread: 0.6 });
    this.vfx.ring(foot, 0.2, 2.4, '#ff8a6e', 14, 0.6);
    this.dirty = true;
  }

  /** The banner just raised: a pale flare on its eye, red sparks and ash off its burnt foot. */
  flareVigilBanner(): void {
    if (!this.vigilBanner) return;
    const p = VIGIL_BANNER_SPOT.clone().applyMatrix4(this.vigilBanner.root.matrix);
    this.flare(p, '#e8e0d6', 3.4, 18);
    this.vfx.burst(p, { n: 14, color: '#ff8a6e', speed: 0.025, up: 0.03, size: 0.06, life: 24, gravity: -0.001, spread: 0.5 });
    const foot = new THREE.Vector3(VIGIL_BANNER_SPOT.x, 0.1, VIGIL_BANNER_SPOT.z).applyMatrix4(this.vigilBanner.root.matrix);
    this.vfx.burst(foot, { n: 10, color: '#6a6466', speed: 0.02, up: 0.02, size: 0.3, life: 30, smoke: true, spread: 0.5 });
    this.vfx.ring(foot, 0.2, 2.0, '#ff8a6e', 14, 0.6);
    this.dirty = true;
  }

  /** The map table just set up: a warm flare over the parchment and pale paper motes off it. */
  flareMapTable(): void {
    if (!this.mapTable) return;
    const p = MAP_TABLE_SPOT.clone().applyMatrix4(this.mapTable.root.matrix);
    this.flare(p, '#ffe2c0', 3.4, 18);
    this.vfx.burst(p, { n: 16, color: '#e6e1d8', speed: 0.025, up: 0.03, size: 0.06, life: 26, gravity: -0.001, spread: 0.5 });
    this.vfx.burst(p, { n: 8, color: '#ff8a6e', speed: 0.02, up: 0.03, size: 0.05, life: 20, gravity: -0.001, spread: 0.4 });
    const foot = new THREE.Vector3(MAP_TABLE_SPOT.x, 0.1, MAP_TABLE_SPOT.z).applyMatrix4(this.mapTable.root.matrix);
    this.vfx.ring(foot, 0.2, 2.0, '#ffe2c0', 14, 0.6);
    this.dirty = true;
  }

  /** The spear just planted: an ember flare at its blade and grey ash lifting off its foot. */
  flareSpear(): void {
    if (!this.spear) return;
    const p = SPEAR_SPOT.clone().applyMatrix4(this.spear.model.root.matrix);
    this.flare(p, '#ffd6c8', 3.4, 16);
    this.vfx.burst(p, { n: 14, color: '#ff8a6e', speed: 0.03, up: 0.03, size: 0.06, life: 22, gravity: -0.001, spread: 0.4 });
    const foot = new THREE.Vector3(SPEAR_SPOT.x, 0.1, SPEAR_SPOT.z).applyMatrix4(this.spear.model.root.matrix);
    this.vfx.burst(foot, { n: 12, color: '#9a9ca4', speed: 0.02, up: 0.02, size: 0.3, life: 30, smoke: true, spread: 0.5 });
    this.vfx.ring(foot, 0.2, 1.8, '#dfe9ff', 14, 0.6);
    this.dirty = true;
  }

  /** The lantern just set up: a warm flare and pale paper motes over it. */
  flareFacesLantern(): void {
    if (!this.facesLantern) return;
    const p = FACES_LANTERN_SPOT.clone().applyMatrix4(this.facesLantern.model.root.matrix);
    this.flare(p, '#ffe2c0', 3.5, 16);
    this.vfx.burst(p, { n: 16, color: '#eef0f3', speed: 0.03, up: 0.04, size: 0.08, life: 22, gravity: 0.001, spread: 0.6 });
    this.dirty = true;
  }

  /** The string of lanterns just hung: a warm flare along it and paper-white sparks. */
  flareLanterns(): void {
    if (!this.lanterns) return;
    const p = LANTERNS_SPOT.clone().applyMatrix4(this.lanterns.model.root.matrix);
    this.flare(p, '#ffe2c0', 3.5, 16);
    this.vfx.burst(p, { n: 14, color: '#ff8a6e', speed: 0.03, up: 0.05, size: 0.07, life: 18, gravity: -0.002, spread: 0.7 });
    this.dirty = true;
  }

  /** A clue just pinned on the board: a pale flare and a red spark over it. */
  flareClues(): void {
    if (!this.clueBoard) return;
    const p = CLUES_SPOT.clone().applyMatrix4(this.clueBoard.model.root.matrix);
    this.flare(p, '#dfe9ff', 3, 14);
    this.vfx.burst(p, { n: 12, color: '#ff8a6e', speed: 0.03, up: 0.05, size: 0.07, life: 18, gravity: -0.002, spread: 0.5 });
    this.dirty = true;
  }

  /** A newly felled Unsleeping's piece by the keep: a cold flare and embers going up over it. */
  flareFallen(id = 'corvin'): void {
    if (!this.fallen) return;
    const p = fallenSpot(id, this.patio.fallen ? FALLEN_PATIO_R : undefined).applyMatrix4(this.fallen.model.root.matrix);
    this.flare(p, '#dfe8ff', 4, 18);
    this.vfx.shaft(p.clone().setY(p.y - 0.9), p.clone().setY(p.y + 4), '#dfe8ff', 24, 0.5);
    this.vfx.burst(p, { n: 24, color: '#ff8a6e', speed: 0.04, up: 0.07, size: 0.08, life: 24, gravity: -0.002, spread: 0.5 });
    this.dirty = true;
  }

  /** Ragna's axe just come home (El Cuerno 4): a warm flare over it (cold if she fell), red wool and ash lifting. */
  flareAxe(call: boolean): void {
    if (!this.fallen) return;
    const p = fallenSpot('ragna', this.patio.fallen ? FALLEN_PATIO_R : undefined).applyMatrix4(this.fallen.model.root.matrix);
    this.flare(p, call ? '#ffd2b0' : '#dfe8ff', 4, 18);
    this.vfx.shaft(p.clone().setY(p.y - 0.9), p.clone().setY(p.y + 4), call ? '#ffb27a' : '#dfe8ff', 24, 0.5);
    this.vfx.burst(p, { n: 20, color: '#ff8a6e', speed: 0.04, up: 0.06, size: 0.08, life: 24, gravity: -0.002, spread: 0.5 });
    this.vfx.burst(p.clone().setY(p.y - 0.6), { n: 10, color: '#8a8478', speed: 0.02, up: 0.03, size: 0.35, life: 22, smoke: true, spread: 0.4 });
    this.dirty = true;
  }

  /** A barrel just won: a warm flare and a puff of sawdust over the pile. */
  flareBarrels(): void {
    if (!this.barrels) return;
    const p = BARREL_SPOT.clone().applyMatrix4(this.barrels.model.root.matrix);
    this.flare(p, '#ffb060', 3, 14);
    this.vfx.burst(p, { n: 14, color: '#c8a070', speed: 0.04, up: 0.05, size: 0.08, life: 18, gravity: 0.002, spread: 0.5 });
    this.dirty = true;
  }

  /** A new tome on the shelf: a red flare over it. */
  flareShelf(): void {
    if (!this.shelf) return;
    const p = SHELF_SPOT.clone().applyMatrix4(this.shelf.model.root.matrix);
    this.flare(p, '#ff8a6e', 3, 14);
    this.vfx.burst(p, { n: 16, color: '#ffffff', speed: 0.04, up: 0.06, size: 0.08, life: 20, gravity: -0.002, spread: 0.5 });
    this.dirty = true;
  }

  /** Where the table of the Furrows stands, in the world. */
  tablePos(): THREE.Vector3 | null {
    return this.table ? TABLE_SPOT.clone().applyMatrix4(this.table.model.root.matrix) : null;
  }

  /** The table lights up (when it is new, or tapped): a red flare over the carving. */
  flareTable(): void {
    const p = this.tablePos();
    if (!p) return;
    for (let i = 0; i < 2; i++) this.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 1.4 + i * 0.8, i === 0 ? '#ff8a6e' : '#eceef2', 10 + i * 4, 0.6);
    this.flare(p, '#ff4f3a', 2, 10);
    this.dirty = true;
  }

  /** Where the bell hangs, in the world (for a word over it or a tap on it). */
  bellPos(): THREE.Vector3 | null {
    return this.bell ? new THREE.Vector3(1.3, 2.45, 1.3).applyMatrix4(this.bell.model.root.matrix) : null;
  }

  /** The bell rings: a hard swing, its sound wave and a red flare (when it is tapped, or a Marea is near). */
  ringBell(): void {
    // The night of the bell's small bell up top rings with it (the plumb moved).
    this.lidBell?.ring();
    const p = this.bellPos();
    if (!this.bell || !p) return;
    this.bell.model.ring();
    for (let i = 0; i < 3; i++) this.vfx.ring(p.clone().setY(p.y - 0.2 * i), 0.2, 1.6 + i * 0.7, i === 0 ? '#ff8a6e' : '#eceef2', 10 + i * 4, 0.7 - i * 0.15);
    this.flare(p, '#ff4f3a', 2.2, 10);
    this.dirty = true;
  }

  /** Force a redraw on the next sync even if the tick didn't change. */
  /**
   * Terraced land changed under the board (an earthwork, docs/OBRAS.md §5): the ground, the water and the decor
   * are rebuilt with the same materials, table and fog bank, so nothing compiles again and only the shapes upload.
   * The old shapes are freed. What stands on the land settles on the new heights by itself on the next draw.
   */
  setLand(map: MapDef): void {
    const old = this.land;
    if (!old || !map.land) return;
    const next = buildLandMesh(map, this.seed, { low: this.opts.quality === 'low', reuse: old });
    this.root.remove(old.mesh, old.decor);
    old.mesh.geometry.dispose();
    old.water.geometry.dispose();
    // The decor meshes hand their geometry and material to the new ones; only their instance buffers go.
    for (const o of old.decor.children) if (o instanceof THREE.InstancedMesh) o.dispose();
    this.root.add(next.mesh, next.decor);
    this.root.updateMatrixWorld(true);
    this.land = next;
    this.terrain = next;
    this.map = map;
    this.invalidate();
  }

  invalidate(): void {
    this.dirty = true;
    this.built = null;
  }

  readonly lib: Library;
  /** The built structures, merged into a few draw calls (see structbatch.ts). */
  private readonly structs: StructBatcher;
  /** The structures' far versions, made the first time the board is seen small (see drawBuilt). */
  private structsFar: StructBatcher | null = null;
  /** The holiday look in force (none on ordinary days). */
  readonly theme: SeasonTheme | undefined;
  private themeScene: ThemeScene | null = null;
  /** The ring pieces the rampart was last built with (a theme dresses them). */
  private ringPieces: RingPiece[] = [];
  private brazierBase = 12;
  private sky: { hemi: THREE.HemisphereLight; key: THREE.DirectionalLight; rim: THREE.DirectionalLight } | null = null;

  constructor(
    /** Replaced (never edited) when an earthwork changes terraced land (`setLand`). */
    public map: MapDef,
    readonly seed: number,
    readonly opts: ViewOptions,
  ) {
    this.lib = new Library(this.entityGlow);
    bakePool.warm();
    this.structs = new StructBatcher(this.entityGlow);
    this.theme = themeOf(opts.season);
    const look = this.theme?.look;
    const sc = this.scene;
    if (map.kind === 'dungeon') {
      // The crypt: black beyond the walls, torches and the party's own light.
      sc.background = new THREE.Color('#07080a');
      sc.fog = new THREE.Fog('#07080a', 26, 52);
      this.crypt = buildCrypt(map, seed, !!opts.exitHall);
      this.terrain = this.crypt;
      this.setupCryptLights(this.crypt);
    } else {
      // The edge of the world is a fog bank (outskirts.ts); the sky behind it is the same far mist.
      sc.background = look ? new THREE.Color(look.background) : MIST.far.clone();
      // Depth fog only far past the bastion, so the board itself reads clearly even from the far phone camera.
      sc.fog = look ? new THREE.Fog(look.fog.color, look.fog.near, look.fog.far) : new THREE.Fog(MIST.far.clone(), 38, 80);
      this.setupLights();
      if (map.land) {
        // The bastion from scratch: terraced land on a table, the far mist well past its rim.
        sc.fog = new THREE.Fog(MIST.far.clone(), 50, 110);
        this.land = buildLandMesh(map, seed, { low: opts.quality === 'low' });
      }
      this.terrain = this.land ?? buildTerrainMesh(map, seed, { low: opts.quality === 'low' });
      if (look) this.terrain.fog?.tint(look.mist);
      else if (this.somnhold()) this.terrain.fog?.tint(SOMN_SKY.mist);
    }
    this.root.add(this.terrain.mesh, this.terrain.decor);
    if (this.terrain.fog) sc.add(this.terrain.fog.group);
    sc.add(this.root, this.shadows.mesh, this.holy.group, this.aura.group, this.gearFx.group, this.skillFx.group, this.vfx.group, this.entityGlow.mesh, this.bars.mesh, this.overlay);
    this.gearFx.quality = opts.quality;
    this.skillFx.quality = opts.quality;
    this.aura.quality = opts.quality;
    this.root.add(this.lib.group);
    this.root.add(this.structs.group);
    // The scene, the board's root and the library never move: kept still, they no longer have three recompute every
    // matrix under them each frame (with a crypt's ~900 objects, 3% of a phone's busy time). What moves under them
    // still updates its own.
    sc.matrixAutoUpdate = false;
    this.root.matrixAutoUpdate = false;
    this.lib.group.matrixAutoUpdate = false;
    // Nothing under the library moves (each instance's place is in its own matrix): three's walk skips it.
    this.lib.group.matrixWorldAutoUpdate = false;
    if (!this.crypt && map.kind === 'bastion' && (map.districts?.length ?? 0) > 0) {
      this.dressing = new DistrictDressing(map, opts.quality === 'low');
      this.root.add(this.dressing.group);
    }
    if (!this.crypt && map.kind === 'bastion' && !map.land) this.buildRampart();
    if (this.somnhold()) this.buildSomnhold();
    // A forest node: woods ring the clearing, so an ambush visibly comes out of the trees.
    if (map.kind === 'incursion' && opts.centerpiece === 'node:forest') this.buildWoods(map, seed);
    // An iron mine: dark crags veined with rust ring the clearing.
    if (map.kind === 'incursion' && opts.centerpiece === 'node:iron') this.buildCrags(map, seed);
    // Selection ring under the local hero, on the floor: the miniature (and whatever stands in front of it)
    // covers it like any decal. Drawing it over everything made it read as a sticker pasted on the figure;
    // in a crowd your name tag is what finds you.
    this.heroRing = new THREE.Mesh(
      new THREE.RingGeometry(0.34, 0.42, 40).rotateX(-Math.PI / 2),
      decalMat({ color: '#ff4f3a', transparent: true, opacity: 0.85, depthWrite: false }),
    );
    this.heroRing.visible = false;
    this.overlay.add(this.heroRing);
    // Hover hex outline.
    const pts: THREE.Vector3[] = [];
    const rad = (WORLD_PER_HEX / Math.sqrt(3)) * 0.96;
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 180) * (60 * i - 30);
      pts.push(new THREE.Vector3(Math.cos(a) * rad, 0.04, Math.sin(a) * rad));
    }
    this.hover = new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(pts), new THREE.LineBasicMaterial({ color: '#eceef2', transparent: true, opacity: 0.9 }));
    this.hover.visible = false;
    this.overlay.add(this.hover);
    this.rangeRing = new THREE.Mesh(
      new THREE.RingGeometry(0.97, 1, 72).rotateX(-Math.PI / 2),
      decalMat({ color: '#eceef2', transparent: true, opacity: 0.35, depthWrite: false }),
    );
    this.rangeRing.visible = false;
    this.overlay.add(this.rangeRing);
    this.areaRing = new THREE.Mesh(
      new THREE.RingGeometry(0.97, 1, 72).rotateX(-Math.PI / 2),
      decalMat({ color: '#7fb8ff', transparent: true, opacity: 0.7, depthWrite: false }),
    );
    this.areaRing.visible = false;
    this.overlay.add(this.areaRing);
    this.selLine = new THREE.LineLoop(this.hover.geometry, new THREE.LineBasicMaterial({ color: '#ff4f3a' }));
    this.selLine.visible = false;
    this.overlay.add(this.selLine);
    this.dress();
  }

  /** A holiday theme dresses the outdoor board (the crypt keeps only the dressed nightmares and bursts). */
  private dress(): void {
    const theme = this.theme;
    const sky = this.sky;
    const brazier = this.lights[0];
    if (!theme || this.crypt || !sky || !brazier) return;
    let keep: THREE.Matrix4 | null = null;
    if (this.map.kind === 'bastion' && this.center().startsWith('keep')) {
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      keep = this.m.clone();
    }
    this.themeScene = theme.build({
      scene: this.scene,
      map: this.map,
      terrain: this.terrain,
      low: this.opts.quality === 'low',
      keep,
      lights: { ...sky, brazier },
      vfx: this.vfx,
      flare: (p, color, size, life) => this.flare(p, color, size, life),
      shake: (a) => {
        this.shake = Math.max(this.shake, a);
      },
    });
    this.scene.add(this.themeScene.group);
    if (this.ringPieces.length > 0) this.themeScene.ring?.(this.ringPieces);
  }

  /** Each district's banners and props (bastions with districts only). */
  private dressing: DistrictDressing | null = null;
  /** The Great Work's scaffold and the ghost of a district-to-be (made on first use). */
  private greatWork: GreatWorkLayer | null = null;

  private gw(): GreatWorkLayer | null {
    if (this.crypt || this.map.kind !== 'bastion') return null;
    if (!this.greatWork) {
      const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
      this.compose(kp, Math.PI / 6, this.lib.scaleOf(this.center()));
      const R = this.map.radius;
      const t = this.terrain.terrain;
      const wallNow = (q: number, r: number): boolean => {
        const id = cellId(R, q, r);
        return id >= 0 && (t[id] === T_RAMPART || t[id] === T_GATE);
      };
      this.greatWork = new GreatWorkLayer(this.map, this.m.clone(), wallNow, this.vfx, this.opts.quality === 'low', (x, z) => this.terrain.ground?.(x, z) ?? 0);
      this.scene.add(this.greatWork.group);
    }
    return this.greatWork;
  }

  /**
   * The Great Work under way on the keep (a campaign job with `kind: 'district'`), or null when there
   * is none. While set: scaffolding climbs the keep (a few posts and the stone waiting at the
   * blueprint stage; while building it fills in with `progress`, cranes swing and golems raise dust)
   * and the ghost of the district-to-be glows on its side (its wall grows with `progress`).
   * Cheap to call every frame; only a change of side or kind rebuilds anything.
   */
  setGreatWork(gw: GreatWorkView | null): void {
    if (!gw && !this.greatWork) return;
    this.gw()?.setWork(gw);
  }

  /**
   * The ghost of a district while a player picks a side in the UI (same look as the Great Work's,
   * red when `ok` is false), or null to clear it. Shown over the Great Work's ghost while set.
   */
  previewDistrict(p: DistrictPreview | null): void {
    if (!p && !this.greatWork) return;
    this.gw()?.setPreview(p);
  }

  /**
   * The district on `side` rises: its walls and towers climb out of the ground over ~1.5 s in a
   * shower of dust, a shaft of light stands over it and its banners unfurl. Call it once right after
   * building the view for a map that has just gained that district. It ends in the exact static
   * state (a no-op if the map has no district on that side).
   */
  celebrateDistrict(side: number): void {
    const d = this.map.districts?.find((x) => x.side === side);
    if (!d || this.crypt) return;
    const t = performance.now() / 1000;
    // The district's own ground (motes of light rise from it while the walls climb).
    const ground: THREE.Vector3[] = [];
    const R = this.map.radius;
    for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (districtAt(this.map, q, r)?.side === side) ground.push(cellWorld(q, r, 0.05));
    this.rising = { side, t0: t, burst: 0, ground };
    this.fillRing({ side, sink: 2.4 });
    this.themeScene?.ring?.(this.ringPieces);
    this.dressing?.rise(side, 0, 0);
    const c = districtCenter(this.map.bastion, side);
    const p = cellWorld(c.q, c.r, 0.05);
    this.gw()?.beam(p, d.kind, t);
    this.flare(p.clone().setY(1.2), '#ffffff', 3.6, 6);
    this.flare(p.clone().setY(0.6), '#cfe6ff', 6, 16);
    this.vfx.ring(p, 0.3, 5.2, '#eceef2', 14, 1);
    this.vfx.ring(p, 0.2, 3.4, '#cfe6ff', 10, 0.8);
    // The ground heaves all along where the wall will stand.
    for (const wc of this.walls?.cells.values() ?? []) {
      if (wc.district?.side !== side) continue;
      this.vfx.burst(wc.p.clone().setY(0.1), { n: this.opts.quality === 'low' ? 1 : 3, color: '#8f887c', speed: 0.04, up: 0.03, size: 0.6, life: 22, smoke: true, spread: 0.45 });
      this.vfx.burst(wc.p.clone().setY(0.15), { n: 3, color: '#6b655c', speed: 0.06, up: 0.1, size: 0.07, life: 12, gravity: 0.012 });
    }
    this.shake = Math.max(this.shake, 0.25);
  }

  /** The rising district, every frame until it stands. */
  private riseFrame(t: number): void {
    const rs = this.rising;
    if (!rs) return;
    const k = Math.min(1, (t - rs.t0) / 1.5);
    // Out of the ground fast, settling at the end (no overshoot: it lands exactly where it stands).
    const e = 1 - Math.pow(1 - k, 3);
    const unfurl = Math.min(1, Math.max(0, (t - rs.t0 - 1.05) / 0.75));
    this.dressing?.rise(rs.side, e, unfurl);
    if (k < 1) this.fillRing({ side: rs.side, sink: (1 - e) * 2.4 });
    else if (rs.burst >= 0) {
      // Standing: the exact static build, dressed by the season.
      this.fillRing();
      rs.burst = -1;
    }
    // Dust bursting along the wall as it climbs.
    const g = this.walls;
    if (g && k < 1 && t - rs.burst > 0.12) {
      rs.burst = t;
      const cells = [...g.cells.values()].filter((c) => c.district?.side === rs.side);
      for (let i = 0; i < (this.opts.quality === 'low' ? 2 : 5); i++) {
        const c = cells[Math.floor(Math.random() * cells.length)];
        if (!c) break;
        this.vfx.burst(c.p.clone().setY(0.15), { n: 3, color: '#8f887c', speed: 0.05, up: 0.04, size: 0.5, life: 18, smoke: true, spread: 0.5 });
        this.vfx.burst(c.p.clone().setY(0.2), { n: 4, color: '#6b655c', speed: 0.07, up: 0.09, size: 0.07, life: 10, gravity: 0.012 });
      }
      // Motes of light rising off the new ground.
      for (let i = 0; i < (this.opts.quality === 'low' ? 2 : 6); i++) {
        const gp = rs.ground[Math.floor(Math.random() * rs.ground.length)];
        if (!gp) break;
        this.vfx.burst(gp.clone().add(this.v.set((Math.random() - 0.5) * 0.9, 0.05, (Math.random() - 0.5) * 0.9)), { n: 1, color: i % 2 ? '#dbeeff' : '#ffffff', speed: 0.004, up: 0.035, size: 0.12, life: 26, gravity: -0.002, spread: 0.1 });
      }
    }
    if (k >= 1 && unfurl >= 1) this.rising = null;
  }

  private crypt: CryptBuild | null = null;
  /** One torch per hero in the crypt (warm light that follows them). */
  private torches = new Map<string, THREE.PointLight>();
  /** Torches lit by no one yet (ViewOptions.torches): dark, but counted. */
  private spareTorches: THREE.PointLight[] | null = null;
  /** In a party past TORCH_LIGHTS, the heroes whose torch burns. */
  private torchPick: Set<string> | null = null;
  private markMeshes: THREE.Mesh[] = [];
  private poolMeshes: THREE.Mesh[] = [];
  private markGeo = { disc: new THREE.CircleGeometry(1, 48).rotateX(-Math.PI / 2), edge: new THREE.RingGeometry(0.95, 1, 64).rotateX(-Math.PI / 2) };

  private cryptSky: { hemi: THREE.HemisphereLight | null; moon: THREE.DirectionalLight | null; rooms: THREE.PointLight[] } = { hemi: null, moon: null, rooms: [] };

  private setupCryptLights(c: CryptBuild): void {
    const sc = this.scene;
    // The Furrows lie open to the sky: more moon than in a crypt, so the walls' tops read against the corridors.
    const open = this.map.look === 'furrows';
    const hemi = new THREE.HemisphereLight(0x5a6478, 0x0a0a0e, open ? 1.25 : 0.9);
    const moon = new THREE.DirectionalLight(0x8a9cc8, open ? 1.1 : 0.7);
    moon.position.set(-10, 24, 8);
    sc.add(hemi, moon);
    this.cryptSky.hemi = hemi;
    this.cryptSky.moon = moon;
    // The deep crypt keeps only one floor's worth of room lights (they move with the party, see deep.ts).
    const L = this.map.dungeon as DungeonLayout | undefined;
    const perFloor = c.deep ? Math.max(...[0, 1, 2].map((f) => c.floors.filter((x) => x === f).length)) : c.roomLights.length;
    c.roomLights.slice(0, perFloor).forEach((p, i) => {
      const last = !c.deep && i === c.roomLights.length - 1;
      const boss = c.deep ? L?.rooms[i]?.kind === 'boss' : last && !c.exit;
      // Not in the scene: frame() lends its look to one of the scene's room lights while it reaches the view.
      const l = last && c.exit ? new THREE.PointLight(0xdfe8ff, 18, 10, 1.4) : new THREE.PointLight(boss ? (open ? 0xff6a50 : 0x9b4dff) : 0xff9a4a, boss ? (open ? 40 : 22) : 14, boss ? (open ? 14 : 11) : 8, 1.4);
      l.position.copy(p);
      this.lights.push(l);
      this.cryptSky.rooms.push(l);
    });
  }

  /**
   * The scene's room lights, a fixed number for the whole fight, each lending its look to one of the crypt's
   * room lights in view (`w`: how far it has faded in).
   */
  private roomSlots: { light: THREE.PointLight; src: THREE.PointLight | null; w: number }[] = [];
  private lastCull = 0;
  private frustum = new THREE.Frustum();
  private lightSphere = new THREE.Sphere();
  private viewProj = new THREE.Matrix4();
  private viewDir = new THREE.Vector3();
  private viewCenter = new THREE.Vector3();
  private inView: THREE.PointLight[] = [];

  /**
   * Every fragment shades every point light in the scene, lit or not, and a crypt has one per room (eight in
   * the crypt in embers, plus a torch per hero): a phone pays for all of them on every pixel. A room light
   * reaches nothing past its distance, so only the ones whose sphere touches the view can change what is on
   * screen; those are copied onto the scene's room lights and the rest cost nothing. The scene keeps the same
   * number of room lights all along (ROOM_SLOTS): a new count recompiles every lit material, a stall of up to a
   * second on a desktop GPU at each room and each new nightmare. With more in view than that, the ones nearest
   * the middle of the screen keep theirs and the farthest fades out, at the edge where it lit little.
   */
  private cullRoomLights(camera: THREE.Camera, now: number): void {
    const rooms = this.cryptSky.rooms;
    const first = !this.roomSlots.length;
    if (first) {
      // The Chasm's floors come and go: always the full count (a new count recompiles every lit material).
      for (let i = 0; i < (this.sima ? ROOM_SLOTS : Math.min(ROOM_SLOTS, rooms.length)); i++) {
        const light = new THREE.PointLight(0xffffff, 0);
        this.scene.add(light);
        this.roomSlots.push({ light, src: null, w: 0 });
      }
    }
    const step = first ? 1 : Math.min(1, (now - this.lastCull) / ROOM_FADE_S);
    this.lastCull = now;
    camera.updateMatrixWorld();
    this.frustum.setFromProjectionMatrix(this.viewProj.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    const want = this.inView;
    want.length = 0;
    for (const src of rooms) {
      if (src.intensity <= 0) continue;
      this.lightSphere.set(src.position, src.distance > 0 ? src.distance : Infinity);
      if (this.frustum.intersectsSphere(this.lightSphere)) want.push(src);
    }
    if (want.length > this.roomSlots.length) {
      // The middle of the screen on the floor.
      camera.getWorldDirection(this.viewDir);
      const t = this.viewDir.y < -0.01 ? -camera.position.y / this.viewDir.y : 0;
      const mid = this.viewCenter.copy(camera.position).addScaledVector(this.viewDir, t);
      want.sort((a, b) => a.position.distanceToSquared(mid) - b.position.distanceToSquared(mid));
      want.length = this.roomSlots.length;
    }
    // A light no longer wanted fades out and frees its slot; a wanted one takes a free slot and fades in.
    for (const sl of this.roomSlots) {
      if (sl.src && want.includes(sl.src)) sl.w = Math.min(1, sl.w + step);
      else if (sl.src && (sl.w -= step) <= 0) sl.src = null;
    }
    for (const src of want) {
      if (this.roomSlots.some((sl) => sl.src === src)) continue;
      const free = this.roomSlots.find((sl) => !sl.src);
      if (!free) break;
      free.src = src;
      free.w = step;
    }
    for (const { light, src, w } of this.roomSlots) {
      if (!src) {
        light.intensity = 0;
        continue;
      }
      light.color.copy(src.color);
      light.intensity = src.intensity * w;
      light.distance = src.distance;
      light.decay = src.decay;
      light.position.copy(src.position);
    }
  }

  /** The crypt each tick: doors, warnings on the floor, ink, and every hero's torch. */
  private drawCrypt(s: BattleState): void {
    const c = this.crypt;
    const d = s.dungeon;
    if (!c || !d) return;
    for (const door of c.doors) {
      const shut = !doorOpen(s, door.i);
      door.seal.material.opacity = shut && d.room === door.i + 1 ? 0.55 + (s.tick % 6 < 3 ? 0.15 : 0) : 0;
      door.bars.userData.target = shut ? 0 : 1;
    }
    // Warnings: red is where the blow lands, pale blue is where it doesn't. A slam or a nova is a red disc that
    // fills up as the hit comes, with ripples running out of it ("get away"); a ring is a red band beyond the line
    // around a pale blue refuge, with ripples closing in on the Warden ("come close"). Nova and ring share a
    // radius, so colour and motion are all that tell them apart: players couldn't read the old two violets.
    let k = 0;
    const take = (list: THREE.Mesh[], geo: THREE.BufferGeometry, color: string): THREE.Mesh => {
      let m = list[k];
      if (!m) {
        m = new THREE.Mesh(geo, decalMat({ color, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
        this.overlay.add(m);
        list.push(m);
      }
      // The pool is shared by discs, edges and bands: a mesh can come back for a different shape.
      m.geometry = geo;
      m.visible = true;
      (m.material as THREE.MeshBasicMaterial).color.set(color);
      return m;
    };
    const used: THREE.Mesh[] = [];
    const put = (geo: THREE.BufferGeometry, color: string, p: THREE.Vector3, y: number, scale: number, opacity: number): void => {
      const m = take(this.markMeshes, geo, color);
      m.position.copy(p).setY(y);
      m.scale.setScalar(Math.max(0.01, scale));
      (m.material as THREE.MeshBasicMaterial).opacity = opacity;
      used.push(m);
      k++;
    };
    const DANGER = '#ff4f3a';
    const SAFE = '#9fe4ff';
    const RIPPLE = 10;
    for (const mk of d.marks) {
      const p = simToWorld(mk.x, mk.y, new THREE.Vector3(), 0.04);
      const r = (mk.r / 1000) * WORLD_PER_HEX;
      const t = Math.max(0, Math.min(1, (s.tick - mk.from) / Math.max(1, mk.at - mk.from)));
      const blink = s.tick % 4 < 2 ? 0.35 : 0;
      if (mk.kind === 'ring') {
        put(this.ringBand(), DANGER, p, 0.04, r, 0.3 + t * 0.4);
        put(this.markGeo.disc, SAFE, p, 0.035, r, 0.22 + t * 0.15);
        // Two ripples falling onto the line from not too far out (over the red band they blend to pink).
        for (let i = 0; i < 2; i++) {
          const ph = ((s.tick - mk.from + (i * RIPPLE) / 2) % RIPPLE) / RIPPLE;
          put(this.markGeo.edge, SAFE, p, 0.045, r * (1.9 - 0.9 * ph), 0.35 + 0.5 * ph);
        }
        put(this.markGeo.edge, SAFE, p, 0.05, r, 0.55 + blink);
      } else {
        put(this.markGeo.disc, DANGER, p, 0.04, r * t, 0.42);
        put(this.markGeo.disc, DANGER, p, 0.035, r, 0.15);
        if (mk.kind === 'nova') {
          // Two ripples running out of the Warden past the line.
          for (let i = 0; i < 2; i++) {
            const ph = ((s.tick - mk.from + (i * RIPPLE) / 2) % RIPPLE) / RIPPLE;
            put(this.markGeo.edge, DANGER, p, 0.045, r * (0.2 + 1.3 * ph), 0.6 * (1 - ph));
          }
        }
        put(this.markGeo.edge, DANGER, p, 0.05, r, 0.55 + blink);
      }
    }
    for (const m of this.markMeshes) if (!used.includes(m)) m.visible = false;
    k = 0;
    for (const pool of d.pools) {
      const m = take(this.poolMeshes, this.markGeo.disc, '#5a1f8a');
      m.position.copy(simToWorld(pool.x, pool.y, new THREE.Vector3(), 0.03));
      m.scale.setScalar((pool.r / 1000) * WORLD_PER_HEX * (0.96 + (s.tick % 6) * 0.01));
      (m.material as THREE.MeshBasicMaterial).opacity = 0.45;
      if (s.tick % 4 === k % 4) this.vfx.burst(m.position.clone().setY(0.1), { n: 1, color: '#9b4dff', speed: 0.01, up: 0.03, size: 0.18, life: 8 });
      k++;
    }
    for (let i = k; i < this.poolMeshes.length; i++) (this.poolMeshes[i] as THREE.Mesh).visible = false;
    // Torches: every hero standing carries one.
    const lowQ = this.opts.quality === 'low';
    // A new count of lights recompiles every lit material (a stall of seconds on an older GPU): a live party's
    // torches are all there from the start, dark until someone joins.
    if (!this.spareTorches) {
      this.spareTorches = [];
      for (let i = 0; i < (lowQ ? 0 : Math.min(TORCH_LIGHTS, this.opts.torches ?? 0)); i++) {
        const l = new THREE.PointLight(0xffb070, 0, 5.5, 1.5);
        this.scene.add(l);
        this.spareTorches.push(l);
      }
    }
    // A party past TORCH_LIGHTS: every lit fragment pays for every light, so only mine and the closest friends'
    // burn; the rest walk in their light. Picked again once a second, so a light doesn't hop between two friends.
    if (!lowQ && s.heroes.length > TORCH_LIGHTS && (s.tick % 12 === 0 || !this.torchPick)) {
      const me = s.heroes.find((h) => h.pid === this.opts.me);
      const d = (h: (typeof s.heroes)[number]): number => (h === me ? -1 : me ? (h.x - me.x) ** 2 + (h.y - me.y) ** 2 : h.id);
      this.torchPick = new Set([...s.heroes].sort((a, b) => d(a) - d(b) || a.id - b.id).slice(0, TORCH_LIGHTS).map((h) => h.pid));
      for (const [pid, l] of this.torches) {
        if (this.torchPick.has(pid)) continue;
        l.intensity = 0;
        this.spareTorches.push(l);
        this.torches.delete(pid);
      }
    }
    for (const h of s.heroes) {
      if (this.torchPick && !lowQ && s.heroes.length > TORCH_LIGHTS && !this.torchPick.has(h.pid)) continue;
      let l = this.torches.get(h.pid);
      if (!l && (!lowQ || h.pid === this.opts.me)) {
        l = this.spareTorches.pop();
        if (!l) {
          l = new THREE.PointLight(0xffb070, 9, 5.5, 1.5);
          this.scene.add(l);
        }
        this.torches.set(h.pid, l);
      }
      if (!l) continue;
      const p = simToWorld(h.x, h.y);
      l.position.set(p.x, 1.6, p.z);
      l.intensity = h.downTicks > 0 ? 2 : 8 + ((s.tick * 31 + h.id * 7) % 5) * 0.5;
      // In the Chasm the torches lean toward the stratum's light (a colour, never another light).
      if (this.sima) l.color.copy(this.sima.torch);
    }
    c.runes.userData.boss = c.deep ? (s.map.dungeon?.rooms[d.room]?.kind === 'boss') : d.room === c.roomLights.length - 1;
    this.drawSeal(s);
    this.deep?.sync(d.deep, s.tick, this.fresh);
    if (this.sima && d.sima) this.sima.sync(d.sima, s.zones, s.tick, this.fresh);
    if (this.deep) {
      this.ascuas?.ambient(this.deep.current === 1);
      this.obsidian?.ambient(this.deep.current === 2);
    }
    this.puzzles?.sync(d.puzzles, s.tick, this.fresh);
    this.cellars?.sync(s, this.fresh, (id) => this.positions.get(id));
    if (this.fresh) this.maze?.sync(s);
  }

  private ringBandGeo: THREE.BufferGeometry | null = null;
  /** Annulus from 1 to 4 (scaled by the safe radius): the floor outside the line. */
  private ringBand(): THREE.BufferGeometry {
    this.ringBandGeo ??= new THREE.RingGeometry(1, 4, 64).rotateX(-Math.PI / 2);
    return this.ringBandGeo;
  }

  private setupLights(): void {
    const sc = this.scene;
    // Candle-lit DM table: warm key, cold rim, dim hemisphere (intensities are physically based in r155+).
    const look = this.theme?.look;
    const hemi = look ? new THREE.HemisphereLight(look.hemi[0], look.hemi[1], look.hemi[2]) : new THREE.HemisphereLight(0xb4b8c4, 0x3a3630, 2.4);
    const key = look ? new THREE.DirectionalLight(look.key[0], look.key[1]) : new THREE.DirectionalLight(0xffe2bc, 3.3);
    if (look) key.position.set(...look.key[2]);
    else key.position.set(-14, 22, 12);
    const rim = look ? new THREE.DirectionalLight(look.rim[0], look.rim[1]) : new THREE.DirectionalLight(0x9fb4ff, 1.8);
    rim.position.set(16, 12, -16);
    sc.add(hemi, key, rim);
    this.sky = { hemi, key, rim };
    // Era V (Somnhold): a colder, bluer night of dreams, unless a holiday dresses the board (omens re-light over it).
    if (!look && this.somnhold()) {
      hemi.color.set(SOMN_SKY.hemi[0]);
      hemi.groundColor.set(SOMN_SKY.hemi[1]);
      hemi.intensity = SOMN_SKY.hemi[2];
      key.color.set(SOMN_SKY.key[0]);
      key.intensity = SOMN_SKY.key[1];
      rim.color.set(SOMN_SKY.rim[0]);
      rim.intensity = SOMN_SKY.rim[1];
      sc.background = new THREE.Color(SOMN_SKY.background);
      if (sc.fog instanceof THREE.Fog) sc.fog.color.set(SOMN_SKY.fog);
    }
    // Keep brazier and a few torch lights (era V: the Dream Beacon's blue-white, beating with it).
    if (look) this.brazierBase = look.brazier;
    const brazier = new THREE.PointLight(this.somnhold() ? SOMN_SKY.brazier : 0xff9a3a, look ? look.brazier : 14, look ? 11 : 9, 1.6);
    // The keep's fire rises with the era (the keep grows a stage each one).
    brazier.position.set(0, [2.6, 3.4, 3.9, 4.6, 5.0][this.keepEra() - 1] as number, 0);
    sc.add(brazier);
    this.lights.push(brazier);
  }

  /** Yaw of each gateway cell ("q,r"), aligned with the rampart runs beside it. */
  private gateYaw = new Map<string, number>();
  /** Outward yaw of each wall cell ("q,r"): which way its tower faces and its breach spills. */
  private wallYaw = new Map<string, number>();
  /** Damaged wall cells ("q,r:x;q,r:2", x = broken) the rampart was last built with. */
  private breachKey = '';
  /** Ring batch keys filled by the last build, so a rebuild can empty the ones no longer used. */
  private ringKeys = new Set<string>();
  /** The shape of the walls (the map is static per view, so it is worked out once). */
  private walls: WallGraph | null = null;
  /** The last build's pieces, each with the side of the district it belongs to (-1: the old ring). */
  private ringBuilt: { key: string; m: THREE.Matrix4; side: number }[] = [];
  /** Which wall cells carry a big tower in the last build. */
  private bigTowers = new Set<string>();
  /** Hilde's ring on a clearing (a bet, `rules.unscathed`): its wall cells, drawn as a rampart ring instead of loose walls. */
  private hildeRing: Set<string> | null = null;
  /** Brann's ring (bet 9, `rules.openGate`): the one cell of it that is its gateway, open (the ring's runs stop at it). */
  private ringGate = '';
  /** A district's walls rising out of the ground (celebrateDistrict), seconds on the frame clock. */
  private rising: { side: number; t0: number; burst: number; ground: THREE.Vector3[] } | null = null;

  /**
   * Builds (or rebuilds) the walls around the whole walled ground, the old ring and every district:
   * a curtain run between every two joined wall cells (see wallgraph.ts), a round tower where the wall
   * turns (one per bend, the sharpest) and a buttress on straight stretches. Runs toward a gateway stop
   * at its edge, so the gatehouse (or the open path, before it is built) fits between them.
   *
   * A broken cell loses its tower and the runs touching it; its intact neighbors keep a half run toward
   * it, so the breach is one cell wide and the rubble fills it. Standing pieces wear with their cells
   * (`wear`, "q,r" → 1 worn, 2 crumbling); a run between two cells takes the worse of them.
   */
  private buildRampart(broken: ReadonlySet<string> = new Set(), wear: ReadonlyMap<string, number> = new Map()): void {
    const R = this.map.radius;
    const at = (q: number, r: number): number => {
      const id = cellId(R, q, r);
      return id < 0 ? -1 : (this.terrain.terrain[id] as number);
    };
    const ring = this.hildeRing;
    const g = (this.walls ??= wallGraph(
      this.map,
      ring ? (q, r) => ring.has(`${q},${r}`) : (q, r) => at(q, r) === T_RAMPART || at(q, r) === T_GATE,
      ring ? (q, r) => `${q},${r}` === this.ringGate : (q, r) => at(q, r) === T_GATE,
    ));
    const pieces: { key: string; m: THREE.Matrix4; side: number }[] = [];
    const push = (key: string, w: number, m: THREE.Matrix4, side: number): void => {
      pieces.push({ key: w > 0 ? `${key}:${w}` : key, m, side });
    };
    const one = new THREE.Vector3(1, 1, 1);
    /** A run from a to b, its outer face (+Z) toward `out`. */
    const run = (a: THREE.Vector3, b: THREE.Vector3, out: THREE.Vector2): THREE.Matrix4 => {
      const mid = a.clone().add(b).multiplyScalar(0.5);
      let yaw = Math.atan2(-(b.z - a.z), b.x - a.x);
      if (Math.sin(yaw) * out.x + Math.cos(yaw) * out.y < 0) yaw += Math.PI;
      this.q.setFromAxisAngle(this.yAxis, yaw);
      return new THREE.Matrix4().compose(mid, this.q, one);
    };
    const sideOf = (c: WallCell): number => c.district?.side ?? -1;
    // Era V: the whole ring is of the titan's stone (the keep's era, so a centerpiece view keeps the plain ring).
    const titan = this.keepEra() >= 5 ? 'Titan' : '';
    for (const l of g.links) {
      const { a, b } = l;
      // A run that closes a district rises with it, even where it starts on the old ring.
      const side = Math.max(sideOf(a), sideOf(b));
      if (a.gate && b.gate) continue;
      if (a.gate || b.gate) {
        const [w, gc] = a.gate ? [b, a] : [a, b];
        const m = run(w.p, w.p.clone().lerp(gc.p, 0.5), l.out);
        // The gatehouse lines up with the runs on both sides of it.
        if (!this.gateYaw.has(gc.key)) {
          this.q.setFromRotationMatrix(m.clone().setPosition(0, 0, 0));
          this.gateYaw.set(gc.key, new THREE.Euler().setFromQuaternion(this.q, 'YXZ').y);
        }
        if (!broken.has(w.key)) push(`curtain:half${titan}`, wear.get(w.key) ?? 0, m, side);
        continue;
      }
      const da = broken.has(a.key);
      const db = broken.has(b.key);
      if (da && db) continue;
      if (da || db) {
        const [s, o] = da ? [b, a] : [a, b];
        push(`curtain:half${titan}`, wear.get(s.key) ?? 0, run(s.p, s.p.clone().lerp(o.p, 0.5), l.out), side);
        continue;
      }
      push(`curtain:full${titan}`, Math.max(wear.get(a.key) ?? 0, wear.get(b.key) ?? 0), run(a.p, b.p, l.out), side);
    }
    // How sharply the wall bends at a cell: 0 straight, 1 a gentle turn, 2 a sharp one, 3 an end.
    const bend = (c: WallCell): number => {
      if (c.links.length !== 2) return 3;
      const d = Math.abs((c.links[0] as number) - (c.links[1] as number));
      return 3 - Math.min(d, 6 - d);
    };
    const near = (c: WallCell, i: number): WallCell | undefined => g.cells.get(`${c.q + DIRS[i]!.q},${c.r + DIRS[i]!.r}`);
    this.bigTowers.clear();
    for (const c of g.cells.values()) {
      this.wallYaw.set(c.key, yawOf(c.out));
      if (c.gate || broken.has(c.key)) continue;
      const k = bend(c);
      // One big tower per bend: where two turns touch, the sharper one (then the first in row order) takes it.
      let big = k > 0 && !c.links.some((i) => near(c, i)?.gate);
      if (big) {
        for (const i of c.links) {
          const n = near(c, i);
          if (!n || n.gate) continue;
          const kn = bend(n);
          if (kn > k || (kn === k && (n.r < c.r || (n.r === c.r && n.q < c.q)))) big = false;
        }
      }
      if (big) this.bigTowers.add(c.key);
      this.q.setFromAxisAngle(this.yAxis, yawOf(c.out));
      const vigil = this.keepEra() >= 5 ? 'Titan' : this.keepEra() >= 4 ? 'Vigil' : '';
      push(big ? `rampartTower:big${vigil}` : `rampartTower:small${vigil}`, wear.get(c.key) ?? 0, new THREE.Matrix4().compose(c.p, this.q, one), sideOf(c));
    }
    this.ringBuilt = pieces;
    this.fillRing();
    this.dressing?.walls(g, this.bigTowers, broken, wear);
  }

  /**
   * Writes the last build's pieces into their batches. While a district rises, its pieces sit `sink`
   * below their place (the ground hides what is under it).
   */
  private fillRing(rise?: { side: number; sink: number }): void {
    const lists = new Map<string, THREE.Matrix4[]>();
    const lifted = new THREE.Matrix4();
    for (const pc of this.ringBuilt) {
      let l = lists.get(pc.key);
      if (!l) lists.set(pc.key, (l = []));
      if (rise && pc.side === rise.side) l.push(lifted.makeTranslation(0, -rise.sink, 0).multiply(pc.m).clone());
      else l.push(pc.m);
    }
    for (const key of new Set([...this.ringKeys, ...lists.keys()])) {
      const batch = this.lib.batch(key);
      batch.begin();
      for (const mm of lists.get(key) ?? []) batch.add(mm, 0);
      batch.end();
    }
    this.ringKeys = new Set(lists.keys());
    // A holiday dresses the standing walls (a rising district only once it stands).
    this.ringPieces = this.ringBuilt.filter((pc) => !rise || pc.side !== rise.side).map(({ key, m }) => ({ key, m }));
    if (!rise || rise.sink === 0) this.themeScene?.ring?.(this.ringPieces);
  }

  /**
   * Every animation frame, `alpha` of the way from the last tick to the next: redraws what moved between the
   * last two ticks part of the way (one tick behind). Nothing moving, nothing to do.
   */
  interpolate(s: BattleState, alpha: number): void {
    // Glides run a little longer than a tick (see Track): alpha may pass 1 while the next tick is late.
    const a = Math.max(0, Math.min(1, alpha * GLIDE));
    this.vfx.alpha = s.tick === this.lastTick ? a : 0;
    const t = this.track;
    if (s.tick !== this.lastTick) return;
    // A tick taken in but not drawn yet (deferDraw) is drawn whatever moved.
    const owed = this.owed;
    // Animated figures move every frame; with `?anim=0` only what moves between ticks needs a redraw.
    if (!owed && !ANIM.on && !this.leading && !(t.hero.moving || t.enemy.moving || t.bot.moving || t.proj.moving)) return;
    const f = Math.max(0, Math.min(1, alpha));
    if (!owed && a === this.alpha && f === this.frac && !this.leading) return;
    this.alpha = a;
    this.frac = f;
    this.dirty = true;
    this.sync(s);
  }

  /**
   * The drawing waits for the frame (`interpolate`): a new tick only takes in its events. Ticks that come faster
   * than frames (a slow phone catching up, a burst of a live room's ticks) are then drawn once, not once each and
   * again for the frame. For a screen that calls `interpolate` before every render.
   */
  deferDraw = false;
  /** A tick taken in and not drawn yet. */
  private owed = false;

  /** Called once per sim tick with the new state and its events. */
  sync(s: BattleState): void {
    const fresh = s.tick !== this.lastTick;
    if (!fresh && !this.dirty) return;
    if (this.map.land?.open && s.started && !this.rifts) this.openRifts();
    if (fresh && s.rifts) this.syncMidRifts(s);
    if (fresh) {
      this.lastTick = s.tick;
      this.takeTick(s);
      this.owed = true;
      if (this.deferDraw) {
        this.dirty = true;
        return;
      }
    }
    this.dirty = false;
    this.draw(s);
  }

  /** The animation clock at this tick and frame (a clock gone back is another battle in this view: its clips start afresh). */
  private advanceClock(s: BattleState): void {
    const clock = (s.tick + this.frac) / TICK_HZ;
    if (clock < this.now - 1) {
      this.now = clock;
      this.animator.clear();
      this.shots.clear();
      this.since.clear();
      this.corpses = [];
    }
    this.now = Math.max(this.now, clock);
  }

  /** What a new tick brings, drawn or not: its events, particles, layers and the countdowns that go by ticks. */
  private takeTick(s: BattleState): void {
    this.fresh = true;
    // The new tick is first drawn where the last one was; the frames that follow walk it over.
    this.alpha = 0;
    this.frac = 0;
    this.vfx.alpha = 0;
    this.advanceClock(s);
    this.sweepAnim();
    this.vfx.muted = false;
    if (!this.hildeRing && s.rules?.unscathed && this.map.kind === 'incursion') {
      this.hildeRing = new Set(s.structures.filter((st) => st.type === 'wall').map((st) => `${st.q},${st.r}`));
      this.buildRampart();
    }
    // Bet 9: Brann's ring, drawn as a rampart like Hilde's, with its gateway where the gap is, and the gate in it open.
    if (!this.hildeRing && s.rules?.openGate && this.map.kind === 'incursion' && !this.crypt) {
      const g = s.rules.openGate;
      this.ringGate = `${g.q},${g.r}`;
      this.hildeRing = new Set([...s.structures.filter((st) => st.type === 'wall').map((st) => `${st.q},${st.r}`), this.ringGate]);
      this.buildRampart();
      this.openGate = new OpenGateLayer(this.omenCtx(), g, this.gateYaw.get(this.ringGate) ?? Math.atan2(hexToWorld(g.q, g.r).x, hexToWorld(g.q, g.r).y));
      this.scene.add(this.openGate.group);
    }
    if ((!this.crypt && this.map.kind === 'bastion') || this.hildeRing) this.syncBreaches(s);
    if (!this.stakesTried && s.rules?.alone && this.map.kind === 'incursion' && !this.crypt) {
      this.stakesTried = true;
      this.stakes = buildStakes(this.omenCtx(), this.map, s.rules.alone.road);
      if (this.stakes) this.scene.add(this.stakes);
    }
    if (!this.ascuas && s.rules?.tier === 2 && s.mode === 'incursion' && !this.crypt) this.startAscuas();
    // The dragon's fight with La Quemadura's beacon: its light on the middle of the field, and the beacon far off.
    if (s.rules?.dragon?.beaconR) this.beaconLight(s);
    if (!this.ascuas && s.rules?.tier === 2 && this.crypt) this.startCryptAscuas();
    if (!this.obsidian && s.rules?.tier === 3 && s.mode === 'incursion' && !this.crypt) this.startObsidian();
    // The Chasm: one floor at a time, rebuilt as the party goes down (before this tick's events: a descent's cut).
    if (!this.sima && this.crypt && s.dungeon?.sima) this.startSima();
    if (this.sima) this.simaFloor(s);
    if (!this.deep && s.rules?.tier === 3 && this.crypt && !s.dungeon?.sima) this.startDeep();
    if (!this.cellars && this.crypt && s.dungeon?.exp) this.startCellars(s.dungeon.exp);
    if (!this.plunder && !this.crypt && s.plunder) this.startPlunder(s.rules?.place === 'camp');
    if (!this.maze && this.crypt && s.dungeon?.maze) this.startMaze(s.dungeon.maze);
    // El pozo 2: the crypt re-dressed as the Order's waterworks under the well, and the flood.
    if (!this.pipes && this.crypt && s.rules?.place === 'pipes') this.startPipes();
    // La Orilla 4: the crypt dressed as the dragon's skeleton on the bare sea floor (and the sea coming back).
    if (!this.ribs && this.crypt && s.rules?.place === 'ribs') this.startRibs(!!s.rules.drinker?.up);
    // El Párpado 1: the crypt dressed as the titan's neck, its folds breathing; up top, the Order's post at night.
    if (!this.neck && this.crypt && s.rules?.place === 'neck') this.startNeck();
    // The warrior's path, chapter 1: the crypt as the Company's burnt guard house.
    if (!this.guardhouse && this.crypt && s.rules?.place === 'guardhouse') {
      this.guardhouse = new GuardhouseCrypt(this.omenCtx(), this.map, this.crypt, this.cryptSky.rooms);
      this.scene.add(this.guardhouse.group);
    }
    if (!this.orderPost && !this.crypt && (s.rules?.place === 'post' || s.rules?.place === 'slope')) this.startOrderPost(s.rules.place === 'slope' ? 'slope' : 'post', !!s.rules.night, s.rules.winch?.at ?? []);
    // The warrior's path (docs/sendas/guerrero.md): the Spine's north face, its places.
    if (!this.spine && !this.crypt && s.rules?.place && SPINE_PLACES.has(s.rules.place)) this.startSpine(s);
    // The paladin's path (docs/sendas/paladin.md): the Way of the Altars, its places.
    if (!this.altars && !this.crypt && s.rules?.place && ALTAR_PLACES.has(s.rules.place)) this.startAltars(s);
    // The artificer's path (docs/sendas/artifice.md): Nadd's workshop on the Flank Cliff, its places.
    // (Not a board of the artificer's chapter 5: its fights take chapter 1's hall and chapter 3's wall by name.)
    if (!this.taller && !this.crypt && s.rules?.place && TALLER_PLACES.has(s.rules.place) && !tormoBoard(s)) this.startTaller(s);
    // The mage's path (docs/sendas/mago.md): the burnt library and the stilts of the fin, their places.
    if (!this.biblio && !this.crypt && s.rules?.place && BIBLIO_PLACES.has(s.rules.place)) this.startBiblio(s);
    // The artificer's chapter 2 (docs/sendas/artifice.md): the cable across the abyss, its places.
    if (!this.cable && !this.crypt && s.rules?.place && CABLE_PLACES.has(s.rules.place)) this.startCable(s);
    // The artificer's chapter 3 (docs/sendas/artifice.md): the Hondura's deep road and the river of embers, its places.
    if (!this.corazon && !this.crypt && s.rules?.place && CORAZON_PLACES.has(s.rules.place) && !tormoBoard(s)) this.startCorazon(s);
    // The artificer's chapter 4 (docs/sendas/artifice.md): Grada in the memory, the mine in the glacier, the drill hall.
    if (!this.ojos && !this.crypt && s.rules?.place && OJOS_PLACES.has(s.rules.place)) this.startOjos(s);
    // The artificer's chapter 5 (docs/sendas/artifice.md): Tormo's hall, the winch under the Raja, the workshop's yard, Grada.
    if (!this.tormoL && !this.crypt && s.rules?.place && tormoBoard(s)) this.startTormo(s);
    // The barbarian's path (docs/sendas/barbaro.md): the Long Hunt on the tail, its places.
    if (!this.caza && !this.crypt && s.rules?.place && CAZA_PLACES.has(s.rules.place)) this.startCaza(s);
    if (!this.vert && !this.crypt && s.rules?.place && VERT_PLACES.has(s.rules.place)) this.startVert(s);
    if (!this.bote && !this.crypt && s.rules?.place && BOTE_PLACES.has(s.rules.place)) this.startBote(s);
    if (!this.isla && !this.crypt && s.rules?.place && ISLA_PLACES.has(s.rules.place)) this.startIsla(s);
    if (!this.roca && !this.crypt && s.rules?.place && ROCA_PLACES.has(s.rules.place)) this.startRoca(s);
    // The cleric's path (docs/sendas/clerigo.md): the south face at night, Musgoviejo, the terraces, the hairpins.
    if (!this.camino && !this.crypt && s.rules?.place && CAMINO_PLACES.has(s.rules.place)) this.startCamino(s);
    // Its chapter 2: the cliff of the south face under the full moon, over the fog (engine/pasarela.ts).
    if (!this.pasarela && !this.crypt && s.rules?.place && PASARELA_PLACES.has(s.rules.place)) this.startPasarela(s);
    // Its chapter 3: the col in the blizzard, the frozen mirrors, the corral (engine/nieve.ts).
    if (!this.nieve && !this.crypt && s.rules?.place && NIEVE_PLACES.has(s.rules.place)) this.startNieve(s);
    // Its chapter 4: the Hermitage, its nave (engine/ermita.ts); the atrium is a crypt re-dressed.
    if (!this.ermita && !this.crypt && s.rules?.place && ERMITA_PLACES.has(s.rules.place)) this.startErmita(s);
    if (!this.atrium && this.crypt && s.rules?.place === 'ermitaAtrium') {
      this.atrium = new AtriumCrypt(this.omenCtx(), this.map, this.crypt, this.cryptSky.rooms);
      this.scene.add(this.atrium.group);
    }
    // The druid's path (docs/sendas/druida.md): the Circle's wood under the Grandsire, its places.
    if (!this.bosque && !this.crypt && s.rules?.place && BOSQUE_PLACES.has(s.rules.place)) this.startBosque(s);
    if (!this.hornos && !this.crypt && s.rules?.place && HORNOS_PLACES.has(s.rules.place)) this.startHornos(s);
    if (!this.otono && !this.crypt && s.rules?.place && OTONO_PLACES.has(s.rules.place)) this.startOtono(s);
    if (!this.copa && !this.crypt && s.rules?.place && COPA_PLACES.has(s.rules.place)) this.startCopa(s);
    // The druid's chapter 5: inside the Grandsire (and the bark, a crypt).
    if (!this.raices && !this.crypt && s.rules?.place && RAICES_PLACES.has(s.rules.place)) this.startRaices(s);
    if (!this.bark && this.crypt && s.rules?.place === 'bark') {
      this.bark = new BarkCrypt(this.omenCtx(), this.map, this.crypt, this.cryptSky.rooms);
      this.scene.add(this.bark.group);
    }
    // The ranger's path (docs/sendas/explorador.md): Aster's stones, the rim of the Furrows, its places.
    if (!this.asterL && !this.crypt && s.rules?.place && ASTER_PLACES.has(s.rules.place)) this.startAster(s);
    if (!this.salt && !this.crypt && s.rules?.place && SALT_PLACES.has(s.rules.place)) this.startSalt(s);
    if (!this.arch && !this.crypt && s.rules?.place && ARCH_PLACES.has(s.rules.place)) this.startArch(s);
    if (!this.monastery && !this.crypt && s.rules?.place && MONASTERY_PLACES.has(s.rules.place)) this.startMonastery(s);
    // The paladin's chapter 5: the keep's yard, its cistern, its top; and the roots under it (a crypt).
    if (!this.seventh && !this.crypt && s.rules?.place && SEVENTH_PLACES.has(s.rules.place)) this.startSeventh(s);
    if (!this.roots && this.crypt && s.rules?.place === 'roots') {
      this.roots = new RootsCrypt(this.omenCtx(), this.map, this.crypt, this.cryptSky.rooms);
      this.scene.add(this.roots.group);
    }
    // El Párpado 2: the Order's bell, dragged up the slope (the escort's caravan), then wound up into its frame.
    if (!this.orderBell && !this.crypt && ((s.rules?.climb && s.escort) || s.rules?.winch || s.rules?.toll)) this.startBell(s);
    // El Párpado 4: the night of the bell at the group's own bastion (dusk deepening, or dawn coming), and the allies.
    if (!this.bellNight && !this.crypt && s.mode === 'wave' && s.rules?.night) this.startBellNight(s);
    // The race (bet 8): Hilde's hourglass in the first room, running with the clock.
    if (!this.race && this.crypt && s.rules?.race && this.map.dungeon) {
      this.race = new RaceLayer(this.omenCtx(), this.map.dungeon, s.rules.race.limit);
      this.scene.add(this.race.group);
    }
    if (!this.ink && s.rules?.omen === 'ink') this.startInk();
    if (!this.omenTried && (s.rules?.omen || betOf(s) === 'dark')) this.startOmen(betOf(s) === 'dark' ? 'night' : (s.rules?.omen as string), s.mode === 'wave' || !!s.rules?.towerRangeBp || !!s.rules?.skyBolts || !!s.rules?.towerReloadBp);
    // A side saga's fight (docs/SAGAS.md): the village it happens in, and the clues lying on the field.
    if (!this.villageTried && s.rules?.place === 'village' && !this.crypt) this.startVillage(!!s.rules.night, s.rules.clues ?? [], !!s.rules.well);
    // El pozo 1: the well on the node, the houses round the square, the mill and its stone.
    if (!this.wellSquare && s.rules?.place === 'village' && s.rules.well && !this.crypt) this.startWell(s.rules.well);
    // El pozo 3: fair night in the same village, its houses round the edge; the shut well, the stalls and their wares.
    if (!this.villageTried && s.rules?.place === 'fair' && !this.crypt) {
      this.villageTried = true;
      this.village = buildVillage(this.omenCtx(), this.map, !!s.rules.night, [], false, false);
      this.scene.add(this.village.group);
    }
    if (!this.fairSquare && s.rules?.place === 'fair' && s.rules.fair && !this.crypt) this.startFair(s.rules.fair);
    // La Orilla 1: the stilt-houses on the fin, the bare sea floor out to the reef, and the water coming back.
    if (!this.shore && s.rules?.place === 'reef' && s.rules.tide && !this.crypt) this.startShore(s.rules.tide);
    // La Orilla 2: the stilt-houses with the sea in, the lanes from the sea, the posts, and the titan's fin far out.
    if (!this.finCoast && s.rules?.place === 'fin' && s.rules.fin && !this.crypt) this.startFin(s.rules.fin, s.rules.fin.breath ? 'breath' : s.rules.sunken?.boss ? 'big' : 'wave');
    // La Orilla 5: over the fin's coast, the dragon's heart, Yorra's boats, the breath's band and the harpoon's rope.
    if (!this.sunken && this.finCoast && s.rules?.sunken && !this.crypt) this.startSunken(s.rules.sunken);
    // La Orilla 3: the lighthouse at the fin's tip, its braziers, the sea, and the stalk of the Maw's light.
    if (!this.lighthouse && s.rules?.place === 'lighthouse' && s.rules.maw && !this.crypt) this.startLighthouse(s.rules.maw);
    // Caras prestadas 3: the forest road at night, the pit, and the two to bring back.
    if (!this.villageTried && s.rules?.place === 'woods' && !this.crypt) {
      this.villageTried = true;
      this.village = buildWoods(this.omenCtx(), this.map, s.rules.rescue?.pit ?? null);
      this.scene.add(this.village.group);
    }
    // El Cuerno 1: the tip of the tail, its corral and the stone boars driven to it.
    if (!this.villageTried && s.rules?.place === 'tail' && !this.crypt) {
      this.villageTried = true;
      const hd = s.rules.herd;
      const ls = s.rules.lash;
      // El Cuerno 2: the clan's summer camp round the node, its mooring posts and barrels.
      this.village = buildTail(this.omenCtx(), this.map, hd ? { start: hd.start, pen: hd.pen, penR: hd.penR } : null, ls ? { posts: ls.posts, barrels: ls.barrels, axis: ls.axis } : null);
      this.scene.add(this.village.group);
    }
    // El Cuerno 4: the clan's graveyard on the tail, at night.
    if (!this.villageTried && s.rules?.place === 'grave' && !this.crypt) {
      this.villageTried = true;
      this.village = buildTail(this.omenCtx(), this.map, null, null, { grave: true });
      this.scene.add(this.village.group);
    }
    // La Quemadura 2: the camp of centuries ago half sunk in the Burn's glass, Orla's cart in the middle.
    if (!this.villageTried && s.rules?.place === 'camp' && !this.crypt) {
      this.villageTried = true;
      if (!this.obsidian) this.startObsidian();
      this.burnDusk();
      this.village = buildCamp(this.omenCtx(), this.map, s.plunder?.caches ?? []);
      this.scene.add(this.village.group);
    }
    // La Quemadura 3: deeper in the Burn, what is left of the camp, and the first Vigil's harpoon engine.
    if (!this.trapLayer && s.rules?.place === 'trap' && s.rules.trap && !this.crypt) this.startTrap(s.rules.trap);
    // The Turtle's last fight: the dragon's own clearing in the Burn, its old harpoons, its scales, its fury's glass.
    if (!this.dragonField && s.rules?.dragon && !this.crypt) this.startDragonField(s.rules.dragon);
    // La Quemadura 4: the first Vigil's watchtower in the glass, the cracks round it, Tobin's powder.
    if (!this.blastLayer && s.rules?.place === 'tower' && s.rules.blast && !this.crypt) this.startBlast(s, s.rules.blast);
    // Las vías 1: the clan's station deep under the shell, the rail round it, the train with no brake, the bridge.
    if (!this.mine && s.rules?.place === 'mine' && s.rules.rails && s.escort && s.rails && !this.crypt) this.startMine(s);
    // Las vías 2: Dorn's halls under the mine, the alarm bells and their levers, his guards' plinths.
    if (!this.halls && s.rules?.place === 'halls' && s.rules.bells && !this.crypt) this.startHalls(s.rules.bells);
    // Las vías 3: the Deep Anvil's hall, the Anvil in the middle, the vein, the beat's rings.
    if (!this.anvilHall && s.rules?.place === 'anvil' && s.rules.anvil && !this.crypt) this.startAnvil(s.rules.anvil);
    // Las vías 4: the bastion's own forge yard, the forge on the node, the fast way coming up, Bruni's marks.
    // Bet 7, Beside: Hilde's towers lit only with a hero by them.
    if (!this.beside && s.rules?.beside && !this.crypt) {
      this.beside = new BesideLayer(this.omenCtx(), s.structures.filter((st) => !st.destroyed), s.rules.beside);
      this.scene.add(this.beside.group);
    }
    if (!this.forgeYard && s.rules?.place === 'forge' && s.rules.rising && !this.crypt) this.startForge(s.rules.rising);
    // La Quemadura 1: the glass at the Burn's edge, breaking from one side, and the patrol lying on it.
    if (!this.collapseLayer && s.rules?.place === 'glass' && s.rules.collapse && !this.crypt) this.startCollapse(s.rules.collapse);
    if (!this.lashLayer && s.rules?.lash && !this.crypt) {
      this.lashLayer = new LashLayer(this.omenCtx(), s.rules.lash);
      this.scene.add(this.lashLayer.group);
    }
    // El Cuerno 3: the Old Uro's tracks, its place and the traps laid for it.
    if (!this.huntLayer && s.rules?.hunt && !this.crypt) {
      this.huntLayer = new HuntLayer(this.omenCtx(), s.rules.hunt);
      this.scene.add(this.huntLayer.group);
    }
    if (!this.herdLayer && s.rules?.herd && !this.crypt) {
      this.herdLayer = new HerdLayer(this.omenCtx(), s.rules.herd.pen, s.rules.herd.penR);
      this.scene.add(this.herdLayer.group);
    }
    if (!this.rescueLayer && s.rules?.rescue && !this.crypt) {
      this.rescueLayer = new RescueLayer(this.omenCtx(), s.rules.rescue.pit, s.rules.rescue.home, s.rules.rescue.ticks);
      this.scene.add(this.rescueLayer.group);
    }
    // Caras prestadas 2, the night of lanterns: the crowd crossing the plaza.
    if (!this.crowd && s.rules?.crowd && !this.crypt) {
      this.crowd = new CrowdLayer(this.omenCtx(), this.map, s.rules.crowd.route, s.rules.crowd.lantern, s.rules.crowd.ticks);
      this.scene.add(this.crowd.group);
    }
    // Caras prestadas 4: the lanterns where the Prestacaras loses its face.
    if (!this.facetaker && (s.rules?.lanterns || s.enemies.some((e) => e.mask)) && !this.crypt) {
      this.facetaker = new FacetakerLayer(this.omenCtx(), s.rules?.lanterns);
      this.scene.add(this.facetaker.group);
    }
    if (!this.clueMarks && s.rules?.clues?.length) {
      this.clueMarks = new ClueMarks(s, (x, y, h = 0) => simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y) + h), this.opts.quality === 'low');
      this.scene.add(this.clueMarks.group);
    }
    {
      this.vfx.step();
      this.stepCinders();
      if (this.later.length > 0) {
        const due = this.later.filter((l) => --l.n <= 0);
        this.later = this.later.filter((l) => l.n > 0);
        for (const l of due) l.run();
      }
      this.critsThisTick = 0;
      for (const e of s._events ?? []) {
        if (e.t === 'unmask' && e.face === 'facetaker') this.facetaker?.unmask(e.x, e.y);
        else if (e.t === 'unmask') this.crowd?.unmask(e.x, e.y);
        else if (e.t === 'face' && !s.rules?.copies) this.facetaker?.face(e.x, e.y);
        else if (e.t === 'copy') this.copyMoment(e.kind, e.x, e.y);
      }
      for (const e of s._events ?? []) if (e.t === 'rescue') this.rescueLayer?.event(e.kind, e.x, e.y);
      this.rescueLayer?.tick(s);
      for (const e of s._events ?? []) if (e.t === 'herd') this.herdLayer?.event(e.kind, e.x, e.y);
      this.herdLayer?.tick(s);
      // The lash: what went over the rim this tick falls to the sea instead of dying where it stood.
      this.fallsNow.clear();
      for (const e of s._events ?? []) {
        if (e.t !== 'lash') continue;
        this.lashLayer?.event(e.kind, e.x, e.y, e.a);
        if (e.kind === 'fall') this.fallsNow.add(`${e.x},${e.y}`);
      }
      this.lashLayer?.tick(s);
      // The collapse: what goes down with the glass tips on into the void instead of dying where it stood.
      for (const e of s._events ?? []) {
        if (e.t !== 'collapse') continue;
        this.collapseLayer?.event(e.kind, e.x, e.y);
        if (e.kind === 'fall') this.fallsNow.add(`${e.x},${e.y}`);
      }
      this.collapseLayer?.tick(s);
      this.huntLayer?.tick(s);
      this.trapLayer?.tick(s);
      this.dragonField?.tick(s);
      this.blastLayer?.tick(s);
      this.mine?.tick(s);
      this.halls?.tick(s);
      this.anvilHall?.tick(s);
      this.forgeYard?.tick(s);
      this.wellSquare?.tick(s);
      this.fairSquare?.tick(s);
      this.shore?.tick(s);
      this.finCoast?.tick(s);
      this.sunken?.tick(s);
      // La Orilla 5: the dragon climbing out of the sea, and its breath, are its own one-shots.
      if (s.sunken?.id) for (const e of s._events ?? []) if (e.t === 'sunken' && (e.kind === 'rise' || e.kind === 'breath')) this.force(s.sunken.id, e.kind);
      this.lighthouse?.tick(s);
      this.pipes?.tick(s);
      this.ribs?.tick(s);
      this.neck?.tick(s);
      this.orderPost?.tick(s);
      this.spine?.tick(s);
      this.altars?.tick(s);
      this.taller?.tick(s);
      this.biblio?.tick(s);
      this.cable?.tick(s);
      this.corazon?.tick(s);
      this.ojos?.tick(s);
      this.tormoL?.tick(s);
      this.caza?.tick(s);
      this.vert?.tick(s);
      this.bote?.tick(s);
      this.isla?.tick(s);
      this.roca?.tick(s);
      this.camino?.tick(s);
      this.pasarela?.tick(s);
      this.nieve?.tick(s);
      this.ermita?.tick(s);
      this.bosque?.tick(s);
      this.hornos?.tick(s);
      this.otono?.tick(s);
      this.copa?.tick(s);
      this.raices?.tick(s);
      this.asterL?.tick(s);
      this.salt?.tick(s);
      this.arch?.tick(s);
      this.monastery?.tick(s);
      this.seventh?.tick(s);
      this.roots?.tick(s);
      this.orderBell?.tick(s);
      this.bellNight?.tick(s);
      for (const e of s._events ?? []) if (e.t === 'anvil' && e.kind !== 'fifth') this.anvilSwing(s, e.kind === 'miss' ? e : null);
      // El Párpado 4: the hammer on the bell, the same swing.
      for (const e of s._events ?? []) if (e.t === 'toll' && (e.kind === 'hit' || e.kind === 'calm' || e.kind === 'miss')) this.anvilSwing(s, e.kind === 'miss' ? e : null);
      // El Cuerno 4: the clan's call, and Ragna kneeling to it.
      for (const e of s._events ?? []) if (e.t === 'call') this.hornCall(e.id, e.x, e.y);
      if (s.call) this.kneeling(s, s.call.id);
      this.crowd?.tick(s);
      for (const e of s._events ?? []) if (!this.omen?.event?.(s, e)) this.onEvent(s, e);
      if (this.ink) this.syncInk(s);
      this.omen?.tick?.(s);
      this.clueMarks?.tick(s, (p) => {
        this.flare(p, '#dfe9ff', 3, 16);
        this.vfx.burst(p, { n: this.opts.quality === 'low' ? 8 : 18, color: '#ffd6a0', speed: 0.04, up: 0.06, size: 0.07, life: 20, gravity: -0.002, spread: 0.4 });
        this.vfx.ring(p.clone().setY(p.y - 0.35), 0.2, 1.6, '#ff8a6e', 14, 0.6);
      });
    }
    {
      for (const [id, t] of this.flashes) {
        if (t <= 1) this.flashes.delete(id);
        else this.flashes.set(id, t - 1);
      }
    }
  }

  /** Everything on the board for this tick and frame. */
  private draw(s: BattleState): void {
    const fresh = this.owed;
    this.owed = false;
    this.fresh = fresh;
    // A new tick glides from where the last frame drew it (over however many ticks came since).
    if (fresh) for (const t of Object.values(this.track)) t.turn();
    this.advanceClock(s);
    // Turns and strides go by the time since the last drawing.
    this.lastNow = Math.min(this.drawnNow, this.now);
    this.drawnNow = this.now;
    if (!this.toBake || this.toBake.length) this.prewarm(s);
    else if (this.linkedAhead) this.bakeLater(s);
    // Only a new tick spawns particles: a redraw between ticks would spawn them again every frame.
    this.vfx.muted = !fresh;
    this.positions.clear();
    // Between ticks the built structures stay as the tick drew them (see drawBuilt).
    const kept = !fresh && this.built?.s === s && this.built.tick === s.tick && this.built.far === this.far ? this.built : null;
    for (const b of this.batchesTouched) b.begin();
    const use = (key: string, far = false): ModelBatch => {
      const b = this.lib.batch(key, far);
      if (!this.batchesTouched.has(b)) {
        b.begin();
        this.batchesTouched.add(b);
      }
      return b;
    };
    this.entityGlow.begin();
    this.bars.begin();
    this.shadows.begin();
    this.holy.begin();
    this.aura.begin();
    this.gearFx.begin();
    this.skillFx.begin();
    this.drawFlares(fresh);
    if (kept) this.pasteBuilt(kept);
    else this.drawBuilt(s);
    this.drawCenter(s, use);
    this.drawHeroes(s, use);
    if (s.senda) this.drawSendaPeople(s, use);
    if (this.fireAt) this.drawFire();
    this.crowd?.draw(s, this.alpha, (x, y, z, size) => this.shadows.add(x, y, z, size));
    this.rescueLayer?.draw(s, this.alpha, (x, y, z, size) => this.shadows.add(x, y, z, size), (p, k) => this.bars.add(p, k, k > 0.35 ? GREEN : RED, 0.5));
    this.lashLayer?.draw(this.alpha, (x, y, z, size) => this.shadows.add(x, y, z, size));
    this.collapseLayer?.draw(s, this.alpha, (x, y, z, size) => this.shadows.add(x, y, z, size), (p, k) => this.bars.add(p, k, k > 0.35 ? GREEN : RED, 0.5));
    this.huntLayer?.draw();
    this.trapLayer?.draw();
    this.blastLayer?.draw();
    this.herdLayer?.draw(s, this.alpha, (x, y, z, size) => this.shadows.add(x, y, z, size), (p, k) => this.bars.add(p, k, k > 0.35 ? GREEN : RED, 0.5));
    this.drawBots(s, use);
    if (this.mine) this.drawTrain(s, use);
    this.halls?.draw(s);
    this.anvilHall?.draw(s, this.alpha, this.flashes.has(-1));
    this.wellSquare?.draw(s, this.alpha);
    this.fairSquare?.draw(s);
    this.shore?.draw(s);
    this.finCoast?.draw(s);
    this.lighthouse?.draw(s);
    this.bossUsed = 0;
    this.dragonDrawn = false;
    this.facetakerAt = null;
    this.drawEnemies(s, use);
    // The dragon's harpoons, chains and scales ride where it was drawn.
    if (this.dragonField) {
      if (!this.dragonDrawn) this.dragonField.setBody(null);
      this.dragonField.draw(s);
    }
    // After the nightmares: the boss's weak seam rides where it was drawn.
    this.forgeYard?.draw(s, this.flashes.has(-1));
    this.beside?.draw(s);
    this.pipes?.draw(s);
    this.ribs?.draw(s);
    this.neck?.draw(s);
    this.orderPost?.draw(s);
    this.spine?.draw(s);
    this.altars?.draw(s);
    this.taller?.draw(s);
    this.biblio?.draw(s);
    this.cable?.draw(s);
    this.corazon?.draw(s);
    this.ojos?.draw(s);
    this.tormoL?.draw(s);
    this.caza?.draw(s);
    this.vert?.draw(s);
    this.bote?.draw(s);
    this.isla?.draw(s);
    this.roca?.draw(s);
    this.camino?.draw(s);
    this.pasarela?.draw(s);
    this.nieve?.draw(s);
    this.ermita?.draw(s);
    this.bosque?.draw(s);
    this.hornos?.draw(s);
    this.otono?.draw(s);
    this.copa?.draw(s);
    this.raices?.draw(s);
    this.asterL?.draw(s);
    this.salt?.draw(s);
    this.arch?.draw(s);
    this.monastery?.draw(s);
    this.seventh?.draw(s);
    this.orderBell?.draw(s);
    this.bellNight?.draw(s);
    this.race?.draw(s);
    this.sunken?.draw(s);
    this.drawTethers(s);
    // drawEnemies set it (the narrowing above doesn't know).
    const ft = this.facetakerAt as { e: Enemy; p: THREE.Vector3; top: number } | null;
    this.facetaker?.draw(s, ft?.e, ft?.p, ft?.top ?? 0);
    this.drawCorpses(use);
    if (!this.dragonDrawn && this.dragonShadow) this.dragonShadow.visible = false;
    for (let i = this.bossUsed; i < this.bossRings.length; i++) {
      (this.bossRings[i] as THREE.Mesh).visible = false;
      (this.bossSkulls[i] as THREE.Sprite).visible = false;
    }
    this.drawTurrets(s, use);
    this.drawProjectiles(s, use);
    this.vortexUsed = 0;
    this.drawZones(s, use);
    this.drawCinders(use);
    for (let i = this.vortexUsed; i < this.vortex.length; i++) {
      const v = this.vortex[i] as THREE.Mesh;
      // Made ahead with an artificer in the fight: parked out of sight (and compiled) until the compile ahead is done.
      if (!this.linkedAhead && v.scale.x < 0.001) continue;
      v.visible = false;
    }
    if (s.map.kind === 'incursion') this.drawAmbush(s);
    this.drawWorks(s, use);
    if (this.gathers.length) this.drawGathers(use);
    // The lectern first: the cat's round keeps off its cell.
    // The yard's still pieces first: the cat's round keeps off the lectern's cell.
    if (this.yardWanted && this.yardRev !== s.gridRev) {
      this.yardRev = s.gridRev;
      this.syncYard(s);
    }
    if (this.yard) this.drawYard(s, use);
    if (this.catOpts) this.drawCat(s, use);
    if (this.rolling.length) this.drawRolling(use);
    if (this.rite?.id === 'props') this.drawProps(s, use);
    if (this.sifting) this.drawSift(s, use);
    if (this.ensign && this.ensignRev !== s.gridRev) {
      this.ensignRev = s.gridRev;
      this.ensignLayer?.set(this.ensign, this.ensignSpots(s));
    }
    // The trophies' glows that stand still (bakeStill took them out of the scene's drawing).
    for (const o of this.scene.children) if (o.userData.stillGlows) stillGlows(o, this.addGlow);
    this.drawArmero(use);
    this.drawPanoplia(use);
    this.drawWinch(s);
    this.drawCrypt(s);
    this.plunder?.sync(s, this.fresh, (id) => this.positions.get(id), this.keepPos);
    if (this.selected) {
      const c = hexToWorld(this.selected.q, this.selected.r);
      this.selLine.position.copy(simToWorld(c.x, c.y, new THREE.Vector3(), this.heightAt(c.x, c.y) + 0.02));
      this.selLine.visible = true;
    } else this.selLine.visible = false;
    for (const b of this.batchesTouched) b.end();
    this.entityGlow.end();
    // A cinematic shows no health at all (the nightmares' bars were added with the rest).
    if (this.cinema) this.bars.begin();
    this.bars.end();
    this.shadows.end();
    this.holy.end();
    this.aura.end();
    this.gearFx.end();
    this.skillFx.end();
    // Keep lights flicker at 12 Hz (the crypt's torches flicker in drawCrypt and frame()).
    const f = this.lights[0];
    if (f && !this.crypt && !this.somn && !this.seventhSteady) f.intensity = this.brazierBase + ((s.tick * 7919) % 5);
    this.vfx.muted = false;
  }

  /** Rebuilds the ring only when a rampart cell breaks, mends or changes wear stage. */
  private syncBreaches(s: BattleState): void {
    let key = '';
    for (const st of s.structures) {
      if (st.type !== 'rampart' && !(st.type === 'wall' && this.hildeRing?.has(`${st.q},${st.r}`))) continue;
      const stage = wearStage(st);
      if (stage !== 0) key += `${key ? ';' : ''}${st.q},${st.r}:${stage}`;
    }
    if (key === this.breachKey) return;
    this.breachKey = key;
    const broken = new Set<string>();
    const wear = new Map<string, number>();
    for (const part of key ? key.split(';') : []) {
      const [cell, stage] = part.split(':') as [string, string];
      if (stage === 'x') broken.add(cell);
      else wear.set(cell, Number(stage));
    }
    this.buildRampart(broken, wear);
  }

  /**
   * Where a structure stands: on its cell, or for a three-cell building on terraced land in the middle of its
   * cells, at the height of its own (they are all on one level).
   */
  private standAt(q: number, r: number, foot?: { q: number; r: number }[]): THREE.Vector3 {
    const c = hexToWorld(q, r);
    const y = this.heightAt(c.x, c.y);
    if (!foot || foot.length === 0) return simToWorld(c.x, c.y, new THREE.Vector3(), y);
    let x = c.x;
    let z = c.y;
    for (const h of foot) {
      const w = hexToWorld(h.q, h.r);
      x += w.x;
      z += w.y;
    }
    return simToWorld(x / (foot.length + 1), z / (foot.length + 1), new THREE.Vector3(), y);
  }

  /** The dressed-stone plinth under a three-cell building: a flat slab on each of its cells. */
  private plinth(p: THREE.Vector3, q: number, r: number, foot: { q: number; r: number }[], use: (k: string) => StructSink | ModelBatch): void {
    const b = use('plinth');
    for (const h of [{ q, r }, ...foot]) {
      const w = hexToWorld(h.q, h.r);
      this.compose(simToWorld(w.x, w.y, new THREE.Vector3(), p.y), Math.PI / 6, 1);
      b.add(this.m, 0);
    }
  }

  private heightAt(x: number, y: number): number {
    // (The black rock's ledges and summit: its own ground.)
    if (this.roca) return this.roca.heightAt(x, y);
    if (this.land) return landHeightAt(this.land, this.map, x, y);
    return terrainHeightAt(this.terrain, this.map, x, y);
  }

  private drawStructures(s: BattleState, use: (k: string) => StructSink): void {
    this.clockAt.clear();
    this.branchAt.clear();
    for (const st of s.structures) {
      if (st.destroyed) continue;
      // A clockwork turret's bolts are darts, on its own cell or mounted on a wall run.
      if (st.type === 'clockwork' || st.mount?.type === 'clockwork') {
        const c = hexToWorld(st.q, st.r);
        this.clockAt.add(`${c.x},${c.y}`);
      }
      if (st.branch && BRANCH_SHOT[st.branch]) {
        const c = hexToWorld(st.q, st.r);
        this.branchAt.set(`${c.x},${c.y}`, st.branch);
      }
    }
    // An aegis on the keep: a pale dome over it.
    if ((s.keepShield ?? 0) > 0 && (s.keepShieldTicks ?? 0) > 0) this.entityGlow.add(0, 1.2, 0, new THREE.Color('#dfe8ff'), 4.2, 0.22 + (s.tick % 6) * 0.01);
    if (this.offerings.length && this.map.kind === 'bastion' && !this.crypt) this.offeringFires(s);
    if (this.bell) this.bellGlow(s);
    if (this.table) {
      const p = this.tablePos();
      if (p) this.entityGlow.add(p.x, p.y, p.z, new THREE.Color('#ff4f3a'), 2.6, 0.42 + 0.08 * Math.sin(s.tick * 0.3));
    }
    // Terraced land: walls and gates the group drew join their neighbours into runs.
    const joins = this.map.land ? this.wallJoins(s) : null;
    for (const st of s.structures) {
      const p = this.standAt(st.q, st.r, st.foot);
      this.positions.set(st.id, p.clone());
      if ((st.shield ?? 0) > 0 && (st.shieldTicks ?? 0) > 0) {
        this.entityGlow.add(p.x, p.y + 0.8, p.z, new THREE.Color('#dfe8ff'), 2.2, 0.3);
        if ((s.tick + st.id) % 8 === 0) this.vfx.ring(p.clone().setY(p.y + 0.05), 0.4, 1.1, '#dfe8ff', 6, 0.7);
      }
      const def = STRUCTURES[st.type];
      if (st.type === 'rampart' || (st.type === 'wall' && this.hildeRing?.has(`${st.q},${st.r}`))) {
        this.drawRampart(s, st, p, use);
        continue;
      }
      const line = st.type === 'gate' || st.type === 'wall';
      if (st.destroyed) {
        if (def.hp > 0) {
          // The ruin of this very building, in its own material, lined up like it stood.
          const rk = `ruin:${st.type}:${st.material}`;
          this.compose(p, line ? (this.gateYaw.get(`${st.q},${st.r}`) ?? Math.atan2(p.x, p.z)) : st.foot ? footYaw(st.q, st.r, st.foot) : (st.id * 1.7) % 6.28, this.lib.scaleOf(rk) * footScale(st.foot));
          if (st.foot) this.plinth(p, st.q, st.r, st.foot, use);
          use(rk).add(this.m, 0);
        }
        continue;
      }
      if (st.type === 'boulder') {
        // The boulder on its chock, turned the way it rolls (its `roll`, DIRS); spent, the empty chock and its rut.
        const key = structKey('boulder', st.level, st.material, 0, st.charges <= 0 ? 'spent' : undefined);
        this.compose(p, rollYaw(st.roll), this.lib.scaleOf(key));
        use(key).add(this.m, 0, this.flashes.has(st.id) ? FLASH : undefined);
        continue;
      }
      if (def.category === 'trap' && st.charges <= 0) {
        const b = use('rubble');
        this.compose(p, 0, 0.35);
        b.add(this.m, 0);
        // An oil that burned its last charge lighting against the frost still burns for the wave.
        if (s.rules?.towerReloadBp && st.type === 'oil') this.frostFx(s, st, p, 0.3);
        continue;
      }
      const stage = def.hp > 0 ? wearStage(st) : 0;
      const key = structKey(st.type, st.level, st.material, stage, st.branch);
      const b = use(key);
      if (def.category !== 'trap') this.shadows.add(p.x, p.y, p.z, line ? 1.25 : st.foot ? (st.foot.length >= 6 ? 3.4 : 1.9) : 1.05);
      let yaw = st.foot ? footYaw(st.q, st.r, st.foot) : 0;
      if (line) yaw = this.gateYaw.get(`${st.q},${st.r}`) ?? Math.atan2(p.x, p.z);
      const links = joins?.get(`${st.q},${st.r}`);
      // A gate in a drawn wall lines up with the run it sits in.
      if (st.type === 'gate') yaw = this.runYaw(links) ?? yaw;
      // A three-cell building on terraced land: bigger, on a plinth over its cells (the Eye is drawn at world size).
      const scale = this.lib.scaleOf(key) * footScale(st.foot);
      if (st.foot) this.plinth(p, st.q, st.r, st.foot, use);
      this.compose(p, yaw, scale);
      const flash = this.flashes.has(st.id) ? FLASH : st.cursed ? GHOSTED : st.cold ? ICED : undefined;
      if (links && links.length > 0 && st.type === 'wall') {
        // Half a run toward each neighbour: two cells side by side make one continuous stretch, and it turns at the corners.
        const tall = this.lib.heightOf(key);
        for (const d of links) {
          this.halfRun(p, d, scale, scale, tall);
          b.add(this.m, 0, flash);
        }
        this.compose(p, yaw, scale);
      } else b.add(this.m, 0, flash);
      this.dynamicParts(s, st, st.type, b, p, yaw, flash);
      // The adarve: a tower mounted on this wall run, its own model on the wall-walk, its turret aiming from there.
      let top = this.lib.heightOf(key) * scale;
      if (st.mount && st.type === 'wall') {
        const mk = structKey(st.mount.type, st.mount.level, st.material, stage);
        const mb = use(mk);
        const ms = this.lib.scaleOf(mk) * MOUNT_K;
        const lift = WALL_WALK * scale;
        this.compose(this.v.copy(p).setY(p.y + lift), yaw, ms);
        mb.add(this.m, 0, flash);
        this.dynamicParts(s, st, st.mount.type, mb, p, yaw, flash);
        top = lift + this.lib.heightOf(mk) * ms;
      }
      if (def.hp > 0 && st.hp < st.maxHp) {
        this.bars.add(this.v.copy(p).setY(p.y + top + 0.15), st.hp / st.maxHp, line ? STONE : BLUE, 0.6);
      }
      if (stage !== 'x') this.wearFx(s, st, stage, p, top, WOODEN(st));
      if (st.type === 'workshop' || st.type === 'lab' || st.type === 'clockwork') this.eraFx(s, st, stage, p, scale, top);
      if (st.enchant && (def.category === 'tower' || st.mount)) this.enchantFx(s, st, p, top, scale);
      if (st.cursed) this.entityGlow.add(p.x, p.y + 1.4, p.z, new THREE.Color('#58ff6a'), 0.9, 0.7);
      // Set burning by a wyvern: flames lick up its sides, embers and smoke rise until a hero puts it out.
      if ((st.burnTicks ?? 0) > 0) this.towerFire(s, st.id, p, top);
      // Cocooned by a weaver: pale silk wound round it, strands catching the light.
      if ((st.cocoon ?? 0) > 0) this.cocoonFx(s, st.id, p, top);
      // Wrapped in Kaela's moss: silent under it until it falls away.
      else if ((st.dozeUntil ?? 0) > s.tick && this.mossed(s).includes(st.id)) this.mossFx(s, st.id, p, top);
      // Taken by Bruni's forge: blue arcs crawl over it while it shoots the heroes.
      if ((s.un || s.rules?.rising) && (st.turnedUntil ?? 0) > s.tick) this.turnedFx(s, st.id, p, top);
      // Under frost: rime on the cold towers, a warm breath round the ones kept warm, the oils that warm them burning.
      if (s.rules?.towerReloadBp) this.frostFx(s, st, p, top);
      else if (this.frostDay && st.type === 'oil' && st.charges > 0) this.oilFire(s, st.id, p, 0.7);
    }
  }

  /** The bastion's board on a day whose next wave comes under frost: its charged oils smoulder, ready to light. */
  private frostDay = false;
  setFrostDay(on: boolean): void {
    this.frostDay = on;
  }

  /** Under frost: rime on a cold tower, a warm breath round one kept warm, fire in an oil that lit (`Structure.lit`). */
  private frostFx(s: BattleState, st: Structure, p: THREE.Vector3, tall: number): void {
    const t = s.tick + st.id * 7;
    if (st.type === 'oil') {
      if (st.lit) this.oilFire(s, st.id, p, 1);
      return;
    }
    if (st.destroyed || !frostable(st)) return;
    if (st.cold) {
      // A crust of rime: a pale cold light over it, frost glittering on its crown, cold mist sinking down its sides.
      this.entityGlow.add(p.x, p.y + tall * 0.7, p.z, RIME, 1.15, 0.26 + (t % 16 < 8 ? 0.04 : 0));
      this.entityGlow.add(p.x, p.y + 0.05, p.z, RIME, 1.6, 0.2);
      if (!this.fresh) return;
      const low = this.opts.quality === 'low';
      const a = ((t * 2654435761) >>> 0) % 628 / 100;
      if (t % (low ? 9 : 5) === 0) this.vfx.burst(this.v.set(p.x + Math.cos(a) * 0.35, p.y + tall * 0.9, p.z + Math.sin(a) * 0.35).clone(), { n: 1, color: '#f4fbff', speed: 0.004, up: 0.002, size: 0.05, life: 10 });
      if (t % (low ? 30 : 14) === 0) this.vfx.burst(this.v.set(p.x, p.y + tall * 0.6, p.z).clone(), { n: 1, color: '#dcefff', speed: 0.006, up: -0.006, size: 0.42, life: 22, smoke: true, spread: 0.3 });
      if (t % 48 === 0) this.vfx.ring(this.v.copy(p).setY(p.y + 0.04).clone(), 0.2, 0.75, '#d8f0ff', 14, 0.5);
      return;
    }
    // Kept warm: an amber breath at its foot and up its side, the odd ember rising past it.
    this.entityGlow.add(p.x, p.y + 0.06, p.z, EMBER, 1.8, 0.26 + (t % 10 < 5 ? 0.04 : 0));
    this.entityGlow.add(p.x, p.y + tall * 0.45, p.z, EMBER, 0.9, 0.12);
    if (this.fresh && t % 12 === 0) this.vfx.burst(this.v.set(p.x, p.y + tall * 0.3, p.z).clone(), { n: 1, color: '#ffb46a', speed: 0.006, up: 0.025, size: 0.05, life: 14, gravity: -0.002 });
  }

  /** A burning-oil trap alight against the frost: flames in its pit, a warm light round it, embers and heat. */
  private oilFire(s: BattleState, id: number, p: THREE.Vector3, k: number): void {
    const low = this.opts.quality === 'low';
    const t = s.tick + id * 3;
    this.entityGlow.add(p.x, p.y + 0.12, p.z, FIRE, 1.9 * k, (0.42 + (t % 4 < 2 ? 0.1 : 0)) * k);
    this.entityGlow.add(p.x, p.y + 0.5, p.z, EMBER, 1.0 * k, 0.28 * k);
    // In the fight, the warmth it spreads: a wide amber pool on the snow, as far as it keeps the towers warm.
    if (k >= 1 && s.rules?.warmRange) this.entityGlow.add(p.x, p.y + 0.05, p.z, EMBER, ((s.rules.warmRange * 2) / 1000) * WORLD_PER_HEX, 0.11 + (t % 12 < 6 ? 0.015 : 0));
    if (!this.fresh) return;
    if (t % 2 === 0) this.vfx.burst(this.v.set(p.x, p.y + 0.15, p.z).clone(), { n: low ? 1 : 2, color: '#ff6a24', speed: 0.015, up: 0.05 * k, size: 0.18 * k, life: 8, gravity: -0.004, spread: 0.25 });
    if (t % 4 === 0) this.vfx.burst(this.v.set(p.x, p.y + 0.3, p.z).clone(), { n: 1, color: '#ffd08a', speed: 0.01, up: 0.07 * k, size: 0.06, life: 12, gravity: -0.003, spread: 0.2 });
    if (k >= 1 && t % 9 === 0) this.vfx.burst(this.v.set(p.x, p.y + 0.9, p.z).clone(), { n: 1, color: '#2a2422', speed: 0.01, up: 0.035, size: 0.55, life: 20, smoke: true, spread: 0.2 });
  }

  /**
   * A structure's moving parts (turret, arm, crew, bell, crystal, portcullis, gears) on `b`, from the matrix just
   * drawn (`this.m`): the same for a tower on its own cell and for one mounted on a wall run.
   */
  private dynamicParts(s: BattleState, st: Structure, type: StructureType, b: StructSink, p: THREE.Vector3, yaw: number, flash: THREE.Color | undefined): void {
    const base = this.m.clone();
    if (b.hasDynamic('turret')) {
      // A tower Bruni's forge took aims at a hero.
      const tgt = st.aim ? (s.enemies.find((e) => e.id === st.aim) ?? ((st.turnedUntil ?? 0) > s.tick ? s.heroes.find((h) => h.id === st.aim) : undefined)) : undefined;
      let a = (st.id * 0.9) % 6.28;
      if (tgt) {
        const tp = simToWorld(tgt.x, tgt.y);
        a = Math.atan2(tp.x - p.x, tp.z - p.z);
      }
      this.local.makeRotationY(a - yaw);
      b.addDynamic('turret', base, this.local, flash);
    }
    if (b.hasDynamic('arm')) {
      const t = st.cd;
      let swing = t > 26 ? -1.1 : t > 18 ? -0.2 : 0.15;
      if (type === 'trebuchet') {
        // Cocked at rest; it whips up and over as it throws, then the winch draws it back down until the next.
        const r = STRUCTURES[type].reload - 4;
        swing = t <= 0 ? 0 : t > r ? TREB_THROW : (TREB_THROW * t) / r;
      }
      this.local.makeRotationZ(swing);
      b.addDynamic('arm', base, this.local, flash);
    }
    if (b.hasDynamic('crew')) {
      // While the engine reloads the crew heaves in a stop-motion bob; idle, they stand still.
      const busy = st.cd > 0;
      this.local.makeTranslation(0, busy && (s.tick + st.id) % 6 < 3 ? 0.035 : 0, 0);
      b.addDynamic('crew', base, this.local, flash);
    }
    if (b.hasDynamic('bell')) {
      this.local.makeRotationZ(st.cd > 64 ? (st.cd % 2 ? 0.35 : -0.35) : 0);
      b.addDynamic('bell', base, this.local, flash);
    }
    if (b.hasDynamic('crystal')) {
      this.local.makeRotationY((s.tick % 24) * 0.26).setPosition(0, s.tick % 12 < 6 ? 0.04 : 0, 0);
      b.addDynamic('crystal', base, this.local, flash);
    }
    if (b.hasDynamic('portcullis')) {
      this.local.makeTranslation(0, 0, 0);
      b.addDynamic('portcullis', base, this.local, flash);
    }
    if (b.hasDynamic('spin')) {
      // Gears and flywheels keep turning; a clockwork turret's stall while it rests after an overload.
      const still = type === 'clockwork' && st.cd > CLOCK_REST;
      const k = type === 'workshop' ? 1 : 2;
      this.local.makeRotationY(still ? (st.id * 0.7) % TAU : (((s.tick + st.id * 13) * k) % 96) * (TAU / 96));
      b.addDynamic('spin', base, this.local, flash);
    }
  }

  /**
   * What the built structures drew this tick, put back on the frames until the next one (pasteBuilt): nothing in
   * them moves between ticks (turrets aim at where the sim has a nightmare, arms, crews, gears and glows go by
   * the tick, and smoke and sparks only start on a new tick), so redrawing them every frame wrote the same
   * thing over. They draw through StructBatcher, untouched until the next tick, and the glows, bars and shadows they
   * added are copied.
   * Kept only for this very state at this tick: a new state, another tick or invalidate() draws them anew.
   */
  private built: {
    s: BattleState;
    tick: number;
    far: boolean;
    glow: Float32Array[];
    bars: Float32Array[];
    shadows: Float32Array;
    positions: [number, THREE.Vector3][];
  } | null = null;

  private drawBuilt(s: BattleState): void {
    const g0 = this.entityGlow.count;
    const b0 = this.bars.count;
    const s0 = this.shadows.count;
    // Seen small, the structures draw their far versions (engine/lod.ts); the other batcher is left empty.
    if (this.far && !this.structsFar) {
      this.structsFar = new StructBatcher(this.entityGlow, true);
      this.root.add(this.structsFar.group);
    }
    const sb = this.far && this.structsFar ? this.structsFar : this.structs;
    const idle = sb === this.structs ? this.structsFar : this.structs;
    idle?.begin();
    idle?.end();
    sb.begin();
    this.drawStructures(s, (k) => sb.batch(k));
    sb.end();
    const positions: [number, THREE.Vector3][] = [];
    for (const st of s.structures) {
      const p = this.positions.get(st.id);
      if (p) positions.push([st.id, p.clone()]);
    }
    this.built = {
      s,
      tick: s.tick,
      far: this.far,
      glow: this.entityGlow.copyFrom(g0),
      bars: this.bars.copyFrom(b0),
      shadows: this.shadows.copyFrom(s0),
      positions,
    };
  }

  private pasteBuilt(k: NonNullable<BattleView['built']>): void {
    this.entityGlow.paste(k.glow);
    this.bars.paste(k.bars);
    this.shadows.paste(k.shadows);
    for (const [id, p] of k.positions) this.positions.set(id, p.clone());
  }

  /** Era V's layer: the shell plates in the turf, the Dream Beacon's beat and the aurora (somnsky.ts). */
  private somn: SomnLayer | null = null;

  /** Somnhold whole again (era V): the bastion itself, not a view with another centerpiece. */
  /** The bastion from scratch: its terraced land (docs/CONSTRUCCION.md). */
  private land: LandBuild | null = null;
  /** The wave's fissures, cracked open when it starts (never before: nobody says where they come from). */
  private rifts: THREE.Group | null = null;

  private openRifts(): void {
    const g = new THREE.Group();
    g.name = 'rifts';
    this.map.sectors.forEach((sec, i) => {
      const f = sec.path[0];
      if (!f) return;
      const c = hexToWorld(f.q, f.r);
      const p = simToWorld(c.x, c.y, new THREE.Vector3(), this.heightAt(c.x, c.y));
      const r = buildRift(this.seed * 7 + i * 131 + f.q * 17 + f.r, this.opts.quality === 'low');
      r.position.copy(p);
      g.add(r);
      // The ground bursts: two rings of ember light and a puff of dust.
      this.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 2.4, '#ff4f3a', 8, 0.8);
      this.vfx.ring(p.clone().setY(p.y + 0.05), 0.1, 1.2, '#ff8a6e', 5, 0.9);
      this.vfx.burst(p.clone().setY(p.y + 0.2), { n: 14, color: '#3b3533', speed: 0.06, up: 0.08, size: 0.25, life: 18, smoke: true, spread: 0.6 });
    });
    this.rifts = g;
    this.root.add(g);
  }

  /** Fissures the general cracks open mid-wave (sim battle/rifts.ts), by id: growing while they tremble, gone once a decoy closes. */
  private midRifts = new Map<number, THREE.Group>();

  /**
   * The general's fissures: while one trembles the ground shakes there (dust, a dim ember ring) and the crack
   * grows; it bursts open like the wave's own; a decoy that let its few out closes in a puff of dust. A decoy
   * looks the same as a real one until it closes. Same meshes and effects as the wave's fissures (no new shaders
   * mid-wave: docs/RENDIMIENTO.md).
   */
  private syncMidRifts(s: BattleState): void {
    const st = s.rifts;
    if (!st || !this.rifts) return;
    const t = s.waveT;
    for (const r of st.list) {
      const p = simToWorld(r.x, r.y, new THREE.Vector3(), this.heightAt(r.x, r.y));
      let g = this.midRifts.get(r.id);
      if (r.close > 0 && t >= r.close) {
        if (g) {
          this.rifts.remove(g);
          this.midRifts.delete(r.id);
          this.vfx.burst(p.clone().setY(p.y + 0.2), { n: 12, color: '#3b3533', speed: 0.05, up: 0.06, size: 0.25, life: 16, smoke: true, spread: 0.6 });
        }
        continue;
      }
      if (t < r.warn) continue;
      if (!g) {
        g = buildRift(this.seed * 7 + r.id * 977 + 31, this.opts.quality === 'low');
        g.position.copy(p);
        this.rifts.add(g);
        this.midRifts.set(r.id, g);
      }
      if (t < r.open) {
        // Trembling: the crack grows toward its full size; dust and a dim ring every half second.
        const k = 0.15 + (0.55 * (t - r.warn)) / Math.max(1, r.open - r.warn);
        g.scale.setScalar(k);
        if ((t - r.warn) % 6 === 0) {
          // Wide enough to read from a phone's camera (a 1.4 ring was ~20 px there).
          this.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, 2.6, '#ff8a6e', 6, 0.6);
          this.vfx.burst(p.clone().setY(p.y + 0.15), { n: 10, color: '#3b3533', speed: 0.06, up: 0.06, size: 0.3, life: 14, smoke: true, spread: 0.9 });
        }
      } else if (t === r.open || g.scale.x < 1) {
        g.scale.setScalar(1);
        this.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 2.4, '#ff4f3a', 8, 0.8);
        this.vfx.ring(p.clone().setY(p.y + 0.05), 0.1, 1.2, '#ff8a6e', 5, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: 14, color: '#3b3533', speed: 0.06, up: 0.08, size: 0.25, life: 18, smoke: true, spread: 0.6 });
        // A column of embers out of the throat: what says "here" from far off.
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: 18, color: '#ff4f3a', speed: 0.03, up: 0.22, size: 0.22, life: 22, spread: 0.3 });
      }
    }
  }

  /**
   * Where a ray from the camera meets terraced land (world XZ, with its height), or null off the land or on a
   * flat board (pick on the y = 0 plane there). Marches down the ray in short steps, then halves the last one.
   */
  groundHit(ray: THREE.Ray): THREE.Vector3 | null {
    const land = this.land;
    if (!land || ray.direction.y >= 0) return null;
    const k = 1000 / WORLD_PER_HEX;
    const p = new THREE.Vector3();
    const top = LEVEL_H * 4;
    let t = (top - ray.origin.y) / ray.direction.y;
    if (t < 0) t = 0;
    const step = 0.12;
    let prev = t;
    for (let i = 0; i < 1200; i++) {
      ray.at(t, p);
      const g = landHeightAt(land, this.map, p.x * k, p.z * k);
      if (p.y <= g) {
        let lo = prev;
        let hi = t;
        for (let j = 0; j < 8; j++) {
          const mid = (lo + hi) / 2;
          ray.at(mid, p);
          if (p.y <= landHeightAt(land, this.map, p.x * k, p.z * k)) hi = mid;
          else lo = mid;
        }
        return ray.at(hi, p);
      }
      if (p.y < -2) return null;
      prev = t;
      t += step;
    }
    return null;
  }

  private somnhold(): boolean {
    return !this.crypt && this.map.kind === 'bastion' && !this.opts.centerpiece && this.keepEra() >= 5;
  }

  private buildSomnhold(): void {
    const key = this.center();
    const foot = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
    // The lens sits 0.72 under the top of the era V keep (structures.ts, belfryAndBeacon).
    const lens = foot.clone().setY(foot.y + (this.lib.heightOf(key) - 0.72) * this.lib.scaleOf(key));
    this.somn = new SomnLayer({ map: this.map, seed: this.seed, low: this.opts.quality === 'low', ground: this.terrain.ground, roadDist: this.terrain.roadDist, lens, foot });
    this.scene.add(this.somn.group);
    const brazier = this.lights[0];
    if (brazier) brazier.position.y = lens.y + 0.3;
  }

  /** The bastion's era, as the sim counts it (districts, then crownings): the keep shows it. */
  private keepEra(): number {
    return Math.min(5, mapEra(this.map));
  }

  /** What stands in the middle: the given centerpiece, or the keep of the bastion's era. */
  private center(): string {
    return this.opts.centerpiece ?? `keep:${this.keepEra()}`;
  }

  /** The middle of the board (the keep, an incursion's node and its golems, a plunder's cart): drawn every frame. */
  private drawCenter(s: BattleState, use: (k: string) => ModelBatch): void {
    if (this.crypt) return;
    // The keep in the middle.
    const center = this.center();
    const kb = use(center);
    const kp = simToWorld(0, 0, new THREE.Vector3(), this.heightAt(0, 0));
    this.keepPos.copy(kp);
    // A saga escort has no node in the middle: the caravan is what they defend. Nor the collapse (the patrol is), nor
    // the old trap (the colossus is), nor the blasting (its watchtower stands there, drawn by its layer), nor the
    // five strikes (the Anvil is the node, drawn by its layer), nor Bruni's (the forge is, drawn by its layer), nor El
    // pozo's (the well is), nor La Orilla's (the stilt-houses, or the lighthouse, drawn by its layer).
    // A path's cinematic neither: its people stand where the node would.
    if (this.cinema || s.rules?.senda?.own || s.escort || s.rules?.dragon || s.rules?.collapse || s.rules?.trap || s.rules?.blast || s.rules?.anvil || s.rules?.place === 'forge' || s.rules?.well || s.rules?.fair || s.rules?.tide || s.rules?.fin || s.rules?.maw) return;
    // A plunder: the golem's cart waits in the middle for the caches.
    if (s.plunder) {
      this.drawPlunderCart(s, kp, use);
      return;
    }
    this.shadows.add(kp.x, kp.y, kp.z, 1.7);
    this.compose(kp, Math.PI / 6, this.lib.scaleOf(center));
    kb.add(this.m, 0, this.flashes.has(-1) ? FLASH : undefined);
    if (center.startsWith('node:') && s.objective) this.drawExtraction(s, center.slice(5), kp, use);
  }

  /**
   * A rampart cell: the ring itself draws the standing wall (worn by stage, see buildRampart),
   * so here only the breach's rubble and the health bar.
   */
  private drawRampart(s: BattleState, st: BattleState['structures'][number], p: THREE.Vector3, use: (k: string) => StructSink): void {
    const yaw = this.wallYaw.get(`${st.q},${st.r}`) ?? Math.atan2(p.x, p.z);
    if (st.destroyed || st.hp <= 0) {
      this.compose(p, yaw, 1);
      use('rampartRubble').add(this.m, 0, this.flashes.has(st.id) ? FLASH : undefined);
      return;
    }
    if (st.hp < st.maxHp) this.bars.add(this.v.copy(p).setY(p.y + this.lib.heightOf('rampartTower:small') + 0.15), st.hp / st.maxHp, STONE, 0.6);
    const stage = wearStage(st);
    if (stage !== 'x') this.wearFx(s, st, stage, p, this.lib.heightOf('rampartTower:small'), false);
  }

  /**
   * What a hurt structure gives off, so it reads as hurt from the table camera: worn ones shed a
   * trickle of grit now and then; crumbling ones a thin thread of smoke from the top (and a stray
   * ember from timber).
   */
  private wearFx(s: BattleState, st: BattleState['structures'][number], stage: 0 | 1 | 2, p: THREE.Vector3, tall: number, wooden: boolean): void {
    if (stage === 1 && (s.tick + st.id * 7) % 30 === 0) {
      const top = this.v.copy(p).setY(p.y + tall * 0.75);
      this.vfx.burst(top, { n: 3, color: wooden ? '#6b4f36' : '#8a8378', speed: 0.012, up: 0.005, size: 0.07, life: 10, gravity: 0.006, spread: 0.3 });
      this.vfx.burst(top, { n: 1, color: '#5a554e', speed: 0.004, up: 0.012, size: 0.16, life: 14, smoke: true, spread: 0.25 });
    }
    if (stage === 2 && (s.tick + st.id * 5) % 10 === 0) {
      const top = this.v.copy(p).setY(p.y + tall * 0.8);
      this.vfx.burst(top, { n: 1, color: '#47433f', speed: 0.004, up: 0.025, size: 0.2, life: 18, smoke: true, spread: 0.25 });
      if (wooden && (s.tick + st.id) % 30 < 10) this.vfx.burst(top, { n: 1, color: '#ff8a3a', speed: 0.01, up: 0.03, size: 0.05, life: 8, gravity: -0.001, spread: 0.3 });
    }
  }

  /**
   * Life in the era II buildings: the workshop's chimney smokes and its forge throws sparks at the door; the
   * laboratory's chimney breathes faintly coloured smoke and its flask bubbles; a clockwork turret puffs
   * steam now and then, and while it rests after an overload it vents hard and its head glows hot.
   */
  private eraFx(s: BattleState, st: BattleState['structures'][number], stage: 0 | 1 | 2 | 'x', p: THREE.Vector3, scale: number, tall: number): void {
    if (!this.fresh) return;
    const t = s.tick + st.id * 7;
    const at = (x: number, y: number, z: number): THREE.Vector3 => this.v.set(p.x + x * scale, p.y + y * scale, p.z + z * scale).clone();
    if (st.type === 'workshop') {
      if (t % 7 === 0) this.vfx.burst(at(-0.4, stage === 2 ? 1.05 : 1.42, -0.31), { n: 1, color: '#55504b', speed: 0.004, up: 0.02, size: 0.22, life: 22, smoke: true, spread: 0.04 });
      if (t % 17 === 0) this.vfx.burst(at(-0.02, 0.2, 0.3), { n: 3, color: '#ffb070', speed: 0.03, up: 0.035, size: 0.05, life: 6, gravity: 0.004 });
    } else if (st.type === 'lab') {
      if (stage !== 2 && t % 9 === 0) this.vfx.burst(at(0.11, 1.5, -0.35), { n: 1, color: t % 18 === 0 ? '#7a8a4a' : '#6f6487', speed: 0.004, up: 0.018, size: 0.2, life: 24, smoke: true, spread: 0.03 });
      if (t % 8 === 0) this.vfx.burst(at(0.65, 0.2, 0.38), { n: 1, color: '#d8f040', speed: 0.004, up: 0.012, size: 0.045, life: 7, gravity: -0.001 });
    } else if (st.cd > CLOCK_REST) {
      // Resting after an overload: steam roars out of the head and the plates glow hot.
      this.vfx.burst(this.v.copy(p).setY(p.y + tall * 0.92).clone(), { n: 1, color: '#d9dde2', speed: 0.02, up: 0.03, size: 0.24, life: 12, smoke: true, spread: 0.08 });
      if (t % 3 === 0) this.vfx.burst(this.v.copy(p).setY(p.y + tall * 0.85).clone(), { n: 1, color: '#ffb070', speed: 0.03, up: 0.02, size: 0.05, life: 5, gravity: 0.004 });
      this.entityGlow.add(p.x, p.y + tall * 0.82, p.z, new THREE.Color('#ff6a3a'), 0.75, 0.3 + (t % 4 < 2 ? 0.12 : 0));
    } else if (t % 41 === 0) {
      this.vfx.burst(this.v.copy(p).setY(p.y + tall * 0.95).clone(), { n: 1, color: '#d9dde2', speed: 0.005, up: 0.02, size: 0.14, life: 10, smoke: true });
    }
  }

  /** An enchanted tower: a glow on its crown and at its foot, and what its element sheds. */
  private enchantFx(s: BattleState, st: BattleState['structures'][number], p: THREE.Vector3, tall: number, scale: number): void {
    const look = ENCHANT_LOOK[st.enchant as string];
    if (!look) return;
    const t = s.tick + st.id * 5;
    const flick = st.enchant === 'lightning' ? (((t * 7919) >>> 3) % 3) * 0.12 : (t % 8 < 4 ? 0.06 : 0);
    this.entityGlow.add(p.x, p.y + tall * 0.9, p.z, look.glow, 1.25, 0.42 + flick);
    this.entityGlow.add(p.x, p.y + tall * 0.55, p.z, look.glow, 0.9, 0.18);
    this.entityGlow.add(p.x, p.y + 0.06, p.z, look.glow, 1.7, look.foot + 0.06);
    if (!this.fresh) return;
    // Every two seconds a ring of its element washes out over the ground round it.
    if (t % 24 === 0) this.vfx.ring(this.v.copy(p).setY(p.y + 0.04).clone(), 0.25, 0.85, look.fx, 12, 0.55);
    const a = ((t * 2654435761) >>> 0) % 628 / 100;
    const r = 0.3 * scale;
    const top = this.v.set(p.x + Math.cos(a) * r, p.y + tall * 0.82, p.z + Math.sin(a) * r).clone();
    switch (st.enchant) {
      case 'fire':
        if (t % 3 === 0) this.vfx.burst(top, { n: 1, color: look.fx, speed: 0.01, up: 0.03, size: 0.06, life: 9, gravity: -0.0015 });
        break;
      case 'frost':
        if (t % 4 === 0) this.vfx.burst(top.setY(top.y + 0.15), { n: 1, color: look.fx, speed: 0.008, up: -0.004, size: 0.06, life: 14, gravity: 0.0008 });
        break;
      case 'lightning':
        if (t % 5 === 0) this.vfx.burst(top, { n: 2, color: look.fx, speed: 0.06, up: 0.01, size: 0.05, life: 3 });
        break;
      case 'acid':
        // Drips running off the crown, and the puddle at the foot bubbling.
        if (t % 3 === 0) this.vfx.burst(top, { n: 1, color: look.fx, speed: 0.002, up: 0, size: 0.07, life: 11, gravity: 0.006 });
        if (t % 6 === 0) {
          const b = ((t * 40503) >>> 0) % 628 / 100;
          this.vfx.burst(this.v.set(p.x + Math.cos(b) * 0.4 * scale, p.y + 0.05, p.z + Math.sin(b) * 0.4 * scale).clone(), { n: 1, color: '#e8ff6a', speed: 0.002, up: 0.012, size: 0.06, life: 6, gravity: -0.0005 });
        }
        break;
    }
  }

  /** Plunder: the golem's cart in the middle, its lanterns lit, with the cart's health (nightmares can still break it). */
  private drawPlunderCart(s: BattleState, kp: THREE.Vector3, use: (k: string) => ModelBatch): void {
    const key = this.rk('bot:cart');
    const batch = use(key);
    const scale = this.lib.scaleOf(key);
    const yaw = Math.PI / 6;
    const anim = batch.baked.anim ? this.act(batch, -2, ['idle'], this.now) : undefined;
    this.shadows.add(kp.x, kp.y, kp.z, 1.3);
    this.compose(kp, yaw, scale);
    batch.add(this.m, 0, this.flashes.has(-1) ? FLASH : undefined, 1, anim);
    const o = s.objective;
    if (o && o.hp < o.maxHp) {
      const k = o.hp / o.maxHp;
      this.bars.add(this.v.copy(kp).setY(kp.y + this.lib.heightOf(key) * scale + 0.15), k, k > 0.35 ? GREEN : RED, 0.9);
    }
    // La Quemadura 2: it is Orla's cart; she stands by it, watching the field, her staff in hand.
    if (s.rules?.place === 'camp') {
      const ob = use('npc:orla');
      const os = this.lib.scaleOf('npc:orla');
      const side = this.v.set(Math.cos(yaw), 0, -Math.sin(yaw)).multiplyScalar(0.95 * scale);
      const op = kp.clone().add(side);
      this.shadows.add(op.x, op.y, op.z, 0.5);
      this.compose(op, yaw + Math.PI / 2 + Math.sin(this.now * 0.4) * 0.15, os);
      ob.add(this.m, 0);
    }
    const flick = 0.85 + ((s.tick * 7) % 5) * 0.04;
    this.entityGlow.add(kp.x, kp.y + 1.0 * scale, kp.z, LANTERN, 0.6, 0.8 * flick);
    // Where the group can leave from: a faint warm ring on the ground round the cart.
    this.entityGlow.add(kp.x, kp.y + 0.05, kp.z, LANTERN, ((PLUNDER.leaveRange * 2) / 1000) * WORLD_PER_HEX, 0.06 * flick);
  }

  /** Incursions: golems from the bastion work the node, and what they get piles up beside it. */
  private drawExtraction(s: BattleState, kind: string, kp: THREE.Vector3, use: (k: string) => ModelBatch): void {
    const o = s.objective;
    if (!o) return;
    const look = EXTRACT[kind] ?? (EXTRACT.quarry as (typeof EXTRACT)[string]);
    const running = s.result === 'running' && o.hp > 0;
    const fast = !!o.quiet;
    for (let i = 0; i < 3; i++) {
      const a = Math.PI / 6 + 0.9 + i * 2.09;
      const gp = this.v.set(kp.x + Math.cos(a) * 1.05, kp.y, kp.z + Math.sin(a) * 1.05).clone();
      const gb = use(this.rk(`golem:${look.golem}`));
      this.compose(gp, Math.atan2(kp.x - gp.x, kp.z - gp.z), this.lib.scaleOf('golem:0') * 1.7);
      // Each blow lands as its chips fly (every sixth tick from its own; twice as often on an empty field).
      const period = fast ? 3 : 6;
      const work = ((((s.tick - i * (fast ? 1 : 2)) % period) + period) % period + this.frac) / TICK_HZ * (6 / period) + 0.375;
      gb.add(this.m, running ? (fast ? s.tick : s.tick >> 1) + i : 0, undefined, 1, this.act(gb, `xg${i}`, [running ? 'work' : 'idle'], running ? work : this.now + i));
      this.shadows.add(gp.x, gp.y, gp.z, 0.7);
      if (running && s.tick % (fast ? 3 : 6) === i * (fast ? 1 : 2)) {
        const hit = gp.clone().lerp(kp, 0.55).setY(kp.y + 0.35);
        this.vfx.burst(hit, { n: fast ? 4 : 3, color: look.color, speed: 0.04, up: 0.05, size: 0.14, life: 7, gravity: 0.006 });
      }
    }
    // The pile grows with the extraction.
    const k = o.progress / o.total;
    const pp = this.v.set(kp.x - 1.15, kp.y, kp.z + 0.75).clone();
    const pb = use(`pile:${kind}`);
    this.q.setFromAxisAngle(this.yAxis, 0.5);
    this.m.compose(pp, this.q, this.s.set(0.8 + 0.8 * k, 0.2 + 1.3 * k, 0.8 + 0.8 * k));
    pb.add(this.m, 0);
    this.shadows.add(pp.x, pp.y, pp.z, 0.7 + 0.4 * k);
    // An empty field speeds the work up: the node glows while it lasts.
    if (fast && running) this.entityGlow.add(kp.x, kp.y + 0.15, kp.z, new THREE.Color('#9fe08a'), 2.6, 0.22 + 0.08 * Math.sin(s.tick * 0.5));
  }

  private drawHeroes(s: BattleState, use: (k: string) => ModelBatch): void {
    let meVisible = false;
    this.aurasN = 0;
    for (const sh of s.heroes) {
      const at = this.track.hero.at(sh.id, sh.x, sh.y, this.alpha, this.fresh);
      // My own hero may be drawn ahead of the room (the client's prediction): there, walking that way.
      const lead = sh.pid === this.opts.me && this.heroLead ? this.heroLead(sh, at.x, at.y) : null;
      let h: Hero = lead ? { ...sh, anim: lead.walk ? 'walk' : sh.anim === 'walk' ? 'idle' : sh.anim, facing: lead.facing } : sh;
      // The hammer coming down on the Anvil (Las vías 3): mid-blow for half a second, facing it.
      if (this.swing?.id === sh.id && s.tick - this.swing.tick < 6 && sh.downTicks === 0) h = { ...h, anim: 'attack', animT: s.tick - this.swing.tick, facing: ((Math.atan2(-sh.y, -sh.x) / (Math.PI * 2)) * 1024 + 1024) % 1024 };
      const x = lead ? Math.round(lead.x) : at.x;
      const y = lead ? Math.round(lead.y) : at.y;
      const p = simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y));
      this.positions.set(h.id, p.clone());
      // Orla (the dragon's fight, La Quemadura done) and the night of the bell's allies (El Párpado 4) fight as people the
      // titan doesn't dream: alive, each their own figure.
      const npc = h.pid === 'npc:orla' || (this.bellNight && h.pid.startsWith('npc:')) ? h.pid.slice(4) : null;
      const orla = !!npc;
      // Mercenaries are flesh and blood: only spectres are drawn see-through.
      const ghost = h.control === 'ghost' && !orla;
      // A druid in bear form is drawn as the bear.
      const bear = (h.bear ?? 0) > 0;
      // A path's memory (docs/SENDAS.md): the hero played as someone of its path, that one's miniature.
      const key = npc ? `ally:${npc}` : h.face ? castKey(sendaCast(h.cls, h.face), h.cls, (k) => this.rk(k)) : this.rk(heroKey(bear ? 'bear' : h.cls, h.level, ghost, h.look));
      if (h.downTicks === 0) this.shadows.add(p.x, p.y, p.z, bear ? 1.1 : 0.62);
      const b = use(key);
      // The barbarian's path done (its look ends in S5): Sigrun's paint lights while it has prey (docs/sendas/barbaro.md §9).
      if (h.cls === 'barbarian' && !npc && !h.face && h.look?.endsWith('S5')) this.lightPrey(key, !!h.lg && !!h.lgPrey && h.downTicks === 0);
      const anim = this.heroAct(b, h, s.tick);
      const frame = anim ? 0 : this.lib.poseFrame(heroPose(h, s.tick));
      const bob = ghost ? 0.06 + Math.sin((anim ? this.now * TICK_HZ : s.tick) * 0.5) * 0.04 : 0;
      this.compose(p.clone().setY(p.y + bob), anim ? this.faceTo(h.id, facingToRotY(h.facing)) : facingToRotY(h.facing), this.lib.scaleOf(key));
      if (h.downTicks > 0) {
        b.add(this.m, frame, GHOSTED, 0.3, anim);
      } else {
        b.add(this.m, frame, this.flashes.has(h.id) ? FLASH : this.furious(h) ? this.furyTint(s.tick + h.id) : undefined, 1, anim);
        const col = h.hp / h.stats.maxHp > 0.35 ? GREEN : RED;
        if (!this.cinema) this.bars.add(this.v.copy(p).setY(p.y + 1.15), h.hp / h.stats.maxHp, col, 0.55);
      }
      if (h.pid === this.opts.me && h.downTicks === 0 && !this.cinema) {
        meVisible = true;
        this.heroRing.position.set(p.x, p.y + 0.03, p.z);
      }
      if (ghost) this.ghostRing(h, p);
      if (h.invuln > 0) this.entityGlow.add(p.x, p.y + 0.5, p.z, new THREE.Color('#ffd6a0'), 1.6, 0.35);
      // Frightened by an ogre's roar: a red pulse over the head. Cursed by a mourner: lilac ink dripping off it.
      if ((h.fear ?? 0) > 0) {
        this.entityGlow.add(p.x, p.y + 1.25, p.z, FEAR, 0.45, 0.45 + (s.tick % 4 < 2 ? 0.25 : 0));
        if (s.tick % 4 === 0) this.vfx.ring(p.clone().setY(p.y + 1.2), 0.08, 0.35, '#ff4f3a', 4, 0.7);
      }
      if ((h.curse ?? 0) > 0) {
        this.entityGlow.add(p.x, p.y + 0.7, p.z, CURSE, 0.9, 0.22);
        if ((s.tick + h.id) % 5 === 0) this.vfx.burst(p.clone().setY(p.y + 1.1), { n: 2, color: '#a77bff', speed: 0.01, up: -0.01, size: 0.07, life: 12, gravity: 0.003, spread: 0.25 });
      }
      // Asleep (sleep moths): drowsy blue motes drift up off it. Poisoned (a weaver): sickly green drips.
      if ((h.sleep ?? 0) > 0) {
        this.entityGlow.add(p.x, p.y + 0.8, p.z, SLEEP, 0.8, 0.3);
        if ((s.tick + h.id) % 4 === 0) this.vfx.burst(p.clone().setY(p.y + 1.2), { n: 1, color: '#bcd8ff', speed: 0.004, up: 0.018, size: 0.11, life: 16, gravity: -0.001, spread: 0.15 });
      }
      if ((h.poison ?? 0) > 0 && (s.tick + h.id) % 4 === 0) this.vfx.burst(p.clone().setY(p.y + 0.9), { n: 2, color: '#8fdc4a', speed: 0.01, up: -0.01, size: 0.07, life: 10, gravity: 0.004, spread: 0.25 });
      // Warded by a cleric: a shimmering shell of pale light (it thins out over its last half second).
      if ((h.ward ?? 0) > 0 && h.downTicks === 0) this.holy.ward(p.clone().setY(p.y + bob), 1, Math.min(1, (h.ward ?? 0) / 6));
      if (h.taunt > 0 && s.tick % 6 < 3) this.vfx.ring(p, 0.4, 1.2, '#ff8a6e', 4, 0.5);
      // (Not in a path's scene: a ring on the ground across the whole stage, under people talking.)
      if (h.cls === 'paladin' && h.downTicks === 0 && !this.cinema && !this.opts.calm) {
        // A path's fight: the ring faint, no runes (it covered the scene on a phone).
        this.aura.dim = s.rules?.senda ? 0.32 : 1;
        this.paladinAura(s, h, p);
      }
      if (h.downTicks === 0) this.kitGlow(s, h.id, p, h.frenzy ?? 0, h.shield ?? 0, (h.bear ?? 0) > 0, h.inspire ?? 0, h.roar ?? 0);
      // The druid's whole look (docs/sendas/druida.md §10): where it treads, a few leaves of the season for two seconds.
      if (h.cls === 'druid' && h.downTicks === 0 && h.moving && this.fresh && (s.tick + h.id) % 3 === 0 && this.bloomOf(h.look)) this.leafPrint(p, s.tick + h.id);
      this.gearGlow(s, h, p);
      if (h.downTicks === 0) this.awakenGlow(s, h, p);
      if (h.downTicks === 0 && !bear) this.ultGlow(s, h, p);
      // Each swing of the work pose kicks up chips and dust in front of the hero.
      if (h.anim === 'work' && s.tick % 6 === 3) {
        const a = (h.facing / 1024) * Math.PI * 2;
        const f = p.clone().add(this.v.set(Math.cos(a) * 0.45, 0.15, Math.sin(a) * 0.45));
        this.vfx.burst(f, { n: 3, color: '#ffd0a0', speed: 0.05, up: 0.05, size: 0.08, life: 4 });
        this.vfx.burst(f, { n: 2, color: '#bfa98a', speed: 0.03, up: 0.03, size: 0.16, life: 6, smoke: true });
      }
    }
    if (this.aurasN) this.auraAllies(s);
    this.heroRing.visible = meVisible;
    for (const [pid, ring] of this.ghostRings) {
      if (!s.heroes.some((h) => h.pid === pid && h.control === 'ghost')) ring.visible = false;
    }
  }

  /**
   * The druid's leaves where it treads (its path's whole look): a fixed ring of instances reused, never new objects per
   * step; each lies two seconds and fades (shrinks) out. One draw call for every druid on the board.
   */
  private prints: THREE.InstancedMesh | null = null;
  private printAt: { x: number; y: number; z: number; t: number; yaw: number }[] = [];
  private printNext = 0;
  private bloomLooks = new Map<string, boolean>();
  private bloomOf(look: string | undefined): boolean {
    if (!look) return false;
    let b = this.bloomLooks.get(look);
    if (b === undefined) {
      b = (parseLook(look)?.senda ?? 0) >= 5;
      this.bloomLooks.set(look, b);
    }
    return b;
  }

  private leafPrint(p: THREE.Vector3, seed: number): void {
    if (!this.prints) {
      const g = new THREE.SphereGeometry(0.09, 6, 4);
      g.scale(1, 0.12, 0.55);
      const pal = SEASON_BLOOM[druidSeason()] as (typeof SEASON_BLOOM)[number];
      const m = new THREE.MeshStandardMaterial({ color: pal.leaf, roughness: 0.7, emissive: pal.glow, emissiveIntensity: 0.35 });
      this.prints = new THREE.InstancedMesh(g, m, 36);
      this.prints.count = 0;
      this.prints.frustumCulled = false;
      this.prints.name = 'druid-leaves';
      this.scene.add(this.prints);
      for (let i = 0; i < 36; i++) this.printAt.push({ x: 0, y: -99, z: 0, t: -99, yaw: 0 });
    }
    for (let k = 0; k < 2; k++) {
      const d = this.printAt[this.printNext] as { x: number; y: number; z: number; t: number; yaw: number };
      this.printNext = (this.printNext + 1) % this.printAt.length;
      const a = seed * 2.39 + k * 2.1;
      d.x = p.x + Math.cos(a) * 0.22;
      d.y = p.y + 0.03;
      d.z = p.z + Math.sin(a) * 0.22;
      d.t = this.now;
      d.yaw = a;
    }
  }

  private drawPrints(now: number): void {
    const m = this.prints;
    if (!m) return;
    let n = 0;
    for (const d of this.printAt) {
      const age = now - d.t;
      if (age < 0 || age > 2) continue;
      const k = age < 1.4 ? 1 : 1 - (age - 1.4) / 0.6;
      this.m.compose(this.v.set(d.x, d.y, d.z), this.printQ.setFromAxisAngle(this.printUp, d.yaw), this.printS.set(k, 1, k));
      m.setMatrixAt(n++, this.m);
    }
    m.count = n;
    m.instanceMatrix.needsUpdate = true;
  }
  private printQ = new THREE.Quaternion();
  private printUp = new THREE.Vector3(0, 1, 0);
  private printS = new THREE.Vector3();

  /**
   * A standing paladin's aura (aura.ts): the ring at its true reach, by the aura it carries and its rank; motes
   * rising off it now and then (more at higher ranks); life's soft flare now and then as it heals (no wave per heal:
   * it heals every second, and a wave each time was a tiring throb).
   */
  private paladinAura(s: BattleState, h: Hero, p: THREE.Vector3): void {
    const kind: AuraKind = h.aura === 'retribution' || h.aura === 'life' ? h.aura : 'devotion';
    const rank = rankAt(h.level);
    const reach = kind === 'devotion' ? PALADIN.devotion.radius : PALADIN.auras[kind].radius;
    const r = (reach / 1000) * WORLD_PER_HEX;
    const slot = this.aura.place(h.id, kind, rank, p, r, this.entityGlow);
    const a = this.auras[this.aurasN] ?? (this.auras[this.aurasN] = { id: 0, kind, slot: 0, p: new THREE.Vector3(), r: 0, r2: 0 });
    this.aurasN++;
    a.id = h.id;
    a.kind = kind;
    a.slot = slot;
    a.p.copy(p);
    a.r = r;
    a.r2 = r * r;
    if (!this.fresh) return;
    const look = AuraLayer.look(kind);
    const low = this.opts.quality === 'low';
    const every = (rank >= 3 ? 2 : rank === 2 ? 3 : 5) * (low ? 2 : 1);
    if ((s.tick + h.id) % every === 0) {
      // A mote off the rim (retribution's embers climb faster).
      const ang = ((s.tick * 2.39996 + h.id) % TAU);
      const d = r * (kind === 'life' ? 0.4 + ((s.tick * 7) % 10) / 18 : 0.92);
      this.vfx.burst(this.v.set(p.x + Math.cos(ang) * d, p.y + 0.12, p.z + Math.sin(ang) * d), { n: 1, color: look.moteHex, speed: 0.004, up: kind === 'retribution' ? 0.03 : 0.018, size: kind === 'retribution' ? 0.07 : 0.08, life: 16, gravity: -0.0008, spread: 0.1 });
    }
    if (kind === 'life' && s.tick % PALADIN.auras.life.every === 0) {
      // Life heals: the ring brightens a little (rate-limited in the layer); the allies' motes say who healed.
      this.aura.pulse(slot);
    }
  }

  /**
   * Allies inside a paladin's aura glint faintly at their feet with its light (the paladin too), and when life
   * heals, green-white motes rise off each.
   */
  private auraAllies(s: BattleState): void {
    for (let i = 0; i < this.aurasN; i++) {
      const a = this.auras[i] as (typeof this.auras)[number];
      const look = AuraLayer.look(a.kind);
      const heal = this.fresh && a.kind === 'life' && s.tick % PALADIN.auras.life.every === 0;
      for (const o of s.heroes) {
        if (o.downTicks > 0) continue;
        const q = this.positions.get(o.id);
        if (!q || (q.x - a.p.x) ** 2 + (q.z - a.p.z) ** 2 > a.r2) continue;
        this.entityGlow.add(q.x, q.y + 0.06, q.z, look.mote, 0.85, o.id === a.id ? 0.16 : 0.24);
        if (heal) this.vfx.burst(this.v.set(q.x, q.y + 0.3, q.z), { n: this.opts.quality === 'low' ? 1 : 3, color: '#eafff0', speed: 0.01, up: 0.03, size: 0.08, life: 12, gravity: -0.001, spread: 0.3 });
      }
    }
  }

  /**
   * Paladin auras acting, read off the tick's hits: a nightmare struck by holy light just before a hero's hit is
   * retribution striking back (a shaft of red-white light on it); a hero hit inside devotion gets a silver glint
   * where the blow was softened.
   */
  private auraHit(s: BattleState, e: Extract<BattleEvent, { t: 'hit' }>): void {
    if (!this.aurasN) return;
    if (this.holyHit && this.holyHit.tick !== s.tick) this.holyHit = null;
    if (this.devotionTick !== s.tick) {
      this.devotionTick = s.tick;
      this.devotionShown = 0;
    }
    const hero = s.heroes.find((x) => x.id === e.id);
    if (!hero) {
      this.holyHit = e.dt === 'holy' ? { tick: s.tick, id: e.id, x: e.x, y: e.y } : null;
      return;
    }
    const q = this.positions.get(hero.id);
    if (!q) return;
    const low = this.opts.quality === 'low';
    for (let i = 0; i < this.aurasN; i++) {
      const a = this.auras[i] as (typeof this.auras)[number];
      if ((q.x - a.p.x) ** 2 + (q.z - a.p.z) ** 2 > a.r2) continue;
      if (a.kind === 'retribution' && this.holyHit) {
        const t = simToWorld(this.holyHit.x, this.holyHit.y, new THREE.Vector3(), this.heightAt(this.holyHit.x, this.holyHit.y));
        this.vfx.shaft(t.clone().setY(t.y + 3.2), t.clone().setY(t.y + 0.1), '#ff8a6e', 4, 0.22);
        this.vfx.beam(t.clone().setY(t.y + 2.2), t.clone().setY(t.y + 0.3), '#ffffff', 2, 0.03, 0);
        this.vfx.ring(t, 0.1, 0.8, '#ff4f3a', 5, 0.8);
        this.vfx.burst(t.clone().setY(t.y + 0.5), { n: low ? 3 : 7, color: '#ffb59e', speed: 0.06, up: 0.06, size: 0.06, life: 8, gravity: -0.002 });
        this.flare(t.clone().setY(t.y + 0.5), '#ff6a4a', 1.2, 3);
        this.aura.pulse(a.slot);
        this.holyHit = null;
        return;
      }
      if (a.kind === 'devotion' && this.devotionShown < 3) {
        this.devotionShown++;
        this.vfx.burst(this.v.set(q.x, q.y + 0.6, q.z), { n: low ? 2 : 4, color: '#dce8ff', speed: 0.05, up: 0.03, size: 0.06, life: 6 });
        this.flare(this.v.set(q.x, q.y + 0.6, q.z).clone(), '#bcd4ff', 0.8, 2);
        this.aura.pulse(a.slot);
        break;
      }
    }
    this.holyHit = null;
  }

  private devotionTick = -1;

  /**
   * The warrior's pull: a faint red thread from each nightmare it has taunted back to it while the taunt lasts
   * (its challenge also pulls those striking walls, gates and towers nearby).
   */
  private drawTethers(s: BattleState): void {
    let n = 0;
    for (const e of s.enemies) {
      if (e.tauntTicks <= 0 || e.hp <= 0 || !e.taunter) continue;
      const q = this.positions.get(e.id);
      const w = this.positions.get(e.taunter);
      if (!q || !w) continue;
      const h = s.heroes.find((x) => x.id === e.taunter);
      if (!h || h.cls !== 'warrior' || h.downTicks > 0) continue;
      if (n++ >= 40) break;
      const dx = q.x - w.x, dy = q.y + 0.55 - (w.y + 0.75), dz = q.z - w.z;
      const len = Math.hypot(dx, dy, dz);
      if (len < 0.3) continue;
      const fade = Math.min(1, e.tauntTicks / 12);
      const flick = 0.8 + 0.2 * Math.sin(this.now * 9 + e.id);
      this.skillFx.streak(q.x, q.y + 0.55, q.z, dx / len, dy / len, dz / len, len, 0.022, RED, 0.55 * fade * flick);
      this.entityGlow.add(q.x, q.y + 0.55, q.z, RED, 0.35, 0.35 * fade);
    }
  }

  /**
   * A hero's clip from what the simulation says it does. Swings and skills are timed from the tick they were
   * dealt (the blow lands when the sim deals it) and play out even after the sim goes back to idle; walking
   * cuts them short.
   */
  private heroAct(b: ModelBatch, h: Hero, tick: number): AnimParams | undefined {
    if (!b.baked.anim) return undefined;
    const pz = this.posed.get(h.id);
    if (pz) return this.act(b, h.id, [pz.clip, 'idle'], pz.t ?? this.now + h.id * 0.37);
    if (h.downTicks > 0) {
      this.shots.delete(h.id);
      return this.act(b, h.id, ['down'], this.sinceTag(h.id, 'down'));
    }
    this.sinceTag(h.id, 'up');
    switch (h.anim) {
      case 'walk':
        this.shots.delete(h.id);
        return this.act(b, h.id, ['walk'], this.stride(h.id, 1));
      case 'work':
        this.shots.delete(h.id);
        // The blow lands on the tick the chips fly (every sixth, at the third).
        return this.act(b, h.id, ['work', 'idle'], ((tick % 6) + this.frac) / TICK_HZ + 0.125);
      case 'attack':
      case 'hit':
      case 'skill0':
      case 'skill1':
      case 'skill2': {
        const t = this.ago(h.animT);
        const names = h.anim === 'attack' || h.anim === 'hit' ? [h.anim] : [h.anim, 'roar', 'attack'];
        const c = names.map((n) => b.clip(n)).find((x) => x !== null);
        if (c && t < c.dur) this.shots.set(h.id, { clip: c.name, at: this.now - t });
        return this.act(b, h.id, ['idle'], this.now + h.id * 0.37);
      }
      default:
        return this.act(b, h.id, ['idle'], this.now + h.id * 0.37);
    }
  }

  /** Where each person of a path stood at the tick before (what walks glides between ticks). */
  private sendaWas = new Map<string, { x: number; y: number; tx: number; ty: number }>();

  /**
   * A path's people on the field (battle/senda.ts): each one's miniature from its path's cast, walking, working or
   * waiting turned to the hero; its life (in blows) as a bar, a flash when struck, faded when down. Where each stands
   * goes to `sendaAt` for the HUD (their names and what they say).
   */
  private drawSendaPeople(s: BattleState, use: (k: string) => ModelBatch): void {
    const st = s.senda;
    const rules = s.rules?.senda;
    this.sendaAt.clear();
    if (!st || !rules) return;
    const cls = s.heroes[0]?.cls ?? 'warrior';
    const hero = s.heroes.find((h) => h.downTicks === 0) ?? s.heroes[0];
    for (let i = 0; i < st.npcs.length; i++) {
      const n = st.npcs[i];
      const def = rules.npcs[i];
      if (!n || !def || n.away) continue;
      // Glide from the last tick's place to this one's, as the heroes do.
      let w = this.sendaWas.get(n.id);
      if (!w) {
        w = { x: n.x, y: n.y, tx: n.x, ty: n.y };
        this.sendaWas.set(n.id, w);
      }
      if (this.fresh && (w.tx !== n.x || w.ty !== n.y)) {
        w.x = w.tx;
        w.y = w.ty;
        w.tx = n.x;
        w.ty = n.y;
      }
      const k = Math.min(1, this.alpha);
      const x = w.x + (w.tx - w.x) * k;
      const y = w.y + (w.ty - w.y) * k;
      // (Fermín stands on the fort's ridge: `lift` over the ground.)
      const p = simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y) + (def.lift ? (def.lift / 1000) * WORLD_PER_HEX : 0));
      this.sendaAt.set(n.id, p.clone());
      // A thing to keep, not a person (the paladin's second altar): the place draws it; its bar and its ring stay.
      if (def.prop) {
        const ratio = n.hp / Math.max(1, n.maxHp);
        if (!this.cinema && n.hp > 0) this.bars.add(this.v.copy(p).setY(p.y + 1.45), ratio, ratio > 0.35 ? GREEN : RED, 0.7);
        continue;
      }
      // Its own miniature for this fight (Wenn on her mule), else the cast's.
      const key = def.model ? this.rk(`senda:${def.model}`) : castKey(sendaCast(cls, n.id), cls, (q) => this.rk(q));
      const b = use(key);
      // Entity ids for the animator: clear of every hero's, bot's and nightmare's.
      const id = -9000 - i;
      const down = n.hp <= 0;
      const spot = def.spots[n.spot];
      const digging = def.does === 'work' && !n.moving && !!spot && n.x === spot.x && n.y === spot.y && n.held > 0;
      const facing = n.moving ? n.facing : digging && spot ? n.facing : hero ? ((Math.atan2(hero.y - n.y, hero.x - n.x) / (Math.PI * 2)) * 1024 + 1024) % 1024 : n.facing;
      // A person working kneels and digs (the folk's own clip).
      // (In the crown's hollow Ceda kneels at Hob's leg and he sits with it out: the druid's chapter 4.)
      const hollow = !!s.rules?.senda?.winter?.hollow && !down;
      if (digging) this.posed.set(id, { clip: 'dig' });
      else if (hollow && n.id === 'ceda') this.posed.set(id, { clip: 'kneel' });
      else if (hollow && n.id === 'hob') this.posed.set(id, { clip: 'sit' });
      else this.posed.delete(id);
      const fake = { id, anim: down ? 'idle' : n.moving ? 'walk' : 'idle', animT: 0, downTicks: down ? 1 : 0, cls } as unknown as Hero;
      const anim = this.heroAct(b, fake, s.tick);
      const frame = anim ? 0 : this.lib.poseFrame(heroPose(fake, s.tick));
      this.compose(p, anim ? this.faceTo(id, facingToRotY(facing)) : facingToRotY(facing), this.lib.scaleOf(key));
      if (down) {
        b.add(this.m, frame, GHOSTED, 0.3, anim);
        continue;
      }
      this.shadows.add(p.x, p.y, p.z, 0.62);
      b.add(this.m, frame, n.flinch === rules.hit.flinch ? FLASH : undefined, 1, anim);
      // Someone kept out of the fight (never struck) has no bar to watch nor a ring to keep.
      if (def.safe) continue;
      const ratio = n.hp / Math.max(1, n.maxHp);
      if (!this.cinema) this.bars.add(this.v.copy(p).setY(p.y + 1.15), ratio, ratio > 0.35 ? GREEN : RED, 0.55);
      // A soft cold ring at their feet: someone to keep, not to strike.
      this.entityGlow.add(p.x, p.y + 0.04, p.z, SENDA_KEEP, 1.1, 0.16);
    }
  }

  /** A campfire on the board: its warm pool on the ground, the flame's glow, embers and a thread of smoke. */
  private drawFire(): void {
    const f = this.fireAt;
    if (!f) return;
    const p = simToWorld(f.x, f.y, new THREE.Vector3(), this.heightAt(f.x, f.y));
    const flick = 0.85 + Math.sin(this.now * 9.1) * 0.08 + Math.sin(this.now * 23.7) * 0.05;
    // On the Spine's snow (a close camera, white ground) the glow adds up to a pink wash over the frame: a tighter,
    // smaller fire there, orange against the blue night.
    // At the seventh altar's cistern the pool would spill over the dark water and stain it brown: the fire's warmth
    // kept to the rim's flags round it, the water left cold.
    const k = this.spine ? 0.55 : 1;
    const pool = this.seventh ? 0.42 : this.spine ? 0.7 : 1;
    this.entityGlow.add(p.x, p.y + 0.05, p.z, FIRE_POOL, 4.2 * pool, 0.22 * k * (this.seventh ? 0.7 : 1) * flick);
    this.entityGlow.add(p.x, p.y + 0.3, p.z, FIRE_CORE, 1.1 * k, 0.85 * flick);
    this.entityGlow.add(p.x, p.y + 0.55, p.z, FIRE_TIP, 0.6 * k, 0.6 * k * flick);
    if (this.fresh) {
      this.vfx.burst(this.v.set(p.x, p.y + 0.3, p.z), { n: 2, color: '#ff8a6e', speed: 0.01, up: 0.03, size: 0.05, life: 18, gravity: -0.0015, spread: 0.18 });
      // (No smoke on the Spine: its common room's camera sits at the fire, and the puffs wash the whole frame.)
      if (this.lastTick % 3 === 0 && !this.spine) this.vfx.burst(this.v.set(p.x, p.y + 0.7, p.z), { n: 1, color: '#5b5f68', speed: 0.004, up: 0.02, size: 0.3, life: 30, gravity: -0.0006, spread: 0.08, smoke: true });
    }
  }

  /**
   * A path's cinematic (client game/cine.ts): no bars or rings, its light (the sky's hour), its weather (an omen's
   * dressing, cosmetic: the sim never sees it) and a campfire if it has one. Called once, before the first frame.
   */
  setCinema(o: { sky?: 'day' | 'dusk' | 'night' | 'dawn'; weather?: string; fire?: { x: number; y: number }; flood?: boolean; torches?: boolean; eye?: boolean; broken?: boolean; face?: boolean; breath?: boolean }): void {
    this.cinema = true;
    this.raicesCine = { ...(o.face ? { face: true } : {}), ...(o.breath ? { breath: true } : {}) };
    this.raices?.setCine(this.raicesCine);
    this.fireAt = o.fire ?? null;
    if (o.weather && !this.omenTried && !this.spine && !this.salt && !this.arch && !this.monastery && !this.seventh && !this.asterL && !this.caza && !this.vert && !this.bote && !this.isla && !this.roca && !this.bosque && !this.hornos && !this.otono && !this.copa && !this.raices) this.startOmen(o.weather);
    const t = this.terrain;
    const sky = this.sky;
    // The Spine paints its own light by the hour; so does the Way of the Altars.
    if (this.spine) {
      this.spine.paint(o.sky ?? 'day');
      this.dirty = true;
      return;
    }
    if (this.taller) {
      this.taller.paint(o.sky ?? 'night');
      // The workshop broken into (the artificer's chapter 4 close): its door down, Tormo's chest open and empty.
      if (o.broken) this.taller.breakIn();
      this.dirty = true;
      return;
    }
    if (this.cable) {
      this.cable.paint(o.sky ?? 'day');
      this.dirty = true;
      return;
    }
    if (this.biblio) {
      this.biblio.paint(o.sky ?? 'night');
      this.dirty = true;
      return;
    }
    if (this.corazon) {
      this.corazon.paint();
      this.dirty = true;
      return;
    }
    if (this.ojos) {
      this.ojos.paint(o.sky ?? 'night');
      this.dirty = true;
      return;
    }
    if (this.tormoL) {
      this.tormoL.paint(o.sky ?? 'day');
      this.dirty = true;
      return;
    }
    if (this.caza) {
      this.caza.paint();
      this.dirty = true;
      return;
    }
    if (this.vert) {
      this.vert.paint();
      this.dirty = true;
      return;
    }
    if (this.bote) {
      this.bote.paint();
      this.dirty = true;
      return;
    }
    if (this.isla) {
      this.isla.paint(o.sky);
      this.dirty = true;
      return;
    }
    if (this.roca) {
      this.roca.paint(o.sky);
      this.dirty = true;
      return;
    }
    if (this.camino) {
      this.camino.paint(o.sky ?? 'night');
      this.dirty = true;
      return;
    }
    if (this.pasarela) {
      this.pasarela.paint('night');
      this.dirty = true;
      return;
    }
    if (this.nieve) {
      this.nieve.paint(o.sky ?? 'night');
      this.dirty = true;
      return;
    }
    if (this.ermita) {
      this.ermita.paint(o.sky ?? 'night');
      this.dirty = true;
      return;
    }
    if (this.bosque) {
      this.bosque.paint(o.sky ?? 'day', o.weather);
      this.dirty = true;
      return;
    }
    if (this.hornos) {
      this.hornos.paint(o.sky ?? 'day', o.weather);
      this.dirty = true;
      return;
    }
    if (this.otono) {
      this.otono.paint(o.sky ?? 'day', o.weather);
      this.dirty = true;
      return;
    }
    if (this.copa) {
      this.copa.paint(o.sky ?? 'day', o.weather);
      this.dirty = true;
      return;
    }
    if (this.raices) {
      this.raices.paint(o.sky ?? 'dusk', o.weather);
      this.dirty = true;
      return;
    }
    if (this.asterL) {
      this.asterL.paint(o.sky ?? 'day');
      this.dirty = true;
      return;
    }
    if (this.altars) {
      this.altars.paint(o.sky ?? 'day');
      this.dirty = true;
      return;
    }
    if (this.salt) {
      this.salt.paint(o.sky ?? 'day');
      this.dirty = true;
      return;
    }
    if (this.arch) {
      this.arch.paint(o.sky ?? 'dusk');
      this.dirty = true;
      return;
    }
    // The monastery of the waterfall: its place's own hour (each place has one: the scenes' skies agree), the chapel's
    // roof on, the flood and the summit's torches when the scene has them.
    if (this.monastery) {
      this.monastery.cue({ scene: true, ...(o.flood ? { flood: true } : {}), ...(o.torches ? { torches: true } : {}) });
      this.dirty = true;
      return;
    }
    // The seventh altar: each place its own hour (the yard and the cistern at night, the keep's top in the morning);
    // the close's with the altar's eye open.
    if (this.seventh) {
      this.seventh.cue('scene');
      if (o.eye) this.seventh.cue('eye');
      this.seventh.paint();
      this.dirty = true;
      return;
    }
    const look = o.sky ? CINE_SKY[o.sky] : null;
    if (look) {
      this.scene.background = new THREE.Color(look.bg);
      if (this.scene.fog instanceof THREE.Fog) this.scene.fog.color.set(look.fog);
      t.fog?.tint({ low: look.low, mid: look.mid, far: look.bg, alpha: 1 });
      if (sky) {
        sky.hemi.color.set(look.hemi[0]);
        sky.hemi.groundColor.set(look.hemi[1]);
        sky.hemi.intensity = look.hemi[2];
        sky.key.color.set(look.key[0]);
        sky.key.intensity = look.key[1];
        sky.rim.color.set(look.rim[0]);
        sky.rim.intensity = look.rim[1];
      }
    }
    this.dirty = true;
  }

  /** This sync is a new sim tick (not a redraw): one-shot particles spawn only then. */
  private fresh = false;
  /** Last known clockwork bots by position, to tell which one broke (its event carries only where). */
  private botKinds = new Map<string, Bot['kind']>();
  /** Starting ticks of each gear grenade (its vortex tightens as they run out). */
  private grenadeMax = new Map<number, number>();

  /**
   * The artificer's clockwork. Automatons and the colossus drop in where they were cast, idle with a
   * breathing core, wind the hammer back and sweep it around them; spider-bots scuttle at the nightmares.
   */
  private drawBots(s: BattleState, use: (k: string) => ModelBatch): void {
    this.botKinds.clear();
    for (const b of s.bots ?? []) {
      this.botKinds.set(`${b.x},${b.y}`, b.kind);
      const { x, y } = this.track.bot.at(b.id, b.x, b.y, this.alpha, this.fresh);
      const ground = this.heightAt(x, y);
      const p = simToWorld(x, y, new THREE.Vector3(), ground);
      if (b.dt) this.veil(s, p, 0.5, b.dt, b.id);
      // Las vías 1: the caravan is the clan's train of mine carts (no wagon model to bake).
      if (b.kind === 'cart' && this.mine) {
        this.trainAt.x = x;
        this.trainAt.y = y;
        continue;
      }
      // El Párpado 2: the caravan is the Order's bell, dragged on its ropes (engine/bell.ts).
      if (b.kind === 'cart' && this.orderBell) {
        this.orderBell.place(x, y);
        const was = this.positions.get(b.id);
        if (was) was.copy(p);
        else this.positions.set(b.id, p.clone());
        this.shadows.add(p.x, ground, p.z, 1.8);
        continue;
      }
      // The artificer's legacy (docs/sendas/artifice.md §9): Tormo at a hex and a half, on his knees while broken.
      if (b.tormo) {
        const tk = this.rk('senda:tormo');
        const tb = use(tk);
        const tsc = this.lib.scaleOf(tk) * 0.5;
        const tall = this.lib.heightOf(tk) * tsc;
        const yaw = this.faceTo(b.id, facingToRotY(b.facing));
        const kneel = b.kneel !== undefined;
        const anim = kneel
          ? this.act(tb, b.id, ['kneel', 'idle'], 1.5)
          : b.anim === 'attack' && this.ago(b.animT) < 0.6
            ? this.act(tb, b.id, ['dig', 'attack', 'idle'], this.ago(b.animT))
            : b.anim === 'walk'
              ? this.act(tb, b.id, ['walk'], this.stride(b.id, 0.6))
              : this.act(tb, b.id, ['idle'], this.now + b.id * 0.5);
        this.positions.set(b.id, p.clone());
        this.shadows.add(p.x, ground, p.z, 1.4);
        this.compose(p, yaw, tsc);
        tb.add(this.m, 0, this.flashes.has(b.id) ? FLASH : undefined, 1, anim);
        if (kneel) this.entityGlow.add(p.x, ground + 0.06, p.z, RED, 1.4, 0.2);
        else if (b.hp < b.maxHp) this.bars.add(this.v.copy(p).setY(p.y + tall + 0.15), b.hp / b.maxHp, b.hp / b.maxHp > 0.35 ? GREEN : RED, 0.8);
        continue;
      }
      // Las vías 2: Dorn's guard automatons (drawn by frames, not rigged).
      if (b.kind === 'guard') {
        this.drawGuard(s, b, p, ground, use);
        continue;
      }
      // Brann's bet «El barril»: the caravan is the garrison's cask on the same wagon.
      const key = this.rk(`bot:${b.kind === 'cart' && betOf(s) === 'barrel' ? 'barrelCart' : b.kind === 'gasparGuard' && b.gaspar ? 'gaspar' : b.kind === 'decoy' && b.uq === 'messengerCape' ? 'cloak' : b.kind}`);
      const batch = use(key);
      const scale = this.lib.scaleOf(key);
      const tall = this.lib.heightOf(key) * scale;
      const big = b.kind === 'colossus';
      let yaw = facingToRotY(b.facing);
      const rigged = batch.baked.anim !== null;
      // The hawk turns its own way while it circles (drawCompanion).
      if (rigged && b.kind !== 'hawk') yaw = this.faceTo(b.id, yaw);
      if (b.kind === 'cart') {
        this.drawCart(s, b, p, ground, yaw, batch, scale, tall);
        continue;
      }
      if (b.kind === 'decoy' || b.kind === 'hawk' || b.kind === 'drone' || b.kind === 'hound' || b.kind === 'treant' || b.kind === 'gasparGuard' || b.kind === 'cloak' || b.kind === 'barkBear' || b.kind === 'shadeWolf' || b.kind === 'mastiff') {
        this.drawCompanion(s, b, p, ground, yaw, batch, scale, tall);
        continue;
      }
      let frame = 0;
      let anim: AnimParams | undefined;
      if (b.kind === 'spider') {
        const walk = b.anim === 'walk';
        // About to go off: the last half second it crouches and springs.
        const left = b.expires - s.tick;
        if (rigged) anim = left <= 5 ? this.act(batch, b.id, ['attack'], (5 - left + this.frac) / TICK_HZ) : this.act(batch, b.id, [walk ? 'walk' : 'idle'], walk ? this.stride(b.id, 1) : this.now + b.id * 0.3);
        else {
          // Scuttling: the four gait frames and a skittering hop; at rest it shifts its feet now and then.
          frame = walk ? (s.tick + b.id) & 3 : ((s.tick >> 2) + b.id) % 3 === 0 ? 1 : 3;
          if (walk) {
            p.y += (s.tick + b.id) & 1 ? 0.035 : 0;
            yaw += ((s.tick + b.id) & 2 ? 0.12 : -0.12);
          }
        }
        // About to go off: the last second it blinks red.
        if (b.expires - s.tick < 12 && s.tick & 1) this.entityGlow.add(p.x, p.y + 0.2, p.z, RED, 0.5, 0.7);
      } else {
        const age = SKILLS.automaton.duration - (b.expires - s.tick);
        if (rigged) {
          // Dropped from the sky where it was cast (the clip falls and lands), then it fights.
          const spawn = batch.clip('spawn');
          if (spawn && age >= 0 && this.ago(age) < spawn.dur) anim = this.act(batch, b.id, ['spawn'], this.ago(age));
          else if (b.anim === 'attack' && this.ago(b.animT) < (batch.clip('attack')?.dur ?? 0)) anim = this.act(batch, b.id, ['attack'], this.ago(b.animT));
          else if (b.anim === 'walk') anim = this.act(batch, b.id, ['walk'], this.stride(b.id, 1));
          else anim = this.act(batch, b.id, ['idle'], this.now + b.id * 0.5);
        } else {
          // Dropped from above where it was cast: it falls the first few ticks and lands with a jolt.
          if (age >= 0 && age < 3) p.y += (3 - age) * (big ? 0.7 : 0.5);
          if (b.anim === 'attack' && b.animT < 4) {
            frame = this.lib.botFrame('sweep');
            // The sweep carries the whole body round (stop-motion: a few big steps).
            yaw += [-0.7, 0.2, 0.9, 1.25][b.animT] ?? 0;
          } else if (b.anim === 'attack' && b.cd <= 2) {
            frame = this.lib.botFrame('windup');
            yaw -= 0.35;
          } else if (b.anim === 'walk') frame = this.lib.botFrame(s.tick % 6 < 3 ? 'walkA' : 'walkB');
          else frame = this.lib.botFrame(((s.tick >> 3) + b.id) & 1 ? 'idleB' : 'idle');
        }
        // The core breathes, and a faint pull runs over the ground around it now and then (it draws the nightmares).
        const k = 0.5 + 0.5 * Math.sin((s.tick + b.id) * 0.45);
        this.entityGlow.add(p.x, ground + 0.06, p.z, CLOCK_CYAN, (big ? 2.2 : 1.5) * (0.85 + 0.15 * k), 0.16 + 0.1 * k);
        if (this.fresh && b.taunt > 0 && (s.tick + b.id) % 30 === 0) {
          this.vfx.ring(this.v.set(p.x, ground, p.z), (b.taunt / 1000) * WORLD_PER_HEX, 0.4, '#4fdcff', 12, 0.3);
        }
        // Steam from the stacks.
        if (this.fresh && (s.tick + b.id) % 5 === 0) {
          this.vfx.burst(this.v.set(p.x - Math.sin(yaw) * 0.25 * scale * 1.6, p.y + tall * 0.95, p.z - Math.cos(yaw) * 0.25 * scale * 1.6), { n: 1, color: '#9aa3ad', speed: 0.01, up: 0.03, size: big ? 0.3 : 0.2, life: 12, smoke: true, gravity: -0.002 });
        }
      }
      this.positions.set(b.id, p.clone());
      this.shadows.add(p.x, ground, p.z, b.kind === 'spider' ? 0.38 : big ? 1.35 : 0.9);
      this.compose(p, yaw, scale);
      batch.add(this.m, frame, this.flashes.has(b.id) ? FLASH : undefined, 1, anim);
      if (b.kind !== 'spider' || b.hp < b.maxHp) {
        const col = b.hp / b.maxHp > 0.35 ? GREEN : RED;
        this.bars.add(this.v.copy(p).setY(p.y + tall + 0.15), b.hp / b.maxHp, col, b.kind === 'spider' ? 0.3 : big ? 0.9 : 0.6);
      }
      if (b.kind !== 'spider') {
        // How long it still stands.
        this.bars.add(this.v.copy(p).setY(p.y + tall + 0.05), Math.max(0, (b.expires - s.tick) / SKILLS.automaton.duration), CLOCK_CYAN, big ? 0.9 : 0.6);
      }
    }
  }

  /**
   * Las vías 2: one of Dorn's guard automatons. Lit red, it shuffles to what it goes for, cocks its maul and brings it
   * down; wound down (its lever pulled) it slumps where it stands, dark. Its life bar is a foe's (it strikes anyone).
   */
  private drawGuard(s: BattleState, b: Bot, p: THREE.Vector3, ground: number, use: (k: string) => ModelBatch): void {
    // Dorn's own (Las vías 4) are his colours: a cold blue-green light, a friend's life bar.
    const key = b.ally ? 'bot:guardDorn' : 'bot:guard';
    const batch = use(key);
    const scale = this.lib.scaleOf(key);
    const tall = this.lib.heightOf(key) * scale;
    let yaw = this.faceTo(b.id, facingToRotY(b.facing));
    let frame: number;
    if (b.off) frame = this.lib.guardFrame('off');
    else if (b.anim === 'attack' && b.animT < 3) {
      frame = this.lib.guardFrame('strike');
      // The blow carries it forward a step.
      p.x += Math.sin(yaw) * 0.08;
      p.z += Math.cos(yaw) * 0.08;
    } else if (b.anim !== 'walk' && b.cd > 0 && b.cd <= 4) {
      frame = this.lib.guardFrame('windup');
      yaw -= 0.25;
    } else if (b.anim === 'walk') {
      frame = this.lib.guardFrame(s.tick % 6 < 3 ? 'walkA' : 'walkB');
      p.y += s.tick % 3 === 0 ? 0.02 : 0;
    } else frame = this.lib.guardFrame(((s.tick >> 3) + b.id) & 1 ? 'idleB' : 'idle');
    this.positions.set(b.id, p.clone());
    this.shadows.add(p.x, ground, p.z, 0.75);
    this.compose(p, yaw, scale);
    batch.add(this.m, frame, this.flashes.has(b.id) ? FLASH : undefined);
    if (!b.off) {
      // Its red light on the floor in front of it.
      const k = 0.5 + 0.5 * Math.sin((s.tick + b.id) * 0.5);
      this.entityGlow.add(p.x + Math.sin(yaw) * 0.25, ground + 0.06, p.z + Math.cos(yaw) * 0.25, b.ally ? DORN_TEAL : RED, 0.9, 0.12 + 0.06 * k);
      if (b.hp < b.maxHp) this.bars.add(this.v.copy(p).setY(p.y + tall + 0.12), b.hp / b.maxHp, b.ally ? GREEN : RED, 0.5);
    }
  }

  /**
   * The learned skills' companions. The decoy stands on its stake, pennant waving, a red pull rippling out
   * now and then (it draws the nightmares), shaking when struck. The hawk circles over the ranger, beats on after
   * its prey and stoops on it when it strikes; a pale ring under it shows where it is.
   */
  private drawCompanion(s: BattleState, b: Bot, p: THREE.Vector3, ground: number, yaw: number, batch: ModelBatch, scale: number, tall: number): void {
    const hawk = b.kind === 'hawk' || b.kind === 'drone';
    let anim: AnimParams | undefined;
    // The hound and the trees trot (their frames turn while they walk) and lunge when they strike.
    const walker = b.kind === 'hound' || b.kind === 'treant' || b.kind === 'gasparGuard' || b.kind === 'barkBear' || b.kind === 'shadeWolf' || b.kind === 'mastiff';
    const frame = walker && b.anim !== 'walk' ? 0 : (s.tick + b.id) & 3;
    if (walker && b.anim === 'attack' && b.animT < 3) {
      const a = (b.facing / 1024) * Math.PI * 2;
      p.x += Math.cos(a) * 0.15;
      p.z += Math.sin(a) * 0.15;
    }
    if (b.kind === 'drone') {
      // The drone: a cyan lamp under it and a little bob; mending, it dips toward its work.
      p.y += Math.sin((s.tick + this.frac + b.id) * 0.6) * 0.06 - (b.anim === 'attack' && b.animT < 3 ? 0.25 : 0);
      this.shadows.add(p.x, ground, p.z, 0.3);
      this.entityGlow.add(p.x, ground + 0.05, p.z, DRONE_CYAN, 0.6, 0.2);
    } else if (hawk && batch.baked.anim) {
      ({ anim, yaw } = this.hawkFlight(s, b, p, yaw, batch));
      this.shadows.add(p.x, ground, p.z, 0.35);
      this.entityGlow.add(p.x, ground + 0.05, p.z, HAWK_PALE, 0.6, 0.18);
    } else if (hawk) {
      // The stoop: down onto the prey and back up.
      if (b.anim === 'attack' && b.animT < 4) p.y -= [0.9, 1.3, 0.8, 0.3][b.animT] ?? 0;
      this.shadows.add(p.x, ground, p.z, 0.35);
      this.entityGlow.add(p.x, ground + 0.05, p.z, HAWK_PALE, 0.6, 0.18);
    } else if (b.kind === 'gasparGuard') {
      // The garrison's spectre: a cold pool of light under it and a taunting ring now and then (Ser Gaspar's, red-edged).
      this.shadows.add(p.x, ground, p.z, 0.4);
      this.entityGlow.add(p.x, ground + 0.06, p.z, SPECTRE_BLUE, 1.4, 0.3);
      this.entityGlow.add(p.x, p.y + 1.2 * scale, p.z, SPECTRE_BLUE, 1.1, 0.22);
      if (this.fresh && (s.tick + b.id) % 12 === 0) this.vfx.ring(this.v.set(p.x, ground, p.z), (b.taunt / 1000) * WORLD_PER_HEX, 0.4, b.gaspar ? '#ff8a6e' : '#eef2ff', 10, 0.35);
      if (this.fresh && (s.tick + b.id) % 3 === 0) this.vfx.burst(this.v.set(p.x, ground + 0.4, p.z), { n: 1, color: '#dfe8ff', speed: 0.01, up: 0.03, size: 0.3, life: 12, smoke: true, gravity: -0.002 });
    } else if (b.kind === 'barkBear') {
      // The bark bear: green sap glowing, leaves shaken off as it goes.
      this.shadows.add(p.x, ground, p.z, 0.8);
      this.entityGlow.add(p.x, ground + 0.06, p.z, SAP_GREEN, 1.1, 0.16);
      if (this.fresh && b.anim === 'walk' && (s.tick + b.id) % 4 === 0) this.vfx.burst(this.v.set(p.x, p.y + 1.1 * scale, p.z), { n: 1, color: '#6fa05a', speed: 0.02, up: 0.02, size: 0.1, life: 12, gravity: 0.006 });
    } else if (b.kind === 'mastiff') {
      // The kennel's mastiffs: a dark shadow under each, dust kicked up as they run, a snarl's red on the bite.
      this.shadows.add(p.x, ground, p.z, 0.7);
      // The garrison's red at its collar, through the glow layer, so a dog reads at the battle camera.
      const a = (b.facing / 1024) * TAU;
      this.entityGlow.add(p.x + Math.cos(a) * 0.22, ground + 0.5 * scale, p.z + Math.sin(a) * 0.22, RED, 0.32, 0.5);
      if (this.fresh && b.anim === 'walk' && (s.tick + b.id) % 2 === 0) this.vfx.burst(this.v.set(p.x - Math.cos(a) * 0.2, ground + 0.06, p.z - Math.sin(a) * 0.2), { n: 2, color: '#8b8072', speed: 0.025, up: 0.02, size: 0.28, life: 12, smoke: true });
      if (this.fresh && b.anim === 'attack' && b.animT === 0) this.vfx.burst(this.v.set(p.x, p.y + 0.45, p.z), { n: 3, color: '#ff6a4a', speed: 0.05, up: 0.03, size: 0.06, life: 5 });
    } else if (b.kind === 'shadeWolf') {
      // The goatherd's shadow wolves: a cold pale pool under each and dusk shed as they run (docs/PANOPLIA.md §9.5).
      this.shadows.add(p.x, ground, p.z, 0.45);
      this.entityGlow.add(p.x, ground + 0.05, p.z, SPECTRE_BLUE, 0.8, 0.16);
      if (this.fresh && b.anim === 'walk' && (s.tick + b.id) % 3 === 0) this.vfx.burst(this.v.set(p.x, p.y + 0.5 * scale, p.z), { n: 1, color: '#2a2e38', speed: 0.01, up: 0.02, size: 0.3, life: 10, smoke: true, gravity: -0.002 });
    } else if (b.uq === 'messengerCape') {
      // The messenger's image (docs/PANOPLIA.md §10): a cloak of cold light where the mage stood, a pull out of it.
      this.shadows.add(p.x, ground, p.z, 0.4);
      this.entityGlow.add(p.x, p.y + 1.1 * scale, p.z, SPECTRE_BLUE, 1.2, 0.25);
      if (this.fresh && (s.tick + b.id) % 12 === 0) this.vfx.ring(this.v.set(p.x, ground, p.z), (b.taunt / 1000) * WORLD_PER_HEX, 0.3, '#cfdcff', 10, 0.3);
    } else if (b.kind === 'cloak') {
      // The empty cloak: what draws the nightmares away, a faint red pull out of it.
      this.shadows.add(p.x, ground, p.z, 0.5);
      if (this.fresh && (s.tick + b.id) % 12 === 0) this.vfx.ring(this.v.set(p.x, ground, p.z), (b.taunt / 1000) * WORLD_PER_HEX, 0.3, '#ff6a4a', 10, 0.3);
    } else {
      // Struck: it rocks on its stake.
      if (this.flashes.has(b.id)) yaw += s.tick & 1 ? 0.12 : -0.12;
      this.shadows.add(p.x, ground, p.z, 0.55);
      if (this.fresh && b.taunt > 0 && (s.tick + b.id) % 24 === 0) this.vfx.ring(this.v.set(p.x, ground, p.z), (b.taunt / 1000) * WORLD_PER_HEX, 0.3, '#ff6a4a', 12, 0.35);
      if (b.reflectBp) this.entityGlow.add(p.x, ground + 0.6, p.z, RED, 0.9, 0.12);
    }
    this.positions.set(b.id, p.clone());
    this.compose(p, yaw, scale);
    batch.add(this.m, frame, this.flashes.has(b.id) ? FLASH : undefined, 1, anim);
    const top = p.y + tall * (hawk ? 0.2 : 1) + 0.15;
    if (!hawk) this.bars.add(this.v.copy(p).setY(top), b.hp / b.maxHp, b.hp / b.maxHp > 0.35 ? GREEN : RED, 0.6);
    // The mastiffs serve the whole wave: no time bar.
    if (b.kind === 'mastiff') return;
    const life = FORM_BOT_LIFE[b.kind] ?? SKILLS[b.kind === 'drone' ? 'repairDrone' : b.kind === 'hound' ? 'clockHound' : b.kind === 'treant' ? 'forestAwakening' : hawk ? 'hawk' : 'decoy'].duration;
    this.bars.add(this.v.copy(p).setY(top - 0.1), Math.min(1, Math.max(0, (b.expires - s.tick) / life)), hawk ? HAWK_BAR : DECOY_BAR, 0.5);
  }

  /** How much each hawk is circling (0 chasing or striking, 1 wheeling over its ranger), eased. */
  private hawkCircle = new Map<number, number>();

  /**
   * The rigged hawk's flight (anim/creatures/hawk.ts). Hunting, it stoops on the pulse's countdown so its talons
   * land on the tick the blow does; chasing, it beats on; with nothing to hunt it wheels round over its ranger,
   * carried on a small circle here (the sim keeps it still) and banked into the turn by its clip.
   */
  private hawkFlight(s: BattleState, b: Bot, p: THREE.Vector3, yaw: number, batch: ModelBatch): { anim: AnimParams | undefined; yaw: number } {
    const lead = Math.round(HAWK_STRIKE * TICK_HZ);
    const o = s.heroes.find((h) => h.id === b.owner);
    // The sim's rule for following its ranger (bots.ts hawkTick): it only moves beyond 600.
    const far = !!o && (b.x - o.x) * (b.x - o.x) + (b.y - o.y) * (b.y - o.y) > 600 * 600;
    const circling = b.anim === 'idle' || (b.anim === 'walk' && !far);
    const dt = Math.max(0, this.now - this.lastNow);
    const was = this.hawkCircle.get(b.id) ?? (circling ? 1 : 0);
    const w = was + ((circling ? 1 : 0) - was) * Math.min(1, dt * 2.5);
    this.hawkCircle.set(b.id, w);
    let target = yaw;
    if (w > 0.01) {
      const a = this.now * 0.85 + b.id * 1.7;
      p.x += Math.cos(a) * 0.5 * w;
      p.z += Math.sin(a) * 0.5 * w;
      if (w > 0.5) target = Math.atan2(-Math.sin(a), Math.cos(a));
    }
    yaw = this.faceTo(b.id, target);
    let anim: AnimParams | undefined;
    if (b.anim === 'attack' && b.cd > 0 && b.cd <= lead) anim = this.act(batch, b.id, ['attack'], (lead - b.cd + this.frac) / TICK_HZ);
    else if (b.anim === 'attack' && HAWK_STRIKE + this.ago(b.animT) < 1) anim = this.act(batch, b.id, ['attack'], HAWK_STRIKE + this.ago(b.animT));
    // Between pulses over its prey (a slow pulse) it hangs there as it does circling.
    else if (circling || b.anim === 'attack') anim = this.act(batch, b.id, ['idle'], this.now + b.id * 0.9);
    else anim = this.act(batch, b.id, ['walk'], this.stride(b.id, 1));
    return { anim, yaw };
  }

  /**
   * The saga's caravan: the golem pushes it along (wheels turning) or it waits while nightmares are on it;
   * its lanterns throw a warm pool of light on the ground (what they reveal), and its health always shows.
   */
  private drawCart(s: BattleState, b: Bot, p: THREE.Vector3, ground: number, yaw: number, batch: ModelBatch, scale: number, tall: number): void {
    const walk = b.anim === 'walk';
    const frame = walk ? s.tick & 3 : 0;
    let anim: AnimParams | undefined;
    if (batch.baked.anim) {
      anim = this.act(batch, b.id, [walk ? 'walk' : 'idle'], walk ? this.stride(b.id, 1) : this.now);
    } else if (walk && s.tick & 1) p.y += 0.015;
    this.positions.set(b.id, p.clone());
    this.shadows.add(p.x, ground, p.z, 1.3);
    this.compose(p, yaw, scale);
    batch.add(this.m, frame, this.flashes.has(b.id) ? FLASH : undefined, 1, anim);
    const k = b.hp / b.maxHp;
    this.bars.add(this.v.copy(p).setY(p.y + tall + 0.15), k, k > 0.35 ? GREEN : RED, 0.9);
    const fx = Math.sin(yaw), fz = Math.cos(yaw);
    const flick = 0.85 + ((s.tick * 7 + b.id) % 5) * 0.04;
    for (const side of [-1, 1]) this.entityGlow.add(p.x + fx * 0.6 * scale + fz * side * 0.33 * scale, ground + 1.0 * scale, p.z + fz * 0.6 * scale - fx * side * 0.33 * scale, LANTERN, 0.5, 0.8 * flick);
    // The lanterns' reach on the ground: the hidden show inside it.
    this.entityGlow.add(p.x, ground + 0.06, p.z, LANTERN, ((SAGA.escort.lantern * 2) / 1000) * WORLD_PER_HEX, 0.1 * flick);
    if (this.fresh && walk && s.tick % 4 === 0) {
      this.vfx.burst(this.v.set(p.x - fx * 0.5 * scale, ground + 0.08, p.z - fz * 0.5 * scale), { n: 1, color: '#6d5a44', speed: 0.01, up: 0.015, size: 0.35, life: 12, smoke: true });
    }
    if (betOf(s) === 'barrel') this.leak(s, b, p, ground, yaw, scale, walk);
  }

  /** The cask's beer on the road (Brann's bet «El barril»): puddles left behind, and the holes it has taken. */
  private beer: BeerLayer | null = null;

  /**
   * The cask leaks as it takes blows: a drip from the tap at first, then from one stave, then from two more
   * (model-space points of `barrelCart`), every drop amber; puddles are left on the road behind it.
   */
  private leak(s: BattleState, b: Bot, p: THREE.Vector3, ground: number, yaw: number, scale: number, walk: boolean): void {
    if (!this.beer) {
      this.beer = new BeerLayer(this.opts.quality === 'low' ? 16 : 28);
      this.scene.add(this.beer.mesh);
    }
    this.beer.frame(this.now);
    const hurt = 1 - b.hp / b.maxHp;
    const holes = hurt < 0.04 ? 0 : hurt < 0.35 ? 1 : hurt < 0.65 ? 2 : 3;
    if (!this.fresh || !holes) return;
    const sn = Math.sin(yaw), cs = Math.cos(yaw);
    const low = this.opts.quality === 'low';
    for (let i = 0; i < holes; i++) {
      const h = BEER_LEAKS[i] as THREE.Vector3;
      const x = p.x + (h.x * cs + h.z * sn) * scale;
      const z = p.z + (-h.x * sn + h.z * cs) * scale;
      // A thin stream: a drop or two a tick, falling to the ground.
      if (!low || (s.tick + i) % 2 === 0) this.vfx.burst(this.v.set(x, p.y + h.y * scale, z), { n: 1, color: '#f0b445', speed: 0.004, up: 0.004, size: 0.09, life: 8, gravity: 0.012, spread: 0.02 });
    }
    if ((s.tick + b.id) % (walk ? 3 : 8) === 0) {
      const h = BEER_LEAKS[0] as THREE.Vector3;
      this.beer.add(p.x + (h.x * cs + h.z * sn) * scale, ground, p.z + (-h.x * sn + h.z * cs) * scale, (0.5 + 0.16 * holes) * (0.8 + ((s.tick * 7) % 5) * 0.08), this.now);
    }
  }

  /**
   * The learned skills' zones. The frost orb: a cold sphere of light rolling over the ground, frost smoking
   * off it, a bite of ice each pulse; when it bursts, the boom draws it. The guard totem: its standing stone,
   * a pale disc of ward around it, a ring each pulse (green motes when it mends). The hammer: a head of holy
   * light whirling round its paladin with a fading trail, and a flash each pulse.
   */
  private drawLearnedZone(s: BattleState, z: Zone, p: THREE.Vector3, use: (k: string) => ModelBatch): void {
    const rr = (z.radius / 1000) * WORLD_PER_HEX;
    const pulse = z.every > 0 && z.ticks % z.every === 0;
    if (z.kind === 'mine') {
      // A mine in the dirt: a dull disc with a light that blinks once armed (tar: darker and glossier).
      const armed = (z.warn ?? 0) <= 0;
      this.entityGlow.add(p.x, p.y, p.z, z.slowBp > 6000 ? TAR_DARK : MINE_DUST, 0.7, 0.5);
      if (armed && (s.tick + z.id) % 8 < 2) this.entityGlow.add(p.x, p.y + 0.15, p.z, RED, 0.35, 0.9);
      return;
    }
    if (z.kind === 'orb') {
      const k = 0.85 + 0.15 * Math.sin(s.tick * 0.9);
      this.entityGlow.add(p.x, p.y + 0.55, p.z, ORB_FROST, 1.3 * k, 0.9);
      this.entityGlow.add(p.x, p.y + 0.05, p.z, ORB_FROST, rr * 1.6, 0.22);
      this.flare(this.v.set(p.x, p.y + 0.55, p.z), '#e8f7ff', 0.9, 1);
      if (this.fresh) {
        this.vfx.burst(this.v.set(p.x, p.y + 0.4, p.z), { n: 2, color: '#cfe6f4', speed: 0.02, up: 0.02, size: 0.3, life: 12, smoke: true, spread: 0.3 });
        if (pulse) this.vfx.ring(p, rr * 0.3, rr, '#bfe8ff', 6, 0.7);
      }
      return;
    }
    if (z.kind === 'fortify') {
      // Fortify: a faint grey-steel dome over the structures it covers.
      this.entityGlow.add(p.x, p.y + 0.1, p.z, STEEL, rr * 1.6, 0.1);
      if (this.fresh && pulse) this.vfx.ring(p, rr * 0.92, rr, '#c4c8d2', 10, 0.35);
      return;
    }
    if (z.kind === 'rift') {
      // The rift: a dark swirl drawing motes inward, violet at its heart.
      this.entityGlow.add(p.x, p.y + 0.3, p.z, RIFT_DARK, rr * 1.2, 0.35);
      this.entityGlow.add(p.x, p.y + 0.6, p.z, RIFT_CORE, 1.2, 0.7);
      if (this.fresh) this.vfx.implode(this.v.set(p.x, p.y + 0.5, p.z), rr, { n: this.opts.quality === 'low' ? 3 : 8, color: '#9a86d0', size: 0.07, life: 10, swirl: 0.8, lift: 0.3 });
      return;
    }
    if (z.kind === 'caltrops') {
      this.entityGlow.add(p.x, p.y + 0.02, p.z, MINE_DUST, rr * 1.4, 0.35);
      if ((s.tick + z.id) % 9 === 0) this.entityGlow.add(p.x, p.y + 0.1, p.z, SNARE_STEEL, rr * 0.8, 0.3);
      return;
    }
    if (z.kind === 'flare') {
      // A flare hanging over the field: a hot red-white light, sparks falling, the ground lit under it.
      this.entityGlow.add(p.x, p.y + 4, p.z, FLARE_HOT, 1.4, 0.95);
      this.entityGlow.add(p.x, p.y + 0.05, p.z, FLARE_HOT, rr * 1.5, 0.18);
      this.flare(this.v.set(p.x, p.y + 4, p.z), '#ffd8c8', 1.4, 1);
      if (this.fresh && s.tick % 2 === 0) this.vfx.burst(this.v.set(p.x, p.y + 3.9, p.z), { n: 1, color: '#ffb070', speed: 0.02, up: -0.02, size: 0.06, life: 24, gravity: 0.004, spread: 0.3 });
      return;
    }
    if (z.kind === 'quake') {
      // Broken ground: dark earth, dust kicked up now and then.
      this.entityGlow.add(p.x, p.y + 0.05, p.z, QUAKE_EARTH, rr * 1.5, 0.3);
      if (this.fresh && s.tick % 4 === 0) {
        const a = Math.random() * TAU;
        const d = Math.random() * rr;
        this.vfx.burst(this.v.set(p.x + Math.cos(a) * d, p.y + 0.1, p.z + Math.sin(a) * d), { n: 1, color: '#5b524a', speed: 0.02, up: 0.03, size: 0.4, life: 14, smoke: true });
      }
      return;
    }
    if (z.kind === 'meteors') {
      // The rain of fire: a red-orange sky glow over the circle, embers drifting down.
      this.entityGlow.add(p.x, p.y + 2.5, p.z, METEOR_SKY, rr * 1.3, 0.25);
      this.entityGlow.add(p.x, p.y + 0.05, p.z, METEOR_SKY, rr * 1.5, 0.12);
      if (this.fresh && s.tick % 2 === 0) {
        const a = Math.random() * TAU;
        const d = Math.sqrt(Math.random()) * rr;
        this.vfx.burst(this.v.set(p.x + Math.cos(a) * d, p.y + 3, p.z + Math.sin(a) * d), { n: 1, color: '#ffb347', speed: 0.01, up: -0.06, size: 0.1, life: 30, gravity: 0.001 });
      }
      return;
    }
    if (z.kind === 'coil') {
      // The coil: a lilac spark crackling on a post, a faint ring of its reach.
      const k = 0.7 + 0.3 * Math.sin(s.tick * 1.3 + z.id);
      this.entityGlow.add(p.x, p.y + 0.9, p.z, COIL_BOLT, 0.9 * k, 0.8);
      this.entityGlow.add(p.x, p.y + 0.05, p.z, COIL_BOLT, rr * 1.2, 0.08);
      this.flare(this.v.set(p.x, p.y + 0.9, p.z), '#e9e3ff', 0.7, 1);
      if (this.fresh && s.tick % 3 === 0) this.vfx.burst(this.v.set(p.x, p.y + 0.9, p.z), { n: 2, color: '#ffffff', speed: 0.05, up: 0.02, size: 0.05, life: 4 });
      return;
    }
    if (z.kind === 'snare') {
      // A set snare: iron jaws in the dirt, a dull glint.
      this.entityGlow.add(p.x, p.y, p.z, MINE_DUST, 0.6, 0.45);
      if ((s.tick + z.id) % 10 < 2) this.entityGlow.add(p.x, p.y + 0.1, p.z, SNARE_STEEL, 0.3, 0.8);
      return;
    }
    if (z.kind === 'banner') {
      // The banner where it was planted, a red light on the ground around it; with rallying, green motes rise.
      this.compose(this.v.set(p.x, p.y - 0.05, p.z), 0, this.lib.scaleOf('banner'));
      use('banner').add(this.m, s.tick & 3);
      this.shadows.add(p.x, p.y - 0.05, p.z, 0.45);
      this.entityGlow.add(p.x, p.y + 0.1, p.z, BANNER_RED, rr * 1.4, 0.18);
      if (this.fresh && pulse) this.vfx.ring(p, rr * 0.2, rr, z.heal ? '#b3f59a' : '#ff8a6e', 8, 0.45);
      return;
    }
    if (z.kind === 'blizzard') {
      // A white-blue disc of frost, snow driving across it, a cold ring with every bite.
      this.entityGlow.add(p.x, p.y + 0.1, p.z, ORB_FROST, rr * 1.6, 0.3);
      this.entityGlow.add(p.x, p.y + 2.2, p.z, SNOW_CLOUD, rr * 1.3, 0.2);
      if (this.fresh) {
        const low = this.opts.quality === 'low';
        for (let i = 0; i < (low ? 2 : 5); i++) {
          const a = Math.random() * TAU;
          const d = Math.sqrt(Math.random()) * rr;
          this.vfx.burst(this.v.set(p.x + Math.cos(a) * d, p.y + 1.6 + Math.random(), p.z + Math.sin(a) * d), { n: 1, color: '#f2faff', speed: 0.06, up: -0.05, size: 0.09, life: 18, gravity: 0.002, spread: 0.1 });
        }
        if (s.tick % 3 === 0) {
          const a = Math.random() * TAU;
          const d = Math.random() * rr;
          this.vfx.burst(this.v.set(p.x + Math.cos(a) * d, p.y + 0.3, p.z + Math.sin(a) * d), { n: 1, color: '#cfe6f4', speed: 0.05, up: 0.005, size: 0.6, life: 16, smoke: true });
        }
        if (pulse) this.vfx.ring(p, rr * 0.3, rr, '#bfe8ff', 6, 0.35);
      }
      return;
    }
    if (z.kind === 'rampart') {
      // The rampart: a pale dome of light, pulsing slowly.
      const k = 0.8 + 0.2 * Math.sin(s.tick * 0.4);
      this.entityGlow.add(p.x, p.y + 0.1, p.z, HOLY_COLD, rr * 1.7, 0.12 * k);
      if (this.fresh && pulse) this.vfx.ring(p, rr * 0.9, rr, '#eef2ff', 10, 0.5);
      return;
    }
    if (z.kind === 'mortar') {
      // Where the shells will fall: a red target ring that tightens.
      if (this.fresh && s.tick % 4 === 0) this.vfx.ring(p, rr, rr * 0.3, '#ff4f3a', 4, 0.6);
      this.entityGlow.add(p.x, p.y + 0.1, p.z, RED, rr * 1.2, 0.18);
      return;
    }
    if (z.kind === 'plague') {
      // A sickly green cloud round the druid, flies glinting in it.
      this.entityGlow.add(p.x, p.y + 0.2, p.z, PLAGUE_GREEN, rr * 1.5, 0.25);
      if (this.fresh) {
        const low = this.opts.quality === 'low';
        if (s.tick % (low ? 3 : 1) === 0) {
          const a = Math.random() * TAU;
          const d = Math.sqrt(Math.random()) * rr * 0.9;
          this.vfx.burst(this.v.set(p.x + Math.cos(a) * d, p.y + 0.4, p.z + Math.sin(a) * d), { n: 1, color: z.slowBp > 0 ? '#7a8a4a' : '#9ab84a', speed: 0.01, up: 0.008, size: 0.7, life: 20, smoke: true });
        }
        if (!low && s.tick % 2 === 0) {
          const a = Math.random() * TAU;
          const d = Math.random() * rr * 0.8;
          this.vfx.burst(this.v.set(p.x + Math.cos(a) * d, p.y + 0.8, p.z + Math.sin(a) * d), { n: 2, color: '#1d1f14', speed: 0.05, up: 0.02, size: 0.05, life: 10, spread: 0.3 });
        }
      }
      return;
    }
    if (z.kind === 'totem') {
      this.compose(this.v.set(p.x, p.y - 0.05, p.z), 0, this.lib.scaleOf('totem'));
      use('totem').add(this.m, s.tick & 3);
      this.shadows.add(p.x, p.y - 0.05, p.z, 0.5);
      this.entityGlow.add(p.x, p.y + 0.1, p.z, HOLY_COLD, rr * 1.5, 0.2);
      if (this.fresh && pulse) {
        this.vfx.ring(p, rr * 0.2, rr, z.heal ? '#b3f59a' : '#eef2ff', 8, 0.5);
        if (z.heal) {
          const a = Math.random() * TAU;
          const d = Math.random() * rr * 0.8;
          this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * d, 0.1, Math.sin(a) * d)), { n: 3, color: '#b3f59a', speed: 0.01, up: 0.05, size: 0.12, life: 14, gravity: -0.002 });
        }
      }
      return;
    }
    // The hammer: its head goes round at the edge of its reach, the trail fading behind it.
    for (let i = 0; i < 4; i++) {
      const a = (s.tick + this.frac - i * 0.35) * 0.95;
      const x = p.x + Math.cos(a) * rr * 0.8;
      const zz = p.z + Math.sin(a) * rr * 0.8;
      this.entityGlow.add(x, p.y + 0.7, zz, HOLY_COLD, i === 0 ? 1.1 : 0.8 - i * 0.15, i === 0 ? 0.9 : 0.45 - i * 0.1);
    }
    this.entityGlow.add(p.x, p.y + 0.05, p.z, HOLY_COLD, rr * 1.4, 0.15);
    if (this.fresh && pulse) {
      const a = s.tick * 0.95;
      this.vfx.burst(this.v.set(p.x + Math.cos(a) * rr * 0.8, p.y + 0.6, p.z + Math.sin(a) * rr * 0.8), { n: 4, color: '#ffffff', speed: 0.06, up: 0.04, size: 0.1, life: 6, gravity: 0.006 });
    }
  }

  /**
   * The lime catapult's cloud (docs/SABERES.md §4.2): a pale low bank of quicklime dust on the ground where the sack
   * burst, thinning as it settles; inside it, those who shoot from afar can't. The shared glow layer and smoke puffs.
   */
  private drawLime(s: BattleState, z: Zone, p: THREE.Vector3): void {
    const rr = (z.radius / 1000) * WORLD_PER_HEX;
    const fade = Math.min(1, z.ticks / 12);
    // A low, dull glow only: the cloud itself is smoke (the glow layer adds light, and bright it read as a flash).
    this.entityGlow.add(p.x, p.y + 0.06, p.z, LIME_PALE, rr * 1.8, 0.07 * fade);
    if (!this.fresh) return;
    const low = this.opts.quality === 'low';
    // A low bank of bone-grey dust that hangs, then settles: big slow puffs near the ground, a few falling motes.
    for (let i = 0; i < (low ? 2 : 4); i++) {
      const a = Math.random() * TAU;
      const d = Math.sqrt(Math.random()) * rr * 0.9;
      this.vfx.burst(this.v.set(p.x + Math.cos(a) * d, p.y + 0.12 + Math.random() * 0.15, p.z + Math.sin(a) * d), { n: 1, color: i % 2 ? '#a9a59b' : '#bdb9ae', speed: 0.004, up: 0.001, size: 0.4 + Math.random() * 0.25, life: 16, smoke: true, gravity: 0.0004 });
    }
    if (!low && s.tick % 2 === 0) {
      const a = Math.random() * TAU;
      const d = Math.random() * rr * 0.8;
      this.vfx.burst(this.v.set(p.x + Math.cos(a) * d, p.y + 0.7, p.z + Math.sin(a) * d), { n: 2, color: '#7d7a72', speed: 0.01, up: 0, size: 0.05, life: 20, gravity: 0.002, spread: 0.3 });
    }
  }

  /** The gear grenade: the device spins on the ground inside a vortex that winds tighter until it bursts. */
  private drawGrenade(s: BattleState, z: Zone, p: THREE.Vector3, use: (k: string) => ModelBatch): void {
    const max = Math.max(this.grenadeMax.get(z.id) ?? 0, z.ticks, 1);
    this.grenadeMax.set(z.id, max);
    if (this.grenadeMax.size > 32) for (const id of [...this.grenadeMax.keys()].slice(0, 16)) this.grenadeMax.delete(id);
    const left = Math.max(0, z.ticks / max);
    const r = (z.radius / 1000) * WORLD_PER_HEX;
    const ground = p.y - 0.05;
    const late = z.ticks <= 6;
    const jit = late ? (s.tick & 1 ? 0.03 : -0.03) : 0;
    this.compose(this.v.set(p.x + jit, ground, p.z - jit), s.tick * (late ? 1.1 : 0.55), this.lib.scaleOf('grenade'));
    use('grenade').add(this.m, s.tick & 3);
    this.shadows.add(p.x, ground, p.z, 0.4);
    // A spiral of light on the ground, wound tighter and turning faster as the fuse burns down.
    const reach = r * (0.3 + 0.7 * left);
    const spin = s.tick * (0.3 + 0.5 * (1 - left));
    const v = this.vortexMesh(this.vortexUsed++);
    v.visible = true;
    v.position.set(p.x, ground + 0.04, p.z);
    v.scale.setScalar(reach);
    v.rotation.y = spin;
    (v.material as THREE.MeshBasicMaterial).opacity = Math.min(1, (max - z.ticks + 1) / 3) * (late ? (s.tick & 1 ? 1 : 0.7) : 0.85);
    this.entityGlow.add(p.x, ground + 0.22, p.z, late ? WHITE : CLOCK_CYAN, late ? 0.7 + (6 - z.ticks) * 0.12 : 0.55, late ? (s.tick & 1 ? 0.95 : 0.5) : 0.6);
    if (!this.fresh) return;
    if (s.tick % 3 === 0) this.vfx.ring(this.v.set(p.x, ground, p.z), reach, 0.12, '#4fdcff', 5, 0.5);
    this.vfx.implode(this.v.set(p.x, ground + 0.06, p.z), reach, { n: 3, color: '#bfefff', size: 0.09, life: 7, swirl: -0.9 });
    if (s.tick % 2 === 0) this.vfx.implode(this.v.set(p.x, ground + 0.1, p.z), reach * 0.9, { n: 1, color: '#9aa3ad', size: 0.18, life: 8, swirl: -0.6 });
  }

  private vortex: THREE.Mesh[] = [];
  private vortexUsed = 0;
  private vortexTex: THREE.Texture | null = null;
  private passCheck = 0;

  /** Pooled ground spiral for gear grenades (unit radius, additive cyan). */
  private vortexMesh(i: number): THREE.Mesh {
    let m = this.vortex[i];
    if (m) return m;
    if (!this.vortexTex) this.vortexTex = spiralTexture();
    m = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2),
      decalMat({ map: this.vortexTex, color: '#7fe6ff', transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false }),
    );
    m.renderOrder = 2;
    this.overlay.add(m);
    this.vortex.push(m);
    return m;
  }

  /** The artificer's blasts: overloads, grenades, the clockwork landing, sweeping, blowing up and winding down. */
  /**
   * An artificer overloads a clockwork turret: it bursts round itself without tearing apart, a shock ring
   * and sparks and a great gout of steam from its head, then sits venting while it rests.
   */
  private clockVent(kind: string, p: THREE.Vector3, r: number): void {
    const up = (h: number): THREE.Vector3 => p.clone().setY(p.y + h);
    const [c, c2] = kind === 'overloadFrost' ? ['#bfe8ff', '#7fc8ff'] : kind === 'overloadFire' ? ['#ffb070', '#ff5a1f'] : ['#d9fbff', '#4fdcff'];
    this.flare(up(0.9), '#ffffff', 1.6, 3);
    this.flare(up(0.8), c, 2.6, 8);
    this.vfx.ring(p, r * 0.15, r, c, 8, 1);
    this.vfx.ring(up(0.3), r * 0.1, r * 0.6, '#ffffff', 5, 0.6);
    this.vfx.burst(up(0.85), { n: 22, color: c2, speed: 0.15, up: 0.1, size: 0.11, life: 8 });
    this.vfx.burst(up(0.95), { n: 14, color: '#eef1f4', speed: 0.05, up: 0.09, size: 0.5, life: 18, smoke: true, spread: 0.15 });
    this.vfx.burst(up(0.4), { n: 8, color: '#d9dde2', speed: 0.09, up: 0.01, size: 0.35, life: 12, smoke: true, spread: 0.3 });
    if (kind === 'overloadFrost') this.vfx.burst(up(0.15), { n: 18, color: '#eaf8ff', speed: 0.09, up: 0.02, size: 0.16, life: 16, gravity: 0.002, spread: r * 0.5 });
    if (kind === 'overloadFire') this.vfx.burst(up(0.2), { n: 18, color: '#ff8a2a', speed: 0.08, up: 0.09, size: 0.24, life: 11, gravity: -0.002 });
    this.later.push({ n: 3, run: () => this.vfx.burst(up(1.0), { n: 8, color: '#e6e9ec', speed: 0.03, up: 0.07, size: 0.42, life: 16, smoke: true, spread: 0.1 }) });
    this.shake = Math.max(this.shake, 0.16);
  }

  private clockBoom(kind: string, p: THREE.Vector3, r: number, x: number, y: number): boolean {
    const up = (h: number): THREE.Vector3 => p.clone().setY(p.y + h);
    switch (kind) {
      case 'overload':
      case 'overloadFrost':
      case 'overloadFire': {
        // The turret tears itself apart: a white-hot flash, a shock ring, bolts and plates flung out.
        const [c, c2] = kind === 'overloadFrost' ? ['#bfe8ff', '#7fc8ff'] : kind === 'overloadFire' ? ['#ffb070', '#ff5a1f'] : ['#d9fbff', '#4fdcff'];
        this.flare(up(0.5), '#ffffff', 1.9, 4);
        this.flare(up(0.4), c, 3, 9);
        this.vfx.ring(p, r * 0.15, r, c, 8, 1);
        this.vfx.ring(up(0.25), r * 0.1, r * 0.65, '#ffffff', 5, 0.7);
        this.vfx.burst(up(0.4), { n: 28, color: c2, speed: 0.16, up: 0.1, size: 0.13, life: 9 });
        this.vfx.burst(up(0.4), { n: 16, color: '#c8d0da', speed: 0.13, up: 0.14, size: 0.09, life: 14, gravity: 0.014 });
        this.vfx.burst(up(0.2), { n: 10, color: kind === 'overloadFrost' ? '#dff2ff' : '#3a3d42', speed: 0.06, up: 0.05, size: 0.45, life: 14, smoke: true, spread: 0.4 });
        if (kind === 'overloadFrost') this.vfx.burst(up(0.15), { n: 18, color: '#eaf8ff', speed: 0.09, up: 0.02, size: 0.16, life: 16, gravity: 0.002, spread: r * 0.5 });
        if (kind === 'overloadFire') this.vfx.burst(up(0.2), { n: 18, color: '#ff8a2a', speed: 0.08, up: 0.09, size: 0.24, life: 11, gravity: -0.002 });
        this.shake = Math.max(this.shake, 0.22);
        return true;
      }
      case 'grenade':
      case 'grenadeShrapnel': {
        // The device bursts: a cyan flash and ring, gear shards and sparks (shrapnel: many more, and faster).
        const shr = kind === 'grenadeShrapnel';
        this.grenadeMax.delete(this.grenadeAt(x, y));
        this.flare(up(0.35), '#ffffff', 1.8, 4);
        this.flare(up(0.3), '#4fdcff', 3, 8);
        this.vfx.ring(p, r * 0.1, r, '#4fdcff', 7, 1);
        this.vfx.ring(up(0.2), r * 0.1, r * 0.7, '#eceef2', 5, 0.7);
        this.vfx.burst(up(0.3), { n: shr ? 40 : 18, color: '#c8d0da', speed: shr ? 0.2 : 0.13, up: 0.1, size: shr ? 0.1 : 0.12, life: shr ? 12 : 10, gravity: 0.012 });
        this.vfx.burst(up(0.3), { n: 14, color: '#ffe2c8', speed: 0.14, up: 0.08, size: 0.07, life: 6 });
        this.vfx.burst(up(0.15), { n: 8, color: '#3a3d42', speed: 0.05, up: 0.04, size: 0.4, life: 12, smoke: true, spread: 0.3 });
        if (shr) this.vfx.ring(up(0.1), r * 0.3, r * 1.15, '#a4512f', 6, 0.7);
        this.shake = Math.max(this.shake, shr ? 0.2 : 0.14);
        return true;
      }
      case 'automaton': {
        // Landing: a ring of dust and sparks where it drops.
        this.later.push({
          n: 2,
          run: () => {
            this.vfx.ring(p, 0.2, r * 1.4, '#cfc6b8', 9, 0.8);
            this.vfx.burst(up(0.1), { n: 18, color: '#8a8378', speed: 0.1, up: 0.04, size: 0.45, life: 14, smoke: true, spread: r * 0.4 });
            this.vfx.burst(up(0.2), { n: 14, color: '#ffd6b0', speed: 0.12, up: 0.08, size: 0.07, life: 7 });
            this.flare(up(0.9), '#4fdcff', 1.8, 8);
            this.shake = Math.max(this.shake, 0.18);
          },
        });
        this.vfx.burst(up(1.6), { n: 10, color: '#bfefff', speed: 0.03, up: -0.05, size: 0.1, life: 6, gravity: 0 });
        return true;
      }
      case 'sweep': {
        // The hammer goes round: a shock along the ground and a ring of dust at its edge.
        this.vfx.ring(p, r * 0.35, r, '#dfe7ef', 6, 0.85);
        this.vfx.ring(up(0.3), r * 0.5, r * 1.05, '#4fdcff', 4, 0.45);
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * TAU;
          this.vfx.burst(this.v.set(p.x + Math.cos(a) * r * 0.85, p.y + 0.1, p.z + Math.sin(a) * r * 0.85), { n: 1, color: '#7d766c', speed: 0.03, up: 0.03, size: 0.35, life: 9, smoke: true });
        }
        this.vfx.burst(up(0.5), { n: 6, color: '#ffe8d0', speed: 0.12, up: 0.05, size: 0.06, life: 4 });
        return true;
      }
      case 'spiderBlast': {
        this.flare(up(0.3), '#ffffff', 1.3, 4);
        this.flare(up(0.25), '#ff8a6e', 2.2, 7);
        this.vfx.ring(p, r * 0.15, r, '#ff8a6e', 6, 0.9);
        this.vfx.burst(up(0.25), { n: 18, color: '#ff9a4a', speed: 0.12, up: 0.08, size: 0.17, life: 7 });
        this.vfx.burst(up(0.2), { n: 8, color: '#c8d0da', speed: 0.12, up: 0.12, size: 0.07, life: 11, gravity: 0.014 });
        this.vfx.burst(up(0.25), { n: 6, color: '#4fdcff', speed: 0.06, up: 0.05, size: 0.12, life: 6 });
        this.vfx.burst(up(0.15), { n: 7, color: '#2c2a2a', speed: 0.05, up: 0.05, size: 0.42, life: 12, smoke: true });
        this.shake = Math.max(this.shake, 0.1);
        return true;
      }
      case 'botBroken': {
        // Smashed: sparks, plates falling, black smoke and the core guttering out.
        const kd = this.botKinds.get(`${x},${y}`);
        const k = kd === 'colossus' ? 1.8 : kd === 'spider' ? 0.5 : 1;
        this.vfx.burst(up(0.6 * k), { n: Math.round(16 * k), color: '#ffd6b0', speed: 0.1, up: 0.1, size: 0.07, life: 8, gravity: 0.01 });
        this.vfx.burst(up(0.7 * k), { n: Math.round(12 * k), color: '#8e97a2', speed: 0.08, up: 0.08, size: 0.14, life: 14, gravity: 0.016, spread: 0.3 * k });
        this.vfx.burst(up(0.4 * k), { n: Math.round(12 * k), color: '#24262a', speed: 0.04, up: 0.05, size: 0.5 * k, life: 18, smoke: true, spread: 0.4 * k });
        this.flare(up(0.8 * k), '#4fdcff', 1.2 * k, 6);
        if (kd !== 'spider') this.vfx.ring(p, 0.2, 1.2 * k, '#8a8378', 8, 0.6);
        this.shake = Math.max(this.shake, 0.12 * k);
        return true;
      }
      case 'botGone': {
        // Wound down: a hiss of steam and the core fading.
        const kd = this.botKinds.get(`${x},${y}`);
        const k = kd === 'colossus' ? 1.7 : kd === 'spider' ? 0.5 : 1;
        this.vfx.burst(up(0.5 * k), { n: Math.round(14 * k), color: '#e6ecf2', speed: 0.035, up: 0.05, size: 0.42 * k, life: 18, smoke: true, spread: 0.5 * k, gravity: -0.003 });
        this.vfx.burst(up(0.8 * k), { n: 8, color: '#9feaff', speed: 0.02, up: 0.03, size: 0.09, life: 12, gravity: -0.002, spread: 0.4 * k });
        this.flare(up(0.8 * k), '#9feaff', 1 * k, 8);
        return true;
      }
      default:
        return false;
    }
  }


  /** The saga's own blasts: the dragon landing, the frost mantle, the caravan home. False if `kind` is another's. */
  private sagaBoom(kind: string, p: THREE.Vector3, r: number): boolean {
    const low = this.opts.quality === 'low';
    switch (kind) {
      case 'dragonLand':
        this.dragonLands(p, r);
        return true;
      case 'frostMantle': {
        // A ring of ice bursts up round the mage: shards thrown out low, frost smoke rolling over the ground.
        this.vfx.ring(p, 0.2, r, '#bfe8ff', 10, 1);
        this.vfx.ring(p.clone().setY(p.y + 0.15), r * 0.5, r * 1.05, '#ffffff', 6, 0.7);
        for (let i = 0; i < 10; i++) {
          const a = (i / 10) * TAU;
          const q = p.clone().add(this.v.set(Math.cos(a) * r * 0.85, 0.05, Math.sin(a) * r * 0.85));
          this.vfx.shaft(q, q.clone().setY(q.y + 0.5 + (i % 3) * 0.15), i % 2 ? '#dff4ff' : '#9fdcff', 10, 0.14);
        }
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 10 : 24, color: '#eaf8ff', speed: 0.12, up: 0.05, size: 0.1, life: 10, gravity: 0.006 });
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 5 : 12, color: '#cfe6f2', speed: 0.06, up: 0.01, size: 0.55, life: 20, smoke: true, spread: r * 0.6 });
        this.flare(p.clone().setY(p.y + 0.6), '#9fdcff', 2.4, 8);
        return true;
      }
      case 'arrived': {
        // The caravan is home: its lanterns flare and warm motes rise round it.
        this.vfx.ring(p, 0.3, 2.6, '#ffd9a8', 12, 0.8);
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 10 : 24, color: '#ffe2bc', speed: 0.03, up: 0.05, size: 0.1, life: 24, gravity: -0.002, spread: 1 });
        this.flare(p.clone().setY(p.y + 0.9), '#ffb877', 3, 14);
        return true;
      }
      default:
        return false;
    }
  }

  /** The garrison key: a burning beam joins two turrets, crackling with sparks. */
  private turretLink(a: THREE.Vector3, b: THREE.Vector3): void {
    this.vfx.beam(a, b, '#ff4f3a', 6, 0.09, 0.08);
    this.vfx.beam(a, b, '#ffd0a0', 5, 0.03, 0.02);
    const m = a.clone().lerp(b, 0.25 + Math.random() * 0.5);
    this.vfx.burst(m, { n: this.opts.quality === 'low' ? 2 : 4, color: '#ff8a6e', speed: 0.04, up: 0.03, size: 0.08, life: 6, gravity: 0.006 });
    for (const q of [a, b]) this.flare(q.clone(), '#ff6a3a', 0.8, 6);
  }

  /** The watchtower bow: an arrow that goes through the whole line, a red streak and a wake of sparks. */
  private pierceShot(a: THREE.Vector3, b: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    this.vfx.beam(a, b, '#ff8a6e', 4, 0.06, 0);
    this.vfx.beam(a, b, '#ffffff', 2, 0.02, 0);
    for (let i = 1; i <= 3; i++) {
      const q = a.clone().lerp(b, i / 3);
      this.later.push({ n: i - 1, run: () => this.vfx.burst(q, { n: low ? 2 : 5, color: i === 3 ? '#ffffff' : '#ff8a6e', speed: 0.04, up: 0.02, size: 0.09, life: 6 }) });
    }
    this.flare(a.clone(), '#ff8a6e', 1, 4);
  }

  /** The grenade zone (by id) sitting at a sim point, or -1. */
  private grenadeAt(x: number, y: number): number {
    return this.lastZones.find((z) => z.kind === 'grenade' && z.x === x && z.y === y)?.id ?? -1;
  }

  private lastZones: Zone[] = [];

  private ghostRing(h: Hero, p: THREE.Vector3): void {
    let ring = this.ghostRings.get(h.pid);
    if (!ring) {
      ring = new THREE.Mesh(
        new THREE.RingGeometry(0.36, 0.44, 32).rotateX(-Math.PI / 2),
        decalMat({ color: this.opts.colors?.[h.pid] ?? '#7fe3d6', transparent: true, opacity: 0.8, depthWrite: false, blending: THREE.AdditiveBlending }),
      );
      // The miniature's rune circle bobs up and down through this height: draw the ring over it.
      (ring.material as THREE.MeshBasicMaterial).depthTest = false;
      ring.renderOrder = 2;
      this.overlay.add(ring);
      this.ghostRings.set(h.pid, ring);
    }
    ring.visible = h.downTicks === 0;
    ring.position.set(p.x, p.y + 0.03, p.z);
    ring.rotation.y = (h.id * 0.7 + this.lastTick * 0.08) % 6.28;
  }

  /**
   * A crowd of nightmares (CULL_FROM and more) is drawn only where the camera can see it: the last frame's view,
   * widened by how far the camera moved then (the camera moves after the drawing). Their clips keep their time
   * either way, and bosses and the Unsleeping are always drawn.
   */
  private cullCam: THREE.Camera | null = null;
  private readonly cullAt = new THREE.Vector3();
  private readonly camWas = new THREE.Vector3();
  private camSeen = false;
  private camMove = Infinity;
  private cullSkip = 0;
  private readonly crowdView = new THREE.Frustum();
  private readonly frustumM = new THREE.Matrix4();
  private readonly cullSphere = new THREE.Sphere();

  /** Drawing height in pixels (the screen sets it on resizing): with it, a crowd seen small draws its far models. */
  pixels = 0;
  /** The nightmares draw their far versions (engine/lod.ts): the camera shows them small. Set each frame. */
  private far = false;
  private readonly farDir = new THREE.Vector3();

  /** Whether the ground where `camera` looks is shown under FAR_BELOW_PX pixels per world unit. */
  private farFor(camera: THREE.Camera): boolean {
    const cam = camera as THREE.PerspectiveCamera;
    if (!LOD.on || !this.pixels || !cam.isPerspectiveCamera) return false;
    const dir = camera.getWorldDirection(this.farDir);
    const at = this.cullAt;
    // Distance along the view's centre to the ground (y = 0); looking at the sky, as far as the camera is high.
    const d = dir.y < -0.05 ? at.y / -dir.y : at.y;
    return this.pixels / (2 * d * Math.tan((cam.fov * Math.PI) / 360)) < FAR_BELOW_PX;
  }

  /** The view to cull a crowd against this drawing, or null to draw everything. */
  private cullView(s: BattleState): THREE.Frustum | null {
    if (this.cullSkip > 0) {
      this.cullSkip--;
      return null;
    }
    const cam = this.cullCam;
    if (!CROWD.cull || !cam || s.enemies.length < CULL_FROM || !(this.camMove < CULL_MOVE)) return null;
    this.frustumM.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    this.crowdView.setFromProjectionMatrix(this.frustumM);
    this.cullSphere.radius = CULL_MARGIN + this.camMove * 2;
    return this.crowdView;
  }

  private drawEnemies(s: BattleState, use: (k: string, far?: boolean) => ModelBatch): void {
    // The stunned's stars: one set parked under the ground until the compile ahead is done, so the first stun of a
    // fight (a cut channel, a shield bash) doesn't compile them on the spot.
    if (!this.linkedAhead) this.skillFx.dazed(-1, PARKED, 0);
    // Kept out of sight under the ground (and drawn) until the compile ahead has linked its shaders; then hidden until he comes.
    if (this.unFx && this.linkedAhead && !s.enemies.some((e) => e.un && e.hp > 0)) this.unFx.hide();
    const view = this.cullView(s);
    for (const e of s.enemies) {
      if (e.hp <= 0) continue;
      if (e.kind === 'dragon') {
        this.drawDragon(s, e, use);
        continue;
      }
      const { x, y } = this.track.enemy.at(e.id, e.x, e.y, this.alpha, this.fresh);
      const ground = this.heightAt(x, y);
      const p = simToWorld(x, y, new THREE.Vector3(), ground);
      // Roots (druid) hold a flyer on the ground while they last.
      // (The cable's gargoyles sit higher: on the crane's arm, the car's cable and roof, the drum; engine/cable.ts.)
      // (The cleric's chapter 3: the ash watcher high in the storm until it dives, engine/nieve.ts.)
      const fly = (e.grounded ?? 0) > 0 ? 0 : (FLY_HEIGHT[e.kind] ?? 0) + (this.cable ? this.cable.lift(s, e.id) : 0) + (this.nieve ? this.nieve.aloft(s, e.id) : 0);
      if ((e.grounded ?? 0) > 0 && (s.tick + e.id) % 6 === 0) this.vfx.ring(p, 0.2, 0.7, '#6fa05a', 5, 0.8);
      // An ash mammoth's tread: every heavy footfall raises a ring of ash and cinders.
      if (e.kind === 'mammoth' && this.fresh && e.anim === 'walk' && (s.tick + e.id) % 6 === 0) this.mammothStep(p);
      // A troll knitting back together: bog-green motes rise off its wounds (none while it burns).
      if (e.kind === 'troll' && e.hp < e.maxHp && e.dotTicks <= 0 && (s.tick + e.id) % 4 === 0) {
        this.vfx.burst(p.clone().setY(p.y + 0.7), { n: 2, color: '#7fd08a', speed: 0.01, up: 0.03, size: 0.09, life: 12, gravity: -0.002, spread: 0.3 });
      }
      // Under an ink oracle's bubble: a shell of cold cyan over it, thinning as it soaks.
      if ((e.ward ?? 0) > 0) {
        const w = Math.min(1, (e.ward ?? 0) / (NIGHTMARES.oracle.wards?.hp ?? 50));
        this.holy.ward(p.clone().setY(p.y + (FLY_HEIGHT[e.kind] ?? 0)), 1.1, 0.12 + 0.2 * w);
        this.entityGlow.add(p.x, p.y + 0.6 + (FLY_HEIGHT[e.kind] ?? 0), p.z, WARD, 0.9, 0.25 + 0.2 * w);
      }
      // The sleeping giant breathes slow dust from its shell; a moth sheds glowing dream-dust as it flies.
      if (e.kind === 'moth' && (s.tick + e.id) % 6 === 0) this.vfx.burst(p.clone().setY(p.y + fly + 0.1), { n: 1, color: '#bcd8ff', speed: 0.01, up: -0.008, size: 0.06, life: 14, gravity: 0.001, spread: 0.2 });
      // A bannerman's banner: a slow ink ring at its feet marks how far it drives the pack.
      // Now and then it plants the banner and rallies the pack.
      if (e.kind === 'bannerman' && (s.tick + e.id) % 96 === 0 && e.anim === 'walk') this.force(e.id, 'rally');
      if (e.kind === 'bannerman' && (s.tick + e.id) % 18 === 0) this.vfx.ring(p.clone().setY(p.y + 0.05), 0.4, ((NIGHTMARES.bannerman.banner?.radius ?? 2600) / 1000) * WORLD_PER_HEX, '#8a5cff', 14, 0.35);
      // Animated flyers bob in their own clips.
      if (fly > 0) p.y = Math.max(p.y, 0) + fly + (!ANIM.on && ((s.tick + e.id) % 8) < 4 ? 0.04 : 0);
      this.positions.set(e.id, p.clone());
      // Las vías 1: the counterweight is the mine layer's stone block hanging in its frame (its life bar here).
      if (this.mine && e.kind === 'boneWall' && e.id === s.rails?.weight) {
        const top = this.mine.weightAt(this.v);
        this.positions.set(e.id, top.clone().setY(top.y - 0.45));
        if (this.flashes.has(e.id)) this.entityGlow.add(top.x, top.y - 0.4, top.z, HOLY_COLD, 1.1, 0.5);
        this.bars.add(top.setY(top.y + 0.3), e.hp / e.maxHp, RED, 1.1);
        continue;
      }
      // Tormo in his fight (the artificer's chapter 5): drawn whole by his layer (engine/tormo.ts), his plates lit there.
      if (this.tormoL?.owns(e.id)) {
        this.shadows.add(p.x, ground, p.z, 3.0);
        continue;
      }
      // The Roller (the ranger's chapter 1): drawn by its layer (engine/aster.ts), walking or curled; its bar here.
      if (this.asterL?.owns(e.id)) {
        this.shadows.add(p.x, ground, p.z, 1.6);
        // (The ranger's chapter 2: the shriekers go without bars, their mother with a wide one.)
        const bw = this.asterL.barOf(e.id);
        if (bw) this.bars.add(this.v.set(p.x, ground + (bw > 1.4 ? 3.6 : 1.9), p.z), e.hp / e.maxHp, RED, bw);
        continue;
      }
      // The Prestacaras wears a friend's own miniature (or, bare, what is under it).
      if (e.mask) {
        this.drawFacetaker(s, e, p, ground, use);
        continue;
      }
      if (e.phase === 1 && NIGHTMARES[e.kind as keyof typeof NIGHTMARES]?.burrows) {
        this.drawBurrow(s, e, p, use);
        // Under a watchtower's eye: a pale mark shows where it digs (and towers can hit it).
        if (spotted(s, e.x, e.y)) {
          this.entityGlow.add(p.x, p.y + 0.35, p.z, SPOTTED, 0.9, 0.45 + ((s.tick + e.id) % 6 < 3 ? 0.1 : 0));
          if ((s.tick + e.id) % 12 === 0) this.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 0.6, '#dfe8ff', 8, 0.6);
        }
        // Going down: the dig plays out before it's gone.
        const dk = this.rk(`enemy:${e.kind}:${e.tier}:${e.elite ? 1 : 0}${this.theme?.dressEnemy ? `:${this.theme.id}` : ''}`);
        const db = use(dk, this.far);
        const dig = db.clip('burrow');
        const t = this.sinceTag(e.id, 'burrow');
        if (dig && t < dig.dur) {
          this.compose(p, this.faceTo(e.id, facingToRotY(e.facing)), this.lib.scaleOf(dk));
          db.add(this.m, (s.tick + e.id) & 3, undefined, 1, this.animator.play(e.id, dig, t, this.now));
        }
        continue;
      }
      // A holiday dresses its nightmares: a baked variant of their own (the season's own creature needs none).
      const dressed = this.theme?.dressEnemy && e.kind !== 'pumpkin' ? `:${this.theme.id}` : '';
      // The crypt in embers: the Warden comes molten (a variant of its own).
      const molten = e.kind === 'warden' && this.crypt !== null && this.ascuas !== null && !this.deep;
      // The obsidian colossus shining (blows bounce): its mirrored variant.
      const shine = e.kind === 'colossus' && (e.shineUntil ?? 0) > s.tick;
      // A beetle whose shell a heavy blow split: its open variant.
      const open = e.kind === 'beetle' && e.cracked;
      // A giant woken at half health: its open-eyed variant.
      const woke = e.kind === 'giant' && e.phase === 1;
      // A shell serpent under the ground: only the mound it pushes up shows.
      const under = e.kind === 'shellSerpent' && e.phase !== 2;
      // A lidless dreamer with its eyes open.
      const eyes = e.kind === 'lidlessDreamer' && (e.openUntil ?? 0) > s.tick;
      // Kaela in her last phase: her bear.
      const bearForm = e.kind === 'kaela' && e.phase >= 2;
      // The Chasm's marrow worm under the ground (docs/SIMA.md): only its mound shows, along the crack.
      const dug = e.kind === 'marrowWorm' && e.phase === 7;
      // El pozo 2: the Drinker swollen with the water it drank.
      const swollen = e.kind === 'drinker' && e.phase === 2;
      // La Orilla 3: the Maw still and dim while its light is broken.
      const blind = e.kind === 'maw' && !!s.maw?.dark;
      // La Orilla 5: the Drowned Dragon steaming, its heart gone to the forge.
      const steaming = e.kind === 'sunken' && !!s.rules?.sunken?.boss?.steam;
      // The warrior's path, chapter 5: the Ventisquero (a giant in the sim) and its cubs (goblins), their own look.
      // (The cleric's chapter 1: its limpets are draggers in the sim, models/camino.ts.)
      const skin = s.senda ? (passSkin(s, e.id) ?? ermitaSkin(s, e) ?? (s.rules?.senda?.litter && e.kind === 'dragger' ? 'lapa' : null)) : null;
      const key = this.rk(`enemy:${skin ?? (molten ? 'wardenEmber' : shine ? 'colossusShine' : open ? 'beetleCracked' : woke ? 'giantAwake' : under ? 'shellSerpentBurrow' : eyes ? 'lidlessDreamerAwake' : bearForm ? 'kaelaBear' : dug ? 'marrowWormBurrowed' : swollen ? 'drinkerFull' : blind ? 'mawDark' : steaming ? 'sunkenSteam' : e.kind)}:${e.tier}:${e.elite ? 1 : 0}${dressed}`);
      // Bosses, the Warden and the Unsleeping keep their full model however far: the eye goes to them.
      // The Chasm's guardians (docs/SIMA.md) are bosses too: full model, mark, big shadow and bar, never culled.
      const guard = SIMA_GUARDIANS.has(e.kind);
      const b = use(key, this.far && !e.elite && !e.un && e.kind !== 'warden' && e.kind !== 'magmaLord' && e.kind !== 'colossus' && e.kind !== 'drinker' && e.kind !== 'maw' && e.kind !== 'lure' && e.kind !== 'sunken' && !guard);
      const anim = this.enemyAct(s, b, e);
      // Elites (warlords) are bosses of their wave: far bigger, swaggering, with an aura and a skull.
      const lord = e.kind === 'magmaLord' || e.kind === 'colossus' || skin === 'ventisquero' || skin === 'fledBoss' || skin === 'picotera';
      const boss = e.elite && e.kind !== 'warden' && !lord;
      const big = e.kind === 'warden' ? 2.6 : lord || guard ? 2.4 : e.kind === 'witch' || e.kind === 'shell' ? 1.8 : e.kind === 'mammoth' ? 1.6 : e.kind === 'uro' ? 1.7 : e.kind === 'drinker' ? 2.2 : e.kind === 'maw' ? 2.8 : e.kind === 'sunken' ? 3.4 : e.kind === 'boneWall' ? 1.1 : boss ? 1.5 : 0.58;
      const revealed = isRevealed(s, e);
      // The crystal echo's halves (a bolt split it): the same miniature, smaller.
      const half = e.split && e.kind === 'shardling' ? 0.72 : 1;
      // The Carcoma shrinks with each root cut, and down to a quarter of her life it keeps on shrinking.
      const shrink = skin === 'carcoma' ? carcomaShrink(s, e) : 1;
      const scale = this.lib.scaleOf(key) * (anim ? 1 : enemyPulse(e)) * (boss ? 1.75 : 1) * half * shrink;
      let lurch = 0;
      let yaw: number;
      // Kneeling at the clan's call, Ragna turns to where it comes from: the camp.
      if (anim) yaw = this.faceTo(e.id, facingToRotY(s.call?.id === e.id ? (Math.atan2(-e.y, -e.x) / (Math.PI * 2)) * 1024 : e.facing));
      else {
        // Lean into the attack: a forward lurch on attack frames (a big one for bosses), and a stomp when they walk.
        if (e.anim === 'attack' && e.animT < 3) lurch = boss ? 0.32 : 0.08;
        if (boss && e.anim === 'walk' && (s.tick + e.id) % 4 < 2) p.y += 0.09;
        yaw = facingToRotY(e.facing) + (boss && e.anim === 'walk' ? ((s.tick + e.id) % 4 < 2 ? 0.14 : -0.14) : 0);
      }
      // Las vías 1: a barricade lies across the rail.
      if (this.mine && e.kind === 'boneWall') yaw = this.mine.wallYaw(e.id, p.x, p.z);
      const lp = this.lurchAt.set(p.x + Math.sin(yaw) * lurch, p.y, p.z + Math.cos(yaw) * lurch);
      if (anim) {
        const d = this.drawnAt.get(e.id);
        if (d && d.key === key) {
          d.p.copy(lp);
          d.yaw = yaw;
          d.scale = scale;
        } else this.drawnAt.set(e.id, { key, p: lp.clone(), yaw, scale, tint: undefined });
      }
      // Out of the camera's sight in a crowd: where it is and its clip's time are kept, nothing more is drawn.
      if (view && !boss && !lord && !guard && !e.un && e.kind !== 'warden' && e.kind !== 'sunken') {
        this.cullSphere.center.copy(p);
        if (!view.intersectsSphere(this.cullSphere)) continue;
      }
      // (The monastery's spectres, unseen, cast no shadow either: only the salt shows them.)
      if (revealed || (!this.monastery && !this.seventh && !this.otono && !this.copa)) this.shadows.add(p.x, ground, p.z, fly > 0 ? big * 0.7 : big);
      if ((boss || lord || guard) && revealed) this.bossMark(e, p, s.tick, this.lib.heightOf(key) * scale);
      if (e.kind === 'colossus') this.colossusFx(s, e, p, yaw, this.lib.heightOf(key) * scale, shine);
      else if ((lord && skin !== 'ventisquero' && skin !== 'fledBoss') || molten) this.lordFx(s, e, p, yaw, this.lib.heightOf(key) * scale);
      else if (e.un) this.unsleepingFx(s, e, p, yaw, this.lib.heightOf(key) * scale);
      this.compose(lp, yaw, scale);
      const acid = (e.acidTicks ?? 0) > 0;
      const tint = this.flashes.has(e.id) && !shine ? FLASH : !revealed ? STALKER_HIDDEN : e.stun > 0 ? STUNNED : acid ? ACID_TINT : undefined;
      // An Unsleeping fading into smoke thins out as he goes.
      const fade = e.un && e.un.flee > 0 ? Math.max(0.15, e.un.flee / UNSLEEPING.fleeTicks) : 1;
      // (In the salt flats' fog what isn't on salt is not there at all: its prints on the dykes are what shows.)
      // (Nor the monastery's spectres out of the salt: only their prints in it.)
      // (Nor La Que Huyó and her squires unseen: their ripples and prints are drawn by the place.)
      b.add(this.m, (s.tick + e.id) & 3, tint, revealed ? fade : this.salt || this.monastery || this.seventh || this.otono || this.copa ? 0 : 0.15, anim);
      // (A barricade on the rail shows how much it has left: it is the thing to break.)
      if (revealed && e.hp < e.maxHp && (e.kind !== 'boneWall' || this.mine) && e.kind !== 'warden') {
        const big = e.kind === 'witch' || e.kind === 'shell' || e.kind === 'uro' || e.kind === 'sunken' || e.kind === 'maw' || lord || guard || !!e.un;
        this.bars.add(this.v.copy(p).setY(p.y + this.lib.heightOf(key) * scale + (boss ? 0.55 : 0.12)), e.hp / e.maxHp, RED, big ? 1.4 : boss ? 1.1 : 0.45);
      }
      // An Unsleeping's rage under his health: when it fills, the Night of the Gate comes.
      if (revealed && e.un) this.bars.add(this.v.copy(p).setY(p.y + this.lib.heightOf(key) * scale + 0.0), e.un.cast > 0 ? 1 : e.un.rage / UNSLEEPING.rageMax, RAGE, 1.4);
      if (e.stun > 0) {
        // Stars round the head of the stunned; a net over whoever the ranger caught.
        const top = this.lib.heightOf(key) * scale;
        if (e.drowse) {
          // Asleep (the drowsing tower): no stars, a slow string of pale motes rising from its head, the shared glow.
          for (let i = 0; i < 3; i++) {
            const k = (this.now * 0.6 + i / 3 + e.id * 0.137) % 1;
            this.entityGlow.add(p.x + k * 0.25, p.y + top + 0.1 + k * 0.45, p.z, DROWSE_PALE, 0.18 + k * 0.16, 0.75 * (1 - k));
          }
          this.entityGlow.add(p.x, p.y + top * 0.6, p.z, DROWSE_PALE, 0.9, 0.18);
        } else if (!s.call) this.skillFx.dazed(e.id, p, top);
        if (this.netHeld.has(e.id)) {
          this.skillFx.netted(e.id, p, Math.max(0.6, top * 0.85));
          this.entityGlow.add(p.x, p.y + 0.1, p.z, NET_GLOW, 1, 0.35);
        }
      } else this.netHeld.delete(e.id);
      if (e.slowBp > 0) this.entityGlow.add(p.x, p.y + 0.15, p.z, new THREE.Color('#9fdcff'), 0.6, 0.35);
      // Chilled (lightning will shatter it): a rim of frost over it.
      if ((e.chill ?? 0) > s.tick) this.entityGlow.add(p.x, p.y + 0.55, p.z, ORB_FROST, 0.9, 0.3);
      // Blinded by a paladin's light: a white haze over its eyes.
      if ((e.blind ?? 0) > 0) this.entityGlow.add(p.x, p.y + 0.8, p.z, HOLY_COLD, 0.6, 0.45);
      // Bound by shadow chains; eaten by acid; in a duel.
      if ((e.linkTicks ?? 0) > 0) this.entityGlow.add(p.x, p.y + 0.6, p.z, SHADOW_LINK, 0.8, 0.4);
      if ((e.acidTicks ?? 0) > 0) this.entityGlow.add(p.x, p.y + 0.3, p.z, ACID_GREEN, 0.7, 0.3);
      if ((e.duelTicks ?? 0) > 0) this.entityGlow.add(p.x, p.y + 0.05, p.z, ROAR_RED, 1.2, 0.45);
      // A paladin's seal: a ring of cold light turning under it.
      if ((e.seal ?? 0) > 0) {
        this.entityGlow.add(p.x, p.y + 0.05, p.z, HOLY_COLD, 1.1, 0.4);
        if (this.fresh && (s.tick + e.id) % 6 === 0) this.vfx.ring(p.clone().setY(p.y + 0.05), 0.5, 0.7, '#eef2ff', 5, 0.6);
      }
      if (e.dotTicks > 0 && e.wasps) {
        // A druid's wasps: a dark cloud of specks buzzing round it, a sickly green under it.
        this.entityGlow.add(p.x, p.y + 0.2, p.z, WASP_GREEN, 0.7, 0.25);
        if (this.fresh) {
          for (let i = 0; i < 3; i++) {
            const a = (s.tick + this.frac) * 1.7 + i * 2.1 + e.id;
            this.vfx.burst(this.v.set(p.x + Math.cos(a) * 0.45, p.y + 0.6 + Math.sin(a * 1.3) * 0.25, p.z + Math.sin(a) * 0.45), { n: 1, color: i ? '#2a2618' : '#c9b03a', speed: 0.004, up: 0, size: 0.07, life: 3 });
          }
        }
      } else if (e.dotTicks > 0 && e.dotType === 'poison') {
        // A ranger's venom: a sickly green sheen and green drips running off it while it lasts.
        this.entityGlow.add(p.x, p.y + 0.35, p.z, VENOM, 0.6, 0.26);
        if (this.fresh && (s.tick + e.id) % 3 === 0) this.vfx.burst(p.clone().setY(p.y + 0.4 + ((e.id * 5 + s.tick) % 3) * 0.08), { n: 1, color: '#8fdc4a', speed: 0.01, up: 0.004, size: 0.07, life: 9, gravity: 0.007 });
        if (this.fresh && (s.tick + e.id) % 8 === 0) this.vfx.burst(p.clone().setY(p.y + 0.5), { n: 1, color: '#5f8a4a', speed: 0.004, up: 0.01, size: 0.28, life: 14, smoke: true });
      } else if (e.dotTicks > 0 && s.tick % 3 === 0) this.vfx.burst(p.clone().setY(p.y + 0.4), { n: 1, color: '#ff7a1a', up: 0.05, size: 0.12, life: 5 });
      if (acid && revealed) {
        // Eaten by acid: a bilious sheen and drips running off it while it lasts.
        this.entityGlow.add(p.x, p.y + 0.3, p.z, ACID, 0.5, 0.28);
        if (this.fresh && (s.tick + e.id) % 3 === 0) this.vfx.burst(p.clone().setY(p.y + 0.35 + ((e.id * 7 + s.tick) % 3) * 0.08), { n: 1, color: '#d8f040', speed: 0.01, up: 0.005, size: 0.07, life: 8, gravity: 0.007 });
      }
    }
  }


  /* ---------- the dragon of the Burn (saga) ---------- */

  /** How high the dragon flies over the ground right now (it climbs and comes down over a few ticks); -1 before it shows. */
  private dragonLift = -1;
  private dragonDrawn = false;
  private dragonShadow: THREE.Mesh | null = null;
  /** La Quemadura's beacon: the pool of its light on the field's middle, and the beacon itself far off. */
  private beaconPool: THREE.Mesh | null = null;
  private beaconFar: THREE.Group | null = null;
  /** Where its jaws are (world), for the breath and the devouring. */
  /** Where an enemy is drawn this frame, lurch included (copied, never kept). */
  private lurchAt = new THREE.Vector3();
  private dragonMouth = new THREE.Vector3();
  private dragonAt = new THREE.Vector3();
  private dragonScale = 1;
  private eul = new THREE.Euler();

  /**
   * The dragon: on the wing it circles high over the clearing, banking into the turn, its wings beating
   * over eight frames and its shadow sweeping the ground; landed it is a mountain of black glass that
   * breathes, with heat under it. Embers fall off it and smoke streams off the wings.
   */
  private drawDragon(s: BattleState, e: Enemy, use: (k: string) => ModelBatch): void {
    const { x, y } = this.track.enemy.at(e.id, e.x, e.y, this.alpha, this.fresh);
    const ground = this.heightAt(x, y);
    const p = simToWorld(x, y, new THREE.Vector3(), ground);
    const air = e.air === true;
    if (this.dragonLift < 0) this.dragonLift = air ? DRAGON_SKY : 0;
    // Brought down (stunned out of the sky), it drops like a stone; landing by itself, it comes down slower.
    if (this.fresh) this.dragonLift = air ? Math.min(DRAGON_SKY, this.dragonLift + 0.3) : Math.max(0, this.dragonLift - (e.anim === 'stun' ? 0.6 + (DRAGON_SKY - this.dragonLift) * 0.9 : 0.5));
    const lift = this.dragonLift;
    const flying = lift > 0.05;
    const key = this.rk(`enemy:${flying ? 'dragonAir' : 'dragon'}:${e.tier}:0`);
    const scale = this.lib.scaleOf(key);
    this.dragonScale = scale;
    const batch = use(key);
    const anim = this.enemyAct(s, batch, e);
    const frame = anim ? (s.tick >> 1) & 3 : flying ? s.tick & 7 : (s.tick >> 1) & 7;
    let yaw = anim ? this.faceTo(e.id, facingToRotY(e.facing)) : facingToRotY(e.facing);
    let roll = 0;
    if (flying) {
      // Its wings lift it on each downbeat in the clip; without clips, the old bob.
      p.y = ground + lift + (anim ? 0 : Math.sin((frame / 8) * TAU + 1.2) * 0.16);
      // Banking into its circle: the wing on the inside dips.
      const len = Math.hypot(p.x, p.z) || 1;
      const side = (-p.x * Math.cos(yaw) + p.z * Math.sin(yaw)) / len;
      roll = -0.3 * side * Math.min(1, lift / DRAGON_SKY);
    } else if (!anim && e.anim === 'attack' && e.animT < 3) {
      // The bite: it lunges.
      p.x += Math.sin(yaw) * 0.35;
      p.z += Math.cos(yaw) * 0.35;
    } else if (!anim && e.anim === 'walk') yaw += (s.tick + e.id) % 6 < 3 ? 0.05 : -0.05;
    this.eul.set(0, yaw, roll, 'YXZ');
    this.q.setFromEuler(this.eul);
    this.m.compose(p, this.q, this.s.setScalar(scale));
    const tint = this.flashes.has(e.id) ? FLASH : e.stun > 0 ? STUNNED : undefined;
    batch.add(this.m, frame, tint, 1, anim);
    this.dragonDrawn = true;
    this.dragonAt.copy(p);
    this.positions.set(e.id, p.clone());
    // Its six glass scales (the diary's cracked one among them) and the chains of the harpoons in it.
    this.dragonField?.setBody(p, this.q, scale, flying, anim);
    // The jaws, where the head is this frame (the breath and the devouring come from there).
    this.dragonMouth.set(0, flying ? 1.95 : 2.7, flying ? 2.85 : 2.4);
    if (anim) posePoint(anim, DRAGON_HEAD_BONE, this.dragonMouth);
    this.dragonMouth.multiplyScalar(scale).applyQuaternion(this.q).add(p);
    if (anim) {
      const d = this.drawnAt.get(e.id);
      if (d && d.key === key) {
        d.p.copy(p);
        d.yaw = yaw;
      } else this.drawnAt.set(e.id, { key, p: p.clone(), yaw, scale, tint: undefined });
    }
    const tall = this.lib.heightOf(key) * scale;
    this.bars.add(this.v.copy(p).setY(p.y + tall + 0.4), e.hp / e.maxHp, RED, 2.6);
    // Its shadow: the shape of it, sweeping the ground in flight; plain blobs once it stands.
    const sh = this.dragonShadowMesh();
    if (flying) {
      sh.visible = true;
      sh.position.set(p.x, ground + 0.03, p.z);
      sh.rotation.y = yaw;
      // Big and marked: its shadow is what tells where it is over the clearing.
      const k = scale * 1.3 * (1 + lift * 0.08);
      sh.scale.set(k, 1, k);
      (sh.material as THREE.MeshBasicMaterial).opacity = Math.max(0.42, 0.78 - lift * 0.08);
    } else sh.visible = false;
    if (lift < 1) {
      const fx = Math.sin(yaw), fz = Math.cos(yaw);
      for (const [d, r] of [[0.8, 2.4], [-0.6, 2.0], [2.2, 1.0], [-2.4, 1.2]] as const) this.shadows.add(p.x + fx * d * scale * 0.5, ground, p.z + fz * d * scale * 0.5, r * scale * 0.75);
    }
    // Heat under it: the glass glows on the ground it stands on (fainter as it climbs).
    this.entityGlow.add(p.x, ground + 0.08, p.z, LAVA_GLOW, 3.4 * scale * 0.7, flying ? 0.08 : 0.2);
    this.entityGlow.add(this.dragonMouth.x, this.dragonMouth.y, this.dragonMouth.z, LAVA_GLOW, 0.9, 0.35 + (s.tick % 4) * 0.05);
    if (!this.fresh) return;
    const low = this.opts.quality === 'low';
    if (s.tick % (low ? 4 : 2) === 0) {
      // Embers dropping off the glass.
      const a = Math.random() * TAU;
      this.vfx.burst(this.v.set(p.x + Math.cos(a) * 0.8 * scale, p.y + (flying ? 0.9 : 1.1) * scale, p.z + Math.sin(a) * 0.8 * scale), { n: 1, color: s.tick % 6 === 0 ? '#c98bff' : '#ff8a3d', speed: 0.01, up: 0, size: 0.12, life: 14, gravity: 0.008 });
    }
    if (flying && s.tick % (low ? 6 : 3) === 0) {
      // Smoke streaming off a wing.
      const k = s.tick % 2 ? 1 : -1;
      const w = this.v.set(k * 2.2 * scale, 1.6 * scale, -0.9 * scale).applyQuaternion(this.q).add(p);
      this.vfx.burst(w, { n: 1, color: '#1d1724', speed: 0.01, up: -0.01, size: 0.9, life: 14, smoke: true, gravity: 0.001 });
    }
    if (!flying && e.anim === 'walk' && (s.tick + e.id) % 4 === 0) {
      // A foot comes down: a ring of dust and a jolt.
      const side = (s.tick >> 2) & 1 ? 1 : -1;
      const fp = new THREE.Vector3(side * 0.5 * scale, 0, 0.6 * scale).applyQuaternion(this.q).add(p).setY(ground + 0.02);
      this.vfx.ring(fp, 0.1, 0.9, '#6e6460', 8, 0.6);
      this.vfx.burst(fp.setY(ground + 0.15), { n: low ? 2 : 4, color: '#3a3330', speed: 0.04, up: 0.03, size: 0.4, life: 12, smoke: true });
      this.shake = Math.max(this.shake, 0.08);
    }
  }

  /**
   * The beacon's light (La Quemadura): a warm pool on the ground of the field's middle, as wide as where no spectre
   * can be devoured, its rim a little brighter; and far off, the watchtower's fire over the Burn. Built once.
   */
  private beaconLight(s: BattleState): void {
    const r = s.rules?.dragon?.beaconR ?? 0;
    if (!this.beaconPool) {
      const c = document.createElement('canvas');
      c.width = c.height = 256;
      const g = c.getContext('2d') as CanvasRenderingContext2D;
      const grad = g.createRadialGradient(128, 128, 0, 128, 128, 126);
      grad.addColorStop(0, 'rgba(255,150,70,0.5)');
      grad.addColorStop(0.7, 'rgba(255,130,60,0.3)');
      grad.addColorStop(0.9, 'rgba(255,170,90,0.7)');
      grad.addColorStop(0.95, 'rgba(255,140,70,0.35)');
      grad.addColorStop(1, 'rgba(255,130,60,0)');
      g.fillStyle = grad;
      g.fillRect(0, 0, 256, 256);
      const size = ((r * 2) / 1000) * WORLD_PER_HEX;
      const geo = new THREE.PlaneGeometry(size, size).rotateX(-Math.PI / 2);
      const m = new THREE.Mesh(geo, decalMat({ map: new THREE.CanvasTexture(c), transparent: true, opacity: 0.4, depthWrite: false }));
      m.position.set(0, this.heightAt(0, 0) + 0.06, 0);
      m.renderOrder = 1;
      this.overlay.add(m);
      this.beaconPool = m;
      // The beacon far off over the Burn: a column of warm light and its fire, beyond the field's edge.
      const far = new THREE.Group();
      const d = ((this.map.radius + 4) * WORLD_PER_HEX);
      far.position.set(-d * 0.7, 0, -d * 0.7);
      const col = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.6, 7, 8, 1, true), new THREE.MeshBasicMaterial({ color: '#ffb070', transparent: true, opacity: 0.18, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
      col.position.y = 3.5;
      far.add(col);
      glowSprite(far, '#ffd08a', 3.2, new THREE.Vector3(0, 6.8, 0), 0.9, true);
      glowSprite(far, '#ff7a2a', 7, new THREE.Vector3(0, 6.6, 0), 0.4, true);
      this.overlay.add(far);
      this.beaconFar = far;
    }
    const breathe = 0.36 + 0.06 * Math.sin(this.now * 1.6) + ((s.tick * 7) % 5) * 0.008;
    (this.beaconPool.material as THREE.MeshBasicMaterial).opacity = breathe;
  }

  /** The dragon's shadow on the ground in flight: its spread silhouette, drawn once. */
  private dragonShadowMesh(): THREE.Mesh {
    if (this.dragonShadow) return this.dragonShadow;
    const c = document.createElement('canvas');
    c.width = c.height = 256;
    const g = c.getContext('2d') as CanvasRenderingContext2D;
    g.filter = 'blur(5px)';
    g.fillStyle = '#000';
    g.strokeStyle = '#000';
    g.translate(128, 128);
    // Seen from above, the head toward the bottom of the canvas (+Z once laid on the ground): body, neck, tail.
    g.beginPath();
    g.ellipse(0, 4, 13, 38, 0, 0, TAU);
    g.fill();
    g.lineWidth = 9;
    g.beginPath();
    g.moveTo(0, 34);
    g.quadraticCurveTo(-4, 52, 0, 72);
    g.stroke();
    g.lineWidth = 6;
    g.beginPath();
    g.moveTo(0, -30);
    g.quadraticCurveTo(-8, -76, 2, -118);
    g.stroke();
    // The wings: fingers fanning out, the trailing edge scalloped.
    for (const k of [-1, 1]) {
      g.beginPath();
      g.moveTo(k * 8, 4);
      g.lineTo(k * 122, 8);
      g.quadraticCurveTo(k * 100, -10, k * 112, -28);
      g.quadraticCurveTo(k * 84, -24, k * 86, -50);
      g.quadraticCurveTo(k * 60, -36, k * 54, -60);
      g.quadraticCurveTo(k * 32, -40, k * 8, -40);
      g.closePath();
      g.fill();
    }
    const tex = new THREE.CanvasTexture(c);
    // The canvas spans about 5.8 model units: the wings, nose to tail.
    const size = 5.8;
    const geo = new THREE.PlaneGeometry(size, size).rotateX(-Math.PI / 2);
    const m = new THREE.Mesh(geo, decalMat({ map: tex, transparent: true, opacity: 0.45, depthWrite: false }));
    m.renderOrder = 1;
    this.overlay.add(m);
    this.dragonShadow = m;
    return m;
  }

  /** The dragon's fire: a stream from its jaws raking the ground along the line it marked. */
  private dragonBreath(s: BattleState, id: number, tx: number, ty: number): void {
    const d = s.enemies.find((x) => x.id === id);
    if (!d) return;
    const low = this.opts.quality === 'low';
    const dx = tx - d.x, dy = ty - d.y;
    const len = Math.hypot(dx, dy) || 1;
    const reach = DRAGON.breath.marks * DRAGON.breath.spacing;
    const from = this.dragonDrawn ? this.dragonMouth.clone() : simToWorld(d.x, d.y, new THREE.Vector3(), 2);
    const endX = d.x + (dx / len) * reach, endY = d.y + (dy / len) * reach;
    const ground = (t: number): THREE.Vector3 => {
      const x = d.x + (endX - d.x) * t, y = d.y + (endY - d.y) * t;
      return simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y) + 0.05);
    };
    this.flare(from.clone(), '#ffd8a0', 1.6, 4);
    this.flare(from.clone(), '#ff5a1f', 3.2, 8);
    this.vfx.burst(from, { n: low ? 6 : 14, color: '#ffb070', speed: 0.06, up: 0.02, size: 0.22, life: 6 });
    const steps = 5;
    for (let i = 1; i <= steps; i++) {
      const t = 0.15 + (0.85 * i) / steps;
      this.later.push({
        n: i - 1,
        run: () => {
          const g = ground(t);
          this.vfx.shaft(from, g, '#ff6a1f', 3, 0.55);
          this.vfx.shaft(from, g, '#ffd08a', 2, 0.2);
          if (i % 2 === 0) this.vfx.shaft(from, g, '#9b4dff', 3, 0.7);
          this.vfx.burst(g.clone().setY(g.y + 0.2), { n: low ? 5 : 12, color: i % 2 ? '#ff8a2a' : '#ffd08a', speed: 0.07, up: 0.1, size: 0.24, life: 10, gravity: -0.002, spread: 0.5 });
          this.vfx.burst(g.clone().setY(g.y + 0.4), { n: low ? 2 : 4, color: '#2a1c18', speed: 0.03, up: 0.05, size: 0.7, life: 18, smoke: true, spread: 0.4 });
          this.vfx.ring(g, 0.2, 1.1, '#ff5a2a', 6, 0.8);
          this.flare(g.clone().setY(g.y + 0.5), '#ff6a2a', 2, 6);
        },
      });
    }
    this.shake = Math.max(this.shake, 0.18);
  }

  /** The dragon comes down: the ground rings, glass and dust fly, and the board jolts hard. */
  private dragonLands(p: THREE.Vector3, r: number): void {
    const low = this.opts.quality === 'low';
    // It strikes as it touches down, a few ticks after the sim set it on the ground.
    this.later.push({
      n: Math.ceil(DRAGON_SKY / 0.5),
      run: () => {
        const at = this.dragonDrawn ? this.dragonAt.clone().setY(p.y) : p.clone();
        this.vfx.ring(at, 0.6, r * 2.6, '#cfc3b4', 14, 1);
        this.vfx.ring(at, 0.4, r * 1.6, '#ff5a2a', 10, 0.9);
        this.vfx.ring(at.clone().setY(at.y + 0.4), 0.3, r * 1.1, '#9b4dff', 8, 0.6);
        this.vfx.burst(at.clone().setY(at.y + 0.3), { n: low ? 20 : 50, color: '#4a4440', speed: 0.16, up: 0.05, size: 0.8, life: 22, smoke: true, spread: r * 0.8 });
        this.vfx.burst(at.clone().setY(at.y + 0.4), { n: low ? 14 : 30, color: '#ff8a3d', speed: 0.14, up: 0.12, size: 0.14, life: 12, gravity: 0.012, spread: r * 0.5 });
        this.vfx.burst(at.clone().setY(at.y + 0.6), { n: low ? 8 : 18, color: '#3a3448', speed: 0.12, up: 0.16, size: 0.12, life: 16, gravity: 0.016, spread: r * 0.4 });
        this.flare(at.clone().setY(at.y + 1), '#ff6a2a', r * 3, 8);
        this.fling(at, low ? 6 : 12, 1);
        this.shake = Math.max(this.shake, 0.9);
      },
    });
    this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 8 : 18, color: '#5a524c', speed: 0.12, up: 0.02, size: 0.6, life: 16, smoke: true, spread: r });
  }

  /** It roars: a shockwave out of its jaws, the air shaking with fire and ink. */
  private dragonRoar(p: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    const m = this.dragonDrawn ? this.dragonMouth.clone() : p.clone().setY(p.y + 2);
    this.vfx.ring(p, 0.6, 9, '#ff4f3a', 16, 1);
    this.vfx.ring(p.clone().setY(p.y + 0.6), 0.4, 6, '#9b4dff', 12, 0.8);
    this.vfx.ring(m, 0.2, 2.8, '#ffd0a0', 6, 0.7);
    this.vfx.burst(m, { n: low ? 16 : 40, color: '#ff7a2a', speed: 0.14, up: 0.06, size: 0.22, life: 14 });
    this.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 10 : 24, color: '#2b1f3a', speed: 0.1, up: 0.04, size: 0.7, life: 20, smoke: true, spread: 1.5 });
    this.flare(m, '#ff6a2a', 4, 8);
    this.flare(m.clone(), '#9b4dff', 6, 12);
    this.shake = Math.max(this.shake, 0.6);
  }

  /** It eats a friend's spectre: the ghost's light is torn out of it in threads and swallowed. */
  private dragonDevour(gp: THREE.Vector3, p: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    const to = this.dragonDrawn ? this.dragonMouth.clone() : p.clone().setY(p.y + 2);
    const g = gp.clone().setY(gp.y + 0.6);
    this.vfx.implode(gp.clone().setY(gp.y + 0.05), 1.6, { n: low ? 8 : 20, color: '#7fe3d6', size: 0.12, life: 8, swirl: 1.2 });
    this.vfx.ring(gp, 1.6, 0.2, '#7fe3d6', 8, 0.9);
    this.flare(g.clone(), '#bff6ee', 1.8, 6);
    for (let i = 1; i <= 4; i++) {
      this.later.push({
        n: i,
        run: () => {
          const a = g.clone().lerp(to, (i - 1) / 4), b = g.clone().lerp(to, i / 4);
          a.y += Math.sin(((i - 1) / 4) * Math.PI) * 0.8;
          b.y += Math.sin((i / 4) * Math.PI) * 0.8;
          this.vfx.beam(a, b, '#9ff0e4', 4, 0.07, 0.12);
          this.vfx.beam(g, b, '#7fe3d6', 2, 0.03, 0.25);
          this.vfx.burst(b, { n: low ? 3 : 7, color: i % 2 ? '#dffcf6' : '#7fe3d6', speed: 0.02, up: 0.01, size: 0.16, life: 8, gravity: -0.002 });
          if (i === 4) {
            this.flare(to.clone(), '#9b4dff', 3.6, 10);
            this.flare(to.clone(), '#7fe3d6', 1.6, 5);
            this.vfx.burst(to, { n: low ? 8 : 18, color: '#b98bff', speed: 0.07, up: 0.04, size: 0.16, life: 12 });
            this.vfx.ring(p, 0.4, 3.2, '#7fe3d6', 10, 0.6);
          }
        },
      });
    }
    // Where the spectre stood: cold mist settling.
    this.vfx.burst(gp.clone().setY(gp.y + 0.2), { n: low ? 4 : 10, color: '#2a4a48', speed: 0.02, up: 0.01, size: 0.6, life: 22, smoke: true, spread: 0.5 });
  }

  /** Its tail goes round: a sweep of dust and sparks running the circle, a quarter a tick. */
  private dragonSweep(p: THREE.Vector3, r: number): void {
    const low = this.opts.quality === 'low';
    for (let i = 0; i < 4; i++) {
      this.later.push({
        n: i,
        run: () => {
          for (let j = 0; j < (low ? 3 : 6); j++) {
            const a = ((i + j / 6) / 4) * TAU + 0.6;
            const q = p.clone().add(new THREE.Vector3(Math.cos(a) * r * 0.9, 0.2, Math.sin(a) * r * 0.9));
            this.vfx.burst(q, { n: 1, color: '#5a524c', speed: 0.05, up: 0.03, size: 0.7, life: 14, smoke: true });
            this.vfx.burst(q, { n: 2, color: '#ff9a4a', speed: 0.08, up: 0.06, size: 0.08, life: 6 });
          }
          this.vfx.ring(p, r * (0.6 + i * 0.1), r * (0.9 + i * 0.05), '#ff8a6e', 5, 0.5);
        },
      });
    }
    this.vfx.ring(p, r * 0.2, r, '#dfe7ef', 8, 0.8);
    this.shake = Math.max(this.shake, 0.3);
  }

  /** The dragon falls: its heart bursts, the glass shatters outward and the fire inside goes up in a column. */
  private dragonFalls(p: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    const q = p.clone();
    const top = q.clone().setY(q.y + 1.6 * this.dragonScale);
    this.flare(top.clone(), '#ffffff', 1.8, 4);
    this.flare(top.clone(), '#ff6a2a', 4.5, 12);
    this.flare(top.clone(), '#9b4dff', 5.5, 18);
    this.vfx.ring(q, 0.6, 12, '#ff4f3a', 22, 1);
    this.vfx.ring(q, 0.4, 7, '#ffd08a', 14, 0.9);
    this.vfx.ring(q.clone().setY(q.y + 0.6), 0.3, 9, '#9b4dff', 18, 0.8);
    this.vfx.shaft(q, q.clone().setY(q.y + 9), '#ff5a1f', 16, 0.9);
    this.vfx.shaft(q, q.clone().setY(q.y + 7), '#c98bff', 20, 1.3);
    this.vfx.burst(top, { n: low ? 30 : 80, color: '#ff8a2a', speed: 0.16, up: 0.16, size: 0.22, life: 22, gravity: 0.01 });
    this.vfx.burst(top, { n: low ? 14 : 36, color: '#2a2436', speed: 0.18, up: 0.18, size: 0.14, life: 22, gravity: 0.016 });
    this.vfx.burst(q.clone().setY(q.y + 1), { n: low ? 14 : 32, color: '#1d1724', speed: 0.08, up: 0.06, size: 1.1, life: 30, smoke: true, spread: 2 });
    this.fling(q, low ? 10 : 24, 1.5);
    // Then the rest of it comes apart: more bursts over the next ticks, and violet ash rising.
    for (let i = 1; i <= 4; i++) {
      this.later.push({
        n: i * 3,
        run: () => {
          const a = Math.random() * TAU;
          const at = q.clone().add(new THREE.Vector3(Math.cos(a) * 1.5, 0.6 + i * 0.3, Math.sin(a) * 1.5));
          this.flare(at.clone(), i % 2 ? '#ff6a2a' : '#9b4dff', 2.6, 8);
          this.vfx.burst(at, { n: low ? 10 : 26, color: i % 2 ? '#ff9a4a' : '#c98bff', speed: 0.12, up: 0.1, size: 0.16, life: 14, gravity: 0.006 });
          this.vfx.ring(q, 0.4, 5 + i, i % 2 ? '#ff8a6e' : '#b98bff', 10, 0.6);
          this.fling(at, low ? 3 : 6, 0.9);
          this.shake = Math.max(this.shake, 0.5);
        },
      });
    }
    this.later.push({ n: 16, run: () => this.vfx.burst(q.clone().setY(q.y + 0.3), { n: low ? 20 : 50, color: '#d6b3ff', speed: 0.03, up: 0.06, size: 0.12, life: 40, gravity: -0.003, spread: 3 }) });
    this.dragonLift = -1;
    this.shake = Math.max(this.shake, 1);
  }

  /**
   * A sapper under the wall: no creature, only a mound of churned earth where it went down (heaving as it
   * digs), a bump of earth crawling under the wall toward where it will come up, and the ground there
   * starting to split and spit clods before it bursts out.
   */
  private drawBurrow(s: BattleState, e: Enemy, p: THREE.Vector3, use: (k: string) => ModelBatch): void {
    const total = NIGHTMARES[e.kind as keyof typeof NIGHTMARES]?.burrows ?? 1;
    const k = Math.min(1, Math.max(0, 1 - e.timer / total));
    const low = this.opts.quality === 'low';
    const beat = (s.tick + e.id) % 3;
    const put = (key: string, q: THREE.Vector3, yaw: number, sx: number, sy: number, sz: number, shadow: number): void => {
      this.q.setFromAxisAngle(this.yAxis, yaw);
      this.m.compose(q, this.q, this.s.set(sx, sy, sz));
      use(key).add(this.m, 0);
      if (shadow > 0) this.shadows.add(q.x, q.y, q.z, shadow);
    };
    // Where it went down: the pit, spitting clods and dust as it digs.
    put('pile:mound', p, e.id * 1.3, 1.7, 1.7 * (beat === 0 ? 1.3 : 1), 1.7, 0);
    if (this.fresh && beat === 0) this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 2 : 4, color: '#5a4632', speed: 0.05, up: 0.08, size: 0.1, life: 8, gravity: 0.014 });
    if (this.fresh && (s.tick + e.id) % 4 === 0) this.vfx.burst(p.clone().setY(p.y + 0.15), { n: 1, color: '#6d5a44', speed: 0.01, up: 0.015, size: 0.45, life: 14, smoke: true });
    if (e.digX === undefined || e.digY === undefined) return;
    const d = simToWorld(e.digX, e.digY, new THREE.Vector3(), this.heightAt(e.digX, e.digY));
    const len = p.distanceTo(d);
    const yaw = Math.atan2(d.x - p.x, d.z - p.z);
    // The tunnel heaving the ground up as it crawls under the wall, and the bump at its head.
    const reach = len * k;
    for (let t = 0.4; t < reach - 0.25; t += 0.42) put('pile:ridge', p.clone().lerp(d, t / len), yaw + Math.sin(t * 7) * 0.2, 1.2, 0.8, 1.2, 0);
    if (k > 0.05 && k < 0.95) {
      const head = p.clone().lerp(d, k);
      put('pile:ridge', head, yaw + (beat - 1) * 0.15, 1.8, beat === 0 ? 2.2 : 1.6, 1.8, 0.35);
      if (this.fresh && s.tick % 2 === 0) this.vfx.burst(head.clone().setY(head.y + 0.12), { n: low ? 1 : 2, color: '#6d5a44', speed: 0.02, up: 0.03, size: 0.3, life: 10, smoke: true });
    }
    // Where it will come up: the ground bulges, splits and spits clods.
    if (k > 0.6) {
      const g = (k - 0.6) / 0.4;
      const sc = 1.2 + g * 1.4;
      put('pile:ridge', d, -e.id + (beat - 1) * 0.2, sc, sc * (beat === 0 ? 1.6 : 1.1), sc, 0.3 + g * 0.3);
      put('pile:ridge', d, -e.id + Math.PI / 2, sc * 0.8, sc * 0.9, sc * 0.8, 0);
      if (this.fresh && beat !== 2) this.vfx.burst(d.clone().setY(d.y + 0.2), { n: low ? 1 : 2 + Math.round(g * 3), color: '#4a3c2e', speed: 0.04 + g * 0.04, up: 0.06 + g * 0.05, size: 0.09, life: 8, gravity: 0.014 });
    }
  }

  /** Boss marks, pooled: a red aura ring on the ground and a skull floating over the head. */
  private bossRings: THREE.Mesh[] = [];
  private bossSkulls: THREE.Sprite[] = [];
  private bossUsed = 0;
  private skullTex: THREE.Texture | null = null;

  private bossMark(e: Enemy, p: THREE.Vector3, tick: number, height: number): void {
    const i = this.bossUsed++;
    let ring = this.bossRings[i];
    if (!ring) {
      ring = new THREE.Mesh(new THREE.RingGeometry(0.62, 0.8, 40).rotateX(-Math.PI / 2), decalMat({ color: '#ff4f3a', transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending, depthWrite: false }));
      this.overlay.add(ring);
      this.bossRings.push(ring);
    }
    let skull = this.bossSkulls[i];
    if (!skull) {
      skull = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.skull(), transparent: true, depthWrite: false }));
      this.overlay.add(skull);
      this.bossSkulls.push(skull);
    }
    const k = 1 + ((tick + e.id) % 6) * 0.05;
    ring.visible = true;
    ring.position.set(p.x, p.y + 0.04, p.z);
    ring.scale.setScalar(k);
    ring.rotation.y = tick * 0.12;
    skull.visible = true;
    skull.position.set(p.x, p.y + height + 0.3 + ((tick + e.id) % 8 < 4 ? 0.05 : 0), p.z);
    skull.scale.setScalar(0.62);
    this.entityGlow.add(p.x, p.y + 0.05, p.z, RED, 1.8, 0.35);
    if ((tick + e.id) % 3 === 0) this.vfx.burst(p.clone().setY(p.y + 0.2), { n: 1, color: '#ff4f3a', speed: 0.02, up: 0.05, size: 0.16, life: 8 });
  }

  /** A white skull with a red rim, drawn once on a canvas. */
  private skull(): THREE.Texture {
    if (this.skullTex) return this.skullTex;
    const c = document.createElement('canvas');
    c.width = 64;
    c.height = 64;
    const g = c.getContext('2d') as CanvasRenderingContext2D;
    g.fillStyle = '#ff4f3a';
    g.beginPath();
    g.arc(32, 30, 26, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#eceef2';
    g.beginPath();
    g.arc(32, 27, 17, 0, Math.PI * 2);
    g.fill();
    g.fillRect(22, 36, 20, 12);
    g.fillStyle = '#16181d';
    g.beginPath();
    g.arc(25, 27, 5, 0, Math.PI * 2);
    g.arc(39, 27, 5, 0, Math.PI * 2);
    g.fill();
    g.fillRect(27, 40, 3, 8);
    g.fillRect(34, 40, 3, 8);
    this.skullTex = new THREE.CanvasTexture(c);
    return this.skullTex;
  }

  private drawTurrets(s: BattleState, use: (k: string) => ModelBatch): void {
    for (const t of s.turrets) {
      const p = simToWorld(t.x, t.y, new THREE.Vector3(), this.heightAt(t.x, t.y));
      if (t.dt) this.veil(s, p, 0.45, t.dt, t.id);
      const b = use('turret');
      // Portable turrets (incursions) stay the whole extraction: bigger, on a lit base. An artificer's
      // skill turrets last seconds: smaller, with their time always showing.
      const portable = t.portable || t.expires - s.tick > 20000;
      this.compose(p, 0, this.lib.scaleOf('turret') * (portable ? 1.9 : 1.25));
      if (portable) {
        this.shadows.add(p.x, p.y, p.z, 0.75);
        this.entityGlow.add(p.x, p.y + 0.05, p.z, new THREE.Color('#ffb070'), 1.1, 0.28);
      }
      b.add(this.m, 0);
      if (b.hasDynamic('turret')) {
        const tgt = t.aim ? s.enemies.find((e) => e.id === t.aim) : undefined;
        let a = 0;
        if (tgt) {
          const tp = simToWorld(tgt.x, tgt.y);
          a = Math.atan2(tp.x - p.x, tp.z - p.z);
        }
        this.local.makeRotationY(a);
        b.addDynamic('turret', this.m.clone(), this.local);
      }
      if (!portable) this.bars.add(this.v.copy(p).setY(p.y + 0.8), Math.max(0, (t.expires - s.tick) / 240), new THREE.Color('#7fb8ff'), 0.35);
      else if (t.portable) {
        // Newer portable turrets: what they can still take, and how long they have left.
        this.bars.add(this.v.copy(p).setY(p.y + 1.15), Math.max(0, (t.hp ?? 0) / (t.maxHp ?? 1)), GREEN, 0.5);
        this.bars.add(this.v.copy(p).setY(p.y + 1.0), Math.max(0, (t.expires - s.tick) / (s.rules?.turrets?.ticks ?? 540)), new THREE.Color('#ffb070'), 0.5);
      }
    }
  }

  /** Nets in flight (projectile → its target), and the nightmares a net has landed on. */
  private netAim = new Map<number, number>();
  private netHeld = new Set<number>();
  /** The ranger's arrows in flight (id → where it was last drawn, whose target, a volley's or not), to bite when they land. */
  private arrowsOut = new Map<number, { p: THREE.Vector3; target: number; volley: boolean; seen: boolean }>();

  private drawProjectiles(s: BattleState, use: (k: string) => ModelBatch): void {
    if (this.fresh && this.netAim.size) {
      for (const [id, target] of this.netAim) {
        if (s.projectiles.some((p) => p.id === id)) continue;
        this.netAim.delete(id);
        this.netHeld.add(target);
        // Caught: the net snaps shut with a puff of rope fibres.
        const at = this.positions.get(target);
        if (at) {
          this.vfx.ring(at.clone().setY(at.y + 0.05), 0.9, 0.3, '#efe9de', 5, 0.9);
          this.vfx.burst(at.clone().setY(at.y + 0.6), { n: this.opts.quality === 'low' ? 4 : 10, color: '#efe9de', speed: 0.06, up: 0.04, size: 0.06, life: 7, gravity: 0.01 });
        }
      }
    }
    if (this.fresh) for (const a of this.arrowsOut.values()) a.seen = false;
    for (const p of s.projectiles) {
      const tr = this.track.proj.at(p.id, p.x, p.y, this.alpha, this.fresh);
      const moved = tr.x !== p.x || tr.y !== p.y;
      const w = simToWorld(tr.x, tr.y);
      const from = simToWorld(p.sx, p.sy);
      const to = simToWorld(p.tx, p.ty);
      let h = 0.75;
      if (p.kind === 'rock' || p.kind === 'boulder' || p.kind === 'fireball') {
        const t = Math.min(1, Math.max(0, moved ? p.t - 1 + this.alpha : p.t) / p.flight);
        h = (p.kind === 'boulder' ? 1.4 : 0.9) + Math.sin(t * Math.PI) * (p.kind === 'rock' ? 2.4 : p.kind === 'boulder' ? 3.6 : 1.2) * Math.min(1, from.distanceTo(to) / 5);
      }
      w.y = h + Math.max(0, this.heightAt(tr.x, tr.y));
      // A shot turned by a glyph flies in its new type's colour, shedding its motes.
      if (p.dt) {
        const look = VEIL[p.dt];
        if (look) {
          this.entityGlow.add(w.x, w.y, w.z, look.glow, 0.7, 0.85);
          if (this.fresh) this.vfx.burst(w.clone(), { n: 1, color: look.mote, speed: 0.01, up: 0.01, size: 0.14, life: 8, spread: 0.1 });
        }
      }
      const dir = to.clone().sub(from).setY(0);
      const yaw = Math.atan2(dir.x, dir.z);
      if (p.kind === 'light') {
        // The cleric's bolt: a white-hot core in a pale blue halo, trailing a short comet tail of sparks.
        const back = dir.lengthSq() > 0 ? dir.clone().normalize() : this.v.set(0, 0, 0);
        this.entityGlow.add(w.x, w.y, w.z, HALO, 0.62, 0.9);
        this.entityGlow.add(w.x, w.y, w.z, WHITE, 0.24, 1);
        for (let i = 1; i <= 3; i++) this.entityGlow.add(w.x - back.x * i * 0.13, w.y, w.z - back.z * i * 0.13, HALO, 0.36 - i * 0.07, 0.55 - i * 0.12);
        if ((s.tick + p.id) % 2 === 0) this.vfx.burst(w, { n: 1, color: '#dbeeff', speed: 0.008, up: 0.012, size: 0.1, life: 5, gravity: -0.001 });
        continue;
      }
      if (p.kind === 'orb' || p.kind === 'fireball') {
        const col = p.kind === 'fireball' ? new THREE.Color('#ff8a2a') : new THREE.Color('#8fc8ff');
        this.entityGlow.add(w.x, w.y, w.z, col, p.kind === 'fireball' ? 0.8 : 0.4, 1);
        this.entityGlow.add(w.x, w.y, w.z, new THREE.Color('#ffffff'), p.kind === 'fireball' ? 0.3 : 0.15, 1);
        if (p.kind === 'fireball') this.vfx.burst(w, { n: 1, color: '#ff6a1a', speed: 0.01, up: 0.01, size: 0.3, life: 4 });
        continue;
      }
      if (p.kind === 'arrow' && p.stun > 0 && p.damage <= 1) {
        // The ranger's net: thrown in an arc, opening as it flies, spinning, weighted at the rim.
        this.netAim.set(p.id, p.target);
        const t = Math.min(1, Math.max(0, moved ? p.t - 1 + this.alpha : p.t) / Math.max(1, p.flight));
        this.skillFx.net(p.id, w.setY(w.y + 0.3 + Math.sin(t * Math.PI) * Math.min(1.3, 0.3 + from.distanceTo(to) * 0.18)), t);
        continue;
      }
      // The clockwork turret's bolts are short steel darts with a cyan glint; a branch throws its own (a harpoon,
      // a pot of pitch or embers, a split stone).
      let shot: string = p.kind;
      // (No key string unless some turret or branch could claim it: this runs per projectile per frame.)
      if ((p.kind === 'bolt' && this.clockAt.size) || ((p.kind === 'bolt' || p.kind === 'rock' || p.kind === 'boulder') && this.branchAt.size)) {
        const at = `${p.sx},${p.sy}`;
        if (p.kind === 'bolt' && this.clockAt.has(at)) shot = 'dart';
        else if (this.branchAt.size) {
          const br = this.branchAt.get(at);
          if (br) shot = BRANCH_SHOT[br]?.[p.kind] ?? shot;
        }
      }
      const b = use(`proj:${shot}`);
      this.q.setFromAxisAngle(this.yAxis, yaw);
      this.m.compose(w, this.q, this.s.set(1, 1, 1));
      b.add(this.m, 0);
      if (shot === 'pitchPot' || shot === 'emberPot') this.entityGlow.add(w.x, w.y + 0.08, w.z, POT_GLOW, 0.5, 0.75);
      if (shot === 'limeSack' && this.fresh && (s.tick + p.id) % 2 === 0) this.vfx.burst(w, { n: 1, color: '#e6e8e4', speed: 0.006, up: 0.004, size: 0.16, life: 10, smoke: true });
      if (shot === 'whistleBolt' && dir.lengthSq() > 0) {
        // The howl made visible: a long pale streak behind it (the arrows' shared streak layer) and a faint halo on
        // the whistle, so the tower that scatters the crowd reads apart from a plain ballista's bolt.
        const k = 1 / Math.sqrt(dir.x * dir.x + dir.z * dir.z);
        const len = Math.min(1.4, Math.hypot(w.x - from.x, w.z - from.z));
        this.skillFx.streak(w.x + dir.x * k * 0.2, w.y, w.z + dir.z * k * 0.2, dir.x * k, 0, dir.z * k, len, 0.04, WHISTLE_TRAIL, 0.7);
        this.entityGlow.add(w.x + dir.x * k * 0.16, w.y, w.z + dir.z * k * 0.16, WHISTLE_TRAIL, 0.2, 0.45);
      }
      if (p.kind === 'arrow' && dir.lengthSq() > 0) {
        // The ranger's arrows: a bright streak behind each, longer and colder for a volley (multishot pierces),
        // a dragon-red ember at the fletching of the volley's.
        const volley = p.pierce;
        const len = Math.min(volley ? 1.7 : 1, Math.hypot(w.x - from.x, w.z - from.z));
        const k = 1 / Math.sqrt(dir.x * dir.x + dir.z * dir.z);
        this.skillFx.streak(w.x + dir.x * k * 0.3, w.y, w.z + dir.z * k * 0.3, dir.x * k, 0, dir.z * k, len, volley ? 0.045 : 0.03, volley ? ARROW_VOLLEY : ARROW_TRAIL, volley ? 0.9 : 0.55);
        this.entityGlow.add(w.x + dir.x * k * 0.3, w.y, w.z + dir.z * k * 0.3, ARROW_TRAIL, volley ? 0.2 : 0.14, volley ? 0.55 : 0.4);
        if (volley) this.entityGlow.add(w.x - dir.x * k * 0.25, w.y, w.z - dir.z * k * 0.25, RED, 0.16, 0.55);
        const o = this.arrowsOut.get(p.id);
        if (o) {
          o.p.copy(w);
          o.seen = true;
        } else this.arrowsOut.set(p.id, { p: w.clone(), target: p.target, volley, seen: true });
      }
      if (shot === 'dart') this.entityGlow.add(w.x, w.y, w.z, CLOCK_CYAN, 0.22, 0.5);
      const look = p.enchant ? ENCHANT_LOOK[p.enchant] : undefined;
      if (look) {
        // An enchanted shot trails its element.
        const back = dir.lengthSq() > 0 ? dir.clone().normalize() : this.v.set(0, 0, 0);
        this.entityGlow.add(w.x, w.y, w.z, look.glow, 0.42, 0.75);
        this.entityGlow.add(w.x - back.x * 0.18, w.y, w.z - back.z * 0.18, look.glow, 0.28, 0.4);
        if (this.fresh && (s.tick + p.id) % 2 === 0) this.vfx.burst(w, { n: 1, color: look.fx, speed: 0.006, up: p.enchant === 'acid' ? -0.01 : 0.008, size: 0.07, life: 5, gravity: p.enchant === 'acid' ? 0.006 : 0 });
      }
    }
    if (this.fresh && this.arrowsOut.size) this.arrowBites();
  }

  /**
   * The ranger's arrows that landed since the last tick bite: a crisp flash, sparks and a puff of dust where they
   * struck (a volley's flash is bigger, with dragon-red sparks). A few per tick at most.
   */
  private arrowBites(): void {
    const low = this.opts.quality === 'low';
    let n = 0;
    let lastTarget = -1;
    for (const [id, a] of this.arrowsOut) {
      if (a.seen) continue;
      this.arrowsOut.delete(id);
      if (n++ >= (low ? 3 : 8)) continue;
      const tp = this.positions.get(a.target);
      const p = tp && tp.distanceToSquared(a.p) < 4 ? this.v.set(tp.x, tp.y + 0.55, tp.z) : a.p;
      // One flash per nightmare a tick (a volley's five arrows on one target would stack into a blot).
      if (a.target !== lastTarget) this.skillFx.flash(p, ARROW_TRAIL, a.volley ? 0.5 : 0.3, 0.12);
      lastTarget = a.target;
      this.vfx.burst(p, { n: low ? 2 : a.volley ? 6 : 3, color: '#f2f6ff', speed: a.volley ? 0.09 : 0.06, up: 0.04, size: 0.05, life: 4, gravity: 0.01 });
      if (a.volley) {
        if (!low) this.vfx.burst(p, { n: 2, color: '#ff6a4a', speed: 0.07, up: 0.05, size: 0.06, life: 5, gravity: 0.008 });
        this.vfx.burst(this.s.set(p.x, p.y - 0.45, p.z), { n: low ? 1 : 2, color: '#6d6458', speed: 0.025, up: 0.02, size: 0.24, life: 8, smoke: true });
      }
    }
  }

  /**
   * A warrior's blow cleaves (WARRIOR.cleave): a flat sweep of steel light across its target at the cleave's
   * reach, and a spark on each nightmare within it that takes its share (a few at most).
   */
  private cleave(s: BattleState, to: number, hx: number, hy: number, tx: number, ty: number, b: THREE.Vector3): void {
    const R = WARRIOR.cleave.radius;
    const low = this.opts.quality === 'low';
    // The sweep starts on the side the blow came from and swings across.
    const yaw = Math.atan2(-(ty - hy), tx - hx) + 1.1;
    this.skillFx.cleave(b.clone().setY(b.y - 0.05), (R / 1000) * WORLD_PER_HEX, yaw);
    let n = 0;
    for (const o of s.enemies) {
      if (o.hp <= 0 || o.id === to) continue;
      const dx = o.x - tx, dy = o.y - ty;
      if (dx * dx + dy * dy > R * R) continue;
      if (n++ >= (low ? 2 : 5)) break;
      const q = this.positions.get(o.id);
      if (!q) continue;
      this.vfx.burst(this.v.set(q.x, q.y + 0.5, q.z), { n: low ? 1 : 2, color: '#eef4ff', speed: 0.06, up: 0.03, size: 0.07, life: 3 });
      this.skillFx.flash(this.v, CLEAVE_EDGE, 0.3, 0.1);
    }
  }

  /** Sim positions of the clockwork turrets this sync (their shots draw as darts). */
  private clockAt = new Set<string>();
  /** Where a tower with a branch that throws its own shot stands (sim x,y of its cell) and its branch. */
  private branchAt = new Map<string, string>();

  /** Pines on the ring just outside the clearing (not on the paths): where a forest ambush comes from. */
  private buildWoods(map: MapDef, seed: number): void {
    const onPath = new Set(map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
    const trunks: THREE.Matrix4[] = [];
    const crowns: THREE.Matrix4[] = [];
    let k = seed >>> 0;
    const rnd = (): number => {
      k = (Math.imul(k ^ (k >>> 15), 2246822519) + 0x9e3779b9) >>> 0;
      return k / 4294967296;
    };
    for (const ring of [map.bastion + 1, map.bastion + 2]) {
      for (const { q, r } of hexRing(ring)) {
        if (onPath.has(`${q},${r}`) || rnd() < (ring === map.bastion + 1 ? 0.15 : 0.45)) continue;
        const c = hexToWorld(q, r);
        const p = simToWorld(c.x, c.y);
        p.x += (rnd() - 0.5) * 0.5;
        p.z += (rnd() - 0.5) * 0.5;
        const h = 0.9 + rnd() * 0.7;
        this.q.setFromAxisAngle(this.yAxis, rnd() * 6.28);
        trunks.push(new THREE.Matrix4().compose(p.clone().setY(0.18), this.q, new THREE.Vector3(0.09, 0.36, 0.09)));
        crowns.push(new THREE.Matrix4().compose(p.clone().setY(0.36 + h * 0.45), this.q, new THREE.Vector3(0.42, h, 0.42)));
        if (rnd() < 0.5) crowns.push(new THREE.Matrix4().compose(p.clone().setY(0.36 + h * 0.85), this.q, new THREE.Vector3(0.3, h * 0.7, 0.3)));
      }
    }
    const add = (geo: THREE.BufferGeometry, mat: THREE.Material, list: THREE.Matrix4[]): void => {
      const im = new THREE.InstancedMesh(geo, mat, list.length);
      list.forEach((m, i) => im.setMatrixAt(i, m));
      im.instanceMatrix.needsUpdate = true;
      this.root.add(im);
    };
    add(new THREE.CylinderGeometry(0.5, 0.7, 1, 6), new THREE.MeshStandardMaterial({ color: '#4a3220', roughness: 1, flatShading: true }), trunks);
    add(new THREE.ConeGeometry(1, 1, 7), new THREE.MeshStandardMaterial({ color: '#2f4a2a', roughness: 0.95, flatShading: true }), crowns);
  }

  /** Dark crags veined with rust-red ore on the rings just outside the clearing (not on the paths), round an iron mine. */
  private buildCrags(map: MapDef, seed: number): void {
    const onPath = new Set(map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
    const rocks: THREE.Matrix4[] = [];
    const dark: THREE.Matrix4[] = [];
    const ore: THREE.Matrix4[] = [];
    const glint: THREE.Matrix4[] = [];
    let k = (seed ^ 0x5bd1e995) >>> 0;
    const rnd = (): number => {
      k = (Math.imul(k ^ (k >>> 15), 2246822519) + 0x9e3779b9) >>> 0;
      return k / 4294967296;
    };
    const e = new THREE.Euler();
    for (const ring of [map.bastion + 1, map.bastion + 2]) {
      for (const { q, r } of hexRing(ring)) {
        if (onPath.has(`${q},${r}`) || rnd() < (ring === map.bastion + 1 ? 0.35 : 0.5)) continue;
        const c = hexToWorld(q, r);
        const p = simToWorld(c.x, c.y);
        const n = 1 + Math.floor(rnd() * 3);
        for (let i = 0; i < n; i++) {
          const s = 0.22 + rnd() * (ring === map.bastion + 1 ? 0.25 : 0.4);
          const at = p.clone().add(new THREE.Vector3((rnd() - 0.5) * 0.7, s * 0.35, (rnd() - 0.5) * 0.7));
          this.q.setFromEuler(e.set(rnd() * 3, rnd() * 3, rnd() * 3));
          (rnd() < 0.4 ? dark : rocks).push(new THREE.Matrix4().compose(at, this.q, new THREE.Vector3(s, s * (0.7 + rnd() * 0.5), s)));
          // A rust vein and a glint of iron breaking the surface.
          for (let j = 0; j < 2; j++) {
            const a = rnd() * TAU;
            const v = at.clone().add(new THREE.Vector3(Math.cos(a) * s * 0.85, s * (0.1 + rnd() * 0.4), Math.sin(a) * s * 0.85));
            this.q.setFromEuler(e.set(rnd() * 3, rnd() * 3, 0));
            (j ? glint : ore).push(new THREE.Matrix4().compose(v, this.q, new THREE.Vector3(1, 1, 1).multiplyScalar(s * (0.18 + rnd() * 0.12))));
          }
        }
      }
    }
    const add = (geo: THREE.BufferGeometry, mat: THREE.Material, list: THREE.Matrix4[]): void => {
      if (list.length === 0) return;
      const im = new THREE.InstancedMesh(geo, mat, list.length);
      list.forEach((m, i) => im.setMatrixAt(i, m));
      im.instanceMatrix.needsUpdate = true;
      this.root.add(im);
    };
    const geo = new THREE.DodecahedronGeometry(1, 0);
    add(geo, new THREE.MeshStandardMaterial({ color: '#4e4c4c', roughness: 0.95, flatShading: true }), rocks);
    add(geo, new THREE.MeshStandardMaterial({ color: '#3a3838', roughness: 0.95, flatShading: true }), dark);
    add(geo, new THREE.MeshStandardMaterial({ color: '#74412c', roughness: 0.85, flatShading: true }), ore);
    add(geo, new THREE.MeshStandardMaterial({ color: '#4a4c52', roughness: 0.3, metalness: 0.85, flatShading: true }), glint);
  }

  /** A forest ambush is foretold: the spot shakes and glows for a moment before they burst out. */
  private drawAmbush(s: BattleState): void {
    const w = s.wave;
    for (let i = w.next; i < w.spawns.length && i < w.next + 40; i++) {
      const sp = w.spawns[i] as (typeof w.spawns)[number];
      if (!sp.at) continue;
      const lead = sp.tick - s.waveT;
      if (lead > 24) break;
      const c = hexToWorld(sp.at.q, sp.at.r);
      const p = simToWorld(c.x, c.y, new THREE.Vector3(), 0.06);
      const pulse = 0.55 + ((s.tick + i) % 4) * 0.12;
      this.entityGlow.add(p.x, p.y, p.z, RED, 1.8 * pulse, 0.75);
      if ((s.tick + i) % 4 === 0) this.vfx.ring(p, 0.1, 0.62, '#ff4f3a', 6, 0.95);
      if ((s.tick + i) % 3 === 0) this.vfx.burst(p.clone().setY(0.5), { n: 3, color: '#4c6a32', speed: 0.05, up: 0.05, size: 0.14, life: 8, gravity: 0.004 });
    }
  }

  /**
   * A conversion glyph's type over whatever a skill left (docs/GLIFOS.md §1, the palette of PODERES §5): its glow
   * every frame and a few motes each tick, on top of the thing's own look, so a storm turned to fire reads as a storm
   * on fire. `r` is how far it reaches (world units); `k` staggers the motes between things.
   */
  private veil(s: BattleState, p: THREE.Vector3, r: number, dt: DamageType, k: number): void {
    const look = VEIL[dt];
    if (!look) return;
    this.entityGlow.add(p.x, p.y + 0.2, p.z, look.glow, Math.max(0.6, r * 1.3), 0.5);
    if (!this.fresh || (s.tick + k) % (this.opts.quality === 'low' ? 4 : 2) !== 0) return;
    const a = ((s.tick * 7 + k * 13) % 64) * (TAU / 64);
    const d = r * (0.25 + ((s.tick + k) % 5) * 0.15);
    const at = p.clone().add(this.v.set(Math.cos(a) * d, 0.1, Math.sin(a) * d));
    if (dt === 'fire') this.vfx.burst(at, { n: 3, color: '#ff8a3a', speed: 0.025, up: 0.07, size: 0.2, life: 12, gravity: -0.002 });
    else if (dt === 'frost') this.vfx.burst(at, { n: 1, color: '#d8f2ff', speed: 0.01, up: 0.015, size: 0.32, life: 12, smoke: true, spread: 0.4 });
    else if (dt === 'lightning') this.vfx.beam(at, at.clone().add(this.v.set(Math.cos(a + 2) * 0.6, 0.5, Math.sin(a + 2) * 0.6)), '#dfe8ff', 2, 0.02, 0.9);
    else if (dt === 'poison') this.vfx.burst(at, { n: 1, color: '#7fd06a', speed: 0.015, up: 0.02, size: 0.3, life: 14, smoke: true, spread: 0.4 });
    else this.vfx.burst(at, { n: 1, color: look.mote, speed: 0.015, up: 0.025, size: 0.22, life: 12, smoke: dt === 'physical' || dt === 'shadow', spread: 0.4 });
  }

  /** Ground a glyph turned (docs/GLIFOS.md §1), drawn as its new type: glowing ground, a ring each pulse and its motes. */
  private turnedGround(s: BattleState, z: Zone, p: THREE.Vector3, dt: DamageType): void {
    const look = VEIL[dt];
    if (!look) return;
    const rr = Math.max(0.6, (z.radius / 1000) * WORLD_PER_HEX);
    this.entityGlow.add(p.x, p.y + 0.15, p.z, look.glow, rr * 1.5, 0.45);
    if (!this.fresh) return;
    if (z.every > 0 && z.ticks % z.every === 0) this.vfx.ring(p, rr * 0.2, rr, look.mote, 8, 0.6);
    const n = this.opts.quality === 'low' ? 1 : 3;
    for (let i = 0; i < n; i++) this.veil(s, p, rr, dt, z.id + i * 7);
    // Lightning crackles across its ground now and then.
    if (dt === 'lightning' && s.tick % 3 === 0) {
      const a = Math.random() * TAU;
      const d = Math.sqrt(Math.random()) * rr;
      const hit = p.clone().add(this.v.set(Math.cos(a) * d, 0, Math.sin(a) * d));
      this.vfx.beam(hit.clone().setY(hit.y + 3), hit, '#e6eeff', 2, 0.03, 0.8);
      this.vfx.ring(hit, 0.1, 0.6, '#cfe0ff', 4, 0.8);
    }
  }

  private drawZones(s: BattleState, use: (k: string) => ModelBatch): void {
    this.lastZones = s.zones;
    // The unique pieces' forms on what carries them (formfx.ts): the beam's frost, the iron plates, the moss, the waiting sun…
    this.formFx.units(s, this.fresh, this.now);
    this.ascuas?.zones(s.zones);
    this.obsidian?.zones(s.zones);
    for (const z of s.zones) {
      const p = simToWorld(z.x, z.y, new THREE.Vector3(), this.heightAt(z.x, z.y) + 0.05);
      // Turned by a glyph: ground whose whole look is its type (a storm, a blizzard, a plague…) is drawn as ground of the
      // new type instead; the rest keep their own look under the new type's veil (a wall, on the hex the motes are on).
      if (z.dt && TYPED_GROUND.has(z.kind) && !z.cells.length) {
        this.turnedGround(s, z, p, z.dt);
        continue;
      }
      if (z.dt) {
        if (z.cells.length) {
          const R = this.map.radius;
          const W = 2 * R + 1;
          const cell = z.cells[s.tick % z.cells.length] as number;
          const c = hexToWorld((cell % W) - R, Math.floor(cell / W) - R);
          this.veil(s, simToWorld(c.x, c.y, new THREE.Vector3(), this.heightAt(c.x, c.y) + 0.1), 0.5, z.dt, z.id);
        } else this.veil(s, p, Math.max(0.5, (z.radius / 1000) * WORLD_PER_HEX), z.dt, z.id);
      }
      // The unique pieces' forms (formfx.ts): their own ground, and their marks over a skill's zone.
      if (z.fm) this.formFx.mark(s, z, p, this.fresh, this.now);
      if (FORM_ZONES.has(z.kind)) {
        this.formFx.zone(s, z, p, this.fresh, this.now);
        continue;
      }
      if (z.kind === 'glass' || z.kind === 'shards') {
        this.drawGlass(s, z, p);
      } else if (z.kind === 'lava') {
        this.drawLava(s, z, p);
      } else if (z.kind === 'wyrmfire') {
        this.drawWyrmfire(s, z, p);
      } else if (z.kind === 'nightfall') {
        this.drawNightfall(s, z, p);
      } else if (z.kind === 'briar') {
        this.drawBriar(s, z, p);
      } else if (z.kind === 'unBlast') {
        this.drawUnBlast(s, z, p);
      } else if (z.kind === 'vent') {
        this.drawVent(s, z, p);
      } else if (z.kind === 'eruption') {
        this.drawEruption(s, z, p);
      } else if (z.kind === 'grenade') {
        this.drawGrenade(s, z, p, use);
      } else if (z.kind === 'lime') {
        this.drawLime(s, z, p);
      } else if (z.kind === 'orb' || z.kind === 'totem' || z.kind === 'hammer' || z.kind === 'mine' || z.kind === 'banner' || z.kind === 'blizzard' || z.kind === 'rampart' || z.kind === 'mortar' || z.kind === 'plague' || z.kind === 'coil' || z.kind === 'snare' || z.kind === 'meteors' || z.kind === 'fortify' || z.kind === 'rift' || z.kind === 'caltrops' || z.kind === 'flare' || z.kind === 'quake') {
        this.drawLearnedZone(s, z, p, use);
      } else if (z.kind === 'barricade') {
        // An iron barricade: steel plates on each hex, a cold glint.
        for (const cell of z.cells) {
          const R = this.map.radius;
          const W = 2 * R + 1;
          const c = hexToWorld((cell % W) - R, Math.floor(cell / W) - R);
          const cp = simToWorld(c.x, c.y, new THREE.Vector3(), this.heightAt(c.x, c.y) + 0.1);
          this.entityGlow.add(cp.x, cp.y + 0.4, cp.z, STEEL, 1.1, 0.4);
          if (this.fresh && (s.tick + cell) % 8 === 0) this.vfx.ring(cp, 0.3, 0.6, z.damage > 0 ? '#ff8a6e' : '#c4c8d2', 5, 0.4);
        }
      } else if (z.kind === 'thorns') {
        // A druid's thorn wall: dark green brambles on each hex, a thorn flicking up now and then.
        for (const cell of z.cells) {
          const R = this.map.radius;
          const W = 2 * R + 1;
          const c = hexToWorld((cell % W) - R, Math.floor(cell / W) - R);
          const cp = simToWorld(c.x, c.y, new THREE.Vector3(), this.heightAt(c.x, c.y) + 0.1);
          this.entityGlow.add(cp.x, cp.y + 0.1, cp.z, THORN_GREEN, 1.1, 0.45);
          if (this.fresh && (s.tick + cell) % 5 === 0) this.vfx.burst(cp.clone().setY(cp.y + 0.2), { n: 2, color: '#4f7a3a', speed: 0.04, up: 0.05, size: 0.08, life: 10, gravity: 0.006, spread: 0.4 });
        }
      } else if (z.kind === 'iceTrail') {
        // The pantry staff's frozen line (docs/PANOPLIA.md §10): a pale rime on each hex, no spikes; it fades as it melts.
        const fade = Math.min(1, z.ticks / 12);
        for (const cell of z.cells) {
          const R = this.map.radius;
          const W = 2 * R + 1;
          const c = hexToWorld((cell % W) - R, Math.floor(cell / W) - R);
          const cp = simToWorld(c.x, c.y, new THREE.Vector3(), this.heightAt(c.x, c.y) + 0.05);
          this.entityGlow.add(cp.x, cp.y, cp.z, ORB_FROST, 1.2, 0.3 * fade);
          if (this.fresh && (s.tick + cell) % 9 === 0) this.vfx.burst(cp.clone().setY(cp.y + 0.1), { n: 1, color: '#e8f7ff', speed: 0.01, up: 0.01, size: 0.2, life: 10, smoke: true, spread: 0.4 });
        }
      } else if (z.kind === 'drip') {
        // The distiller's drop on the ground: a small green gleam, hissing now and then.
        this.entityGlow.add(p.x, p.y + 0.03, p.z, ACID_GREEN, 0.55, 0.5);
        if (this.fresh && (s.tick + z.id) % 10 === 0) this.vfx.burst(this.v.set(p.x, p.y + 0.1, p.z), { n: 1, color: '#b5e06a', speed: 0.01, up: 0.02, size: 0.12, life: 8, smoke: true });
      } else if (z.kind === 'frost') {
        const spikes: THREE.Vector3[] = [];
        for (const cell of z.cells) {
          const R = this.map.radius;
          const W = 2 * R + 1;
          const q = (cell % W) - R;
          const r = Math.floor(cell / W) - R;
          const c = hexToWorld(q, r);
          const cp = simToWorld(c.x, c.y, new THREE.Vector3(), this.heightAt(c.x, c.y) + 0.1);
          if (s.tick % 6 === 0) this.vfx.burst(cp.clone().setY(cp.y + 0.2), { n: 1, color: '#cfe6f4', speed: 0.01, up: 0.015, size: 0.25, life: 10, smoke: true, spread: 0.5 });
          this.entityGlow.add(cp.x, cp.y + 0.1, cp.z, new THREE.Color('#9fdcff'), 1.1, 0.35);
          spikes.push(cp.clone().setY(cp.y - 0.1));
        }
        // The wall itself: spikes of ice out of each hex, shattering when it melts.
        this.skillFx.frost(z.id, spikes);
      } else if (z.kind === 'storm') {
        const rr = (z.radius / 1000) * WORLD_PER_HEX;
        this.skillFx.storm(z.id, p, rr);
        // Bolts out of the cloud, often: forked, a white flash and a scorch where each lands.
        if (this.fresh && s.tick % 2 === 0) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.sqrt(Math.random()) * rr;
          const hit = p.clone().add(new THREE.Vector3(Math.cos(a) * d, 0, Math.sin(a) * d));
          const top = this.skillFx.stormBolt(z.id) ?? hit.clone().setY(hit.y + 5);
          top.x += (hit.x - top.x) * 0.6;
          top.z += (hit.z - top.z) * 0.6;
          this.vfx.beam(top, hit, '#ffffff', 2, 0.035, 0.7);
          this.vfx.beam(top, hit, '#8fb4ff', 3, 0.015, 1.1);
          const mid = top.clone().lerp(hit, 0.45);
          const fork = mid.clone().add(new THREE.Vector3((Math.random() - 0.5) * 1.6, -1.2, (Math.random() - 0.5) * 1.6));
          this.vfx.beam(mid, fork, '#bcd2ff', 2, 0.015, 0.4);
          this.vfx.ring(hit, 0.1, 0.8, '#cfe0ff', 4, 0.9);
          this.vfx.burst(hit.clone().setY(hit.y + 0.1), { n: this.opts.quality === 'low' ? 3 : 7, color: '#eaf2ff', speed: 0.08, up: 0.07, size: 0.07, life: 5, gravity: 0.01 });
          this.flare(hit.clone().setY(hit.y + 0.4), '#bcd6ff', 1.6, 3);
        }
        // A cold wind under the cloud.
        if (s.tick % 3 === 0) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.random() * rr;
          this.vfx.burst(p.clone().add(new THREE.Vector3(Math.cos(a) * d, 0.3, Math.sin(a) * d)), { n: 1, color: '#4a5266', speed: 0.03, up: 0.005, size: 0.5, life: 14, smoke: true });
        }
        this.entityGlow.add(p.x, p.y + 2.5, p.z, new THREE.Color('#5a6aa0'), rr * 1.3, 0.22);
      } else if (z.kind === 'burn') {
        // Embers: the ground glows and flickers where the fireball fell.
        const rr = (z.radius / 1000) * WORLD_PER_HEX;
        this.entityGlow.add(p.x, p.y + 0.2, p.z, new THREE.Color('#ff6a2a'), rr * 1.4, 0.55 + Math.random() * 0.25);
        for (let i = 0; i < 2; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.random() * rr * 0.85;
          this.vfx.burst(p.clone().add(new THREE.Vector3(Math.cos(a) * d, 0.05, Math.sin(a) * d)), { n: 2, color: i ? '#ffb347' : '#ff5a1f', speed: 0.02, up: 0.05, size: 0.18, life: 9, gravity: -0.002 });
        }
      } else if (z.kind === 'sanctuary') {
        this.drawSanctuary(s, z, p);
      } else if (z.kind === 'bloom') {
        // The druid's grove: green light on the ground, leaves drifting up, a ring with every heal; briars darken it.
        const rr = (z.radius / 1000) * WORLD_PER_HEX;
        const briars = z.damage > 0;
        this.entityGlow.add(p.x, p.y + 0.15, p.z, new THREE.Color(briars ? '#4f7a3a' : '#7fc06a'), rr * 1.5, 0.3);
        for (let i = 0; i < 2; i++) {
          const a = Math.random() * TAU;
          const d = Math.sqrt(Math.random()) * rr * 0.92;
          this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * d, 0.05, Math.sin(a) * d)), { n: 1, color: i ? '#b3f59a' : '#6fa05a', speed: 0.006, up: 0.025, size: 0.12, life: 18, gravity: -0.002, spread: 0.05 });
        }
        if (z.every > 0 && z.ticks % z.every === 0) this.vfx.ring(p, rr * 0.15, rr, briars ? '#8a9a5a' : '#b3f59a', 8, 0.6);
      } else if (z.kind === 'hallow') {
        // Hallowed ground: a pale disc of light, motes rising off it, a ring sweeping out each pulse.
        const rr = (z.radius / 1000) * WORLD_PER_HEX;
        this.entityGlow.add(p.x, p.y + 0.1, p.z, new THREE.Color('#dfe8ff'), rr * 1.6, 0.28);
        if (s.tick % z.every === 0) this.vfx.ring(p, rr * 0.2, rr, '#eef2ff', 8, 0.6);
        if (s.tick % 2 === 0) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.random() * rr * 0.9;
          this.vfx.burst(p.clone().add(new THREE.Vector3(Math.cos(a) * d, 0.05, Math.sin(a) * d)), { n: 1, color: '#ffffff', speed: 0.01, up: 0.06, size: 0.12, life: 12, gravity: -0.002 });
        }
      } else if (z.kind === 'spores') {
        // The spore cloud, once the roots let go.
        if ((z.warn ?? 0) <= 0 && s.tick % 3 === 0) {
          const rr = (z.radius / 1000) * WORLD_PER_HEX;
          const a = Math.random() * Math.PI * 2;
          const d = Math.random() * rr * 0.8;
          this.vfx.burst(p.clone().add(new THREE.Vector3(Math.cos(a) * d, 0.3, Math.sin(a) * d)), { n: 2, color: '#c9d77a', speed: 0.015, up: 0.01, size: 0.45, life: 18, smoke: true });
        }
      } else if (z.kind === 'rain') {
        // Real arrows falling into a marked circle, standing in the ground a moment (skillfx.ts).
        this.skillFx.rain(z.id, p, (z.radius / 1000) * WORLD_PER_HEX);
      }
    }
  }

  /* ---------- the day's omen, seen (omens/) ---------- */

  private omen: OmenScene | null = null;
  private omenTried = false;

  /** What an omen (or a side saga's village) gets from this view. */
  private omenCtx(): OmenCtx {
    const k = WORLD_PER_HEX / 1000;
    const sky = this.sky;
    return {
      scene: this.scene,
      crypt: !!this.crypt,
      low: this.opts.quality === 'low',
      crowd: false,
      hemi: sky?.hemi ?? this.cryptSky.hemi,
      key: sky?.key ?? this.cryptSky.moon,
      rim: sky?.rim ?? null,
      fogBank: this.terrain.fog,
      vfx: this.vfx,
      flare: (p, color, size, life) => this.flare(p, color, size, life),
      shake: (a) => {
        this.shake = Math.max(this.shake, a);
      },
      ground: (x, z) => this.heightAt(x / k, z / k),
      pos: (id) => this.positions.get(id),
      at: (x, y, h = 0) => simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y) + h),
    };
  }

  private startOmen(id: string, crowd = false): void {
    this.omenTried = true;
    this.omen = buildOmen(id, { ...this.omenCtx(), crowd }, (this.map.radius + 2) * WORLD_PER_HEX);
    if (this.omen) this.scene.add(this.omen.group);
  }

  /* ---------- a side saga's place and clues (omens/village.ts, engine/clues.ts) ---------- */

  private village: OmenScene | null = null;
  private villageTried = false;
  /** One alone (a bet): Brann's stakes at the open road's mouth and the other roads shut, one static mesh. */
  private stakes: THREE.Mesh | null = null;
  private stakesTried = false;
  private clueMarks: ClueMarks | null = null;
  private crowd: CrowdLayer | null = null;
  /** Caras prestadas 4: the lanterns on the field, the paper off the Prestacaras, whose name it wears. */
  private facetaker: FacetakerLayer | null = null;
  private facetakerAt: { e: Enemy; p: THREE.Vector3; top: number } | null = null;
  private rescueLayer: RescueLayer | null = null;
  private herdLayer: HerdLayer | null = null;
  private lashLayer: LashLayer | null = null;
  private huntLayer: HuntLayer | null = null;
  private collapseLayer: CollapseLayer | null = null;
  private trapLayer: TrapLayer | null = null;
  private dragonField: DragonFieldLayer | null = null;
  private blastLayer: BlastLayer | null = null;
  private mine: MineLayer | null = null;
  private halls: HallsLayer | null = null;
  private anvilHall: AnvilLayer | null = null;
  private forgeYard: ForgeLayer | null = null;
  private beside: BesideLayer | null = null;
  private race: RaceLayer | null = null;
  private openGate: OpenGateLayer | null = null;
  private wellSquare: WellLayer | null = null;
  private fairSquare: FairLayer | null = null;
  private shore: ShoreLayer | null = null;
  private finCoast: FinLayer | null = null;
  private sunken: SunkenLayer | null = null;
  private lighthouse: LighthouseLayer | null = null;
  private pipes: PipesLayer | null = null;
  private ribs: RibsLayer | null = null;
  private neck: NeckLayer | null = null;
  private orderPost: OrderPostLayer | null = null;
  private orderBell: BellLayer | null = null;
  private bellNight: BellNightLayer | null = null;
  /** The hero swinging the hammer at the Anvil (Las vías 3) and the tick it struck: drawn mid-blow for a moment. */
  private swing: { id: number; tick: number } | null = null;

  /** Las vías 3: the board re-dressed as the Deep Anvil's hall. */
  private startAnvil(r: NonNullable<NonNullable<BattleState['rules']>['anvil']>): void {
    const layer = new AnvilLayer(this.omenCtx(), this.map, r);
    layer.apply(this.scene, this.terrain, this.sky, this.root.children.filter((o) => (o as THREE.InstancedMesh).isInstancedMesh));
    this.scene.add(layer.group);
    this.anvilHall = layer;
  }

  /**
   * Who swung at the Anvil: off the beat, the hero where the clank was; on it (the event doesn't say), the standing hero
   * by the Anvil nearest it.
   */
  private anvilSwing(s: BattleState, miss: { x: number; y: number } | null): void {
    const reach = s.rules?.anvil?.reach ?? s.rules?.toll?.reach ?? 0;
    let best: Hero | undefined;
    let bd = Infinity;
    for (const h of s.heroes) {
      if (h.downTicks > 0) continue;
      const d = miss ? (h.x - miss.x) ** 2 + (h.y - miss.y) ** 2 : h.x * h.x + h.y * h.y;
      if (d < bd && (miss ? d === 0 : d <= reach * reach)) {
        bd = d;
        best = h;
      }
    }
    if (best) this.swing = { id: best.id, tick: s.tick };
  }

  /** El pozo 1: Musgoviejo's square (over the village's night): the well, its houses, the mill and the stone. */
  private startWell(r: NonNullable<NonNullable<BattleState['rules']>['well']>): void {
    const layer = new WellLayer(this.omenCtx(), r);
    this.scene.add(layer.group);
    this.wellSquare = layer;
  }

  /** La Orilla 1: the stilt-houses, the causeway and the reef; the sea floor repainted on the terrain. */
  private startShore(r: NonNullable<NonNullable<BattleState['rules']>['tide']>): void {
    const layer = new ShoreLayer(this.omenCtx(), this.map, r);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.shore = layer;
  }

  /** La Orilla 2: the stilt-houses with the sea in, the three lanes and their posts, the fin far out; wet rock past the rim. */
  private startFin(r: NonNullable<NonNullable<BattleState['rules']>['fin']>, mode: 'wave' | 'breath' | 'big' = 'wave'): void {
    const layer = new FinLayer(this.omenCtx(), this.map, r, mode);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.finCoast = layer;
  }

  /** La Orilla 5: the dragon's heart, Yorra's boats, its breath's band and the harpoon's rope, over the fin's coast. */
  private startSunken(r: NonNullable<NonNullable<BattleState['rules']>['sunken']>): void {
    const layer = new SunkenLayer(this.omenCtx(), r, this.finCoast?.seaY ?? 0);
    this.scene.add(layer.group);
    this.sunken = layer;
  }

  /** La Orilla 3: the lighthouse at the fin's tip, its braziers, the sea past the rim and the Maw's stalk. */
  private startLighthouse(r: NonNullable<NonNullable<BattleState['rules']>['maw']>): void {
    const layer = new LighthouseLayer(this.omenCtx(), this.map, r);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.lighthouse = layer;
  }

  /** El pozo 3: fair night in Musgoviejo's square: the shut well with its bunting, the stalls, Fenn's cart, the wares. */
  private startFair(r: NonNullable<NonNullable<BattleState['rules']>['fair']>): void {
    const layer = new FairLayer(this.omenCtx(), r);
    this.scene.add(layer.group);
    this.fairSquare = layer;
  }

  /** El pozo 2: the crypt dressed as the waterworks under Musgoviejo's well (its vein last), and the water coming back. */
  private startPipes(): void {
    const c = this.crypt;
    if (!c) return;
    const layer = new PipesLayer(this.omenCtx(), this.map, c, this.cryptSky.rooms);
    this.scene.add(layer.group);
    this.pipes = layer;
  }

  /** La Orilla 4: the crypt dressed as the dragon's bones on the sea floor; with `up`, the way back with the sea coming in. */
  private startRibs(up: boolean): void {
    const c = this.crypt;
    if (!c) return;
    const layer = new RibsLayer(this.omenCtx(), this.map, c, this.cryptSky.rooms, up);
    this.scene.add(layer.group);
    this.ribs = layer;
  }

  /** El Párpado 1: the crypt dressed as the titan's neck, a gorge of its hide whose folds breathe. */
  private startNeck(): void {
    const c = this.crypt;
    if (!c) return;
    const layer = new NeckLayer(this.omenCtx(), this.map, c, this.cryptSky.rooms);
    this.scene.add(layer.group);
    this.neck = layer;
  }

  /** El Párpado 1 and 2: the board re-dressed as the titan's head (the Order's ruined post on it, or the slope below), the eye beyond. */
  private startOrderPost(mode: 'post' | 'slope', night: boolean, winches: { x: number; y: number }[]): void {
    const layer = new OrderPostLayer(this.omenCtx(), this.map, mode, night, winches);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.orderPost = layer;
  }

  /** The warrior's path: the board re-dressed as a place of the Spine's north face (engine/spine.ts). */
  private spine: SpineLayer | null = null;
  private guardhouse: GuardhouseCrypt | null = null;
  private startSpine(s: BattleState): void {
    const layer = new SpineLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, this.cinema ? 'dawn' : s.rules?.night ? 'night' : 'day');
    this.scene.add(layer.group);
    this.spine = layer;
  }

  /** The Way of the Altars: the board re-dressed as a place of the paladin's path (engine/altars.ts). */
  private altars: AltarsLayer | null = null;
  private startAltars(s: BattleState): void {
    const layer = new AltarsLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, this.cinema ? 'dawn' : s.rules?.night ? 'night' : s.rules?.place === 'glassShore' ? 'dawn' : s.rules?.place === 'watchtower' ? 'dusk' : 'day');
    this.scene.add(layer.group);
    this.altars = layer;
  }

  /** Nadd's workshop: the board re-dressed as a place of the artificer's path (engine/taller.ts). */
  private taller: TallerLayer | null = null;
  private startTaller(s: BattleState): void {
    const layer = new TallerLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    // (The goat path at dusk, the rest at night or inside the rock: the scenes repaint it by their sky.)
    layer.apply(this.terrain, s.rules?.place === 'cornice' ? 'dusk' : 'night');
    this.scene.add(layer.group);
    this.taller = layer;
  }

  /** The burnt library (the mage's chapter 1): the board re-dressed as a place of it (engine/biblio.ts). */
  private biblio: BiblioLayer | null = null;
  private startBiblio(s: BattleState): void {
    const layer = new BiblioLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, s.rules?.place === 'labYard' ? 'dusk' : s.rules?.place === 'pier' ? 'dawn' : 'night');
    this.scene.add(layer.group);
    this.biblio = layer;
  }

  /** The cable (the artificer's chapter 2): the board re-dressed as a place of it (engine/cable.ts). */
  private cable: CableLayer | null = null;
  private startCable(s: BattleState): void {
    const layer = new CableLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    // (The crane by day, the car crossing at dusk with Grada's windows lit, the winch house in the morning wind.)
    const p = s.rules?.place;
    layer.apply(this.terrain, p === 'gondola' ? 'dusk' : p === 'cableNight' ? 'night' : 'day');
    this.scene.add(layer.group);
    this.cable = layer;
  }

  /** The heart (the artificer's chapter 3): the board re-dressed as a place of the Hondura (engine/corazon.ts). */
  private corazon: CorazonLayer | null = null;
  private startCorazon(s: BattleState): void {
    const layer = new CorazonLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.corazon = layer;
  }

  /** Aster's stones (the ranger's chapter 1): the board re-dressed as the rim of the Furrows, the fog, the slope (engine/aster.ts). */
  private asterL: AsterLayer | null = null;
  private startAster(s: BattleState): void {
    const layer = new AsterLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.asterL = layer;
  }

  /** Tormo (the artificer's chapter 5): the board re-dressed as his hall, the winch's ledge, the workshop's yard, Grada (engine/tormo.ts). */
  private tormoL: TormoLayer | null = null;
  private startTormo(s: BattleState): void {
    const layer = new TormoLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.tormoL = layer;
    // The board's fire: a lamp in the hall, the forge's glow in the yard, nothing on the cliffs by day.
    const f = this.lights[0];
    if (f instanceof THREE.PointLight) {
      if (layer.kind === 'knees') {
        f.color.set('#ffb07a');
        f.position.set(0, 3.4, 0);
      } else if (layer.kind === 'torno' || layer.kind === 'raja') f.intensity = 0;
      else {
        // (Low: the forge's warm light turned the yard's pale stone pink.)
        f.color.set('#ffb48a');
        f.intensity *= 0.45;
        f.position.set(-6, 1.6, -4);
      }
    }
  }

  /** The eyes (the artificer's chapter 4): the board re-dressed as Grada's ledge, the ice mine, the drill hall (engine/ojos.ts). */
  private ojos: OjosLayer | null = null;
  private startOjos(s: BattleState): void {
    const layer = new OjosLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, 'night');
    this.scene.add(layer.group);
    this.ojos = layer;
  }

  /** The Long Hunt: the board re-dressed as a place of the barbarian's path (engine/caza.ts). */
  private caza: CazaLayer | null = null;
  private startCaza(s: BattleState): void {
    const layer = new CazaLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.caza = layer;
  }

  /** The Vertebrae (the barbarian's chapter 2): the board re-dressed as the arches over the sea (engine/vertebras.ts). */
  private vert: VertLayer | null = null;
  private startVert(s: BattleState): void {
    const layer = new VertLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.vert = layer;
  }

  /** Bera's boat (the barbarian's chapter 3): the board re-dressed as the boat on the open sea (engine/bote.ts). */
  private bote: BoteLayer | null = null;
  private startBote(s: BattleState): void {
    const layer = new BoteLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.bote = layer;
  }

  /** The White Island (the barbarian's chapter 4): the board re-dressed as the island's ice and snow (engine/isla.ts). */
  private isla: IslaLayer | null = null;
  private startIsla(s: BattleState): void {
    const layer = new IslaLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.isla = layer;
  }

  /** The black rock (the barbarian's chapter 5): the board re-dressed as its wall, its cave, its den and summit (engine/roca.ts). */
  private roca: RocaLayer | null = null;
  private startRoca(s: BattleState): void {
    const layer = new RocaLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.roca = layer;
    // The keep's fire (the board's one point light) is no fire here: cold daylight down the roof's cracks in the caves,
    // nothing outside (it warmed the middle of every board pink).
    const f = this.lights[0];
    if (f instanceof THREE.PointLight) {
      const inside = s.rules?.place === 'rocaHall' || s.rules?.place === 'rocaBed';
      f.color.set('#b9cbe6');
      f.position.set(0, 6, 0);
      f.distance = 18;
      f.intensity = inside ? 9 : 0;
      this.seventhSteady = true;
    }
  }

  /** The black rock's ground under a sim point (world units; 0 off it): its ledges and summit stand high. */
  rocaHeight(x: number, y: number): number {
    return this.roca ? this.roca.heightAt(x, y) : 0;
  }

  /** The cleric's last road: the board re-dressed as a place of the cleric's path (engine/camino.ts). */
  private camino: CaminoLayer | null = null;
  private startCamino(s: BattleState): void {
    const layer = new CaminoLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, 'night');
    this.scene.add(layer.group);
    this.camino = layer;
    // The keep's fire (the board's one point light) becomes the stretcher's lantern: it goes with it and lights the
    // walls and the people as it passes (moving it compiles nothing; its count never changes).
    const f = this.lights[0];
    if (f instanceof THREE.PointLight) {
      layer.takeLight(f);
      this.brazierBase = layer.lightBase;
    }
  }

  /** The cleric's last road, chapter 2: the board put away for the cliff, the walkway and the bridge (engine/pasarela.ts). */
  private pasarela: PasarelaLayer | null = null;
  private startPasarela(s: BattleState): void {
    const layer = new PasarelaLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, 'night');
    this.scene.add(layer.group);
    this.pasarela = layer;
    // (The board's one point light goes with the stretcher's lantern, as in chapter 1.)
    const f = this.lights[0];
    if (f instanceof THREE.PointLight) {
      layer.takeLight(f);
      this.brazierBase = layer.lightBase;
    }
  }

  /** The cleric's last road, chapter 3: the col, the frozen mirrors and the corral on chapter 1's craft (engine/nieve.ts). */
  private nieve: NieveLayer | null = null;
  private startNieve(s: BattleState): void {
    const layer = new NieveLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, 'night');
    this.scene.add(layer.group);
    this.nieve = layer;
    // (The board's one point light goes with the stretcher's lantern, as in chapters 1 and 2.)
    const f = this.lights[0];
    if (f instanceof THREE.PointLight) {
      layer.takeLight(f);
      this.brazierBase = layer.lightBase;
    }
  }

  /** The cleric's last road, chapter 4: the Hermitage and its nave on chapter 1's craft (engine/ermita.ts). */
  private ermita: ErmitaLayer | null = null;
  private atrium: AtriumCrypt | null = null;
  private startErmita(s: BattleState): void {
    const pl = s.rules?.place as string;
    const layer = new ErmitaLayer(this.omenCtx(), this.map, pl, s);
    layer.apply(this.terrain, pl === 'ermitaDawn' || pl === 'ermitaDoor' || pl === 'navePast' || pl === 'naveDawn' || pl === 'ermitaTomb' ? 'dawn' : 'night');
    this.scene.add(layer.group);
    this.ermita = layer;
    const f = this.lights[0];
    if (f instanceof THREE.PointLight) {
      layer.takeLight(f);
      this.brazierBase = layer.lightBase;
    }
  }

  /** The Circle's wood (the druid's chapter 1): the board re-dressed as a place of the druid's path (engine/bosque.ts). */
  private bosque: BosqueLayer | null = null;
  private startBosque(s: BattleState): void {
    const layer = new BosqueLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, 'day');
    this.scene.add(layer.group);
    this.bosque = layer;
  }

  /** The Slope of the kilns (the druid's chapter 2): the board re-dressed as a place of the druid's path (engine/hornos.ts). */
  private hornos: HornosLayer | null = null;
  private startHornos(s: BattleState): void {
    const layer = new HornosLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, 'day');
    this.scene.add(layer.group);
    this.hornos = layer;
  }

  /** The autumn wood over Los Hornos (the druid's chapter 3): the board re-dressed as a place of the druid's path (engine/otono.ts). */
  private otono: OtonoLayer | null = null;
  private startOtono(s: BattleState): void {
    const layer = new OtonoLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, s.rules?.night ? 'night' : 'day');
    this.scene.add(layer.group);
    this.otono = layer;
  }

  /** The Grandsire's crown over the clouds (the druid's chapter 4): the board re-dressed as a place of the druid's path (engine/copa.ts). */
  private copa: CopaLayer | null = null;
  private startCopa(s: BattleState): void {
    const layer = new CopaLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, s.rules?.night ? 'night' : 'day');
    this.scene.add(layer.group);
    this.copa = layer;
  }

  /** A moment of the crown's scenes (the bough falling with Hob, the cure, the snow let go); and inside the Grandsire (the lanterns, the hands, the face carved). */
  copaCue(c: string): void {
    this.copa?.cue(c);
    this.raices?.cue(c);
  }

  /** Inside the Grandsire (the druid's chapter 5): the board re-dressed as a place of the druid's path (engine/raices.ts). */
  private raices: RaicesLayer | null = null;
  private bark: BarkCrypt | null = null;
  private raicesCine: { face?: boolean; breath?: boolean } = {};
  private startRaices(s: BattleState): void {
    const layer = new RaicesLayer(this.omenCtx(), this.map, s.rules?.place as string, s, this.raicesCine);
    layer.apply(this.terrain, s.rules?.place === 'grandsireFallen' ? 'dawn' : 'dusk');
    this.scene.add(layer.group);
    this.raices = layer;
  }

  /** Inside the Grandsire: its marks in world points (the HUD's offer). */
  raicesMarks(s: BattleState): THREE.Vector3[] {
    return this.raices?.markPoints(s) ?? [];
  }

  /** The test's knots in world points (the HUD's offer). */
  copaKnots(): THREE.Vector3[] {
    return this.copa?.knotPoints() ?? [];
  }

  /** The memory's root cells and the test's cells with roots, in world points (the HUD's cut offer). */
  otonoCuts(): THREE.Vector3[] {
    return this.otono?.cutPoints() ?? [];
  }

  /** The counterfire's patches in world points (the HUD's marks). */
  hornosPatches(): THREE.Vector3[] {
    return this.hornos?.patchPoints() ?? [];
  }

  /** The salt flats (the paladin's chapter 2): the board re-dressed as the flats (engine/salt.ts). */
  private salt: SaltLayer | null = null;
  private startSalt(s: BattleState): void {
    const layer = new SaltLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, s.rules?.place === 'saltNight' ? 'night' : s.rules?.place === 'saltFog' ? 'dusk' : 'day');
    this.scene.add(layer.group);
    this.salt = layer;
  }

  /** The salt flats' marks (the gates, the altar, the gate house's door), in world points; null off them. */
  saltAnchors(): SaltLayer['anchors'] | null {
    return this.salt?.anchors ?? null;
  }

  /** The bridge's altar (the paladin's chapter 3): the board re-dressed as the bridge in the rain (engine/arch.ts). */
  private arch: ArchLayer | null = null;
  private startArch(s: BattleState): void {
    const layer = new ArchLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain, s.rules?.place === 'rainNight' ? 'night' : 'dusk');
    this.scene.add(layer.group);
    this.arch = layer;
  }

  /** The monastery of the waterfall (the paladin's chapter 4): the board re-dressed as the monastery in its gorge (engine/monastery.ts). */
  private monastery: MonasteryLayer | null = null;
  private startMonastery(s: BattleState): void {
    const layer = new MonasteryLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.monastery = layer;
  }

  /** The seventh altar (the paladin's chapter 5): the keep's yard, the cistern under it, the keep's top (engine/seventh.ts). */
  private seventh: SeventhLayer | null = null;
  private roots: RootsCrypt | null = null;
  private startSeventh(s: BattleState): void {
    const layer = new SeventhLayer(this.omenCtx(), this.map, s.rules?.place as string, s);
    layer.apply(this.terrain);
    this.scene.add(layer.group);
    this.seventh = layer;
    // The keep's fire (the board's one point light) becomes the place's: a torch at the stair's mouth in the yard; the
    // moon down the old well over the island in the cistern (cold, steady); nothing on the keep's top in the morning.
    const f = this.lights[0];
    if (f instanceof THREE.PointLight) {
      const place = s.rules?.place;
      if (place === 'cistern7') {
        f.color.set('#a9bdf0');
        f.position.set(0, 4.2, 0);
        f.distance = 14;
        f.intensity = 16;
        this.seventhSteady = true;
      } else if (place === 'keepTop') {
        f.intensity = 0;
        this.seventhSteady = true;
      } else {
        f.color.set('#ff9a52');
        f.position.set(0, 2.2, -3.0);
        this.brazierBase = 9;
      }
    }
  }
  private seventhSteady = false;

  /** The seventh altar's marks (the heaps, the drains, the gates, the runnels, the altar, the island, the stair), in world points; null off it. */
  seventhAnchors(): SeventhLayer['anchors'] | null {
    return this.seventh?.anchors ?? null;
  }

  /** A moment of the seventh altar's scenes: `kneel` (the knights' fists on their shields, the water trembling), `eye` (the altar's eye opens). */
  seventhCue(c: 'kneel' | 'eye'): void {
    this.seventh?.cue(c);
    this.dirty = true;
  }

  /** The monastery's marks (the salt's spots, the cells' doors, the sacks, the gates, the altars, the cistern, the door), in world points; null off it. */
  monasteryAnchors(): MonasteryLayer['anchors'] | null {
    return this.monastery?.anchors ?? null;
  }

  /** The bridge's marks (the levers, the cistern, the ends), in world points; null off it. */
  archAnchors(): ArchLayer['anchors'] | null {
    return this.arch?.anchors ?? null;
  }

  /** The Way of the Altars' marks (the canals' stones, the pools, the altars), in world points; null off it. */
  altarAnchors(): AltarsLayer['anchors'] | null {
    return this.altars?.anchors ?? null;
  }

  /** Where a path's place hangs its marks (the ledge's gaps, rock, cornice and house), in world points; null off the Spine. */
  sendaAnchors(): SpineLayer['anchors'] | null {
    return this.spine?.anchors ?? null;
  }

  /** The cut bridge's marks (hooks, pans, posts, chains, the pile, the flag), in world points; null off the gorge. */
  chasmAnchors(): ChasmPlace['anchors'] | null {
    return this.spine?.chasm?.anchors ?? null;
  }

  /** The avalanche's marks (the large wedges' rocks, the cave's mouth, the ice cave's doors), in world points; null off it. */
  passAnchors(): PassPlace['anchors'] | null {
    return this.spine?.pass?.anchors ?? null;
  }

  /** The speech's names (the warrior's chapter 5): the shield in slot `n` (1 to 11) lit; -1, the close's whole line. */
  passLight(n: number): void {
    if (n < 0) this.spine?.pass?.closeLine();
    else this.spine?.pass?.lightSlot(n);
  }

  slideAnchors(): { rocks: THREE.Vector3[]; mouth: THREE.Vector3 | null; doors: THREE.Vector3[] } | null {
    return this.spine?.slide?.anchors ?? null;
  }

  /** The fort's marks (the eaves, hooks, pile, hearth, flag; the climb's torch and door), in world points; null off it. */
  fortAnchors(): FortPlace['anchors'] | null {
    return this.spine?.fort?.anchors ?? null;
  }

  /** The fort's roof tipping in a cinematic (Fermín's «¡Ahora!»), toward the far yard. */
  tipRoof(): void {
    const f = this.spine?.fort;
    if (f) f.startTip(-f.side);
  }

  /** The cut bridge's spans coming down together (its closing cinematic). */
  /**
   * A cinematic's camera way kept clear (the board's trees and rocks): what stands within `r` of the camera's path, or
   * of the first part of its look at the scene, is put away. A tree the camera flew through filled half the frame
   * with a dark block for seconds (the warrior's chapter 1, the wood's edge).
   */
  clearWay(segs: [THREE.Vector3, THREE.Vector3][], r: number): void {
    const m = new THREE.Matrix4();
    const p = new THREE.Vector3();
    const q = new THREE.Vector3();
    const zero = new THREE.Matrix4().makeScale(0, 0, 0);
    const line = new THREE.Line3();
    this.terrain.decor.traverse((o) => {
      const im = o as THREE.InstancedMesh;
      if (!im.isInstancedMesh) return;
      let touched = false;
      for (let i = 0; i < im.count; i++) {
        im.getMatrixAt(i, m);
        p.setFromMatrixPosition(m);
        for (const [a, b] of segs) {
          line.set(a, b);
          line.closestPointToPoint(p, true, q);
          // Trees: their height counts (a trunk under the way still pokes its crown into it).
          const dx = q.x - p.x;
          const dz = q.z - p.z;
          if (dx * dx + dz * dz < r * r && q.y < p.y + 3.5) {
            im.setMatrixAt(i, zero);
            touched = true;
            break;
          }
        }
      }
      if (touched) im.instanceMatrix.needsUpdate = true;
    });
  }

  lowerSpans(): void {
    this.spine?.chasm?.lowerSpans();
  }

  /** El Párpado 2: the Order's bell (on the climb, drawn in the caravan's place), with Ulla's boars or the winches. */
  private startBell(s: BattleState): void {
    const layer = new BellLayer(this.omenCtx(), this.map, s.rules?.toll ? 'toll' : s.rules?.winch ? 'hang' : 'climb', s.rules?.climb?.boars ?? 0, s.rules?.winch?.at ?? [], s.rules?.toll);
    this.scene.add(layer.group);
    this.orderBell = layer;
  }

  /** The night of the bell won at dawn: its last stroke rolls off the keep (false: not that fight). */
  bellFinale(): boolean {
    if (!this.bellNight?.dawn) return false;
    this.bellNight.finale();
    this.dirty = true;
    return true;
  }

  /** El Párpado 4: the bastion's night (dusk with the Hollow Shell, or dawn) and the allies coming in. */
  private startBellNight(s: BattleState): void {
    const layer = new BellNightLayer(this.omenCtx(), s.wave.spawns.some((x) => x.kind === 'shell'));
    this.scene.add(layer.group);
    this.bellNight = layer;
  }

  /** Las vías 4: the board re-dressed as the bastion's forge yard. */
  private startForge(r: NonNullable<NonNullable<BattleState['rules']>['rising']>): void {
    const layer = new ForgeLayer(this.omenCtx(), this.map, r);
    layer.apply(this.scene, this.terrain, this.sky, this.root.children.filter((o) => (o as THREE.InstancedMesh).isInstancedMesh));
    this.scene.add(layer.group);
    this.forgeYard = layer;
  }

  /** Las vías 2: the board re-dressed as Dorn's halls, the rooms' bells and levers. */
  private startHalls(r: NonNullable<NonNullable<BattleState['rules']>['bells']>): void {
    const layer = new HallsLayer(this.omenCtx(), this.map, r);
    layer.apply(this.scene, this.terrain, this.sky, this.root.children.filter((o) => (o as THREE.InstancedMesh).isInstancedMesh));
    this.scene.add(layer.group);
    this.halls = layer;
  }

  /** Las vías 1: the board re-dressed as the clan's station under the shell, its rail, train, bridge and counterweight. */
  private startMine(s: BattleState): void {
    const e = s.escort;
    const st = s.rails;
    if (!e || !st) return;
    const layer = new MineLayer(this.omenCtx(), this.map, e.road, st.bridge);
    layer.apply(this.scene, this.terrain, this.sky, this.root.children.filter((o) => (o as THREE.InstancedMesh).isInstancedMesh));
    this.scene.add(layer.group);
    this.mine = layer;
  }

  /** Where the train's head is drawn this frame (sim units; drawBots sets it from the escort's cart). */
  private trainAt = { x: 0, y: 0 };

  /** Las vías 1: the train (the escort's cart as the mine layer draws it), Hilde riding in the first cart, its life. */
  private drawTrain(s: BattleState, use: (k: string) => ModelBatch): void {
    const mine = this.mine as MineLayer;
    const b = s.bots?.find((o) => o.id === s.escort?.cart);
    const left = (s.rules?.rails?.carts ?? 0) - (s.rails?.derailed ?? 0);
    mine.draw(s, this.trainAt.x, this.trainAt.y, s.escort?.next ?? 1, b && b.hp > 0 ? left : 0);
    if (!mine.shown || !b) return;
    const p = mine.head;
    this.positions.set(b.id, p.clone());
    this.shadows.add(p.x, p.y - 0.1, p.z, 0.9);
    const hb = use('npc:hilde');
    const hs = this.lib.scaleOf('npc:hilde') * 0.82;
    // Leaning into the run, a sway with the rattle.
    this.compose(this.v.set(p.x, p.y + 0.05, p.z), mine.headYaw + Math.sin(this.now * 2.1) * 0.08, hs);
    hb.add(this.m, 0, this.flashes.has(b.id) ? FLASH : undefined);
    const k = b.hp / b.maxHp;
    this.bars.add(this.v.copy(p).setY(p.y + this.lib.heightOf('npc:hilde') * hs + 0.2), k, k > 0.35 ? GREEN : RED, 0.9);
  }

  /** La Quemadura 4: the Burn's glass and dusk, a few pieces of the old camp clear of the cracks, the tower and its powder. */
  private startBlast(s: BattleState, r: NonNullable<NonNullable<BattleState['rules']>['blast']>): void {
    if (!this.obsidian) this.startObsidian();
    this.burnDusk();
    const cracks = r.cracks.map((c) => simToWorld(c.x, c.y, new THREE.Vector3()));
    const stack = simToWorld(r.stack.x, r.stack.y, new THREE.Vector3());
    // Clear of the tower, its stack and the cracks (the nightmares climb out of them).
    const avoid = (p: THREE.Vector3): boolean => p.x * p.x + p.z * p.z < 9 || p.distanceTo(stack) < 2 || cracks.some((c) => p.distanceTo(c) < 2.6);
    if (!this.villageTried) {
      this.villageTried = true;
      this.village = buildCamp(this.omenCtx(), this.map, [r.stack, ...r.cracks], { sparse: true, avoid });
      this.scene.add(this.village.group);
    }
    const layer = new BlastLayer(this.omenCtx(), r, (pid) => s.heroes.find((h) => h.pid === pid)?.id);
    this.scene.add(layer.group);
    this.blastLayer = layer;
  }

  /** The dragon's clearing: the Burn's glass and dusk, and its own layer (engine/dragonfield.ts). */
  private startDragonField(r: NonNullable<NonNullable<BattleState['rules']>['dragon']>): void {
    if (!this.obsidian) this.startObsidian();
    this.burnDusk();
    const layer = new DragonFieldLayer(this.omenCtx(), r);
    this.scene.add(layer.group);
    this.dragonField = layer;
  }

  /** La Quemadura 3: the Burn's glass and dusk, a few pieces of the camp off the harpoon's line, and the engine. */
  private startTrap(r: NonNullable<NonNullable<BattleState['rules']>['trap']>): void {
    if (!this.obsidian) this.startObsidian();
    this.burnDusk();
    const at = simToWorld(r.at.x, r.at.y, new THREE.Vector3());
    const dir = new THREE.Vector3(r.ax, 0, r.ay).normalize();
    const k = WORLD_PER_HEX / 1000;
    const half = r.half * k + 1.4;
    // Off the line (and a little behind the engine) and off the winches and the lever.
    const avoid = (p: THREE.Vector3): boolean => {
      const d = p.clone().sub(at);
      const along = d.dot(dir);
      const off = Math.abs(d.x * dir.z - d.z * dir.x);
      return along > -2.5 && along < r.len * k + 1.5 && off < half;
    };
    if (!this.villageTried) {
      this.villageTried = true;
      this.village = buildCamp(this.omenCtx(), this.map, [r.at, r.lever, ...r.winches], { sparse: true, avoid });
      this.scene.add(this.village.group);
    }
    const layer = new TrapLayer(this.omenCtx(), r);
    this.scene.add(layer.group);
    this.trapLayer = layer;
  }

  /**
   * La Quemadura 1: the board glassed as the Burn's edge (the level III glass, its dusk turned to embers), and the
   * collapse layer: every glassed surface drops away past the break (see collapse.ts), the patrol on it.
   */
  private startCollapse(r: NonNullable<NonNullable<BattleState['rules']>['collapse']>): void {
    if (!this.obsidian) this.startObsidian();
    const layer = new CollapseLayer(this.omenCtx(), r, (this.map.radius - 1) * WORLD_PER_HEX);
    const t = this.terrain;
    layer.sink(t.mesh, true);
    t.decor.traverse((o) => {
      if (o.name === 'outskirts-ground') layer.sink(o, true);
    });
    layer.sink(t.decor, false);
    if (this.obsidian) layer.sink(this.obsidian.group, false);
    for (const o of this.root.children) if ((o as THREE.InstancedMesh).isInstancedMesh) layer.sink(o, false);
    this.burnDusk();
    this.scene.add(layer.group);
    this.collapseLayer = layer;
  }

  /** The Burn's dusk (La Quemadura): ash and embers instead of the violet of the deep glass. */
  private burnDusk(): void {
    const t = this.terrain;
    this.scene.background = new THREE.Color('#0f0a0a');
    if (this.scene.fog instanceof THREE.Fog) this.scene.fog.color.set('#1d1413');
    t.fog?.tint({ low: '#3a2824', mid: '#241816', far: '#0f0a0a', alpha: 1 });
    if (this.sky) {
      this.sky.hemi.color.set('#a89894');
      this.sky.hemi.groundColor.set('#2a100a');
      this.sky.hemi.intensity *= 0.85;
      this.sky.key.color.set('#ffe6d8');
      this.sky.key.intensity *= 0.85;
      this.sky.rim.color.set('#ff6a3a');
    }
  }
  /** Where nightmares went over the tail's rim this tick (their death is a fall to the sea). */
  private fallsNow = new Set<string>();

  private startVillage(night: boolean, clues: { x: number; y: number }[] = [], square = false): void {
    this.villageTried = true;
    this.village = buildVillage(this.omenCtx(), this.map, night, clues, square);
    this.scene.add(this.village.group);
  }

  /* ---------- the ink omen (cosmetic) ---------- */

  private ink: InkLayer | null = null;
  /** Inked nightmares last tick (id → was inked), to know who fell inked. */
  private inked = new Set<number>();

  private startInk(): void {
    const k = WORLD_PER_HEX / 1000;
    this.ink = new InkLayer((x, z) => this.heightAt(x / k, z / k), this.opts.quality === 'low');
    this.scene.add(this.ink.group);
  }

  private syncInk(s: BattleState): void {
    const ink = this.ink as InkLayer;
    const w = new THREE.Vector3();
    const walkers: { id: number; x: number; z: number }[] = [];
    this.inked.clear();
    for (const e of s.enemies) {
      if (e.hp <= 0 || e.tier < 2) continue;
      this.inked.add(e.id);
      if (NIGHTMARES[e.kind]?.flying) continue;
      simToWorld(e.x, e.y, w);
      walkers.push({ id: e.id, x: w.x, z: w.z });
    }
    ink.walkers(walkers);
    ink.heroes(s.heroes.filter((h) => h.downTicks === 0).map((h) => (simToWorld(h.x, h.y, w), { id: h.id, x: w.x, z: w.z })));
  }

  /** An inked nightmare fell: the floor keeps it, and a spray of wet black. */
  private inkFall(e: { id: number; kind: string; x: number; y: number }): void {
    if (!this.ink || !this.inked.has(e.id)) return;
    const w = simToWorld(e.x, e.y);
    const big = e.kind === 'slime' || e.kind === 'gargoyle' || e.kind === 'ram' || e.kind === 'mammoth' || e.kind === 'warden' || e.kind === 'witch';
    this.ink.splat(w.x, w.z, big);
    const p = w.setY(this.heightAt(e.x, e.y) + 0.4);
    this.vfx.burst(p, { n: this.opts.quality === 'low' ? 2 : 5, color: '#8a4dff', speed: 0.05, up: 0.06, size: 0.06, life: 6, gravity: 0.01 });
  }

  /* ---------- Ascuas (incursion level II) ---------- */

  private ascuas: Ascuas | null = null;
  /** Molten rock flung by eruptions and the magma lord, flying at 12 Hz until it lands and cools. */
  private cinders: { p: THREE.Vector3; v: THREE.Vector3; rot: THREE.Euler; spin: number; life: number; s: number; landed: boolean; glass?: boolean }[] = [];

  /** Burns the board the first time the state says it is an Ascuas incursion. */
  private startAscuas(): void {
    const k = WORLD_PER_HEX / 1000;
    const a = new Ascuas(this.map, (x, z) => terrainHeightAt(this.terrain, this.map, x / k, z / k), this.opts.quality === 'low');
    a.apply(this.scene, this.terrain, this.sky, this.root.children.filter((o) => (o as THREE.InstancedMesh).isInstancedMesh));
    this.scene.add(a.group);
    this.ascuas = a;
  }

  /** The crypt in embers (level II): burns the crypt the first time the state says so. */
  private startCryptAscuas(): void {
    const c = this.crypt;
    if (!c) return;
    const k = WORLD_PER_HEX / 1000;
    const L = this.map.dungeon as DungeonLayout | undefined;
    const spots = (L?.floor ?? []).map((h) => {
      const w = hexToWorld(h.q, h.r);
      return [w.x * k, w.y * k] as const;
    });
    const a = new Ascuas(this.map, (x, z) => terrainHeightAt(this.terrain, this.map, x / k, z / k), this.opts.quality === 'low', spots);
    a.applyCrypt(this.scene, c, this.cryptSky);
    this.scene.add(a.group);
    this.ascuas = a;
  }

  /** A fire vent (crypt trap): the grate is drawn by the Ascuas layer; here its glow and the smoke off it. */
  private drawVent(s: BattleState, z: Zone, p: THREE.Vector3): void {
    const r = (z.radius / 1000) * WORLD_PER_HEX;
    const heat = this.ascuas?.ventHeat(z, s.zones.filter((e) => e.kind === 'eruption' && (e.warn ?? 0) > 0)) ?? 0;
    const beat = 0.85 + 0.15 * Math.sin(s.tick * 0.5 + z.id);
    this.entityGlow.add(p.x, p.y + 0.1, p.z, LAVA_GLOW, r * (1.3 + 1.2 * heat), (0.16 + 0.5 * heat) * beat);
    if (!this.fresh) return;
    const low = this.opts.quality === 'low';
    if ((s.tick + z.id * 5) % (low ? 16 : 9) === 0) this.vfx.burst(p.clone().setY(p.y + 0.12), { n: 1, color: '#2a1a16', speed: 0.004, up: 0.025, size: 0.4, life: 18, smoke: true });
    if (heat > 0.2 && (s.tick + z.id) % 2 === 0) this.vfx.burst(p.clone().setY(p.y + 0.05), { n: 1 + Math.round(heat * 2), color: heat > 0.6 ? '#ffd08a' : '#ff6a2a', speed: 0.012, up: 0.05 + 0.1 * heat, size: 0.08, life: 7, gravity: 0.008, spread: r * 0.5 });
  }

  /* ---------- the rune seal (crypt in embers) ---------- */

  private seal: SealPlates | null = null;
  private sealFlash: number[] = [];

  /** The plates on the floor before the Warden's hall: carved and dim, flashing the order, lit when stepped right. */
  private drawSeal(s: BattleState): void {
    const look = (s.dungeon as { seal?: SealLook } | undefined)?.seal;
    if (!look || look.plates.length === 0) return;
    if (!this.seal) {
      const centers = look.plates.map((h) => {
        const c = hexToWorld(h.q, h.r);
        return simToWorld(c.x, c.y, new THREE.Vector3(), this.heightAt(c.x, c.y) + 0.035);
      });
      this.seal = new SealPlates(centers, WORLD_PER_HEX * 0.42);
      this.root.add(this.seal.mesh);
    }
    const st = this.seal.sync(look, s.tick);
    st.forEach((p, i) => {
      const at = this.seal!.at[i] as THREE.Vector3;
      const glow = Math.max(p.lit * 0.6, p.flash);
      if (glow > 0) this.entityGlow.add(at.x, at.y + 0.1, at.z, p.flash > 0 ? FLASH_RUNE : RUNE_LIT, 1.1, 0.08 + 0.14 * glow);
      const was = this.sealFlash[i] ?? 0;
      if (this.fresh && p.flash > 0.9 && was <= 0.9) {
        // Each plate's turn in the order: a ring goes out from it and sparks lift off the rune.
        this.vfx.ring(at.clone().setY(at.y + 0.02), 0.2, 1.1, '#ff8a6e', 9, 0.95);
        this.vfx.burst(at.clone().setY(at.y + 0.15), { n: this.opts.quality === 'low' ? 5 : 10, color: '#ffb59e', speed: 0.02, up: 0.07, size: 0.1, life: 10, gravity: -0.001, spread: 0.25 });
      }
      this.sealFlash[i] = p.flash;
    });
  }

  /** A crack of lava: the pool itself is drawn by the Ascuas layer; here its glow, bubbles and smoke. */
  private drawLava(s: BattleState, z: Zone, p: THREE.Vector3): void {
    const r = (z.radius / 1000) * WORLD_PER_HEX;
    const beat = 0.85 + 0.15 * Math.sin(s.tick * 0.4 + z.id);
    this.entityGlow.add(p.x, p.y + 0.12, p.z, LAVA_GLOW, r * 1.9, 0.36 * beat);
    if (!this.fresh) return;
    const low = this.opts.quality === 'low';
    if ((s.tick + z.id) % (low ? 4 : 2) === 0) {
      const a = Math.random() * TAU;
      const d = Math.sqrt(Math.random()) * r * 0.75;
      this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * d, 0.02, Math.sin(a) * d)), { n: 2, color: (s.tick & 2) === 0 ? '#ffb060' : '#ff6a2a', speed: 0.012, up: 0.05, size: 0.09, life: 8, gravity: 0.007, spread: 0.04 });
    }
    if ((s.tick + z.id * 3) % (low ? 14 : 8) === 0) {
      const a = Math.random() * TAU;
      this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * r * 0.4, 0.15, Math.sin(a) * r * 0.4)), { n: 1, color: '#2a1a16', speed: 0.004, up: 0.03, size: 0.55, life: 20, smoke: true });
    }
  }

  /** Longest warning seen per eruption (to tell how near it is to bursting without the Ascuas layer). */
  private eruptMax = new Map<number, number>();

  /** An eruption about to burst: the telegraph is drawn by the Ascuas layer; the ground glows and spits as it nears. */
  private drawEruption(s: BattleState, z: Zone, p: THREE.Vector3): void {
    if ((z.warn ?? 0) <= 0) return;
    const r = (z.radius / 1000) * WORLD_PER_HEX;
    let k = this.ascuas?.progress(z) ?? 0.5;
    if (!this.ascuas) {
      // No burnt ground to draw the mark on (the dragon's fire on an ordinary clearing): a red circle on
      // the ground and a second one closing in on its middle as the burst nears.
      const max = Math.max(this.eruptMax.get(z.id) ?? 0, z.warn ?? 0);
      this.eruptMax.set(z.id, max);
      if (this.eruptMax.size > 96) for (const id of [...this.eruptMax.keys()].slice(0, 48)) this.eruptMax.delete(id);
      k = 1 - (z.warn ?? 0) / Math.max(1, max);
      if (this.fresh) {
        this.vfx.ring(p, r * 0.97, r, '#ff4f3a', 2, 0.55 + 0.4 * k);
        const c = Math.max(0.05, r * (1 - k));
        this.vfx.ring(p, c, c + 0.04, '#ff8a6e', 2, 0.5 + 0.4 * k);
      }
    }
    this.entityGlow.add(p.x, p.y + 0.1, p.z, LAVA_GLOW, r * (0.5 + 1.3 * k), 0.2 + 0.5 * k);
    if (!this.fresh) return;
    const low = this.opts.quality === 'low';
    if (k > 0.3 && (s.tick + z.id) % (low ? 3 : 2) === 0) {
      for (let i = 0; i < 1 + Math.round(k * 2); i++) {
        const a = Math.random() * TAU;
        const d = Math.sqrt(Math.random()) * r * 0.85;
        this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * d, 0.03, Math.sin(a) * d)), { n: 1, color: i ? '#ffd08a' : '#ff7a2a', speed: 0.01, up: 0.05 + 0.08 * k, size: 0.08, life: 7, gravity: 0.01, spread: 0.03 });
      }
    }
    if (k > 0.55 && (s.tick + z.id) % 3 === 0) {
      // The ground splits: grit hops and dust curls up.
      this.vfx.burst(p.clone().setY(p.y + 0.05), { n: low ? 2 : 4, color: '#2e2420', speed: 0.03, up: 0.05 + 0.05 * k, size: 0.07, life: 8, gravity: 0.012, smoke: true, spread: r * 0.4 });
    }
    if (k > 0.85) this.shake = Math.max(this.shake, 0.035);
  }

  /** An eruption bursts: a column of fire, a ring of heat, molten rock flung out and embers raining down. */
  private eruptionBurst(p: THREE.Vector3, r: number): void {
    const low = this.opts.quality === 'low';
    const q = p.clone();
    // The wave stops at the line it hits within (at r·1.3 it read as a burst landing outside the circle).
    this.vfx.ring(q, r * 0.15, r, '#ff5a2a', 8, 1);
    this.vfx.ring(q, r * 0.1, r * 0.85, '#ffd08a', 5, 0.85);
    this.vfx.shaft(q.clone().setY(q.y + 0.05), q.clone().setY(q.y + 3.8), '#ff5a1f', 8, r * 0.5);
    this.vfx.shaft(q.clone().setY(q.y + 0.05), q.clone().setY(q.y + 2.6), '#ffd27a', 5, r * 0.22);
    this.vfx.burst(q.clone().setY(q.y + 0.3), { n: low ? 16 : 34, color: '#ff8a2a', speed: 0.05, up: 0.22, size: 0.22, life: 12, gravity: 0.016, spread: r * 0.5 });
    this.vfx.burst(q.clone().setY(q.y + 0.2), { n: low ? 8 : 16, color: '#ffe0a0', speed: 0.09, up: 0.14, size: 0.1, life: 9, gravity: 0.012 });
    this.vfx.burst(q.clone().setY(q.y + 0.5), { n: low ? 6 : 12, color: '#2a1c18', speed: 0.04, up: 0.07, size: 0.7, life: 22, smoke: true, spread: r * 0.6 });
    // The glow's billboard is as wide as the burst (its edge fades to nothing at r): wider, it lit the ground outside.
    this.flare(q.clone().setY(q.y + 0.7), '#ff6a2a', r * 2, 7);
    this.flare(q.clone().setY(q.y + 0.25), '#ffe2b0', r * 1.5, 4);
    this.fling(q, low ? 5 : 10, 1);
    for (let i = 1; i <= 3; i++) {
      this.later.push({ n: i * 2, run: () => this.vfx.burst(q.clone().setY(q.y + 1.5 + i * 0.3), { n: low ? 3 : 6, color: '#ff9a4a', speed: 0.04, up: 0, size: 0.09, life: 12, gravity: 0.01, spread: r }) });
    }
    this.shake = Math.max(this.shake, 0.3);
  }

  /** Throws `n` lumps of molten rock (or splinters of black glass) out of `p` (`force` 1 for an eruption). */
  private fling(p: THREE.Vector3, n: number, force: number, glass = false): void {
    for (let i = 0; i < n && this.cinders.length < 80; i++) {
      const a = (i / n) * TAU + Math.random() * 0.6;
      const sp = (0.04 + Math.random() * 0.07) * force;
      this.cinders.push({
        p: p.clone().setY(p.y + 0.2),
        v: new THREE.Vector3(Math.cos(a) * sp, (0.15 + Math.random() * 0.15) * force, Math.sin(a) * sp),
        rot: new THREE.Euler(Math.random() * 3, Math.random() * 3, Math.random() * 3),
        spin: (Math.random() - 0.5) * 1.2,
        life: 26,
        s: 0.7 + Math.random() * 0.9,
        landed: false,
        glass,
      });
    }
  }

  private stepCinders(): void {
    if (this.cinders.length === 0) return;
    const k = WORLD_PER_HEX / 1000;
    this.cinders = this.cinders.filter((c) => {
      if (--c.life <= 0) return false;
      if (c.landed) return true;
      c.p.add(c.v);
      c.v.y -= 0.022;
      c.rot.x += c.spin;
      c.rot.z += c.spin * 0.7;
      const g = terrainHeightAt(this.terrain, this.map, c.p.x / k, c.p.z / k) + 0.04;
      if (c.p.y <= g && c.v.y < 0) {
        c.p.y = g;
        c.landed = true;
        c.life = Math.min(c.life, 10);
        this.vfx.burst(c.p.clone().setY(g + 0.05), { n: 3, color: c.glass ? '#d9ccff' : '#ff8a3d', speed: 0.03, up: 0.04, size: c.glass ? 0.05 : 0.07, life: 6, gravity: 0.01 });
      }
      return true;
    });
  }

  private drawCinders(use: (k: string) => ModelBatch): void {
    if (this.cinders.length === 0) return;
    for (const c of this.cinders) {
      this.q.setFromEuler(c.rot);
      this.m.compose(c.p, this.q, this.s.setScalar(c.s));
      use(c.glass ? 'proj:glassShard' : 'proj:cinder').add(this.m, 0);
      const t = c.landed ? (c.life / 10) * 0.6 : 1;
      if (c.glass) {
        if (!c.landed) this.entityGlow.add(c.p.x, c.p.y + 0.05 * c.s, c.p.z, OBSIDIAN.glint, 0.16 * c.s, 0.4);
      } else this.entityGlow.add(c.p.x, c.p.y + 0.09 * c.s, c.p.z, LAVA_GLOW, 0.22 * c.s, 0.75 * t);
    }
  }

  /** The magma lord: heat under its feet, drops of fire falling off it, smoke off the shoulders, glowing prints. */
  /* ---------- The Unsleeping (docs/ANTIHEROES.md) ---------- */

  private unFx: UnsleepingFx | null = null;
  /** Unsleeping that faded into smoke: their 'die' is no fall. */
  private readonly unFled = new Set<number>();
  private unCtxMemo: UnCtx | null = null;
  private readonly unGlow = new THREE.Color();

  /** What an Unsleeping's effects borrow from the world. */
  private get unCtx(): UnCtx {
    if (!this.unCtxMemo) {
      const low = (): boolean => this.opts.quality === 'low';
      this.unCtxMemo = {
        vfx: this.vfx,
        get low() {
          return low();
        },
        flare: (p, color, size, life) => this.flare(p, color, size, life),
        shake: (v) => {
          this.shake = Math.max(this.shake, v);
        },
        force: (id, clip) => this.force(id, clip),
        at: (x, y, h = 0) => simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y) + h),
        glow: (p, color, size, alpha) => this.entityGlow.add(p.x, p.y, p.z, color, size, alpha),
      };
    }
    return this.unCtxMemo;
  }

  /** His marks on the ground, made (and compiled with the rest) as soon as the fight knows he is coming. */
  private armUnsleeping(): UnsleepingFx {
    if (!this.unFx) {
      this.unFx = new UnsleepingFx();
      this.overlay.add(this.unFx.group, this.unFx.world);
      this.unFx.park();
    }
    return this.unFx;
  }

  /** Each frame: his marks on the ground, his presence, and what is his own. */
  private unsleepingFx(s: BattleState, e: Enemy, p: THREE.Vector3, yaw: number, h: number): void {
    const u = e.un;
    if (!u) return;
    const r = UN_RENDER[u.id];
    this.armUnsleeping().frame(e, p, this.now, r, (x, y) => simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y)));
    presence(this.unCtx, s, e, p, h, r.look, this.fresh, this.unGlow.set(r.look.glow));
    if (this.fresh) r.frame?.(this.unCtx, s, e, p, yaw, h);
  }

  /** His skill events: the shared moments in his colours, else his own. */
  private unsleepingSkill(s: BattleState, e: Extract<BattleEvent, { t: 'skill' }>, id: UnsleepingId): void {
    const who = s.enemies.find((x) => x.id === e.hero);
    const p = who ? simToWorld(who.x, who.y, new THREE.Vector3(), this.heightAt(who.x, who.y)) : simToWorld(e.x, e.y, new THREE.Vector3(), this.heightAt(e.x, e.y));
    // The key he is drawn with (baked ahead): another one would bake a whole model mid-fight just to read its height.
    const key = this.enemyKey(id, who?.tier ?? 1);
    const h = this.lib.heightOf(key) * this.lib.scaleOf(key);
    const r = UN_RENDER[id];
    if (r.skill?.(this.unCtx, s, who, e.skill, p, h)) return;
    moment(this.unCtx, e.skill.slice(id.length), p, h, r.look, e.skill === `${id}${UNSLEEPING_OF[id].ult}`);
  }

  /** His bursts: gone into smoke, else his own. */
  private unsleepingBoom(s: BattleState, id: UnsleepingId, kind: string, x: number, y: number, p: THREE.Vector3, r: number): void {
    const who = this.nearestOf(s, id, x, y);
    if (kind === `${id}Vanish`) {
      if (who) this.unFled.add(who.id);
      vanished(this.unCtx, p, UN_RENDER[id].look);
      return;
    }
    UN_RENDER[id].boom?.(this.unCtx, s, who, kind, p, r);
  }

  /** Kaela's thorns marked on the ground: a green circle, a second one closing in, thorns pricking up as the burst nears. */
  private drawBriar(s: BattleState, z: Zone, p: THREE.Vector3): void {
    if ((z.warn ?? 0) <= 0) return;
    const r = (z.radius / 1000) * WORLD_PER_HEX;
    const max = Math.max(this.eruptMax.get(z.id) ?? 0, z.warn ?? 0);
    this.eruptMax.set(z.id, max);
    if (this.eruptMax.size > 96) for (const id of [...this.eruptMax.keys()].slice(0, 48)) this.eruptMax.delete(id);
    const k = 1 - (z.warn ?? 0) / Math.max(1, max);
    this.entityGlow.add(p.x, p.y + 0.1, p.z, KAELA_MOSS, r * (0.6 + 1.2 * k), 0.25 + 0.45 * k);
    if (!this.fresh) return;
    this.vfx.ring(p, r * 0.96, r, '#5fae3a', 2, 0.55 + 0.4 * k);
    const c = Math.max(0.05, r * (1 - k));
    this.vfx.ring(p, c, c + 0.04, '#dcffc0', 2, 0.5 + 0.4 * k);
    if (k > 0.35 && (s.tick + z.id) % (this.opts.quality === 'low' ? 3 : 2) === 0) {
      const a = Math.random() * TAU;
      const d = Math.sqrt(Math.random()) * r * 0.8;
      this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * d, 0.03, Math.sin(a) * d)), { n: 1, color: '#2a3a22', speed: 0.01, up: 0.04 + 0.06 * k, size: 0.12, life: 7, gravity: 0.01, spread: 0.03 });
    }
  }

  /** The towers Kaela's moss holds now (her state keeps them; the board draws the moss on them). */
  private mossed(s: BattleState): readonly number[] {
    if (this.mossTick !== s.tick) {
      this.mossTick = s.tick;
      const k = s.un ? s.enemies.find((e) => e.id === s.un && e.un?.id === 'kaela') : undefined;
      this.mossIds = k?.un?.tgts ?? [];
    }
    return this.mossIds;
  }
  private mossTick = -1;
  private mossIds: readonly number[] = [];

  /** A tower wrapped in moss: a green glow up its sides, vines criss-crossing it, spores drifting off. */
  private mossFx(s: BattleState, id: number, p: THREE.Vector3, tall: number): void {
    this.entityGlow.add(p.x, p.y + tall * 0.45, p.z, KAELA_MOSS, 1.3, 0.4);
    if ((s.tick + id) % 6 !== 0) return;
    for (let i = 0; i < 3; i++) {
      const a = (s.tick / 6 + id + i * 2.1) % 6.283;
      const o = new THREE.Vector3(Math.cos(a) * 0.45, 0, Math.sin(a) * 0.45);
      const lo = p.clone().add(o).setY(p.y + tall * (0.05 + 0.2 * i));
      const hi = p.clone().sub(o).setY(p.y + tall * (0.5 + 0.15 * i));
      this.vfx.beam(lo, hi, '#4f8a32', 6, 0.03, 0.08);
    }
    this.vfx.burst(p.clone().setY(p.y + tall * 0.7), { n: this.opts.quality === 'low' ? 1 : 3, color: '#5fae3a', speed: 0.01, up: 0.01, size: 0.06, life: 16, gravity: -0.001, spread: 0.4 });
  }

  /** Longest warning seen per marked blast of an Unsleeping (how near it is to bursting). */
  private blastMax = new Map<number, number>();

  /** An Unsleeping's marked blast (Bruni's mortar, Ragna's landing): a circle in his colours and a second one closing in. */
  private drawUnBlast(s: BattleState, z: Zone, p: THREE.Vector3): void {
    if ((z.warn ?? 0) <= 0 || !z.un) return;
    const look = UN_RENDER[z.un].look;
    const r = (z.radius / 1000) * WORLD_PER_HEX;
    const max = Math.max(this.blastMax.get(z.id) ?? 0, z.warn ?? 0);
    this.blastMax.set(z.id, max);
    if (this.blastMax.size > 96) for (const id of [...this.blastMax.keys()].slice(0, 48)) this.blastMax.delete(id);
    const k = 1 - (z.warn ?? 0) / Math.max(1, max);
    this.entityGlow.add(p.x, p.y + 0.1, p.z, this.unGlow.set(look.glow), r * (0.6 + 1.2 * k), 0.2 + 0.5 * k);
    if (!this.fresh) return;
    this.vfx.ring(p, r * 0.96, r, look.accent, 2, 0.6 + 0.4 * k);
    const c = Math.max(0.05, r * (1 - k));
    this.vfx.ring(p, c, c + 0.05, look.pale, 2, 0.5 + 0.4 * k);
    if (k > 0.6 && (s.tick + z.id) % 3 === 0) this.vfx.burst(p.clone().setY(p.y + 0.05), { n: this.opts.quality === 'low' ? 1 : 3, color: look.pale, speed: 0.02, up: 0.06, size: 0.06, life: 7, gravity: 0.01, spread: r * 0.5 });
  }

  /** A tower Bruni's forge took: a cold blue light over it and arcs crawling up its sides. */
  private turnedFx(s: BattleState, id: number, p: THREE.Vector3, tall: number): void {
    this.entityGlow.add(p.x, p.y + tall * 0.6, p.z, TURNED_BLUE, 1.6, 0.75);
    this.entityGlow.add(p.x, p.y + 0.08, p.z, TURNED_BLUE, 1.4, 0.45);
    if ((s.tick + id) % 3 !== 0) return;
    for (let i = 0; i < 2; i++) {
      const a = (s.tick * 0.9 + id + i * 3.1) % 6.283;
      const o = new THREE.Vector3(Math.cos(a) * 0.42, 0, Math.sin(a) * 0.42);
      const lo = p.clone().add(o).setY(p.y + tall * 0.1);
      const hi = p.clone().add(o.multiplyScalar(-0.6)).setY(p.y + tall * (0.75 + 0.2 * i));
      this.vfx.beam(lo, hi, i ? '#c8f6ff' : '#22c4ff', 4, 0.03, 0.25);
    }
    if ((s.tick + id) % 6 === 0) this.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, 0.9, '#4fe0ff', 8, 0.7);
  }

  /** The dark his Night leaves in his last phase: shadow on the ground with embers rising through it. */
  private drawNightfall(s: BattleState, z: { id: number; radius: number; ticks: number }, p: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    const r = (z.radius / 1000) * WORLD_PER_HEX;
    this.entityGlow.add(p.x, p.y + 0.1, p.z, EMBER_GLOW, r * 1.6, 0.25);
    if ((s.tick + z.id) % 3 === 0) {
      const q = p.clone().add(new THREE.Vector3(Math.sin(s.tick + z.id) * r * 0.5, 0.2, Math.cos(s.tick * 1.3 + z.id) * r * 0.5));
      this.vfx.burst(q, { n: low ? 1 : 3, color: '#0f1015', speed: 0.01, up: 0.03, size: 0.7, life: 20, smoke: true, spread: r * 0.5 });
      this.vfx.burst(q, { n: 1, color: '#ff4f3a', speed: 0.01, up: 0.06, size: 0.07, life: 12, gravity: -0.003, spread: r * 0.4 });
    }
  }

  private lordFx(s: BattleState, e: Enemy, p: THREE.Vector3, yaw: number, h: number): void {
    this.entityGlow.add(p.x, p.y + 0.06, p.z, LAVA_GLOW, 1.7, 0.2);
    if (!this.fresh) return;
    const low = this.opts.quality === 'low';
    const beat = (s.tick + e.id) % 12;
    if (beat % 3 === 0) {
      const a = Math.random() * TAU;
      this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * 0.3, h * (0.35 + Math.random() * 0.35), Math.sin(a) * 0.3)), { n: 1, color: '#ff7a2a', speed: 0.01, up: 0, size: 0.1, life: 9, gravity: 0.014 });
    }
    if (!low && beat % 5 === 0) this.vfx.burst(p.clone().setY(p.y + h * 0.82), { n: 1, color: '#2a201c', speed: 0.01, up: 0.04, size: 0.5, life: 16, smoke: true, spread: 0.5 });
    const fwd = this.v.set(Math.sin(yaw), 0, Math.cos(yaw));
    if (e.anim === 'walk' && beat % 4 === 0) {
      const side = beat % 8 === 0 ? 1 : -1;
      const fp = p.clone().add(new THREE.Vector3(fwd.z * side * 0.3, 0.02, -fwd.x * side * 0.3));
      this.vfx.ring(fp, 0.05, 0.32, '#ff6a2a', 12, 0.65);
    }
    if (e.anim === 'attack' && e.animT === 2) {
      // The maul comes down in front of it.
      const hit = p.clone().addScaledVector(fwd, 1.1);
      this.vfx.ring(hit, 0.1, 1.3, '#ff6a2a', 7, 1);
      this.vfx.burst(hit.clone().setY(hit.y + 0.15), { n: low ? 6 : 12, color: '#ff8a3d', speed: 0.08, up: 0.1, size: 0.11, life: 8, gravity: 0.014 });
      this.vfx.burst(hit.clone().setY(hit.y + 0.2), { n: low ? 2 : 5, color: '#2e2420', speed: 0.05, up: 0.04, size: 0.45, life: 14, smoke: true, spread: 0.3 });
      this.shake = Math.max(this.shake, 0.14);
    }
  }

  /** The magma lord calls the ground to burst: a shockwave from where it stands and a mark where it aims. */
  private magmaSlam(from: THREE.Vector3 | null, to: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    if (from) {
      this.vfx.ring(from, 0.4, 4.8, '#ff4f3a', 12, 1);
      this.vfx.ring(from.clone().setY(from.y + 0.4), 0.3, 2.6, '#ffb060', 8, 0.7);
      this.vfx.burst(from.clone().setY(from.y + 1.2), { n: low ? 14 : 30, color: '#ff7a2a', speed: 0.09, up: 0.12, size: 0.16, life: 14, gravity: 0.01 });
      this.flare(from.clone().setY(from.y + 1.2), '#ff6a2a', 3, 6);
      this.fling(from, low ? 3 : 6, 0.8);
    }
    this.vfx.ring(to, 0.2, 1.4, '#ff8a6e', 8, 0.9);
    this.shake = Math.max(this.shake, 0.4);
  }

  /** The magma lord breaks apart: its heart bursts and its body falls in burning slabs. */
  private lordFalls(p: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    const q = p.clone().setY(p.y - 0.35);
    this.vfx.ring(q, 0.5, 8, '#ff5a2a', 18, 1);
    this.vfx.ring(q, 0.3, 4, '#ffd08a', 10, 0.8);
    this.vfx.shaft(q, q.clone().setY(q.y + 5), '#ff5a1f', 10, 0.9);
    this.vfx.burst(q.clone().setY(q.y + 1.2), { n: low ? 30 : 70, color: '#ff8a2a', speed: 0.12, up: 0.14, size: 0.2, life: 20, gravity: 0.01 });
    this.vfx.burst(q.clone().setY(q.y + 1), { n: low ? 12 : 26, color: '#241a17', speed: 0.06, up: 0.06, size: 0.9, life: 26, smoke: true, spread: 0.8 });
    this.flare(q.clone().setY(q.y + 1.2), '#ff7a2a', 6, 10);
    this.flare(q.clone().setY(q.y + 1.2), '#fff0c8', 2.5, 5);
    this.fling(q, low ? 8 : 16, 1.2);
    this.shake = Math.max(this.shake, 0.9);
  }

  /* ---------- Obsidiana (incursion level III) and the deep crypt ---------- */

  private obsidian: Obsidian | null = null;
  private deep: DeepCryptLook | null = null;
  private puzzles: PuzzleLayer | null = null;

  /** Glasses the board the first time the state says it is an Obsidiana incursion. */
  private startObsidian(): void {
    const k = WORLD_PER_HEX / 1000;
    const o = new Obsidian(this.map, (x, z) => terrainHeightAt(this.terrain, this.map, x / k, z / k), this.opts.quality === 'low');
    o.apply(this.scene, this.terrain, this.sky, this.root.children.filter((c) => (c as THREE.InstancedMesh).isInstancedMesh));
    this.scene.add(o.group);
    this.obsidian = o;
  }

  /**
   * The deep crypt (level III): every floor dressed its own way, embers on the second (lava, vents, the
   * same layer as the crypt in embers) and glass on the third, the puzzles and the descent.
   */
  private startDeep(): void {
    const c = this.crypt;
    const L = this.map.dungeon as DungeonLayout | undefined;
    if (!c || !L) return;
    const k = WORLD_PER_HEX / 1000;
    const ground = (x: number, z: number): number => terrainHeightAt(this.terrain, this.map, x / k, z / k);
    const low = this.opts.quality === 'low';
    const d = new DeepCryptLook(this.scene, c, this.cryptSky, L, ground, this.entityGlow, this.vfx, low);
    d.apply();
    this.deep = d;
    this.ascuas = new Ascuas(this.map, ground, low, d.spots(1));
    this.scene.add(this.ascuas.group);
    this.obsidian = new Obsidian(this.map, ground, low, d.spots(2));
    this.scene.add(this.obsidian.group);
    this.puzzles = new PuzzleLayer(L, ground, { vfx: this.vfx, glow: this.entityGlow, flare: (p, color, size, life) => this.flare(p, color, size, life), low });
    this.root.add(this.puzzles.group);
  }

  /* ---------- the Chasm (la Sima) ---------- */

  private sima: SimaLook | null = null;
  /** The layout the crypt on screen was built from (a new floor replaces `map.dungeon`). */
  private simaLayout: DungeonLayout | null = null;

  /**
   * The Chasm: its look (one material for every stratum, props, hazards, motes, the stairs and the way out), and the
   * layers that draw the deep crypt's lava and vents (the burning marrow) and its cracking glass (the black glass).
   * Those have nothing in the air of their own here: the Chasm's motes are its own.
   */
  private startSima(): void {
    const low = this.opts.quality === 'low';
    this.sima = new SimaLook(this.scene, { vfx: this.vfx, glow: this.entityGlow, flare: (p, color, size, life) => this.flare(p, color, size, life), shake: (n) => (this.shake = Math.max(this.shake, n)), low });
    const ground = (): number => 0;
    this.ascuas = new Ascuas(this.map, ground, low, []);
    this.ascuas.ambient(false);
    this.scene.add(this.ascuas.group);
    this.obsidian = new Obsidian(this.map, ground, low, []);
    this.obsidian.ambient(false);
    this.scene.add(this.obsidian.group);
    this.simaLayout = this.map.dungeon ?? null;
    // A floor's hazard room may hold one of the deep crypt's puzzles: one of each, far under the board, so their
    // shaders compile with the rest as the Chasm opens instead of on the floor that first shows one.
    const room = { q: 0, r: -44, radius: 3, kind: 'room' as const };
    const cells: { q: number; r: number }[] = [];
    for (let q = -3; q <= 3; q++) for (let r = -3; r <= 3; r++) if (Math.abs(q) + Math.abs(r) + Math.abs(q + r) <= 6) cells.push({ q: room.q + q, r: room.r + r });
    const fake: DungeonLayout = { rooms: [room, room, room, room], doors: [], floor: cells };
    const spots = cells.slice(0, 4);
    const puzzle = (i: number, kind: DungeonPuzzle['kind']): DungeonPuzzle => ({ room: i, kind, solved: false, fails: 0, spots, lit: [0, 1, 2, 3], step: 0, hold: 0, need: 1, showFrom: 0, showUntil: 0, on: [], pressAt: 0, heat: [0, 0, 0, 0] });
    this.simaDummy = new PuzzleLayer(fake, () => -40, { vfx: this.vfx, glow: this.entityGlow, flare: () => undefined, low });
    this.simaDummy.sync([puzzle(0, 'braziers'), puzzle(1, 'levers'), puzzle(2, 'runes'), puzzle(3, 'glass')], 0, false);
    this.root.add(this.simaDummy.group);
  }

  private simaDummy: PuzzleLayer | null = null;
  /** Library keys asked for the Chasm so far (each once). */
  private simaAsked = new Set<string>();

  /**
   * The nightmares of floors `from` and the two after it, by the week's order of the strata: each stratum's own
   * (era-filtered as the sim does), its captain, its guardian on a threshold floor and what its hazards and the signs
   * bring. Only those not asked for yet.
   */
  private simaKeys(sm: SimaRun, from: number): string[] {
    const HAZ: Record<string, string> = { niche: 'skeleton', deep: 'leech', web: 'spiderling', rockfall: 'boneworm' };
    const ok = (k: string): boolean => sm.allowed.includes(k as never) || SIMA_ANY_ERA.includes(k as never);
    const out: string[] = [];
    const add = (k: string, tier: number, elite = false): void => {
      const key = this.enemyKey(k, tier, elite);
      if (this.simaAsked.has(key)) return;
      this.simaAsked.add(key);
      out.push(key);
    };
    for (let f = Math.max(1, from); f < from + 3; f++) {
      const st = STRATA[sm.order[(f - 1) % sm.order.length] ?? 0];
      if (!st) continue;
      const def = STRATUM[st];
      for (const k of Object.keys(def.bestiary).sort()) {
        if (!ok(k)) continue;
        add(k, 1);
        add(k, 2);
        add(k, 2, true);
      }
      const cap = captainOf(sm, st);
      add(cap, 2, true);
      if (f % SIMA.threshold === 0) {
        add(def.guardian, 1);
        for (const k of ['crystal', 'cocoon', 'iceBlock']) add(k, 1);
      }
      if (def.hazard && HAZ[def.hazard]) add(HAZ[def.hazard] as string, 1);
      add('mourner', 1);
      // A puzzle in the hazard room brings the deep crypt's first floor's beasts (and its flyers on the glass).
      if (f >= 3) for (const k of [...Object.keys(DEEP_CRYPT.bestiary[0] ?? {}), ...Object.keys(DEEP_CRYPT.flyers)].sort()) {
        add(k, 1);
        add(k, 2);
      }
    }
    return out;
  }

  /**
   * A new floor (`sima.rev` changed): the crypt is built again for its layout, the old one's geometry dropped (its
   * materials are kept: freeing the last user of a program throws the program away, and the next floor would compile
   * it again), the room lights moved, and the look dressed for the new stratum. Behind the descent's cut to black.
   */
  private simaFloor(s: BattleState): void {
    const sm = s.dungeon?.sima;
    const L = s.map.dungeon;
    const look = this.sima;
    if (!sm || !L || !look || sm.rev === look.floorRev) return;
    let c = this.crypt as CryptBuild;
    if (L !== this.simaLayout) {
      const old = c;
      c = buildCrypt(s.map, this.seed);
      this.root.remove(old.mesh, old.decor);
      old.mesh.geometry.dispose();
      old.decor.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) m.geometry.dispose();
        if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose();
      });
      this.root.add(c.mesh, c.decor);
      this.crypt = c;
      this.terrain = c;
      this.simaLayout = L;
      // The room lights: the same slots in the scene, new sources for the new rooms (the slots fade them in).
      this.cryptSky.rooms = c.roomLights.map((p) => {
        const l = new THREE.PointLight(0xffffff, 14, 8, 1.4);
        l.position.copy(p);
        return l;
      });
      for (const sl of this.roomSlots) {
        sl.src = null;
        sl.w = 0;
      }
      this.markMeshes.forEach((m) => (m.visible = false));
    }
    c.roomLights.forEach((_, i) => {
      const l = this.cryptSky.rooms[i];
      if (l) l.distance = L.rooms[i]?.kind === 'boss' ? 11 : 8;
    });
    look.dress(c, L, sm, s.zones, this.cryptSky);
    // What the floors below can bring, baked and compiled while this one is fought (the first ones by prewarm).
    if (this.toBake) {
      const more = this.simaKeys(sm, sm.floor + 1);
      if (more.length) {
        this.toBake.push(...more);
        bakePool.ask(more);
        this.compiledAhead = false;
      }
    }
    // The hazard room's puzzle, if this floor has one.
    if (this.puzzles) {
      this.root.remove(this.puzzles.group);
      this.puzzles.group.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) m.geometry.dispose();
      });
    }
    const low = this.opts.quality === 'low';
    this.puzzles = new PuzzleLayer(L, () => 0, { vfx: this.vfx, glow: this.entityGlow, flare: (p, color, size, life) => this.flare(p, color, size, life), low });
    this.root.add(this.puzzles.group);
  }

  private maze: MazeLayer | null = null;

  /** The Furrows: the dark over what the group hasn't mapped, the levers, camps, ropes, chests and the waystone. */
  private startMaze(m: Maze): void {
    this.maze = new MazeLayer(this.map, m, { low: this.opts.quality === 'low' });
    this.root.add(this.maze.group);
  }

  private cellars: CellarsLayer | null = null;
  private plunder: PlunderLayer | null = null;

  /** A plunder incursion: the old garrison's caches on the field and the stack by the cart. */
  private startPlunder(vigil = false): void {
    const k = WORLD_PER_HEX / 1000;
    const ground = (gx: number, gz: number): number => terrainHeightAt(this.terrain, this.map, gx / k, gz / k);
    this.plunder = new PlunderLayer(ground, { vfx: this.vfx, glow: this.entityGlow, flare: (p, color, size, life) => this.flare(p, color, size, life), low: this.opts.quality === 'low' }, vigil);
    this.root.add(this.plunder.group);
  }

  /** A group expedition (the garrison cellars): its wheels, levers, locks, winch, loads, water and ink. */
  private startCellars(x: Expedition): void {
    const L = this.map.dungeon as DungeonLayout | undefined;
    if (!L) return;
    const k = WORLD_PER_HEX / 1000;
    const ground = (gx: number, gz: number): number => terrainHeightAt(this.terrain, this.map, gx / k, gz / k);
    this.cellars = new CellarsLayer(L, ground, x, { vfx: this.vfx, glow: this.entityGlow, flare: (p, color, size, life) => this.flare(p, color, size, life), low: this.opts.quality === 'low' });
    this.root.add(this.cellars.group);
  }

  /** After a descent: where the camera should jump at once (the new floor's entrance), then null. */
  takeJump(): THREE.Vector3 | null {
    const j = this.deep?.takeJump() ?? this.sima?.takeJump() ?? null;
    // The camera cuts: the last frame's view says nothing about the next.
    if (j) this.cullSkip = 2;
    return j;
  }

  /** The party goes down the stairs (the heroes are already at the new floor's entrance, `to`, this tick): a cut to black. */
  private descend(to: THREE.Vector3): void {
    this.deep?.descend(to);
    // The old floor's lingering sparks and rings stay behind in the dark.
    this.flares.length = 0;
  }

  /** Cracking glass, a pit or shards: the decal is drawn by the Obsidian layer; here the glow and the grit. */
  private drawGlass(s: BattleState, z: Zone, p: THREE.Vector3): void {
    const r = (z.radius / 1000) * WORLD_PER_HEX;
    const low = this.opts.quality === 'low';
    if (z.kind === 'shards') {
      this.entityGlow.add(p.x, p.y + 0.05, p.z, RED, r * 1.3, 0.1);
      if (this.fresh && (s.tick + z.id) % (low ? 6 : 3) === 0) {
        const a = Math.random() * TAU;
        const d = Math.sqrt(Math.random()) * r * 0.8;
        this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * d, 0.04, Math.sin(a) * d)), { n: 1, color: '#ffffff', speed: 0, up: 0.004, size: 0.07, life: 4, gravity: 0 });
      }
      return;
    }
    const k = this.obsidian?.progress(z) ?? 1;
    if ((z.warn ?? 0) > 0) {
      // Giving way under someone: red light growing under the feet, chips hopping, a jolt as it nears.
      this.entityGlow.add(p.x, p.y + 0.06, p.z, RED, r * (0.6 + 1.1 * k), 0.12 + 0.4 * k);
      if (this.fresh && (s.tick + z.id) % (k > 0.6 ? 1 : 3) === 0) this.vfx.burst(p.clone().setY(p.y + 0.04), { n: 1 + Math.round(k * 2), color: k > 0.6 ? '#ffffff' : '#d9ccff', speed: 0.02, up: 0.03 + 0.04 * k, size: 0.05, life: 6, gravity: 0.012, spread: r * 0.6 });
      if (k > 0.85) this.shake = Math.max(this.shake, 0.03);
      return;
    }
    // A pit: a faint violet haze rising out of the void.
    this.entityGlow.add(p.x, p.y + 0.05, p.z, OBSIDIAN.violet, r * 1.2, 0.12);
    if (this.fresh && !low && (s.tick + z.id) % 8 === 0) this.vfx.burst(p.clone().setY(p.y + 0.02), { n: 1, color: '#3a2a5a', speed: 0.003, up: 0.015, size: 0.35, life: 14, smoke: true, spread: r * 0.3 });
  }

  /** The glass gives way: it bursts in splinters, and the pit opens. */
  private glassBreak(p: THREE.Vector3, r: number): void {
    const low = this.opts.quality === 'low';
    // Within the pit's line: past it, it read as glass giving way outside.
    this.vfx.ring(p, r * 0.2, r, '#f4f0ff', 6, 1);
    this.vfx.ring(p, r * 0.1, r * 0.9, '#ff4f3a', 5, 0.7);
    this.vfx.burst(p.clone().setY(p.y + 0.15), { n: low ? 10 : 24, color: '#eee8ff', speed: 0.06, up: 0.09, size: 0.07, life: 10, gravity: 0.014, spread: r * 0.5 });
    this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 3 : 6, color: '#2a2236', speed: 0.02, up: 0.03, size: 0.4, life: 14, smoke: true, spread: r * 0.4 });
    this.flare(p.clone().setY(p.y + 0.3), '#e6dcff', r * 2, 4);
    this.fling(p, low ? 3 : 6, 0.6, true);
    this.shake = Math.max(this.shake, 0.12);
  }

  /** The colossus's rhythm: a glint before it shines, the flash of the shine, and the slam that ends it. */
  private colossusCast(kind: string, p: THREE.Vector3, h: number): void {
    const low = this.opts.quality === 'low';
    if (kind === 'colossusGlint') {
      // A second's warning: light runs up it and a thin ring goes out, as far as the slam will reach.
      this.vfx.ring(p, 0.3, COLOSSUS_REACH, '#d9ccff', 8, 0.7);
      this.vfx.burst(p.clone().setY(p.y + h * 0.6), { n: low ? 6 : 14, color: '#f4f0ff', speed: 0.03, up: 0.03, size: 0.08, life: 10, gravity: -0.002, spread: 0.6 });
      return;
    }
    if (kind === 'colossusShine') {
      // The shine: a white flash, the mirror closing over it.
      this.flare(p.clone().setY(p.y + h * 0.55), '#ffffff', 6.5, 8);
      this.flare(p.clone().setY(p.y + h * 0.55), '#b9a6ff', 9, 12);
      // The flash's wave stops at the slam's reach (out to 4.2 it read as a blow landing past the red line).
      this.vfx.ring(p, 0.5, COLOSSUS_REACH, '#f4f0ff', 10, 1);
      this.vfx.ring(p.clone().setY(p.y + h * 0.5), 0.4, 2.6, '#b9a6ff', 8, 0.7);
      this.vfx.burst(p.clone().setY(p.y + h * 0.6), { n: low ? 14 : 32, color: '#ffffff', speed: 0.08, up: 0.05, size: 0.1, life: 12, gravity: 0, spread: 0.5 });
      this.shake = Math.max(this.shake, 0.2);
    }
  }

  /** The slam that ends the shine: a shockwave and a ring of splinters flung out to where the shards fall. */
  private colossusSlam(p: THREE.Vector3, r: number): void {
    const low = this.opts.quality === 'low';
    // The wave ends at the slam's real reach (COLOSSUS.slamRadius), not past it.
    this.vfx.ring(p, 0.4, r, '#f4f0ff', 12, 1);
    this.vfx.ring(p, 0.3, r * 0.9, '#ff4f3a', 10, 0.8);
    this.vfx.ring(p.clone().setY(p.y + 0.4), 0.3, r * 0.6, '#b9a6ff', 8, 0.6);
    this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 18 : 40, color: '#eee8ff', speed: 0.14, up: 0.06, size: 0.08, life: 12, gravity: 0.01 });
    this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 6 : 14, color: '#2a2433', speed: 0.08, up: 0.04, size: 0.6, life: 18, smoke: true, spread: 0.6 });
    this.flare(p.clone().setY(p.y + 0.8), '#d9ccff', 4, 6);
    this.fling(p, low ? 6 : 12, 1, true);
    this.shake = Math.max(this.shake, 0.5);
  }

  /**
   * The colossus each tick: a red-violet heart glowing on the ground, glints running over it; shining, it
   * blazes white-violet with a pulsing rim on the floor round it, so nobody mistakes it: don't hit it now.
   */
  private colossusFx(s: BattleState, e: Enemy, p: THREE.Vector3, yaw: number, h: number, shine: boolean): void {
    const low = this.opts.quality === 'low';
    if (shine) {
      const left = (e.shineUntil ?? 0) - s.tick;
      const beat = 0.75 + 0.25 * Math.sin(s.tick * 1.3);
      this.entityGlow.add(p.x, p.y + h * 0.55, p.z, OBSIDIAN.white, 4.2 * beat, 0.3);
      this.entityGlow.add(p.x, p.y + h * 0.55, p.z, OBSIDIAN.violet, 5.5, 0.14);
      this.entityGlow.add(p.x, p.y + 0.06, p.z, OBSIDIAN.glint, 3.6, 0.3 * beat);
      // On the floor, where the slam that ends the shine lands: a red line at its real reach (COLOSSUS.slamRadius;
      // the old pale rim pulsed out to 1.9 and the slam hit to 2.64) and a second one running out to it as the slam
      // nears, like the Warden's filling disc. Rings of the shared Vfx pool: no new mesh or material.
      if (this.fresh) {
        const t = 1 - Math.max(0, Math.min(1, left / COLOSSUS.shineTicks));
        const q = this.v.copy(p).setY(p.y + 0.05);
        this.vfx.ring(q, COLOSSUS_REACH * 0.97, COLOSSUS_REACH, '#ff4f3a', 2, 0.6 + (s.tick % 4 < 2 ? 0.35 : 0));
        const c = Math.max(0.1, COLOSSUS_REACH * t);
        this.vfx.ring(q, c * 0.97, c, '#ff8a6e', 2, 0.45 + 0.4 * t);
      }
      // The last second it flickers: the slam is coming.
      if (this.fresh && left < 12 && s.tick % 2 === 0) this.vfx.ring(p.clone().setY(p.y + h * 0.5), 0.6, 1.6, '#ff8a6e', 3, 0.6);
      if (this.fresh && s.tick % (low ? 3 : 1) === 0) {
        const a = Math.random() * TAU;
        this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * 0.7, h * (0.3 + Math.random() * 0.6), Math.sin(a) * 0.7)), { n: 1, color: '#ffffff', speed: 0.01, up: 0.01, size: 0.12, life: 5, gravity: 0 });
      }
      return;
    }
    this.entityGlow.add(p.x, p.y + 0.06, p.z, OBSIDIAN.heart, 1.7, 0.16);
    if (!this.fresh) return;
    const beat = (s.tick + e.id) % 12;
    if (beat % 4 === 0) {
      const a = Math.random() * TAU;
      this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * 0.4, h * (0.3 + Math.random() * 0.5), Math.sin(a) * 0.4)), { n: 1, color: '#d9ccff', speed: 0.004, up: 0.004, size: 0.07, life: 6, gravity: 0 });
    }
    const fwd = this.v.set(Math.sin(yaw), 0, Math.cos(yaw));
    if (e.anim === 'walk' && beat % 4 === 0) {
      // Each step cracks the ground a little.
      const side = beat % 8 === 0 ? 1 : -1;
      const fp = p.clone().add(new THREE.Vector3(fwd.z * side * 0.35, 0.02, -fwd.x * side * 0.35));
      this.vfx.ring(fp, 0.05, 0.36, '#d9ccff', 10, 0.5);
    }
    if (e.anim === 'attack' && e.animT === 2) {
      const hit = p.clone().addScaledVector(fwd, 1.1);
      this.vfx.ring(hit, 0.1, 1.3, '#d9ccff', 7, 1);
      this.vfx.burst(hit.clone().setY(hit.y + 0.15), { n: low ? 6 : 12, color: '#eee8ff', speed: 0.08, up: 0.1, size: 0.07, life: 8, gravity: 0.014 });
      this.shake = Math.max(this.shake, 0.14);
    }
  }

  /** The colossus breaks: its heart bursts red-violet and it falls apart in a storm of black glass. */
  private colossusFalls(p: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    const q = p.clone().setY(p.y - 0.35);
    this.vfx.ring(q, 0.5, 8, '#f4f0ff', 18, 1);
    this.vfx.ring(q, 0.3, 4.5, '#ff3d74', 12, 0.9);
    this.vfx.shaft(q, q.clone().setY(q.y + 5), '#d9ccff', 10, 0.9);
    this.vfx.burst(q.clone().setY(q.y + 1.4), { n: low ? 30 : 70, color: '#eee8ff', speed: 0.13, up: 0.12, size: 0.09, life: 20, gravity: 0.012 });
    this.vfx.burst(q.clone().setY(q.y + 1.3), { n: low ? 10 : 24, color: '#ff3d74', speed: 0.09, up: 0.1, size: 0.12, life: 14, gravity: 0.006 });
    this.vfx.burst(q.clone().setY(q.y + 1), { n: low ? 10 : 22, color: '#1e1a26', speed: 0.06, up: 0.05, size: 0.9, life: 26, smoke: true, spread: 0.8 });
    this.flare(q.clone().setY(q.y + 1.3), '#ff3d74', 6, 10);
    this.flare(q.clone().setY(q.y + 1.3), '#ffffff', 3, 6);
    this.fling(q, low ? 10 : 22, 1.3, true);
    this.shake = Math.max(this.shake, 0.9);
  }

  private drawWorks(s: BattleState, use: (k: string) => ModelBatch): void {
    let golem = 0;
    const joins = this.map.land ? this.wallJoins(s) : null;
    for (const w of this.works) {
      const p = this.standAt(w.q, w.r, w.foot);
      const key = `struct:${w.structure}:${w.level}:${w.material}`;
      const scale = this.lib.scaleOf(key) * footScale(w.foot);
      let yaw = w.foot ? footYaw(w.q, w.r, w.foot) : 0;
      if (w.foot && !w.upgrade) this.plinth(p, w.q, w.r, w.foot, use);
      if (w.structure === 'gate' || w.structure === 'wall') yaw = this.gateYaw.get(`${w.q},${w.r}`) ?? Math.atan2(p.x, p.z);
      const links = !w.upgrade && w.structure === 'wall' ? joins?.get(`${w.q},${w.r}`) : undefined;
      const gateLinks = !w.upgrade && w.structure === 'gate' ? joins?.get(`${w.q},${w.r}`) : undefined;
      yaw = this.runYaw(gateLinks) ?? yaw;
      // Only what the golems are on rises in scaffolding: a plan, or a paid job waiting for a golem, is its ghost.
      if (w.stage === 'blueprint' || !w.active) {
        if (w.upgrade) {
          this.entityGlow.add(p.x, p.y + 0.2, p.z, new THREE.Color('#7fb8ff'), 1.2, 0.35);
          continue;
        }
        // A ring run has no free-standing model to sketch.
        if (w.structure === 'rampart') {
          this.entityGlow.add(p.x, p.y + 0.2, p.z, new THREE.Color('#7fb8ff'), 1.2, 0.35);
          continue;
        }
        const b = use(`bp:${w.structure}:${w.level}:${w.material}`);
        this.addRun(b, p, yaw, scale, links, scale, undefined, this.lib.heightOf(key));
        this.entityGlow.add(p.x, p.y + 0.05, p.z, new THREE.Color(w.draft ? '#eceef2' : '#7fb8ff'), 1.1, w.draft ? 0.45 : 0.3);
        // A paid job waiting for a golem: its stone and timber already piled by it.
        if (w.stage === 'building' && !w.upgrade) this.bars.add(this.v.copy(p).setY(p.y + 1.1), w.progress, new THREE.Color('#7fb8ff'), 0.5);
        continue;
      }
      // Under construction: the structure rises inside its scaffold.
      if (!w.upgrade && w.structure !== 'rampart') {
        const b = use(key);
        const k = 0.25 + 0.75 * w.progress;
        this.addRun(b, p, yaw, scale, links, scale * k, new THREE.Color(0.85, 0.85, 0.9), this.lib.heightOf(key));
      }
      const sb = use('scaffold');
      this.compose(p, yaw + 0.3, 0.85 * (w.foot ? BIG : 1));
      sb.add(this.m, 0);
      this.shadows.add(p.x, p.y, p.z, w.foot ? 1.8 : 1.15);
      this.bars.add(this.v.copy(p).setY(p.y + 1.35), w.progress, new THREE.Color('#7fb8ff'), 0.6);
      if (w.active) {
        const gb = use(this.rk(`golem:${golem % 4}`));
        const a = (golem * 2.1 + 0.6) % 6.28;
        const gp = p.clone().add(new THREE.Vector3(Math.cos(a) * 0.62, 0, Math.sin(a) * 0.62));
        this.compose(gp, Math.atan2(p.x - gp.x, p.z - gp.z), this.lib.scaleOf('golem:0'));
        // The blow lands as the dust puffs (every sixth tick, offset by golem).
        const work = (((((s.tick - golem) % 6) + 6) % 6) + this.frac) / TICK_HZ + 0.375;
        gb.add(this.m, (s.tick >> 1) + golem, undefined, 1, this.act(gb, `wg${golem}`, ['work'], work));
        this.shadows.add(gp.x, gp.y, gp.z, 0.5);
        if (s.tick % 6 === golem % 6) this.vfx.burst(p.clone().setY(p.y + 0.3), { n: 2, color: '#bfa98a', speed: 0.03, up: 0.04, size: 0.18, life: 6, smoke: true });
        golem++;
      }
    }
  }

  /**
   * The gathering golems: each walks out from its store to the source, works it a while, and plods back with its
   * load held high, staggered along the way so a few on one source read as a line of workers.
   */
  private drawGathers(use: (k: string) => ModelBatch): void {
    const WALK = 0.75;
    const WORK = 2.2;
    const DROP = 0.8;
    let golem = 0;
    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    for (const g of this.gathers) {
      const ca = hexToWorld(g.store.q, g.store.r);
      const cb = hexToWorld(g.source.q, g.source.r);
      const len = Math.hypot(cb.x - ca.x, cb.y - ca.y);
      // Stop a little short of each end: by the store's door, beside the wood or the rock.
      const k0 = Math.min(0.45, 650 / Math.max(1, len));
      const k1 = 1 - Math.min(0.45, 520 / Math.max(1, len));
      const legT = ((len * (k1 - k0)) / 1000) * WORLD_PER_HEX / WALK;
      const period = legT * 2 + WORK + DROP;
      for (let i = 0; i < g.n; i++, golem++) {
        const ph = (this.now + (i * period) / Math.max(1, g.n) + golem * 0.37) % period;
        let k: number;
        let carrying = false;
        let clip = 'walk';
        if (ph < legT) k = k0 + (k1 - k0) * (ph / legT);
        else if (ph < legT + WORK) {
          k = k1;
          clip = 'work';
        } else if (ph < legT * 2 + WORK) {
          k = k1 - (k1 - k0) * ((ph - legT - WORK) / legT);
          carrying = true;
          clip = 'carry';
        } else {
          k = k0;
          clip = 'work';
        }
        const x = ca.x + (cb.x - ca.x) * k + ((golem * 97) % 7 - 3) * 40;
        const y = ca.y + (cb.y - ca.y) * k + ((golem * 53) % 7 - 3) * 40;
        const p = simToWorld(x, y, a, this.heightAt(x, y));
        const out = clip === 'walk' || (clip === 'work' && k === k1);
        simToWorld(out ? cb.x : ca.x, out ? cb.y : ca.y, b, 0);
        const yaw = Math.atan2(b.x - p.x, b.z - p.z);
        const gb = use(this.rk(`golem:${golem % 4}`));
        this.compose(p, yaw, this.lib.scaleOf('golem:0'));
        gb.add(this.m, golem, undefined, 1, this.act(gb, `gg${golem}`, [clip, 'walk'], this.now + golem * 0.21));
        this.shadows.add(p.x, p.y, p.z, 0.5);
        if (carrying) {
          const load = use(LOAD[g.kind]);
          this.compose(p.clone().setY(p.y + 0.62), yaw, 0.34);
          load.add(this.m, 0);
        }
      }
    }
  }

  /** Consecrated ground: the sigil (HolyLayer), rising motes, a pulse with every heal, pale fire if it burns. */
  private drawSanctuary(s: BattleState, z: Zone, p: THREE.Vector3): void {
    const r = (z.radius / 1000) * WORLD_PER_HEX;
    const burn = z.damage > 0;
    this.holy.sanctuary(z.id, p.clone().setY(p.y - 0.03), r, s.tick, z.ticks, burn);
    const fade = Math.min(1, z.ticks / 8);
    for (let i = 0; i < 2; i++) {
      const a = Math.random() * TAU;
      const d = Math.sqrt(Math.random()) * r * 0.92;
      this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * d, 0.05, Math.sin(a) * d)), { n: 1, color: i ? '#ffffff' : '#cfe6ff', speed: 0.003, up: 0.02, size: 0.09, life: 18, gravity: -0.0025, spread: 0.05 });
    }
    if (z.every > 0 && z.ticks % z.every === 0 && fade > 0.5) {
      this.vfx.ring(p, r * 0.12, r, burn ? '#ffe8d4' : '#dcecff', 8, 0.7);
      this.vfx.burst(p.clone().setY(p.y + 0.1), { n: 10, color: '#eef6ff', speed: 0.004, up: 0.03, size: 0.1, life: 14, gravity: -0.002, spread: r * 1.1 });
    }
    if (!burn) return;
    // Consecrated: pale flames lick at the rim and here and there inside, burning what stands on it.
    for (let i = 0; i < 3; i++) {
      const a = Math.random() * TAU;
      const d = i === 2 ? Math.random() * r * 0.8 : r * (0.86 + Math.random() * 0.12);
      const fp = p.clone().add(this.v.set(Math.cos(a) * d, 0.04, Math.sin(a) * d));
      this.vfx.burst(fp, { n: 2, color: i === 1 ? '#cfe3ff' : '#fff0e2', speed: 0.005, up: 0.045, size: 0.15, life: 7, gravity: -0.003, spread: 0.08 });
    }
    for (let i = 0; i < 5; i++) {
      const a = Math.random() * TAU;
      const d = r * (0.9 + Math.random() * 0.08);
      const fp = p.clone().add(this.v.set(Math.cos(a) * d, 0, Math.sin(a) * d));
      this.vfx.shaft(fp, fp.clone().setY(fp.y + 0.2 + Math.random() * 0.3), i % 2 ? '#ffd8b8' : '#fff1e4', 2, 0.07 + Math.random() * 0.05);
    }
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * TAU + s.tick * 0.03;
      const k = ((s.tick * 7 + i * 13) % 5) / 4;
      this.entityGlow.add(p.x + Math.cos(a) * r * 0.94, p.y + 0.1 + k * 0.05, p.z + Math.sin(a) * r * 0.94, WARM, 0.38 + k * 0.2, (0.3 + k * 0.25) * fade);
    }
  }

  /** The cleric's spells as cast: the arc of a mend, the fall of a sanctuary, the sunrise of dawn. */
  private holyCast(s: BattleState, skill: 'mend' | 'sanctuary' | 'dawn', h: Hero, p: THREE.Vector3, tp: THREE.Vector3): void {
    if (skill === 'mend') {
      const to = this.v.copy(tp).sub(p).setY(0);
      const flat = to.length();
      if (flat > 0.01) to.multiplyScalar(0.3 / flat);
      const a = p.clone().add(to).setY(p.y + 1.15);
      const b = tp.clone().setY(tp.y + 0.6);
      const self = flat < 0.3;
      if (self) a.set(b.x, b.y + 1.6, b.z);
      const c = a.clone().lerp(b, 0.5);
      c.y += self ? 0 : 0.45 + a.distanceTo(b) * 0.16;
      // The arc travels from the staff to the ally over three ticks, a bright spark at its head.
      const N = 18, STEPS = 3;
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= N; i++) {
        const t = i / N;
        pts.push(new THREE.Vector3(
          (1 - t) * (1 - t) * a.x + 2 * (1 - t) * t * c.x + t * t * b.x,
          (1 - t) * (1 - t) * a.y + 2 * (1 - t) * t * c.y + t * t * b.y,
          (1 - t) * (1 - t) * a.z + 2 * (1 - t) * t * c.z + t * t * b.z,
        ));
      }
      for (let k = 0; k < STEPS; k++) {
        const run = (): void => {
          const i0 = Math.floor((k * N) / STEPS), i1 = Math.floor(((k + 1) * N) / STEPS);
          for (let i = i0; i < i1; i++) {
            const p0 = pts[i] as THREE.Vector3, p1 = pts[i + 1] as THREE.Vector3;
            this.vfx.shaft(p0, p1, '#9fc8f0', 7 - k, 0.11);
            this.vfx.beam(p0, p1, '#ffffff', 5 - k, 0.018);
          }
          const head = pts[i1] as THREE.Vector3;
          this.flare(head.clone(), '#e6f4ff', 0.7, 3);
          this.vfx.burst(head, { n: 3, color: '#e6f4ff', speed: 0.012, up: 0.01, size: 0.08, life: 8, gravity: 0.001 });
        };
        if (k === 0) run();
        else this.later.push({ n: k, run });
      }
      this.flare(a, '#dcecff', 0.9, 5);
      return;
    }
    if (skill === 'sanctuary') {
      const r = (SKILLS.sanctuary.radius / 1000) * WORLD_PER_HEX;
      this.vfx.ring(tp, 0.2, r, '#eaf4ff', 7, 1);
      this.vfx.shaft(tp.clone().setY(tp.y + 5.5), tp, '#bcdcff', 6, 0.8);
      this.vfx.shaft(tp.clone().setY(tp.y + 5.5), tp, '#ffffff', 4, 0.22);
      this.vfx.burst(tp.clone().setY(tp.y + 0.15), { n: 18, color: '#e6f2ff', speed: 0.05, up: 0.04, size: 0.12, life: 12, gravity: -0.001, spread: r * 0.6 });
      this.flare(tp.clone().setY(tp.y + 1.0), '#cfe4ff', 1.3, 8);
      this.flare(p.clone().setY(p.y + 1.25), '#ffffff', 0.8, 5);
      return;
    }
    // Dawn: a column of light on the cleric, shafts of sun all around, and rings of daybreak to the edge.
    const R = (SKILLS.dawn.radius / 1000) * WORLD_PER_HEX;
    const up = p.clone().setY(p.y + 8);
    this.vfx.shaft(p, up, '#9cc4ff', 16, 0.65);
    this.vfx.shaft(p, up, '#ffffff', 12, 0.2);
    this.vfx.ring(p, 0.3, R, '#fff3e8', 12, 1);
    this.vfx.ring(p, 0.2, R * 0.6, '#ffffff', 7, 0.8);
    this.vfx.ring(p.clone().setY(p.y + 0.2), 0.3, R * 1.04, '#cfe4ff', 18, 0.65);
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU + (s.tick % 7) * 0.13;
      const d = R * (0.3 + ((i * 7) % 5) * 0.14);
      const g = p.clone().add(this.v.set(Math.cos(a) * d, 0.02, Math.sin(a) * d));
      const from = p.clone().add(this.v.set(Math.cos(a) * 0.5, 5.2, Math.sin(a) * 0.5));
      this.vfx.shaft(from, g, i % 2 ? '#e8d8c8' : '#bcd4f4', 9 + (i % 4) * 2, 0.12 + (i % 3) * 0.06);
    }
    this.vfx.burst(p.clone().setY(p.y + 0.2), { n: 50, color: '#f2f7ff', speed: 0.13, up: 0.04, size: 0.14, life: 22, gravity: -0.002, spread: 1.2 });
    this.vfx.burst(p.clone().setY(p.y + 1.3), { n: 24, color: '#ffffff', speed: 0.05, up: 0.08, size: 0.1, life: 18, gravity: 0.001 });
    this.flare(p.clone().setY(p.y + 1.6), '#bdb6ae', 3.2, 16);
    this.flare(p.clone().setY(p.y + 1.6), '#ffffff', 0.9, 12);
    this.shake = Math.max(this.shake, 0.12);
    void h;
  }

  /** Impacts of holy light: a lance on a nightmare (smite), a pillar raising a hero (revive), a mend landing. */
  private holyBoom(kind: 'smite' | 'revive' | 'mend', p: THREE.Vector3): void {
    if (kind === 'smite') {
      const top = p.clone().add(this.v.set(0.35, 6, 0.2));
      this.vfx.shaft(top, p, '#bcdcff', 5, 0.32);
      this.vfx.beam(top, p, '#ffffff', 3, 0.025);
      this.vfx.ring(p, 0.1, 0.9, '#eaf4ff', 6, 1);
      this.vfx.burst(p.clone().setY(p.y + 0.15), { n: 14, color: '#f2f8ff', speed: 0.08, up: 0.07, size: 0.1, life: 8, gravity: 0.004 });
      this.flare(p.clone().setY(p.y + 0.3), '#dcecff', 1.6, 5);
    } else if (kind === 'revive') {
      const top = p.clone().setY(p.y + 7);
      this.vfx.shaft(p, top, '#a8ccff', 18, 0.75);
      this.vfx.shaft(p, top, '#ffffff', 14, 0.22);
      this.vfx.ring(p, 0.2, 1.4, '#ffffff', 10, 1);
      this.vfx.ring(p, 0.1, 0.8, '#cfe4ff', 14, 0.8);
      this.vfx.burst(p.clone().setY(p.y + 0.1), { n: 30, color: '#eef6ff', speed: 0.02, up: 0.05, size: 0.11, life: 20, gravity: -0.003, spread: 0.6 });
      this.flare(p.clone().setY(p.y + 0.8), '#eaf4ff', 2.4, 14);
    } else {
      this.vfx.burst(p.clone().setY(p.y + 0.6), { n: 14, color: '#e6fff0', speed: 0.05, up: 0.04, size: 0.09, life: 9, gravity: -0.001 });
      this.vfx.ring(p, 0.1, 0.7, '#d6ffe4', 6, 0.9);
      this.flare(p.clone().setY(p.y + 0.6), '#dfffea', 1.2, 6);
    }
  }

  /**
   * The moment a hero casts (the warrior, the artificer's turret, the mage, the ranger): what reads at the hero and
   * where the skill lands. What lasts (a rain, a wall, a cloud) is drawn from its zone; false if not one of these.
   */
  private heroCast(s: BattleState, skill: string, h: Hero, p: THREE.Vector3, to: THREE.Vector3): boolean {
    const low = this.opts.quality === 'low';
    const chest = p.clone().setY(p.y + 0.8);
    switch (skill) {
      case 'challenge': {
        // A war cry: red shockwaves rolling out, and a red line to each nightmare it pulls.
        const r = (SKILLS.challenge.radius / 1000) * WORLD_PER_HEX;
        this.vfx.ring(p, 0.3, r, '#ff4f3a', 9, 1);
        this.vfx.ring(p.clone().setY(p.y + 0.5), 0.2, r * 0.7, '#ff8a6e', 7, 0.7);
        this.later.push({ n: 1, run: () => this.vfx.ring(p, 0.2, r * 0.55, '#ff8a6e', 7, 0.6) });
        this.flare(chest, '#ff4f3a', 2, 5);
        for (const e of s.enemies) {
          if (e.taunter !== h.id) continue;
          const q = simToWorld(e.x, e.y, new THREE.Vector3(), this.heightAt(e.x, e.y) + 0.9);
          this.vfx.beam(chest, q, '#ff4f3a', 4, 0.035, 0.25);
          this.vfx.burst(q, { n: low ? 2 : 4, color: '#ff8a6e', speed: 0.03, up: 0.05, size: 0.09, life: 7 });
        }
        this.shake = Math.max(this.shake, 0.12);
        return true;
      }
      case 'oath': {
        // Unbreakable: a dome of warm light over the warrior, a pillar through it, embers rising.
        const r = (SKILLS.oath.radius / 1000) * WORLD_PER_HEX;
        this.skillFx.dome(p, 1.5, '#ffe2c4', 1.1);
        this.vfx.ring(p, 0.3, r, '#ffd6b8', 10, 0.9);
        this.vfx.shaft(p, p.clone().setY(p.y + 5), '#ffe6cc', 12, 0.6);
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 12 : 28, color: '#ffe8cf', speed: 0.05, up: 0.1, size: 0.12, life: 14, gravity: -0.003, spread: 0.6 });
        this.flare(chest, '#ffe2c4', 2.6, 8);
        this.shake = Math.max(this.shake, 0.15);
        return true;
      }
      case 'turret': {
        // Dropped in: a cyan shaft from above and a rune-ring where the turret stands.
        this.vfx.shaft(to.clone().setY(to.y + 4), to, '#9feaff', 4, 0.3);
        this.vfx.ring(to, 0.2, 1.1, '#4fdcff', 6, 0.8);
        this.vfx.burst(to.clone().setY(to.y + 0.2), { n: low ? 4 : 10, color: '#bfefff', speed: 0.07, up: 0.06, size: 0.07, life: 7, gravity: 0.008 });
        return true;
      }
      case 'frostWall': {
        // A breath of frost from the staff along the line, the spikes come up from the zone.
        this.vfx.burst(chest, { n: low ? 4 : 10, color: '#cfeeff', speed: 0.05, up: 0.02, size: 0.12, life: 8 });
        this.vfx.ring(to, 0.2, 2.2, '#bfe8ff', 8, 0.8);
        this.vfx.burst(to.clone().setY(to.y + 0.3), { n: low ? 6 : 14, color: '#e6f6ff', speed: 0.08, up: 0.04, size: 0.08, life: 12, gravity: 0.006, spread: 1.2 });
        this.flare(to.clone().setY(to.y + 0.5), '#9fdcff', 2.2, 6);
        return true;
      }
      case 'storm': {
        // The staff lifts and the sky answers: a bolt from the staff up into the cloud.
        this.vfx.beam(chest.clone().setY(chest.y + 0.6), to.clone().setY(to.y + 3.3), '#bcd6ff', 3, 0.02, 0.8);
        this.vfx.ring(to, 0.3, 2.6, '#8fb4ff', 10, 0.6);
        this.flare(chest.clone().setY(chest.y + 0.6), '#bcd6ff', 1.4, 4);
        return true;
      }
      case 'arrowRain': {
        // The volley goes up in a sheaf off the bow, a ring of air round the ranger: the rain comes down a moment later.
        this.skillFx.volley(p, to);
        this.vfx.ring(p, 0.2, 1.4, '#dfe8f4', 5, 0.6);
        this.vfx.ring(to, 0.2, 1.2, '#ff8a6e', 6, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 2 : 5, color: '#6d6458', speed: 0.05, up: 0.02, size: 0.3, life: 10, smoke: true });
        this.flare(chest, '#eceef2', 1.6, 3);
        return true;
      }
      case 'multishot': {
        // A sharpshooter's volley: the bow flashes, a gust of air fans out from it and five streaks spread off
        // the string toward where it aimed (the arrows themselves fly as projectiles, each with its trail).
        const dir = to.clone().sub(p).setY(0);
        if (dir.lengthSq() < 0.01) dir.set(0, 0, 1);
        dir.normalize();
        const bow = chest.clone().addScaledVector(dir, 0.35);
        for (let i = -2; i <= 2; i++) {
          const d = dir.clone().applyAxisAngle(this.yAxis, i * 0.15);
          this.vfx.beam(bow.clone().addScaledVector(d, 0.1), bow.clone().addScaledVector(d, 2.6), '#eef4ff', 2, 0.018);
          this.vfx.shaft(bow.clone().addScaledVector(d, 0.2), bow.clone().addScaledVector(d, 2.2), '#9fb4d0', 3, 0.09);
        }
        this.skillFx.gust(bow.clone().setY(bow.y - 0.25), dir, 2.4, '#dfe8f4');
        this.skillFx.gust(p.clone().setY(p.y + 0.06), dir, 1.4, '#9fb4d0', 0.42);
        this.vfx.burst(p.clone().addScaledVector(dir, 0.4).setY(p.y + 0.08), { n: low ? 2 : 4, color: '#6d6458', speed: 0.05, up: 0.015, size: 0.26, life: 9, smoke: true, spread: 0.3 });
        this.flare(bow, '#ffffff', 1.5, 3);
        this.flare(bow, '#ff8a6e', 0.6, 2);
        return true;
      }
      case 'net': {
        // The throw: a twist of rope fibres off the hand and a short swish of air.
        const dir = to.clone().sub(p).setY(0);
        if (dir.lengthSq() < 0.01) dir.set(0, 0, 1);
        dir.normalize();
        this.vfx.burst(chest, { n: low ? 3 : 7, color: '#e3ddd2', speed: 0.05, up: 0.04, size: 0.06, life: 7, gravity: 0.006 });
        this.skillFx.gust(chest.clone().addScaledVector(dir, 0.3).setY(chest.y - 0.2), dir, 1.4, '#cfd8e6', 0.3);
        return true;
      }
      case 'aegis': {
        // On whom it lands (a hero or the keep): a dome of pale light settling over it.
        this.skillFx.dome(to, 1.3, '#cfe0ff', 1);
        return false;
      }
    }
    return false;
  }

  /** Dust where the skills' arrows and ice hit the ground (queued by skillfx.ts, dressed here every frame). */
  private skillImpacts(): void {
    const low = this.opts.quality === 'low';
    let n = 0;
    for (const im of this.skillFx.impacts) {
      if (im.kind === 'arrow') {
        // Dust on one bite in two (a rain's dozens a second would fog the circle pale).
        if (n++ % (low ? 4 : 2) === 0) this.vfx.burst(im.p.clone().setY(im.p.y + 0.05), { n: 1, color: '#4f4942', speed: 0.025, up: 0.02, size: 0.24, life: 8, smoke: true });
        else if (low) continue;
        if (!low) this.vfx.burst(im.p.clone().setY(im.p.y + 0.05), { n: 2, color: '#cfc3a4', speed: 0.04, up: 0.05, size: 0.04, life: 4, gravity: 0.01 });
      } else {
        this.vfx.burst(im.p, { n: low ? 3 : 7, color: '#e6f6ff', speed: 0.09, up: 0.07, size: 0.08, life: 9, gravity: 0.012 });
        this.vfx.burst(im.p, { n: 1, color: '#cfeeff', speed: 0.02, up: 0.02, size: 0.4, life: 10, smoke: true });
      }
    }
    this.skillFx.impacts.length = 0;
  }

  /**
   * What a hero's kit shows on it: a frenzy's red heat, an aegis's pale shell, the bear's green breath, a
   * cleric's inspiration as motes of light circling up its arms.
   */
  /**
   * A hero wearing an awakened unique (docs/PROGRESION.md) carries it on them, growing with its highest stage:
   * I a slow dragon-red glow at the feet; II embers rising off it; III a red halo at the chest and a ring now and
   * then; IV a crown of fire over its head.
   */
  private awakenGlow(s: BattleState, h: BattleState['heroes'][number], p: THREE.Vector3): void {
    let lv = 0;
    for (const x of h.uqLv ?? []) if (x > lv) lv = x;
    if (lv <= 0) return;
    const beat = ((s.tick + h.id) % 12) / 12;
    const t = this.now;
    // I: a slow dragon-red glow at the feet, ringed by a turning band of embers (a contour: it reads over the selection ring).
    this.entityGlow.add(p.x, p.y + 0.05, p.z, RED, 1.0 + lv * 0.25, 0.22 + lv * 0.06 + Math.sin(beat * Math.PI * 2) * 0.06);
    const band = 4 + lv * 2;
    for (let i = 0; i < band; i++) {
      const a = (i / band) * Math.PI * 2 + t * 0.4;
      this.entityGlow.add(p.x + Math.cos(a) * 0.62, p.y + 0.08, p.z + Math.sin(a) * 0.62, RED, 0.2, 0.55);
    }
    if (lv >= 2 && this.fresh && (s.tick + h.id) % Math.max(1, 5 - lv) === 0) {
      this.vfx.burst(this.vq.copy(p).setY(p.y + 0.2), { n: lv >= 4 ? 2 : 1, color: lv >= 4 ? '#ffb070' : '#ff6a3d', speed: 0.012, up: 0.045 + lv * 0.006, size: 0.07 + lv * 0.01, life: 12 + lv * 2, gravity: -0.002, spread: 0.35 });
    }
    if (lv >= 3) {
      this.entityGlow.add(p.x, p.y + 0.7, p.z, ROAR_RED, 0.9, 0.22);
      if (this.fresh && (s.tick + h.id) % 36 === 0) this.vfx.ring(this.vq.copy(p).setY(p.y + 0.08), 0.3, 1.1, '#ff4f3a', 8, 0.5);
    }
    if (lv >= 4) {
      // IV: a crown of fire over the head, its points turning.
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 - t * 0.6;
        this.entityGlow.add(p.x + Math.cos(a) * 0.26, p.y + 1.18 + (i % 2) * 0.08, p.z + Math.sin(a) * 0.26, i % 2 ? RED : ROAR_RED, 0.3, 0.95);
      }
      this.entityGlow.add(p.x, p.y + 1.18, p.z, RED, 0.6, 0.35);
      this.entityGlow.add(p.x, p.y + 0.9, p.z, ROAR_RED, 1.4, 0.18);
      if (this.fresh && (s.tick + h.id) % 3 === 0) {
        const a = ((s.tick + h.id) % 24) / 24 * Math.PI * 2;
        this.vfx.burst(this.vq.copy(p).add(this.v.set(Math.cos(a) * 0.26, 1.22, Math.sin(a) * 0.26)), { n: 1, color: '#ff8a3a', speed: 0.004, up: 0.03, size: 0.09, life: 8, gravity: -0.001, spread: 0.05 });
      }
    }
  }

  private kitGlow(s: BattleState, id: number, p: THREE.Vector3, frenzy: number, shield: number, bear: boolean, inspire: number, roar = 0): void {
    // Deep roots: a green glow at its feet, leaves stirring.
    // Ancestral fury and avatar of light: a halo of their colour while they last; the last line, a red rim.
    const hh = s.heroes.find((x) => x.id === id);
    if ((hh?.areaBlow ?? 0) > 0) this.entityGlow.add(p.x, p.y + 0.6, p.z, hh?.areaHoly ? HOLY_COLD : ROAR_RED, 1.7, 0.3 + ((s.tick + id) % 4) * 0.03);
    if ((hh?.undying ?? 0) > 0) this.entityGlow.add(p.x, p.y + 0.05, p.z, RED, 1.2, 0.35);
    if ((hh?.intercede ?? 0) > 0 && (s.tick + id) % 8 < 4) this.entityGlow.add(p.x, p.y + 1.4, p.z, HOLY_COLD, 0.5, 0.6);
    // The hidden shot fades the ranger into a dark haze; a choir, an atonement, a blessed weapon and a sacrifice glow pale.
    if ((hh?.hidden ?? 0) > 0) this.entityGlow.add(p.x, p.y + 0.5, p.z, RIFT_DARK, 1.4, 0.5);
    if ((hh?.choir ?? 0) > 0 && (s.tick + id) % 6 < 3) this.entityGlow.add(p.x, p.y + 1.3, p.z, HOLY_COLD, 0.4, 0.5);
    if ((hh?.atone ?? 0) > 0 || (hh?.blessed ?? 0) > 0) this.entityGlow.add(p.x, p.y + 0.7, p.z, HOLY_COLD, 0.6, 0.55);
    if ((hh?.sacrifice ?? 0) > 0) this.entityGlow.add(p.x, p.y + 0.05, p.z, HOLY_COLD, ((hh?.sacrificeR ?? 0) / 1000) * WORLD_PER_HEX * 1.4, 0.12);
    if ((hh?.wolf ?? 0) > 0) this.entityGlow.add(p.x, p.y + 0.4, p.z, WASP_GREEN, 1, 0.3);
    // A barbarian's bloodletting: a dark red haze, drops falling off its weapon.
    if ((s.heroes.find((x) => x.id === id)?.bleedOn ?? 0) > 0) {
      this.entityGlow.add(p.x, p.y + 0.5, p.z, ROAR_RED, 1.1, 0.25);
      if (this.fresh && (s.tick + id) % 4 === 0) this.vfx.burst(p.clone().setY(p.y + 0.7), { n: 1, color: '#b3121a', speed: 0.01, up: -0.01, size: 0.07, life: 10, gravity: 0.006, spread: 0.3 });
    }
    // A cleric's blessing: soft white motes rising off the blessed.
    if ((s.heroes.find((x) => x.id === id)?.renew ?? 0) > 0) {
      this.entityGlow.add(p.x, p.y + 0.6, p.z, HOLY_COLD, 1, 0.22);
      if (this.fresh && (s.tick + id) % 4 === 0) this.vfx.burst(p.clone().setY(p.y + 0.3), { n: 1, color: '#ffffff', speed: 0.008, up: 0.04, size: 0.09, life: 14, gravity: -0.002, spread: 0.3 });
    }
    if ((s.heroes.find((x) => x.id === id)?.rooted ?? 0) > 0) {
      this.entityGlow.add(p.x, p.y + 0.05, p.z, WASP_GREEN, 1.1, 0.35);
      if ((s.tick + id) % 5 === 0) this.vfx.burst(p.clone().setY(p.y + 0.2), { n: 1, color: '#7fc06a', speed: 0.01, up: 0.03, size: 0.1, life: 10 });
    }
    // The clan's roar: an ember-red glow round each hero it reached, sparks rising off them.
    if (roar > 0) {
      this.entityGlow.add(p.x, p.y + 0.5, p.z, ROAR_RED, 1.5, 0.22 + ((s.tick + id) % 4) * 0.03);
      if ((s.tick + id) % 4 === 0) this.vfx.burst(p.clone().setY(p.y + 0.3), { n: 1, color: '#ff8a4a', speed: 0.01, up: 0.05, size: 0.09, life: 9, gravity: -0.002 });
    }
    if (inspire > 0) {
      this.entityGlow.add(p.x, p.y + 0.9, p.z, new THREE.Color('#f4f7ff'), 1.4, 0.18 + Math.min(1, inspire / 12) * 0.14);
      if ((s.tick + id) % 2 === 0) {
        const a = ((s.tick + id) * 0.9) % TAU;
        this.vfx.burst(p.clone().add(this.v.set(Math.cos(a) * 0.45, 0.5 + ((s.tick + id) % 6) * 0.12, Math.sin(a) * 0.45)), { n: 1, color: '#ffffff', speed: 0.004, up: 0.03, size: 0.08, life: 8, gravity: -0.002 });
      }
    }
    if (frenzy > 0) {
      this.entityGlow.add(p.x, p.y + 0.6, p.z, new THREE.Color('#ff4f3a'), 1.8, 0.35 + ((s.tick + id) % 4) * 0.04);
      if ((s.tick + id) % 3 === 0) this.vfx.burst(p.clone().setY(p.y + 0.9), { n: 2, color: '#ff6a3a', speed: 0.03, up: 0.05, size: 0.1, life: 6, gravity: -0.002 });
    }
    if (shield > 0) {
      this.entityGlow.add(p.x, p.y + 0.7, p.z, new THREE.Color('#dfe8ff'), 1.7, 0.3);
      if ((s.tick + id) % 8 === 0) this.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, 0.9, '#dfe8ff', 6, 0.7);
    }
    if (bear && (s.tick + id) % 5 === 0) this.vfx.burst(p.clone().setY(p.y + 1.2), { n: 1, color: '#b3f59a', speed: 0.01, up: 0.03, size: 0.16, life: 10 });
  }

  /** Fury burns while the hero is low (rare gear). */
  /**
   * Sigrun's paint (models/dress.ts preyPaint): its own material in the barbarian's baked model, turned toward the
   * dragon's red while it has prey and back to dull red when not. No light: the material's colour and glow only. The
   * baked model is shared, so two barbarians with the same look and tier share it too (the last drawn sets it).
   */
  private preyMats = new Map<string, THREE.MeshStandardMaterial[]>();
  private lightPrey(key: string, on: boolean): void {
    let ms = this.preyMats.get(key);
    if (!ms) {
      if (!isBaked(key)) return;
      ms = [];
      for (const f of baked(key).baked.frames)
        for (const p of f.parts) {
          const m = p.material as THREE.MeshStandardMaterial;
          // (Rigged, it is its skinned twin: `prey-paint+skin`, anim/skin.ts.)
          if (m.name.startsWith(PREY_PAINT) && !ms.includes(m)) ms.push(m);
        }
      this.preyMats.set(key, ms);
    }
    for (const m of ms) {
      if (m.userData.lit === on) continue;
      m.userData.lit = on;
      m.color.copy(on ? PREY_LIT : PREY_DULL);
      m.emissive.copy(on ? PREY_LIT_E : PREY_DULL_E);
      m.emissiveIntensity = on ? 1.5 : 0.6;
    }
  }

  private furious(h: Hero): boolean {
    return !!h.stats.furyGearBp && h.downTicks === 0 && h.hp * 10000 < h.stats.maxHp * GEAR_EFFECT_FX.fury.belowBp;
  }

  /** A hero in a fury runs red-hot: its miniature pulses toward red. */
  private furyTint(t: number): THREE.Color {
    const k = 0.5 + 0.5 * Math.sin(t * 0.9);
    return this.furyCol.setRGB(1.35 + 0.35 * k, 0.62 - 0.12 * k, 0.5 - 0.12 * k);
  }
  private furyCol = new THREE.Color();
  /** Heroes seen in a fury, and how much shield each had on the last tick (to see a guard break). */
  private wasFurious = new Set<number>();
  private lastShield = new Map<number, number>();

  /** An ultimate leaves its caster in a rush of its type's colour: a rising column, a ring, a jolt. */
  private ultCast(s: BattleState, heroId: number, skill: string): void {
    const def = SKILLS[skill as keyof typeof SKILLS];
    const h = s.heroes.find((x) => x.id === heroId);
    if (!def || def.slot !== 2 || !h || def.cls !== h.cls) return;
    const look = lookOf(def.id, h.forks?.[2] ?? null, glyphTurns(def.id, h.glyphs?.[2]));
    const col = '#' + (ULT_GLOW[look]?.color ?? ULT_PHYS).getHexString();
    const p = simToWorld(h.x, h.y, new THREE.Vector3(), this.heightAt(h.x, h.y));
    const low = this.opts.quality === 'low';
    this.vfx.shaft(p.clone(), p.clone().setY(p.y + 4), col, 8, 0.45);
    this.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 1.8, col, 8, 0.9);
    this.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 6 : 16, color: col, speed: 0.06, up: 0.08, size: 0.09, life: 14, gravity: -0.002, spread: 0.4 });
    this.flare(p.clone().setY(p.y + 1), col, 2.6, 6);
    this.shake = Math.max(this.shake, 0.12);
  }

  /**
   * The hero shows what it carries (docs/PODERES.md §5): its weapon glows softly with its ultimate's type, and
   * walking it sheds a mote of that type now and then (embers, frost, sparks, drips, light). Plain blows show nothing.
   */
  private ultGlow(s: BattleState, h: Hero, p: THREE.Vector3): void {
    const look = lookOf(kitOf(h)[2]!, h.forks?.[2] ?? null, glyphTurns(kitOf(h)[2]!, h.glyphs?.[2]));
    const c = ULT_GLOW[look];
    if (!c) return;
    const a = (h.facing / 1024) * Math.PI * 2;
    // The weapon hand: a little ahead and to the right of the hero.
    const wx = p.x + Math.cos(a) * 0.22 - Math.sin(a) * 0.2;
    const wz = p.z + Math.sin(a) * 0.22 + Math.cos(a) * 0.2;
    const k = 0.75 + 0.25 * Math.sin((s.tick + h.id * 3) * 0.6);
    this.entityGlow.add(wx, p.y + 0.72, wz, c.color, 0.5, 0.42 * k);
    if (!this.fresh || this.opts.quality === 'low' || h.anim !== 'walk' || (s.tick + h.id) % 3 !== 0) return;
    this.vfx.burst(this.v.set(wx, p.y + 0.7, wz), { n: 1, color: c.mote, speed: 0.01, up: c.up, size: 0.07, life: 12, gravity: -c.up / 10, spread: 0.15 });
  }

  /**
   * What a hero's rare gear shows on it, every tick: the guard's hexagonal shell while its shield holds
   * (and a burst when it breaks), fury's flames and red crown (with a roar the moment it lights), the
   * banner over its shoulder with its reach on the ground and the towers it lifts crowned in red, and the
   * chain lightning's charge as sparks circling it, one per blow toward the next arc.
   */
  private gearGlow(s: BattleState, h: Hero, p: THREE.Vector3): void {
    const st = h.stats;
    const up = h.downTicks === 0;
    const low = this.opts.quality === 'low';
    if (st.guardBp) {
      const sh = up ? (h.shield ?? 0) : 0;
      const full = Math.max(1, Math.trunc((h.stats.maxHp * st.guardBp) / 10000));
      if (sh > 0) this.gearFx.guard(p, sh / full);
      if (this.fresh && sh <= 0 && (this.lastShield.get(h.id) ?? 0) > 0) {
        this.gearFx.guardBreak(p);
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 6 : 16, color: '#cfe8ff', speed: 0.11, up: 0.05, size: 0.08, life: 9, gravity: 0.01, spread: 0.5 });
      }
      if (this.fresh) this.lastShield.set(h.id, sh);
    }
    if (st.furyGearBp) {
      const on = this.furious(h);
      if (on) {
        this.gearFx.fury(p);
        this.entityGlow.add(p.x, p.y + 0.55, p.z, new THREE.Color('#ff4f3a'), 1.8, 0.32 + ((s.tick + h.id) % 4) * 0.04);
        if ((s.tick + h.id) % 2 === 0) this.vfx.burst(p.clone().setY(p.y + 0.25), { n: low ? 1 : 3, color: '#ffb08a', speed: 0.02, up: 0.07, size: 0.07, life: 10, gravity: -0.003, spread: 0.5 });
      }
      if (this.fresh && on && !this.wasFurious.has(h.id)) {
        // The fury lights: a roar of red heat.
        this.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, 2.2, '#ff4f3a', 8, 1);
        this.vfx.burst(p.clone().setY(p.y + 0.7), { n: low ? 10 : 26, color: '#ff6a3a', speed: 0.1, up: 0.08, size: 0.13, life: 11 });
        this.flare(p.clone().setY(p.y + 0.8), '#ff4f3a', 2.4, 6);
        this.shake = Math.max(this.shake, 0.12);
      }
      if (this.fresh) {
        if (on) this.wasFurious.add(h.id);
        else this.wasFurious.delete(h.id);
      }
    }
    if (st.bannerBp && up) {
      const range = GEAR_EFFECT_FX.banner.range;
      this.gearFx.banner(p, facingToRotY(h.facing), (range / 1000) * WORLD_PER_HEX, h.pid === this.opts.me);
      // The towers it lifts: a red crown over each, and now and then a thread of light from the banner.
      for (const tw of s.structures) {
        if (tw.destroyed || STRUCTURES[tw.type].category !== 'tower' || tw.type === 'beacon') continue;
        const c = hexToWorld(tw.q, tw.r);
        if ((c.x - h.x) * (c.x - h.x) + (c.y - h.y) * (c.y - h.y) > range * range) continue;
        const tp = this.positions.get(tw.id);
        if (!tp) continue;
        this.entityGlow.add(tp.x, tp.y + 1.5, tp.z, new THREE.Color('#ff6a50'), 0.9, 0.45 + ((s.tick + tw.id) % 6) * 0.03);
        if (this.fresh && (s.tick + tw.id) % 30 === 0) this.vfx.shaft(p.clone().setY(p.y + 1.6), tp.clone().setY(tp.y + 1.4), '#ff8a6e', 8, 0.08);
      }
    }
    if (st.chainBp && up && (h.gearHits ?? 0) > 0) {
      const n = h.gearHits ?? 0;
      for (let i = 0; i < n; i++) {
        const a = this.now * 4 + (i * Math.PI * 2) / GEAR_EFFECT_FX.chain.every;
        this.entityGlow.add(p.x + Math.cos(a) * 0.38, p.y + 0.75 + Math.sin(a * 2) * 0.05, p.z + Math.sin(a) * 0.38, new THREE.Color('#9fd0ff'), 0.22, 0.9);
      }
    }
  }

  /**
   * The offerings at the keep: a brazier's light and sparks around it for each one lit, in its own colour
   * (the braziers themselves are in gearfx.ts).
   */
  private offeringFires(s: BattleState): void {
    this.offeringSpots().forEach((o, i) => {
      const flick = ((s.tick * 7 + i * 13) % 5) * 0.04;
      this.entityGlow.add(o.p.x, o.p.y + 0.85, o.p.z, new THREE.Color(o.color), 1.0, 0.3 + flick);
      this.entityGlow.add(o.p.x, o.p.y + 0.05, o.p.z, new THREE.Color(o.color), 2.6, 0.2);
      if ((s.tick + i * 3) % 2 === 0) this.vfx.burst(o.p.clone().setY(o.p.y + 1.1), { n: 2, color: o.core, speed: 0.012, up: 0.05, size: 0.06, life: 14, gravity: -0.002, spread: 0.18 });
    });
  }

  /** The trophy bell's glow: its runes light the air around it and the ground under it red, brighter with the sash. */
  private bellGlow(s: BattleState): void {
    const p = this.bellPos();
    if (!p || !this.bell) return;
    const sash = this.bell.key.endsWith('true');
    const pulse = 0.5 + 0.5 * Math.sin(s.tick * 0.35);
    this.entityGlow.add(p.x, p.y - 0.2, p.z, new THREE.Color('#ff4f3a'), sash ? 2.4 : 1.9, (sash ? 0.6 : 0.45) + pulse * 0.15);
    this.entityGlow.add(p.x, this.heightAt(0, 0) + 0.05, p.z, new THREE.Color('#ff4f3a'), 3, 0.35 + pulse * 0.08);
    if (sash && s.tick % 3 === 0) this.vfx.burst(p.clone().setY(p.y - 0.3), { n: 1, color: '#ff8a6e', speed: 0.01, up: 0.03, size: 0.05, life: 18, gravity: -0.001, spread: 0.3 });
  }

  /** Where each lit offering's brazier stands: on the keep's plaza, a third of the way around each. */
  private offeringSpots(): { p: THREE.Vector3; color: string; core: string }[] {
    const LOOK: Record<string, [string, string]> = { oil: ['#ff4a1a', '#ffd0a0'], mortar: ['#cfe0ff', '#ffffff'], vigil: ['#2fd8c0', '#d8fff8'] };
    const R = WORLD_PER_HEX * 1.05;
    return this.offerings.map((k, i) => {
      const [color, core] = LOOK[k] ?? (LOOK.oil as [string, string]);
      const a = -Math.PI / 2 + (i * Math.PI * 2) / 3 + Math.PI / 6;
      return { p: new THREE.Vector3(Math.cos(a) * R, this.heightAt(0, 0), Math.sin(a) * R), color, core };
    });
  }

  /** The rare effects that land on one spot: guard rising, frost; false if `kind` is not one of them. */
  private gearBoom(kind: string, p: THREE.Vector3): boolean {
    const low = this.opts.quality === 'low';
    switch (kind) {
      case 'gearGuard':
        this.gearFx.guardRise(p);
        this.vfx.implode(p.clone().setY(p.y + 0.5), 1.1, { n: low ? 6 : 14, color: '#dfeeff', size: 0.07, life: 8, swirl: 0.6, lift: 0.8 });
        this.flare(p.clone().setY(p.y + 0.7), '#bfe0ff', 1.6, 5);
        return true;
      case 'gearFrost':
        this.gearFx.frost(p);
        this.vfx.burst(p.clone().setY(p.y + 0.45), { n: low ? 4 : 9, color: '#e6f6ff', speed: 0.07, up: 0.06, size: 0.06, life: 8, gravity: 0.008, spread: 0.3 });
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 1 : 3, color: '#cfeaff', speed: 0.02, up: 0.01, size: 0.4, life: 12, smoke: true, spread: 0.3 });
        return true;
      // The Unsleeping's relics: Ansel's page bursting in fire, Edda's shield holding a hero up, Kaela's seed pinning a nightmare.
      case 'anselPage':
        this.vfx.ring(p.clone().setY(p.y + 0.1), 0.2, 1.4, '#ff9a3a', 7, 0.85);
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 5 : 12, color: '#ffb060', speed: 0.08, up: 0.06, size: 0.08, life: 8, spread: 0.6 });
        this.flare(p.clone().setY(p.y + 0.6), '#ff9a3a', 1.8, 5);
        return true;
      case 'eddaShield':
        this.vfx.implode(p.clone().setY(p.y + 0.6), 1.2, { n: low ? 6 : 14, color: '#f2f6ff', size: 0.08, life: 9, swirl: 0.4, lift: 1 });
        this.vfx.ring(p.clone().setY(p.y + 0.1), 0.2, 1.2, '#dfe8ff', 8, 0.9);
        this.flare(p.clone().setY(p.y + 0.9), '#dfe8ff', 2.2, 7);
        return true;
      case 'kaelaSeed':
        this.vfx.ring(p.clone().setY(p.y + 0.05), 0.5, 0.15, '#5fae3a', 8, 0.8);
        this.vfx.burst(p.clone().setY(p.y + 0.15), { n: low ? 3 : 7, color: '#8fdc5a', speed: 0.03, up: 0.05, size: 0.06, life: 9, spread: 0.35 });
        return true;
      default:
        return false;
    }
  }

  /**
   * The rare effects that go from one body to another: chain lightning arcing between nightmares, the
   * cleave's sweep and its cuts, the executioner's cross, the echo's wisp flying back to the hero.
   */
  private gearShot(e: Extract<BattleEvent, { t: 'shot' }>): void {
    const low = this.opts.quality === 'low';
    const at = (x: number, y: number, h: number): THREE.Vector3 => simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y) + h);
    const from = at(e.x, e.y, 0.6);
    const to = at(e.tx, e.ty, 0.6);
    switch (e.kind) {
      case 'gearChain': {
        // Three strands that re-jag every frame (it crackles), a blue sheath round a white core, a fork
        // spitting off sideways, and a crack of sparks where it lands.
        this.vfx.beam(from, to, '#7fb8ff', 5, 0.13, 0.7);
        this.vfx.beam(from, to, '#dff0ff', 5, 0.06, 0.55);
        this.vfx.beam(from, to, '#ffffff', 3, 0.025, 0.4);
        const mid = from.clone().lerp(to, 0.5);
        const side = new THREE.Vector3(to.z - from.z, 0.4, from.x - to.x).normalize().multiplyScalar(0.55);
        this.vfx.beam(mid, mid.clone().add(side), '#cfe6ff', 3, 0.03, 0.35);
        this.vfx.burst(to, { n: low ? 5 : 12, color: '#e6f4ff', speed: 0.09, up: 0.05, size: 0.07, life: 5 });
        this.vfx.ring(to.clone().setY(to.y - 0.55), 0.1, 0.8, '#9fc8ff', 5, 0.8);
        this.flare(to.clone(), '#bfdcff', 1.5, 4);
        this.flare(from.clone(), '#ffffff', 1.0, 3);
        this.flashes.set(e.to, 2);
        break;
      }
      case 'gearCleave':
        this.gearFx.cleave(at(e.x, e.y, 0), at(e.tx, e.ty, 0), (GEAR_EFFECT_FX.cleave.radius / 1000) * WORLD_PER_HEX);
        this.vfx.burst(to, { n: low ? 4 : 10, color: '#ffd9cf', speed: 0.12, up: 0.02, size: 0.06, life: 5, spread: 0.6 });
        break;
      case 'gearCleaveHit':
        this.gearFx.cut(to);
        this.vfx.burst(to, { n: low ? 2 : 5, color: '#ff8a6e', speed: 0.08, up: 0.04, size: 0.07, life: 5 });
        break;
      case 'gearExecute':
        this.gearFx.execute(to);
        this.flare(to.clone(), '#ff2a1a', 2.6, 6);
        this.vfx.burst(to, { n: low ? 6 : 16, color: '#b3121a', speed: 0.1, up: 0.07, size: 0.1, life: 10, gravity: 0.01 });
        this.vfx.burst(to, { n: low ? 2 : 5, color: '#2a0d10', speed: 0.04, up: 0.03, size: 0.45, life: 12, smoke: true });
        this.shake = Math.max(this.shake, 0.1);
        break;
      case 'gearEcho': {
        const id = e.to;
        this.gearFx.echo(from, () => this.positions.get(id)?.clone() ?? null);
        this.vfx.burst(from, { n: low ? 3 : 8, color: '#bfefff', speed: 0.05, up: 0.06, size: 0.07, life: 8, gravity: -0.002 });
        break;
      }
    }
  }

  /** Where a barbarian jumped from this tick (its landing draws the arc). */
  private leapFrom: THREE.Vector3 | null = null;

  /** The new classes' blows and casts (barbarian, paladin, druid); false if `kind` is someone else's. */
  private kitBoom(kind: string, p: THREE.Vector3, r: number): boolean {
    const low = this.opts.quality === 'low';
    switch (kind) {
      // The learned skills (docs/PODERES.md §7).
      case 'lowBlow':
        this.gearFx.cut(p.clone().setY(p.y + 0.5), '#ff8a6e', 0.9, 0.2);
        this.vfx.ring(p, 0.1, 0.7, '#ff8a6e', 5, 0.7);
        return true;
      case 'overcharge':
        this.vfx.ring(p, r * 0.1, r, '#d6ccff', 7, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 8 : 18, color: '#e9e3ff', speed: 0.11, up: 0.06, size: 0.07, life: 6 });
        this.flare(p.clone().setY(p.y + 0.6), '#ffffff', 2, 3);
        return true;
      case 'blind':
      case 'blindWide':
        // The flash ahead of the paladin.
        this.vfx.ring(p.clone().setY(p.y + 0.4), r * 0.2, r, '#ffffff', 5, 0.8);
        this.flare(p.clone().setY(p.y + 1), '#ffffff', kind === 'blindWide' ? 3 : 2.4, 3);
        return true;
      case 'minePlanted':
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 2 : 4, color: '#5b524a', speed: 0.03, up: 0.02, size: 0.2, life: 8, smoke: true });
        return true;
      case 'mineBurst':
        this.vfx.ring(p, r * 0.1, r, '#ffb070', 7, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 10 : 22, color: '#c4c8d2', speed: 0.13, up: 0.08, size: 0.06, life: 9, gravity: 0.012 });
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 3 : 6, color: '#5b524a', speed: 0.04, up: 0.04, size: 0.5, life: 14, smoke: true });
        this.flare(p.clone().setY(p.y + 0.4), '#ffb070', 1.8, 3);
        this.shake = Math.max(this.shake, 0.1);
        return true;
      case 'deepRoot':
        this.vfx.ring(p, 0.2, 1.2, '#7fc06a', 8, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 4 : 8, color: '#4f7a3a', speed: 0.03, up: 0.03, size: 0.14, life: 12 });
        return true;
      // The third of each slot.
      case 'shieldSlam':
      case 'flamethrower': {
        const fire = kind === 'flamethrower';
        this.vfx.ring(p.clone().setY(p.y + 0.3), r * 0.15, r, fire ? '#ff7a2a' : '#c4c8d2', 6, 0.85);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 8 : 20, color: fire ? '#ffb347' : '#e3e6ec', speed: 0.14, up: 0.03, size: fire ? 0.14 : 0.07, life: fire ? 10 : 6, spread: 0.7, gravity: fire ? -0.002 : 0.008 });
        if (fire) this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 2 : 5, color: '#3a3430', speed: 0.06, up: 0.03, size: 0.5, life: 16, smoke: true, spread: 0.5 });
        this.shake = Math.max(this.shake, 0.08);
        return true;
      }
      case 'shieldWall':
      case 'fortifyAll':
        this.vfx.ring(p, r * 0.1, r, '#c4c8d2', 10, 0.7);
        this.flare(p.clone().setY(p.y + 0.8), '#e3e6ec', 1.8, 4);
        return true;
      case 'shieldEcho':
        this.vfx.ring(p, r * 0.05, r, '#ffffff', 12, 0.95);
        this.vfx.ring(p, r * 0.05, r * 0.7, '#c4c8d2', 9, 0.8);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 6 : 16, color: '#7a6e60', speed: 0.1, up: 0.05, size: 0.4, life: 16, smoke: true });
        this.flare(p.clone().setY(p.y + 0.8), '#ffffff', 3, 5);
        this.shake = Math.max(this.shake, 0.25);
        return true;
      case 'barricade':
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 4 : 10, color: '#c4c8d2', speed: 0.08, up: 0.06, size: 0.06, life: 6, gravity: 0.01 });
        this.shake = Math.max(this.shake, 0.08);
        return true;
      case 'shadowChains':
      case 'riftBurst':
      case 'hiddenShot':
        this.vfx.ring(p, r * 0.1, r, '#9a86d0', kind === 'riftBurst' ? 10 : 7, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 6 : 16, color: kind === 'hiddenShot' ? '#3a3d44' : '#b9a8ff', speed: 0.09, up: 0.05, size: kind === 'hiddenShot' ? 0.5 : 0.08, life: 12, smoke: kind === 'hiddenShot' });
        if (kind === 'riftBurst') {
          this.flare(p.clone().setY(p.y + 0.6), '#b9a8ff', 3, 5);
          this.shake = Math.max(this.shake, 0.2);
        }
        return true;
      case 'caltrops':
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 4 : 10, color: '#c4c8d2', speed: 0.06, up: 0.05, size: 0.05, life: 8, gravity: 0.012 });
        return true;
      case 'signalFlare':
        // A flare shot high: a red streak up, a burst of light over the field and red sparks drifting down; a ring
        // of light sweeps out over all it shows.
        this.vfx.beam(p.clone().setY(p.y + 0.2), p.clone().setY(p.y + 4), '#ffd8c8', 3, 0.04, 0);
        this.vfx.shaft(p.clone().setY(p.y + 0.2), p.clone().setY(p.y + 4), '#ff6a4a', 5, 0.14);
        this.flare(p.clone().setY(p.y + 4), '#ffffff', 3, 6);
        this.flare(p.clone().setY(p.y + 4), '#ff6a4a', 2.2, 8);
        this.vfx.burst(p.clone().setY(p.y + 4), { n: low ? 6 : 16, color: '#ff8a6e', speed: 0.06, up: 0.02, size: 0.08, life: 22, gravity: 0.004 });
        this.vfx.ring(p, r * 0.1, r, '#ff8a6e', 10, 0.55);
        return true;
      case 'cleanse':
      case 'choir':
      case 'atone':
      case 'blessWeapon':
      case 'sacrifice':
        this.vfx.ring(p, r * 0.1, r, '#eef2ff', kind === 'choir' || kind === 'sacrifice' ? 10 : 7, 0.75);
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 5 : 12, color: '#ffffff', speed: 0.04, up: 0.07, size: 0.09, life: 16, gravity: -0.002 });
        if (kind === 'choir' || kind === 'sacrifice') this.vfx.shaft(p.clone(), p.clone().setY(p.y + 4), '#eef2ff', 8, 0.4);
        return true;
      case 'kick':
        this.gearFx.cut(p.clone().setY(p.y + 0.5), '#ffffff', 1, 0.25);
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 4 : 10, color: '#7a6e60', speed: 0.1, up: 0.05, size: 0.35, life: 12, smoke: true });
        this.shake = Math.max(this.shake, 0.1);
        return true;
      case 'terrify':
        this.vfx.ring(p, r * 0.1, r, '#ff4f3a', 9, 0.85);
        this.vfx.ring(p, r * 0.1, r * 0.6, '#3a1612', 7, 0.7);
        this.shake = Math.max(this.shake, 0.15);
        return true;
      case 'duel':
        this.vfx.ring(p, r * 0.9, r, '#ff6a3a', 12, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 8 : 20, color: '#ff8a4a', speed: 0.05, up: 0.08, size: 0.1, life: 14, gravity: -0.002 });
        this.flare(p.clone().setY(p.y + 1), '#ff6a3a', 2.6, 5);
        return true;
      case 'wolfForm':
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 5 : 12, color: '#6b675f', speed: 0.06, up: 0.05, size: 0.35, life: 12, smoke: true });
        this.vfx.ring(p, 0.2, 1.1, '#9df0c4', 7, 0.6);
        return true;
      case 'rootQuake':
        this.vfx.ring(p, r * 0.1, r, '#7a5634', 12, 0.95);
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 10 : 26, color: '#5b4632', speed: 0.12, up: 0.1, size: 0.12, life: 14, gravity: 0.012 });
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 4 : 10, color: '#6b5f52', speed: 0.06, up: 0.04, size: 0.7, life: 20, smoke: true });
        this.shake = Math.max(this.shake, 0.3);
        return true;
      // The tomes' ultimates.
      case 'meteor': {
        // A meteor: a streak from the sky, a fireball, a ring of fire, the board jolts.
        const top = p.clone().add(this.v.set(-1.5, 7, -1));
        this.vfx.beam(top, p.clone().setY(p.y + 0.3), '#ffb347', 4, 0.12, 0);
        this.vfx.beam(top, p.clone().setY(p.y + 0.3), '#ffffff', 3, 0.04, 0);
        this.vfx.ring(p, r * 0.1, r, '#ff7a2a', 7, 0.95);
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 8 : 18, color: '#ffb347', speed: 0.12, up: 0.08, size: 0.11, life: 9, gravity: 0.006 });
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 2 : 5, color: '#3a3430', speed: 0.04, up: 0.04, size: 0.6, life: 18, smoke: true });
        this.flare(p.clone().setY(p.y + 0.5), '#ff7a2a', 2.4, 4);
        this.shake = Math.max(this.shake, 0.12);
        return true;
      }
      case 'lastStand':
        this.vfx.ring(p, 0.2, r, '#ff8a6e', 9, 0.9);
        this.vfx.shaft(p.clone(), p.clone().setY(p.y + 3), '#ff8a6e', 6, 0.35);
        this.flare(p.clone().setY(p.y + 1), '#ffffff', 2.4, 5);
        return true;
      case 'hound':
      case 'treant':
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 4 : 10, color: kind === 'hound' ? '#c4c8d2' : '#4f7a3a', speed: 0.07, up: 0.07, size: 0.12, life: 12, gravity: 0.008 });
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 2 : 4, color: '#5b524a', speed: 0.04, up: 0.03, size: 0.5, life: 16, smoke: true });
        this.vfx.ring(p, 0.2, 1.4, kind === 'hound' ? '#ff4f3a' : '#7fc06a', 8, 0.8);
        this.shake = Math.max(this.shake, 0.1);
        return true;
      case 'eagleEye':
        // The ranger's eye sharpens: a glint at the eye and a cold ring rolling far out over the ground.
        this.vfx.ring(p.clone().setY(p.y + 1.2), 0.1, 1.2, '#ffffff', 6, 0.8);
        this.vfx.ring(p, 0.4, 4.5, '#dfe8f4', 10, 0.45);
        this.flare(p.clone().setY(p.y + 1.3), '#ffd8c8', 1.8, 4);
        return true;
      case 'intercession':
        this.vfx.ring(p, 0.2, 1, '#ffffff', 8, 0.7);
        this.vfx.shaft(p.clone().setY(p.y + 4), p.clone(), '#eef2ff', 4, 0.25);
        return true;
      case 'intercede':
        // The fallen rises at once: a column of light, a white burst.
        this.vfx.shaft(p.clone(), p.clone().setY(p.y + 5), '#ffffff', 10, 0.6);
        this.vfx.ring(p, 0.2, 1.8, '#ffffff', 10, 1);
        this.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 8 : 20, color: '#ffffff', speed: 0.08, up: 0.09, size: 0.1, life: 16, gravity: -0.003 });
        this.flare(p.clone().setY(p.y + 1), '#ffffff', 3, 6);
        return true;
      case 'ancestralFury':
      case 'avatarOfLight': {
        const holy = kind === 'avatarOfLight';
        this.vfx.ring(p, 0.2, r, holy ? '#eef2ff' : '#ff6a3a', 10, 0.85);
        this.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 8 : 20, color: holy ? '#ffffff' : '#ff8a4a', speed: 0.1, up: 0.08, size: 0.1, life: 14, gravity: -0.001 });
        this.flare(p.clone().setY(p.y + 1), holy ? '#ffffff' : '#ff6a3a', 2.6, 5);
        this.shake = Math.max(this.shake, 0.14);
        return true;
      }
      case 'furyBlow':
      case 'avatarBlow':
        this.vfx.ring(p, 0.1, r, kind === 'avatarBlow' ? '#eef2ff' : '#ff6a3a', 5, 0.6);
        return true;
      // The tomes' medium skills.
      case 'defyShout':
        this.vfx.ring(p, r * 0.1, r, '#ff8a6e', 9, 0.85);
        this.vfx.ring(p, r * 0.05, r * 0.5, '#ffffff', 6, 0.6);
        this.flare(p.clone().setY(p.y + 1), '#ff8a6e', 2, 4);
        this.shake = Math.max(this.shake, 0.12);
        return true;
      case 'coilPlanted':
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 4 : 10, color: '#e9e3ff', speed: 0.08, up: 0.06, size: 0.06, life: 6 });
        return true;
      case 'blink':
      case 'blinkFrost': {
        const frost = kind === 'blinkFrost';
        this.vfx.ring(p, r * 0.1, r, frost ? '#bfe8ff' : '#ff7a2a', 8, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 8 : 20, color: frost ? '#e8f7ff' : '#ffb347', speed: 0.1, up: 0.07, size: 0.1, life: 10, gravity: 0.004 });
        this.flare(p.clone().setY(p.y + 0.6), frost ? '#e8f7ff' : '#ff7a2a', 2.2, 4);
        return true;
      }
      case 'blinkIn':
        this.vfx.implode(p.clone().setY(p.y + 0.6), 1, { n: low ? 6 : 14, color: '#d6ccff', size: 0.06, life: 8, swirl: 0.6, lift: 0.6 });
        this.flare(p.clone().setY(p.y + 0.7), '#ffffff', 1.4, 3);
        return true;
      case 'snareSet':
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 2 : 4, color: '#5b524a', speed: 0.03, up: 0.02, size: 0.2, life: 8, smoke: true });
        return true;
      case 'snareShut':
        this.vfx.ring(p, 0.1, 0.8, '#e3e6ec', 5, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 4 : 10, color: '#c4c8d2', speed: 0.08, up: 0.06, size: 0.05, life: 6, gravity: 0.012 });
        this.shake = Math.max(this.shake, 0.06);
        return true;
      case 'prayer':
        this.vfx.shaft(p.clone().setY(p.y + 4), p.clone(), '#eef2ff', 5, 0.25);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 3 : 8, color: '#ffffff', speed: 0.02, up: 0.05, size: 0.09, life: 14, gravity: -0.002 });
        return true;
      case 'bloodletting':
        this.vfx.ring(p, 0.2, 1.2, '#b3121a', 7, 0.8);
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 4 : 10, color: '#b3121a', speed: 0.06, up: 0.05, size: 0.08, life: 10, gravity: 0.01 });
        return true;
      case 'intervene':
        this.vfx.ring(p, 0.2, 1.4, '#eef2ff', 8, 0.9);
        this.flare(p.clone().setY(p.y + 0.8), '#ffffff', 2, 4);
        this.shake = Math.max(this.shake, 0.1);
        return true;
      // The tomes' short skills.
      case 'tuneUp':
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 5 : 12, color: '#ffd0a0', speed: 0.08, up: 0.07, size: 0.06, life: 7, gravity: 0.012 });
        this.vfx.ring(p, 0.2, 1, '#7fffb0', 6, 0.7);
        this.flare(p.clone().setY(p.y + 0.7), '#7fffb0', 1.4, 3);
        return true;
      case 'renew':
        this.vfx.ring(p, 0.2, 1, '#ffffff', 7, 0.6);
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 4 : 10, color: '#ffffff', speed: 0.02, up: 0.06, size: 0.1, life: 16, gravity: -0.002 });
        return true;
      case 'brutalBlow':
        this.gearFx.cut(p.clone().setY(p.y + 0.5), '#ff8a6e', 1.3, 0.25);
        this.vfx.ring(p, 0.1, 1.1, '#ff6a4a', 6, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 4 : 10, color: '#b3121a', speed: 0.1, up: 0.06, size: 0.08, life: 8, gravity: 0.012 });
        this.shake = Math.max(this.shake, 0.12);
        return true;
      case 'holySeal':
        this.vfx.ring(p.clone().setY(p.y + 0.05), 1, 0.3, '#ffffff', 8, 0.9);
        this.vfx.shaft(p.clone().setY(p.y + 3), p.clone(), '#eef2ff', 5, 0.25);
        this.flare(p.clone().setY(p.y + 0.6), '#ffffff', 1.8, 4);
        return true;
      case 'briarLash':
        // A sweep of thorny green ahead of the druid.
        this.vfx.ring(p.clone().setY(p.y + 0.3), r * 0.2, r, '#6fa05a', 5, 0.8);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 6 : 14, color: '#4f7a3a', speed: 0.12, up: 0.03, size: 0.08, life: 8, spread: 0.6 });
        return true;
      case 'banner':
        // The banner driven in: a crack of dust, a red ring rolling out over the allies.
        this.vfx.ring(p, r * 0.1, r, '#ff6a4a', 10, 0.75);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 3 : 7, color: '#6b5f52', speed: 0.05, up: 0.04, size: 0.45, life: 14, smoke: true });
        this.flare(p.clone().setY(p.y + 2.5), '#ff8a6e', 2.2, 4);
        this.shake = Math.max(this.shake, 0.12);
        return true;
      case 'deepFreeze':
        this.vfx.ring(p, r * 0.2, r, '#ffffff', 8, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 8 : 20, color: '#e8f7ff', speed: 0.12, up: 0.05, size: 0.08, life: 8, gravity: 0.01 });
        this.flare(p.clone().setY(p.y + 0.8), '#e8f7ff', 2.4, 3);
        return true;
      case 'arrowBurst':
        this.vfx.ring(p, r * 0.1, r, '#ff8a6e', 7, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 8 : 18, color: '#c4c8d2', speed: 0.14, up: 0.06, size: 0.07, life: 7, gravity: 0.01 });
        this.flare(p.clone().setY(p.y + 0.5), '#ffd8c8', 2, 3);
        this.shake = Math.max(this.shake, 0.1);
        return true;
      case 'dawnJudgment':
        // A pillar of dawn: a shaft of light from the sky, rings of light, motes rising, the ground shaking.
        this.vfx.shaft(p.clone().setY(p.y + 12), p.clone(), '#f4f7ff', 14, 1.2);
        this.vfx.shaft(p.clone().setY(p.y + 12), p.clone(), '#ffffff', 10, 0.5);
        this.vfx.ring(p, r * 0.1, r, '#eef2ff', 12, 0.95);
        this.vfx.ring(p, r * 0.05, r * 0.6, '#ffffff', 8, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 12 : 30, color: '#ffffff', speed: 0.12, up: 0.1, size: 0.1, life: 16, gravity: -0.002 });
        this.flare(p.clone().setY(p.y + 1.5), '#ffffff', 4, 6);
        this.shake = Math.max(this.shake, 0.22);
        return true;
      case 'rampart':
        // The wall of light rises: a ring sweeping out to every structure it covers.
        this.vfx.ring(p, r * 0.05, r, '#eef2ff', 14, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 1), { n: low ? 8 : 18, color: '#ffffff', speed: 0.08, up: 0.1, size: 0.09, life: 18, gravity: -0.003 });
        this.flare(p.clone().setY(p.y + 1.4), '#eef2ff', 3, 5);
        return true;
      case 'mortarAim':
        this.vfx.ring(p, r * 1.2, r * 0.2, '#ff4f3a', 8, 0.7);
        return true;
      case 'mortar':
      case 'mortarHeavy': {
        // A shell lands: a fireball, a ring of fire, smoke and dirt; the heavy one shakes the board.
        const heavy = kind === 'mortarHeavy';
        this.vfx.ring(p, r * 0.1, r, '#ff7a2a', heavy ? 10 : 7, 0.95);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 10 : heavy ? 34 : 22, color: '#ffb347', speed: heavy ? 0.16 : 0.12, up: 0.09, size: 0.12, life: 10, gravity: 0.006 });
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 3 : 8, color: '#3a3430', speed: 0.05, up: 0.04, size: heavy ? 0.9 : 0.6, life: 20, smoke: true });
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 3 : 8, color: '#5b524a', speed: 0.1, up: 0.1, size: 0.1, life: 12, gravity: 0.014 });
        this.flare(p.clone().setY(p.y + 0.6), '#ff7a2a', heavy ? 3.4 : 2.4, 4);
        this.shake = Math.max(this.shake, heavy ? 0.28 : 0.14);
        return true;
      }
      case 'plague':
        this.vfx.ring(p, r * 0.1, r, '#9ab84a', 9, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 4 : 10, color: '#7f9a3a', speed: 0.06, up: 0.02, size: 0.8, life: 22, smoke: true });
        return true;
      case 'clanRoar':
        // The roar: two ember rings rolling out over everyone it reaches, a gust of sparks, the ground shaking.
        this.vfx.ring(p, r * 0.1, r, '#ff6a3a', 10, 0.8);
        this.vfx.ring(p, r * 0.05, r * 0.6, '#ffb070', 7, 0.6);
        this.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 10 : 24, color: '#ff8a4a', speed: 0.12, up: 0.06, size: 0.1, life: 12, gravity: -0.001 });
        this.flare(p.clone().setY(p.y + 1), '#ff8a4a', 2.2, 4);
        this.shake = Math.max(this.shake, 0.18);
        return true;
      case 'orbBurst':
        // The orb melts in a burst of ice: shards fly and a white ring freezes the ground.
        this.vfx.ring(p, r * 0.15, r, '#bfe8ff', 8, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 10 : 22, color: '#e8f7ff', speed: 0.1, up: 0.07, size: 0.09, life: 10, gravity: 0.01 });
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 3 : 6, color: '#cfe6f4', speed: 0.03, up: 0.02, size: 0.5, life: 16, smoke: true });
        this.flare(p.clone().setY(p.y + 0.6), '#e8f7ff', 1.8, 3);
        this.shake = Math.max(this.shake, 0.08);
        return true;
      case 'decoy':
        // The stake driven into the ground: a thud of dust and a red pull going out.
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 4 : 8, color: '#5b524a', speed: 0.05, up: 0.03, size: 0.3, life: 10, smoke: true });
        this.vfx.ring(p, 0.2, 1.4, '#ff6a4a', 8, 0.6);
        return true;
      case 'hawk':
        // Off the ranger's arm: a flurry of feathers and a pale streak climbing as it takes to the air.
        this.vfx.burst(p.clone().setY(p.y + 1.2), { n: low ? 4 : 8, color: '#8a7a68', speed: 0.05, up: 0.04, size: 0.08, life: 12, gravity: 0.003 });
        this.vfx.shaft(p.clone().setY(p.y + 1.1), p.clone().add(this.v.set(0.6, 3.2, 0.4)), '#e8e2d6', 4, 0.1);
        return true;
      // Reactions between damage types (docs/PODERES.md §4).
      case 'shatter':
        // Lightning on the chilled: it bursts in ice, shards flying, a white ring and a crack of light.
        this.vfx.ring(p, r * 0.1, r, '#e8f7ff', 7, 0.95);
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 12 : 28, color: '#e8f7ff', speed: 0.13, up: 0.08, size: 0.09, life: 11, gravity: 0.012 });
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 4 : 8, color: '#d6ccff', speed: 0.09, up: 0.05, size: 0.06, life: 5 });
        this.flare(p.clone().setY(p.y + 0.7), '#ffffff', 2.4, 4);
        this.shake = Math.max(this.shake, 0.12);
        return true;
      case 'combust':
        // Fire on the poisoned: the poison goes up in a green-and-orange blast.
        this.vfx.ring(p, r * 0.1, r, '#ff9a3a', 8, 0.9);
        this.vfx.ring(p, r * 0.05, r * 0.7, '#9ab84a', 6, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 10 : 24, color: '#ffb347', speed: 0.12, up: 0.09, size: 0.12, life: 10, gravity: -0.002 });
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 4 : 8, color: '#5a7a2a', speed: 0.05, up: 0.04, size: 0.5, life: 16, smoke: true });
        this.flare(p.clone().setY(p.y + 0.6), '#ffb347', 2.6, 4);
        this.shake = Math.max(this.shake, 0.14);
        return true;
      case 'drone':
        this.vfx.burst(p.clone().setY(p.y + 1.2), { n: low ? 3 : 6, color: '#4fdcff', speed: 0.04, up: 0.03, size: 0.07, life: 8 });
        return true;
      case 'droneMend':
        // A cyan weld on the stone and sparks off it.
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 3 : 6, color: '#bff4ff', speed: 0.05, up: 0.05, size: 0.06, life: 6, gravity: 0.01 });
        this.vfx.ring(p, 0.1, 0.6, '#4fdcff', 5, 0.6);
        this.flare(p.clone().setY(p.y + 0.6), '#9feaff', 0.8, 2);
        return true;
      case 'droneSpark':
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 2 : 5, color: '#d6ccff', speed: 0.07, up: 0.04, size: 0.06, life: 4 });
        this.flare(p.clone().setY(p.y + 0.5), '#e9e3ff', 0.7, 2);
        return true;
      case 'wasps':
        this.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 6 : 14, color: '#2a2618', speed: 0.06, up: 0.02, size: 0.06, life: 8, spread: 0.6 });
        return true;
      case 'hawkStrike': {
        // The hawk's dive: a pale streak stooping from the sky onto its prey, talons raking, feathers flying.
        const top = p.clone().add(this.v.set(-0.9, 3.4, -0.7));
        const hit = p.clone().setY(p.y + 0.55);
        this.vfx.shaft(top, hit, '#e8e2d6', 3, 0.12);
        this.vfx.beam(top.lerp(hit, 0.45), hit, '#ffffff', 2, 0.015, 0);
        this.gearFx.cut(hit, '#f0e8dc', 0.8, 0.16);
        this.vfx.burst(hit, { n: low ? 3 : 6, color: '#8a7a68', speed: 0.05, up: 0.04, size: 0.07, life: 12, gravity: 0.004 });
        this.vfx.ring(p.clone().setY(p.y + 0.05), 0.1, 0.6, '#e8e2d6', 4, 0.6);
        return true;
      }
      case 'totemLight':
        // The totem's light comes down on a nightmare.
        this.vfx.shaft(p.clone().setY(p.y + 3), p, '#eef2ff', 6, 0.25);
        this.vfx.ring(p, 0.1, 0.7, '#eef2ff', 5, 0.8);
        this.flare(p.clone().setY(p.y + 0.4), '#ffffff', 1.1, 2);
        return true;
      case 'hammer':
        this.vfx.ring(p.clone().setY(p.y + 0.5), r * 0.6, r, '#eef2ff', 4, 0.35);
        return true;
      case 'hammerStun':
        this.vfx.ring(p.clone().setY(p.y + 0.5), r * 0.2, r, '#ffffff', 6, 0.8);
        this.flare(p.clone().setY(p.y + 0.6), '#ffffff', 1.6, 3);
        this.shake = Math.max(this.shake, 0.06);
        return true;
      case 'bash':
        // The shield's blow: a white star of impact (the ring and sparks follow below).
        this.gearFx.cut(p, '#ffffff', 1.1, 0.24);
        this.flare(p.clone().setY(p.y + 0.5), '#ffffff', 1.2, 3);
        this.shake = Math.max(this.shake, 0.08);
        return false;
      case 'echo':
        // An echo glyph's second cast (docs/GLIFOS.md §1): a pale ripple round the hero as the skill comes again.
        this.vfx.ring(p.clone().setY(p.y + 0.6), 0.2, 1.2, '#e9ecf1', 6, 0.7);
        this.vfx.ring(p.clone().setY(p.y + 0.6), 0.1, 0.8, '#ff8a6e', 5, 0.5);
        return true;
      case 'vortex':
        // A vortex glyph: rings closing in on the middle as it drags them.
        this.vfx.ring(p.clone().setY(p.y + 0.3), r, r * 0.25, '#c4c8d2', 8, 0.7);
        this.vfx.ring(p.clone().setY(p.y + 0.3), r * 0.7, r * 0.15, '#e9ecf1', 6, 0.5);
        return true;
      case 'whirl':
        this.skillFx.whirl(p, r);
        this.vfx.ring(p.clone().setY(p.y + 0.5), r * 0.3, r, '#eceef2', 5, 0.8);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 6 : 12, color: '#ffffff', speed: 0.12, up: 0.02, size: 0.08, life: 4, spread: r * 0.5 });
        return true;
      // The panoply, the warrior's pieces (docs/PANOPLIA.md §9.5): physical, dust and steel; never a red fill (red is the
      // nightmares' warning), and what comes back (the second slam, the echoes) smaller and colder than the first.
      // The panoply, the druid's pieces (docs/PANOPLIA.md §9.5): poison's green, earth's browns, the wolves' cold dusk;
      // never a red fill, and what comes again (the hive's pulses, the mushrooms) smaller and paler than the first.
      case 'hive':
        // The hive set down: a brown ring and a little cloud of wasps.
        this.vfx.ring(p, 0.1, r * 0.4, '#8a7448', 8, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 4 : 10, color: '#2a2618', speed: 0.05, up: 0.02, size: 0.06, life: 10, spread: 0.4 });
        return true;
      case 'hivePulse':
      case 'hivePulseQueen':
        // Each pulse: wasps boiling off the hive, the queen's with a cold ring (it slows).
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 3 : 7, color: '#2a2618', speed: 0.07, up: 0.02, size: 0.05, life: 7, spread: 0.3 });
        if (kind === 'hivePulseQueen') this.vfx.ring(p, 0.1, r * 0.5, '#b8d4ff', 6, 0.45);
        return true;
      case 'mushrooms':
        // The mushrooms letting the cloud out again: a paler, smaller puff than the first.
        this.vfx.ring(p, r * 0.1, r * 0.8, '#b8c88a', 8, 0.5);
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 3 : 7, color: '#a0b070', speed: 0.04, up: 0.03, size: 0.6, life: 20, smoke: true });
        return true;
      case 'hedge':
        // A row of briars springing up: splinters of bark and a brown ring.
        this.vfx.ring(p, 0.1, r, '#5a4030', 6, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 4 : 10, color: '#4a3424', speed: 0.08, up: 0.08, size: 0.07, life: 9, gravity: 0.012, spread: r * 0.4 });
        return true;
      case 'sprout':
        // The sprout after the root: a soft green ring and leaves rising.
        this.vfx.ring(p, r * 0.1, r, '#9df0c4', 10, 0.6);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 4 : 9, color: '#6fa05a', speed: 0.03, up: 0.05, size: 0.1, life: 16, gravity: -0.002, spread: 0.5 });
        return true;
      case 'shadeWolves':
        // The shadow wolves coming out of the druid: dusk smoke and a cold ring.
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 4 : 10, color: '#23262e', speed: 0.06, up: 0.03, size: 0.45, life: 14, smoke: true });
        this.vfx.ring(p, 0.2, r, '#cfdcff', 7, 0.5);
        return true;
      case 'howl':
        // The howl: a wide pale ring rolling out, the stars of the stunned do the rest.
        this.vfx.ring(p, r * 0.1, r, '#cfdcff', 10, 0.75);
        this.vfx.ring(p, r * 0.05, r * 0.6, '#9aa8c8', 8, 0.55);
        return true;
      case 'thornHold':
        // Caught on the thorns: a few splinters off the wall.
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 2 : 5, color: '#4a3424', speed: 0.06, up: 0.05, size: 0.06, life: 7, gravity: 0.01 });
        return true;
      case 'crack':
        // A crack running out: earth thrown up along it, a dark scar of dust.
        this.vfx.ring(p, 0.05, r, '#7a5634', 5, 0.75);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 2 : 6, color: '#5b4632', speed: 0.07, up: 0.09, size: 0.09, life: 9, gravity: 0.014, spread: r * 0.5 });
        return true;
      case 'uqLeapFrom':
        // Where a piece's leap left from (the hook, the decoy's swap): a puff of dust; its landing draws the arc.
        this.leapFrom = p.clone();
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 3 : 6, color: '#5b524a', speed: 0.04, up: 0.03, size: 0.26, life: 8, smoke: true });
        return true;
      case 'uqLeapTo':
        // The arc it flew (a trail of half a second) and a mark where it landed.
        if (this.leapFrom) this.skillFx.leap(this.leapFrom, p);
        this.leapFrom = null;
        this.vfx.ring(p, 0.15, r, '#d8cbb8', 6, 0.85);
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 4 : 9, color: '#8a7e70', speed: 0.09, up: 0.07, size: 0.08, life: 8, gravity: 0.012 });
        return true;
      case 'maceClash':
        // Two knocked into each other: a sharp ring, splinters of iron and a white spark between them.
        this.vfx.ring(p.clone().setY(p.y + 0.4), 0.05, r, '#e3e6ec', 4, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 5 : 12, color: '#c4c8d2', speed: 0.11, up: 0.05, size: 0.06, life: 6, gravity: 0.01 });
        this.flare(p.clone().setY(p.y + 0.5), '#ffffff', 1.2, 2);
        return true;
      case 'shieldSlam2':
        // The second slam: the first's sweep, smaller and colder.
        this.vfx.ring(p.clone().setY(p.y + 0.3), r * 0.15, r * 0.8, '#b8c8e0', 5, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 5 : 12, color: '#d6e0ee', speed: 0.12, up: 0.03, size: 0.06, life: 5, spread: 0.6, gravity: 0.008 });
        return true;
      case 'wallPush':
        // The shield wall shoving: a low steel ring out from the warrior and the dust kicked up under it.
        this.vfx.ring(p.clone().setY(p.y + 0.2), r * 0.4, r, '#c4c8d2', 5, 0.6);
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 4 : 10, color: '#7a6e60', speed: 0.08, up: 0.02, size: 0.3, life: 10, smoke: true, spread: r * 0.4 });
        return true;
      case 'quail':
        // The shout's end: a grey ring and dark wisps over the challenged, who falter.
        this.vfx.ring(p, r * 0.2, r, '#9aa0a8', 8, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 4 : 10, color: '#3a3d44', speed: 0.04, up: 0.04, size: 0.4, life: 14, smoke: true, spread: r * 0.5 });
        return true;
      case 'bannerLand':
        // The thrown banner driving into the ground: a ring of dust, splinters, and a flash at the pole.
        this.vfx.ring(p, r * 0.15, r, '#d8cbb8', 7, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 6 : 14, color: '#8a7e70', speed: 0.11, up: 0.09, size: 0.08, life: 9, gravity: 0.014, spread: r * 0.3 });
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 3 : 7, color: '#4a4440', speed: 0.06, up: 0.05, size: 0.35, life: 12, smoke: true, spread: r * 0.3 });
        this.flare(p.clone().setY(p.y + 1), '#e3e6ec', 1.6, 3);
        return true;
      case 'shieldEchoAgain':
        // The bell nail's echo: the shield echo's ring again, smaller and colder, without its flash.
        this.vfx.ring(p, r * 0.05, r, '#d6e0ee', 9, 0.8);
        this.vfx.ring(p, r * 0.05, r * 0.7, '#b8c8e0', 7, 0.6);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 3 : 8, color: '#7a6e60', speed: 0.08, up: 0.04, size: 0.34, life: 12, smoke: true });
        return true;
      case 'standBurst':
        // What the last line took coming back out: a pale ward ring and steel shards flying off the warrior.
        this.vfx.ring(p, r * 0.1, r, '#eef2ff', 8, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 8 : 18, color: '#c4c8d2', speed: 0.13, up: 0.06, size: 0.07, life: 7, gravity: 0.01 });
        this.flare(p.clone().setY(p.y + 0.8), '#ffffff', 2.2, 4);
        return true;
      // The barbarian's pieces: dust, iron, old blood and spirits' smoke; never a red fill.
      case 'kickAgain':
        // The kick ready again: a short pale flick at the barbarian's feet.
        this.vfx.ring(p.clone().setY(p.y + 0.1), 0.1, r, '#e3e6ec', 4, 0.7);
        return true;
      case 'carterEdge':
        // The carter's edge: a heavy chop ring and wood-like splinters.
        this.vfx.ring(p.clone().setY(p.y + 0.2), r * 0.2, r, '#d8cbb8', 5, 0.85);
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 5 : 12, color: '#8a7e70', speed: 0.1, up: 0.06, size: 0.07, life: 7, gravity: 0.012, spread: r * 0.3 });
        return true;
      case 'fearSpread':
        // The fear passing on: a dark ring and grey wisps rising from the stunned.
        this.vfx.ring(p, r * 0.2, r, '#6a6e78', 7, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 3 : 7, color: '#2e3036', speed: 0.03, up: 0.05, size: 0.36, life: 12, smoke: true, spread: r * 0.4 });
        return true;
      case 'bloodStain':
        // A stain of old blood on the ground, pulsing: a low dark ring, no fill.
        this.vfx.ring(p.clone().setY(p.y + 0.05), r * 0.6, r, '#5a1a1e', 10, 0.6);
        return true;
      case 'roarEcho':
        // The roar again, from the ally: the clan roar's ring, smaller and colder.
        this.vfx.ring(p.clone().setY(p.y + 0.4), r * 0.1, r * 0.8, '#c4c8d2', 8, 0.7);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 3 : 8, color: '#7a6e60', speed: 0.07, up: 0.03, size: 0.3, life: 10, smoke: true, spread: 0.8 });
        return true;
      // The paladin's pieces: pale light and steel; its burns holy, white, never fire.
      case 'blindBack':
        // The flash behind: a pale ring around the paladin, a white flare.
        this.vfx.ring(p.clone().setY(p.y + 0.5), r * 0.2, r * 0.6, '#eef2ff', 5, 0.6);
        this.flare(p.clone().setY(p.y + 1), '#ffffff', 1.6, 3);
        return true;
      case 'absolution':
        // The atoning blow landing around: a pale ring and white motes.
        this.vfx.ring(p.clone().setY(p.y + 0.3), r * 0.1, r, '#eef2ff', 6, 0.8);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 4 : 9, color: '#ffffff', speed: 0.06, up: 0.05, size: 0.06, life: 8, spread: r * 0.3 });
        return true;
      case 'sealBlood':
        // The seal breaking on a death: a pale ring rising and motes drifting out to those who struck.
        this.vfx.ring(p.clone().setY(p.y + 0.3), r * 0.1, r, '#e9ecf1', 8, 0.8);
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 4 : 10, color: '#eef2ff', speed: 0.05, up: 0.06, size: 0.07, life: 12, spread: 0.4 });
        return true;
      case 'stairStun':
        // Landing by the ally: steel ringing out low, dust under it.
        this.vfx.ring(p.clone().setY(p.y + 0.1), r * 0.1, r, '#e3e6ec', 6, 0.85);
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 4 : 10, color: '#7a6e60', speed: 0.08, up: 0.03, size: 0.3, life: 10, smoke: true, spread: r * 0.3 });
        return true;
      case 'alwaysThere':
        this.flare(p.clone().setY(p.y + 1), '#eef2ff', 1.2, 3);
        return true;
      case 'lightHammer':
        // A hammer of light from above: a white shaft down onto it and a ring where it strikes.
        this.vfx.shaft(p.clone().setY(p.y + 4), p.clone().setY(p.y + 0.2), '#eef2ff', 5, 0.3);
        this.vfx.ring(p.clone().setY(p.y + 0.1), 0.1, r, '#ffffff', 6, 0.9);
        this.flare(p.clone().setY(p.y + 0.6), '#ffffff', 1.6, 3);
        return true;
      // The panoply, the ranger's and the cleric's pieces (docs/PANOPLIA.md §9.5): wood, iron and venom for the one, pale
      // light for the other; never a red fill, and what comes back (a second pass, the lingering ray, the prayer's amen)
      // smaller and fainter than the first.
      case 'arrowAgain':
        // A volley's lost arrow falling again: a short pale streak down and a puff where it lands.
        this.vfx.shaft(p.clone().setY(p.y + 2.5), p.clone(), '#d8cbb8', 2, 0.05);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 2 : 5, color: '#8a7e70', speed: 0.05, up: 0.04, size: 0.06, life: 5, gravity: 0.012 });
        return true;
      case 'poisonCloud':
        // The tracker's cloud: a low venom haze rolling out from the burst one.
        this.vfx.ring(p, r * 0.2, r, VENOM, 8, 0.6);
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 5 : 12, color: '#3f5a2e', speed: 0.04, up: 0.02, size: 0.5, life: 18, smoke: true, spread: r * 0.5 });
        return true;
      case 'snareDrag':
        // The dragging snare: a tight steel ring drawn in toward the caught one.
        this.vfx.ring(p, r, r * 0.2, '#c4c8d2', 6, 0.8);
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 3 : 7, color: '#7a6e60', speed: 0.06, up: 0.02, size: 0.3, life: 10, smoke: true, spread: r * 0.4 });
        return true;
      case 'nurseAgain':
        // The nurse's staff recharging the mend: a small white ring at the cleric.
        this.vfx.ring(p.clone().setY(p.y + 0.5), 0.1, r, '#eef2ff', 5, 0.7);
        return true;
      case 'renewBurst':
        // A renewal bursting all at once: the renew's motes, quicker and in one ring.
        this.vfx.ring(p, 0.2, r, '#ffffff', 6, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 6 : 14, color: '#ffffff', speed: 0.07, up: 0.06, size: 0.1, life: 12, gravity: -0.002 });
        this.flare(p.clone().setY(p.y + 0.7), '#ffffff', 1.6, 3);
        return true;
      case 'prayerLight':
        // A holy light falling on a nightmare: a thin shaft and a pale cut.
        this.vfx.shaft(p.clone().setY(p.y + 4), p.clone(), '#eef2ff', 3, 0.15);
        this.vfx.ring(p, 0.1, r, '#eef2ff', 5, 0.8);
        return true;
      case 'prayerAgain':
        // The amen: the prayer's ring again, fainter.
        this.vfx.ring(p, 0.2, r, '#d6e0ee', 9, 0.55);
        this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 3 : 7, color: '#eef2ff', speed: 0.02, up: 0.04, size: 0.08, life: 12, gravity: -0.002 });
        return true;
      case 'dawnColumn':
        // The walking column: the judgment's shaft again, thinner, and the ground lit under it.
        this.vfx.shaft(p.clone().setY(p.y + 8), p.clone(), '#f4f7ff', 7, 0.35);
        this.vfx.ring(p, r * 0.1, r * 0.7, '#eef2ff', 6, 0.7);
        return true;
      case 'holyGround':
        // Holy ground left behind: a pale ring settling and motes rising.
        this.vfx.ring(p, r * 0.3, r, '#eef2ff', 10, 0.6);
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 3 : 8, color: '#ffffff', speed: 0.02, up: 0.04, size: 0.08, life: 16, gravity: -0.002, spread: r * 0.5 });
        return true;
      case 'vigilLight':
        // The marked one getting up: a white burst that throws those around back a moment.
        this.vfx.ring(p, 0.2, r, '#ffffff', 8, 0.95);
        this.vfx.shaft(p.clone().setY(p.y + 5), p.clone(), '#eef2ff', 6, 0.3);
        this.flare(p.clone().setY(p.y + 0.8), '#ffffff', 2.4, 4);
        this.shake = Math.max(this.shake, 0.1);
        return true;
      // The panoply, the artificer's and the mage's pieces (docs/PANOPLIA.md §9.5): each in its damage type's colour,
      // never a red fill; what comes again (the second volley, the drops, the hail) smaller than its skill's own.
      case 'emberBurst':
        // Embers bursting off what died burning: an orange ring, sparks flying up, no smoke column.
        this.vfx.ring(p, r * 0.1, r, '#ff9a3a', 6, 0.85);
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 6 : 14, color: '#ffb347', speed: 0.1, up: 0.08, size: 0.08, life: 8, gravity: -0.002 });
        this.flare(p.clone().setY(p.y + 0.4), '#ff9a3a', 1.4, 3);
        return true;
      case 'squareShield':
      case 'limeAndStone':
        // A steel-pale shield raised on whoever it reached (the square's), or a pale ring over all that was inside (the apron's).
        this.vfx.ring(p, r * 0.2, r, '#e3e6ec', kind === 'limeAndStone' ? 10 : 6, 0.7);
        this.flare(p.clone().setY(p.y + 0.8), '#eef2ff', kind === 'limeAndStone' ? 2 : 1.2, 3);
        return true;
      case 'droneHeal':
        // The drone's weld on a hero: the mend's cyan, green motes.
        this.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 3 : 6, color: '#b3f59a', speed: 0.04, up: 0.05, size: 0.06, life: 8 });
        this.vfx.ring(p, 0.1, 0.6, '#4fdcff', 5, 0.6);
        return true;
      case 'barricadeRoll':
        // The barricade grinding a hex on: dust under it and a scrape of steel.
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 3 : 8, color: '#7a6e60', speed: 0.06, up: 0.02, size: 0.3, life: 10, smoke: true, spread: 0.8 });
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 2 : 5, color: '#e3e6ec', speed: 0.08, up: 0.04, size: 0.05, life: 5, gravity: 0.01 });
        return true;
      case 'plateBurst':
        // A plate bursting: a sharp steel ring and splinters.
        this.vfx.ring(p, r * 0.1, r, '#c4c8d2', 5, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 5 : 12, color: '#c4c8d2', speed: 0.12, up: 0.06, size: 0.06, life: 7, gravity: 0.012 });
        return true;
      case 'lastSpark':
        // The coil's last spark: a lilac ring as wide as it reached and a white flash on the post.
        this.vfx.ring(p, r * 0.1, r, '#c8bcff', 7, 0.8);
        this.flare(p.clone().setY(p.y + 0.9), '#ffffff', 2, 4);
        return true;
      case 'houndPin':
        // The hound's jaws closing: a small iron ring at the feet.
        this.vfx.ring(p.clone().setY(p.y + 0.05), 0.1, 0.5, '#c4c8d2', 4, 0.8);
        return true;
      case 'dripEat':
        // A drop of acid eating into what stepped on it: green hiss.
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 3 : 7, color: '#b5e06a', speed: 0.04, up: 0.05, size: 0.07, life: 9 });
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 1 : 2, color: '#4f7a3a', speed: 0.02, up: 0.02, size: 0.3, life: 12, smoke: true });
        return true;
      case 'icicle':
      case 'wallBurst':
        // Ice bursting along the frozen line, or off the wall's end: white shards, a cold ring.
        this.vfx.ring(p, r * 0.1, r, '#e8f7ff', 6, 0.85);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 6 : 14, color: '#e8f7ff', speed: 0.11, up: 0.07, size: 0.08, life: 9, gravity: 0.012 });
        return true;
      case 'orbTurn':
        // The orb turning back: a cold ring where it stood.
        this.vfx.ring(p, r * 0.3, r, '#bfe8ff', 6, 0.6);
        return true;
      case 'mirrorImage':
        // The image left behind: a pale shimmer.
        this.vfx.implode(p.clone().setY(p.y + 0.6), 1, { n: low ? 4 : 10, color: '#cfdcff', size: 0.06, life: 8, swirl: 0.5, lift: 0.5 });
        return true;
      case 'blink2':
      case 'blinkFrost2': {
        // The second flare, where the mage lands: as the first, a little smaller.
        const frost = kind === 'blinkFrost2';
        this.vfx.ring(p, r * 0.1, r * 0.85, frost ? '#bfe8ff' : '#ff9a3a', 7, 0.8);
        this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 6 : 14, color: frost ? '#e8f7ff' : '#ffb347', speed: 0.09, up: 0.06, size: 0.09, life: 9, gravity: 0.004 });
        return true;
      }
      case 'hail': {
        // A hailstone dropping on the chilled one (the shatter draws the rest).
        const top = p.clone().add(this.v.set(0, 4, 0));
        this.vfx.beam(top, p.clone().setY(p.y + 0.4), '#e8f7ff', 2, 0.03, 0);
        return true;
      }
      case 'riftOpen':
        // The rift holding open: a violet ring settling at its edge.
        this.vfx.ring(p, r * 0.6, r, '#9a86d0', 9, 0.6);
        return true;
      case 'cometWarn':
        // Where the comet's tail will fall: a wide faint ring, the warning of its blows.
        this.vfx.ring(p, r * 0.9, r, '#ffb070', 12, 0.5);
        return true;
      case 'cometTail': {
        // Each blow of the comet's tail: a meteor's streak and ring, wide.
        const top = p.clone().add(this.v.set(-1.5, 7, -1));
        this.vfx.beam(top, p.clone().setY(p.y + 0.3), '#ffb347', 5, 0.14, 0);
        this.vfx.ring(p, r * 0.1, r, '#ff7a2a', 8, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 8 : 18, color: '#ffb347', speed: 0.14, up: 0.08, size: 0.11, life: 9, gravity: 0.006 });
        this.shake = Math.max(this.shake, 0.14);
        return true;
      }
      case 'leapFrom':
        this.leapFrom = p.clone();
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 4 : 8, color: '#5b524a', speed: 0.05, up: 0.04, size: 0.3, life: 10, smoke: true });
        return true;
      case 'leap':
      case 'quake':
        // The arc it flew (when it jumped this tick) and the ground it broke.
        if (this.leapFrom) this.skillFx.leap(this.leapFrom, p);
        this.leapFrom = null;
        this.skillFx.crater(p, r * 0.9);
        if (kind === 'quake') this.skillFx.crater(p, r * 0.7, true);
        this.vfx.ring(p, r * 0.2, r, kind === 'quake' ? '#ff8a6e' : '#d8cbb8', 8, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 6 : 14, color: '#8a7e70', speed: 0.12, up: 0.1, size: 0.09, life: 10, gravity: 0.014, spread: r * 0.3 });
        this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 10 : 22, color: '#4a4440', speed: 0.1, up: 0.07, size: 0.35, life: 12, smoke: true, spread: r * 0.4 });
        this.shake = Math.max(this.shake, kind === 'quake' ? 0.3 : 0.18);
        return true;
      case 'frenzy':
        this.vfx.ring(p, 0.3, 2.4, '#ff4f3a', 9, 1);
        this.vfx.shaft(p, p.clone().setY(p.y + 3), '#ff4f3a', 7, 0.5);
        this.later.push({ n: 1, run: () => this.vfx.ring(p, 0.2, 1.6, '#ff8a6e', 7, 0.8) });
        this.shake = Math.max(this.shake, 0.12);
        this.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 10 : 24, color: '#ff6a3a', speed: 0.08, up: 0.08, size: 0.14, life: 10 });
        this.flare(p.clone().setY(p.y + 0.8), '#ff4f3a', 2.2, 6);
        return true;
      case 'aegis':
        this.vfx.ring(p, 0.2, 1.4, '#dfe8ff', 8, 0.9);
        this.vfx.shaft(p.clone().setY(p.y + 0.02), p.clone().setY(p.y + 2.2), '#eef2ff', 6, 0.25);
        return true;
      case 'entangle':
        this.vfx.ring(p, r * 0.2, r, '#6fa05a', 10, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.15), { n: low ? 8 : 18, color: '#4f6b34', speed: 0.06, up: 0.08, size: 0.16, life: 12, gravity: 0.006, spread: r * 0.5 });
        return true;
      case 'spores':
        this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 6 : 14, color: '#c9d77a', speed: 0.04, up: 0.02, size: 0.5, life: 20, smoke: true, spread: r * 0.5 });
        return true;
      case 'bloom':
        this.vfx.ring(p, r * 0.15, r, '#b3f59a', 12, 0.9);
        this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 10 : 24, color: '#7fc06a', speed: 0.05, up: 0.09, size: 0.16, life: 16, gravity: -0.001, spread: r * 0.6 });
        return true;
      case 'bear':
        this.vfx.ring(p, 0.3, 3, '#6fa05a', 10, 1);
        this.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 12 : 28, color: '#b3f59a', speed: 0.09, up: 0.08, size: 0.16, life: 12 });
        this.flare(p.clone().setY(p.y + 1), '#6fa05a', 2.6, 7);
        this.shake = Math.max(this.shake, 0.25);
        return true;
      default:
        return false;
    }
  }

  private flare(p: THREE.Vector3, color: string, size: number, life: number): void {
    this.flares.push({ p, color: new THREE.Color(color), size, life, max: life });
  }

  private drawFlares(fresh: boolean): void {
    if (fresh) this.flares = this.flares.filter((f) => --f.life > 0);
    for (const f of this.flares) {
      const t = f.life / f.max;
      this.entityGlow.add(f.p.x, f.p.y, f.p.z, f.color, f.size * (0.8 + 0.2 * t), t);
    }
  }

  /* ---------- animation: GPU clips per entity ---------- */

  private animator = new Animator();
  /** View clock (seconds): the tick plus how far into the next one this frame is. */
  private now = 0;
  private lastNow = 0;
  /** The clock at the last drawing. */
  private drawnNow = 0;
  /** How far into the next tick this frame is, for clips (0..1; unlike `alpha`, not slowed by the glide). */
  private frac = 0;
  /** Each walker's own stride clock (it advances slower while slowed). */
  private walkT = new Map<number | string, number>();
  /** Where each thing faces as drawn (turns are eased, not snapped). */
  private yawShown = new Map<number | string, number>();
  /** When each thing entered a state the simulation doesn't time (down, stunned, roaring…). */
  private since = new Map<number | string, { tag: string; at: number }>();
  /** One-shots shown on top of what the simulation says: a boss's power, a swing finishing after the sim moved on. */
  private shots = new Map<number | string, { clip: string; at: number }>();
  /** Where each nightmare was last drawn, so its body can fall where it died. */
  private drawnAt = new Map<number, { key: string; p: THREE.Vector3; yaw: number; scale: number; tint: THREE.Color | undefined }>();
  private corpses: { id: number; key: string; p: THREE.Vector3; yaw: number; scale: number; at: number; fall?: THREE.Vector3; copy?: boolean }[] = [];

  /** The wave's nightmares and projectiles not baked yet, soonest first (null: not listed yet). */
  private toBake: string[] | null = null;
  /** The baked nightmares' shaders went to compile (see frame). */
  private compiledAhead = false;
  /** Their shaders are linked: each frame one more batch draws an empty instance (Library.warmNext). */
  private warmAhead = false;
  /** The compile ahead is over: every baked batch's shaders are linked. */
  private linkedAhead = false;
  /** Gone (dispose): a compile ahead still under way stops. */
  private gone = false;

  /**
   * Every nightmare the wave will send, baked ahead of its first appearance, one per frame from the moment the
   * view opens: a kind first seen mid-fight was baked right then, a stall of a good part of a second on a phone
   * with the field full (with the variants a blow or a phase can switch it to). The bake worker does the heavy
   * part (bake-pool.ts); here each is taken in, in order, one per frame. False when nothing was taken.
   */
  private prewarm(s: BattleState): boolean {
    if (!this.toBake) {
      const first = new Map<string, number>();
      const dress = this.theme?.dressEnemy ? `:${this.theme.id}` : '';
      const VARIANT: Partial<Record<string, string>> = { beetle: 'beetleCracked', giant: 'giantAwake', colossus: 'colossusShine', warden: 'wardenEmber', shellSerpent: 'shellSerpentBurrow', lidlessDreamer: 'lidlessDreamerAwake', kaela: 'kaelaBear', drinker: 'drinkerFull', maw: 'mawDark' };
      // A wave by its clock; a crypt, the Furrows and the cellars by room and pack.
      const d = s.dungeon;
      const list = [
        ...(s.wave?.spawns ?? []).map((sp) => ({ ...sp, at: sp.tick })),
        ...[...(d?.spawns ?? []), ...(d?.maze?.spawns ?? []), ...(d?.exp?.spawns ?? [])].map((sp) => ({ ...sp, at: sp.room * 1000 + sp.wave })),
      ];
      for (const sp of list) {
        // One of the Unsleeping brings his guard (docs/ANTIHEROES.md), and his marks on the ground: made now, so
        // their shaders compile with the rest instead of when he comes in.
        if ('un' in sp && sp.un) {
          const g = this.rk(`enemy:${UNSLEEPING.escort.kind}:${sp.tier >= 2 ? 2 : 1}:0${dress}`);
          if (!first.has(g) || (first.get(g) as number) > sp.at) first.set(g, sp.at);
          this.armUnsleeping();
        }
        for (const kind of [sp.kind, VARIANT[sp.kind]]) {
          if (!kind || kind === 'dragon' || kind === 'dragonAir') continue;
          const key = this.rk(`enemy:${kind}:${sp.tier}:${sp.elite ? 1 : 0}${kind === 'pumpkin' ? '' : dress}`);
          if (!first.has(key) || (first.get(key) as number) > sp.at) first.set(key, sp.at);
        }
      }
      // What a crypt brings in outside its planned packs: each hall's guardian (and the form a blow or a phase
      // switches it to), the bones the Warden calls and a puzzle's reinforcements. They compiled when they first
      // came in: up to 1.7 s still on a phone in the deep crypt's halls (client-perf, 2026-10-30).
      const L = s.map.dungeon;
      if (d && L && !d.maze && !d.exp) {
        L.rooms.forEach((room, i) => {
          const add = (kind: string, tier: number): void => {
            const key = this.rk(`enemy:${kind}:${tier}:0${dress}`);
            if (!first.has(key) || (first.get(key) as number) > i * 1000) first.set(key, i * 1000);
          };
          if (room.kind === 'boss' && d.sima) {
            // The Chasm's hall: this floor's guardian with its things, or its captain (an elite, as the floor's own).
            const g = STRATUM[d.sima.stratum].guardian;
            if (d.sima.threshold) {
              add(g, 1);
              for (const k of g === 'marrowWorm' ? ['marrowWormBurrowed'] : g === 'prism' ? ['crystal'] : g === 'broodQueen' ? ['cocoon', 'spiderling'] : g === 'frostLady' ? ['iceBlock'] : g === 'leechMother' ? ['leech'] : g === 'archivist' ? Object.keys(STRATUM.archive.bestiary) : []) add(k, 1);
            }
            // What the floor's hazards spill (niches, deep water, rockfalls, webs).
            for (const k of { ossuary: ['skeleton'], cisterns: ['leech'], ribs: ['boneworm'], nest: ['spiderling'] }[d.sima.stratum as string] ?? []) add(k, 1);
          } else if (room.kind === 'boss' && !d.hallWins) {
            const g = d.deep ? guardianOf(room.floor ?? 0) : s.rules?.drinker ? 'drinker' : 'warden';
            add(g, 1);
            const v = VARIANT[g];
            if (v) add(v, 1);
            if (g === 'warden') for (const k of ['skeleton', 'wraith']) add(k, 1);
          }
          if (room.puzzle && d.deep) {
            const beasts = room.puzzle === 'glass' ? DEEP_CRYPT.flyers : (DEEP_CRYPT.bestiary[room.floor ?? 0] ?? {});
            for (const k of Object.keys(beasts)) for (const tier of [1, 2]) add(k, tier);
          }
        });
      }
      // La Orilla: the Drowned climb out of the sea outside any wave's list; the Maw and its light come by its rule.
      if (s.rules?.tide || s.rules?.fin || s.rules?.maw || s.rules?.drinker?.up) for (const k of s.rules?.maw ? ['drowned', 'maw', 'mawDark', 'lure'] : s.rules?.sunken?.boss ? ['drowned', s.rules.sunken.boss.steam ? 'sunkenSteam' : 'sunken'] : ['drowned']) {
        const key = this.rk(`enemy:${k}:1:0${dress}`);
        if (!first.has(key)) first.set(key, 0);
      }
      // The Chasm: what its next floors can bring (each floor is laid only as the party goes down, so its packs are
      // in no list yet); more is asked for as it goes (simaFloor).
      if (d?.sima) for (const key of this.simaKeys(d.sima, d.sima.floor)) if (!first.has(key)) first.set(key, 1);
      // Then what flies (small: each model is a few parts), so the first shot or eruption doesn't build one.
      // Then every hero both as flesh and as a spectre (and the druid's bear): a friend coming online in the
      // bastion, or dropping out of a fight, switched miniatures on the spot, a stall of half a second on a phone.
      // An artificer's grenades leave a spiral on the ground: made now so its shader compiles with the rest.
      if (s.heroes.some((h) => h.cls === 'artificer') && !this.vortex.length) {
        const v = this.vortexMesh(0);
        v.position.set(0, -60, 0);
        v.scale.setScalar(0.0001);
      }
      // The warrior's guard banner, a model of its own (drawn by key, not rigged), and the like: the cleric's totem,
      // the artificer's turret and grenade. Each was baked on the spot the first time it was cast.
      const props: [string, string[]][] = [
        ['warrior', ['banner']],
        ['cleric', ['totem']],
        ['artificer', ['turret', 'grenade']],
      ];
      for (const [cls, keys] of props) if (s.heroes.some((h) => h.cls === cls)) for (const k of keys) first.set(k, Number.MAX_SAFE_INTEGER);
      // The companions each hero's kit calls (the artificer's automaton, colossus or spiders; the learned hawk, decoy,
      // drone, hound, treant): the colossus compiled two programs the first time it came, 150–530 ms for the whole
      // party at once in the Chasm's first hall (client-perf, 2026-11-03).
      for (const h of s.heroes) for (const k of companionsOf(h)) if (!first.has(this.rk(`bot:${k}`))) first.set(this.rk(`bot:${k}`), 1);
      // The unique forms' summons (docs/UNICAS.md §3), for whoever carries the form that calls them.
      for (const h of s.heroes) {
        if (!h.uqForm || !h.uq) continue;
        h.uq.forEach((id, i) => {
          const f = h.uqForm?.[i] ?? 0;
          if (id === 'gasparHammer' && f & 4) first.set(this.rk(f & 8 ? 'bot:gaspar' : 'bot:gasparGuard'), Number.MAX_SAFE_INTEGER);
          if ((id === 'wandererCloak' && f & 1) || (id === 'messengerCape' && f & 4)) first.set(this.rk('bot:cloak'), Number.MAX_SAFE_INTEGER);
          if (id === 'dwarfBark' && f & 4) first.set(this.rk('bot:barkBear'), Number.MAX_SAFE_INTEGER);
        });
      }
      // The panoply's own summons: the goatherd's pelt's shadow wolves, for whoever wears it.
      for (const h of s.heroes) if (h.uq?.includes('goatherdPelt')) first.set(this.rk('bot:shadeWolf'), Number.MAX_SAFE_INTEGER);
      // The kennel lets its mastiffs out into the wave (a structure's, no skill's: companionsOf doesn't list them).
      if (s.structures.some((st) => st.type === 'kennel' && !st.destroyed)) first.set(this.rk('bot:mastiff'), 1);
      // Las vías 1: the barricades rise mid-run (bone walls nobody's wave lists), and Hilde rides the train.
      if (s.rules?.rails) {
        first.set(this.enemyKey('boneWall', 1), 0);
        first.set('npc:hilde', 0);
      }
      // El Párpado 3: the group's copies come out one by one by the act's rule (no wave lists them): each one's own
      // miniature, and the Unsleeping's marks their kits leave.
      if (s.rules?.copies) {
        for (const f of s.rules.copies.faces) first.set(this.rk(heroKey(f.cls, 20, false, f.look)), 0);
        if (s.rules.copies.faces.length) this.armUnsleeping();
      }
      // Las vías 2: Dorn's guards are bots nobody's wave lists.
      if (s.rules?.bells) first.set('bot:guard', 0);
      // Las vías 4: Dorn's own come up the rails at half the boss's life; the turned automaton, if Bruni already fell.
      if (s.rules?.rising?.dorn) first.set('bot:guardDorn', 0);
      // The night of the bell's allies come later: their figures baked with the rest.
      const later = (s.rules?.allies?.come ?? []).map((a) => `ally:${a.pid.slice(4)}`);
      const npcs = (h: Hero): string | null => (h.pid === 'npc:orla' || (s.mode === 'wave' && s.rules?.night && h.pid.startsWith('npc:')) ? `ally:${h.pid.slice(4)}` : null);
      // A path's people, and a memory's face (docs/SENDAS.md): their miniatures from the path's cast.
      const cast = (cls: Hero['cls'], who: string): string => castKey(sendaCast(cls, who), cls, (k) => this.rk(k));
      for (const n of s.rules?.senda?.npcs ?? []) if (!n.prop) later.push(n.model ? this.rk(`senda:${n.model}`) : cast(s.heroes[0]?.cls ?? 'warrior', n.id));
      // The paladin's Arado comes later in its fight: its own look baked with the rest.
      if (s.rules?.senda?.canal?.plow) first.set(this.rk('enemy:arado:1:0'), 0);
      // The burnt library's pages, risen and scribes come as the fight goes: their looks baked with the rest.
      if (s.rules?.senda?.soot || s.rules?.senda?.seals) {
        first.set(this.rk('enemy:page:1:0'), 0);
        first.set(this.rk(s.rules.senda.soot ? 'enemy:skeleton:1:0' : 'enemy:scribe:1:0'), 0);
        if (s.rules.senda.soot) first.set(this.rk('enemy:wraith:1:0'), 0);
      }
      // The artificer's golems stand from the start (the Overseer by the lift).
      if (s.rules?.senda?.golem) {
        first.set(this.rk('enemy:workGolem:1:0'), 0);
        if (s.rules.senda.gears) first.set(this.rk('enemy:capataz:1:0'), 0);
      }
      // The cable's gargoyles come from the start; La Picotera sits on the drum.
      if (s.rules?.senda?.garg) {
        first.set(this.rk('enemy:gargoyle:1:0'), 0);
        if (s.rules.senda.gondola) first.set(this.rk('enemy:crow:1:0'), 0);
        if (s.rules.senda.gearbox) first.set(this.rk('enemy:picotera:3:0'), 0);
      }
      // The heart's ash and oozes come from the start; La Tenaza out of the river in the test.
      if (s.rules?.senda?.vent || s.rules?.senda?.ember || s.rules?.senda?.cams) {
        first.set(this.rk('enemy:wraith:1:0'), 0);
        if (!s.rules.senda.cams) first.set(this.rk('enemy:slime:1:0'), 0);
        else first.set(this.rk('enemy:tenaza:1:0'), 0);
      }
      // The eyes: the Marea up Grada's wall, the mine's beetles and shades, La Barrena out of the broken drills.
      if (s.rules?.senda?.struts) for (const k of ['goblin', 'wraith', 'skeleton']) first.set(this.rk(`enemy:${k}:1:0`), 0);
      if (s.rules?.senda?.carts) for (const k of ['beetle', 'wraith']) first.set(this.rk(`enemy:${k}:1:0`), 0);
      if (s.rules?.senda?.drills) {
        for (const k of ['wraith', 'skeleton']) first.set(this.rk(`enemy:${k}:1:0`), 0);
        first.set(this.rk('enemy:barrena:1:0'), 0);
      }
      // Tormo's chapter: the hall's shades, the ash cubs; Tormo the cast's (a legacy's, and the scenes').
      if (s.rules?.senda?.knees) for (const k of ['wraith', 'skeleton']) first.set(this.rk(`enemy:${k}:1:0`), 0);
      if (s.rules?.senda?.shell || s.rules?.senda?.plates) first.set(this.rk('enemy:ashCub:1:0'), 0);
      if (s.heroes.some((h) => h.lg && h.cls === 'artificer')) later.push(this.rk('senda:tormo'));
      // The ledge's howler (the barbarian's chapter 1) stands from the start.
      if (s.rules?.senda?.trail?.howler) first.set(this.rk('enemy:howler:1:0'), 0);
      // The cleric's limpets come out of the ground at the first bend: baked with the rest.
      if (s.rules?.senda?.litter?.limpet) first.set(this.rk('enemy:lapa:1:0'), 0);
      // Its chapter 4: the Doliente and her tongues in the memory.
      if (s.rules?.senda?.litter?.hermit?.vigil) {
        first.set(this.rk('enemy:doliente:3:0'), 0);
        first.set(this.rk('enemy:lengua:1:0'), 0);
      }
      // The druid's: the Lodazal from the start; the clearing's swollen slimes.
      if (s.rules?.senda?.druid?.roots) first.set(this.rk('enemy:lodazal:1:0'), 0);
      if (s.rules?.senda?.druid?.glade) for (const k of ['slimeFat', 'slimeFat2']) first.set(this.rk(`enemy:${k}:1:0`), 1);
      if (s.rules?.senda?.summer?.counter) first.set(this.rk('enemy:wyvernOld:1:0'), 0);
      if (s.rules?.senda?.autumn?.under) first.set(this.rk('enemy:broodmother:1:0'), 0);
      if (s.rules?.senda?.winter?.sap) first.set(this.rk('enemy:escarcha:1:0'), 0);
      if (s.rules?.senda?.deep?.carcoma) first.set(this.rk('enemy:carcoma:1:0'), 0);
      const heroes = later.concat(s.heroes.flatMap((h) => (npcs(h) ? [npcs(h) as string] : h.face ? [cast(h.cls, h.face)] : [h.cls, ...(h.cls === 'druid' ? ['bear' as const] : [])].flatMap((c) => [false, true].map((g) => this.rk(heroKey(c, h.level, g, h.look)))))));
      this.toBake = [...new Set([...[...first].sort((x, y) => x[1] - y[1]).map(([k]) => k), ...PROJECTILES.map((k) => `proj:${k}`), ...heroes])];
      bakePool.ask(this.toBake);
      // The skills' and the gear's effect textures too, while the board still stands still.
      warmSkillFx();
      warmGearFx();
    }
    // The bake worker has them all, in this order: each is taken in as it comes back (a frame without one is free).
    const key = this.toBake[0];
    if (key === undefined || bakePool.waiting(key)) return false;
    this.toBake.shift();
    this.lib.batch(key);
    if (key.includes('enemy:')) this.lib.farAhead(key);
    return true;
  }

  /** What a fight may need later (see bakeLater): in the worker's hands once the fight is under way. */
  private bakeLate: string[] | null = null;

  /**
   * What only shows when something breaks — a building worn or in ruins, the ring worn, rubble — baked in the
   * worker once the fight is under way (it doesn't hold the start), one taken in per tick: a wall falling baked
   * its worn ring and the ruins on the spot, 120 ms in that tick on a phone at CPU ×6 (Corvin's Night).
   */
  private bakeLater(s: BattleState): void {
    if (!this.bakeLate) {
      const keys = new Set<string>();
      if (!this.crypt && this.map.kind === 'bastion') {
        for (const st of s.structures) {
          const def = STRUCTURES[st.type];
          // A boulder (a trap, no life) lies spent once it rolls: its empty chock, baked before it goes.
          if (st.type === 'boulder' && st.charges > 0) keys.add(structKey('boulder', st.level, st.material, 0, 'spent'));
          if (st.destroyed || st.maxHp <= 0) continue;
          if (st.type === 'rampart') keys.add('rampartRubble');
          else if (def.category === 'trap') keys.add('rubble');
          else if (def.hp > 0) {
            for (const w of [1, 2]) keys.add(structKey(st.type, st.level, st.material, w, st.branch));
            keys.add(`ruin:${st.type}:${st.material}`);
            // The tower on a wall run (the adarve) wears with its wall.
            if (st.mount) for (const w of [1, 2]) keys.add(structKey(st.mount.type, st.mount.level, st.material, w));
          }
        }
        for (const pc of this.ringBuilt) for (const w of [1, 2]) keys.add(`${pc.key.split(':').slice(0, 2).join(':')}:${w}`);
      }
      this.bakeLate = [...keys];
      bakePool.ask(this.bakeLate);
    }
    const key = this.bakeLate[0];
    if (key === undefined || bakePool.waiting(key)) return;
    this.bakeLate.shift();
  }

  /**
   * Bakes ahead for up to `ms` (at least one): true while some is left. A fight on this device's own clock holds
   * it until then, so a phone spends that second on a still board instead of in stalls as the fight opens.
   */
  /** Baked and every program linked (see frame): a held clock can start. */
  get readyAhead(): boolean {
    return this.toBake?.length === 0 && this.linkedAhead;
  }

  bakeAhead(s: BattleState, ms: number): boolean {
    const end = performance.now() + ms;
    while (this.prewarm(s) && (this.toBake as string[]).length > 0 && performance.now() < end);
    return (this.toBake as string[]).length > 0;
  }

  /** The library key a nightmare of this kind is drawn with, dressed by the holiday if any (as drawEnemies keys it). */
  private enemyKey(kind: string, tier: number, elite = false): string {
    return this.rk(`enemy:${kind}:${tier}:${elite ? 1 : 0}${this.theme?.dressEnemy && kind !== 'pumpkin' ? `:${this.theme.id}` : ''}`);
  }

  /** Rigged library key (the GPU-animated variant) unless `?anim=0`. */
  private rk(key: string): string {
    return ANIM.on ? `rig:${key}` : key;
  }

  /** Seconds since `id` entered `tag` (first seen now). */
  private sinceTag(id: number | string, tag: string): number {
    const c = this.since.get(id);
    if (c && c.tag === tag) return this.now - c.at;
    this.since.set(id, { tag, at: this.now });
    return 0;
  }

  /** The eased facing of `id` toward `target` (radians). */
  private faceTo(id: number | string, target: number): number {
    const y = this.yawShown.get(id);
    if (y === undefined) {
      this.yawShown.set(id, target);
      return target;
    }
    let d = (target - y) % TAU;
    if (d > Math.PI) d -= TAU;
    if (d < -Math.PI) d += TAU;
    // A big turn (a blink, a respawn) snaps; otherwise it turns over about a sixth of a second.
    const next = Math.abs(d) > 2.6 ? target : y + d * Math.min(1, (this.now - this.lastNow) * 14);
    this.yawShown.set(id, next);
    return next;
  }

  /** `id`'s stride clock, advanced by this frame at `rate` (1 = the clip's own pace). */
  private stride(id: number | string, rate: number): number {
    const t = (this.walkT.get(id) ?? (typeof id === 'number' ? id * 0.37 : 0)) + Math.max(0, this.now - this.lastNow) * rate;
    this.walkT.set(id, t);
    return t;
  }

  /** Seconds since a sim one-shot started `animT` ticks ago. */
  private ago(animT: number): number {
    return (animT + this.frac) / TICK_HZ;
  }

  /**
   * What `id` plays: the first of `names` the model has, at `t`. A forced one-shot (`shots`) still running wins.
   * Returns undefined for models without clips (drawn as before).
   */
  private act(b: ModelBatch, id: number | string, names: string[], t: number): AnimParams | undefined {
    if (!b.baked.anim) return undefined;
    const sh = this.shots.get(id);
    if (sh) {
      const c = b.clip(sh.clip);
      const st = this.now - sh.at;
      if (c && st < c.dur) return this.animator.play(id, c, st, this.now);
      this.shots.delete(id);
    }
    let c: ClipInfo | null = null;
    for (const n of names) {
      c = b.clip(n);
      if (c) break;
    }
    return c ? this.animator.play(id, c, t, this.now) : undefined;
  }

  /** Starts a one-shot clip on `id` now (a boss's power, an emerge…). */
  private force(id: number | string, clip: string): void {
    this.shots.set(id, { clip, at: this.now });
  }

  /** The nightmare of `kind` nearest a sim point (events that carry only where). */
  private nearestOf(s: BattleState, kind: string, x: number, y: number): Enemy | undefined {
    let best: Enemy | undefined;
    let bd = Infinity;
    for (const e of s.enemies) {
      if (e.kind !== kind || e.hp <= 0) continue;
      const d = (e.x - x) * (e.x - x) + (e.y - y) * (e.y - y);
      if (d < bd) {
        bd = d;
        best = e;
      }
    }
    return best;
  }

  /** Bodies of the fallen: their death clip, then they sink into the ground. */
  private drawCorpses(use: (k: string, far?: boolean) => ModelBatch): void {
    const SINK = 1.1;
    this.corpses = this.corpses.filter((c) => {
      // Seen small, the fallen draw in the far batch with the living (one draw for both, not two).
      const b = use(c.key, this.far && c.key.includes('enemy:'));
      if (c.fall) {
        // Off the rim: on out a little, then down to the sea, tumbling.
        const t = this.now - c.at;
        const y = c.p.y - 5.2 * t * t;
        if (y < TAIL_SEA_Y) return false;
        const out = 0.4 + t * 1.6;
        this.q.setFromEuler(this.eu.set(Math.min(1.4, t * 3), c.yaw, 0, 'YXZ'));
        this.m.compose(this.v.set(c.p.x + c.fall.x * out, y, c.p.z + c.fall.z * out), this.q, this.s.set(c.scale, c.scale, c.scale));
        const clip = b.clip('die') ?? b.clip('walk');
        b.add(this.m, Math.floor(this.now * TICK_HZ) + c.id, undefined, 1, clip ? this.animator.play(c.id, clip, t, this.now) : undefined);
        return true;
      }
      const die = b.clip(c.copy ? 'down' : 'die');
      if (!die) return false;
      const t = this.now - c.at;
      // A copy kneels a moment, itself at last, and then is glass and gone (its shards are copyMoment's).
      if (c.copy) {
        if (t > COPY_KNEEL) return false;
        this.compose(c.p, c.yaw, c.scale);
        b.add(this.m, Math.floor(this.now * TICK_HZ) + c.id, t > COPY_KNEEL - 0.2 ? GLASS_STUN : undefined, 1, this.animator.play(c.id, die, Math.min(t, die.dur * 0.55), this.now));
        this.entityGlow.add(c.p.x, c.p.y + 0.9 * c.scale, c.p.z, GLASS_CRACK, 1.2, 0.25 * Math.max(0, 1 - t / COPY_KNEEL));
        return true;
      }
      const rest = die.dur + 0.5;
      if (t > rest + SINK) return false;
      const k = t > rest ? (t - rest) / SINK : 0;
      const p = this.v.copy(c.p);
      p.y -= k * k * this.lib.heightOf(c.key) * c.scale * 0.6;
      this.compose(p, c.yaw, c.scale);
      b.add(this.m, Math.floor(this.now * TICK_HZ) + c.id, undefined, 1 - k, this.animator.play(c.id, die, t, this.now));
      if (k < 0.5) this.shadows.add(c.p.x, c.p.y, c.p.z, 0.5 * c.scale * (1 - k));
      return true;
    });
  }

  /** One per tick: forget what stopped being drawn. */
  private sweepAnim(): void {
    this.animator.sweep(this.now, 3);
    for (const m of [this.walkT, this.yawShown, this.since, this.drawnAt]) if (m.size > 4096) m.clear();
  }

  /**
   * Terraced land: for each standing wall or gate, the way (world XZ, full hex) to each neighbour that is a
   * wall or gate too. Keyed "q,r"; built again only when something was built or fell.
   */
  private wallJoins(s: BattleState): Map<string, THREE.Vector3[]> {
    // Plans and works of walls and gates join the runs too, so a wall drawn in one stroke reads as one.
    const planned: string[] = [];
    for (const w of this.works) if (!w.upgrade && (w.structure === 'wall' || w.structure === 'gate')) planned.push(`${w.q},${w.r}`);
    const rev = `${s.gridRev}|${planned.join(';')}`;
    if (this.joins && this.joins.rev === rev) return this.joins.map;
    const at = new Set<string>(planned);
    for (const st of s.structures) if (!st.destroyed && (st.type === 'wall' || st.type === 'gate')) at.add(`${st.q},${st.r}`);
    // Who touches whom; then, where three pieces touch each other, the shortcut between the two busiest goes:
    // a run that turns reads as a corner, not a little triangle.
    const near = new Map<string, Set<string>>();
    for (const k of at) {
      const [q, r] = k.split(',').map(Number) as [number, number];
      const set = new Set<string>();
      for (const d of DIRS) if (at.has(`${q + d.q},${r + d.r}`)) set.add(`${q + d.q},${r + d.r}`);
      near.set(k, set);
    }
    const deg = (k: string): number => near.get(k)?.size ?? 0;
    for (const a of [...at].sort()) {
      for (const b of [...(near.get(a) ?? [])].sort()) {
        if (b <= a) continue;
        for (const c of near.get(a) ?? []) {
          if (c === b || !near.get(b)?.has(c)) continue;
          const [x, y] = [a, b, c].sort((u, v) => deg(v) - deg(u)) as [string, string, string];
          if (deg(y) < 3) continue;
          near.get(x)?.delete(y);
          near.get(y)?.delete(x);
          break;
        }
      }
    }
    const out = new Map<string, THREE.Vector3[]>();
    for (const k of at) {
      const [q, r] = k.split(',').map(Number) as [number, number];
      const a = hexToWorld(q, r);
      const list: THREE.Vector3[] = [];
      for (const d of DIRS) {
        if (!near.get(k)?.has(`${q + d.q},${r + d.r}`)) continue;
        const b = hexToWorld(q + d.q, r + d.r);
        // y: how far the neighbour stands above (below) this cell, so runs step down a change of level unbroken.
        list.push(new THREE.Vector3(((b.x - a.x) / 1000) * WORLD_PER_HEX, this.heightAt(b.x, b.y) - this.heightAt(a.x, a.y), ((b.y - a.y) / 1000) * WORLD_PER_HEX));
      }
      out.set(k, list);
    }
    this.joins = { rev, map: out };
    return out;
  }
  private joins: { rev: string; map: Map<string, THREE.Vector3[]> } | null = null;

  /** The ways from a cell to the walls and gates beside it (as `wallJoins` keeps them), for a piece not placed yet. */
  private previewLinks(cell: { q: number; r: number }): THREE.Vector3[] {
    const map = this.joins?.map;
    if (!map) return [];
    const a = hexToWorld(cell.q, cell.r);
    const out: THREE.Vector3[] = [];
    for (const d of DIRS) {
      if (!map.has(`${cell.q + d.q},${cell.r + d.r}`)) continue;
      const b = hexToWorld(cell.q + d.q, cell.r + d.r);
      out.push(new THREE.Vector3(((b.x - a.x) / 1000) * WORLD_PER_HEX, 0, ((b.y - a.y) / 1000) * WORLD_PER_HEX));
    }
    return out;
  }

  /**
   * The yaw that lays a piece along the run it sits in: between the two neighbours furthest apart (a gate in a
   * wall faces across it), else toward its one neighbour; null with none.
   */
  private runYaw(links: THREE.Vector3[] | undefined): number | null {
    if (!links || links.length === 0) return null;
    let dx = links[0]!.x;
    let dz = links[0]!.z;
    let best = -1;
    for (let i = 0; i < links.length; i++)
      for (let j = i + 1; j < links.length; j++) {
        const a = links[i]!;
        const b = links[j]!;
        const far = (a.x - b.x) ** 2 + (a.z - b.z) ** 2;
        if (far > best) {
          best = far;
          dx = a.x - b.x;
          dz = a.z - b.z;
        }
      }
    return Math.atan2(-dz, dx);
  }

  /** Terraced land: a wall piece as half a run toward each neighbour in `links` (see `wallJoins`), else whole at `yaw`. */
  private addRun(b: ModelBatch, p: THREE.Vector3, yaw: number, scale: number, links: THREE.Vector3[] | undefined, sy = scale, color?: THREE.Color, tall = 0): void {
    if (!links || links.length === 0) {
      this.q.setFromAxisAngle(this.yAxis, yaw);
      this.m.compose(p, this.q, this.s.set(scale, sy, scale));
      b.add(this.m, 0, color);
      return;
    }
    for (const d of links) {
      this.halfRun(p, d, scale, sy, tall);
      b.add(this.m, 0, color);
    }
  }

  /**
   * Half a run from a wall at `p` toward a neighbour `d` away (into `this.m`). Across a change of level the run
   * slopes: its top climbs (or falls) halfway to the neighbour's by the midpoint, posts kept upright, and it reaches
   * down so its foot meets the lower ground there (`tall`: the model's height).
   */
  private halfRun(p: THREE.Vector3, d: THREE.Vector3, scale: number, sy: number, tall: number): void {
    const flat = Math.hypot(d.x, d.z);
    this.q.setFromAxisAngle(this.yAxis, Math.atan2(-d.z, d.x));
    const dy = Math.abs(d.y) > 0.01 && tall > 0 ? d.y : 0;
    const slope = flat > 0 ? dy / flat : 0;
    const ext = Math.abs(dy) / 2;
    const h = tall * sy;
    const ys = h > 0 ? (sy * (h + ext)) / h : sy;
    this.v.set(p.x + d.x * 0.25, p.y + dy * 0.25 - ext, p.z + d.z * 0.25);
    this.m.compose(this.v, this.q, this.s.set(1, 1, 1));
    if (slope !== 0) this.m.multiply(this.shear.set(1, 0, 0, 0, slope, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1));
    this.m.multiply(this.shear.makeScale(flat * 0.54, ys, scale));
  }
  private shear = new THREE.Matrix4();

  private compose(p: THREE.Vector3, yaw: number, scale: number): void {
    this.q.setFromAxisAngle(this.yAxis, yaw);
    this.m.compose(p, this.q, this.s.set(scale, scale, scale));
  }

  /**
   * A spectral archer's arrow: a pale blue-grey streak arcing to the tower, a ghostly head flying along it
   * leaving cold motes, and a small frosty burst where it strikes.
   */
  private spectralArrow(a: THREE.Vector3, b: THREE.Vector3): void {
    const from = a.clone().setY(a.y + 0.2);
    const to = b.clone().setY(b.y + 0.9);
    const lift = Math.min(1.6, from.distanceTo(to) * 0.15);
    const arc = (t: number): THREE.Vector3 => from.clone().lerp(to, t).setY(from.y + (to.y - from.y) * t + Math.sin(t * Math.PI) * lift);
    const low = this.opts.quality === 'low';
    // A bright head flies the arc tick by tick, a long pale streak trailing it; the string's flash at the bow.
    this.vfx.burst(from, { n: low ? 3 : 6, color: '#cfdcf2', speed: 0.03, up: 0.01, size: 0.16, life: 7 });
    this.flare(from.clone(), '#9fc0ff', 0.8, 4);
    const steps = 4;
    for (let i = 1; i <= steps; i++) {
      const p0 = arc((i - 1) / steps);
      const q = arc(i / steps);
      const last = i === steps;
      this.later.push({
        n: i - 1,
        run: () => {
          this.vfx.beam(p0, q, '#bcd0ff', 6, 0.07, 0);
          this.vfx.beam(p0, q, '#ffffff', 3, 0.03, 0);
          this.flare(q.clone(), '#dbe6ff', last ? 1.3 : 0.7, last ? 7 : 3);
          this.vfx.burst(q, { n: last ? (low ? 6 : 14) : 2, color: last ? '#e6f0ff' : '#b8c8e0', speed: last ? 0.07 : 0.01, up: last ? 0.04 : 0, size: last ? 0.14 : 0.2, life: last ? 10 : 8, gravity: last ? 0.004 : -0.001 });
          if (last) {
            this.vfx.burst(q, { n: low ? 2 : 4, color: '#8a9ab8', speed: 0.02, up: 0.02, size: 0.45, life: 14, smoke: true });
            this.vfx.ring(q.clone().setY(q.y - 0.5), 0.1, 0.7, '#bcd0ff', 5, 0.6);
          }
        },
      });
    }
  }

  /**
   * A slinger's stone: a dark pebble on a high arc with a faint dust trail, a crack of grit where it lands.
   * The blow itself is dealt when thrown; the stone is how it reads.
   */
  private slingStone(a: THREE.Vector3, b: THREE.Vector3): void {
    const from = a.clone().setY(a.y + 0.5);
    const to = b.clone().setY(b.y + 0.4);
    const lift = Math.min(1.4, from.distanceTo(to) * 0.22);
    const arc = (t: number): THREE.Vector3 => from.clone().lerp(to, t).setY(from.y + (to.y - from.y) * t + Math.sin(t * Math.PI) * lift);
    const low = this.opts.quality === 'low';
    this.vfx.burst(from, { n: 2, color: '#8a7a68', speed: 0.02, up: 0.01, size: 0.1, life: 4 });
    for (let i = 1; i <= 4; i++) {
      const q = arc(i / 4);
      const last = i === 4;
      this.later.push({
        n: Math.ceil(i / 2) - 1,
        run: () => {
          this.vfx.burst(q, { n: last ? (low ? 4 : 8) : 1, color: last ? '#b8a890' : '#3a332c', speed: last ? 0.06 : 0, up: last ? 0.05 : 0, size: last ? 0.08 : 0.13, life: last ? 7 : 5, gravity: last ? 0.01 : 0 });
          if (last) this.vfx.burst(q, { n: low ? 1 : 3, color: '#6d6458', speed: 0.02, up: 0.02, size: 0.3, life: 10, smoke: true });
        },
      });
    }
  }

  /**
   * A mangonel's stone: a dark rock of ink on a very high arc, trailing violet smoke so it can be followed
   * from the walls; it bursts into stone and ink on the wall it hits and the board jolts.
   */
  private mangonelRock(a: THREE.Vector3, b: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    const from = a.clone();
    const to = b.clone();
    const lift = Math.min(4.5, from.distanceTo(to) * 0.45);
    const arc = (t: number): THREE.Vector3 => from.clone().lerp(to, t).setY(from.y + (to.y - from.y) * t + Math.sin(t * Math.PI) * lift);
    this.vfx.burst(from, { n: low ? 2 : 5, color: '#8a7a68', speed: 0.03, up: 0.03, size: 0.3, life: 10, smoke: true, spread: 0.2 });
    const steps = 8;
    for (let i = 1; i <= steps; i++) {
      const q = arc(i / steps);
      const last = i === steps;
      this.later.push({
        n: i - 1,
        run: () => {
          if (!last) {
            this.vfx.burst(q, { n: 1, color: '#2a2630', speed: 0, up: 0, size: 0.36, life: 4 });
            this.vfx.burst(q, { n: low ? 1 : 2, color: '#6b4fa0', speed: 0.01, up: 0, size: 0.3, life: 14, smoke: true, spread: 0.12 });
            this.flare(q.clone(), '#8b4dff', 0.6, 3);
            return;
          }
          this.vfx.burst(q, { n: low ? 8 : 18, color: '#6d6458', speed: 0.1, up: 0.1, size: 0.15, life: 12, gravity: 0.014 });
          this.vfx.burst(q, { n: low ? 3 : 6, color: '#8a4dff', speed: 0.06, up: 0.06, size: 0.08, life: 8, gravity: 0.01 });
          this.vfx.burst(q, { n: low ? 3 : 7, color: '#7a6e60', speed: 0.05, up: 0.04, size: 0.6, life: 16, smoke: true, spread: 0.35 });
          this.vfx.ring(q.clone().setY(q.y - 0.4), 0.2, 1.2, '#cfc3a4', 6, 0.7);
          this.shake = Math.max(this.shake, 0.1);
        },
      });
    }
  }

  /** A giant's rock: a boulder torn from the ground, arcing high on a trail of dust, bursting into stone on the tower. */
  private giantRock(a: THREE.Vector3, b: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    const from = a.clone();
    const to = b.clone();
    const lift = Math.min(3.2, from.distanceTo(to) * 0.35);
    const arc = (t: number): THREE.Vector3 => from.clone().lerp(to, t).setY(from.y + (to.y - from.y) * t + Math.sin(t * Math.PI) * lift);
    const steps = 6;
    for (let i = 1; i <= steps; i++) {
      const q = arc(i / steps);
      const last = i === steps;
      this.later.push({
        n: i - 1,
        run: () => {
          if (!last) {
            this.vfx.burst(q, { n: 1, color: '#4a4440', speed: 0, up: 0, size: 0.42, life: 5 });
            this.vfx.burst(q, { n: low ? 1 : 2, color: '#8a7a68', speed: 0.01, up: 0, size: 0.35, life: 12, smoke: true, spread: 0.15 });
            return;
          }
          this.vfx.burst(q, { n: low ? 8 : 20, color: '#6d6458', speed: 0.1, up: 0.1, size: 0.16, life: 12, gravity: 0.014 });
          this.vfx.burst(q, { n: low ? 3 : 8, color: '#7a6e60', speed: 0.06, up: 0.04, size: 0.7, life: 18, smoke: true, spread: 0.4 });
          this.vfx.ring(q.clone().setY(q.y - 0.4), 0.2, 1.4, '#cfc3a4', 6, 0.7);
          this.shake = Math.max(this.shake, 0.14);
        },
      });
    }
  }

  /** The giant's stomp: the ground cracks in a ring, dust rolls out and the whole board jumps. */
  private giantStomp(p: THREE.Vector3, r: number): void {
    const low = this.opts.quality === 'low';
    this.vfx.ring(p.clone().setY(p.y + 0.05), 0.4, r, '#cfc3a4', 10, 0.9);
    this.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, r * 0.6, '#3a332c', 12, 0.8);
    this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 8 : 22, color: '#7a6e60', speed: 0.12, up: 0.03, size: 0.6, life: 20, smoke: true, spread: 0.6 });
    this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 6 : 16, color: '#5a4632', speed: 0.1, up: 0.12, size: 0.12, life: 12, gravity: 0.014 });
    this.shake = Math.max(this.shake, 0.35);
  }

  /** A weaver's silk: a pale strand whipped out at the tower, a puff of web where it wraps. */
  private silkShot(a: THREE.Vector3, b: THREE.Vector3): void {
    this.vfx.beam(a, b, '#e8e4dc', 10, 0.025, 0.08);
    this.vfx.beam(a, b.clone().setY(b.y - 0.6), '#cfc8bc', 8, 0.015, 0.1);
    this.vfx.burst(b, { n: this.opts.quality === 'low' ? 4 : 10, color: '#f2efe8', speed: 0.04, up: 0.01, size: 0.12, life: 14, gravity: 0.002, spread: 0.4 });
  }

  /** A tower wound in silk: pale strands criss-cross it and catch the light; the odd thread trails off in the wind. */
  private cocoonFx(s: BattleState, id: number, p: THREE.Vector3, tall: number): void {
    this.entityGlow.add(p.x, p.y + tall * 0.5, p.z, SILK, 1.2, 0.35);
    if ((s.tick + id) % 6 !== 0) return;
    for (let i = 0; i < 3; i++) {
      const a = ((s.tick / 6 + id + i * 2.1) % 6.283);
      const o = new THREE.Vector3(Math.cos(a) * 0.45, 0, Math.sin(a) * 0.45);
      const lo = p.clone().add(o).setY(p.y + tall * (0.1 + 0.2 * i));
      const hi = p.clone().sub(o).setY(p.y + tall * (0.55 + 0.15 * i));
      this.vfx.beam(lo, hi, '#e8e4dc', 6, 0.02, 0.06);
    }
  }

  /** A Burn wyrmling's breath: a jet from the beast raking a line of fire along the ground. */
  private wyrmBreath(w: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    const n = 6;
    for (let i = 0; i <= n; i++) {
      const q = a.clone().lerp(b, i / n);
      this.later.push({
        n: Math.floor(i / 2),
        run: () => {
          this.vfx.beam(w, q.clone().setY(q.y + 0.1), '#ff6a24', 3, 0.12, 0.1);
          this.vfx.beam(w, q.clone().setY(q.y + 0.1), '#ffd08a', 2, 0.05, 0.05);
          this.vfx.burst(q.clone().setY(q.y + 0.2), { n: low ? 3 : 7, color: '#ff5a1a', speed: 0.05, up: 0.06, size: 0.22, life: 9, gravity: -0.003, spread: 0.3 });
          this.flare(q.clone().setY(q.y + 0.3), '#ff7a2a', 1.6, 5);
        },
      });
    }
  }

  /** The ground a wyrmling set burning: tongues of flame, embers and black smoke over it while it lasts. */
  private drawWyrmfire(s: BattleState, z: { id: number; radius: number; ticks: number }, p: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    const k = (s.tick + z.id) % 4;
    const r = (z.radius / 1000) * WORLD_PER_HEX;
    this.entityGlow.add(p.x, p.y + 0.15, p.z, FIRE, r * 2.2, 0.5 + (k < 2 ? 0.15 : 0));
    if ((s.tick + z.id) % 2 === 0) {
      const q = p.clone().add(new THREE.Vector3(Math.sin(s.tick + z.id) * r * 0.5, 0.15, Math.cos(s.tick * 1.7 + z.id) * r * 0.5));
      this.vfx.burst(q, { n: low ? 2 : 4, color: '#ff6a24', speed: 0.02, up: 0.07, size: 0.2, life: 8, gravity: -0.004, spread: r * 0.5 });
      this.vfx.burst(q, { n: 1, color: '#ffd08a', speed: 0.01, up: 0.09, size: 0.07, life: 10, gravity: -0.003, spread: r * 0.4 });
    }
    if ((s.tick + z.id) % 7 === 0) this.vfx.burst(p.clone().setY(p.y + 0.6), { n: 1, color: '#1e1a1a', speed: 0.01, up: 0.04, size: 0.6, life: 20, smoke: true, spread: r * 0.4 });
  }

  /** A tower on fire (a wyvern's spit): flickering flame light, rising embers and a column of smoke. */
  private towerFire(s: BattleState, id: number, p: THREE.Vector3, tall: number): void {
    const low = this.opts.quality === 'low';
    const k = (s.tick + id) % 4;
    this.entityGlow.add(p.x, p.y + tall * 0.6, p.z, FIRE, 1.3, 0.45 + (k < 2 ? 0.15 : 0));
    if ((s.tick + id) % 2 === 0) {
      const q = p.clone().setY(p.y + tall * (0.35 + 0.15 * k));
      this.vfx.burst(q, { n: low ? 2 : 4, color: '#ff7a2a', speed: 0.03, up: 0.06, size: 0.18, life: 8, gravity: -0.004, spread: 0.35 });
      this.vfx.burst(q, { n: 1, color: '#ffd08a', speed: 0.02, up: 0.08, size: 0.07, life: 10, gravity: -0.003, spread: 0.3 });
    }
    if ((s.tick + id) % 5 === 0) this.vfx.burst(p.clone().setY(p.y + tall), { n: low ? 1 : 2, color: '#2a2422', speed: 0.01, up: 0.04, size: 0.7, life: 22, smoke: true, spread: 0.3 });
  }

  /** A wyvern's spit: a roaring jet of fire from the hovering beast down onto the tower, a splash of flame where it hits. */
  private wyvernFire(a: THREE.Vector3, b: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    const from = a.clone();
    const to = b.clone().setY(b.y + 0.7);
    this.vfx.beam(from, to, '#ff6a24', 5, 0.16, 0.12);
    this.vfx.beam(from, to, '#ffd08a', 3, 0.06, 0.06);
    for (let i = 1; i <= 3; i++) {
      const q = from.clone().lerp(to, i / 3);
      this.later.push({ n: i - 1, run: () => this.vfx.burst(q, { n: low ? 3 : 7, color: i === 3 ? '#ffb04a' : '#ff5a1a', speed: 0.05, up: 0.04, size: 0.22, life: 8, gravity: -0.003, spread: 0.2 }) });
    }
    this.flare(to.clone(), '#ff7a2a', 2.2, 7);
    this.vfx.burst(to, { n: low ? 2 : 5, color: '#3a3028', speed: 0.03, up: 0.05, size: 0.6, life: 18, smoke: true });
  }

  /** An ogre's roar: a dark-red shockwave rolling out over the heroes it frightens, dust kicked up, the board shivers. */
  private ogreRoar(p: THREE.Vector3, r: number): void {
    const low = this.opts.quality === 'low';
    this.vfx.ring(p.clone().setY(p.y + 0.4), 0.3, r, '#ff4f3a', 9, 0.75);
    this.vfx.ring(p.clone().setY(p.y + 0.9), 0.2, r * 0.7, '#ff8a6e', 7, 0.45);
    this.vfx.ring(p, 0.2, r * 0.55, '#2a2224', 10, 0.6);
    this.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 6 : 16, color: '#5a5048', speed: 0.09, up: 0.03, size: 0.4, life: 14, smoke: true, spread: 0.5 });
    this.vfx.burst(p.clone().setY(p.y + 1.1), { n: low ? 3 : 8, color: '#ff6a4a', speed: 0.05, up: 0.04, size: 0.08, life: 9 });
    this.shake = Math.max(this.shake, 0.18);
  }

  /** An ash mammoth's footfall: a ring of ash at its feet and a few cinders kicked up. */
  private mammothStep(p: THREE.Vector3): void {
    this.vfx.ring(p.clone().setY(p.y + 0.03), 0.3, 1.2, '#4a4446', 7, 0.45);
    if (this.opts.quality !== 'low') this.vfx.burst(p.clone().setY(p.y + 0.12), { n: 3, color: '#3a3436', speed: 0.04, up: 0.02, size: 0.45, life: 12, smoke: true, spread: 0.6 });
  }

  /** An ash mammoth's tusks driven into a wall or a tower: timber and stone flying, a burst of ash and cinders, the board jolts hard. */
  private mammothCrash(p: THREE.Vector3): void {
    const low = this.opts.quality === 'low';
    this.vfx.ring(p, 0.3, 1.9, '#cfc3a4', 8, 0.75);
    this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 8 : 18, color: '#7a6e60', speed: 0.13, up: 0.11, size: 0.14, life: 12, gravity: 0.013 });
    this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 4 : 10, color: '#3e383a', speed: 0.07, up: 0.04, size: 0.6, life: 18, smoke: true, spread: 0.5 });
    this.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 3 : 8, color: '#ff8a3d', speed: 0.08, up: 0.08, size: 0.08, life: 10, gravity: 0.006 });
    this.shake = Math.max(this.shake, 0.24);
  }

  /**
   * Fire panics an ash mammoth: a blast of ash and cinders off its hide, a ring rolling out over the nightmares it
   * will trample (`r`), and the trumpet itself: a hot flare and a pale ring at the raised trunk (ahead along `yaw`).
   */
  private mammothPanic(p: THREE.Vector3, r: number, yaw: number): void {
    const low = this.opts.quality === 'low';
    const head = p.clone().add(this.v.set(Math.sin(yaw) * 0.9, 2.4, Math.cos(yaw) * 0.9));
    this.flare(head.clone(), '#ff8a6e', 2.0, 8);
    this.vfx.ring(head, 0.15, 1.3, '#ffd8c8', 6, 0.6);
    this.vfx.ring(p.clone().setY(p.y + 0.1), 0.4, r, '#4a4446', 10, 0.6);
    this.vfx.burst(p.clone().setY(p.y + 1.4), { n: low ? 4 : 12, color: '#4a4446', speed: 0.06, up: 0.04, size: 0.6, life: 18, smoke: true, spread: 0.6 });
    this.vfx.burst(p.clone().setY(p.y + 1.6), { n: low ? 6 : 18, color: '#ff8a3d', speed: 0.09, up: 0.1, size: 0.08, life: 14, gravity: 0.004, spread: 0.5 });
    this.shake = Math.max(this.shake, 0.12);
  }

  /** A sapper going down (a spray of earth and pebbles) or bursting up inside the walls (an eruption of dirt and rock). */
  private earthBurst(up: boolean, p: THREE.Vector3, r: number): void {
    const low = this.opts.quality === 'low';
    const q = p.clone().setY(p.y + 0.15);
    this.vfx.ring(p, r * 0.2, r * (up ? 1.6 : 1), '#8a7255', up ? 8 : 6, 0.8);
    this.vfx.burst(q, { n: (up ? 30 : 16) >> (low ? 1 : 0), color: up ? '#7a5e40' : '#5a4632', speed: up ? 0.08 : 0.06, up: up ? 0.16 : 0.08, size: 0.13, life: up ? 14 : 10, gravity: 0.014, spread: 0.2 });
    this.vfx.burst(q, { n: (up ? 14 : 6) >> (low ? 1 : 0), color: '#3a3028', speed: 0.1, up: up ? 0.2 : 0.1, size: 0.09, life: 14, gravity: 0.02 });
    this.vfx.burst(q, { n: (up ? 10 : 6) >> (low ? 1 : 0), color: '#6d5a44', speed: 0.04, up: 0.03, size: up ? 0.7 : 0.5, life: 18, smoke: true, spread: 0.3 });
    if (up) this.shake = Math.max(this.shake, 0.1);
  }

  /** Criticals already flashed this tick (a crowd of them would blind the board). */
  private critsThisTick = 0;

  /** Last phase seen of each boss (the shell's phases have no event of their own). */
  private phaseSeen = new Map<number, number>();

  /** A nightmare's clip from its sim state; bosses' powers come from their events (see bossPower). */
  /**
   * The Prestacaras (Caras prestadas 4): the miniature of the absent friend whose face it wears, boss-sized and a
   * shade too pale (Cal's face if nobody is absent); bare under a lantern, the tall faceless thing under it. While Mada
   * says goodbye it stands still, leaning in. It fights with an Unsleeping's kit: his effects draw on it as on him.
   */
  private drawFacetaker(s: BattleState, e: Enemy, p: THREE.Vector3, ground: number, use: (k: string) => ModelBatch): void {
    const m = e.mask as NonNullable<Enemy['mask']>;
    const bare = m.bare > 0;
    const face = m.faces.length ? (m.faces[m.at % m.faces.length] as (typeof m.faces)[number]) : null;
    const key = this.rk(bare ? 'enemy:prestado:1:0' : face ? heroKey(face.cls, 20, false, face.look) : 'enemy:calFace:1:0');
    const b = use(key);
    let anim: AnimParams | undefined;
    if (b.baked.anim) {
      const idle = this.now + e.id * 0.37;
      if (m.hold > 0) anim = this.act(b, e.id, ['idle'], idle);
      else if (bare) anim = this.enemyAct(s, b, e);
      else if (e.un && e.un.cast > 0) anim = this.act(b, e.id, ['roar', 'attack', 'idle'], this.sinceTag(e.id, 'channel'));
      else if (e.anim === 'walk') anim = this.act(b, e.id, ['walk'], this.stride(e.id, 1));
      else if (e.anim === 'attack') {
        const tt = this.ago(e.animT);
        const c = b.clip('attack');
        anim = this.act(b, e.id, c && tt < c.dur ? ['attack'] : ['idle'], c && tt < c.dur ? tt : idle);
      } else anim = this.act(b, e.id, ['idle'], idle);
    }
    const frame = anim ? 0 : bare ? (s.tick + e.id) & 3 : this.lib.poseFrame(e.anim === 'walk' ? (s.tick % 6 < 3 ? 'walkA' : 'walkB') : e.anim === 'attack' ? (e.animT < 3 ? 'attackA' : 'attackB') : 'idle');
    // El Párpado 3: one of the group's own, in black glass with a cloak of smoke; a little bigger than a hero.
    const glass = !!s.rules?.copies && !bare;
    const big = bare ? 2.2 : glass ? 1.45 : 1.9;
    const scale = this.lib.scaleOf(key) * big;
    const yaw = anim ? this.faceTo(e.id, facingToRotY(e.facing)) : facingToRotY(e.facing);
    const top = this.lib.heightOf(key) * scale;
    this.shadows.add(p.x, ground, p.z, big * 0.55);
    if (e.un && !bare) this.unsleepingFx(s, e, p, yaw, top);
    const lp = m.hold > 0 ? this.lurchAt.set(p.x + Math.sin(yaw) * 0.05, p.y - 0.03, p.z + Math.cos(yaw) * 0.05) : p;
    this.compose(lp, yaw, scale);
    const tint = this.flashes.has(e.id) ? FLASH : glass ? (e.stun > 0 ? GLASS_STUN : GLASS) : e.stun > 0 ? STUNNED : bare ? undefined : PAPER_SKIN;
    if (glass) this.glassFx(s, e, p, yaw, top);
    b.add(this.m, frame, tint, 1, anim);
    if (anim) {
      const d = this.drawnAt.get(e.id);
      if (d && d.key === key) {
        d.p.copy(lp);
        d.yaw = yaw;
        d.scale = scale;
      } else this.drawnAt.set(e.id, { key, p: lp.clone(), yaw, scale, tint: undefined });
    }
    this.bars.add(this.v.copy(p).setY(p.y + top + 0.12), e.hp / e.maxHp, RED, 1.4);
    if (e.un && !bare) this.bars.add(this.v.copy(p).setY(p.y + top), e.un.cast > 0 ? 1 : e.un.rage / UNSLEEPING.rageMax, RAGE, 1.4);
    // (At the clan's call everything stands still to hear it: no daze stars.)
    if (e.stun > 0 && !s.call) this.skillFx.dazed(e.id, p, top);
    this.facetakerAt = { e, p: p.clone(), top };
  }


  /**
   * A copy's glass (El Párpado 3): a cold sheen down its edge, the smoke trailing off its back like a cloak, and cracks
   * of pale light that open over it as it loses life (one more every sixth of it).
   */
  private glassFx(s: BattleState, e: Enemy, p: THREE.Vector3, yaw: number, top: number): void {
    const t = this.now;
    this.entityGlow.add(p.x, p.y + top * 0.55, p.z, GLASS_SHEEN, top * 0.7, 0.07 + 0.03 * Math.sin(t * 2.3 + e.id));
    const cracks = Math.min(6, Math.floor((1 - Math.max(0, e.hp) / Math.max(1, e.maxHp)) * 6));
    for (let i = 0; i < cracks; i++) {
      // Fixed spots on its body (by its id), each flickering on its own.
      const a = yaw + (((e.id * 7 + i * 13) % 12) / 12) * Math.PI * 2;
      const hgt = top * (0.25 + (((e.id + i * 5) % 7) / 7) * 0.6);
      const f = 0.55 + 0.45 * Math.sin(t * (3 + i) + i * 1.7);
      this.entityGlow.add(p.x + Math.sin(a) * 0.16, p.y + hgt, p.z + Math.cos(a) * 0.16, GLASS_CRACK, 0.28 + i * 0.03, 0.35 * f);
    }
    // The cloak: dark smoke off its back, thicker while it walks.
    const every = e.anim === 'walk' ? 2 : 4;
    if ((s.tick + e.id) % every === 0) {
      const q = this.v.set(p.x - Math.sin(yaw) * 0.2, p.y + top * 0.55, p.z - Math.cos(yaw) * 0.2);
      this.vfx.burst(q, { n: 1, color: '#15161d', speed: 0.01, up: 0.012, size: 0.5, life: 22, smoke: true, spread: 0.25 });
    }
  }

  /**
   * A copy coming out (smoke rising off the eyelid's edge as it steps out of the dark) or falling (its body kneels in
   * its real colours a moment, the die clip of the hero's own miniature, then shatters to black shards).
   */
  private copyMoment(kind: 'out' | 'fell', x: number, y: number): void {
    const low = this.opts.quality === 'low';
    const at = simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y));
    if (kind === 'out') {
      this.vfx.burst(at.clone().setY(at.y + 0.4), { n: low ? 6 : 12, color: '#15161d', speed: 0.03, up: 0.02, size: 0.8, life: 30, smoke: true, spread: 0.5 });
      this.vfx.ring(at.clone().setY(at.y + 0.06), 0.3, 2.2, '#8f9cff', 16, 0.55);
      return;
    }
    this.later.push({
      n: Math.round(COPY_KNEEL * TICK_HZ),
      run: () => {
        const q = at.clone().setY(at.y + 0.6);
        this.flare(q, '#dfe6ff', 4, 12);
        this.vfx.burst(q, { n: low ? 14 : 28, color: '#0f1117', speed: 0.09, up: 0.12, size: 0.12, life: 22, gravity: 0.012, spread: 0.3 });
        this.vfx.burst(q, { n: low ? 4 : 8, color: '#c7d0ff', speed: 0.07, up: 0.1, size: 0.06, life: 16, gravity: 0.01, spread: 0.3 });
        this.vfx.burst(q, { n: low ? 3 : 6, color: '#15161d', speed: 0.02, up: 0.02, size: 0.7, life: 26, smoke: true, spread: 0.4 });
        this.vfx.ring(at.clone().setY(at.y + 0.06), 0.3, 1.8, '#c7d0ff', 12, 0.6);
      },
    });
  }

  private enemyAct(s: BattleState, b: ModelBatch, e: Enemy): AnimParams | undefined {
    if (!b.baked.anim) return undefined;
    const idle = this.now + e.id * 0.37;
    if (e.kind === 'shell') {
      const was = this.phaseSeen.get(e.id);
      if (was !== undefined && e.phase > was) this.force(e.id, e.phase === 2 ? 'walls' : 'enrage');
      this.phaseSeen.set(e.id, e.phase);
    }
    // The Warden roars (untouchable) for a while: the sim keeps it in 'attack' meanwhile.
    if (s.dungeon && s.dungeon.boss === e.id && s.dungeon.roarUntil > s.tick) return this.act(b, e.id, ['roar'], this.sinceTag(e.id, 'roar'));
    // An Unsleeping channelling his ultimate holds his sword up as long as it lasts; fading, he stands still.
    if (e.un && e.un.cast > 0) return this.act(b, e.id, ['channel', 'idle'], this.sinceTag(e.id, 'channel'));
    if (e.un && e.un.flee > 0) return this.act(b, e.id, ['idle'], this.sinceTag(e.id, 'fade'));
    // A Chasm guardian under its rule (docs/SIMA.md): what it is doing is the sim's `sima.act`, not its anim.
    const sa = s.dungeon?.sima?.act;
    if (sa && sa.by === e.id) {
      const since = (sa.from > s.tick ? 0 : s.tick - sa.from) / 12;
      if (sa.kind === 'low') return this.act(b, e.id, ['exposed', 'idle'], this.sinceTag(e.id, 'exposed'));
      if (sa.kind === 'read') return this.act(b, e.id, ['read', 'idle'], this.sinceTag(e.id, 'read'));
      if (sa.kind === 'swell') return this.act(b, e.id, ['swell', 'idle'], since);
      if (sa.kind === 'inhale') return this.act(b, e.id, ['inhale', 'idle'], since);
    }
    // La Tenaza (the artificer's chapter 3): its pincers open wide as it warns, clamped round the wheel as it grips.
    const tz = s.senda?.cams?.tz;
    if (tz && tz.id === e.id && e.hp > 0) {
      if (tz.wind >= 0) return this.act(b, e.id, ['open', 'idle'], this.sinceTag(e.id, 'open'));
      if (tz.mode === 2) return this.act(b, e.id, ['grip', 'idle'], this.sinceTag(e.id, 'grip'));
    }
    // La Barrena (the artificer's chapter 4): its augers lifted as it warns its slash, turning on its legs before its spin.
    const bz = s.senda?.drills?.bz;
    if (bz && bz.id === e.id && e.hp > 0) {
      if (bz.wind >= 0) return this.act(b, e.id, ['raise', 'idle'], this.sinceTag(e.id, 'raise'));
      if (bz.spinAt >= 0) return this.act(b, e.id, ['spin', 'idle'], this.sinceTag(e.id, 'spin'));
    }
    // The worm come up at the end of its crack lies dazed.
    if (e.kind === 'marrowWorm' && e.stun > 0) return this.act(b, e.id, ['dazed', 'stun', 'idle'], this.sinceTag(e.id, 'dazed'));
    // Ragna kneeling at the clan's call (El Cuerno 4): down on one knee, the axe planted, until the fight ends.
    if (s.call && s.call.id === e.id) return this.act(b, e.id, ['kneel', 'idle'], this.sinceTag(e.id, 'kneel'));
    // The Old Uro (El Cuerno 3): what it is doing is the hunt's, not its anim's (a blow doesn't stop a charge).
    if (e.kind === 'uro' && s.hunt) {
      if (e.stun > 0) return this.act(b, e.id, ['stun', 'idle'], this.sinceTag(e.id, 'stun'));
      if (s.hunt.mode === 'wind') return this.act(b, e.id, ['paw', 'idle'], this.sinceTag(e.id, 'paw'));
      if (s.hunt.mode === 'charge') return this.act(b, e.id, ['charge', 'walk'], this.stride(e.id, 1));
      if (s.hunt.mode === 'rest') return this.act(b, e.id, ['idle'], this.sinceTag(e.id, 'blow'));
      return this.act(b, e.id, ['walk'], this.stride(e.id, 1));
    }
    // La Orilla 5: the Drowned Dragon drawing breath, breathing, held by a harpoon; it climbs out of the sea at first.
    if (e.kind === 'sunken' && s.sunken) {
      if (e.stun > 0) return this.act(b, e.id, ['stun', 'idle'], this.sinceTag(e.id, 'stun'));
      if (s.sunken.draw) return this.act(b, e.id, ['inhale', 'idle'], this.sinceTag(e.id, 'inhale'));
      if (s.sunken.held > s.tick) return this.act(b, e.id, ['held', 'idle'], this.sinceTag(e.id, 'held'));
    }
    switch (e.anim) {
      case 'attack': {
        const t = this.ago(e.animT);
        // Waiting out its cooldown in reach, it stays in 'attack': once the swing is over it idles.
        const c = b.clip('attack');
        this.sinceTag(e.id, 'attack');
        return this.act(b, e.id, c && t < c.dur ? ['attack'] : ['idle', 'walk'], c && t < c.dur ? t : idle);
      }
      case 'hit':
        this.sinceTag(e.id, 'hit');
        return this.act(b, e.id, ['hit', 'walk'], this.ago(e.animT));
      case 'stun':
        return this.act(b, e.id, ['stun', 'idle'], this.sinceTag(e.id, 'stun'));
      // Waiting for its general's horn (a battle plan's staging cell, or by its engine).
      case 'idle':
        this.sinceTag(e.id, 'idle');
        return this.act(b, e.id, ['idle', 'walk'], idle);
      default: {
        this.sinceTag(e.id, 'walk');
        // Slowed (frost, nets), the stride slows with it.
        const rate = Math.max(0.3, 1 - e.slowBp / 10000);
        return this.act(b, e.id, ['walk'], this.stride(e.id, rate));
      }
    }
  }

  /** A one-shot on the nightmare of `kind` nearest a burst. */
  private forceNear(s: BattleState, kind: string, x: number, y: number, clip: string): void {
    const n = this.nearestOf(s, kind, x, y);
    if (n) this.force(n.id, clip);
  }

  /** Bosses' powers (skill events carry the caster's id): their own clips. */
  private bossPower(s: BattleState, e: Extract<BattleEvent, { t: 'skill' }>): void {
    const clip: Record<string, string> = {
      magmaSlam: 'erupt', colossusGlint: 'glint', colossusShine: 'shine', colossusSlam: 'slam', wardenCast: 'cast',
      dragonBreath: 'breath', dragonRoar: 'roar', dragonDevour: 'devour', dragonSweep: 'sweep', guardianRise: 'roar',
      // The Chasm's guardians (docs/SIMA.md): the moment of each rule.
      simaRoots: 'plunge', simaBeams: 'beam', simaIce: 'cast', simaCocoon: 'lay', simaDive: 'dive', simaSurface: 'surface', simaLow: 'rise', simaRise: 'sink', simaReadCut: 'interrupt',
    };
    const boss = s.enemies.find((x) => x.id === e.hero);
    if (!boss) return;
    const un = unOf(e.skill);
    if (un) {
      const c = UN_RENDER[un].clips?.[e.skill] ?? (e.skill.endsWith('Arrive') || e.skill.endsWith('Phase') ? 'rally' : undefined);
      if (c) this.force(boss.id, c);
      return;
    }
    // The molten Warden's own slam is a double-fist blow; the magma lord's raises an eruption.
    const c = e.skill === 'magmaSlam' && boss.kind === 'warden' ? 'slam' : clip[e.skill];
    if (c) this.force(boss.id, c);
  }

  private onEvent(s: BattleState, e: BattleEvent): void {
    const at = (x: number, y: number, h = 0.5): THREE.Vector3 => simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y) + h);
    switch (e.t) {
      case 'beam': {
        // A skill drawn as a line: a chain of lightning, a poisoned arrow, a ray of faith, an axe, a charge.
        const a = at(e.x, e.y, 0.8);
        const b = at(e.tx, e.ty, 0.6);
        const low = this.opts.quality === 'low';
        const un = unOf(e.kind);
        if (un && UN_RENDER[un].beam?.(this.unCtx, e.kind, a, b)) {
          // One of the Unsleeping's lines (unsleeping/<id>.ts).
        } else if (this.formFx.beam(e.kind, a, b)) {
          // A unique piece's form (formfx.ts).
        } else if (e.kind === 'inkSpit') {
          // El pozo 2: the Drinker spits ink.
          this.pipes?.spit(a, b);
        } else if (e.kind === 'chain') {
          this.vfx.beam(a, b, '#ffffff', 2, 0.04, 0.6);
          this.vfx.beam(a, b, '#d6ccff', 3, 0.02, 1);
          this.vfx.burst(b, { n: low ? 2 : 5, color: '#e9e3ff', speed: 0.07, up: 0.04, size: 0.06, life: 4 });
          this.flare(b, '#d6ccff', 1, 2);
        } else if (e.kind === 'poisonArrow') {
          // A venom arrow: it flies with a green glint, leaving a trail of green vapour that hangs and drifts; it
          // bursts in a splash of venom that drips off its mark.
          this.skillFx.shot(a, b, 0.16, 1, VENOM);
          this.vfx.beam(a, b, '#b8f070', 3, 0.014, 0);
          this.vfx.shaft(a, b, '#4f9a5a', 6, 0.1);
          const steps = low ? 3 : 7;
          for (let i = 1; i <= steps; i++) this.later.push({ n: Math.floor(i / 3), run: () => this.vfx.burst(a.clone().lerp(b, i / (steps + 1)), { n: 1, color: '#5f8a4a', speed: 0.008, up: 0.006, size: 0.26, life: 18, smoke: true }) });
          this.later.push({
            n: 1,
            run: () => {
              this.vfx.burst(b, { n: low ? 4 : 10, color: '#8fdc4a', speed: 0.06, up: 0.05, size: 0.06, life: 9, gravity: 0.012 });
              this.vfx.burst(b, { n: low ? 1 : 3, color: '#4f7a3a', speed: 0.03, up: 0.02, size: 0.4, life: 16, smoke: true });
              this.vfx.ring(b.clone().setY(b.y - 0.55), 0.1, 0.7, '#9ab84a', 6, 0.7);
              this.flare(b, '#8fdc4a', 1, 3);
            },
          });
        } else if (e.kind === 'bond') {
          // A bond glyph (docs/GLIFOS.md §1): a pale thread with a red core from the one it reached to the next.
          this.vfx.beam(a, b, '#e9ecf1', 6, 0.03, 0.4);
          this.vfx.beam(a, b, '#ff8a6e', 5, 0.012, 0.6);
          this.vfx.ring(b.clone().setY(b.y - 0.4), 0.1, 0.7, '#ff8a6e', 6, 0.6);
        } else if (e.kind === 'faithRay') {
          this.vfx.shaft(a, b, '#eef2ff', 6, 0.35);
          this.vfx.beam(a, b, '#ffffff', 3, 0.05, 0);
          this.flare(b, '#ffffff', 1.2, 3);
        } else if (e.kind === 'axe') {
          this.vfx.beam(a, b, '#c4c8d2', 3, 0.025, 0.2);
          this.gearFx.cut(b, '#ffffff', 0.9, 0.2);
        } else if (e.kind === 'acidDart') {
          this.vfx.beam(a, b, '#b5e06a', 3, 0.03, 0);
          this.vfx.burst(b, { n: low ? 3 : 7, color: '#b5e06a', speed: 0.05, up: 0.03, size: 0.08, life: 10, gravity: 0.006 });
        } else if (e.kind === 'shadowChain') {
          this.vfx.beam(a, b, '#9a86d0', 4, 0.04, 0.3);
        } else if (e.kind === 'piercingLight') {
          this.vfx.beam(a, b, '#ffffff', 3, 0.03, 0);
          this.flare(b, '#ffffff', 1.4, 3);
        } else if (e.kind === 'duel') {
          this.vfx.beam(a, b, '#ff6a3a', 3, 0.04, 0);
        } else if (e.kind === 'coil') {
          this.vfx.beam(a.clone().setY(a.y + 0.1), b, '#c8bcff', 4, 0.06, 0.7);
          this.vfx.beam(a.clone().setY(a.y + 0.1), b, '#ffffff', 3, 0.02, 0.5);
          this.vfx.burst(b, { n: low ? 2 : 4, color: '#e9e3ff', speed: 0.06, up: 0.03, size: 0.05, life: 4 });
        } else if (e.kind === 'intervene') {
          this.vfx.beam(a, b, '#eef2ff', 4, 0.08, 0);
        } else if (e.kind === 'hiveSting' || e.kind === 'contagion') {
          // A wasp off the hive, or the plague passing on: a thin poison-green thread and a puff where it lands.
          this.vfx.beam(a, b, '#9ab84a', 2, 0.025, 0.15);
          this.vfx.burst(b, { n: low ? 1 : 3, color: '#7f9a3a', speed: 0.03, up: 0.02, size: 0.2, life: 8, smoke: true });
        } else if (e.kind === 'briarBack') {
          // The thorns answering a blow: a short brown lash back at the striker.
          this.vfx.beam(a, b, '#6a4a34', 2, 0.03, 0.1);
          this.gearFx.cut(b, '#c4a888', 0.6, 0.15);
        } else if (e.kind === 'treeHold') {
          // A tree's roots grabbing what it struck: a dark root line and a ring at its feet.
          this.vfx.beam(a.clone().setY(a.y - 0.4), b.clone().setY(b.y - 0.4), '#4a3424', 3, 0.04, 0.1);
          this.vfx.ring(b.clone().setY(b.y - 0.5), 0.1, 0.55, '#7a5634', 6, 0.7);
        } else if (e.kind === 'axeLeap') {
          // The shepherd's axe leaping on: the axe's streak again, and a cut where it lands.
          this.vfx.beam(a, b, '#c4c8d2', 3, 0.025, 0.25);
          this.gearFx.cut(b, '#ffffff', 0.8, 0.2);
        } else if (e.kind === 'chargeBack') {
          // The run back: a dust streak low over the ground.
          this.vfx.beam(a.clone().setY(a.y - 0.4), b.clone().setY(b.y - 0.4), '#8a7e70', 4, 0.08, 0);
          this.vfx.burst(b.clone().setY(b.y - 0.5), { n: low ? 3 : 7, color: '#5b524a', speed: 0.05, up: 0.03, size: 0.3, life: 10, smoke: true });
        } else if (e.kind === 'dustTrail') {
          // The broken path: dust hanging over it while it lasts.
          for (let i = 0; i <= (low ? 2 : 4); i++) this.vfx.burst(a.clone().lerp(b, i / (low ? 2 : 4)).setY(a.y - 0.6), { n: 1, color: '#6a5e50', speed: 0.02, up: 0.015, size: 0.4, life: 8, smoke: true });
        } else if (e.kind === 'spirit') {
          // An ancestor's spirit leaping: a grey smoke thread with a pale core.
          this.vfx.beam(a, b, '#8a8e98', 4, 0.06, 0.5);
          this.vfx.beam(a, b, '#e3e6ec', 3, 0.015, 0.5);
          this.vfx.burst(b, { n: low ? 2 : 4, color: '#5a5e68', speed: 0.03, up: 0.04, size: 0.3, life: 8, smoke: true });
        } else if (e.kind === 'twoSeals') {
          this.vfx.beam(a, b, '#e9ecf1', 3, 0.025, 0.2);
        } else if (e.kind === 'hammerLoose') {
          // The loose hammer flying straight out: a white streak along its line.
          this.vfx.shaft(a.clone().setY(a.y + 0.2), b.clone().setY(b.y + 0.2), '#eef2ff', 5, 0.18);
          this.vfx.beam(a.clone().setY(a.y + 0.2), b.clone().setY(b.y + 0.2), '#ffffff', 4, 0.04, 0);
        } else if (e.kind === 'blessShare') {
          this.vfx.beam(a, b, '#eef2ff', 4, 0.03, 0.4);
        } else if (e.kind === 'arrowJump' || e.kind === 'leapArrow' || e.kind === 'splinter') {
          // The ranger's pieces' extra arrows (the jump, the leap's, a splinter): a quick pale streak, no trail.
          this.vfx.beam(a, b, '#d8cbb8', 2, e.kind === 'splinter' ? 0.02 : 0.03, 0);
          this.vfx.burst(b, { n: low ? 1 : 3, color: '#c4c8d2', speed: 0.05, up: 0.03, size: 0.05, life: 4 });
        } else if (e.kind === 'deflect' || e.kind === 'titanArrowBack') {
          // The quarry bow's turned line, the ballista's arrow coming back: the piercing shot's streak, thinner.
          this.vfx.shaft(a, b, '#e3e6ec', 3, 0.12);
          this.vfx.beam(a, b, '#ffffff', 2, 0.03, 0);
        } else if (e.kind === 'hawkDive') {
          // The hawk's dive: a dark streak from above onto its prey and a small dust ring there.
          this.vfx.beam(a.clone().setY(a.y + 1.2), b, '#4a4440', 2, 0.04, 0);
          this.vfx.ring(b.clone().setY(b.y - 0.5), 0.1, 0.6, '#d8cbb8', 4, 0.8);
        } else if (e.kind === 'rayStay') {
          // The lingering ray: the faith ray's shaft, fainter, flickering where it lay.
          this.vfx.shaft(a, b, '#d6e0ee', 4, 0.18);
        } else if (e.kind === 'lightJump' || e.kind === 'stateBack') {
          // A light jumping on, a state given back: a thin pale thread.
          this.vfx.beam(a, b, e.kind === 'stateBack' ? '#c4c8d2' : '#eef2ff', 2, 0.03, 0.15);
          this.flare(b, '#ffffff', 0.8, 2);
        } else if (e.kind === 'renewPass' || e.kind === 'markPass' || e.kind === 'totemLeap') {
          // A renewal, a mark or a totem passing to another: a soft white arc.
          this.vfx.beam(a.clone().setY(a.y + 0.6), b.clone().setY(b.y + 0.6), '#eef2ff', 2, 0.04, 0);
          this.vfx.ring(b, 0.1, 0.7, '#ffffff', 5, 0.7);
        } else if (e.kind === 'markJump') {
          // The cleaver's mark jumping to the next (docs/PANOPLIA.md §9.5): a pale thread, two ticks long, and a cut where it lands.
          this.vfx.beam(a, b, '#e3e6ec', 2, 0.03, 0.2);
          this.gearFx.cut(b, '#e3e6ec', 0.8, 0.2);
          this.vfx.ring(b.clone().setY(b.y - 0.5), 0.1, 0.6, '#c4c8d2', 4, 0.7);
        } else if (e.kind === 'mineTrail') {
          // The miner's pick: a spark running along the ground from mine to mine.
          this.vfx.beam(a.clone().setY(a.y - 0.4), b.clone().setY(b.y - 0.4), '#ffb070', 2, 0.03, 0.3);
        } else if (e.kind === 'sparkArc' || e.kind === 'boltBack' || e.kind === 'boltSplit') {
          // The coil's last spark, the storm staff's bolt back (thinner) and its split: lightning's lilac and white.
          const thin = e.kind === 'boltBack';
          this.vfx.beam(a, b, '#ffffff', 2, thin ? 0.02 : 0.04, 0.6);
          this.vfx.beam(a, b, '#d6ccff', 3, thin ? 0.012 : 0.02, 1);
          this.vfx.burst(b, { n: low ? 1 : 3, color: '#e9e3ff', speed: 0.06, up: 0.03, size: 0.05, life: 4 });
        } else if (e.kind === 'shareDeath') {
          // A bound one's death shared down the binding: a dark violet line.
          this.vfx.beam(a, b, '#6b5aa0', 4, 0.04, 0.3);
          this.vfx.burst(b, { n: low ? 2 : 4, color: '#b9a8ff', speed: 0.05, up: 0.03, size: 0.06, life: 6 });
        } else if (e.kind === 'decoySwap') {
          // The drill plate's swap: a faint line between where the warrior was and where it stands now.
          this.vfx.beam(a, b, '#c4c8d2', 2, 0.04, 0);
          this.vfx.burst(a, { n: low ? 2 : 5, color: '#8a7e70', speed: 0.05, up: 0.04, size: 0.1, life: 6 });
        } else if (e.kind === 'bannerThrow') {
          // The banner thrown: a streak of its pole through the air.
          this.vfx.beam(a.clone().setY(a.y + 0.8), b.clone().setY(b.y + 0.8), '#d8cbb8', 2, 0.035, 0);
          this.vfx.shaft(a.clone().setY(a.y + 0.8), b.clone().setY(b.y + 0.8), '#e3e6ec', 3, 0.08);
        } else if (e.kind === 'chainHook') {
          // The chain: dark links with a pale glint along them, a hook's flash where it bites.
          this.vfx.beam(a, b, '#4a4f57', 3, 0.05, 0.15);
          this.vfx.beam(a, b, '#c4c8d2', 2, 0.015, 0.1);
          this.flare(b, '#ffffff', 0.9, 2);
        } else if (e.kind === 'frostLance') {
          this.vfx.beam(a, b, '#9fdcff', 4, 0.08, 0);
          this.vfx.beam(a, b, '#ffffff', 3, 0.03, 0);
          for (let i = 1; i <= 5; i++) this.vfx.burst(a.clone().lerp(b, i / 5), { n: 1, color: '#e8f7ff', speed: 0.03, up: 0.01, size: 0.08, life: 10, gravity: 0.004 });
        } else if (e.kind === 'piercingShot') {
          // A piercing shot: one arrow driven flat through the line, a long line of wind behind it, the air
          // rippling and the dust lifting under it as it passes.
          this.skillFx.shot(a, b, 0.14, 1.15);
          this.vfx.shaft(a, b, '#b8c8e0', 5, 0.16);
          this.vfx.beam(a, b, '#ffffff', 2, 0.012, 0);
          const dir = b.clone().sub(a).setY(0).normalize();
          this.skillFx.gust(a.clone().setY(a.y - 0.2), dir, 1.5, '#dfe8f4', 0.3);
          const n = low ? 3 : 6;
          for (let i = 1; i <= n; i++) {
            const q = a.clone().lerp(b, i / (n + 1)).setY(a.y - 0.75);
            this.later.push({ n: Math.floor((i * 2) / n), run: () => {
              this.vfx.ring(q, 0.1, 0.6, '#dfe8f4', 5, 0.5);
              this.vfx.burst(q, { n: 1, color: '#6d6458', speed: 0.03, up: 0.02, size: 0.28, life: 10, smoke: true });
            } });
          }
          this.flare(a, '#ffffff', 1.3, 2);
        } else if (e.kind === 'titanArrow') {
          // The titan's arrow: a huge heavy shaft driven through the line, a thick streak with a white core and a
          // dragon-red edge, the ground torn in shockwaves all along it and a crater where it ends.
          this.skillFx.shot(a, b, 0.2, 3, WHITE);
          this.vfx.beam(a, b, '#ff4f3a', 4, 0.14, 0);
          this.vfx.beam(a, b, '#ffffff', 3, 0.05, 0);
          this.vfx.shaft(a, b, '#ffd8c8', 6, 0.26);
          const dir = b.clone().sub(a).setY(0).normalize();
          this.skillFx.gust(a.clone().setY(a.y - 0.3), dir, 2.2, '#ffd8c8', 0.4);
          const n = low ? 4 : 8;
          for (let i = 1; i <= n; i++) {
            const q = a.clone().lerp(b, i / n).setY(a.y - 0.75);
            this.later.push({ n: Math.floor((i * 2) / n), run: () => {
              this.vfx.ring(q, 0.15, 1, i % 2 ? '#ff8a6e' : '#f2f6ff', 6, 0.5);
              this.vfx.burst(q, { n: low ? 1 : 2, color: '#6b5f52', speed: 0.05, up: 0.04, size: 0.45, life: 14, smoke: true });
              if (!low) this.vfx.burst(q, { n: 2, color: '#8a7e70', speed: 0.08, up: 0.08, size: 0.07, life: 9, gravity: 0.014 });
            } });
          }
          const end = b.clone().setY(b.y - 0.55);
          this.later.push({ n: 2, run: () => {
            this.skillFx.crater(end, 1.3);
            this.vfx.ring(end, 0.3, 2.4, '#ffffff', 8, 0.8);
            this.flare(b, '#ffd8c8', 2.6, 4);
            this.shake = Math.max(this.shake, 0.16);
          } });
          this.flare(a, '#ffd8c8', 1.3, 3);
          this.shake = Math.max(this.shake, 0.12);
        } else if (e.kind === 'charge') {
          // The trail of dust it ran through.
          for (let i = 0; i <= 6; i++) this.vfx.burst(a.clone().lerp(b, i / 6).setY(a.y - 0.6), { n: 1, color: '#5b524a', speed: 0.03, up: 0.03, size: 0.35, life: 12, smoke: true });
          this.vfx.beam(a, b, '#ff8a6e', 3, 0.05, 0);
        }
        break;
      }
      case 'hit': {
        this.flashes.set(e.id, 2);
        this.auraHit(s, e);
        // A hero's critical: a white-hot flash and a spray of sparks, the board jolts a hair (a few per tick at most).
        if (e.crit && this.critsThisTick++ < 4) {
          const p = at(e.x, e.y, 0.6);
          const low = this.opts.quality === 'low';
          this.flare(p.clone(), '#ffd8c8', 1.5, 4);
          this.vfx.burst(p, { n: low ? 5 : 12, color: '#ffffff', speed: 0.13, up: 0.06, size: 0.08, life: 6, gravity: 0.006 });
          this.vfx.burst(p, { n: low ? 3 : 8, color: '#ff4f3a', speed: 0.09, up: 0.05, size: 0.1, life: 8, gravity: 0.004 });
          this.vfx.ring(p.clone().setY(p.y - 0.3), 0.1, 0.8, '#ff8a6e', 4, 0.7);
          this.shake = Math.max(this.shake, 0.05);
        }
        if (s.bots?.some((b) => b.id === e.id && b.kind === 'cart')) {
          this.force(e.id, 'hit');
          // The cask takes it: a spurt of beer.
          if (betOf(s) === 'barrel') this.vfx.burst(at(e.x, e.y, 0.55), { n: this.opts.quality === 'low' ? 4 : 9, color: '#e8ac3c', speed: 0.05, up: 0.06, size: 0.07, life: 9, gravity: 0.012, spread: 0.15 });
        }
        this.vfx.burst(at(e.x, e.y, 0.5), { n: 2, color: '#ffd0a0', speed: 0.05, up: 0.04, size: 0.1, life: 3 });
        // A bone ram battering a gate or wall: a heavy crash of splinters and dust, and the board jolts.
        const ram = s.enemies.find((x) => (x.kind === 'ram' || x.kind === 'mammoth') && x.hp > 0 && x.tgtKind === 2 && x.tgtId === e.id);
        if (ram?.kind === 'mammoth') this.mammothCrash(at(e.x, e.y, 0.1).lerp(at(ram.x, ram.y, 0.1), 0.35));
        else if (ram) {
          const low = this.opts.quality === 'low';
          const p = at(e.x, e.y, 0.1).lerp(at(ram.x, ram.y, 0.1), 0.4);
          this.vfx.ring(p, 0.2, 1.3, '#cfc3a4', 7, 0.7);
          this.vfx.burst(p.clone().setY(p.y + 0.5), { n: low ? 6 : 14, color: '#7a5634', speed: 0.11, up: 0.09, size: 0.12, life: 11, gravity: 0.012 });
          this.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 3 : 8, color: '#7a6e60', speed: 0.06, up: 0.03, size: 0.5, life: 16, smoke: true, spread: 0.4 });
          this.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 2 : 5, color: '#e6dccb', speed: 0.08, up: 0.06, size: 0.09, life: 6 });
          this.shake = Math.max(this.shake, 0.16);
        }
        break;
      }
      case 'mend': {
        // A mourner's thread of ink to the nightmare it mends: a lilac strand from its censer, motes where it lands.
        const m = s.enemies.find((x) => x.id === e.from);
        // The mourner mends; the bonesinger sings a fallen one back up.
        this.force(e.from, m?.kind === 'bonesinger' ? 'raise' : 'heal');
        const to = at(e.x, e.y, 0.6);
        if (m) {
          const from = at(m.x, m.y, 1.15);
          this.vfx.beam(from, to, '#b48cff', 6, 0.035, 0.12);
          this.vfx.beam(from, to, '#e8dcff', 3, 0.015, 0.05);
          this.flare(from.clone(), '#a77bff', 1.1, 6);
        }
        this.vfx.burst(to, { n: this.opts.quality === 'low' ? 2 : 5, color: '#c9b0ff', speed: 0.015, up: 0.03, size: 0.09, life: 12, gravity: -0.002, spread: 0.3 });
        break;
      }
      case 'heal': {
        // Rising motes of green-white light on the healed hero (the "+N" itself is drawn by the HUD).
        const h = s.heroes.find((x) => x.id === e.id);
        const hp = h ? at(h.x, h.y, 0.1) : at(e.x, e.y, 0.1);
        this.vfx.burst(hp.clone().setY(hp.y + 0.15), { n: 8, color: '#cfffe0', speed: 0.012, up: 0.035, size: 0.1, life: 14, gravity: -0.002, spread: 0.45 });
        this.vfx.burst(hp.clone().setY(hp.y + 0.45), { n: 4, color: '#ffffff', speed: 0.01, up: 0.03, size: 0.07, life: 12, gravity: -0.002, spread: 0.35 });
        this.flare(hp.clone().setY(hp.y + 0.55), '#bfffd4', 1.0, 6);
        break;
      }
      case 'shot': {
        const a = at(e.x, e.y, 0.9);
        const b = at(e.tx, e.ty, 0.5);
        if (e.kind === 'boulder') {
          // The boulder lets go: it rolls down its line once (drawRolling), dust thrown up behind it.
          this.rolling.push({ ax: a.x, az: a.z, bx: b.x, bz: b.z, sx: e.x, sy: e.y, tx: e.tx, ty: e.ty, at: this.now, dur: Math.max(0.4, Math.hypot(b.x - a.x, b.z - a.z) / 4.2) });
          this.vfx.burst(a.setY(a.y - 0.6), { n: 10, color: '#8b857a', speed: 0.05, up: 0.04, size: 0.25, life: 14, smoke: true });
        } else if (e.kind === 'arcane') {
          // The drowsing tower's shot is a pale, slow-looking ray; any other arcane tower's the usual blue.
          const st = this.branchAt.size ? s.structures.find((x) => x.id === e.from) : undefined;
          if (st?.branch === 'drowse') {
            this.vfx.beam(a.setY(a.y + 0.6), b, '#e4ecff', 2, 0.09, 0.3);
            this.vfx.burst(b, { n: 5, color: '#f2f6ff', speed: 0.02, up: 0.03, size: 0.16, life: 12, gravity: -0.002 });
          } else {
            this.vfx.beam(a.setY(a.y + 0.6), b, '#7fc8ff', 2, 0.07, 0.25);
            this.vfx.burst(b, { n: 3, color: '#8fd8ff', speed: 0.06, up: 0.05, size: 0.14, life: 4 });
          }
        } else if (e.kind === 'chain') {
          this.vfx.beam(a.setY(a.y - 0.4), b, '#e0f0ff', 2, 0.05, 0.4);
        } else if (e.kind.startsWith('gear')) {
          this.gearShot(e);
        } else if (this.sima?.shot(e.kind, e.x, e.y, e.tx, e.ty)) {
          // The Chasm's breaths (the bellows' fire, the frost lady's frost), and the clip that blows them.
          this.force(e.from, e.kind === 'simaBlow' ? 'blow' : 'breath');
        } else if (e.kind === 'curse') {
          this.force(e.from, 'curse');
          this.vfx.beam(a.setY(a.y + 0.6), b.setY(b.y + 0.8), '#58ff6a', 6, 0.08, 0.5);
        } else if (e.kind === 'slash') {
          this.vfx.burst(b, { n: 3, color: '#ffffff', speed: 0.07, up: 0.03, size: 0.09, life: 3 });
          if (s.heroes.some((h) => h.id === e.from && h.cls === 'warrior')) this.cleave(s, e.to, e.x, e.y, e.tx, e.ty, b);
        } else if (e.kind === 'claw') {
          this.vfx.burst(b, { n: 6, color: '#cfe0b0', speed: 0.09, up: 0.04, size: 0.12, life: 4 });
          this.vfx.ring(b.clone().setY(0.1), 0.2, 1.2, '#6fa05a', 4, 0.6);
        } else if (e.kind === 'smite') {
          this.vfx.beam(a, b, '#f4f7ff', 3, 0.06, 0.3);
        } else if (e.kind === 'spikes') {
          this.vfx.burst(at(e.x, e.y, 0.1), { n: 6, color: '#8a6a4a', speed: 0.05, up: 0.06, size: 0.14, life: 6, smoke: true });
        } else if (e.kind === 'oil') {
          this.vfx.burst(at(e.x, e.y, 0.1), { n: 8, color: '#ff7a1a', speed: 0.04, up: 0.07, size: 0.22, life: 8 });
        } else if (e.kind === 'spectral') {
          this.spectralArrow(a, b);
        } else if (e.kind === 'wyvernFire') {
          const w = s.enemies.find((x) => x.id === e.from);
          this.wyvernFire(w ? (this.positions.get(w.id)?.clone().setY((this.positions.get(w.id)?.y ?? 0) + 0.5) ?? a) : a, b);
        } else if (e.kind === 'sling') {
          this.slingStone(at(e.x, e.y, 0.5), at(e.tx, e.ty, 0.5));
        } else if (e.kind === 'mangonelRock') {
          this.mangonelRock(at(e.x, e.y, 1.6), at(e.tx, e.ty, 0.6));
        } else if (e.kind === 'giantRock') {
          this.force(e.from, 'throw');
          this.giantRock(at(e.x, e.y, 2.4), at(e.tx, e.ty, 0.6));
        } else if (e.kind === 'stormBolt' || e.kind === 'stormChain') {
          // The storm herald's lightning: a white bolt from the cloud, then leaping from building to building.
          if (e.kind === 'stormBolt') this.force(e.from, 'cast');
          const from = e.kind === 'stormBolt' ? (this.positions.get(e.from)?.clone() ?? at(e.x, e.y, 2.5)) : at(e.x, e.y, 0.9);
          const to = at(e.tx, e.ty, 0.9);
          this.vfx.beam(from, to, '#e8f0ff', 4, 0.07, 0.35);
          this.vfx.beam(from, to, '#8fb4ff', 6, 0.16, 0.18);
          this.vfx.burst(to, { n: this.opts.quality === 'low' ? 4 : 10, color: '#dfe8ff', speed: 0.1, up: 0.05, size: 0.08, life: 6 });
          this.flare(to.clone(), '#cfdcff', 2.2, 4);
        } else if (e.kind === 'drakeSweep') {
          // The glass drake's sweep: a fan of fire rolling over the buildings before it.
          this.force(e.from, 'sweep');
          const from = this.positions.get(e.from)?.clone() ?? at(e.x, e.y, 2.2);
          const to = at(e.tx, e.ty, 0.6);
          this.vfx.beam(from, to, '#ff8a3a', 14, 0.5, 0.35);
          this.vfx.beam(from, to, '#ff4f3a', 10, 0.8, 0.2);
          this.vfx.burst(to, { n: this.opts.quality === 'low' ? 8 : 20, color: '#ff6a1a', speed: 0.08, up: 0.06, size: 0.18, life: 12, spread: 1.2 });
          this.vfx.burst(to, { n: 6, color: '#2a1a14', speed: 0.04, up: 0.05, size: 0.7, life: 20, smoke: true, spread: 1.0 });
        } else if (e.kind === 'steal') {
          // An ash watcher tears a hero's boons away: a pale glimmer flies from the hero to it.
          this.forceNear(s, 'ashWatcher', e.tx, e.ty, 'steal');
          this.vfx.beam(at(e.x, e.y, 0.9), at(e.tx, e.ty, 1.4), '#e8e4f4', 4, 0.06, 0.4);
          this.vfx.burst(at(e.x, e.y, 0.9), { n: 8, color: '#f4f0ff', speed: 0.06, up: 0.06, size: 0.08, life: 8 });
        } else if (e.kind === 'frostBreath') {
          // The frost colossus's breath: a cone of pale frost rolling toward the hero, snow where it lands.
          this.force(e.from, 'breath');
          const from = at(e.x, e.y, 1.8);
          const to = at(e.tx, e.ty, 0.6);
          this.vfx.beam(from, to, '#cfeaff', 10, 0.35, 0.25);
          this.vfx.burst(to, { n: this.opts.quality === 'low' ? 8 : 20, color: '#e8f6ff', speed: 0.06, up: 0.04, size: 0.14, life: 14, gravity: 0.003, spread: 0.6 });
          this.vfx.burst(to, { n: 5, color: '#a8d4f0', speed: 0.03, up: 0.02, size: 0.6, life: 18, smoke: true, spread: 0.5 });
        } else if (e.kind === 'silk') {
          this.force(e.from, 'weave');
          this.silkShot(at(e.x, e.y, 0.5), at(e.tx, e.ty, 1.0));
        } else if (e.kind === 'wyrmBreath') {
          this.force(e.from, 'breath');
          const w = this.positions.get(e.from);
          this.wyrmBreath(w ? w.clone() : at(e.x, e.y, 1.2), at(e.x, e.y, 0.05), at(e.tx, e.ty, 0.05));
        } else if (e.kind === 'link') {
          this.turretLink(at(e.x, e.y, 0.55), at(e.tx, e.ty, 0.55));
        } else if (e.kind === 'pierceShot') {
          this.pierceShot(at(e.x, e.y, 0.7), at(e.tx, e.ty, 0.6));
        } else if (e.kind === 'bolt' || e.kind === 'rock' || e.kind === 'boulder') {
          this.vfx.burst(a.setY(a.y + 0.3), { n: e.kind === 'boulder' ? 6 : 3, color: '#c9b08a', speed: 0.03, up: 0.02, size: 0.16, life: 4, smoke: true });
        }
        break;
      }
      case 'die': {
        this.inkFall(e);
        // The body falls where it was last drawn, plays its death and sinks (a few dozen at most at once).
        const was = this.drawnAt.get(e.id);
        // Slid over the tail's rim (El Cuerno 2): it tips off and drops to the sea (LashLayer splashes it).
        const fell = this.fallsNow.has(`${e.x},${e.y}`);
        if (fell && was) {
          this.corpses.push({ id: e.id, key: was.key, p: was.p, yaw: was.yaw, scale: was.scale, at: this.now, fall: this.collapseLayer?.fallDir() ?? new THREE.Vector3(was.p.x, 0, was.p.z).normalize() });
          if (this.corpses.length > 60) this.corpses.shift();
          this.drawnAt.delete(e.id);
          break;
        }
        // El Párpado 3: a copy of the group's own goes down on its knees in its real colours, and shatters (copyMoment).
        if (was && ANIM.on && s.rules?.copies && unOf(e.kind) === e.kind && this.lib.batch(was.key).clip('down')) {
          this.corpses.push({ id: e.id, key: was.key, p: was.p, yaw: was.yaw, scale: was.scale, at: this.now, copy: true });
          if (this.corpses.length > 60) this.corpses.shift();
        } else if (was && ANIM.on && this.lib.batch(was.key).clip('die')) {
          this.corpses.push({ id: e.id, key: was.key, p: was.p, yaw: was.yaw, scale: was.scale, at: this.now });
          if (this.corpses.length > 60) this.corpses.shift();
        }
        this.drawnAt.delete(e.id);
        const p = at(e.x, e.y, 0.4);
        const d = NIGHTMARES[e.kind as keyof typeof NIGHTMARES];
        const big = e.kind === 'witch' || e.kind === 'shell' || e.kind === 'warden' || e.kind === 'magmaLord' || e.kind === 'colossus' || SIMA_GUARDIANS.has(e.kind);
        if (e.kind === 'magmaLord') this.lordFalls(p);
        if (e.kind === 'colossus') this.colossusFalls(p);
        if (e.kind === 'dragon') this.dragonFalls(at(e.x, e.y, 0.05));
        const un = unOf(e.kind);
        // (A copy of the group's own falls its own way: kneeling, then shattering — copyMoment.)
        if (un && un === e.kind && !s.rules?.copies) {
          if (this.unFled.delete(e.id)) break;
          felled(this.unCtx, at(e.x, e.y, 0.05), UN_RENDER[un].look);
        }
        if (e.kind === 'warden') {
          this.vfx.ring(p, 0.5, 8, '#e7c9ff', 18, 1);
          this.vfx.burst(p.clone().setY(1), { n: 70, color: '#cfc3a4', speed: 0.12, up: 0.12, size: 0.22, life: 22, gravity: 0.008 });
          this.shake = Math.max(this.shake, 0.9);
        }
        this.vfx.burst(p, { n: big ? 40 : 10, color: '#1a1622', speed: 0.05, up: 0.04, size: big ? 0.7 : 0.35, life: 12, smoke: true, spread: 0.3 });
        this.vfx.burst(p, { n: big ? 30 : 8, color: '#ff8a2a', speed: 0.07, up: 0.07, size: 0.1, life: 10, gravity: -0.002 });
        if (d && d.flying) this.vfx.burst(p, { n: 6, color: '#c9b8ff', speed: 0.04, up: 0.02, size: 0.18, life: 8 });
        break;
      }
      case 'boom': {
        const p = at(e.x, e.y, 0.05);
        const r = (e.r / 1000) * WORLD_PER_HEX;
        // Powers that land as a burst: the witch's summoning, the shell spitting, a sapper coming up.
        if (e.kind === 'summon') this.forceNear(s, 'witch', e.x, e.y, 'summon');
        else if (e.kind === 'spit') this.forceNear(s, 'shell', e.x, e.y, 'spit');
        else if (e.kind === 'emerge') {
          this.forceNear(s, 'sapper', e.x, e.y, 'emerge');
          this.forceNear(s, 'shellSerpent', e.x, e.y, 'emerge');
        } else if (e.kind === 'dive') {
          this.earthBurst(false, p, r);
          break;
        } else if (e.kind === 'shake') {
          // A shellback shakes the nightmares off its back: dust and falling shapes.
          this.forceNear(s, 'shellback', e.x, e.y, 'shake');
          this.vfx.ring(p.clone().setY(p.y + 0.1), 0.3, r, '#8a8070', 10, 0.6);
          this.vfx.burst(p.clone().setY(p.y + 1.2), { n: this.opts.quality === 'low' ? 6 : 14, color: '#6d6458', speed: 0.06, up: 0.05, size: 0.5, life: 18, smoke: true, spread: 0.8 });
          this.shake = Math.max(this.shake, 0.08);
          break;
        } else if (e.kind === 'dream') {
          // A lidless dreamer opens its eyes: a violet wave of sleep rolls out.
          this.forceNear(s, 'lidlessDreamer', e.x, e.y, 'open');
          this.vfx.ring(p.clone().setY(p.y + 0.4), 0.3, r, '#a07aff', 18, 0.7);
          this.vfx.ring(p.clone().setY(p.y + 1.2), 0.2, r * 0.6, '#d8c8ff', 12, 0.4);
          this.flare(p.clone().setY(p.y + 1.6), '#b896ff', 3, 6);
          break;
        } else if (e.kind === 'blink' || e.kind === 'blinkIn') {
          // A lidless dreamer vanishes and comes back.
          if (e.kind === 'blinkIn') this.forceNear(s, 'lidlessDreamer', e.x, e.y, 'blink');
          this.vfx.burst(p.clone().setY(p.y + 1.4), { n: this.opts.quality === 'low' ? 6 : 12, color: '#c8b4ff', speed: 0.07, up: 0.03, size: 0.1, life: 8 });
          this.flare(p.clone().setY(p.y + 1.4), '#d8c8ff', 2, 4);
          break;
        } else if (e.kind === 'dreamBeacon') {
          // The Dream Beacon pulses on the keep: the spectres wake to full strength.
          const kp = this.keepPos.clone();
          this.vfx.ring(kp.clone().setY(0.2), 0.5, 9, '#cfe0ff', 24, 0.5);
          this.flare(kp.clone().setY(4.5), '#e8f0ff', 5, 10);
          break;
        } else if (e.kind === 'blocked') {
          // A shot glances off a sentinel's tower shield: a spark of steel.
          this.forceNear(s, 'sentinel', e.x, e.y, 'block');
          this.vfx.burst(p.clone().setY(p.y + 0.9), { n: 4, color: '#dfe8ff', speed: 0.08, up: 0.03, size: 0.06, life: 4 });
          break;
        } else if (e.kind === 'bash') {
          // A sentinel's shield bash: the hero reels in a ring of dust.
          this.forceNear(s, 'sentinel', e.x, e.y, 'attack');
          this.vfx.ring(p.clone().setY(p.y + 0.1), 0.1, 0.8, '#cfd6e0', 6, 0.6);
          this.vfx.burst(p.clone().setY(p.y + 0.8), { n: 6, color: '#f0f4ff', speed: 0.06, up: 0.05, size: 0.08, life: 6 });
          break;
        }
        else if (e.kind === 'roar') {
          this.forceNear(s, 'ogre', e.x, e.y, 'roar');
          this.ogreRoar(p, r);
          break;
        } else if (e.kind === 'panic') {
          // Fire touched an ash mammoth: it rears and trumpets, shedding ash and cinders, and turns to flee.
          const m = this.nearestOf(s, 'mammoth', e.x, e.y);
          if (m) this.force(m.id, 'panic');
          this.mammothPanic(p, r, m ? facingToRotY(m.facing) : 0);
          break;
        } else if (e.kind === 'crack') {
          // A beetle's shell splits: violet shards fly and the soft flesh shows.
          const bt = this.nearestOf(s, 'beetle', e.x, e.y);
          if (bt) this.force(bt.id, 'crack');
          this.vfx.burst(p.clone().setY(p.y + 0.4), { n: this.opts.quality === 'low' ? 8 : 18, color: '#2a1f3a', speed: 0.11, up: 0.1, size: 0.14, life: 12, gravity: 0.012 });
          this.vfx.burst(p.clone().setY(p.y + 0.4), { n: 6, color: '#b48cff', speed: 0.07, up: 0.05, size: 0.1, life: 9 });
          this.vfx.ring(p, 0.1, 0.9, '#b48cff', 5, 0.7);
          this.shake = Math.max(this.shake, 0.08);
          break;
        } else if (e.kind === 'raise') {
          // A fallen nightmare clawing back up as bone.
          this.vfx.burst(p.clone().setY(p.y + 0.1), { n: this.opts.quality === 'low' ? 5 : 12, color: '#d8d0c0', speed: 0.05, up: 0.09, size: 0.08, life: 12, gravity: 0.006 });
          this.vfx.ring(p, 0.1, 0.8, '#c9b0ff', 7, 0.8);
          this.vfx.shaft(p, p.clone().setY(p.y + 1.2), '#c9b0ff', 6, 0.15);
          break;
        } else if (e.kind === 'falter') {
          // The banner falls: a dull wave of doubt washes over the pack.
          this.vfx.ring(p.clone().setY(p.y + 0.2), 0.3, r, '#5a4a6a', 10, 0.6);
          this.vfx.burst(p.clone().setY(p.y + 0.8), { n: 8, color: '#8a7aa0', speed: 0.04, up: -0.01, size: 0.3, life: 14, smoke: true, spread: 0.4 });
          break;
        } else if (e.kind === 'stomp') {
          this.forceNear(s, 'giant', e.x, e.y, 'stomp');
          this.giantStomp(p, r);
          break;
        } else if (e.kind === 'wake') {
          // The giant opens its eyes: its waking roar rolls out like the ogre's, bigger.
          this.forceNear(s, 'giant', e.x, e.y, 'wake');
          this.ogreRoar(p, r);
          this.shake = Math.max(this.shake, 0.3);
          break;
        } else if (e.kind === 'sleep') {
          // A moth wraps a hero: a puff of glowing dream-dust and it slumps.
          this.forceNear(s, 'moth', e.x, e.y, 'dust');
          this.vfx.burst(p.clone().setY(p.y + 0.8), { n: this.opts.quality === 'low' ? 6 : 14, color: '#bcd8ff', speed: 0.04, up: 0.02, size: 0.12, life: 18, gravity: -0.001, spread: 0.4 });
          this.vfx.burst(p.clone().setY(p.y + 0.6), { n: 4, color: '#6c7cc0', speed: 0.02, up: 0.01, size: 0.5, life: 18, smoke: true, spread: 0.3 });
          this.flare(p.clone().setY(p.y + 0.8), '#9fc0ff', 1.4, 8);
          break;
        } else if (e.kind === 'rouse') {
          // Shaken awake: the dust scatters.
          this.vfx.burst(p.clone().setY(p.y + 0.9), { n: 6, color: '#dce8ff', speed: 0.07, up: 0.04, size: 0.08, life: 8, spread: 0.3 });
          break;
        } else if (e.kind === 'ward') {
          // An oracle raises its bubbles: a cold ring rolls out from its orb.
          this.forceNear(s, 'oracle', e.x, e.y, 'ward');
          this.vfx.ring(p.clone().setY(p.y + 0.3), 0.3, r, '#7fe8ff', 12, 0.6);
          this.vfx.ring(p.clone().setY(p.y + 0.9), 0.2, r * 0.6, '#e6fbff', 9, 0.35);
          break;
        } else if (e.kind === 'wardBreak') {
          // A bubble bursts: shards of ink-glass and a cyan flash.
          this.vfx.burst(p.clone().setY(p.y + 0.7), { n: this.opts.quality === 'low' ? 6 : 14, color: '#9ff0ff', speed: 0.09, up: 0.05, size: 0.08, life: 9, gravity: 0.006 });
          this.vfx.burst(p.clone().setY(p.y + 0.7), { n: 5, color: '#14202a', speed: 0.07, up: 0.04, size: 0.11, life: 10, gravity: 0.01 });
          this.vfx.ring(p.clone().setY(p.y + 0.6), 0.2, 0.9, '#7fe8ff', 5, 0.8);
          this.flare(p.clone().setY(p.y + 0.7), '#bff6ff', 1.6, 5);
          break;
        } else if (e.kind === 'cut') {
          // The silk cut free: pale shreds fall away.
          this.vfx.burst(p.clone().setY(p.y + 1.2), { n: this.opts.quality === 'low' ? 6 : 14, color: '#e8e4dc', speed: 0.05, up: 0.02, size: 0.1, life: 16, gravity: 0.004, spread: 0.5 });
          break;
        } else if (e.kind === 'simaSpores') {
          // The spore mother bursts at the end of her swelling (a puffball's burst has no mother to play).
          this.forceNear(s, 'sporeMother', e.x, e.y, 'burst');
        } else if (e.kind === 'brood') {
          // A weaver bursts: venom and a scatter of spiderlings.
          this.vfx.burst(p.clone().setY(p.y + 0.3), { n: this.opts.quality === 'low' ? 8 : 18, color: '#8fdc4a', speed: 0.08, up: 0.07, size: 0.09, life: 10, gravity: 0.01 });
          this.vfx.burst(p.clone().setY(p.y + 0.3), { n: 6, color: '#1a1622', speed: 0.06, up: 0.03, size: 0.45, life: 14, smoke: true, spread: 0.4 });
          this.vfx.ring(p, 0.2, r, '#8fdc4a', 6, 0.6);
          break;
        } else if (e.kind === 'douse') {
          // A hero beats the fire out: a hiss of steam.
          this.vfx.burst(p.clone().setY(p.y + 0.9), { n: this.opts.quality === 'low' ? 4 : 10, color: '#cfd6dc', speed: 0.03, up: 0.06, size: 0.5, life: 16, smoke: true, spread: 0.4 });
          break;
        } else if (e.kind === 'knock') {
          // A hero thrown back by the ogre lands in a puff of dust.
          this.vfx.burst(p.clone().setY(p.y + 0.15), { n: this.opts.quality === 'low' ? 4 : 9, color: '#6d6458', speed: 0.05, up: 0.03, size: 0.35, life: 12, smoke: true, spread: 0.25 });
          this.vfx.ring(p, 0.1, 0.6, '#cfc3a4', 5, 0.5);
          break;
        }
        if (e.kind === 'burrow' || e.kind === 'emerge') {
          this.earthBurst(e.kind === 'emerge', p, r);
          break;
        }
        if (e.kind === 'eruption') {
          this.eruptionBurst(p, r);
          break;
        }
        const unB = unOf(e.kind);
        if (unB) {
          this.unsleepingBoom(s, unB, e.kind, e.x, e.y, p, r);
          break;
        }
        if (e.kind === 'glassBreak' || e.kind === 'colossusSlam') {
          if (e.kind === 'glassBreak') this.glassBreak(p, r);
          else this.colossusSlam(p, r);
          break;
        }
        if (this.puzzles?.boom(e.kind, e.x, e.y, s.tick)) break;
        if (e.kind === 'plunderLost') {
          // A dragger got a cache down its hole: a red flare and a puff of dust.
          this.flare(p.clone().setY(p.y + 0.4), '#ff4f3a', 2, 16);
          this.vfx.burst(p, { n: this.opts.quality === 'low' ? 4 : 10, color: '#4a3d33', speed: 0.04, up: 0.03, size: 0.3, life: 14, smoke: true });
          break;
        }
        const cellarShake = this.cellars?.boom(e.kind, e.x, e.y, e.r) ?? null;
        if (cellarShake !== null) {
          this.shake = Math.max(this.shake, cellarShake);
          break;
        }
        if (this.formFx.boom(e.kind, p, r)) break;
        if (this.sagaBoom(e.kind, p, r)) break;
        if (this.sima?.boom(e.kind, e.x, e.y, e.r, s.zones)) break;
        if (e.kind === 'rune') {
          // A plate of the seal stepped on in the right order.
          this.vfx.ring(p, 0.15, Math.max(0.9, r * 1.6), '#ff8a6e', 9, 1);
          this.vfx.shaft(p.clone().setY(p.y + 0.02), p.clone().setY(p.y + 1.6), '#ff6a3a', 6, 0.18);
          this.vfx.burst(p.clone().setY(p.y + 0.2), { n: this.opts.quality === 'low' ? 6 : 14, color: '#ffb59e', speed: 0.04, up: 0.08, size: 0.1, life: 10, gravity: 0.004 });
          this.flare(p.clone().setY(p.y + 0.4), '#ff6a3a', 1.6, 5);
          break;
        }
        const col =
          e.kind === 'fireball' || e.kind === 'pumpkin' ? '#ff7a2a' : e.kind === 'rock' || e.kind === 'boulder' ? '#c9b08a' : e.kind === 'storm' ? '#a8c8ff' : e.kind === 'repair' ? '#7fffb0' : e.kind === 'summon' || e.kind === 'spit' ? '#9b4dff' : e.kind === 'bash' ? '#ffffff' : '#eceef2';
        if (e.kind === 'smite' || e.kind === 'revive' || e.kind === 'mend') {
          this.holyBoom(e.kind, p);
          break;
        }
        if (this.kitBoom(e.kind, p, r)) break;
        if (this.gearBoom(e.kind, p)) break;
        if (e.kind.startsWith('overload') && this.clockAt.has(`${e.x},${e.y}`)) {
          this.clockVent(e.kind, p, r);
          break;
        }
        if (this.clockBoom(e.kind, p, r, e.x, e.y)) break;
        if (this.themeScene?.boom?.(e.kind, p, r)) break;
        if (e.kind === 'slam' || e.kind === 'nova' || e.kind === 'ring') {
          // The Warden's blows: a shockwave, dust and a jolt of the camera. A slam's or nova's wave stops at the line it
          // hits within (it went 10 % past it and read as a blow landing outside); a ring's runs out over what it hits.
          const c = '#ff6a4a';
          this.vfx.ring(p, e.kind === 'ring' ? r : r * 0.2, e.kind === 'ring' ? r * 2.6 : r, c, 9, 1);
          this.vfx.ring(p.clone().setY(p.y + 0.3), r * 0.1, r * 0.8, '#eceef2', 6, 0.6);
          this.vfx.burst(p.clone().setY(p.y + 0.2), { n: e.kind === 'slam' ? 26 : 40, color: '#4a4440', speed: 0.12, up: 0.06, size: 0.4, life: 14, smoke: true, spread: r * 0.6 });
          this.vfx.burst(p.clone().setY(p.y + 0.2), { n: 18, color: c, speed: 0.14, up: 0.1, size: 0.14, life: 10 });
          this.shake = Math.max(this.shake, e.kind === 'slam' ? 0.35 : 0.5);
          break;
        }
        if (e.kind === 'rain') {
          // An arrow rain's pulse: the arrows themselves show it (skillfx.ts); only a faint shiver at the rim.
          this.vfx.ring(p, r * 0.88, r, '#c4ccd8', 5, 0.28);
          break;
        }
        this.vfx.ring(p, r * 0.3, r, col, 6, 0.9);
        if (e.kind === 'fireball' || e.kind === 'pumpkin') {
          this.vfx.burst(p.setY(p.y + 0.3), { n: 22, color: '#ff8a2a', speed: 0.12, up: 0.08, size: 0.25, life: 8 });
          this.vfx.burst(p, { n: 10, color: '#2a2422', speed: 0.06, up: 0.05, size: 0.5, life: 12, smoke: true });
        } else if (e.kind === 'rock') {
          this.vfx.burst(p, { n: 12, color: '#6d6559', speed: 0.09, up: 0.08, size: 0.2, life: 9, smoke: true, gravity: 0.01 });
        } else if (e.kind === 'boulder') {
          // The trebuchet's stone lands: a heavier spray of earth and grit, and the board jolts.
          this.vfx.burst(p, { n: this.opts.quality === 'low' ? 10 : 22, color: '#6d6559', speed: 0.12, up: 0.1, size: 0.26, life: 11, smoke: true, gravity: 0.012, spread: r * 0.4 });
          this.vfx.ring(p.clone().setY(p.y + 0.05), r * 0.2, r * 0.9, '#8a8172', 7, 0.6);
          this.shake = Math.max(this.shake, 0.12);
        } else if (e.kind === 'summon' || e.kind === 'spit') {
          this.vfx.burst(p.setY(p.y + 0.5), { n: 16, color: '#7b3dff', speed: 0.08, up: 0.06, size: 0.3, life: 10 });
        }
        break;
      }
      case 'bell': {
        const st = s.structures.find((x) => x.id === e.id);
        if (st) {
          const c = hexToWorld(st.q, st.r);
          const p = at(c.x, c.y, 0.2);
          this.vfx.ring(p, 0.3, (2500 / 1000) * WORLD_PER_HEX, '#ffe0a0', 8, 0.8);
          this.vfx.ring(p.setY(p.y + 0.6), 0.2, 1.5, '#ffe0a0', 6, 0.6);
        }
        break;
      }
      case 'skill': {
        if (this.cellars?.skill(e.skill, e.x, e.y)) break;
        if (e.skill.startsWith('plunder')) {
          // A cache picked up, put down or loaded, and the group heading home: a warm flare where it happened.
          const at = simToWorld(e.x, e.y, new THREE.Vector3(), this.heightAt(e.x, e.y) + 0.5);
          if (e.skill === 'plunderLoad' || e.skill === 'plunderLeave') this.flare(at, '#ffb877', e.skill === 'plunderLeave' ? 3 : 1.6, 14);
          else this.flare(at, '#ff8a6e', 0.8, 8);
          break;
        }
        this.bossPower(s, e);
        this.ultCast(s, e.hero, e.skill);
        const unS = unOf(e.skill);
        if (unS) {
          this.unsleepingSkill(s, e, unS);
          break;
        }
        if (this.sima?.skill(e.skill, e.x, e.y)) {
          // A new floor laid under the party (not the first): the cut to black, the camera to its entrance.
          if (e.skill === 'simaFloor' && (s.dungeon?.sima?.rev ?? 0) > 1) this.sima.descend(at(e.x, e.y, 0));
          break;
        }
        if (e.skill === 'sealBroken') {
          // The Warden's door unseals: fire runs over it, the bars shake and the plates' light goes to the door.
          const p = at(e.x, e.y, 0.1);
          const low = this.opts.quality === 'low';
          this.vfx.ring(p, 0.3, 3.2, '#ff4f3a', 12, 1);
          this.vfx.ring(p.clone().setY(p.y + 0.7), 0.2, 1.6, '#ff8a6e', 8, 0.8);
          this.vfx.shaft(p, p.clone().setY(p.y + 2.6), '#ff5a24', 8, 0.5);
          this.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 14 : 30, color: '#ff8a3d', speed: 0.07, up: 0.09, size: 0.14, life: 14, gravity: 0.008, spread: 0.4 });
          this.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 4 : 8, color: '#3a2a26', speed: 0.03, up: 0.03, size: 0.6, life: 20, smoke: true, spread: 0.5 });
          this.flare(p.clone().setY(p.y + 0.8), '#ff6a3a', 3.5, 8);
          if (this.seal) for (const a of this.seal.at) this.vfx.beam(a.clone().setY(a.y + 0.1), p.clone().setY(p.y + 0.6), '#ff6a3a', 6, 0.03, 0.8);
          this.shake = Math.max(this.shake, 0.35);
          break;
        }
        if (e.skill === 'colossusGlint' || e.skill === 'colossusShine' || e.skill === 'colossusSlam') {
          const w = s.enemies.find((x) => x.id === e.hero);
          // The key it is drawn with (baked ahead): the plain one baked a whole colossus on its first glint, and in a
          // holiday the undressed one did on its first shine (a fifth of a second, every screen of a live party).
          const ck = this.enemyKey('colossus', w?.tier ?? 1);
          this.colossusCast(e.skill, w ? at(w.x, w.y, 0.05) : at(e.x, e.y, 0.05), w ? this.lib.heightOf(ck) * this.lib.scaleOf(ck) : 4);
          break;
        }
        if (e.skill === 'descend' || e.skill === 'stairsOpen') {
          if (e.skill === 'descend') this.descend(simToWorld(e.x, e.y));
          break;
        }
        if (e.skill === 'magmaSlam') {
          const w = s.enemies.find((x) => x.id === e.hero);
          this.magmaSlam(w ? at(w.x, w.y, 0.05) : null, at(e.x, e.y, 0.05));
          break;
        }
        if (e.skill === 'dragonBreath') {
          this.dragonBreath(s, e.hero, e.x, e.y);
          break;
        }
        if (e.skill === 'dragonRoar' || e.skill === 'dragonDevour' || e.skill === 'dragonSweep') {
          const d = s.enemies.find((x) => x.id === e.hero);
          const dp = d ? at(d.x, d.y, 0.05) : at(e.x, e.y, 0.05);
          if (e.skill === 'dragonRoar') this.dragonRoar(dp);
          else if (e.skill === 'dragonDevour') this.dragonDevour(at(e.x, e.y, 0.05), dp);
          else this.dragonSweep(dp, (DRAGON.sweep.radius / 1000) * WORLD_PER_HEX);
          break;
        }
        if (e.skill === 'wardenRoar' || e.skill === 'wardenCast') {
          const w = s.enemies.find((x) => x.id === e.hero);
          if (!w) break;
          const p = at(w.x, w.y, 0.1);
          // Molten (crypt in embers): the same roar and casts, in fire instead of violet.
          const hot = this.ascuas !== null && !this.deep;
          if (e.skill === 'wardenRoar') {
            this.vfx.ring(p, 0.5, 6.5, hot ? '#ff4f3a' : '#9b4dff', 14, 1);
            this.vfx.ring(p.clone().setY(1.2), 0.3, 4, hot ? '#ffb070' : '#e7c9ff', 10, 0.7);
            this.vfx.burst(p.clone().setY(1.4), { n: 40, color: hot ? '#ff6a2a' : '#7b3dff', speed: 0.1, up: 0.08, size: 0.3, life: 16 });
            this.shake = Math.max(this.shake, 0.6);
          } else this.vfx.burst(p.clone().setY(1.6), { n: 10, color: hot ? '#ff8a6e' : '#c98bff', speed: 0.05, up: 0.06, size: 0.2, life: 8 });
          break;
        }
        const h = s.heroes.find((x) => x.id === e.hero);
        if (!h) break;
        const p = at(h.x, h.y, 0.05);
        if (e.skill === 'mend' || e.skill === 'sanctuary' || e.skill === 'dawn') {
          this.holyCast(s, e.skill, h, p, at(e.x, e.y, 0.05));
          break;
        }
        if (this.heroCast(s, e.skill, h, p, at(e.x, e.y, 0.05))) break;
        if (e.skill === 'gearGrenade') {
          // Lobbed: a cyan spark arcs from the artificer's hand to where the grenade lands.
          const from = p.clone().setY(p.y + 0.8);
          const to = at(e.x, e.y, 0.1);
          for (let i = 1; i <= 3; i++) {
            const t = i / 4;
            const q = from.clone().lerp(to, t);
            q.y += Math.sin(t * Math.PI) * 1.1;
            this.later.push({ n: i - 1, run: () => this.vfx.burst(q, { n: 3, color: i === 3 ? '#ffffff' : '#9feaff', speed: 0.01, up: 0, size: 0.14, life: 4, gravity: 0 }) });
          }
          this.vfx.ring(to, 0.2, 0.9, '#4fdcff', 5, 0.7);
        } else if (e.skill === 'automaton') {
          // Summoned: a rune-ring of cyan at the artificer and a beam to the drop point.
          this.vfx.ring(p, 0.2, 1.1, '#4fdcff', 6, 0.7);
          const to = at(e.x, e.y, 0.05);
          this.vfx.shaft(to.clone().setY(to.y + 4), to, '#9feaff', 4, 0.35);
        }
        break;
      }
      case 'struct': {
        const st = s.structures.find((x) => x.id === e.id);
        if (!st) break;
        const c = hexToWorld(st.q, st.r);
        const p = at(c.x, c.y, 0.3);
        if (e.what === 'destroyed') {
          this.vfx.burst(p, { n: 26, color: '#6d6559', speed: 0.1, up: 0.1, size: 0.35, life: 14, smoke: true, spread: 0.6 });
          this.vfx.burst(p, { n: 12, color: '#ff9a3a', speed: 0.1, up: 0.1, size: 0.12, life: 10 });
          this.shake = Math.max(this.shake, 0.25);
        } else if (e.what === 'built') {
          this.vfx.ring(p, 0.2, 1, '#7fb8ff', 8, 0.8);
          this.vfx.burst(p, { n: 14, color: '#bfa98a', speed: 0.06, up: 0.05, size: 0.3, life: 10, smoke: true, spread: 0.5 });
        } else if (e.what === 'purified') {
          this.vfx.burst(p.setY(p.y + 0.8), { n: 18, color: '#eaffff', speed: 0.05, up: 0.08, size: 0.16, life: 12 });
        }
        break;
      }
      case 'keep':
        this.flashes.set(-1, 2);
        this.shake = Math.max(this.shake, Math.min(0.2, e.dmg / 200));
        this.vfx.burst(this.keepPos.clone().setY(1.2), { n: 3, color: '#ffb070', speed: 0.06, up: 0.05, size: 0.12, life: 5 });
        break;
      case 'down': {
        const h = s.heroes.find((x) => x.id === e.id);
        if (h) this.vfx.burst(at(h.x, h.y, 0.5), { n: 16, color: '#c9d4ff', speed: 0.05, up: 0.08, size: 0.2, life: 14 });
        break;
      }
      case 'up': {
        const h = s.heroes.find((x) => x.id === e.id);
        if (h) this.vfx.ring(at(h.x, h.y, 0.05), 0.2, 1.4, '#eceef2', 8, 0.9);
        break;
      }
      case 'spawn': {
        // Those that rise play it (the gargoyle tearing off its perch, the bone wall out of the ground).
        if (ANIM.on) this.force(e.id, 'spawn');
        const en = s.enemies.find((x) => x.id === e.id);
        if (en) this.vfx.burst(at(en.x, en.y, 0.3), { n: 6, color: '#2b2244', speed: 0.04, up: 0.05, size: 0.4, life: 10, smoke: true });
        break;
      }
    }
  }

  /** Camera shake requested by events since the last read. */
  shake = 0;
  takeShake(): number {
    const s = this.shake;
    this.shake = 0;
    return s;
  }

  /** Every animation frame. */
  /**
   * `compile`: compiles the scene's shaders the way the screen draws it (into the tilt-shift's target or not:
   * a different program), so the nightmares baked ahead are ready before they appear.
   */
  frame(camera: THREE.Camera, compile?: (part?: THREE.Object3D) => Promise<unknown> | void): void {
    const now = performance.now() / 1000;
    this.cullCam = camera;
    const at = this.cullAt.setFromMatrixPosition(camera.matrixWorld);
    this.far = this.farFor(camera);
    this.camMove = this.camSeen ? at.distanceTo(this.camWas) : Infinity;
    this.camWas.copy(at);
    this.camSeen = true;
    // Every half second, anything new on screen that is translucent and double-sided is drawn in one pass
    // (see singlePass): cheap next to what it saves.
    if (this.passCheck++ % 30 === 0) singlePass(this.scene);
    this.riseFrame(now);
    this.dressing?.frame(now);
    this.greatWork?.frame(now);
    this.vfx.draw(camera);
    this.holy.frame(performance.now() / 1000);
    this.aura.frame(performance.now() / 1000);
    this.gearFx.frame(now);
    this.skillFx.frame(now);
    this.skillImpacts();
    this.ascuas?.frame(now);
    this.ink?.frame(now);
    this.omen?.frame?.(now, camera);
    this.village?.frame?.(now, camera);
    this.clueMarks?.frame(now);
    this.crowd?.frame(now);
    this.facetaker?.frame(now);
    this.rescueLayer?.frame(now);
    this.herdLayer?.frame(now);
    this.lashLayer?.frame(now, camera);
    this.huntLayer?.frame(now);
    this.trapLayer?.frame(now);
    this.dragonField?.frame(now);
    this.blastLayer?.frame(now);
    this.mine?.frame(now);
    this.halls?.frame(now);
    this.anvilHall?.frame(now);
    this.forgeYard?.frame(now);
    this.beside?.frame(now);
    this.wellSquare?.frame(now);
    this.fairSquare?.frame(now);
    this.shore?.frame(now);
    this.finCoast?.frame(now);
    this.sunken?.frame(now);
    this.lighthouse?.frame(now);
    this.pipes?.frame(now);
    this.ribs?.frame(now);
    this.neck?.frame(now);
    this.orderPost?.frame(now);
    this.spine?.frame(now, camera);
    this.altars?.frame(now, camera);
    this.taller?.frame(now, camera);
    this.biblio?.frame(now, camera);
    this.cable?.frame(now, camera);
    this.corazon?.frame(now, camera);
    this.ojos?.frame(now, camera);
    this.tormoL?.frame(now, camera);
    this.caza?.frame(now, camera);
    this.vert?.frame(now, camera);
    this.bote?.frame(now, camera);
    this.isla?.frame(now, camera);
    this.roca?.frame(now, camera);
    this.camino?.frame(now, camera);
    this.pasarela?.frame(now, camera);
    this.nieve?.frame(now, camera);
    this.ermita?.frame(now, camera);
    this.atrium?.frame(now);
    this.bosque?.frame(now, camera);
    this.hornos?.frame(now, camera);
    this.otono?.frame(now, camera);
    this.copa?.frame(now, camera);
    this.raices?.frame(now, camera);
    this.bark?.frame(now);
    this.asterL?.frame(now, camera);
    this.salt?.frame(now, camera);
    this.arch?.frame(now, camera);
    this.monastery?.frame(now, camera);
    this.seventh?.frame(now, camera);
    this.roots?.frame(now);
    // The Way's candle on a paladin's shield (models/dress.ts): its flame leans with the wind.
    CANDLE_TIME.value = now;
    this.drawPrints(now);
    this.orderBell?.frame(now);
    this.bellNight?.frame(now);
    this.race?.frame(now);
    this.seal?.frame(now);
    this.obsidian?.frame(now);
    this.collapseLayer?.frame(now);
    this.deep?.frame(now);
    this.sima?.frame(now, camera);
    this.puzzles?.frame(now);
    this.cellars?.frame(now);
    this.maze?.frame();
    this.bell?.model.tick(Math.floor(now * TICK_HZ));
    this.yard?.frame(now);
    this.ensignLayer?.frame(now);
    this.lidBell?.tick(Math.floor(now * TICK_HZ));
    this.table?.model.tick(Math.floor(now * TICK_HZ));
    this.terrain.fog?.update(performance.now() / 1000);
    if (this.somn) {
      this.somn.frame(now);
      const f = this.lights[0];
      if (f) f.intensity = this.brazierBase * (0.85 + 0.45 * this.somn.beat);
    }
    this.themeScene?.frame?.(performance.now() / 1000, camera);
    const c = this.crypt;
    if (c) this.cullRoomLights(camera, now);
    // The Chasm compiles everything any stratum draws once, as it opens (after the room lights: the same count).
    if (compile && this.sima && this.sima.prime(compile, [this.ascuas?.group, this.obsidian?.group, this.simaDummy?.group].filter((g): g is THREE.Group => !!g)) && this.simaDummy) {
      // The stand-in puzzles were only there to be compiled: their materials stay alive (and so their programs).
      this.root.remove(this.simaDummy.group);
      this.simaDummy.group.traverse((o) => (o as THREE.Mesh).isMesh && (o as THREE.Mesh).geometry.dispose());
      this.simaDummy = null;
    }
    // Once the fight's nightmares are baked, their shaders compile instead of on the spot as each first appears:
    // a stall of a fifth of a second on a desktop GPU, up to a second on an older one, felt as lag when a pack
    // comes in.
    if (compile && this.toBake?.length === 0 && !this.compiledAhead) {
      this.compiledAhead = true;
      // One mesh at a time, the next once the last is linked (Post.compile), in the order they were baked: the
      // soonest to appear first. The browser compiles in its GPU process, and the whole fight's shaders sent at
      // once held it (and every frame) a second on a phone a couple of seconds into a live crypt. Then the rest of the scene (most of it compiled already), and once linked each batch draws
      // once with nothing in it: the first real drawing was a stall too (the buffers' upload and the driver's
      // draw state), felt when a crypt room's pack came in.
      const parts = this.lib.meshes();
      const next = (): Promise<unknown> => {
        if (this.gone) return Promise.reject(new Error('gone'));
        const part = parts.shift();
        return Promise.resolve(compile(part)).then(() => (part ? next() : undefined));
      };
      void next().then(
        () => {
          this.warmAhead = this.linkedAhead = true;
          this.vfx.primed = this.skillFx.primed = this.gearFx.primed = this.holy.primed = this.aura.primed = true;
        },
        () => undefined,
      );
    }
    if (this.warmAhead) this.warmAhead = this.lib.warmNext();
    if (!c) return;
    for (const door of c.doors) {
      const target = (door.bars.userData.target as number | undefined) ?? 0;
      door.open += (target - door.open) * 0.08;
      door.bars.position.y = door.open * 1.25;
    }
    if (c.exit) c.exit.time.value = now % 3600;
    for (const f of c.flames) {
      const k = 0.85 + Math.sin(now * 9 + f.room * 1.7 + f.mesh.position.x) * 0.1 + Math.sin(now * 23 + f.mesh.position.z) * 0.05;
      f.mesh.scale.set(1, k, 1);
      f.glow.material.opacity = 0.5 + k * 0.25;
    }
    const pulse = 0.22 + (Math.sin(now * 2.2) + 1) * 0.1 + (c.runes.userData.boss ? 0.15 : 0);
    c.runes.traverse((m) => {
      if ((m as THREE.Mesh).isMesh) ((m as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = pulse;
    });
    for (const circle of c.runes.children) circle.rotation.y = now * 0.05;
  }

  setHover(cell: { q: number; r: number } | null, valid: boolean): void {
    if (!cell) {
      this.hover.visible = false;
      return;
    }
    const c = hexToWorld(cell.q, cell.r);
    const p = simToWorld(c.x, c.y, new THREE.Vector3(), this.heightAt(c.x, c.y));
    this.hover.position.copy(p);
    (this.hover.material as THREE.LineBasicMaterial).color.set(valid ? '#eceef2' : '#ff4f3a');
    this.hover.visible = true;
  }

  /** Translucent chalk-blue preview of a structure at a cell, with its range. */
  /** `tint` colours the reach ring (the frost's warmth, docs/PRESAGIOS.md §3.3). */
  setPreview(type: string | null, cell: { q: number; r: number } | null, valid: boolean, range = 0, foot?: { q: number; r: number }[], tint?: string): void {
    if (this.rollArrow) this.rollArrow.visible = false;
    if (!type || !cell) {
      if (this.preview) this.preview.visible = false;
      this.rangeRing.visible = false;
      this.footMarks(null, false);
      return;
    }
    // A boulder's plan: `foot` is the one cell below it, the way it rolls (docs/SABERES.md §4.1). Its ghost faces
    // there and an arrow on the ground runs from its cell to that one.
    if (type === 'boulder' && foot?.length === 1) {
      this.boulderPreview(cell, foot[0] as { q: number; r: number }, valid);
      this.rangeRing.visible = false;
      return;
    }
    const key = `struct:${type}:1:1`;
    if (this.previewKey !== key) {
      if (this.preview) this.overlay.remove(this.preview);
      this.preview = this.lib.ghostOf(key, this.blueprintMat);
      this.previewKey = key;
      this.overlay.add(this.preview);
    }
    const p = this.standAt(cell.q, cell.r, foot);
    const pv = this.preview as THREE.Group;
    pv.visible = true;
    pv.position.copy(p);
    const sc = this.lib.scaleOf(key) * footScale(foot);
    pv.scale.setScalar(sc);
    // A wall or gate set beside a run lines up with it (on terraced land); otherwise round the keep.
    const run = (type === 'wall' || type === 'gate') && this.map.land ? this.runYaw(this.previewLinks(cell)) : null;
    if (run !== null) pv.rotation.y = run;
    else if (type === 'wall' || type === 'gate') pv.rotation.y = Math.atan2(p.x, p.z);
    else pv.rotation.y = foot ? footYaw(cell.q, cell.r, foot) : 0;
    this.footMarks(foot ? [cell, ...foot] : null, valid);
    this.blueprintMat.color.set(valid ? '#7fb8ff' : '#ff4f3a');
    if (range > 0) {
      const r = (range / 1000) * WORLD_PER_HEX;
      this.rangeRing.visible = true;
      this.rangeRing.position.set(p.x, p.y + 0.05, p.z);
      this.rangeRing.scale.setScalar(r);
      const m = this.rangeRing.material as THREE.MeshBasicMaterial;
      m.color.set(valid ? (tint ?? '#eceef2') : '#ff4f3a');
      m.opacity = tint && valid ? 0.6 : 0.35;
    } else this.rangeRing.visible = false;
  }

  /** The arrow on a boulder's plan, from its cell to the one below (made the first time; the ghost's own paint). */
  private rollArrow: THREE.Mesh | null = null;

  private boulderPreview(cell: { q: number; r: number }, below: { q: number; r: number }, valid: boolean): void {
    const key = 'struct:boulder:1:1';
    if (this.previewKey !== key) {
      if (this.preview) this.overlay.remove(this.preview);
      this.preview = this.lib.ghostOf(key, this.blueprintMat);
      this.previewKey = key;
      this.overlay.add(this.preview);
    }
    const p = this.standAt(cell.q, cell.r);
    const b = this.standAt(below.q, below.r);
    const yaw = Math.atan2(b.x - p.x, b.z - p.z);
    const pv = this.preview as THREE.Group;
    pv.visible = true;
    pv.position.copy(p);
    pv.scale.setScalar(this.lib.scaleOf(key));
    pv.rotation.y = yaw;
    if (!this.rollArrow) {
      // A flat arrow one unit long along +Z from its tail: a shaft and a broad head.
      const sh = new THREE.Shape();
      // Drawn toward −Y and laid face up (rotateX −π/2 turns −Y into +Z and the face to the sky).
      sh.moveTo(-0.1, -0.32);
      sh.lineTo(-0.1, -0.68);
      sh.lineTo(-0.28, -0.68);
      sh.lineTo(0, -1);
      sh.lineTo(0.28, -0.68);
      sh.lineTo(0.1, -0.68);
      sh.lineTo(0.1, -0.32);
      sh.closePath();
      const geo = new THREE.ShapeGeometry(sh).rotateX(-Math.PI / 2);
      this.rollArrow = new THREE.Mesh(geo, this.blueprintMat);
      this.rollArrow.renderOrder = 5;
      this.overlay.add(this.rollArrow);
    }
    const a = this.rollArrow;
    const len = Math.hypot(b.x - p.x, b.z - p.z);
    a.visible = true;
    a.position.set(p.x, Math.max(p.y, b.y) + 0.06, p.z);
    a.rotation.set(0, yaw, 0);
    a.scale.set(len * 0.9, 1, len);
    this.footMarks([cell, below], valid);
    this.blueprintMat.color.set(valid ? '#7fb8ff' : '#ff4f3a');
  }

  /** Red outlines on the cells a correction would clear (none with null). */
  markCells(cells: { q: number; r: number }[] | null, valid = false): void {
    this.footMarks(cells && cells.length ? cells : null, valid);
  }

  /** Outlines of the cells a big building's plan would take (hidden with null), white where it fits, red where not. */
  private feet: THREE.LineLoop[] = [];
  private footMarks(cells: { q: number; r: number }[] | null, valid: boolean): void {
    const n = cells?.length ?? 0;
    while (this.feet.length < n) {
      const f = this.hover.clone() as THREE.LineLoop;
      f.material = (this.hover.material as THREE.LineBasicMaterial).clone();
      this.overlay.add(f);
      this.feet.push(f);
    }
    this.feet.forEach((f, i) => {
      const h = cells?.[i];
      f.visible = !!h;
      if (!h) return;
      const c = hexToWorld(h.q, h.r);
      f.position.copy(simToWorld(c.x, c.y, new THREE.Vector3(), this.heightAt(c.x, c.y) + 0.02));
      (f.material as THREE.LineBasicMaterial).color.set(valid ? '#7fb8ff' : '#ff4f3a');
    });
  }

  showRange(x: number, y: number, range: number, color = '#eceef2', opacity = 0.35): void {
    const p = simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y));
    const r = (range / 1000) * WORLD_PER_HEX;
    const m = this.rangeRing.material as THREE.MeshBasicMaterial;
    m.color.set(color);
    m.opacity = opacity;
    this.rangeRing.visible = true;
    this.rangeRing.position.set(p.x, p.y + 0.05, p.z);
    this.rangeRing.scale.setScalar(r);
  }

  hideRange(): void {
    this.rangeRing.visible = false;
  }

  showArea(x: number, y: number, radius: number): void {
    const p = simToWorld(x, y, new THREE.Vector3(), this.heightAt(x, y));
    this.areaRing.visible = true;
    this.areaRing.position.set(p.x, p.y + 0.06, p.z);
    this.areaRing.scale.setScalar((radius / 1000) * WORLD_PER_HEX);
  }

  hideArea(): void {
    this.areaRing.visible = false;
  }

  dispose(): void {
    for (const k of this.preyMats.keys()) this.lightPrey(k, false);
    this.gone = true;
    this.ascuas?.dispose();
    this.somn?.dispose();
    this.ink?.dispose();
    this.omen?.dispose();
    this.village?.dispose();
    this.clueMarks?.dispose();
    this.crowd?.dispose();
    this.facetaker?.dispose();
    this.rescueLayer?.dispose();
    this.herdLayer?.dispose();
    this.lashLayer?.dispose();
    this.huntLayer?.dispose();
    this.trapLayer?.dispose();
    this.dragonField?.dispose();
    this.blastLayer?.dispose();
    this.mine?.dispose();
    this.halls?.dispose();
    this.anvilHall?.dispose();
    this.forgeYard?.dispose();
    this.beside?.dispose();
    this.wellSquare?.dispose();
    this.fairSquare?.dispose();
    this.shore?.dispose();
    this.finCoast?.dispose();
    this.sunken?.dispose();
    this.lighthouse?.dispose();
    this.pipes?.dispose();
    this.ribs?.dispose();
    this.neck?.dispose();
    this.orderPost?.dispose();
    this.spine?.dispose();
    this.altars?.dispose();
    this.taller?.dispose();
    this.biblio?.dispose();
    this.cable?.dispose();
    this.corazon?.dispose();
    this.ojos?.dispose();
    this.tormoL?.dispose();
    this.caza?.dispose();
    this.vert?.dispose();
    this.bote?.dispose();
    this.isla?.dispose();
    this.roca?.dispose();
    this.camino?.dispose();
    this.pasarela?.dispose();
    this.nieve?.dispose();
    this.ermita?.dispose();
    this.atrium?.dispose();
    this.bosque?.dispose();
    this.hornos?.dispose();
    this.otono?.dispose();
    this.copa?.dispose();
    this.raices?.dispose();
    this.bark?.dispose();
    if (this.prints) {
      this.prints.geometry.dispose();
      (this.prints.material as THREE.Material).dispose();
    }
    this.asterL?.dispose();
    this.salt?.dispose();
    this.arch?.dispose();
    this.monastery?.dispose();
    this.seventh?.dispose();
    this.roots?.dispose();
    this.guardhouse?.dispose();
    this.orderBell?.dispose();
    this.bellNight?.dispose();
    this.race?.dispose();
    this.openGate?.dispose();
    if (this.stakes) {
      this.stakes.geometry.dispose();
      (this.stakes.material as THREE.Material).dispose();
    }
    this.seal?.dispose();
    this.obsidian?.dispose();
    this.collapseLayer?.dispose();
    this.unFx?.dispose();
    this.deep?.dispose();
    this.sima?.dispose();
    this.puzzles?.dispose();
    this.cellars?.dispose();
    this.plunder?.dispose();
    this.beer?.dispose();
    this.maze?.dispose();
    this.setTrophies({ bell: null, table: null });
    this.setSimaWinch(null);
    this.dressing?.dispose();
    this.greatWork?.dispose();
    this.gearFx.dispose();
    this.skillFx.dispose();
    this.themeScene?.dispose();
    this.themeScene = null;
    this.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
      // An instanced mesh's per-instance buffers (thousands of nightmares) live on the GPU until told.
      if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose();
      // A batched mesh's matrices and colors are textures of its own.
      if ((o as THREE.BatchedMesh).isBatchedMesh) (o as THREE.BatchedMesh).dispose();
      // Textures too (the view's rings, soft sprites, shadows, fog; a shared one is simply uploaded again
      // when next used). Materials stay: freeing them would recompile shaders on the next screen.
      for (const mat of m.material ? (Array.isArray(m.material) ? m.material : [m.material]) : []) {
        (mat as THREE.MeshBasicMaterial).map?.dispose();
        for (const u of Object.values((mat as THREE.ShaderMaterial).uniforms ?? {})) if ((u.value as THREE.Texture | null)?.isTexture) (u.value as THREE.Texture).dispose();
      }
    });
  }
}

/**
 * Translucent, double-sided materials, drawn in one pass. Otherwise three draws them twice (back faces, then
 * front) and flips `side` with a needsUpdate on every object every frame, which re-keys the program each
 * time: with a crowd of miniatures and effects on screen that was a slow phone's whole CPU. On these thin,
 * small pieces one pass looks the same.
 */
const singlePass = (scene: THREE.Object3D): void => {
  scene.traverse((o) => {
    const m = (o as THREE.Mesh).material;
    if (!m) return;
    for (const x of Array.isArray(m) ? m : [m]) if (x.transparent && x.side === THREE.DoubleSide && !x.forceSinglePass) x.forceSinglePass = true;
  });
};

/** Three arms of light wound into a vortex, inside a ring of gear teeth (the gear grenade's pull). */
const spiralTexture = (): THREE.Texture => {
  const S = 256, C = S / 2, R = S * 0.47;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d') as CanvasRenderingContext2D;
  g.lineCap = 'round';
  for (let k = 0; k < 3; k++) {
    let px = C, py = C;
    for (let i = 1; i <= 48; i++) {
      const t = i / 48;
      const a = (k * TAU) / 3 + t * 3.4;
      const x = C + Math.cos(a) * R * t, y = C + Math.sin(a) * R * t;
      g.strokeStyle = `rgba(255,255,255,${(1 - t * 0.55).toFixed(3)})`;
      g.lineWidth = 2 + t * 9;
      g.beginPath();
      g.moveTo(px, py);
      g.lineTo(x, y);
      g.stroke();
      px = x;
      py = y;
    }
  }
  // The rim: teeth of a gear, and a thin ring.
  g.strokeStyle = 'rgba(255,255,255,.55)';
  g.lineWidth = 2.5;
  g.beginPath();
  g.arc(C, C, R * 0.97, 0, TAU);
  g.stroke();
  g.fillStyle = 'rgba(255,255,255,.7)';
  for (let i = 0; i < 24; i++) {
    g.save();
    g.translate(C, C);
    g.rotate((i / 24) * TAU);
    g.fillRect(R * 0.9, -4, R * 0.1, 8);
    g.restore();
  }
  const gr = g.createRadialGradient(C, C, 0, C, C, R * 0.3);
  gr.addColorStop(0, 'rgba(255,255,255,.9)');
  gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr;
  g.fillRect(0, 0, S, S);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
};

const STALKER_HIDDEN = new THREE.Color(0.3, 0.3, 0.4);
const STUNNED = new THREE.Color(1.3, 1.25, 0.9);
/** The Prestacaras in a borrowed face: a shade too pale, cold, like wet paper. */
const PAPER_SKIN = new THREE.Color(1.22, 1.26, 1.4);
/** El Párpado 3: the copies' black glass (dark, a touch cold), stunned a shade lighter; its sheen and its cracks. */
const GLASS = new THREE.Color(0.17, 0.18, 0.24);
const GLASS_STUN = new THREE.Color(0.32, 0.33, 0.4);
const GLASS_SHEEN = new THREE.Color('#9aa8ff');
const GLASS_CRACK = new THREE.Color('#d7deff');
/** How long a felled copy kneels in its real colours before it shatters (s; copyMoment's shards come with its end). */
const COPY_KNEEL = 0.95;
const FEAR = new THREE.Color('#ff4f3a');
const CURSE = new THREE.Color('#8a5cff');
const FIRE = new THREE.Color('#ff6a24');
const SLEEP = new THREE.Color('#8fb0ff');
const WARD = new THREE.Color('#7fe8ff');
/** A path's people's ring, and a campfire's light (red-orange embers, never gold). */
const SENDA_KEEP = new THREE.Color('#cfe0ff');
/** A path's statue: light stone, a little warm in the light so it reads apart from the plinth's grey. */
const STATUE_STONE = new THREE.MeshStandardMaterial({ color: '#d9dce2', roughness: 0.92, metalness: 0 });
const FIRE_POOL = new THREE.Color('#ff6a3a');
const FIRE_CORE = new THREE.Color('#ff8a4e');
const FIRE_TIP = new THREE.Color('#ffd2b0');
/**
 * The hours a path's cinematic can be set at: background and fog, the terrain's mist, and the three lights. Dusk
 * goes ash-rose, dawn pale cold blue with a rose rim, night blue-black: cold charcoal like the rest, never gold.
 */
const CINE_SKY: Record<'day' | 'dusk' | 'night' | 'dawn', { bg: string; fog: string; low: string; mid: string; hemi: [string, string, number]; key: [string, number]; rim: [string, number] }> = {
  day: { bg: '#1a1d23', fog: '#2a2e36', low: '#3a3e46', mid: '#262a31', hemi: ['#c4c9d4', '#3a3632', 2.5], key: ['#fff0e2', 3.2], rim: ['#9fb4ff', 1.8] },
  dusk: { bg: '#140e10', fog: '#2a1c1e', low: '#4a2e2c', mid: '#2a1a1a', hemi: ['#a898a2', '#2a1210', 1.9], key: ['#ffcfc0', 2.4], rim: ['#ff6a4e', 2.2] },
  night: { bg: '#07090e', fog: '#0e131c', low: '#18202c', mid: '#0f141c', hemi: ['#6f80a8', '#14161c', 1.15], key: ['#a8bce8', 0.9], rim: ['#6f8fff', 1.4] },
  dawn: { bg: '#141820', fog: '#2a303c', low: '#3c4250', mid: '#262b36', hemi: ['#b4bccc', '#2c2826', 2.0], key: ['#ffd8d0', 2.2], rim: ['#ff9a8a', 1.6] },
};
const SILK = new THREE.Color('#f2efe8');

/** Stop-motion "breath": tiny per-tick scale wobble so idle nightmares never look frozen. */
const enemyPulse = (e: Enemy): number => 1 + (((e.id * 13 + e.animT) % 3) - 1) * 0.015;

export type PoseName = 'idle' | 'walkA' | 'walkB' | 'attackA' | 'attackB' | 'cast' | 'down' | 'work' | 'block' | 'bless' | 'raise';
export const POSES: PoseName[] = ['idle', 'walkA', 'walkB', 'attackA', 'attackB', 'cast', 'down', 'work', 'block', 'bless', 'raise'];

export const heroPose = (h: Hero, tick: number): PoseName => {
  if (h.downTicks > 0) return 'down';
  switch (h.anim) {
    case 'walk':
      return tick % 6 < 3 ? 'walkA' : 'walkB';
    case 'attack':
      return h.animT < 3 ? 'attackA' : 'attackB';
    case 'skill0':
    case 'skill1':
    case 'skill2':
      // The cleric holds out the sun-staff to bless, and lifts it to the sky for the dawn.
      if (h.cls === 'cleric') return h.anim === 'skill2' ? (h.animT < 8 ? 'raise' : 'idle') : h.animT < 5 ? 'bless' : 'idle';
      return h.animT < 5 ? 'cast' : 'idle';
    case 'hit':
      return 'block';
    case 'work':
      return tick % 6 < 3 ? 'work' : 'idle';
    default:
      return 'idle';
  }
};
